import { z } from 'zod';

// src/tenant.types.ts
var planSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  monthlyPrice: z.number().nonnegative(),
  annualPrice: z.number().nonnegative(),
  smsCreditsPerMonth: z.number().int().nonnegative(),
  emailCreditsPerMonth: z.number().int().nonnegative(),
  features: z.record(z.boolean()),
  isActive: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date()
});
var tenantSettingsSchema = z.object({
  timezone: z.string().default("America/New_York"),
  dateFormat: z.string().default("MM/DD/YYYY"),
  timeFormat: z.enum(["12h", "24h"]).default("12h"),
  defaultLanguage: z.enum(["en", "es", "fr"]).default("en"),
  branding: z.object({
    logoUrl: z.string().url().optional(),
    primaryColor: z.string().optional(),
    companyName: z.string().optional()
  }).optional()
});
var ORG_CUSTOM_FIELD_RESERVED = /* @__PURE__ */ new Set([
  "companyName",
  "logo",
  "timezone",
  "dateFormat",
  "bookingLink",
  "firstName",
  "lastName",
  "fullName",
  "email",
  "phone",
  "address",
  "city",
  "state",
  "zipCode",
  "country",
  "fullAddress",
  "name"
]);
var OrgCustomFieldSchema = z.object({
  fieldKey: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]*$/, "Must start with a letter; letters/numbers/underscore only").max(64).refine((k) => !ORG_CUSTOM_FIELD_RESERVED.has(k), {
    message: "Reserved field name \u2014 already an organisation field"
  }),
  label: z.string().min(1).max(100),
  value: z.string().max(500).default("")
});
var OrgCustomFieldsSchema = z.array(OrgCustomFieldSchema).max(50).superRefine((arr, ctx) => {
  const seen = /* @__PURE__ */ new Set();
  arr.forEach((f, i) => {
    if (seen.has(f.fieldKey)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [i, "fieldKey"],
        message: "Duplicate field key"
      });
    }
    seen.add(f.fieldKey);
  });
});
var tenantSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  slug: z.string().min(1).regex(/^[a-z0-9-]+$/),
  planId: z.string().uuid(),
  stripeCustomerId: z.string().optional(),
  settings: tenantSettingsSchema,
  isActive: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
  // OOBE
  setupCompletedAt: z.date().nullable().optional(),
  setupSkippedAt: z.date().nullable().optional(),
  setupStepsCompleted: z.string().nullable().optional(),
  subscriptionPromptTiming: z.enum(["early", "late"]).default("late"),
  // Business identity
  businessId: z.string().nullable().optional(),
  businessType: z.enum(["individual", "business"]).nullable().optional(),
  businessWebsite: z.string().nullable().optional(),
  customFields: OrgCustomFieldsSchema.optional()
});
var createTenantSchema = tenantSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
var updateTenantSchema = createTenantSchema.partial();
var userRoleSchema = z.enum(["SuperAdmin", "UserTenant", "TeamUser"]);
var userSettingsSchema = z.object({
  notifications: z.object({
    email: z.boolean().default(true),
    sms: z.boolean().default(false),
    push: z.boolean().default(true)
  }).optional(),
  preferences: z.object({
    theme: z.enum(["light", "dark", "system"]).default("system"),
    language: z.enum(["en", "es", "fr"]).default("en"),
    defaultView: z.enum(["grid", "list"]).default("list")
  }).optional()
});
var userSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  azureAdUserId: z.string(),
  email: z.string().email(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  phone: z.string().optional(),
  role: userRoleSchema,
  avatar: z.string().url().optional(),
  settings: userSettingsSchema,
  isActive: z.boolean(),
  lastLoginAt: z.date().optional(),
  createdAt: z.date(),
  updatedAt: z.date()
});
var userWithFullNameSchema = userSchema.extend({
  fullName: z.string()
});
var createUserSchema = userSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  lastLoginAt: true
});
var updateUserSchema = createUserSchema.partial().omit({
  tenantId: true,
  azureAdUserId: true
});
var authUserSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  email: z.string().email(),
  role: userRoleSchema
});
var contactPipelineMembershipSchema = z.object({
  groupId: z.string().uuid(),
  groupName: z.string(),
  groupColor: z.string(),
  stageId: z.string().uuid(),
  stageName: z.string(),
  stageColor: z.string()
});
var consentLevelSchema = z.enum(["transactional", "informational", "marketing"]);
var optInSourceSchema = z.enum(["import", "form", "manual", "api", "voice"]);
var tagSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string().min(1).max(50),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  createdAt: z.date()
});
var createTagSchema = tagSchema.omit({
  id: true,
  tenantId: true,
  createdAt: true
});
var contactSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  firstName: z.string().min(1),
  lastName: z.string().optional(),
  company: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zipCode: z.string().optional(),
  country: z.string().optional(),
  timezone: z.string().optional(),
  customFields: z.record(z.unknown()).default({}),
  // Consent Management
  smsOptIn: z.boolean().default(false),
  smsOptInAt: z.date().optional(),
  smsOptInSource: optInSourceSchema.optional(),
  smsOptOutAt: z.date().optional(),
  emailOptIn: z.boolean().default(false),
  emailOptInAt: z.date().optional(),
  emailOptInSource: optInSourceSchema.optional(),
  emailOptOutAt: z.date().optional(),
  voiceOptIn: z.boolean().default(false),
  voiceOptInAt: z.date().optional(),
  consentLevel: consentLevelSchema.default("transactional"),
  tcpaProofUrl: z.string().url().optional(),
  isActive: z.boolean().default(true),
  createdAt: z.date(),
  updatedAt: z.date(),
  pipelineMemberships: z.array(contactPipelineMembershipSchema).optional(),
  // Relations (optional for API responses)
  tags: z.array(tagSchema).optional()
});
var createContactSchema = contactSchema.omit({
  id: true,
  tenantId: true,
  createdAt: true,
  updatedAt: true,
  tags: true,
  pipelineMemberships: true
});
var updateContactSchema = createContactSchema.partial();
var contactImportRowSchema = z.object({
  email: z.string().email().optional(),
  phone: z.string().optional(),
  firstName: z.string().min(1),
  lastName: z.string().optional(),
  company: z.string().optional(),
  tags: z.array(z.string()).optional()
});
var contactFilterSchema = z.object({
  search: z.string().optional(),
  tagIds: z.array(z.string().uuid()).optional(),
  smsOptIn: z.boolean().optional(),
  emailOptIn: z.boolean().optional(),
  consentLevel: consentLevelSchema.optional(),
  isActive: z.boolean().optional()
});
var channelConsentSchema = z.object({
  informational: z.boolean().default(false),
  marketing: z.boolean().default(false)
});
var consentFormValuesSchema = z.object({
  sms: channelConsentSchema,
  email: channelConsentSchema,
  voice: channelConsentSchema
});
var createContactWithConsentSchema = createContactSchema.extend({
  consent: consentFormValuesSchema.optional(),
  tags: z.array(z.string().uuid()).optional()
});
var updateContactWithConsentSchema = createContactWithConsentSchema.partial();
var CONTACT_NOTE_MAX_LENGTH = 4e3;
var contactNoteSchema = z.object({
  id: z.string().uuid(),
  contactId: z.string().uuid(),
  authorId: z.string().nullable(),
  authorName: z.string().nullable(),
  body: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createContactNoteSchema = z.object({
  body: z.string().trim().min(1, "Note cannot be empty").max(CONTACT_NOTE_MAX_LENGTH)
});
var updateContactNoteSchema = createContactNoteSchema;
var formFieldTypeSchema = z.enum([
  "text",
  "textarea",
  "email",
  "phone",
  "number",
  "date",
  "time",
  "dropdown",
  "radio",
  "checkbox",
  "file",
  "signature",
  "section_header",
  "divider",
  "spacer"
]);
var fieldValidationSchema = z.object({
  required: z.boolean().default(false),
  minLength: z.number().int().optional(),
  maxLength: z.number().int().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  pattern: z.string().optional(),
  customMessage: z.string().optional()
});
var formFieldSchema = z.object({
  id: z.string(),
  type: formFieldTypeSchema,
  label: z.string().min(1),
  placeholder: z.string().optional(),
  helpText: z.string().optional(),
  defaultValue: z.unknown().optional(),
  options: z.array(z.object({ label: z.string(), value: z.string() })).optional(),
  validation: fieldValidationSchema.optional(),
  conditionalLogic: z.object({
    fieldId: z.string(),
    operator: z.enum(["equals", "notEquals", "contains", "notContains"]),
    value: z.unknown()
  }).optional()
});
var formSchemaDefinition = z.object({
  fields: z.array(formFieldSchema),
  layout: z.enum(["single", "two-column"]).default("single")
});
var formSettingsSchema = z.object({
  submitButtonText: z.string().default("Submit"),
  successMessage: z.string().default("Thank you for your submission!"),
  redirectUrl: z.string().url().optional(),
  // Email notifications
  notifyOnSubmission: z.boolean().default(true),
  notificationEmails: z.array(z.string().email()).optional(),
  webhookUrl: z.string().url().optional(),
  // Consent capture
  captureEmailConsent: z.boolean().default(false),
  captureSmsConsent: z.boolean().default(false),
  consentText: z.string().optional(),
  // Submission control
  allowMultipleSubmissions: z.boolean().default(true),
  maxSubmissions: z.number().int().positive().optional(),
  requireAuth: z.boolean().default(false)
});
var formBrandingSchema = z.object({
  logoUrl: z.string().url().nullable().optional(),
  primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default("#3b82f6"),
  logoPosition: z.enum(["left", "center", "right"]).default("center"),
  inputBorderRadius: z.number().int().min(0).max(20).default(8),
  inputBorderColor: z.string().nullable().optional(),
  inputBackgroundColor: z.string().nullable().optional(),
  inputTextColor: z.string().nullable().optional()
});
var formSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string().min(1),
  description: z.string().optional(),
  schema: formSchemaDefinition,
  settings: formSettingsSchema,
  branding: formBrandingSchema.optional(),
  qrCodeUrl: z.string().url().optional(),
  publicUrl: z.string(),
  isPublished: z.boolean().default(false),
  isActive: z.boolean().default(true),
  createdAt: z.date(),
  updatedAt: z.date()
});
var formSubmissionSchema = z.object({
  id: z.string().uuid(),
  formId: z.string().uuid(),
  contactId: z.string().uuid().optional(),
  data: z.record(z.unknown()),
  ipAddress: z.string().optional(),
  userAgent: z.string().optional(),
  submittedAt: z.date()
});
var createFormSchema = formSchema.omit({
  id: true,
  tenantId: true,
  publicUrl: true,
  qrCodeUrl: true,
  createdAt: true,
  updatedAt: true
});
var updateFormSchema = createFormSchema.partial();
var documentTypeSchema = z.enum(["template", "generated"]);
var documentStatusSchema = z.enum(["draft", "sent", "signed", "expired", "cancelled"]);
var signatureFieldPositionSchema = z.object({
  page: z.number().int().positive(),
  x: z.number().min(0).max(1),
  // Normalized position 0-1
  y: z.number().min(0).max(1),
  // Normalized position 0-1
  width: z.number().min(0).max(1),
  // Normalized width 0-1
  height: z.number().min(0).max(1)
  // Normalized height 0-1
});
var templateSignerSchema = z.object({
  id: z.string(),
  name: z.string(),
  order: z.number().int().positive()
});
var textFieldSchema = z.object({
  id: z.string(),
  variableName: z.string(),
  position: signatureFieldPositionSchema,
  signerId: z.string().optional()
  // Associate with specific signer for multi-signer documents
});
var templateContentSchema = z.object({
  html: z.string().optional(),
  // For rich text templates
  variables: z.array(z.string()),
  signatureFields: z.array(
    z.object({
      id: z.string(),
      signerId: z.string().optional(),
      // Associate with specific signer for multi-signer documents
      label: z.string(),
      required: z.boolean(),
      position: signatureFieldPositionSchema.optional()
    })
  ),
  // PDF-specific fields
  pdfPageCount: z.number().int().positive().optional(),
  // Number of pages in PDF
  textFields: z.array(textFieldSchema).optional(),
  // Variable placeholders on PDF
  signers: z.array(templateSignerSchema).optional()
  // Signers for multi-signer documents
});
var documentSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string().min(1),
  type: documentTypeSchema,
  templateContent: templateContentSchema.optional(),
  fileUrl: z.string().url().optional(),
  fileSize: z.number().int().optional(),
  originalName: z.string().optional(),
  pageCount: z.number().int().positive().optional(),
  status: documentStatusSchema.default("draft"),
  expiresAt: z.date().optional(),
  createdAt: z.date(),
  updatedAt: z.date()
});
var createDocumentSchema = documentSchema.omit({
  id: true,
  tenantId: true,
  createdAt: true,
  updatedAt: true
});
var updateDocumentSchema = createDocumentSchema.partial();

// src/document-signature-validation.ts
function validateAssignments(signers, fields) {
  const signerIds = new Set(signers.map((signer) => signer.id));
  const invalidFieldIds = fields.filter(
    (field) => typeof field.signerId !== "string" || field.signerId.trim().length === 0 || !signerIds.has(field.signerId)
  ).map((field) => field.id);
  const signersWithoutFields = signers.filter(
    (signer) => !fields.some((field) => field.signerId === signer.id)
  );
  if (invalidFieldIds.length > 0) {
    return {
      valid: false,
      invalidFieldIds,
      signersWithoutFields,
      error: "Every signature field must be assigned to a valid signer"
    };
  }
  if (signersWithoutFields.length > 0) {
    const names = signersWithoutFields.map((signer) => signer.name || signer.id).join(", ");
    return {
      valid: false,
      invalidFieldIds,
      signersWithoutFields,
      error: `Please assign at least one signature field to: ${names}`
    };
  }
  return { valid: true, invalidFieldIds: [], signersWithoutFields: [] };
}
function validateNdfSignatureAssignments(document) {
  const signatureFields = document.blocks.filter((block) => block.type === "field_signature").map((block) => ({ id: block.id, signerId: block.signerId }));
  return validateAssignments(document.signers ?? [], signatureFields);
}
function validateTemplateSignatureAssignments(templateContent) {
  const signatureFields = (templateContent.signatureFields ?? []).filter(
    (field) => !field.fieldType || field.fieldType === "signature"
  );
  return validateAssignments(templateContent.signers ?? [], signatureFields);
}
var signatureStatusSchema = z.enum([
  "pending",
  "sent",
  "viewed",
  "signed",
  "declined",
  "expired"
]);
var signatureDataSchema = z.object({
  type: z.enum(["draw", "type", "upload"]),
  data: z.string(),
  // Base64 image or typed name
  fontFamily: z.string().optional()
  // For typed signatures
});
var signatureSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  documentId: z.string().uuid(),
  contactId: z.string().uuid(),
  status: signatureStatusSchema.default("pending"),
  signedAt: z.date().optional(),
  signatureData: signatureDataSchema.optional(),
  ipAddress: z.string().optional(),
  userAgent: z.string().optional(),
  // Audit trail
  sentAt: z.date().optional(),
  viewedAt: z.date().optional(),
  remindersSent: z.number().int().default(0),
  lastReminderAt: z.date().optional(),
  createdAt: z.date(),
  updatedAt: z.date()
});
var createSignatureRequestSchema = z.object({
  documentId: z.string().uuid(),
  contactIds: z.array(z.string().uuid()).min(1),
  deliveryMethod: z.enum(["sms", "email", "both"]),
  expiresAt: z.date().optional(),
  message: z.string().optional()
});
var signDocumentSchema = z.object({
  signatureData: signatureDataSchema,
  ipAddress: z.string().optional(),
  userAgent: z.string().optional()
});
var signatureWithRelationsSchema = signatureSchema.extend({
  document: z.object({
    id: z.string().uuid(),
    name: z.string()
  }),
  contact: z.object({
    id: z.string().uuid(),
    firstName: z.string(),
    lastName: z.string().optional(),
    email: z.string().email().optional()
  })
});
var signerMappingSchema = z.object({
  signerId: z.string(),
  // Template signer ID (e.g., "signer_1")
  signerRole: z.string(),
  // Human-readable role name
  contactId: z.string().uuid(),
  signingOrder: z.number().int().positive()
});
var signatureInstanceStatusSchema = z.enum([
  "pending",
  "in_progress",
  "paused",
  "completed",
  "expired",
  "cancelled"
]);
var signatureInstanceSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  documentId: z.string().uuid(),
  campaignId: z.string().uuid().optional(),
  signerMap: z.record(z.string(), z.string().uuid()),
  // signerId -> contactId
  currentSignerId: z.string().optional(),
  status: signatureInstanceStatusSchema,
  signedPdfUrl: z.string().url().optional(),
  completedAt: z.date().optional(),
  expiresAt: z.date().optional(),
  createdAt: z.date(),
  updatedAt: z.date()
});
var signerMappingInstanceSchema = z.object({
  instanceId: z.string(),
  // Client-side temporary ID
  mappings: z.array(signerMappingSchema)
});
var multiSignerStatusSchema = signatureStatusSchema.or(z.enum(["waiting"]));
var signerContextSchema = z.object({
  role: z.string(),
  order: z.number().int().positive(),
  totalSigners: z.number().int().positive()
});
var contactColumnFilterSchema = z.object({
  field: z.string().min(1).max(150),
  operator: z.enum(["contains", "equals", "notEquals", "before", "after", "empty", "notEmpty"]),
  value: z.string().max(500).default("")
});
var contactColumnFiltersSchema = z.array(contactColumnFilterSchema).max(20);
var contactSavedViewSchema = z.object({
  id: z.string().uuid(),
  name: z.string().trim().min(1).max(60),
  search: z.string().max(200).default(""),
  tagIds: z.array(z.string().uuid()).max(100).default([]),
  columnFilters: contactColumnFiltersSchema.default([]),
  sortBy: z.string().max(150).default("createdAt"),
  sortOrder: z.enum(["asc", "desc"]).default("desc"),
  columns: z.array(z.string().max(150)).min(1).max(100),
  viewMode: z.enum(["table", "grid", "kanban"]).default("table"),
  pipelineMembership: z.object({
    operator: z.enum(["in", "notIn"]),
    groupId: z.string().uuid().optional(),
    stageId: z.string().uuid().optional()
  }).refine((value) => !value.stageId || !!value.groupId, {
    message: "A stage requires its parent pipeline"
  }).nullable().default(null)
});
var contactFilterChannelSchema = z.enum(["sms", "email", "voice"]);
var contactEligibilityFilterSchema = z.object({
  channel: contactFilterChannelSchema,
  messageType: z.enum(["transactional", "informational", "marketing", "any"])
});
var pipelineMembershipFilterSchema = z.object({
  operator: z.enum(["in", "notIn"]),
  groupId: z.string().uuid().optional(),
  stageId: z.string().uuid().optional()
}).refine((value) => !value.stageId || !!value.groupId, {
  message: "A stage requires its parent pipeline",
  path: ["stageId"]
});
var contactListFilterSchema = z.object({
  columnFilters: contactColumnFiltersSchema.default([]),
  pipelineMembership: pipelineMembershipFilterSchema.optional(),
  search: z.string().trim().max(200).optional(),
  tagIds: z.array(z.string().uuid()).default([]),
  groupIds: z.array(z.string().uuid()).default([]),
  stageIds: z.array(z.string().uuid()).default([]),
  states: z.array(z.string().max(100)).default([]),
  countries: z.array(z.string().max(100)).default([]),
  companies: z.array(z.string().max(200)).default([]),
  /** Matches if ANY entry is eligible (multi-channel sends are OR). */
  eligibleFor: z.array(contactEligibilityFilterSchema).default([])
});
function isEmptyContactListFilter(filter) {
  if (!filter) return true;
  return !filter.pipelineMembership && !filter.columnFilters?.length && !filter.search?.trim() && !filter.tagIds?.length && !filter.groupIds?.length && !filter.stageIds?.length && !filter.states?.length && !filter.countries?.length && !filter.companies?.length && !filter.eligibleFor?.length;
}
function contactListFilterToSearchParams(filter, params = new URLSearchParams()) {
  if (filter.columnFilters?.length)
    params.set("columnFilters", JSON.stringify(filter.columnFilters));
  if (filter.pipelineMembership)
    params.set("pipelineMembership", JSON.stringify(filter.pipelineMembership));
  if (filter.search?.trim()) params.set("search", filter.search.trim());
  if (filter.tagIds?.length) params.set("tags", filter.tagIds.join(","));
  if (filter.groupIds?.length) params.set("groupIds", filter.groupIds.join(","));
  if (filter.stageIds?.length) params.set("stageIds", filter.stageIds.join(","));
  filter.states?.forEach((v) => params.append("state", v));
  filter.countries?.forEach((v) => params.append("country", v));
  filter.companies?.forEach((v) => params.append("company", v));
  if (filter.eligibleFor?.length) {
    params.set(
      "eligibleFor",
      filter.eligibleFor.map((e) => `${e.channel}:${e.messageType}`).join(",")
    );
  }
  return params;
}
function parseEligibleForParam(value) {
  const raw = Array.isArray(value) ? value.join(",") : typeof value === "string" ? value : "";
  return raw.split(",").map((pair) => {
    const [channel, messageType] = pair.trim().split(":");
    const parsed = contactEligibilityFilterSchema.safeParse({ channel, messageType });
    return parsed.success ? parsed.data : null;
  }).filter((v) => v !== null);
}
var messageTypeSchema = z.enum(["transactional", "informational", "marketing"]);
var sendTypeSchema = z.enum(["message", "document", "form", "voice"]);
var deliveryMethodSchema = z.enum(["sms", "email", "sms_email", "voice"]);
var sendIntentSchema = z.enum(["esign", "form", "message"]);
var deliveryChannelSchema = z.enum(["sms", "email", "voice"]);
var deliveryConfigSchema = z.object({
  sms: z.boolean().default(false),
  email: z.boolean().default(true),
  voice: z.boolean().default(false)
});
var campaignTypeSchema = deliveryMethodSchema;
var campaignStatusSchema = z.enum([
  "draft",
  "scheduled",
  "sending",
  "paused",
  "completed",
  "cancelled",
  "failed"
]);
var recipientStatusSchema = z.enum([
  "pending",
  "sent",
  "delivered",
  "failed",
  "opened",
  "clicked",
  "bounced",
  "unsubscribed"
]);
var conversationStatusSchema = z.enum(["active", "waiting", "resolved", "archived"]);
var prioritySchema = z.enum(["low", "normal", "high", "urgent"]);
var channelSchema = z.enum(["sms", "email"]);
var messageDirectionSchema = z.enum(["inbound", "outbound"]);
var messageStatusSchema = z.enum(["pending", "sent", "delivered", "failed", "read"]);
var complianceCheckSchema = z.object({
  score: z.number().int().min(0).max(100),
  passed: z.boolean(),
  risks: z.array(
    z.object({
      type: z.string(),
      severity: z.enum(["low", "medium", "high"]),
      description: z.string(),
      suggestion: z.string().optional()
    })
  ),
  checkedAt: z.date()
});
var recipientSelectionSchema = z.object({
  type: z.enum(["manual", "tags", "all"]),
  contactIds: z.array(z.string().uuid()).default([]),
  tagIds: z.array(z.string().uuid()).default([]),
  excludeContactIds: z.array(z.string().uuid()).default([])
});
var recipientScheduleOverrideSchema = z.object({
  contactId: z.string().uuid(),
  channel: z.enum(["sms", "email", "voice"]),
  scheduledAt: z.coerce.date()
});
var scheduleOptionsSchema = z.object({
  type: z.enum(["immediate", "scheduled", "recurring"]),
  scheduledAt: z.coerce.date().optional(),
  timezone: z.string().default("America/New_York"),
  recurringRule: z.string().optional()
  // iCal RRULE format
});
var costEstimateSchema = z.object({
  recipientCount: z.number().int().min(0),
  smsCount: z.number().int().min(0),
  emailCount: z.number().int().min(0),
  voiceCount: z.number().int().min(0),
  estimatedCost: z.object({
    sms: z.number().min(0),
    email: z.number().min(0),
    voice: z.number().min(0),
    total: z.number().min(0)
  }),
  creditsAvailable: z.object({
    sms: z.number().int().min(0),
    email: z.number().int().min(0),
    voice: z.number().int().min(0)
  }),
  isAffordable: z.boolean()
});
var complianceCheckRequestSchema = z.object({
  content: z.string().min(1),
  messageType: messageTypeSchema,
  recipientCount: z.number().int().min(1),
  deliveryMethod: deliveryMethodSchema
});
var campaignSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  createdById: z.string().uuid(),
  name: z.string().min(1),
  // Send configuration
  sendType: sendTypeSchema,
  deliveryMethod: deliveryMethodSchema,
  messageType: messageTypeSchema,
  status: campaignStatusSchema.default("draft"),
  // Content - text content
  messageContent: z.string().optional(),
  smsContent: z.string().optional(),
  emailSubject: z.string().optional(),
  emailContent: z.string().optional(),
  // Content - references
  documentId: z.string().uuid().optional(),
  formId: z.string().uuid().optional(),
  voiceScriptId: z.string().uuid().optional(),
  // Scheduling
  scheduledAt: z.coerce.date().optional(),
  timezone: z.string().default("America/New_York"),
  isRecurring: z.boolean().default(false),
  recurringRule: z.string().optional(),
  // iCal RRULE format
  // AI Compliance
  complianceScore: z.number().int().optional(),
  complianceNotes: z.string().optional(),
  // JSON string
  complianceCheckedAt: z.date().optional(),
  // Stats
  totalRecipients: z.number().int().default(0),
  sentCount: z.number().int().default(0),
  deliveredCount: z.number().int().default(0),
  failedCount: z.number().int().default(0),
  openedCount: z.number().int().default(0),
  clickedCount: z.number().int().default(0),
  startedAt: z.date().optional(),
  completedAt: z.date().optional(),
  createdAt: z.date(),
  updatedAt: z.date()
});
var conversationSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  contactId: z.string().uuid(),
  assignedToId: z.string().uuid().optional(),
  channel: channelSchema,
  status: conversationStatusSchema.default("active"),
  priority: prioritySchema.default("normal"),
  lastMessageAt: z.date().optional(),
  unreadCount: z.number().int().default(0),
  createdAt: z.date(),
  updatedAt: z.date()
});
var messageSchema = z.object({
  id: z.string().uuid(),
  conversationId: z.string().uuid(),
  direction: messageDirectionSchema,
  channel: channelSchema,
  content: z.string(),
  // Email specific
  subject: z.string().optional(),
  htmlContent: z.string().optional(),
  attachments: z.array(z.object({ name: z.string(), url: z.string().url() })).optional(),
  // Status
  status: messageStatusSchema.default("pending"),
  externalId: z.string().optional(),
  failureReason: z.string().optional(),
  sentAt: z.date().optional(),
  deliveredAt: z.date().optional(),
  readAt: z.date().optional(),
  createdAt: z.date()
});
var createCampaignSchema = z.object({
  name: z.string().min(1).max(200),
  sendType: sendTypeSchema,
  deliveryMethod: deliveryMethodSchema,
  messageType: messageTypeSchema,
  // Content fields (varies by sendType)
  messageContent: z.string().optional(),
  documentId: z.string().uuid().optional(),
  formId: z.string().uuid().optional(),
  voiceScriptId: z.string().uuid().optional(),
  smsContent: z.string().max(1600).optional(),
  emailSubject: z.string().max(200).optional(),
  emailContent: z.string().optional(),
  // Template each channel was sent from. nullish: the send wizard posts null
  // for channels that aren't in template mode.
  smsTemplateId: z.string().uuid().nullish(),
  emailTemplateId: z.string().uuid().nullish(),
  // Recipients
  recipientSelection: recipientSelectionSchema,
  // Schedule
  schedule: scheduleOptionsSchema,
  recipientScheduleOverrides: z.array(recipientScheduleOverrideSchema).optional(),
  // Tags
  tagIds: z.array(z.string().uuid()).optional(),
  // Multi-signer support (for document templates with 2+ signers)
  signerMappings: z.array(signerMappingInstanceSchema).optional()
});
var updateCampaignSchema = createCampaignSchema.partial().extend({
  id: z.string().uuid()
});
var sendMessageSchema = z.object({
  contactIds: z.array(z.string().uuid()).min(1),
  channel: channelSchema,
  messageType: messageTypeSchema,
  content: z.string().min(1),
  subject: z.string().optional(),
  // For email
  htmlContent: z.string().optional(),
  // For email
  scheduledAt: z.coerce.date().optional()
});
var conversationListItemSchema = z.object({
  id: z.string().uuid(),
  channel: channelSchema,
  status: conversationStatusSchema,
  priority: prioritySchema,
  unreadCount: z.number().int().min(0),
  lastMessageAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  // Contact info (denormalized for list display)
  contact: z.object({
    id: z.string().uuid(),
    firstName: z.string(),
    lastName: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    avatar: z.string().nullable().optional()
  }),
  // Assigned user info
  assignedTo: z.object({
    id: z.string().uuid(),
    firstName: z.string(),
    lastName: z.string(),
    avatar: z.string().nullable().optional()
  }).nullable(),
  // Last message preview
  lastMessage: z.object({
    id: z.string().uuid(),
    content: z.string(),
    direction: messageDirectionSchema,
    createdAt: z.string().datetime()
  }).nullable()
});
var conversationMessageSchema = z.object({
  id: z.string().uuid(),
  conversationId: z.string().uuid(),
  direction: messageDirectionSchema,
  channel: channelSchema,
  content: z.string(),
  // Email specific
  subject: z.string().nullable(),
  htmlContent: z.string().nullable(),
  attachments: z.array(
    z.object({
      name: z.string(),
      url: z.string(),
      size: z.number().optional(),
      type: z.string().optional()
    })
  ).nullable(),
  messageId: z.string().nullable().optional(),
  inReplyTo: z.string().nullable().optional(),
  references: z.string().nullable().optional(),
  providerThreadId: z.string().nullable().optional(),
  // Status
  status: messageStatusSchema,
  externalId: z.string().nullable(),
  failureReason: z.string().nullable(),
  // Timestamps
  sentAt: z.string().datetime().nullable(),
  deliveredAt: z.string().datetime().nullable(),
  readAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime()
});
var conversationDetailSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  channel: channelSchema,
  status: conversationStatusSchema,
  priority: prioritySchema,
  unreadCount: z.number().int().min(0),
  lastMessageAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  // Full contact info
  contact: z.object({
    id: z.string().uuid(),
    firstName: z.string(),
    lastName: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    company: z.string().nullable(),
    smsOptIn: z.boolean(),
    emailOptIn: z.boolean(),
    tags: z.array(
      z.object({
        id: z.string().uuid(),
        name: z.string(),
        color: z.string()
      })
    )
  }),
  // Assigned user info
  assignedTo: z.object({
    id: z.string().uuid(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    avatar: z.string().nullable().optional()
  }).nullable(),
  // Messages in this conversation
  messages: z.array(conversationMessageSchema)
});
var listConversationsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  channel: channelSchema.optional(),
  status: conversationStatusSchema.optional(),
  assignedToId: z.string().uuid().optional(),
  search: z.string().optional(),
  unreadOnly: z.coerce.boolean().optional()
});
var conversationsListResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    conversations: z.array(conversationListItemSchema),
    pagination: z.object({
      page: z.number().int(),
      limit: z.number().int(),
      totalItems: z.number().int(),
      totalPages: z.number().int(),
      hasMore: z.boolean()
    })
  })
});
var conversationDetailResponseSchema = z.object({
  success: z.boolean(),
  data: conversationDetailSchema
});
var sendReplyRequestSchema = z.object({
  content: z.string().min(1).max(1e4),
  // For email replies
  subject: z.string().max(200).optional(),
  htmlContent: z.string().optional(),
  attachments: z.array(
    z.object({
      name: z.string(),
      url: z.string().optional(),
      size: z.number().optional(),
      type: z.string().optional(),
      contentBase64: z.string().optional()
    })
  ).optional(),
  inReplyTo: z.string().optional(),
  references: z.string().optional(),
  providerThreadId: z.string().optional()
});
var sendReplyResponseSchema = z.object({
  success: z.boolean(),
  data: conversationMessageSchema
});
var updateConversationRequestSchema = z.object({
  status: conversationStatusSchema.optional(),
  priority: prioritySchema.optional(),
  assignedToId: z.string().uuid().nullable().optional()
});
var updateConversationResponseSchema = z.object({
  success: z.boolean(),
  data: conversationListItemSchema
});
var conversationActionResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    id: z.string().uuid(),
    status: conversationStatusSchema,
    message: z.string()
  })
});
var unreadCountResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    totalUnread: z.number().int().min(0),
    byChannel: z.object({
      sms: z.number().int().min(0),
      email: z.number().int().min(0)
    })
  })
});
var voiceCallTypeSchema = z.enum(["outbound", "inbound", "three_way"]);
var voiceCallStatusSchema = z.enum(["pending", "in_progress", "completed", "failed"]);
var voiceConsentTypeSchema = z.enum(["sms", "email", "voice", "all"]);
var voiceCallSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  contactId: z.string().uuid(),
  type: voiceCallTypeSchema,
  status: voiceCallStatusSchema.default("pending"),
  // UltraVox integration
  ultravoxCallId: z.string().optional(),
  scriptContent: z.string(),
  // Recording
  recordingUrl: z.string().url().optional(),
  transcription: z.string().optional(),
  duration: z.number().int().optional(),
  // seconds
  // Consent outcome
  consentCaptured: z.boolean().default(false),
  consentType: voiceConsentTypeSchema.optional(),
  startedAt: z.date().optional(),
  endedAt: z.date().optional(),
  createdAt: z.date()
});
var voiceScriptCallTypeSchema = z.enum(["outbound_ai", "inbound_hotline", "three_way_merge"]);
var voiceScriptSettingsSchema = z.object({
  maxDuration: z.number().min(1).max(60).default(10),
  // minutes
  retryAttempts: z.number().min(0).max(5).default(2),
  retryInterval: z.number().min(1).max(72).default(24),
  // hours
  preferredCallTime: z.enum(["morning", "afternoon", "evening"]).optional(),
  timezone: z.string().default("America/New_York"),
  successWebhookUrl: z.string().url().optional(),
  failureWebhookUrl: z.string().url().optional()
});
var voiceScriptSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string().min(3).max(100),
  description: z.string().max(500).optional(),
  callType: voiceScriptCallTypeSchema,
  content: z.string().min(50).max(5e3),
  settings: voiceScriptSettingsSchema,
  isActive: z.boolean().default(true),
  createdAt: z.date(),
  updatedAt: z.date()
});
var voiceScriptWithTagsSchema = voiceScriptSchema.extend({
  tags: z.array(z.object({
    id: z.string().uuid(),
    name: z.string(),
    color: z.string()
  }))
});
var voiceScriptUsageStatsSchema = z.object({
  totalCalls: z.number().int().min(0),
  successfulCalls: z.number().int().min(0),
  failedCalls: z.number().int().min(0),
  averageDuration: z.number().min(0),
  // seconds
  successRate: z.number().min(0).max(100)
  // percentage
});
var createVoiceScriptSchema = z.object({
  name: z.string().min(3).max(100),
  description: z.string().max(500).optional(),
  callType: voiceScriptCallTypeSchema,
  content: z.string().min(50).max(5e3),
  settings: voiceScriptSettingsSchema.optional(),
  tags: z.array(z.string().uuid()).optional()
});
var updateVoiceScriptSchema = createVoiceScriptSchema.partial();
var previewVoiceScriptSchema = z.object({
  sampleData: z.record(z.string(), z.string())
  // Variable name -> sample value
});
var initiateVoiceCallSchema = z.object({
  contactId: z.string().uuid(),
  type: voiceCallTypeSchema,
  scriptContent: z.string().min(1)
});
var voiceCallResultSchema = z.object({
  callId: z.string(),
  status: voiceCallStatusSchema,
  duration: z.number().int().optional(),
  recordingUrl: z.string().url().optional(),
  transcription: z.string().optional(),
  consentCaptured: z.boolean(),
  consentType: voiceConsentTypeSchema.optional()
});
var ultravoxStatusSchema = z.enum([
  "queued",
  "ringing",
  "in_progress",
  "completed",
  "failed",
  "no_answer"
]);
var ultravoxWebhookPayloadSchema = z.object({
  callId: z.string(),
  status: ultravoxStatusSchema,
  duration: z.number().int().optional(),
  recordingUrl: z.string().url().optional(),
  transcription: z.string().optional(),
  consentCaptured: z.boolean().optional(),
  consentResponse: z.enum(["yes", "no"]).nullable().optional(),
  endedAt: z.string().optional(),
  // ISO timestamp
  failureReason: z.string().optional()
});
function mapUltravoxStatus(ultravoxStatus) {
  switch (ultravoxStatus) {
    case "queued":
      return "pending";
    case "ringing":
    case "in_progress":
      return "in_progress";
    case "completed":
      return "completed";
    case "failed":
    case "no_answer":
      return "failed";
    default:
      return "failed";
  }
}
var dateRangeSchema = z.object({
  start: z.date(),
  end: z.date()
});
var aggregationPeriodSchema = z.enum(["hour", "day", "week", "month", "year"]);
var dashboardStatsSchema = z.object({
  totalContacts: z.number().int(),
  activeContacts: z.number().int(),
  totalSends: z.number().int(),
  deliveryRate: z.number(),
  // percentage
  responseRate: z.number(),
  // percentage
  // By type
  messagesSent: z.number().int(),
  documentsSent: z.number().int(),
  formsSent: z.number().int(),
  voiceCallsMade: z.number().int(),
  // Signatures
  pendingSignatures: z.number().int(),
  completedSignatures: z.number().int(),
  signatureCompletionRate: z.number(),
  // percentage
  // Forms
  formSubmissions: z.number().int(),
  // Period comparison
  periodChange: z.object({
    contacts: z.number(),
    // percentage change
    sends: z.number(),
    deliveryRate: z.number()
  })
});
var timeSeriesPointSchema = z.object({
  timestamp: z.date(),
  value: z.number()
});
var chartDataSchema = z.object({
  labels: z.array(z.string()),
  datasets: z.array(
    z.object({
      label: z.string(),
      data: z.array(z.number()),
      color: z.string().optional()
    })
  )
});
var activitySummarySchema = z.object({
  date: z.date(),
  sends: z.object({
    messages: z.number().int(),
    documents: z.number().int(),
    forms: z.number().int(),
    voice: z.number().int()
  }),
  deliveries: z.object({
    sms: z.number().int(),
    email: z.number().int()
  }),
  responses: z.object({
    signed: z.number().int(),
    submitted: z.number().int(),
    replied: z.number().int()
  })
});
var analyticsQuerySchema = z.object({
  dateRange: dateRangeSchema,
  aggregation: aggregationPeriodSchema.optional(),
  sendTypes: z.array(sendTypeSchema).optional(),
  tagIds: z.array(z.string().uuid()).optional()
});
var exportFormatSchema = z.enum(["csv", "pdf", "xlsx"]);
var reportRequestSchema = z.object({
  type: z.enum(["activity", "delivery", "signatures", "forms", "voice"]),
  dateRange: dateRangeSchema,
  format: exportFormatSchema,
  includeDetails: z.boolean().default(false)
});
var activityStatusSchema = z.enum([
  "draft",
  "scheduled",
  "sending",
  "sent",
  "delivered",
  "completed",
  "paused",
  "cancelled",
  "failed",
  // Document-specific statuses
  "awaiting_signature",
  "signed",
  // Form-specific statuses
  "awaiting_submission",
  "submitted",
  // Voice-specific statuses
  "calling",
  "answered",
  "no_answer"
]);
var activityTabSchema = z.enum(["activity", "inbox", "drafts"]);
var activityStatsSchema = z.object({
  sent: z.number().int().min(0).default(0),
  delivered: z.number().int().min(0).default(0),
  opened: z.number().int().min(0).default(0),
  failed: z.number().int().min(0).default(0),
  pending: z.number().int().min(0).default(0)
});
var activityTagSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  color: z.string()
});
var activityItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  sendType: sendTypeSchema,
  deliveryMethod: deliveryMethodSchema,
  status: activityStatusSchema,
  // Recipients
  recipientCount: z.number().int().min(0),
  // Stats (computed)
  stats: activityStatsSchema,
  // Progress (for bulk sends)
  progress: z.number().min(0).max(100).optional(),
  // Dates
  createdAt: z.string(),
  scheduledAt: z.string().optional(),
  sentAt: z.string().optional(),
  completedAt: z.string().optional(),
  // Tags
  tags: z.array(activityTagSchema),
  // Content preview
  preview: z.string().optional(),
  // Created by
  createdBy: z.object({
    id: z.string().uuid(),
    name: z.string()
  }).optional()
});
var activityQueryParamsSchema = z.object({
  // Pagination
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
  // Search
  search: z.string().optional(),
  // Filters
  sendType: z.string().optional(),
  // Comma-separated: message,document,form,voice
  deliveryMethod: z.string().optional(),
  // Comma-separated: sms,email,voice
  status: z.string().optional(),
  // Comma-separated status values
  tagIds: z.string().optional(),
  // Comma-separated tag IDs
  // Date filters
  startDate: z.string().optional(),
  // ISO date string
  endDate: z.string().optional(),
  // ISO date string
  dateField: z.enum(["created", "scheduled", "sent"]).default("created"),
  // Sorting
  sortBy: z.enum(["name", "createdAt", "scheduledAt", "status", "recipientCount"]).default("createdAt"),
  sortOrder: z.enum(["asc", "desc"]).default("desc"),
  // Tab filter
  tab: activityTabSchema.default("activity")
});
var activityPaginationSchema = z.object({
  page: z.number().int(),
  limit: z.number().int(),
  total: z.number().int(),
  totalPages: z.number().int(),
  hasMore: z.boolean()
});
var activityListResponseSchema = z.object({
  data: z.array(activityItemSchema),
  pagination: activityPaginationSchema,
  filters: z.object({
    appliedFilters: z.number().int()
  })
});
var STATUS_BADGE_CONFIG = {
  draft: { label: "Draft", color: "gray", icon: "circle" },
  scheduled: { label: "Scheduled", color: "blue", icon: "clock" },
  sending: { label: "Sending", color: "yellow", icon: "loader", pulse: true },
  sent: { label: "Sent", color: "green", icon: "check" },
  delivered: { label: "Delivered", color: "green", icon: "check-double" },
  completed: { label: "Completed", color: "green", icon: "check" },
  paused: { label: "Paused", color: "orange", icon: "pause" },
  cancelled: { label: "Cancelled", color: "red", icon: "x" },
  failed: { label: "Failed", color: "red", icon: "alert" },
  awaiting_signature: { label: "Awaiting Signature", color: "yellow", icon: "pen" },
  signed: { label: "Signed", color: "green", icon: "pen" },
  awaiting_submission: { label: "Awaiting Submission", color: "yellow", icon: "clipboard" },
  submitted: { label: "Submitted", color: "green", icon: "clipboard" },
  calling: { label: "Calling", color: "yellow", icon: "loader", pulse: true },
  answered: { label: "Answered", color: "green", icon: "check" },
  no_answer: { label: "No Answer", color: "orange", icon: "x" }
};
var SEND_TYPE_CONFIG = {
  message: { label: "Message", color: "text-blue-600", bgColor: "bg-blue-100" },
  document: { label: "Document", color: "text-purple-600", bgColor: "bg-purple-100" },
  form: { label: "Form", color: "text-orange-600", bgColor: "bg-orange-100" },
  voice: { label: "Voice", color: "text-green-600", bgColor: "bg-green-100" }
};
var DELIVERY_METHOD_CONFIG = {
  sms: { label: "SMS", shortLabel: "SMS", color: "bg-blue-100 text-blue-700" },
  email: { label: "Email", shortLabel: "Email", color: "bg-indigo-100 text-indigo-700" },
  sms_email: { label: "SMS + Email", shortLabel: "SMS+Email", color: "bg-violet-100 text-violet-700" },
  voice: { label: "Voice", shortLabel: "Voice", color: "bg-green-100 text-green-700" }
};
var timelineEventTypeSchema = z.enum([
  "created",
  "scheduled",
  "started",
  "sent",
  "delivered",
  "opened",
  "clicked",
  "failed",
  "completed",
  "paused",
  "resumed",
  "cancelled",
  "reminder_sent",
  "signed",
  "submitted"
]);
var timelineEventSchema = z.object({
  id: z.string().uuid(),
  type: timelineEventTypeSchema,
  timestamp: z.string(),
  // ISO date string
  description: z.string(),
  metadata: z.record(z.unknown()).optional(),
  // Optional recipient info for recipient-specific events
  recipientId: z.string().uuid().optional(),
  recipientName: z.string().optional()
});
var timelineResponseSchema = z.object({
  events: z.array(timelineEventSchema),
  pagination: activityPaginationSchema
});
var channelConsentFlagsSchema = z.object({
  informational: z.boolean(),
  marketing: z.boolean()
});
var consentMatrixSchema = z.object({
  sms: channelConsentFlagsSchema,
  email: channelConsentFlagsSchema,
  voice: channelConsentFlagsSchema
});
var recipientWithContactSchema = z.object({
  id: z.string().uuid(),
  campaignId: z.string().uuid(),
  contactId: z.string().uuid(),
  status: z.enum(["pending", "sent", "delivered", "failed", "opened", "clicked", "bounced", "unsubscribed"]),
  channel: z.enum(["sms", "email", "voice"]),
  // Delivery timestamps
  sentAt: z.string().optional(),
  deliveredAt: z.string().optional(),
  openedAt: z.string().optional(),
  clickedAt: z.string().optional(),
  failedAt: z.string().optional(),
  failureReason: z.string().optional(),
  // External provider reference
  externalId: z.string().optional(),
  // Contact details (joined from Contact table)
  contact: z.object({
    id: z.string().uuid(),
    firstName: z.string(),
    lastName: z.string().optional(),
    email: z.string().email().optional(),
    phone: z.string().optional(),
    company: z.string().optional(),
    // Consent status. The flat *OptIn columns are a denormalized mirror; the
    // granular matrix below is authoritative whenever hasGranularConsentData.
    smsOptIn: z.boolean().optional(),
    emailOptIn: z.boolean().optional(),
    voiceOptIn: z.boolean().optional(),
    consentLevel: z.string().optional(),
    consent: consentMatrixSchema.nullable().optional(),
    hasGranularConsentData: z.boolean().optional()
  }),
  // PRE routing decision (null when no decision was logged — e.g. draft campaigns)
  preDecision: z.enum(["ALLOW", "ALLOW_WITH_CONDITIONS", "BLOCK"]).nullable().optional(),
  preEligibility: z.enum(["ELIGIBLE", "NOT_ELIGIBLE"]).nullable().optional(),
  preConditionCount: z.number().int().nullable().optional(),
  preRiskFlagCount: z.number().int().nullable().optional(),
  // Actual reason codes parsed from the PRE output JSON. Used by the recipient
  // table to render human-readable reason chips next to the Decision badge
  // (labels live in PRE_*_LABELS in pre.types). All optional / capped at the
  // most informative codes server-side — the UI just renders what it gets.
  preRemediationCodes: z.array(z.string()).optional(),
  preConditionCodes: z.array(z.string()).optional(),
  preRiskFlagCodes: z.array(z.string()).optional(),
  // When the send is held for calling hours (ALLOW_WITH_CONDITIONS +
  // COND_SCHEDULE_WITHIN_HOURS), the absolute time it will be sent. ISO string.
  scheduledSendAt: z.string().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional()
});
var recipientsListResponseSchema = z.object({
  data: z.array(recipientWithContactSchema),
  pagination: activityPaginationSchema
});
var campaignDetailSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string(),
  // Send configuration
  sendType: sendTypeSchema,
  deliveryMethod: deliveryMethodSchema,
  messageType: z.enum(["transactional", "informational", "marketing"]),
  status: z.enum(["draft", "scheduled", "sending", "paused", "completed", "cancelled", "failed"]),
  // Content - text content
  messageContent: z.string().optional(),
  smsContent: z.string().optional(),
  emailSubject: z.string().optional(),
  emailContent: z.string().optional(),
  // Content - references (IDs)
  documentId: z.string().uuid().optional(),
  formId: z.string().uuid().optional(),
  voiceScriptId: z.string().uuid().optional(),
  // Related entities (populated)
  document: z.object({
    id: z.string().uuid(),
    name: z.string(),
    fileName: z.string(),
    fileUrl: z.string(),
    signedFileUrl: z.string().optional(),
    pageCount: z.number().int().optional()
  }).optional(),
  form: z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().optional()
  }).optional(),
  voiceScript: z.object({
    id: z.string().uuid(),
    name: z.string(),
    scriptContent: z.string(),
    voiceType: z.string().optional()
  }).optional(),
  // Scheduling
  scheduledAt: z.string().optional(),
  timezone: z.string(),
  isRecurring: z.boolean(),
  recurringRule: z.string().optional(),
  // AI Compliance
  complianceScore: z.number().int().optional(),
  complianceNotes: z.string().optional(),
  complianceCheckedAt: z.string().optional(),
  // Stats
  totalRecipients: z.number().int(),
  sentCount: z.number().int(),
  deliveredCount: z.number().int(),
  failedCount: z.number().int(),
  openedCount: z.number().int(),
  clickedCount: z.number().int(),
  // Progress percentage (calculated)
  progress: z.number().min(0).max(100).optional(),
  // Timestamps
  startedAt: z.string().optional(),
  completedAt: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // Created by user
  createdBy: z.object({
    id: z.string().uuid(),
    firstName: z.string(),
    lastName: z.string().optional(),
    email: z.string().email()
  }),
  // Tags
  tags: z.array(activityTagSchema)
});
var campaignDetailResponseSchema = z.object({
  success: z.boolean(),
  data: campaignDetailSchema
});
var sendReminderRequestSchema = z.object({
  recipientIds: z.array(z.string().uuid()).optional()
  // If empty, send to all pending
});
var sendReminderResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    remindersSent: z.number().int(),
    recipientIds: z.array(z.string().uuid())
  })
});
var paginationSchema = z.object({
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(100).default(20),
  sortBy: z.string().optional(),
  sortOrder: z.enum(["asc", "desc"]).default("desc")
});
var paginatedResponseSchema = (itemSchema) => z.object({
  items: z.array(itemSchema),
  meta: z.object({
    page: z.number().int(),
    limit: z.number().int(),
    total: z.number().int(),
    totalPages: z.number().int(),
    hasNextPage: z.boolean(),
    hasPrevPage: z.boolean()
  })
});
var apiErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.record(z.unknown()).optional(),
  timestamp: z.string().datetime(),
  path: z.string().optional(),
  requestId: z.string().optional()
});
var apiSuccessSchema = (dataSchema) => z.object({
  success: z.literal(true),
  data: dataSchema,
  meta: z.record(z.unknown()).optional()
});
var apiErrorResponseSchema = z.object({
  success: z.literal(false),
  error: apiErrorSchema
});
var webhookEventTypeSchema = z.enum([
  "send.completed",
  "document.signed",
  "document.viewed",
  "document.expired",
  "form.submitted",
  "voice.completed",
  "voice.consent_captured",
  "contact.created",
  "contact.updated",
  "consent.captured",
  "consent.revoked",
  "message.received",
  "message.delivered",
  "message.failed"
]);
var webhookEventSchema = z.object({
  id: z.string().uuid(),
  type: webhookEventTypeSchema,
  tenantId: z.string().uuid(),
  timestamp: z.string().datetime(),
  data: z.record(z.unknown())
});
var webhookSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  url: z.string().url(),
  events: z.array(webhookEventTypeSchema),
  secret: z.string(),
  isActive: z.boolean().default(true),
  lastTriggeredAt: z.date().optional(),
  failureCount: z.number().int().default(0),
  createdAt: z.date(),
  updatedAt: z.date()
});
var createWebhookSchema = z.object({
  url: z.string().url(),
  events: z.array(webhookEventTypeSchema).min(1)
});
var auditActionSchema = z.enum([
  "create",
  "update",
  "delete",
  "send",
  "sign",
  "view",
  "login",
  "logout",
  "export",
  "import"
]);
var auditLogSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  userId: z.string().uuid().optional(),
  action: auditActionSchema,
  entityType: z.string(),
  entityId: z.string(),
  changes: z.record(z.unknown()).optional(),
  ipAddress: z.string().optional(),
  userAgent: z.string().optional(),
  createdAt: z.date()
});
var optOutChannelSchema = z.enum(["sms", "email", "voice"]);
var optOutActionSchema = z.enum(["opt_out", "opt_in"]);
var optOutSourceSchema = z.enum([
  "keyword",
  "link",
  "manual",
  "form",
  "import",
  "api",
  "voice",
  "confirmation_sent",
  // double opt-in: confirmation message dispatched
  "confirmation_expired",
  // double opt-in: token expired without action
  "confirmation_link",
  // double opt-in: contact clicked confirmation link
  "auto_jurisdiction_rule"
  // informational consent auto-applied by PRE rule
]);
var SMS_OPT_OUT_KEYWORDS = ["STOP", "UNSUBSCRIBE", "CANCEL", "END", "QUIT"];
var SMS_OPT_IN_KEYWORDS = ["START", "SUBSCRIBE", "YES"];
var optOutEventSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  contactId: z.string().uuid(),
  channel: optOutChannelSchema,
  action: optOutActionSchema,
  source: optOutSourceSchema,
  metadata: z.object({
    keyword: z.string().optional(),
    ipAddress: z.string().optional(),
    userAgent: z.string().optional(),
    userId: z.string().uuid().optional(),
    // For manual changes
    reason: z.string().optional(),
    evidenceUrl: z.string().optional(),
    // For manual opt-in with uploaded proof
    evidenceFileName: z.string().optional(),
    writtenNote: z.string().optional(),
    formId: z.string().optional(),
    // For form-sourced events
    messageId: z.string().optional()
    // For link-sourced events
  }).optional(),
  createdAt: z.date()
});
var unsubscribeTokenSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  contactId: z.string().uuid(),
  token: z.string(),
  messageId: z.string().optional(),
  usedAt: z.date().optional(),
  expiresAt: z.date(),
  createdAt: z.date()
});
var sinchInboundSmsSchema = z.object({
  type: z.literal("mo_text").optional(),
  id: z.string().optional(),
  from: z.string(),
  // Sender phone number
  to: z.string(),
  // Recipient phone number (our number)
  body: z.string(),
  // SMS content
  received_at: z.string().optional(),
  operator_id: z.string().optional(),
  client_reference: z.string().optional()
});
var clicksendInboundSmsMessageSchema = z.object({
  body: z.string(),
  from: z.string(),
  to: z.string(),
  message_id: z.string().optional(),
  date: z.number().optional(),
  list_id: z.number().optional()
});
var clicksendInboundSmsSchema = z.object({
  messages: z.array(clicksendInboundSmsMessageSchema)
});
var manualOptChangeRequestSchema = z.object({
  channel: optOutChannelSchema,
  optIn: z.union([z.boolean(), z.string().transform((v) => v === "true")]),
  // supports JSON and form-data strings
  reason: z.string().optional(),
  writtenNote: z.string().optional(),
  evidenceFileName: z.string().optional()
});
var optOutHistoryQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
  channel: optOutChannelSchema.optional(),
  action: optOutActionSchema.optional()
});
var optOutHistoryResponseSchema = z.object({
  events: z.array(optOutEventSchema),
  pagination: z.object({
    page: z.number(),
    limit: z.number(),
    totalItems: z.number(),
    totalPages: z.number(),
    hasMore: z.boolean()
  })
});
var adminOptOutEventsQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(50),
  channel: optOutChannelSchema.optional(),
  action: optOutActionSchema.optional(),
  source: optOutSourceSchema.optional(),
  contactId: z.string().uuid().optional(),
  startDate: z.coerce.date().optional(),
  endDate: z.coerce.date().optional()
});
var unsubscribeInfoSchema = z.object({
  contactEmail: z.string().optional(),
  contactFirstName: z.string().optional(),
  tenantName: z.string(),
  alreadyUnsubscribed: z.boolean()
});
var OPT_OUT_CONFIRMATION_SMS = "You have been unsubscribed. Reply START to re-subscribe.";
var OPT_IN_CONFIRMATION_SMS = (tenantName) => `You have been re-subscribed to messages from ${tenantName}.`;
var OPT_IN_CONFIRMATION_SMS_GENERIC = "You have been re-subscribed. Reply STOP to opt out.";
var tokenStatusSchema = z.enum([
  "valid",
  "expired",
  "already_completed",
  "not_found",
  "invalid"
]);
var tokenValidationResponseSchema = z.object({
  status: tokenStatusSchema,
  message: z.string().optional(),
  expiresAt: z.date().optional()
});
var publicDocumentSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  fileUrl: z.string().url(),
  pageCount: z.number().int().positive().optional(),
  signatureFields: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      required: z.boolean(),
      position: z.object({
        page: z.number().int().positive(),
        x: z.number().min(0).max(1),
        y: z.number().min(0).max(1),
        width: z.number().min(0).max(1),
        height: z.number().min(0).max(1)
      }).optional(),
      fieldType: z.string().optional(),
      signerId: z.string().optional(),
      placeholder: z.string().optional(),
      helpText: z.string().optional(),
      defaultValue: z.union([z.string(), z.boolean()]).optional(),
      options: z.array(z.object({ label: z.string(), value: z.string() })).optional(),
      validation: z.object({
        minLength: z.number().int().nonnegative().optional(),
        maxLength: z.number().int().positive().optional(),
        pattern: z.string().optional(),
        customMessage: z.string().optional()
      }).optional()
    })
  ),
  senderName: z.string().optional(),
  message: z.string().optional(),
  expiresAt: z.date().optional()
});
var publicFormSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().optional(),
  schema: formSchemaDefinition,
  settings: formSettingsSchema,
  branding: z.object({
    logoUrl: z.string().url().optional(),
    primaryColor: z.string().optional(),
    companyName: z.string().optional()
  }).optional()
});
var publicSignDocumentSchema = z.object({
  signatureId: z.string().uuid(),
  signatureData: signatureDataSchema,
  signedFields: z.array(
    z.object({
      fieldId: z.string(),
      signatureData: signatureDataSchema
    })
  )
});
var declineDocumentSchema = z.object({
  signatureId: z.string().uuid(),
  reason: z.string().min(1).max(500)
});
var publicFormSubmissionSchema = z.object({
  formId: z.string().uuid(),
  data: z.record(z.unknown()),
  token: z.string().optional()
  // For private form links
});
var formSubmissionResponseSchema = z.object({
  success: z.boolean(),
  submissionId: z.string().uuid().optional(),
  message: z.string(),
  redirectUrl: z.string().url().optional()
});
var signatureCompletionResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  downloadUrl: z.string().url().optional()
});
var publicFileUploadResponseSchema = z.object({
  success: z.boolean(),
  fileUrl: z.string().url(),
  fileName: z.string(),
  fileSize: z.number().int()
});
var customFieldTypeSchema = z.enum([
  "text",
  "textarea",
  "dropdown",
  "radio",
  "checkboxes",
  "date",
  "image",
  "section_header",
  "spacer"
]);
var LAYOUT_FIELD_TYPES = ["section_header", "spacer"];
function isLayoutFieldType(fieldType) {
  return LAYOUT_FIELD_TYPES.includes(fieldType);
}
var customFieldDefinitionSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string().min(1).max(100),
  fieldKey: z.string().min(1).max(50).regex(/^[a-z][a-z0-9_]*$/, {
    message: "Field key must start with a letter and contain only lowercase letters, numbers, and underscores"
  }),
  fieldType: customFieldTypeSchema,
  isRequired: z.boolean().default(false),
  isVisible: z.boolean().default(true),
  sortOrder: z.number().int().default(0),
  options: z.array(z.string()).nullable(),
  placeholder: z.string().max(255).nullable(),
  helpText: z.string().max(500).nullable(),
  isActive: z.boolean().default(true),
  createdAt: z.date(),
  updatedAt: z.date()
});
var createCustomFieldSchema = z.object({
  name: z.string().min(1, "Name is required").max(100, "Name is too long"),
  fieldKey: z.string().min(1, "Field key is required").max(50).regex(/^[a-z][a-z0-9_]*$/, {
    message: "Field key must start with a letter and contain only lowercase letters, numbers, and underscores"
  }),
  fieldType: customFieldTypeSchema,
  isRequired: z.boolean().default(false),
  isVisible: z.boolean().default(true),
  options: z.array(z.string().min(1)).optional(),
  placeholder: z.string().max(255).optional(),
  helpText: z.string().max(500).optional()
}).refine(
  (data) => {
    if (["dropdown", "radio", "checkboxes"].includes(data.fieldType)) {
      return data.options && data.options.length > 0;
    }
    return true;
  },
  {
    message: "Options are required for dropdown, radio, and checkboxes field types",
    path: ["options"]
  }
);
var updateCustomFieldSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  fieldType: customFieldTypeSchema.optional(),
  isRequired: z.boolean().optional(),
  isVisible: z.boolean().optional(),
  options: z.array(z.string().min(1)).nullable().optional(),
  placeholder: z.string().max(255).nullable().optional(),
  helpText: z.string().max(500).nullable().optional()
}).refine(
  (data) => {
    if (data.fieldType && ["dropdown", "radio", "checkboxes"].includes(data.fieldType)) {
      return data.options && data.options.length > 0;
    }
    return true;
  },
  {
    message: "Options are required for dropdown, radio, and checkboxes field types",
    path: ["options"]
  }
);
var reorderCustomFieldsSchema = z.object({
  fieldIds: z.array(z.string().uuid()).min(1)
});
var customFieldValueSchema = z.union([
  z.string(),
  z.array(z.string()),
  z.null()
]);
var customFieldValuesSchema = z.record(z.string(), customFieldValueSchema);
var customFieldDefinitionListResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(customFieldDefinitionSchema.omit({ tenantId: true }))
});
var customFieldFilterSchema = z.object({
  isActive: z.boolean().optional(),
  isVisible: z.boolean().optional(),
  fieldType: customFieldTypeSchema.optional()
});

// src/group.types.ts
var viewModeSchema = z.enum(["table", "grid", "kanban"]);
var contactGroupSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string().min(1).max(100),
  description: z.string().max(500).nullable().optional(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  icon: z.string().nullable().optional(),
  isActive: z.boolean().default(true),
  sortOrder: z.number().int().default(0),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createContactGroupSchema = z.object({
  name: z.string().min(1).max(100),
  industry: z.string().max(100).optional(),
  description: z.string().max(500).optional(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default("#3b82f6"),
  icon: z.string().optional(),
  stages: z.array(z.object({
    name: z.string().min(1).max(100),
    description: z.string().max(255).optional(),
    exitCriteria: z.array(z.string().min(1).max(255)).max(20).optional(),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    isDefault: z.boolean().optional(),
    isFinal: z.boolean().optional()
  })).optional()
});
var updateContactGroupSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().max(500).nullable().optional(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
  icon: z.string().nullable().optional()
});
var contactGroupStageSchema = z.object({
  id: z.string().uuid(),
  groupId: z.string().uuid(),
  name: z.string().min(1).max(100),
  description: z.string().max(255).nullable().optional(),
  exitCriteria: z.array(z.string()).nullable().optional(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  sortOrder: z.number().int().default(0),
  isDefault: z.boolean().default(false),
  isFinal: z.boolean().default(false),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createContactGroupStageSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(255).optional(),
  exitCriteria: z.array(z.string().min(1).max(255)).max(20).optional(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default("#64748b"),
  isDefault: z.boolean().optional(),
  isFinal: z.boolean().optional()
});
var updateContactGroupStageSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().max(255).nullable().optional(),
  exitCriteria: z.array(z.string().min(1).max(255)).max(20).nullable().optional(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
  isDefault: z.boolean().optional(),
  isFinal: z.boolean().optional()
});
var contactGroupMembershipSchema = z.object({
  id: z.string().uuid(),
  groupId: z.string().uuid(),
  stageId: z.string().uuid(),
  contactId: z.string().uuid(),
  addedAt: z.coerce.date(),
  movedAt: z.coerce.date(),
  notes: z.string().max(1e3).nullable().optional(),
  sortOrder: z.number().int().default(0)
});
var moveContactSchema = z.object({
  toStageId: z.string().uuid(),
  sortOrder: z.number().int().optional(),
  notes: z.string().max(1e3).nullable().optional()
});
var kanbanContactSchema = z.object({
  membershipId: z.string().uuid(),
  customFields: customFieldValuesSchema.optional(),
  contactId: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  company: z.string().nullable().optional(),
  tags: z.array(z.object({
    id: z.string().uuid(),
    name: z.string(),
    color: z.string()
  })).optional(),
  addedAt: z.coerce.date(),
  movedAt: z.coerce.date(),
  daysInStage: z.number().int(),
  notes: z.string().nullable().optional(),
  sortOrder: z.number().int()
});
var kanbanStageSchema = contactGroupStageSchema.extend({
  contactCount: z.number().int(),
  contacts: z.array(kanbanContactSchema)
});
var kanbanGroupSchema = contactGroupSchema.extend({
  stages: z.array(kanbanStageSchema),
  totalContacts: z.number().int()
});
var groupListItemSchema = contactGroupSchema.extend({
  stageCount: z.number().int(),
  contactCount: z.number().int(),
  stages: z.array(z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable().optional(),
    exitCriteria: z.array(z.string()).nullable().optional(),
    color: z.string(),
    isDefault: z.boolean(),
    isFinal: z.boolean()
  }))
});
var contactGroupInfoSchema = z.object({
  membershipId: z.string().uuid(),
  group: z.object({
    id: z.string().uuid(),
    name: z.string(),
    color: z.string(),
    icon: z.string().nullable().optional()
  }),
  stage: z.object({
    id: z.string().uuid(),
    name: z.string(),
    color: z.string(),
    description: z.string().nullable().optional(),
    isDefault: z.boolean(),
    isFinal: z.boolean()
  }),
  addedAt: z.coerce.date(),
  movedAt: z.coerce.date(),
  daysInStage: z.number().int(),
  notes: z.string().nullable().optional()
});

// src/contact-import.types.ts
var CONTACT_IMPORT_REQUIRED_SUFFIX = " (Required)";
var CONTACT_IMPORT_CUSTOM_PREFIX = "custom_";
function normalizeImportHeader(header) {
  return header.trim().toLowerCase();
}
function stripImportHeaderHint(header) {
  return header.replace(/\s*\(\s*required\s*\)\s*$/i, "").replace(/\s*\*\s*$/, "").trim();
}
function isImportableCustomField(def) {
  return !isLayoutFieldType(def.fieldType);
}
function customFieldTarget(def) {
  return `${CONTACT_IMPORT_CUSTOM_PREFIX}${def.fieldKey}`;
}
function customFieldHeaderAliases(def) {
  const name = def.name.trim();
  const aliases = [
    def.fieldKey,
    customFieldTarget(def),
    name,
    name.replace(/\s+/g, "_"),
    name.replace(/\s+/g, "")
  ].map(normalizeImportHeader);
  return Array.from(new Set(aliases.filter(Boolean)));
}
function importTemplateHeaderForCustomField(def, takenNormalized) {
  const name = def.name.trim();
  if (!def.isRequired) return name;
  const hinted = `${name}${CONTACT_IMPORT_REQUIRED_SUFFIX}`;
  return takenNormalized.has(normalizeImportHeader(hinted)) ? name : hinted;
}
var CONTACT_IMPORT_STANDARD_ALIASES = {
  firstname: "firstName",
  first_name: "firstName",
  "first name": "firstName",
  lastname: "lastName",
  last_name: "lastName",
  "last name": "lastName",
  email: "email",
  email_address: "email",
  "email address": "email",
  phone: "phone",
  mobile: "phone",
  phone_number: "phone",
  "phone number": "phone",
  company: "company",
  organization: "company",
  business: "company",
  // Address fields
  address: "address",
  street: "address",
  street_address: "address",
  "street address": "address",
  address1: "address",
  city: "city",
  state: "state",
  province: "state",
  "state/province": "state",
  state_province: "state",
  zipcode: "zipCode",
  zip_code: "zipCode",
  "zip code": "zipCode",
  zip: "zipCode",
  postal_code: "zipCode",
  "postal code": "zipCode",
  postalcode: "zipCode",
  country: "country"
};
var CONTACT_IMPORT_OPTIN_ALIASES = {
  smsoptin: "smsOptIn",
  sms_opt_in: "smsOptIn",
  "sms opt-in": "smsOptIn",
  emailoptin: "emailOptIn",
  email_opt_in: "emailOptIn",
  "email opt-in": "emailOptIn"
};
function resolveContactImportHeaders(headers, defs, standardAliases = CONTACT_IMPORT_STANDARD_ALIASES) {
  const importable = defs.filter(isImportableCustomField);
  const aliasToFieldKey = /* @__PURE__ */ new Map();
  for (const def of importable) {
    for (const alias of customFieldHeaderAliases(def)) {
      if (!aliasToFieldKey.has(alias)) aliasToFieldKey.set(alias, def.fieldKey);
    }
  }
  const headerTargets = headers.map(() => null);
  const customFieldHeaders = /* @__PURE__ */ new Map();
  const unresolved = [];
  headers.forEach((header, index) => {
    const normalized = normalizeImportHeader(header);
    const standard = standardAliases[normalized];
    if (standard) {
      headerTargets[index] = standard;
      return;
    }
    const fieldKey = aliasToFieldKey.get(normalized);
    if (fieldKey && !customFieldHeaders.has(fieldKey)) {
      customFieldHeaders.set(fieldKey, header);
      headerTargets[index] = `${CONTACT_IMPORT_CUSTOM_PREFIX}${fieldKey}`;
      return;
    }
    unresolved.push(index);
  });
  for (const index of unresolved) {
    const header = headers[index];
    if (header === void 0) continue;
    const stripped = normalizeImportHeader(stripImportHeaderHint(header));
    if (!stripped) continue;
    const standard = standardAliases[stripped];
    if (standard) {
      headerTargets[index] = standard;
      continue;
    }
    const fieldKey = aliasToFieldKey.get(stripped);
    if (fieldKey && !customFieldHeaders.has(fieldKey)) {
      customFieldHeaders.set(fieldKey, header);
      headerTargets[index] = `${CONTACT_IMPORT_CUSTOM_PREFIX}${fieldKey}`;
    }
  }
  return { headerTargets, customFieldHeaders };
}
function findUnmappedRequiredCustomFields(mappedTargets, defs) {
  const mapped = new Set(mappedTargets.filter((t) => !!t));
  return defs.filter(
    (def) => def.isRequired && isImportableCustomField(def) && !mapped.has(customFieldTarget(def))
  );
}
var unifiedInboxChannelSchema = z.enum(["email", "sms", "voice", "form"]);
var unifiedInboxFilterSchema = z.enum([
  "all",
  "campaigns",
  "documents",
  "forms",
  "sms",
  "email",
  "voice",
  // Website-widget conversations, for a tenant that embeds a widget on its own
  // site. Its own filter rather than folded into `all` only, because a live chat
  // is the one inbox row where somebody may be waiting on a reply right now.
  "chat",
  "opted_out",
  "opted_in"
]);
var unifiedInboxItemTypeSchema = z.enum(["campaign", "conversation", "form_submission", "form", "signature_request", "consent", "chat"]);
var unifiedInboxContactSchema = z.object({
  id: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string().optional(),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  company: z.string().optional()
});
var formResponseFieldSchema = z.object({
  fieldId: z.string(),
  label: z.string(),
  type: z.string(),
  value: z.union([z.string(), z.array(z.string()), z.boolean(), z.null()])
});
var unifiedInboxMessageSchema = z.object({
  id: z.string().uuid(),
  direction: z.enum(["inbound", "outbound"]),
  channel: channelSchema.optional(),
  content: z.string(),
  subject: z.string().optional(),
  // For email
  htmlContent: z.string().optional(),
  // For email
  status: messageStatusSchema,
  sentAt: z.string().optional(),
  deliveredAt: z.string().optional(),
  readAt: z.string().optional(),
  createdAt: z.string().optional(),
  failureReason: z.string().optional(),
  // System-generated notice (e.g. shared-number ambiguity warning).
  // Renders as a banner, not a contact reply bubble.
  isSystemNotice: z.boolean().optional(),
  // Inbound SMS: how the reply was matched to this tenant. 'ledger_recent' means
  // it arrived on a shared number and was attributed by recency.
  routingMethod: z.string().optional()
});
var voiceScriptReferenceSchema = z.object({
  id: z.string().uuid(),
  name: z.string()
});
var preSummarySchema = z.object({
  blockedCount: z.number().int().min(0),
  conditionalCount: z.number().int().min(0),
  allowedCount: z.number().int().min(0),
  // Up to 3 REMED_* codes seen most often across blocked recipients.
  topBlockCodes: z.array(z.string()).default([]),
  // Up to 3 COND_* codes seen most often across conditioned recipients.
  topConditionCodes: z.array(z.string()).default([]),
  // Earliest UPCOMING calling-hours-deferred send across the campaign's
  // recipients (absolute ISO instant, future-only). Present only when at least
  // one recipient is held for calling hours (COND_SCHEDULE_WITHIN_HOURS) and
  // hasn't sent yet. Powers the inbox's "Scheduled · sends in X" treatment.
  nextScheduledSendAt: z.string().optional(),
  // How many recipients are currently held for a future calling-hours send.
  scheduledCount: z.number().int().min(0).optional()
});
var unifiedInboxItemSchema = z.object({
  id: z.string().min(1),
  // Composite ID format: type:uuid (e.g. campaign:abc-123)
  type: unifiedInboxItemTypeSchema,
  // Display fields
  name: z.string(),
  // Contact name or campaign name
  subject: z.string(),
  // Subject line or campaign name/preview
  description: z.string().optional(),
  // Message preview or status description
  // Type classification (optional for non-campaign types like consent)
  sendType: sendTypeSchema.optional(),
  deliveryMethod: deliveryMethodSchema.optional(),
  // Status
  status: activityStatusSchema,
  // Campaigns: the campaign's own status (status above is what's displayed,
  // e.g. a document reads awaiting_signature whatever stage its send is at)
  campaignStatus: z.string().optional(),
  // Documents: someone can still sign (a decline elsewhere shows the send as
  // cancelled, but the other signers' links still work)
  hasOpenSignatures: z.boolean().optional(),
  isRead: z.boolean().default(true),
  isArchived: z.boolean().default(false),
  // Timestamps
  createdAt: z.string(),
  updatedAt: z.string(),
  lastActivityAt: z.string(),
  scheduledAt: z.string().optional(),
  timezone: z.string().optional(),
  // Counts (optional based on type)
  unreadCount: z.number().int().min(0).optional(),
  recipientCount: z.number().int().min(0).optional(),
  formSubmissionCount: z.number().int().min(0).optional(),
  // Stats (for campaigns)
  stats: activityStatsSchema.optional(),
  // Policy Rule Engine outcome summary (campaigns only).
  // Drives the inbox row's Blocked / Conditional badge and the detail banner.
  preSummary: preSummarySchema.optional(),
  // Contact info
  contact: unifiedInboxContactSchema.optional(),
  // Original entity references
  campaignId: z.string().uuid().optional(),
  conversationId: z.string().uuid().optional(),
  contactId: z.string().uuid().optional(),
  documentId: z.string().uuid().optional(),
  formId: z.string().uuid().optional(),
  signatureRequestId: z.string().uuid().optional(),
  chatSessionId: z.string().uuid().optional(),
  // Tags
  tags: z.array(activityTagSchema).default([])
});
var signatureDeliveryStatusSchema = z.enum(["queued", "sent", "delivered", "opened", "failed", "not_sent"]);
var signatureDeliveryChannelSchema = z.object({
  status: signatureDeliveryStatusSchema,
  address: z.string().optional(),
  // Plain-language reason when failed / not_sent, and what the sender can do
  error: z.string().optional(),
  fix: z.string().optional(),
  // Raw provider or rule text, for the tooltip
  detail: z.string().optional(),
  at: z.string().optional()
});
var unifiedInboxItemDetailSchema = unifiedInboxItemSchema.extend({
  // Form-specific fields
  formName: z.string().optional(),
  submittedAt: z.string().optional(),
  responses: z.array(formResponseFieldSchema).optional(),
  consentCaptured: z.boolean().optional(),
  signature: z.string().optional(),
  // Base64 or URL
  signedAt: z.string().optional(),
  // Document-specific fields
  documentName: z.string().optional(),
  documentUrl: z.string().optional(),
  // URL to view original document
  signedDocumentUrl: z.string().optional(),
  // URL to download signed document with signatures
  pageCount: z.number().int().optional(),
  sentAt: z.string().optional(),
  viewedAt: z.string().optional(),
  expiresAt: z.string().optional(),
  reminderCount: z.number().int().optional(),
  signatureData: z.object({
    type: z.enum(["draw", "type", "upload"]),
    image: z.string()
  }).optional(),
  recipientEmail: z.string().optional(),
  recipientPhone: z.string().optional(),
  // Recipients array for multi-recipient signature requests (campaigns)
  signatureRecipients: z.array(z.object({
    id: z.string().uuid(),
    contactId: z.string().uuid(),
    contactName: z.string(),
    contactEmail: z.string().optional(),
    contactPhone: z.string().optional(),
    signatureStatus: z.enum(["pending", "sent", "viewed", "signed", "declined", "failed", "expired"]),
    sentAt: z.string().optional(),
    viewedAt: z.string().optional(),
    signedAt: z.string().optional(),
    signedPdfUrl: z.string().optional(),
    remindersSent: z.number().optional(),
    lastReminderAt: z.string().optional(),
    signingOrder: z.number().int().optional(),
    // Name of the signer this one is waiting on (multi-signer, not yet their turn)
    waitingOn: z.string().optional(),
    // What actually happened to the invite on each channel it was meant to go out on
    delivery: z.object({
      email: signatureDeliveryChannelSchema.optional(),
      sms: signatureDeliveryChannelSchema.optional()
    }).optional(),
    // Set when no channel reached the recipient and nothing more is coming
    deliveryProblem: z.string().optional(),
    // A resend for just this person would re-invite them
    resendable: z.boolean().optional(),
    // They have their signing link and haven't signed: a reminder can go
    remindable: z.boolean().optional()
  })).optional(),
  // Message/Conversation-specific fields
  messages: z.array(unifiedInboxMessageSchema).optional(),
  canReply: z.boolean().optional(),
  // True for conversations, false for campaigns
  conversationStatus: conversationStatusSchema.optional(),
  conversationPriority: prioritySchema.optional(),
  isOptedIn: z.boolean().optional(),
  // Contact's opt-in status for the channel
  // Voice-specific fields
  callType: z.enum(["outbound_ai", "inbound_hotline", "three_way_merge"]).optional(),
  duration: z.number().int().optional(),
  // in seconds
  calledAt: z.string().optional(),
  recordingUrl: z.string().optional(),
  transcript: z.string().optional(),
  voiceScript: voiceScriptReferenceSchema.optional(),
  // Timeline events
  timeline: z.array(timelineEventSchema).optional(),
  // Consent event-specific fields
  consentEventId: z.string().uuid().optional(),
  consentChannel: z.enum(["sms", "email", "voice"]).optional(),
  consentAction: z.enum(["opt_in", "opt_out", "confirmation_sent", "confirmation_expired"]).optional(),
  consentSource: z.string().optional(),
  // 'keyword' | 'link' | 'manual' | 'form' | 'import' | 'confirmation_link'
  consentMetadata: z.record(z.unknown()).optional(),
  // Raw metadata JSON
  consentEvidenceUrl: z.string().optional(),
  // URL to evidence file
  consentEvidenceFileName: z.string().optional(),
  consentWrittenNote: z.string().optional(),
  consentPendingExpiry: z.string().optional()
  // ISO date when confirmation token expires
});
var unifiedInboxCountsSchema = z.object({
  all: z.number().int().min(0),
  campaigns: z.number().int().min(0),
  documents: z.number().int().min(0),
  forms: z.number().int().min(0),
  sms: z.number().int().min(0),
  email: z.number().int().min(0),
  voice: z.number().int().min(0),
  /**
   * Open website-widget conversations. Optional because a tenant that owns no
   * widget never gets one, and a hard-required 0 would make every older client
   * fail validation on an inbox that has nothing to do with chat.
   */
  chat: z.number().int().min(0).optional(),
  optedOut: z.number().int().min(0),
  optedIn: z.number().int().min(0),
  /** @deprecated Legacy mirror of `optedOut`; `tallyInboxCounts` keeps them equal. */
  recentlyOptedOut: z.number().int().min(0).optional()
});
var TAB_COUNT_KEY = {
  all: "all",
  campaigns: "campaigns",
  documents: "documents",
  forms: "forms",
  sms: "sms",
  email: "email",
  voice: "voice",
  chat: "chat",
  opted_out: "optedOut",
  opted_in: "optedIn"
};
var unifiedInboxFiltersSchema = z.object({
  channel: unifiedInboxFilterSchema.default("all"),
  search: z.string().default(""),
  dateRange: z.enum(["all", "today", "7days", "30days"]).default("all"),
  status: z.array(z.string()).default([]),
  isRead: z.boolean().nullable().default(null)
});
var unifiedInboxQueryParamsSchema = z.object({
  // Pagination
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
  // Search
  search: z.string().optional(),
  // Filters
  channel: unifiedInboxFilterSchema.optional(),
  status: z.string().optional(),
  // Comma-separated
  isRead: z.boolean().optional(),
  // Date filters
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  dateRange: z.enum(["all", "today", "7days", "30days"]).optional(),
  // Sorting
  sortBy: z.enum(["lastActivityAt", "createdAt", "name"]).default("lastActivityAt"),
  sortOrder: z.enum(["asc", "desc"]).default("desc")
});
var unifiedInboxPaginationSchema = z.object({
  page: z.number().int(),
  limit: z.number().int(),
  total: z.number().int(),
  totalPages: z.number().int(),
  hasMore: z.boolean()
});
var unifiedInboxListResponseSchema = z.object({
  data: z.array(unifiedInboxItemSchema),
  pagination: unifiedInboxPaginationSchema,
  /**
   * @deprecated GET /api/inbox no longer returns counts. It used to carry a
   * second, differently-computed counts block that the client discarded; that
   * was one of the three drifting lineages behind #2239/#2240. Read
   * GET /api/inbox/counts instead.
   */
  counts: unifiedInboxCountsSchema.optional()
});
var unifiedInboxDetailResponseSchema = z.object({
  success: z.boolean(),
  data: unifiedInboxItemDetailSchema
});
var bulkActionRequestSchema = z.object({
  ids: z.array(z.string().uuid()).min(1)
});
var bulkActionResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    processedCount: z.number().int(),
    failedCount: z.number().int(),
    failedIds: z.array(z.string().uuid()).optional()
  })
});
var exportRequestSchema = z.object({
  ids: z.array(z.string().uuid()).min(1),
  format: z.enum(["csv", "json", "pdf"]).default("csv")
});
var exportResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    downloadUrl: z.string().url(),
    expiresAt: z.string(),
    recordCount: z.number().int()
  })
});
var auditEntityTypeSchema = z.enum([
  "contact",
  "campaign",
  "document",
  "form",
  "voice_script"
]);
var changeActionSchema = z.enum([
  "create",
  "update",
  "delete",
  "consent_change",
  "opt_in",
  "opt_out",
  "send",
  "sign",
  "view",
  // Emitted by the Policy Rule Engine when a campaign send produces blocked
  // or conditioned recipients — drives the in-app bell notification.
  "policy_review"
]);
var changeDetailSchema = z.object({
  field: z.string(),
  fieldLabel: z.string(),
  from: z.unknown().optional(),
  to: z.unknown().optional()
});
var changeUserSchema = z.object({
  id: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string()
});
var changeEntrySchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  userId: z.string().uuid().optional(),
  user: changeUserSchema.optional(),
  action: changeActionSchema,
  entityType: auditEntityTypeSchema,
  entityId: z.string().uuid(),
  entityName: z.string().optional(),
  changes: z.array(changeDetailSchema),
  ipAddress: z.string().optional(),
  userAgent: z.string().optional(),
  createdAt: z.coerce.date(),
  isRead: z.boolean().default(false)
});
var changeListResponseSchema = z.object({
  changes: z.array(changeEntrySchema),
  pagination: z.object({
    page: z.number(),
    limit: z.number(),
    total: z.number(),
    totalPages: z.number()
  }),
  unreadCount: z.number()
});
var changesQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
  entityType: auditEntityTypeSchema.optional(),
  entityId: z.string().uuid().optional(),
  action: changeActionSchema.optional(),
  unreadOnly: z.coerce.boolean().optional(),
  startDate: z.coerce.date().optional(),
  endDate: z.coerce.date().optional()
});
var markAsReadRequestSchema = z.object({
  ids: z.union([z.array(z.string().uuid()), z.literal("all")]),
  entityType: auditEntityTypeSchema.optional(),
  entityId: z.string().uuid().optional()
});
var CONTACT_FIELD_LABELS = {
  firstName: "First Name",
  lastName: "Last Name",
  email: "Email Address",
  phone: "Phone Number",
  company: "Company",
  address: "Address",
  city: "City",
  state: "State",
  zipCode: "ZIP Code",
  country: "Country",
  timezone: "Timezone",
  customFields: "Custom Fields",
  smsOptIn: "SMS Consent",
  smsOptInAt: "SMS Consent Date",
  smsOptInSource: "SMS Consent Source",
  smsOptOutAt: "SMS Opt-Out Date",
  emailOptIn: "Email Consent",
  emailOptInAt: "Email Consent Date",
  emailOptInSource: "Email Consent Source",
  emailOptOutAt: "Email Opt-Out Date",
  voiceOptIn: "Voice Consent",
  voiceOptInAt: "Voice Consent Date",
  consentLevel: "Consent Level",
  tcpaProofUrl: "TCPA Proof URL",
  isActive: "Active Status"
};
var CAMPAIGN_FIELD_LABELS = {
  name: "Campaign Name",
  sendType: "Send Type",
  deliveryMethod: "Delivery Method",
  messageType: "Message Type",
  status: "Status",
  messageContent: "Message Content",
  smsContent: "SMS Content",
  emailSubject: "Email Subject",
  emailContent: "Email Content",
  scheduledAt: "Scheduled Date",
  isRecurring: "Recurring"
};
var DOCUMENT_FIELD_LABELS = {
  name: "Document Name",
  type: "Document Type",
  status: "Status",
  templateContent: "Template Content",
  expiresAt: "Expiration Date",
  isActive: "Active Status"
};
var FORM_FIELD_LABELS = {
  name: "Form Name",
  description: "Description",
  schema: "Form Schema",
  settings: "Settings",
  isPublished: "Published",
  isActive: "Active Status"
};
var VOICE_SCRIPT_FIELD_LABELS = {
  name: "Script Name",
  description: "Description",
  callType: "Call Type",
  content: "Script Content",
  settings: "Settings",
  isActive: "Active Status"
};
var FIELD_LABELS = {
  contact: CONTACT_FIELD_LABELS,
  campaign: CAMPAIGN_FIELD_LABELS,
  document: DOCUMENT_FIELD_LABELS,
  form: FORM_FIELD_LABELS,
  voice_script: VOICE_SCRIPT_FIELD_LABELS
};
function getFieldLabel(entityType, field) {
  return FIELD_LABELS[entityType]?.[field] || formatFieldName(field);
}
function formatFieldName(field) {
  return field.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase()).trim();
}
var ENTITY_ICONS = {
  contact: "User",
  campaign: "Send",
  document: "FileText",
  form: "ClipboardList",
  voice_script: "Phone"
};
var ACTION_COLORS = {
  create: "green",
  update: "blue",
  delete: "red",
  consent_change: "purple",
  opt_in: "green",
  opt_out: "red",
  send: "blue",
  sign: "green",
  view: "gray",
  policy_review: "amber"
};
var ACTION_LABELS = {
  create: "Created",
  update: "Updated",
  delete: "Deleted",
  consent_change: "Consent Changed",
  opt_in: "Opted In",
  opt_out: "Opted Out",
  send: "Sent",
  sign: "Signed",
  view: "Viewed",
  policy_review: "Policy Review Required"
};
var superAdminRoleSchema = z.enum([
  "SuperAdmin",
  "SupportAdmin",
  "BillingAdmin",
  "ComplianceAdmin",
  "ReadOnlyAdmin"
]);
var ROLE_PERMISSIONS = {
  SuperAdmin: ["*"],
  SupportAdmin: ["support:*", "accounts:read", "billing:read"],
  BillingAdmin: ["billing:*", "accounts:read", "support:read", "plans:*"],
  ComplianceAdmin: ["audit:*", "compliance:*", "accounts:read"],
  ReadOnlyAdmin: ["*:read"]
};
var superAdminUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  role: superAdminRoleSchema,
  avatar: z.string().url().optional().nullable(),
  phone: z.string().optional().nullable(),
  isActive: z.boolean(),
  failedLoginAttempts: z.number().int().default(0),
  lockedUntil: z.coerce.date().optional().nullable(),
  lastLoginAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var superAdminAuthUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  role: superAdminRoleSchema
});
var createSuperAdminUserSchema = superAdminUserSchema.omit({
  id: true,
  failedLoginAttempts: true,
  lockedUntil: true,
  lastLoginAt: true,
  createdAt: true,
  updatedAt: true
}).extend({
  password: z.string().min(8)
});
var updateSuperAdminUserSchema = superAdminUserSchema.partial().omit({
  id: true,
  failedLoginAttempts: true,
  lockedUntil: true,
  lastLoginAt: true,
  createdAt: true,
  updatedAt: true
});
var superAdminAuditActionSchema = z.enum([
  "login",
  "logout",
  "create",
  "update",
  "delete",
  "suspend",
  "reactivate",
  "cancel",
  "impersonate",
  "password_reset",
  "toggle_maintenance",
  "clear_cache"
]);
var superAdminEntityTypeSchema = z.enum([
  "tenant",
  "user",
  "plan",
  "template",
  "super_admin_user",
  "feature_flag",
  "platform"
]);
var superAdminAuditLogSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid().optional().nullable(),
  user: superAdminUserSchema.pick({ id: true, email: true, firstName: true, lastName: true }).optional().nullable(),
  action: z.string(),
  entityType: z.string(),
  entityId: z.string().optional().nullable(),
  entityName: z.string().optional().nullable(),
  details: z.record(z.unknown()).optional().nullable(),
  ipAddress: z.string().optional().nullable(),
  userAgent: z.string().optional().nullable(),
  createdAt: z.coerce.date()
});
var platformServiceSchema = z.enum([
  "api-gateway",
  "database",
  "redis",
  "sinch",
  "mailgun",
  "ultravox",
  "stripe"
]);
var healthStatusSchema = z.enum(["healthy", "degraded", "down"]);
var platformHealthCheckSchema = z.object({
  id: z.string().uuid(),
  service: z.string(),
  status: healthStatusSchema,
  latencyMs: z.number().optional().nullable(),
  errorMessage: z.string().optional().nullable(),
  metadata: z.record(z.unknown()).optional().nullable(),
  checkedAt: z.coerce.date()
});
var superAdminLoginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1)
});
var superAdminLoginResponseSchema = z.object({
  user: superAdminAuthUserSchema,
  accessToken: z.string(),
  refreshToken: z.string()
});
var superAdminRefreshRequestSchema = z.object({
  refreshToken: z.string()
});
var superAdminRefreshResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string()
});
var tenantStatusSchema = z.enum(["active", "trial", "suspended", "cancelled"]);
var billingStatusSchema = z.enum([
  "current",
  "past_due",
  "grace_period",
  "suspended",
  "pending_card"
]);
var tenantSourceSchema = z.enum(["signup", "referral", "sales", "import"]);
var templateVisibilitySchema = z.enum(["all", "plans", "tenants", "none"]);
var lifecycleEventTypeSchema = z.enum([
  "created",
  "activated",
  "suspended",
  "reactivated",
  "cancelled",
  "plan_changed",
  "billing_status_changed"
]);
var tenantLifecycleEventSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  event: lifecycleEventTypeSchema,
  previousStatus: z.string().optional().nullable(),
  newStatus: z.string().optional().nullable(),
  reason: z.string().optional().nullable(),
  performedById: z.string().uuid().optional().nullable(),
  performedBy: superAdminUserSchema.pick({ id: true, firstName: true, lastName: true, email: true }).optional().nullable(),
  metadata: z.record(z.unknown()).optional().nullable(),
  createdAt: z.coerce.date()
});
var impersonationSessionSchema = z.object({
  id: z.string().uuid(),
  superAdminUserId: z.string().uuid(),
  superAdminUser: superAdminUserSchema.pick({ id: true, firstName: true, lastName: true, email: true }).optional(),
  tenantId: z.string().uuid(),
  tenant: z.object({
    id: z.string().uuid(),
    name: z.string(),
    slug: z.string()
  }).optional(),
  targetUserId: z.string().uuid(),
  targetUser: z.object({
    id: z.string().uuid(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string()
  }).optional(),
  startedAt: z.coerce.date(),
  endedAt: z.coerce.date().optional().nullable(),
  ipAddress: z.string().optional().nullable(),
  userAgent: z.string().optional().nullable()
});
var accountNoteSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  authorId: z.string().uuid(),
  author: superAdminUserSchema.pick({ id: true, firstName: true, lastName: true, email: true }).optional(),
  content: z.string().min(1),
  isPinned: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createAccountNoteSchema = z.object({
  content: z.string().min(1),
  isPinned: z.boolean().optional()
});
var updateAccountNoteSchema = z.object({
  content: z.string().min(1).optional(),
  isPinned: z.boolean().optional()
});
var accountListFiltersSchema = z.object({
  search: z.string().optional(),
  status: tenantStatusSchema.optional(),
  billingStatus: billingStatusSchema.optional(),
  planId: z.string().uuid().optional(),
  assignedToId: z.string().uuid().optional(),
  source: tenantSourceSchema.optional(),
  dateRange: z.enum(["all", "7days", "30days", "90days"]).optional()
});
var accountSortFieldSchema = z.enum([
  "name",
  "createdAt",
  "status",
  "billingStatus",
  "plan"
]);
var accountListItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  status: tenantStatusSchema,
  billingStatus: billingStatusSchema,
  plan: z.object({
    id: z.string().uuid(),
    name: z.string()
  }),
  userCount: z.number().int(),
  contactCount: z.number().int(),
  assignedTo: superAdminUserSchema.pick({ id: true, firstName: true, lastName: true }).optional().nullable(),
  source: tenantSourceSchema.optional().nullable(),
  industry: z.string().optional().nullable(),
  trialStatus: z.enum(["none", "pending_card", "trialing", "active"]).optional().nullable(),
  trialEndsAt: z.coerce.date().optional().nullable(),
  /** Number of open disputes (status = opened|under_review). Renders as a badge on the AccountsList row when > 0. */
  openDisputeCount: z.number().int().optional().default(0),
  createdAt: z.coerce.date(),
  lastActivityAt: z.coerce.date().optional().nullable(),
  ownerEmail: z.string().email().optional().nullable()
});
var accountDetailSchema = accountListItemSchema.extend({
  helcimCustomerId: z.string().optional().nullable(),
  settings: z.record(z.unknown()),
  suspendedAt: z.coerce.date().optional().nullable(),
  cancelledAt: z.coerce.date().optional().nullable(),
  notes: z.string().optional().nullable(),
  updatedAt: z.coerce.date(),
  // Nested data for tabs
  users: z.array(
    z.object({
      id: z.string().uuid(),
      email: z.string().email(),
      firstName: z.string(),
      lastName: z.string(),
      role: z.string(),
      isActive: z.boolean(),
      lastLoginAt: z.coerce.date().optional().nullable(),
      createdAt: z.coerce.date()
    })
  ).optional(),
  recentActivity: z.array(tenantLifecycleEventSchema).optional(),
  pinnedNotes: z.array(accountNoteSchema).optional()
});
var suspendAccountDtoSchema = z.object({
  reason: z.string().min(1, "Suspension reason is required"),
  notifyUsers: z.boolean().optional()
});
var reactivateAccountDtoSchema = z.object({
  reason: z.string().optional(),
  notifyUsers: z.boolean().optional()
});
var cancelAccountDtoSchema = z.object({
  reason: z.string().min(1, "Cancellation reason is required"),
  effectiveDate: z.coerce.date().optional(),
  feedback: z.string().optional()
});
var createAccountDtoSchema = z.object({
  name: z.string().min(1),
  slug: z.string().min(1).regex(/^[a-z0-9-]+$/, "Slug must be lowercase with hyphens only"),
  planId: z.string().uuid(),
  industry: z.string().optional(),
  source: tenantSourceSchema.optional(),
  notes: z.string().optional(),
  // Initial admin user
  adminEmail: z.string().email(),
  adminFirstName: z.string().min(1),
  adminLastName: z.string().min(1),
  skipWelcomeEmail: z.boolean().optional()
});
var updateAccountDtoSchema = z.object({
  name: z.string().min(1).optional(),
  planId: z.string().uuid().optional(),
  industry: z.string().optional().nullable(),
  assignedToId: z.string().uuid().optional().nullable(),
  notes: z.string().optional().nullable()
});
var startImpersonationDtoSchema = z.object({
  targetUserId: z.string().uuid().optional(),
  reason: z.string().optional()
});
var impersonationResponseSchema = z.object({
  session: impersonationSessionSchema,
  accessToken: z.string(),
  redirectUrl: z.string()
});
var accountUsageStatsSchema = z.object({
  period: z.string(),
  documentsCreated: z.number().int(),
  documentsLimit: z.number().int(),
  smsSent: z.number().int(),
  smsLimit: z.number().int(),
  emailsSent: z.number().int(),
  emailsLimit: z.number().int(),
  formsSubmitted: z.number().int(),
  formsLimit: z.number().int(),
  aiCreditsUsed: z.number().int(),
  aiCreditsLimit: z.number().int()
});
var paginationMetaSchema = z.object({
  page: z.number().int(),
  limit: z.number().int(),
  total: z.number().int(),
  totalPages: z.number().int(),
  sortBy: z.string().optional(),
  sortOrder: z.enum(["asc", "desc"]).default("desc")
});
var paginatedAccountListSchema = z.object({
  data: z.array(accountListItemSchema),
  pagination: paginationMetaSchema
});
var supportRequestStatusSchema = z.enum([
  "open",
  "in_progress",
  "resolved",
  "closed"
]);
var supportPrioritySchema = z.enum(["low", "medium", "high", "urgent"]);
var supportCategorySchema = z.enum([
  "billing",
  "technical",
  "compliance",
  "account",
  "feature_request",
  "other"
]);
var authorTypeSchema = z.enum(["tenant_user", "super_admin"]);
var flaggedContentTypeSchema = z.enum(["message", "document", "form"]);
var flagSeveritySchema = z.enum(["low", "medium", "high"]);
var flagStatusSchema = z.enum([
  "pending",
  "reviewed",
  "dismissed",
  "actioned"
]);
var supportRequestSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  tenant: z.object({
    id: z.string().uuid(),
    name: z.string(),
    slug: z.string()
  }).optional(),
  subject: z.string().min(1).max(500),
  description: z.string().min(1),
  status: supportRequestStatusSchema,
  priority: supportPrioritySchema,
  category: supportCategorySchema,
  assignedToId: z.string().uuid().optional().nullable(),
  assignedTo: superAdminUserSchema.pick({ id: true, firstName: true, lastName: true, email: true }).optional().nullable(),
  createdById: z.string(),
  resolvedAt: z.coerce.date().optional().nullable(),
  resolvedById: z.string().uuid().optional().nullable(),
  resolvedBy: superAdminUserSchema.pick({ id: true, firstName: true, lastName: true }).optional().nullable(),
  metadata: z.record(z.unknown()).optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var supportMessageSchema = z.object({
  id: z.string().uuid(),
  requestId: z.string().uuid(),
  authorType: authorTypeSchema,
  authorId: z.string(),
  authorName: z.string().optional(),
  // Populated from lookup
  content: z.string().min(1),
  attachments: z.array(
    z.object({
      url: z.string(),
      fileName: z.string(),
      fileSize: z.number().optional(),
      mimeType: z.string().optional()
    })
  ).optional().nullable(),
  isInternal: z.boolean(),
  createdAt: z.coerce.date()
});
var aiFlaggedContentSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  tenant: z.object({
    id: z.string().uuid(),
    name: z.string(),
    slug: z.string()
  }).optional(),
  contentType: flaggedContentTypeSchema,
  contentId: z.string(),
  contentPreview: z.string().optional(),
  // Populated preview
  flagReason: z.string(),
  severity: flagSeveritySchema,
  status: flagStatusSchema,
  reviewedById: z.string().uuid().optional().nullable(),
  reviewedBy: superAdminUserSchema.pick({ id: true, firstName: true, lastName: true }).optional().nullable(),
  reviewedAt: z.coerce.date().optional().nullable(),
  notes: z.string().optional().nullable(),
  createdAt: z.coerce.date()
});
var createSupportRequestDtoSchema = z.object({
  tenantId: z.string().uuid(),
  subject: z.string().min(1).max(500),
  description: z.string().min(1),
  priority: supportPrioritySchema.optional(),
  category: supportCategorySchema,
  createdById: z.string()
});
var updateSupportRequestDtoSchema = z.object({
  subject: z.string().min(1).max(500).optional(),
  priority: supportPrioritySchema.optional(),
  category: supportCategorySchema.optional(),
  status: supportRequestStatusSchema.optional()
});
var assignSupportRequestDtoSchema = z.object({
  assignedToId: z.string().uuid().nullable()
});
var resolveSupportRequestDtoSchema = z.object({
  resolution: z.string().min(1).optional()
});
var addSupportMessageDtoSchema = z.object({
  content: z.string().min(1),
  isInternal: z.boolean().optional(),
  attachments: z.array(
    z.object({
      url: z.string(),
      fileName: z.string(),
      fileSize: z.number().optional(),
      mimeType: z.string().optional()
    })
  ).optional()
});
var reviewAiFlagDtoSchema = z.object({
  status: z.enum(["reviewed", "dismissed", "actioned"]),
  notes: z.string().optional()
});
var supportQueueSummarySchema = z.object({
  total: z.number().int(),
  open: z.number().int(),
  inProgress: z.number().int(),
  resolved: z.number().int(),
  closed: z.number().int(),
  urgent: z.number().int(),
  unassigned: z.number().int(),
  avgResolutionTimeHours: z.number().optional(),
  flaggedContentCount: z.number().int()
});
var supportListFiltersSchema = z.object({
  search: z.string().optional(),
  status: supportRequestStatusSchema.optional(),
  priority: supportPrioritySchema.optional(),
  category: supportCategorySchema.optional(),
  assignedToId: z.string().uuid().optional(),
  tenantId: z.string().uuid().optional(),
  dateRange: z.enum(["all", "7days", "30days", "90days"]).optional()
});
var paginatedSupportRequestListSchema = z.object({
  data: z.array(supportRequestSchema),
  pagination: paginationMetaSchema
});
var paginatedAiFlagsListSchema = z.object({
  data: z.array(aiFlaggedContentSchema),
  pagination: paginationMetaSchema
});
var planStatusSchema = z.enum(["active", "inactive", "deprecated"]);
var superAdminPlanSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  monthlyPrice: z.coerce.number(),
  annualPrice: z.coerce.number(),
  smsCreditsPerMonth: z.number().int(),
  emailCreditsPerMonth: z.number().int(),
  aiCreditsPerMonth: z.number().int().default(0),
  // Extended usage limits (from schema.prisma)
  maxContacts: z.number().int().default(100),
  maxUsers: z.number().int().default(1),
  maxSmsPerBroadcast: z.number().int().default(10),
  maxEmailPerBroadcast: z.number().int().default(25),
  maxForms: z.number().int().default(1),
  features: z.string(),
  // JSON string of features
  isActive: z.boolean(),
  /** @deprecated Use helcimPlanId / helcimAnnualPlanId instead */
  stripePriceId: z.string().optional().nullable(),
  helcimPlanId: z.string().optional().nullable(),
  helcimAnnualPlanId: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  displayOrder: z.number().int().default(0),
  tierLevel: z.number().int().default(0),
  highlightLabel: z.string().optional().nullable(),
  badgeColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional().nullable(),
  recommendedForIndividual: z.boolean().default(false),
  recommendedForBusiness: z.boolean().default(false),
  // Trial configuration: 0 = no trial (charge immediately on card capture).
  // Surfaces in OOBE plan picker as "X-day free trial" badge when > 0.
  trialDays: z.number().int().min(0).max(90).default(0),
  // When true, a trial plan still requires card capture before the trial starts.
  // When false (default), trial plans start a no-card trial in the OOBE.
  requireCardForTrial: z.boolean().default(false),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var planWithStatsSchema = superAdminPlanSchema.extend({
  subscriberCount: z.number().int(),
  mrrContribution: z.coerce.number(),
  percentOfTotal: z.number()
});
var createPlanDtoSchema = z.object({
  name: z.string().min(1),
  monthlyPrice: z.coerce.number().min(0),
  annualPrice: z.coerce.number().min(0),
  smsCreditsPerMonth: z.number().int().min(0),
  emailCreditsPerMonth: z.number().int().min(0),
  aiCreditsPerMonth: z.number().int().min(0).optional(),
  maxContacts: z.number().int().min(0).optional(),
  maxUsers: z.number().int().min(0).optional(),
  maxSmsPerBroadcast: z.number().int().min(0).optional(),
  maxEmailPerBroadcast: z.number().int().min(0).optional(),
  maxForms: z.number().int().min(0).optional(),
  features: z.string(),
  isActive: z.boolean().optional(),
  /** @deprecated Use helcimPlanId / helcimAnnualPlanId instead */
  stripePriceId: z.string().optional(),
  helcimPlanId: z.string().optional().nullable(),
  helcimAnnualPlanId: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  displayOrder: z.number().int().optional(),
  tierLevel: z.number().int().min(0).max(3).optional(),
  highlightLabel: z.string().max(50).optional().nullable(),
  badgeColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional().nullable(),
  recommendedForIndividual: z.boolean().optional(),
  recommendedForBusiness: z.boolean().optional(),
  trialDays: z.number().int().min(0).max(90).optional(),
  requireCardForTrial: z.boolean().optional()
});
var updatePlanDtoSchema = createPlanDtoSchema.partial();
var planListFiltersSchema = z.object({
  search: z.string().optional(),
  isActive: z.boolean().optional()
});
var paginatedPlansListSchema = z.object({
  data: z.array(planWithStatsSchema),
  pagination: paginationMetaSchema
});
var revenueMetricsSchema = z.object({
  mrr: z.coerce.number(),
  arr: z.coerce.number(),
  totalRevenue: z.coerce.number(),
  activeSubscriptions: z.number().int(),
  churnRate: z.number(),
  averageRevenuePerAccount: z.coerce.number(),
  growthRate: z.number()
});
var mrrBreakdownSchema = z.object({
  newMrr: z.coerce.number(),
  expansionMrr: z.coerce.number(),
  contractionMrr: z.coerce.number(),
  churnedMrr: z.coerce.number(),
  netNewMrr: z.coerce.number()
});
var revenueTrendDataPointSchema = z.object({
  date: z.string(),
  mrr: z.coerce.number(),
  arr: z.coerce.number(),
  newMrr: z.coerce.number(),
  churnedMrr: z.coerce.number(),
  activeSubscriptions: z.number().int()
});
var invoiceStatusSchema = z.enum(["draft", "paid", "pending", "overdue", "cancelled", "refunded", "disputed"]);
var invoiceLineItemSchema = z.object({
  description: z.string(),
  quantity: z.number(),
  unitPrice: z.coerce.number(),
  total: z.coerce.number(),
  type: z.enum(["subscription", "addon", "overage", "credit"])
});
var invoiceSchema = z.object({
  id: z.string().uuid(),
  helcimInvoiceId: z.string().optional().nullable(),
  invoiceNumber: z.string(),
  tenantId: z.string().uuid(),
  tenantName: z.string().optional(),
  tenant: z.object({
    id: z.string().uuid(),
    name: z.string(),
    slug: z.string()
  }).optional(),
  subtotal: z.coerce.number(),
  tax: z.coerce.number(),
  total: z.coerce.number(),
  amountPaid: z.coerce.number(),
  amountDue: z.coerce.number(),
  currency: z.string().default("USD"),
  status: invoiceStatusSchema,
  dueDate: z.coerce.date(),
  paidAt: z.coerce.date().optional().nullable(),
  periodStart: z.coerce.date(),
  periodEnd: z.coerce.date(),
  lineItems: z.array(invoiceLineItemSchema).optional(),
  notes: z.string().optional().nullable(),
  createdAt: z.coerce.date()
});
var invoiceListFiltersSchema = z.object({
  search: z.string().optional(),
  status: invoiceStatusSchema.optional(),
  tenantId: z.string().uuid().optional(),
  dateRange: z.enum(["all", "7days", "30days", "90days"]).optional()
});
var paginatedInvoicesListSchema = z.object({
  data: z.array(invoiceSchema),
  pagination: paginationMetaSchema
});
var pastDueAccountSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  tenantName: z.string(),
  tenantSlug: z.string(),
  planName: z.string(),
  amountDue: z.coerce.number(),
  daysOverdue: z.number().int(),
  lastPaymentDate: z.coerce.date().optional().nullable(),
  invoiceCount: z.number().int(),
  contactEmail: z.string().email().optional().nullable()
});
var disputeStatusSchema = z.enum([
  // New (Helcim-sourced) lifecycle values from the Dispute model. The
  // BillingDashboard's existing DisputesList component still understands the
  // legacy `needs_response`/`withdrawn` aliases.
  "opened",
  "under_review",
  "won",
  "lost",
  "accepted",
  // Legacy aliases — kept until the BillingDashboard list is migrated.
  "needs_response",
  "withdrawn"
]);
var paymentDisputeSchema = z.object({
  id: z.string(),
  helcimDisputeId: z.string().optional().nullable(),
  tenantId: z.string().uuid(),
  tenant: z.object({
    id: z.string().uuid(),
    name: z.string(),
    slug: z.string()
  }).optional(),
  paymentId: z.string().uuid().optional().nullable(),
  payment: z.object({
    id: z.string().uuid(),
    helcimTransactionId: z.string().optional().nullable(),
    amount: z.coerce.number(),
    currency: z.string()
  }).optional().nullable(),
  amount: z.coerce.number(),
  currency: z.string().default("USD"),
  reason: z.string().nullable(),
  status: disputeStatusSchema,
  evidenceDueBy: z.coerce.date().optional().nullable(),
  openedAt: z.coerce.date().optional().nullable(),
  resolvedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var overageTypeSchema = z.enum(["documents", "sms", "email", "storage", "voice"]);
var usageOverageSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  tenantName: z.string(),
  tenantSlug: z.string(),
  type: overageTypeSchema,
  limit: z.number().int(),
  used: z.number().int(),
  overage: z.number().int(),
  percentOver: z.number(),
  period: z.string(),
  estimatedCharge: z.coerce.number(),
  createdAt: z.coerce.date()
});
var paymentSummarySchema = z.object({
  id: z.string().uuid(),
  tenantName: z.string(),
  amount: z.coerce.number(),
  status: z.string(),
  date: z.coerce.date()
});
var planDistributionSchema = z.object({
  planId: z.string().uuid(),
  planName: z.string(),
  tierLevel: z.number().int(),
  subscriberCount: z.number().int(),
  mrrContribution: z.coerce.number(),
  percentOfTotal: z.number()
});
var billingDashboardSchema = z.object({
  metrics: revenueMetricsSchema,
  mrrBreakdown: mrrBreakdownSchema,
  revenueTrend: z.array(revenueTrendDataPointSchema),
  pastDueCount: z.number().int(),
  pastDueAmount: z.coerce.number(),
  recentPayments: z.array(paymentSummarySchema),
  planDistribution: z.array(planDistributionSchema)
});
var freePlanAccountSchema = z.object({
  tenantId: z.string().uuid(),
  tenantName: z.string(),
  tenantSlug: z.string(),
  contactEmail: z.string().email().optional().nullable(),
  daysOnFreePlan: z.number().int(),
  joinedAt: z.coerce.date(),
  billingStatus: z.string(),
  status: z.string(),
  userCount: z.number().int()
});
var billingCycleSchema = z.enum(["monthly", "annual"]);
var subscriptionInfoSchema = z.object({
  subscriptionId: z.string(),
  helcimSubscriptionId: z.number().nullable(),
  planId: z.string().uuid(),
  planName: z.string(),
  status: z.string(),
  billingCycle: billingCycleSchema,
  amount: z.coerce.number(),
  currentPeriodStart: z.coerce.date().nullable(),
  currentPeriodEnd: z.coerce.date().nullable(),
  nextBillingDate: z.coerce.date().nullable()
});
var usageTypeSchema = z.enum([
  "sms",
  "email",
  "whatsapp",
  "voice_minutes",
  "documents",
  "forms",
  "contacts",
  "api_calls"
]);
var usageStatusSchema = z.object({
  type: usageTypeSchema,
  used: z.number().int(),
  limit: z.number().int(),
  remaining: z.number().int(),
  percentUsed: z.number(),
  isAtLimit: z.boolean(),
  canExceedLimit: z.boolean()
});
var tenantUsageSummarySchema = z.object({
  tenantId: z.string().uuid(),
  period: z.string(),
  planName: z.string(),
  tierLevel: z.number().int(),
  usage: z.array(usageStatusSchema),
  totalOverages: z.coerce.number()
});
var addOnTypeSchema = z.enum([
  "sms_pack",
  "email_pack",
  "whatsapp_pack",
  "voice_pack",
  "user_seat",
  "phone_number"
]);
var addOnPurchaseSchema = z.object({
  id: z.string().uuid(),
  type: addOnTypeSchema,
  name: z.string(),
  quantity: z.number().int(),
  unitPrice: z.coerce.number(),
  totalPrice: z.coerce.number(),
  creditsIncluded: z.number().int().nullable(),
  creditsRemaining: z.number().int().nullable(),
  billingType: z.enum(["one_time", "recurring"]),
  status: z.string(),
  activatedAt: z.coerce.date(),
  expiresAt: z.coerce.date().nullable()
});
var availableAddOnSchema = z.object({
  type: addOnTypeSchema,
  name: z.string(),
  description: z.string(),
  unitPrice: z.coerce.number(),
  credits: z.number().int().optional(),
  recurring: z.boolean().optional()
});
var addOnCategorySchema = z.enum(["usage", "seat", "limit-increase", "phone-number", "feature"]);
var limitIncreaseTypeSchema = z.enum(["absolute_increase", "multiplicative", "set_value"]);
var addOnCatalogSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  // e.g., "extended_limit_monthly"
  name: z.string(),
  description: z.string().optional().nullable(),
  category: addOnCategorySchema,
  billingType: z.enum(["one_time", "recurring"]),
  unitPrice: z.coerce.number(),
  currency: z.string().default("USD"),
  // For credit-based add-ons
  creditsValue: z.number().int().optional().nullable(),
  creditType: z.string().optional().nullable(),
  // sms, email, whatsapp, voice_minutes, documents, contacts
  // For limit-increase add-ons
  affectedLimitField: z.string().optional().nullable(),
  // e.g., "maxContacts"
  increaseAmount: z.number().int().optional().nullable(),
  increaseType: limitIncreaseTypeSchema.optional(),
  // Helcim Integration
  helcimAddOnId: z.string().optional().nullable(),
  // Metadata
  isActive: z.boolean(),
  displayOrder: z.number().int(),
  icon: z.string().optional().nullable(),
  metadata: z.record(z.unknown()).optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var planAddOnAssignmentSchema = z.object({
  id: z.string().uuid(),
  planId: z.string().uuid(),
  addOnId: z.string().uuid(),
  addOn: addOnCatalogSchema.optional(),
  overriddenUnitPrice: z.coerce.number().optional().nullable(),
  isEnabled: z.boolean(),
  maxQuantityPerPeriod: z.number().int().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createAddOnCatalogDtoSchema = z.object({
  key: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional().nullable(),
  category: addOnCategorySchema,
  billingType: z.enum(["one_time", "recurring"]),
  unitPrice: z.coerce.number().positive(),
  currency: z.string().default("USD"),
  creditsValue: z.number().int().optional(),
  creditType: z.string().optional(),
  affectedLimitField: z.string().optional(),
  increaseAmount: z.number().int().optional(),
  increaseType: limitIncreaseTypeSchema.optional(),
  helcimAddOnId: z.string().optional(),
  isActive: z.boolean().default(true),
  displayOrder: z.number().int().default(0),
  icon: z.string().optional(),
  metadata: z.record(z.unknown()).optional()
});
var updateAddOnCatalogDtoSchema = createAddOnCatalogDtoSchema.partial();
var assignAddOnToPlanDtoSchema = z.object({
  planId: z.string().uuid(),
  addOnIds: z.array(z.string().uuid()),
  // Can assign multiple at once
  overriddenUnitPrice: z.coerce.number().optional(),
  isEnabled: z.boolean().default(true),
  maxQuantityPerPeriod: z.number().int().optional()
});
var unassignAddOnFromPlanDtoSchema = z.object({
  planId: z.string().uuid(),
  addOnIds: z.array(z.string().uuid())
});
var addOnCatalogFiltersSchema = z.object({
  search: z.string().optional(),
  category: addOnCategorySchema.optional(),
  billingType: z.enum(["one_time", "recurring"]).optional(),
  isActive: z.boolean().optional()
});
var paginatedAddOnCatalogSchema = z.object({
  data: z.array(addOnCatalogSchema),
  pagination: paginationMetaSchema
});
var templateTypeSchema = z.enum(["email", "sms", "document", "form"]);
var templateCategorySchema = templateTypeSchema;
var globalTemplateSchema = z.object({
  id: z.string().uuid(),
  type: templateTypeSchema,
  // Template type: form, document, sms, email
  name: z.string().min(1),
  description: z.string().optional().nullable(),
  category: z.string().optional().nullable(),
  // Subcategory (e.g., 'popular', 'healthcare' for forms, 'sales', 'hr' for documents)
  content: z.string(),
  // JSON or rich text content
  visibility: templateVisibilitySchema,
  visibleToPlans: z.array(z.string().uuid()).optional().nullable(),
  visibleToTenants: z.array(z.string().uuid()).optional().nullable(),
  usageCount: z.number().int().default(0),
  isActive: z.boolean().default(true),
  createdById: z.string().uuid().optional().nullable(),
  createdBy: superAdminUserSchema.pick({ id: true, firstName: true, lastName: true, email: true }).optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createTemplateDtoSchema = z.object({
  type: templateTypeSchema,
  // Required: form, document, sms, email
  name: z.string().min(1),
  description: z.string().optional().nullable(),
  category: z.string().optional().nullable(),
  // Optional subcategory
  content: z.string(),
  visibility: templateVisibilitySchema.optional(),
  visibleToPlans: z.array(z.string().uuid()).optional(),
  visibleToTenants: z.array(z.string().uuid()).optional(),
  isActive: z.boolean().optional()
});
var updateTemplateDtoSchema = createTemplateDtoSchema.partial();
var updateTemplateVisibilityDtoSchema = z.object({
  visibility: templateVisibilitySchema,
  visibleToPlans: z.array(z.string().uuid()).optional(),
  visibleToTenants: z.array(z.string().uuid()).optional()
});
var templateListFiltersSchema = z.object({
  search: z.string().optional(),
  category: z.string().optional().nullable(),
  // Subcategory filter (e.g., 'popular', 'healthcare', 'sales', 'hr')
  visibility: templateVisibilitySchema.optional(),
  isActive: z.boolean().optional()
});
var paginatedTemplatesListSchema = z.object({
  data: z.array(globalTemplateSchema),
  pagination: paginationMetaSchema
});
var templateCategoryTypeSchema = z.enum(["form", "document"]);
var templateCategoryEntitySchema = z.object({
  id: z.string().uuid(),
  slug: z.string().min(1),
  // e.g., "sales-contracts"
  name: z.string().min(1),
  // e.g., "Sales & Contracts"
  description: z.string().optional().nullable(),
  color: z.string().default("neutral"),
  // Badge color
  icon: z.string().optional().nullable(),
  // Lucide icon name
  type: templateCategoryTypeSchema,
  // "form" or "document"
  sortOrder: z.number().int().default(0),
  isActive: z.boolean().default(true),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createTemplateCategoryDtoSchema = z.object({
  slug: z.string().min(1).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, {
    message: "Slug must be lowercase alphanumeric with hyphens only"
  }),
  name: z.string().min(1),
  description: z.string().optional().nullable(),
  color: z.string().optional(),
  icon: z.string().optional().nullable(),
  type: templateCategoryTypeSchema
});
var updateTemplateCategoryDtoSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().optional().nullable(),
  color: z.string().optional(),
  icon: z.string().optional().nullable(),
  isActive: z.boolean().optional()
});
var reorderCategoriesDtoSchema = z.object({
  type: templateCategoryTypeSchema,
  categoryIds: z.array(z.string().uuid())
});
var platformCampaignStatusSchema = z.enum([
  "draft",
  "active",
  "paused",
  "completed",
  "archived"
]);
var campaignTriggerSchema = z.enum([
  "signup",
  "trial_end",
  "plan_change",
  "inactivity",
  "manual"
]);
var campaignStepTypeSchema = z.enum(["email", "sms", "wait"]);
var campaignAudienceTypeSchema = z.enum(["all", "plans", "tags", "custom"]);
var enrollmentStatusSchema = z.enum(["active", "completed", "paused", "exited"]);
var campaignStepSchema = z.object({
  id: z.string(),
  order: z.number().int(),
  type: campaignStepTypeSchema,
  // For wait steps - delay in days
  delay: z.number().int().nullable().optional(),
  // For email steps - inline content (supports {{merge.tags}})
  emailSubject: z.string().nullable().optional(),
  emailContent: z.string().nullable().optional(),
  // HTML body
  // For sms steps - inline content (supports {{merge.tags}})
  smsContent: z.string().nullable().optional()
});
var platformCampaignSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  description: z.string().optional().nullable(),
  status: platformCampaignStatusSchema,
  trigger: campaignTriggerSchema,
  // Audience targeting
  audienceType: campaignAudienceTypeSchema,
  audiencePlans: z.array(z.string().uuid()).optional().nullable(),
  audienceTags: z.array(z.string().uuid()).optional().nullable(),
  audienceFilter: z.string().optional().nullable(),
  // JSON for custom filters
  // Steps
  steps: z.array(campaignStepSchema),
  // Stats
  enrolledCount: z.number().int().default(0),
  completedCount: z.number().int().default(0),
  exitedCount: z.number().int().default(0),
  // Scheduling
  startDate: z.coerce.date().optional().nullable(),
  endDate: z.coerce.date().optional().nullable(),
  // Metadata
  createdById: z.string().uuid().optional().nullable(),
  createdBy: superAdminUserSchema.pick({ id: true, firstName: true, lastName: true, email: true }).optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var campaignEnrollmentSchema = z.object({
  id: z.string().uuid(),
  campaignId: z.string().uuid(),
  tenantId: z.string().uuid(),
  tenantName: z.string().optional(),
  tenant: z.object({
    id: z.string().uuid(),
    name: z.string(),
    slug: z.string()
  }).optional(),
  currentStepIndex: z.number().int(),
  status: enrollmentStatusSchema,
  startedAt: z.coerce.date(),
  completedAt: z.coerce.date().optional().nullable(),
  exitedAt: z.coerce.date().optional().nullable(),
  exitReason: z.string().optional().nullable()
});
var campaignStepStatsSchema = z.object({
  stepId: z.string(),
  reached: z.number().int(),
  completed: z.number().int(),
  exitRate: z.number()
});
var campaignStatsSchema = z.object({
  campaignId: z.string().uuid(),
  totalEnrolled: z.number().int(),
  activeEnrollments: z.number().int(),
  completedEnrollments: z.number().int(),
  exitedEnrollments: z.number().int(),
  averageCompletionDays: z.number().optional(),
  // Funnel data for visualization
  stepMetrics: z.array(campaignStepStatsSchema),
  // Email metrics
  emailMetrics: z.object({
    sent: z.number().int(),
    delivered: z.number().int(),
    opened: z.number().int(),
    clicked: z.number().int(),
    openRate: z.number(),
    clickRate: z.number()
  }).optional()
});
var createCampaignStepDtoSchema = z.object({
  id: z.string(),
  order: z.number().int(),
  type: campaignStepTypeSchema,
  // For wait steps
  delay: z.number().int().nullable().optional(),
  // For email steps
  emailSubject: z.string().nullable().optional(),
  emailContent: z.string().nullable().optional(),
  // For sms steps
  smsContent: z.string().nullable().optional()
});
var createPlatformCampaignDtoSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional().nullable(),
  trigger: campaignTriggerSchema,
  audienceType: campaignAudienceTypeSchema,
  audiencePlans: z.array(z.string().uuid()).optional(),
  audienceTags: z.array(z.string().uuid()).optional(),
  audienceFilter: z.string().optional(),
  steps: z.array(createCampaignStepDtoSchema),
  startDate: z.coerce.date().optional(),
  endDate: z.coerce.date().optional()
});
var updatePlatformCampaignDtoSchema = createPlatformCampaignDtoSchema.partial();
var platformCampaignListFiltersSchema = z.object({
  search: z.string().optional(),
  status: platformCampaignStatusSchema.optional(),
  trigger: campaignTriggerSchema.optional()
});
var paginatedCampaignsListSchema = z.object({
  data: z.array(platformCampaignSchema),
  pagination: paginationMetaSchema
});
var paginatedEnrollmentsListSchema = z.object({
  data: z.array(campaignEnrollmentSchema),
  pagination: paginationMetaSchema
});
var enrollTenantsInCampaignDtoSchema = z.object({
  tenantIds: z.array(z.string().uuid()).min(1)
});
var accountTagSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  color: z.string().default("#6366f1"),
  // Hex color
  description: z.string().optional().nullable(),
  accountCount: z.number().int().default(0),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createAccountTagDtoSchema = z.object({
  name: z.string().min(1),
  color: z.string().optional(),
  description: z.string().optional().nullable()
});
var paginatedTagsListSchema = z.object({
  data: z.array(accountTagSchema),
  pagination: paginationMetaSchema
});
var serviceHealthSchema = z.object({
  service: z.string(),
  status: healthStatusSchema,
  latencyMs: z.number().optional().nullable(),
  lastCheck: z.coerce.date(),
  errorMessage: z.string().optional().nullable(),
  metadata: z.record(z.unknown()).optional().nullable()
});
var platformHealthStatusSchema = z.object({
  services: z.array(serviceHealthSchema),
  overallStatus: healthStatusSchema,
  lastUpdated: z.coerce.date()
});
var queueStatsSchema = z.object({
  name: z.string(),
  displayName: z.string(),
  pending: z.number().int(),
  processing: z.number().int(),
  failed: z.number().int(),
  completed: z.number().int(),
  paused: z.boolean(),
  lastProcessedAt: z.coerce.date().optional().nullable()
});
var providerStatusSchema = z.object({
  name: z.string(),
  displayName: z.string(),
  status: healthStatusSchema,
  latencyMs: z.number().optional().nullable(),
  lastCheck: z.coerce.date(),
  errorCount24h: z.number().int().default(0),
  successRate24h: z.number().optional().nullable(),
  lastError: z.string().optional().nullable()
});
var maintenanceModeSchema = z.object({
  isActive: z.boolean(),
  message: z.string().optional().nullable(),
  scheduledEnd: z.coerce.date().optional().nullable(),
  startedAt: z.coerce.date().optional().nullable(),
  startedById: z.string().uuid().optional().nullable(),
  startedBy: superAdminUserSchema.pick({ id: true, firstName: true, lastName: true, email: true }).optional().nullable()
});
var toggleMaintenanceModeDtoSchema = z.object({
  isActive: z.boolean(),
  message: z.string().optional(),
  scheduledEnd: z.coerce.date().optional()
});
var auditLogFiltersSchema = z.object({
  search: z.string().optional(),
  userId: z.string().uuid().optional(),
  action: superAdminAuditActionSchema.optional(),
  entityType: superAdminEntityTypeSchema.optional(),
  entityId: z.string().optional(),
  dateRange: z.enum(["all", "24h", "7days", "30days", "90days"]).optional(),
  startDate: z.coerce.date().optional(),
  endDate: z.coerce.date().optional()
});
var paginatedAuditLogsSchema = z.object({
  data: z.array(superAdminAuditLogSchema),
  pagination: paginationMetaSchema
});
var auditExportRequestSchema = z.object({
  format: z.enum(["csv", "json"]),
  filters: auditLogFiltersSchema.optional()
});
var auditActivityDataPointSchema = z.object({
  date: z.string(),
  count: z.number().int()
});
var auditStatsSchema = z.object({
  totalLogs: z.number().int(),
  logsToday: z.number().int(),
  logsByAction: z.record(z.string(), z.number().int()),
  logsByEntityType: z.record(z.string(), z.number().int()),
  recentActivity: z.array(auditActivityDataPointSchema)
});
var superAdminUserListItemSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  role: superAdminRoleSchema,
  avatar: z.string().url().optional().nullable(),
  isActive: z.boolean(),
  lastLoginAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date()
});
var paginatedSuperAdminUserListSchema = z.object({
  data: z.array(superAdminUserListItemSchema),
  pagination: paginationMetaSchema
});
var adminUserFiltersSchema = z.object({
  search: z.string().optional(),
  role: superAdminRoleSchema.optional(),
  isActive: z.boolean().optional()
});
var resetPasswordDtoSchema = z.object({
  sendEmail: z.boolean().optional()
});
var featureFlagScopeSchema = z.enum(["global", "plan", "tenant"]);
var TENANT_FEATURE_KEYS = [
  "messaging",
  "contacts",
  "send",
  "templates",
  "forms_documents",
  "voice_scripts",
  "workflows",
  "ai_generation",
  "cowork",
  "use_cases"
];
var TENANT_FEATURE_CATALOG = {
  messaging: { name: "Messaging / Inbox", description: "Unified inbox: campaigns, conversations, forms." },
  contacts: { name: "Contacts", description: "Contact directory and contact collection forms." },
  send: { name: "Send / Campaigns", description: "The Send wizard and outbound campaigns." },
  templates: { name: "Templates", description: "Email and SMS message templates." },
  forms_documents: { name: "Forms & Documents", description: "Forms, documents, NDF/PDF signing." },
  voice_scripts: { name: "Voice Scripts", description: "Voice call scripts." },
  workflows: { name: "Workflows", description: "Automation builder and workflow runs." },
  ai_generation: { name: "AI Generation", description: "AI-assisted template and document generation." },
  cowork: { name: "CoWork Agent", description: "The tenant-facing do-everything AI agent." },
  use_cases: { name: "Use Cases", description: "Industry use-case showcase + one-click starter-kit provisioning." }
};
var featureFlagSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  key: z.string().regex(/^[a-z0-9_]+$/),
  description: z.string().optional().nullable(),
  enabled: z.boolean(),
  scope: featureFlagScopeSchema,
  targetPlans: z.array(z.string().uuid()).optional().nullable(),
  targetTenants: z.array(z.string().uuid()).optional().nullable(),
  createdById: z.string().uuid().optional().nullable(),
  createdBy: superAdminUserSchema.pick({ id: true, firstName: true, lastName: true }).optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var featureFlagFiltersSchema = z.object({
  search: z.string().optional(),
  scope: featureFlagScopeSchema.optional(),
  enabled: z.boolean().optional()
});
var createFeatureFlagDtoSchema = z.object({
  name: z.string().min(1),
  key: z.string().regex(/^[a-z0-9_]+$/),
  description: z.string().optional().nullable(),
  enabled: z.boolean().optional(),
  scope: featureFlagScopeSchema,
  targetPlans: z.array(z.string().uuid()).optional(),
  targetTenants: z.array(z.string().uuid()).optional()
});
var updateFeatureFlagDtoSchema = createFeatureFlagDtoSchema.partial();
var toggleFeatureFlagDtoSchema = z.object({
  enabled: z.boolean()
});
var paginatedFeatureFlagsListSchema = z.object({
  data: z.array(featureFlagSchema),
  pagination: paginationMetaSchema
});
var emailProviderSchema = z.enum(["mailgun", "sendgrid", "smtp"]);
var emailConfigSchema = z.object({
  provider: emailProviderSchema,
  apiKey: z.string().optional(),
  domain: z.string().optional(),
  fromEmail: z.string().email(),
  fromName: z.string(),
  replyToEmail: z.string().email().optional().nullable(),
  smtpHost: z.string().optional().nullable(),
  smtpPort: z.number().int().optional().nullable(),
  smtpUsername: z.string().optional().nullable(),
  smtpPassword: z.string().optional().nullable(),
  smtpSecure: z.boolean().optional().nullable(),
  trackOpens: z.boolean().default(true),
  trackClicks: z.boolean().default(true),
  updatedAt: z.coerce.date().optional()
});
var updateEmailConfigDtoSchema = emailConfigSchema.omit({ updatedAt: true }).partial();
var smsProviderSchema = z.enum(["sinch", "twilio"]);
var smsConfigSchema = z.object({
  provider: smsProviderSchema,
  apiKey: z.string().optional(),
  apiSecret: z.string().optional(),
  senderId: z.string(),
  servicePlanId: z.string().optional().nullable(),
  accountSid: z.string().optional().nullable(),
  authToken: z.string().optional().nullable(),
  messagingServiceSid: z.string().optional().nullable(),
  deliveryReports: z.boolean().default(true),
  optOutHandling: z.boolean().default(true),
  updatedAt: z.coerce.date().optional()
});
var updateSMSConfigDtoSchema = smsConfigSchema.omit({ updatedAt: true }).partial();
var complianceSettingsSchema = z.object({
  dataRetentionDays: z.number().int().min(30).max(3650),
  auditLogRetentionDays: z.number().int().min(90).max(3650),
  gdprEnabled: z.boolean(),
  hipaaEnabled: z.boolean(),
  ccpaEnabled: z.boolean(),
  anonymizeDeletedUsers: z.boolean(),
  requireConsentForMarketing: z.boolean(),
  enforcePasswordPolicy: z.boolean(),
  passwordMinLength: z.number().int().min(8).max(32),
  passwordRequireUppercase: z.boolean(),
  passwordRequireNumber: z.boolean(),
  passwordRequireSpecial: z.boolean(),
  sessionTimeoutMinutes: z.number().int().min(15).max(1440),
  maxFailedLoginAttempts: z.number().int().min(3).max(10),
  lockoutDurationMinutes: z.number().int().min(5).max(60),
  logAllApiCalls: z.boolean(),
  logSensitiveDataAccess: z.boolean(),
  updatedAt: z.coerce.date().optional()
});
var updateComplianceSettingsDtoSchema = complianceSettingsSchema.omit({ updatedAt: true }).partial();
var discountTypeSchema = z.enum(["percentage", "fixed"]);
var discountScopeSchema = z.enum(["all_plans", "specific_plans", "account"]);
var discountSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().optional().nullable(),
  code: z.string().optional().nullable(),
  type: discountTypeSchema,
  value: z.coerce.number(),
  scope: discountScopeSchema,
  planIds: z.array(z.string().uuid()).optional().nullable(),
  tenantId: z.string().uuid().optional().nullable(),
  startsAt: z.coerce.date().optional().nullable(),
  expiresAt: z.coerce.date().optional().nullable(),
  maxUses: z.number().int().optional().nullable(),
  usedCount: z.number().int(),
  maxUsesPerAccount: z.number().int().optional().nullable(),
  isActive: z.boolean(),
  createdBy: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var discountWithStatsSchema = discountSchema.extend({
  assignmentCount: z.number().int(),
  tenantName: z.string().optional().nullable()
});
var tenantDiscountSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  discountId: z.string().uuid(),
  discount: discountSchema,
  limitOverrides: z.record(z.number()).optional().nullable(),
  assignedAt: z.coerce.date(),
  assignedBy: z.string(),
  notes: z.string().optional().nullable(),
  isActive: z.boolean()
});
var createDiscountDtoSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(2e3).optional(),
  code: z.string().max(50).regex(/^[A-Z0-9_-]*$/, "Code must be uppercase alphanumeric").optional().nullable(),
  type: discountTypeSchema,
  value: z.coerce.number().positive(),
  scope: discountScopeSchema,
  planIds: z.array(z.string().uuid()).optional(),
  tenantId: z.string().uuid().optional().nullable(),
  startsAt: z.coerce.date().optional().nullable(),
  expiresAt: z.coerce.date().optional().nullable(),
  maxUses: z.number().int().positive().optional().nullable(),
  maxUsesPerAccount: z.number().int().positive().optional().nullable(),
  isActive: z.boolean().optional()
});
var updateDiscountDtoSchema = createDiscountDtoSchema.partial();
var assignDiscountDtoSchema = z.object({
  discountId: z.string().uuid(),
  limitOverrides: z.record(z.number()).optional(),
  notes: z.string().optional()
});
var discountListFiltersSchema = z.object({
  search: z.string().optional(),
  type: discountTypeSchema.optional(),
  scope: discountScopeSchema.optional(),
  isActive: z.boolean().optional(),
  hasCode: z.boolean().optional()
});
var paginatedDiscountsListSchema = z.object({
  data: z.array(discountWithStatsSchema),
  pagination: paginationMetaSchema
});
var messagePurposeSchema = z.enum(["MARKETING", "INFORMATIONAL"]);
var preChannelSchema = z.enum(["SMS", "EMAIL", "VOICE", "RCS", "SOCIAL_MESSAGE"]);
var consentStatusSchema = z.enum([
  "UNKNOWN",
  "NONE",
  "OPTOUT",
  "IMPLICIT_OPT_IN",
  "EXPRESS_OPTIN",
  "WRITTEN_EXPRESS_OPTIN"
]);
var ebrEventTypeSchema = z.enum([
  "PURCHASE",
  "INQUIRY",
  "CONSPICUOUS_PUBLICATION"
]);
var preDecisionSchema = z.enum(["ALLOW", "ALLOW_WITH_CONDITIONS", "BLOCK"]);
var eligibilityStatusSchema = z.enum(["ELIGIBLE", "NOT_ELIGIBLE"]);
var dncStatusSchema = z.enum(["UNKNOWN", "NOT_ON_LIST", "ON_LIST"]);
var b2bOrB2cSchema = z.enum(["B2B", "B2C"]);
var ruleLayerSchema = z.enum(["eligibility", "constraint", "consent_capture"]);
var ruleEffectSchema = z.enum(["ALLOW", "CONDITION", "BLOCK"]);
var subdivisionTypeSchema = z.enum(["GLOBAL", "FEDERAL", "STATE", "PROVINCE", "NATIONAL"]);
var registryTypeSchema = z.enum(["NATIONAL", "INTERNAL"]);
var PRE_CHECK_IDS = {
  CHK_INTERNAL_DNC_CLEAR: "CHK_INTERNAL_DNC_CLEAR",
  CHK_NATIONAL_REGISTRY_SCRUBBED: "CHK_NATIONAL_REGISTRY_SCRUBBED",
  CHK_NATIONAL_REGISTRY_CLEAR: "CHK_NATIONAL_REGISTRY_CLEAR",
  CHK_WITHIN_CALLING_HOURS: "CHK_WITHIN_CALLING_HOURS",
  CHK_FREQUENCY_CAP_24H_OK: "CHK_FREQUENCY_CAP_24H_OK",
  CHK_FREQUENCY_CAP_7D_OK: "CHK_FREQUENCY_CAP_7D_OK",
  CHK_CHANNEL_PREFERENCE_MATCH: "CHK_CHANNEL_PREFERENCE_MATCH",
  CHK_CONSENT_AT_LEAST: "CHK_CONSENT_AT_LEAST",
  CHK_CONTACT_ELIGIBLE: "CHK_CONTACT_ELIGIBLE",
  CHK_SENDER_ID_AND_UNSUB_PRESENT: "CHK_SENDER_ID_AND_UNSUB_PRESENT",
  CHK_BRAZIL_0303_PREFIX: "CHK_BRAZIL_0303_PREFIX",
  CHK_REVOCATION_HANDLING_ENABLED: "CHK_REVOCATION_HANDLING_ENABLED",
  CHK_WIRELESS_MARKETING_CONSENT_OK: "CHK_WIRELESS_MARKETING_CONSENT_OK",
  CHK_AUTOMATION_ALLOWED_FOR_SEND: "CHK_AUTOMATION_ALLOWED_FOR_SEND",
  CHK_END_CALL_WITHIN_10S_ON_REQUEST: "CHK_END_CALL_WITHIN_10S_ON_REQUEST",
  CHK_DNC_DISCLOSURE_AT_CALL_START: "CHK_DNC_DISCLOSURE_AT_CALL_START",
  CHK_CASL_CONSENT_VALID: "CHK_CASL_CONSENT_VALID",
  CHK_CASL_CONSPICUOUS_PUBLICATION_VALID: "CHK_CASL_CONSPICUOUS_PUBLICATION_VALID",
  CHK_LGPD_LAWFUL_BASIS_VALID: "CHK_LGPD_LAWFUL_BASIS_VALID",
  CHK_BRAZIL_NAO_ME_PERTURBE_CLEAR: "CHK_BRAZIL_NAO_ME_PERTURBE_CLEAR",
  CHK_RIGHT_TO_DISCONNECT_OK: "CHK_RIGHT_TO_DISCONNECT_OK"
};
var preCheckIdSchema = z.enum([
  "CHK_INTERNAL_DNC_CLEAR",
  "CHK_NATIONAL_REGISTRY_SCRUBBED",
  "CHK_NATIONAL_REGISTRY_CLEAR",
  "CHK_WITHIN_CALLING_HOURS",
  "CHK_FREQUENCY_CAP_24H_OK",
  "CHK_FREQUENCY_CAP_7D_OK",
  "CHK_CHANNEL_PREFERENCE_MATCH",
  "CHK_CONSENT_AT_LEAST",
  "CHK_CONTACT_ELIGIBLE",
  "CHK_SENDER_ID_AND_UNSUB_PRESENT",
  "CHK_BRAZIL_0303_PREFIX",
  "CHK_REVOCATION_HANDLING_ENABLED",
  "CHK_WIRELESS_MARKETING_CONSENT_OK",
  "CHK_AUTOMATION_ALLOWED_FOR_SEND",
  "CHK_END_CALL_WITHIN_10S_ON_REQUEST",
  "CHK_DNC_DISCLOSURE_AT_CALL_START",
  "CHK_CASL_CONSENT_VALID",
  "CHK_CASL_CONSPICUOUS_PUBLICATION_VALID",
  "CHK_LGPD_LAWFUL_BASIS_VALID",
  "CHK_BRAZIL_NAO_ME_PERTURBE_CLEAR",
  "CHK_RIGHT_TO_DISCONNECT_OK"
]);
var PRE_CONDITION_CODES = {
  COND_SCHEDULE_WITHIN_HOURS: "COND_SCHEDULE_WITHIN_HOURS",
  COND_REGISTRY_SCRUB_REQUIRED: "COND_REGISTRY_SCRUB_REQUIRED",
  COND_INCLUDE_SENDER_ID_AND_UNSUB: "COND_INCLUDE_SENDER_ID_AND_UNSUB",
  COND_RECORD_RETENTION: "COND_RECORD_RETENTION",
  COND_CAPTURE_EVIDENCE_SNAPSHOT: "COND_CAPTURE_EVIDENCE_SNAPSHOT",
  COND_VERIFY_RELEVANCE_RATIONALE: "COND_VERIFY_RELEVANCE_RATIONALE"
};
var preConditionCodeSchema = z.enum([
  "COND_SCHEDULE_WITHIN_HOURS",
  "COND_REGISTRY_SCRUB_REQUIRED",
  "COND_INCLUDE_SENDER_ID_AND_UNSUB",
  "COND_RECORD_RETENTION",
  "COND_CAPTURE_EVIDENCE_SNAPSHOT",
  "COND_VERIFY_RELEVANCE_RATIONALE"
]);
var PRE_REMEDIATION_CODES = {
  REMED_RETRY_AFTER_WINDOW_RESETS: "REMED_RETRY_AFTER_WINDOW_RESETS",
  REMED_REMOVE_FROM_CALL_LIST: "REMED_REMOVE_FROM_CALL_LIST",
  REMED_INTERNAL_DNC_CLEARANCE_REQUIRED: "REMED_INTERNAL_DNC_CLEARANCE_REQUIRED"
};
var preRemediationCodeSchema = z.enum([
  "REMED_RETRY_AFTER_WINDOW_RESETS",
  "REMED_REMOVE_FROM_CALL_LIST",
  "REMED_INTERNAL_DNC_CLEARANCE_REQUIRED"
]);
var PRE_RISK_FLAGS = {
  RISK_UNKNOWN_JURISDICTION: "RISK_UNKNOWN_JURISDICTION",
  RISK_UNKNOWN_LINE_TYPE: "RISK_UNKNOWN_LINE_TYPE",
  RISK_UNKNOWN_NATIONAL_REGISTRY_STATUS: "RISK_UNKNOWN_NATIONAL_REGISTRY_STATUS",
  RISK_UNKNOWN_INTERNAL_DNC_STATUS: "RISK_UNKNOWN_INTERNAL_DNC_STATUS",
  RISK_STATE_ASSUMED_BY_AREA_CODE: "RISK_STATE_ASSUMED_BY_AREA_CODE",
  RISK_CHANNEL_FALLBACK_TO_SMS: "RISK_CHANNEL_FALLBACK_TO_SMS",
  RISK_SMS_B2B_NORMALIZED_TO_B2C: "RISK_SMS_B2B_NORMALIZED_TO_B2C",
  /** Recipient timezone could not be determined from contact data, phone, or
   *  the calling-hour window's own timezone. When a rule requires
   *  CHK_WITHIN_CALLING_HOURS this drives a fail-closed BLOCK. */
  RISK_NO_TIMEZONE: "RISK_NO_TIMEZONE",
  /** Recipient timezone was derived from a non-explicit signal (phone area
   *  code, state, country, or window). Description carries the source. */
  RISK_TIMEZONE_DERIVED: "RISK_TIMEZONE_DERIVED"
};
var preRiskFlagSchema = z.enum([
  "RISK_UNKNOWN_JURISDICTION",
  "RISK_UNKNOWN_LINE_TYPE",
  "RISK_UNKNOWN_NATIONAL_REGISTRY_STATUS",
  "RISK_UNKNOWN_INTERNAL_DNC_STATUS",
  "RISK_STATE_ASSUMED_BY_AREA_CODE",
  "RISK_CHANNEL_FALLBACK_TO_SMS",
  "RISK_SMS_B2B_NORMALIZED_TO_B2C",
  "RISK_NO_TIMEZONE",
  "RISK_TIMEZONE_DERIVED"
]);
var PRE_RISK_FLAG_LABELS = {
  RISK_UNKNOWN_JURISDICTION: "Unknown Jurisdiction",
  RISK_UNKNOWN_LINE_TYPE: "Unknown Line Type",
  RISK_UNKNOWN_NATIONAL_REGISTRY_STATUS: "Unknown Registry Status",
  RISK_UNKNOWN_INTERNAL_DNC_STATUS: "Unknown DNC Status",
  RISK_STATE_ASSUMED_BY_AREA_CODE: "State Inferred from Area Code",
  RISK_CHANNEL_FALLBACK_TO_SMS: "Channel Fell Back to SMS",
  RISK_SMS_B2B_NORMALIZED_TO_B2C: "SMS B2B Treated as B2C (TCPA)",
  RISK_NO_TIMEZONE: "Recipient Timezone Unknown",
  RISK_TIMEZONE_DERIVED: "Recipient Timezone Inferred"
};
var PRE_CONDITION_LABELS = {
  COND_SCHEDULE_WITHIN_HOURS: "Schedule Within Calling Hours",
  COND_REGISTRY_SCRUB_REQUIRED: "National DNC Scrub Required",
  COND_INCLUDE_SENDER_ID_AND_UNSUB: "Include Sender ID, Postal Address, and Unsubscribe",
  COND_RECORD_RETENTION: "Retain Consent Evidence",
  COND_CAPTURE_EVIDENCE_SNAPSHOT: "Capture Evidence Snapshot",
  COND_VERIFY_RELEVANCE_RATIONALE: "Verify Message Relevance to Recipient"
};
var PRE_REMEDIATION_LABELS = {
  REMED_RETRY_AFTER_WINDOW_RESETS: "Retry After Window Resets",
  REMED_REMOVE_FROM_CALL_LIST: "Remove From Call List",
  REMED_INTERNAL_DNC_CLEARANCE_REQUIRED: "Internal DNC Clearance Required"
};
var PRE_DECISION_LABELS = {
  ALLOW: "Allow",
  ALLOW_WITH_CONDITIONS: "Conditional",
  BLOCK: "Blocked"
};
var PRE_DECISION_DESCRIPTIONS = {
  ALLOW: "This send is compliant and was delivered.",
  ALLOW_WITH_CONDITIONS: "This send was allowed but required additional conditions to stay compliant.",
  BLOCK: "This send was blocked because it would violate one or more policy rules."
};
var PRE_REMEDIATION_DESCRIPTIONS = {
  REMED_RETRY_AFTER_WINDOW_RESETS: "These sends will retry automatically when the calling-hours window resets.",
  REMED_REMOVE_FROM_CALL_LIST: "Remove these contacts from your call list \u2014 they cannot be contacted.",
  REMED_INTERNAL_DNC_CLEARANCE_REQUIRED: "These contacts are on your internal Do-Not-Contact list and need clearance before sending."
};
var PRE_CONDITION_DESCRIPTIONS = {
  COND_SCHEDULE_WITHIN_HOURS: "This message is held and will send automatically during the recipient's local calling hours \u2014 you don't need to do anything.",
  COND_REGISTRY_SCRUB_REQUIRED: "This send requires a current national Do-Not-Call registry scrub before it can go out.",
  COND_INCLUDE_SENDER_ID_AND_UNSUB: "This message must identify your business and include a working unsubscribe link and postal address.",
  COND_RECORD_RETENTION: "A record of the recipient\u2019s consent is kept on file for this send.",
  COND_CAPTURE_EVIDENCE_SNAPSHOT: "A snapshot of the consent evidence is captured for this send.",
  COND_VERIFY_RELEVANCE_RATIONALE: "This message must be relevant to the recipient\u2019s relationship with your business."
};
function describePreDecision(params) {
  const { decision, conditionCodes = [], formattedSendTime } = params;
  if (decision === "ALLOW") {
    return PRE_DECISION_DESCRIPTIONS.ALLOW;
  }
  if (decision === "BLOCK") {
    return PRE_DECISION_DESCRIPTIONS.BLOCK;
  }
  const isScheduled = conditionCodes.includes(
    PRE_CONDITION_CODES.COND_SCHEDULE_WITHIN_HOURS
  );
  if (isScheduled) {
    return formattedSendTime ? `Held for calling hours \u2014 scheduled to send automatically on ${formattedSendTime}.` : "Held for calling hours \u2014 this message will send automatically during the recipient's local calling hours.";
  }
  return PRE_DECISION_DESCRIPTIONS.ALLOW_WITH_CONDITIONS;
}
var consentRecordSchema = z.object({
  status: consentStatusSchema,
  capturedAt: z.coerce.date().optional().nullable(),
  source: z.string().optional().nullable(),
  scope: z.string().optional().nullable(),
  proof: z.string().optional().nullable(),
  agreementArtifactRef: z.string().optional().nullable(),
  disclosureTextSnapshot: z.string().optional().nullable(),
  captureContext: z.string().optional().nullable(),
  // EBR fields (issue #757 §1 + §6a). Required when status === 'IMPLICIT_OPT_IN'.
  ebrEventType: ebrEventTypeSchema.optional().nullable(),
  ebrEventAt: z.coerce.date().optional().nullable(),
  expiresAt: z.coerce.date().optional().nullable(),
  sourceUrl: z.string().optional().nullable()
});
var eligibilityFlagsSchema = z.object({
  informationalSms: z.boolean(),
  informationalEmail: z.boolean(),
  informationalVoice: z.boolean(),
  marketingSms: z.boolean(),
  marketingEmail: z.boolean(),
  marketingVoice: z.boolean()
});
var jurisdictionSchema = z.object({
  id: z.string().uuid(),
  code: z.string().min(1),
  name: z.string().min(1),
  country: z.string().min(1),
  subdivisionType: subdivisionTypeSchema,
  parentCode: z.string().optional().nullable(),
  isActive: z.boolean().default(true),
  isSystem: z.boolean().default(true),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createJurisdictionSchema = jurisdictionSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
var updateJurisdictionSchema = createJurisdictionSchema.partial();
var ruleRequiredCheckSchema = z.object({
  checkId: z.string().min(1),
  required: z.boolean(),
  params: z.record(z.unknown()).optional().nullable()
});
var ruleSchema = z.object({
  id: z.string().uuid(),
  ruleId: z.string().min(1),
  name: z.string().min(1),
  description: z.string(),
  jurisdictionCode: z.string().min(1),
  layer: ruleLayerSchema,
  channel: preChannelSchema.optional().nullable(),
  messagePurpose: messagePurposeSchema.optional().nullable(),
  b2bOrB2c: b2bOrB2cSchema.optional().nullable(),
  tenantIndustry: z.string().optional().nullable(),
  requiredChecks: z.array(ruleRequiredCheckSchema),
  effect: ruleEffectSchema,
  remediation: z.string().optional().nullable(),
  sourceRef: z.string().optional().nullable(),
  /** Plain-language override of the per-check rendered bullets shown on the
   *  tenant Policies page. When null/empty, the renderer falls back to per-check
   *  userFacingTemplate values. */
  userFacingSummary: z.string().optional().nullable(),
  /** Short citation displayed under the rule's bullets on the tenant Policies
   *  page. Falls back to sourceRef when null. */
  userFacingCitation: z.string().optional().nullable(),
  priority: z.number().int().min(0),
  isActive: z.boolean().default(true),
  isOverridable: z.boolean().default(false),
  isSystem: z.boolean().default(true),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createRuleSchema = ruleSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
var updateRuleSchema = createRuleSchema.partial();
var callingHourWindowSchema = z.object({
  id: z.string().uuid(),
  windowId: z.string().min(1),
  jurisdictionCode: z.string().min(1),
  name: z.string().min(1),
  channel: preChannelSchema.optional().nullable(),
  messagePurpose: messagePurposeSchema.optional().nullable(),
  startHour: z.number().int().min(0).max(23),
  startMinute: z.number().int().min(0).max(59),
  endHour: z.number().int().min(0).max(23),
  endMinute: z.number().int().min(0).max(59),
  daysOfWeek: z.array(z.number().int().min(0).max(6)),
  timezone: z.string(),
  sourceRef: z.string().optional().nullable(),
  isActive: z.boolean().default(true),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createCallingHourWindowSchema = callingHourWindowSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
var updateCallingHourWindowSchema = createCallingHourWindowSchema.partial();
var dncRegistrySchema = z.object({
  id: z.string().uuid(),
  registryId: z.string().min(1),
  name: z.string().min(1),
  country: z.string().min(1),
  jurisdictionCode: z.string().min(1),
  registryType: registryTypeSchema,
  scrubCadenceDays: z.number().int().min(1),
  channel: preChannelSchema.optional().nullable(),
  description: z.string().optional().nullable(),
  isActive: z.boolean().default(true),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createDncRegistrySchema = dncRegistrySchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
var updateDncRegistrySchema = createDncRegistrySchema.partial();
var frequencyCapConfigSchema = z.object({
  id: z.string().uuid(),
  jurisdictionCode: z.string().min(1),
  name: z.string().min(1),
  channel: preChannelSchema.optional().nullable(),
  messagePurpose: messagePurposeSchema.optional().nullable(),
  maxAttempts: z.number().int().min(1),
  periodHours: z.number().int().min(1),
  crossChannel: z.boolean().default(false),
  sourceRef: z.string().optional().nullable(),
  isActive: z.boolean().default(true),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createFrequencyCapConfigSchema = frequencyCapConfigSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
var updateFrequencyCapConfigSchema = createFrequencyCapConfigSchema.partial();
var preRecipientSchema = z.object({
  addressing: z.object({
    phone: z.string().optional().nullable(),
    email: z.string().optional().nullable()
  }),
  country: z.string().optional().nullable(),
  usAreaCode: z.string().optional().nullable(),
  b2bOrB2c: b2bOrB2cSchema.optional().nullable()
});
var preContactFrequencySchema = z.object({
  attemptsLast24hSameSubject: z.number().int().min(0),
  contactsLast7dSameSubjectAnyChannel: z.number().int().min(0),
  preferredChannel: preChannelSchema.optional().nullable()
});
var preInputSchema = z.object({
  recipient: preRecipientSchema,
  channel: preChannelSchema,
  messagePurpose: messagePurposeSchema,
  contactEligibility: eligibilityFlagsSchema,
  // Per-cell consent status keyed by `${channel}_${purpose}` (e.g. "SMS_MARKETING").
  // Sourced from ConsentRecord.status after IMPLICIT_OPT_IN expiry downgrade.
  // CHK_CONSENT_AT_LEAST consults this so that a rule declaring
  // `minConsent: 'WRITTEN_EXPRESS_OPTIN'` actually requires written consent
  // (the eligibility-flag proxy collapses WRITTEN and non-written into a single
  // boolean and cannot distinguish the two). Optional for back-compat; when
  // absent, the engine falls back to the eligibility-flag proxy.
  contactConsentStatuses: z.record(consentStatusSchema).optional(),
  internalDncStatus: dncStatusSchema,
  nationalRegistryStatus: dncStatusSchema,
  // Per-registry status map keyed by registryId (e.g. 'MX_REPEP', 'MX_REUS', 'CA_DNCL').
  // When present, registry-specific checks consult this; otherwise they fall back to
  // the aggregate `nationalRegistryStatus` above.
  nationalRegistryStatuses: z.record(dncStatusSchema).optional(),
  // Per-registry last-checked timestamp keyed by registryId (e.g. 'US_DNC').
  // Sourced from ContactDNCStatus.lastCheckedAt. When a rule declares
  // `maxAgeDays` (e.g. PRE-US-DNC-001 requires a scrub within 31 days),
  // CHK_NATIONAL_REGISTRY_SCRUBBED enforces freshness against this map and
  // fails-closed if the scrub is missing or stale.
  nationalRegistryCheckedAt: z.record(z.coerce.date().nullable()).optional(),
  // Tenant industry — used to gate sector-specific rules like Mexico REUS (financial sector).
  // Matches `Tenant.industry` (e.g. 'FINANCIAL_SERVICES').
  tenantIndustry: z.string().optional().nullable(),
  // Wall-clock time at the recipient (Date constructed so .getHours/.getDay etc.
  // return the recipient's local time). Nullable when the recipient's timezone
  // could not be resolved from contact data, phone, or window TZ — rules that
  // require CHK_WITHIN_CALLING_HOURS will fail-closed in that case.
  localTimeAtRecipient: z.coerce.date().nullable(),
  contactFrequency: preContactFrequencySchema,
  tenantId: z.string().uuid(),
  campaignId: z.string().uuid().optional().nullable(),
  // Tenant-level compliance attestations. Populated by the delivery worker
  // and the /api/pre/evaluate route. Used by CHK_REVOCATION_HANDLING_ENABLED
  // (the tenant has STOP/unsubscribe handlers wired live). Optional for
  // back-compat — absent → check fails closed.
  tenantConfig: z.object({
    smsStopHandlerEnabled: z.boolean(),
    emailUnsubHandlerEnabled: z.boolean()
  }).optional(),
  // Email-template compliance signals. Populated by the email delivery
  // worker after the template is rendered for the recipient. Used by
  // CHK_SENDER_ID_AND_UNSUB_PRESENT (CAN-SPAM). Optional — absent →
  // check fails closed.
  emailTemplate: z.object({
    hasUnsubscribeLink: z.boolean(),
    hasPhysicalPostalAddress: z.boolean(),
    hasIdentifiableFromAddress: z.boolean(),
    unsubscribeHonorWindowBusinessDays: z.number().int().min(1).max(30)
  }).optional(),
  // Recipient phone line type for TCPA wireless gating. Populated from
  // Contact.lineType (written by the line-type-lookup worker). Used by
  // CHK_WIRELESS_MARKETING_CONSENT_OK: MOBILE or UNKNOWN require the
  // rule's wireless min consent; LANDLINE/VOIP fall back to the channel's
  // normal consent gate.
  recipientLineType: z.enum(["MOBILE", "LANDLINE", "VOIP", "UNKNOWN"]).optional()
});
var preTraceEntrySchema = z.object({
  ruleId: z.string(),
  ruleName: z.string(),
  result: z.enum(["PASS", "FAIL", "SKIP"]),
  sourceRef: z.string().optional().nullable()
});
var preConditionOutputSchema = z.object({
  code: z.string(),
  params: z.record(z.unknown()).optional().nullable(),
  description: z.string().optional().nullable()
});
var preRemediationOutputSchema = z.object({
  code: z.string(),
  params: z.record(z.unknown()).optional().nullable(),
  description: z.string().optional().nullable()
});
var preRiskFlagOutputSchema = z.object({
  code: z.string(),
  description: z.string().optional().nullable()
});
var preConsentCaptureTraceEntrySchema = z.object({
  ruleId: z.string(),
  ruleName: z.string(),
  jurisdictionCode: z.string(),
  effect: z.enum(["ALLOW", "BLOCK"]),
  result: z.enum(["PASS", "SKIP"]),
  sourceRef: z.string().optional().nullable()
});
var preOutputSchema = z.object({
  decision: preDecisionSchema,
  eligibility: eligibilityStatusSchema,
  conditions: z.array(preConditionOutputSchema),
  riskFlags: z.array(preRiskFlagOutputSchema),
  remediation: z.array(preRemediationOutputSchema),
  trace: z.array(preTraceEntrySchema),
  // Layer-0 consent-capture rules evaluated for this (channel × purpose) cell.
  // Reflects what the Super Admin "Opt-in Requirements" section displays; the
  // winner's effect (ALLOW/BLOCK) gates initial informational auto-grant at
  // contact creation. Omitted on engines that haven't been updated.
  consentCapture: z.array(preConsentCaptureTraceEntrySchema).optional(),
  evaluatedAt: z.coerce.date()
});
var preDecisionLogSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  contactId: z.string().optional().nullable(),
  campaignId: z.string().optional().nullable(),
  channel: preChannelSchema,
  messagePurpose: messagePurposeSchema,
  jurisdictionCode: z.string(),
  decision: preDecisionSchema,
  eligibility: eligibilityStatusSchema,
  conditionCount: z.number().int().min(0),
  riskFlagCount: z.number().int().min(0),
  input: preInputSchema,
  output: preOutputSchema,
  internalDncSuppressed: z.boolean().optional(),
  internalDncReason: z.string().optional().nullable(),
  createdAt: z.coerce.date()
});
var preDecisionLogFiltersSchema = z.object({
  search: z.string().optional(),
  tenantId: z.string().uuid().optional(),
  channel: preChannelSchema.optional(),
  messagePurpose: messagePurposeSchema.optional(),
  decision: preDecisionSchema.optional(),
  dateRange: z.enum(["all", "24h", "7days", "30days", "90days"]).optional(),
  // True → only decisions where the SMS provider call was suppressed by Internal DNC.
  // Accepts string "true"/"false" from query params or boolean from JSON.
  internalDncOnly: z.union([z.boolean(), z.literal("true"), z.literal("false")]).transform((v) => v === true || v === "true").optional()
});
var preAiClassificationSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  messageContent: z.string(),
  classifiedPurpose: messagePurposeSchema,
  confidence: z.number().min(0).max(1),
  reasoning: z.string().optional().nullable(),
  isFlagged: z.boolean(),
  overriddenPurpose: messagePurposeSchema.optional().nullable(),
  overriddenBy: z.string().uuid().optional().nullable(),
  overriddenAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date()
});
var preAiClassificationFiltersSchema = z.object({
  search: z.string().optional(),
  tenantId: z.string().uuid().optional(),
  classifiedPurpose: messagePurposeSchema.optional(),
  isFlagged: z.boolean().optional(),
  dateRange: z.enum(["all", "24h", "7days", "30days", "90days"]).optional()
});
var ruleTypeSchema = z.enum(["OPT_IN", "OPT_OUT", "SENDING"]);
var contactOrSendSchema = ruleTypeSchema;
var preCheckDefinitionSchema = z.object({
  /** DB-generated UUID. Optional because the static catalogue arrays defined
   *  in app code don't carry an id; the API populates it. */
  id: z.string().uuid().optional(),
  checkId: z.string(),
  name: z.string(),
  description: z.string(),
  paramSchema: z.record(z.string()).optional().nullable(),
  /** Plain-language sentence rendered on the Policies page. Supports
   *  {paramName} placeholders substituted from per-check params at display
   *  time. */
  userFacingTemplate: z.string(),
  /** Which section the bullet renders in (Opt-in / Opt-out / Sending). */
  ruleType: ruleTypeSchema
});
var createPreCheckDefinitionSchema = preCheckDefinitionSchema.omit({ id: true });
var updatePreCheckDefinitionSchema = createPreCheckDefinitionSchema.partial();
var PRE_BUNDLE_SCHEMA_VERSION = 1;
var PRE_BUNDLE_CANONICALIZATION_VERSION = 1;
var bundleJurisdictionSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  country: z.string().min(1),
  subdivisionType: subdivisionTypeSchema,
  parentCode: z.string().nullable(),
  isActive: z.boolean(),
  isSystem: z.boolean()
}).strict();
var bundleCheckDefinitionSchema = z.object({
  checkId: z.string().min(1),
  name: z.string().min(1),
  description: z.string(),
  paramSchema: z.record(z.string()).nullable(),
  userFacingTemplate: z.string(),
  ruleType: ruleTypeSchema,
  isSystem: z.boolean()
}).strict();
var bundleRuleSchema = z.object({
  ruleId: z.string().min(1),
  name: z.string().min(1),
  description: z.string(),
  jurisdictionCode: z.string().min(1),
  layer: ruleLayerSchema,
  channel: preChannelSchema.nullable(),
  messagePurpose: messagePurposeSchema.nullable(),
  b2bOrB2c: b2bOrB2cSchema.nullable(),
  tenantIndustry: z.string().nullable(),
  requiredChecks: z.array(ruleRequiredCheckSchema),
  effect: ruleEffectSchema,
  remediation: z.string().nullable(),
  sourceRef: z.string().nullable(),
  userFacingSummary: z.string().nullable(),
  userFacingCitation: z.string().nullable(),
  priority: z.number().int().min(0),
  isActive: z.boolean(),
  isOverridable: z.boolean(),
  isSystem: z.boolean()
}).strict();
var bundleCallingHourWindowSchema = z.object({
  windowId: z.string().min(1),
  jurisdictionCode: z.string().min(1),
  name: z.string().min(1),
  channel: preChannelSchema.nullable(),
  messagePurpose: messagePurposeSchema.nullable(),
  startHour: z.number().int().min(0).max(23),
  startMinute: z.number().int().min(0).max(59),
  endHour: z.number().int().min(0).max(23),
  endMinute: z.number().int().min(0).max(59),
  daysOfWeek: z.array(z.number().int().min(0).max(6)),
  timezone: z.string().min(1),
  sourceRef: z.string().nullable(),
  isActive: z.boolean()
}).strict();
var bundleDncRegistrySchema = z.object({
  registryId: z.string().min(1),
  name: z.string().min(1),
  country: z.string().min(1),
  jurisdictionCode: z.string().min(1),
  registryType: registryTypeSchema,
  scrubCadenceDays: z.number().int().min(1),
  channel: preChannelSchema.nullable(),
  description: z.string().nullable(),
  isActive: z.boolean()
}).strict();
var bundleFrequencyCapSchema = z.object({
  jurisdictionCode: z.string().min(1),
  name: z.string().min(1),
  channel: preChannelSchema.nullable(),
  messagePurpose: messagePurposeSchema.nullable(),
  maxAttempts: z.number().int().min(1),
  periodHours: z.number().int().min(1),
  crossChannel: z.boolean(),
  sourceRef: z.string().nullable(),
  isActive: z.boolean()
}).strict();
var bundleFeatureFlagDefaultSchema = z.object({
  key: z.string().min(1),
  name: z.string().min(1),
  description: z.string().nullable(),
  defaultEnabled: z.boolean()
}).strict();
var preBundleMetadataSchema = z.object({
  /** Stable id of the bundle generation event. */
  bundleId: z.string().uuid(),
  /** ISO-8601 UTC timestamp (with Z suffix). */
  generatedAt: z.string().datetime({ offset: false }),
  /** Super-admin id who triggered the export. */
  generatedBy: z.string().min(1),
  /** Optional human label (e.g. email) for the actor. */
  generatedByLabel: z.string().nullable(),
  /** Source environment of the export ('dev' | 'staging' | 'prod' | 'unknown'). */
  sourceEnv: z.enum(["dev", "staging", "prod", "unknown"]),
  /** Application version, if available (from package.json or env). */
  appVersion: z.string().nullable(),
  /** Git SHA, if available (from env CI_COMMIT_SHA / GIT_SHA). */
  gitSha: z.string().nullable(),
  /** Free-text description supplied by the operator at export time. */
  description: z.string().nullable(),
  /** Optional release tag (e.g. 'v2.4.0-rc1') for golden promotion. */
  releaseTag: z.string().nullable(),
  /** Parent bundle id this was derived from (for promotion lineage). */
  parentBundleId: z.string().uuid().nullable()
}).strict();
var preBundleIntegritySchema = z.object({
  algorithm: z.literal("sha256"),
  /** Hex-encoded SHA-256 of the canonical payload (bundle minus the
   * `integrity.checksum` field). Required. */
  checksum: z.string().regex(/^[a-f0-9]{64}$/),
  /** Optional reference to a detached signature file (e.g. '*.json.sig'). */
  signatureRef: z.string().nullable(),
  /** Canonicalization algorithm version used to compute `checksum`. */
  canonicalizationVersion: z.literal(PRE_BUNDLE_CANONICALIZATION_VERSION)
}).strict();
var preBundlePayloadSchema = z.object({
  jurisdictions: z.array(bundleJurisdictionSchema),
  checkDefinitions: z.array(bundleCheckDefinitionSchema),
  rules: z.array(bundleRuleSchema),
  callingHourWindows: z.array(bundleCallingHourWindowSchema),
  dncRegistries: z.array(bundleDncRegistrySchema),
  frequencyCaps: z.array(bundleFrequencyCapSchema),
  featureFlagDefaults: z.array(bundleFeatureFlagDefaultSchema)
}).strict();
var preBundleSchema = z.object({
  bundleSchemaVersion: z.literal(PRE_BUNDLE_SCHEMA_VERSION),
  metadata: preBundleMetadataSchema,
  integrity: preBundleIntegritySchema,
  payload: preBundlePayloadSchema
}).strict();
var ndfVersionSchema = z.literal("1.0");
var ndfKindSchema = z.enum(["document", "form", "hybrid", "sms", "email"]);
var textStyleSchema = z.object({
  bold: z.boolean().optional(),
  italic: z.boolean().optional(),
  align: z.enum(["left", "center", "right"]).optional(),
  fontSize: z.number().positive().optional()
});
var normalizedPositionSchema = z.object({
  page: z.number().int().positive(),
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  width: z.number().min(0).max(1),
  height: z.number().min(0).max(1)
});
var ndfFieldValidationSchema = z.object({
  required: z.boolean().optional(),
  minLength: z.number().int().optional(),
  maxLength: z.number().int().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  pattern: z.string().optional(),
  customMessage: z.string().optional()
});
var ndfConditionalLogicSchema = z.object({
  fieldId: z.string(),
  operator: z.enum(["equals", "notEquals", "contains", "notContains"]),
  value: z.unknown()
});
var ndfSignerSchema = z.object({
  id: z.string(),
  name: z.string(),
  order: z.number().int().positive()
});
var ndfVariableDefinitionSchema = z.object({
  name: z.string(),
  label: z.string(),
  type: z.enum(["text", "date", "number", "email", "phone"]),
  defaultValue: z.string().optional()
});
var ndfDocumentMetaSchema = z.object({
  title: z.string().min(1),
  description: z.string().optional(),
  category: z.string().optional(),
  variables: z.array(ndfVariableDefinitionSchema).optional(),
  layout: z.enum(["single", "two-column"]).optional()
});
var ndfPdfSourceSchema = z.object({
  fileUrl: z.string(),
  originalName: z.string(),
  fileSize: z.number().int().nonnegative(),
  pageCount: z.number().int().positive()
});
var ndfFormBrandingSchema = z.object({
  logoUrl: z.string().nullable().optional(),
  primaryColor: z.string().optional().default("#3b82f6"),
  logoPosition: z.enum(["left", "center", "right"]).optional().default("center"),
  inputBorderRadius: z.number().int().min(0).max(20).optional().default(8),
  inputBorderColor: z.string().nullable().optional(),
  inputBackgroundColor: z.string().nullable().optional(),
  inputTextColor: z.string().nullable().optional()
});
var ndfFormSettingsSchema = z.object({
  submitButtonText: z.string().optional(),
  successMessage: z.string().optional(),
  redirectUrl: z.string().optional(),
  notifyOnSubmission: z.boolean().optional(),
  notificationEmails: z.array(z.string().email()).optional(),
  webhookUrl: z.string().optional(),
  captureEmailConsent: z.boolean().optional(),
  captureSmsConsent: z.boolean().optional(),
  consentText: z.string().optional(),
  allowMultipleSubmissions: z.boolean().optional(),
  maxSubmissions: z.number().int().positive().optional(),
  requireAuth: z.boolean().optional(),
  // Branding settings
  branding: ndfFormBrandingSchema.optional()
});
var paragraphBlockSchema = z.object({
  type: z.literal("paragraph"),
  id: z.string(),
  content: z.string(),
  style: textStyleSchema.optional()
});
var headingBlockSchema = z.object({
  type: z.literal("heading"),
  id: z.string(),
  level: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
  content: z.string()
});
var listBlockSchema = z.object({
  type: z.literal("list"),
  id: z.string(),
  ordered: z.boolean(),
  items: z.array(z.string())
});
var imageBlockSchema = z.object({
  type: z.literal("image"),
  id: z.string(),
  src: z.string(),
  alt: z.string().optional(),
  width: z.number().positive().optional(),
  height: z.number().positive().optional()
});
var tableBlockSchema = z.object({
  type: z.literal("table"),
  id: z.string(),
  columns: z.array(z.object({
    header: z.string(),
    width: z.number().positive().optional()
  })),
  rows: z.array(z.array(z.string()))
});
var dividerBlockSchema = z.object({
  type: z.literal("divider"),
  id: z.string()
});
var spacerBlockSchema = z.object({
  type: z.literal("spacer"),
  id: z.string(),
  height: z.number().positive().optional()
});
var sectionHeaderBlockSchema = z.object({
  type: z.literal("section_header"),
  id: z.string(),
  content: z.string()
});
var pageBreakBlockSchema = z.object({
  type: z.literal("page_break"),
  id: z.string()
});
var ndfFieldWidthSchema = z.enum(["full", "half", "third"]);
var baseFieldBlockSchema = z.object({
  id: z.string(),
  label: z.string(),
  placeholder: z.string().optional(),
  helpText: z.string().optional(),
  defaultValue: z.unknown().optional(),
  validation: ndfFieldValidationSchema.optional(),
  conditionalLogic: ndfConditionalLogicSchema.optional(),
  signerId: z.string().optional(),
  width: ndfFieldWidthSchema.optional()
});
var textFieldBlockSchema = baseFieldBlockSchema.extend({
  type: z.literal("field_text"),
  inputType: z.enum(["text", "textarea", "email", "phone", "number"])
});
var dateFieldBlockSchema = baseFieldBlockSchema.extend({
  type: z.literal("field_date")
});
var timeFieldBlockSchema = baseFieldBlockSchema.extend({
  type: z.literal("field_time")
});
var selectFieldBlockSchema = baseFieldBlockSchema.extend({
  type: z.literal("field_select"),
  selectType: z.enum(["dropdown", "radio", "checkbox"]),
  options: z.array(z.object({
    label: z.string(),
    value: z.string()
  }))
});
var fileFieldBlockSchema = baseFieldBlockSchema.extend({
  type: z.literal("field_file"),
  accept: z.array(z.string()).optional(),
  maxSize: z.number().int().positive().optional()
});
var signatureFieldBlockSchema = baseFieldBlockSchema.extend({
  type: z.literal("field_signature"),
  required: z.boolean(),
  position: normalizedPositionSchema.optional()
});
var pdfOverlayFieldBlockSchema = baseFieldBlockSchema.extend({
  type: z.literal("pdf_overlay"),
  overlayType: z.enum(["signature", "initials", "text", "date", "checkbox"]),
  position: normalizedPositionSchema,
  variableName: z.string().optional()
});
var ndfBlockSchema = z.discriminatedUnion("type", [
  // Content blocks
  paragraphBlockSchema,
  headingBlockSchema,
  listBlockSchema,
  imageBlockSchema,
  tableBlockSchema,
  dividerBlockSchema,
  spacerBlockSchema,
  sectionHeaderBlockSchema,
  pageBreakBlockSchema,
  // Field blocks
  textFieldBlockSchema,
  dateFieldBlockSchema,
  timeFieldBlockSchema,
  selectFieldBlockSchema,
  fileFieldBlockSchema,
  signatureFieldBlockSchema,
  // PDF overlay block
  pdfOverlayFieldBlockSchema
]);
var nexusDocumentSchema = z.object({
  version: ndfVersionSchema,
  kind: ndfKindSchema,
  meta: ndfDocumentMetaSchema,
  blocks: z.array(ndfBlockSchema),
  settings: ndfFormSettingsSchema.optional(),
  pdf: ndfPdfSourceSchema.optional(),
  signers: z.array(ndfSignerSchema).optional()
});
var NDF_CONTENT_BLOCK_TYPES = [
  "paragraph",
  "heading",
  "list",
  "image",
  "table",
  "divider",
  "spacer",
  "section_header",
  "page_break"
];
var NDF_FIELD_BLOCK_TYPES = [
  "field_text",
  "field_date",
  "field_time",
  "field_select",
  "field_file",
  "field_signature"
];
var NDF_BLOCK_TYPES = [
  ...NDF_CONTENT_BLOCK_TYPES,
  ...NDF_FIELD_BLOCK_TYPES,
  "pdf_overlay"
];
function isContentBlock(block) {
  return NDF_CONTENT_BLOCK_TYPES.includes(block.type);
}
function isFieldBlock(block) {
  return NDF_FIELD_BLOCK_TYPES.includes(block.type);
}
function isPdfOverlayBlock(block) {
  return block.type === "pdf_overlay";
}
function getFieldBlocks(doc) {
  return doc.blocks.filter(isFieldBlock);
}
function getSignatureBlocks(doc) {
  return doc.blocks.filter(
    (b) => b.type === "field_signature" || b.type === "pdf_overlay" && b.overlayType === "signature"
  );
}
function ndfFieldWidthBasis(width) {
  switch (width) {
    case "half":
      return "calc(50% - 0.5rem)";
    case "third":
      return "calc(33.333% - 0.667rem)";
    default:
      return "100%";
  }
}
function ndfFieldWidthClass(width) {
  if (width === "half") return "ndf-field--half";
  if (width === "third") return "ndf-field--third";
  return "";
}
function groupNdfFieldRows(blocks) {
  const rows = [];
  let run = [];
  const flush = () => {
    if (run.length > 0) {
      rows.push({ kind: "fields", fields: run });
      run = [];
    }
  };
  for (const b of blocks) {
    if (isFieldBlock(b)) {
      run.push(b);
    } else {
      flush();
      rows.push({ kind: "block", block: b });
    }
  }
  flush();
  return rows;
}
function extractVariableReferences(doc) {
  const variablePattern = /\{\{([^}]+)\}\}/g;
  const variables = /* @__PURE__ */ new Set();
  for (const block of doc.blocks) {
    let text = "";
    if ("content" in block && typeof block.content === "string") {
      text = block.content;
    }
    if ("items" in block && Array.isArray(block.items)) {
      text += " " + block.items.join(" ");
    }
    if ("rows" in block && Array.isArray(block.rows)) {
      text += " " + block.rows.flat().join(" ");
    }
    if ("src" in block && typeof block.src === "string") {
      text += " " + block.src;
    }
    for (const match of text.matchAll(variablePattern)) {
      const varName = match[1]?.trim();
      if (varName) variables.add(varName);
    }
  }
  return Array.from(variables);
}

// src/ndf-converters.ts
function formSchemaToNdf(schema, settings, name, description, branding) {
  const blocks = schema.fields.map((field) => formFieldToNdfBlock(field));
  const ndfSettings = {
    submitButtonText: settings.submitButtonText,
    successMessage: settings.successMessage,
    redirectUrl: settings.redirectUrl,
    notifyOnSubmission: settings.notifyOnSubmission,
    notificationEmails: settings.notificationEmails,
    webhookUrl: settings.webhookUrl,
    captureEmailConsent: settings.captureEmailConsent,
    captureSmsConsent: settings.captureSmsConsent,
    consentText: settings.consentText,
    allowMultipleSubmissions: settings.allowMultipleSubmissions,
    maxSubmissions: settings.maxSubmissions,
    requireAuth: settings.requireAuth,
    branding
  };
  const cleanSettings = JSON.parse(JSON.stringify(ndfSettings));
  return {
    version: "1.0",
    kind: "form",
    meta: {
      title: name,
      description,
      layout: schema.layout || "single"
    },
    blocks,
    settings: cleanSettings
  };
}
function formFieldToNdfBlock(field) {
  const baseProps = {
    id: field.id,
    label: field.label,
    placeholder: field.placeholder,
    helpText: field.helpText,
    defaultValue: field.defaultValue,
    validation: field.validation ? {
      required: field.validation.required,
      minLength: field.validation.minLength,
      maxLength: field.validation.maxLength,
      min: field.validation.min,
      max: field.validation.max,
      pattern: field.validation.pattern,
      customMessage: field.validation.customMessage
    } : void 0,
    conditionalLogic: field.conditionalLogic ? {
      fieldId: field.conditionalLogic.fieldId,
      operator: field.conditionalLogic.operator,
      value: field.conditionalLogic.value
    } : void 0
  };
  const clean = (obj) => JSON.parse(JSON.stringify(obj));
  switch (field.type) {
    // Text-based fields → field_text
    case "text":
    case "textarea":
    case "email":
    case "phone":
    case "number":
      return clean({ ...baseProps, type: "field_text", inputType: field.type });
    // Date/Time → field_date / field_time
    case "date":
      return clean({ ...baseProps, type: "field_date" });
    case "time":
      return clean({ ...baseProps, type: "field_time" });
    // Selection fields → field_select
    case "dropdown":
    case "radio":
    case "checkbox":
      return clean({
        ...baseProps,
        type: "field_select",
        selectType: field.type,
        options: field.options || []
      });
    // File → field_file
    case "file":
      return clean({ ...baseProps, type: "field_file" });
    // Signature → field_signature
    case "signature":
      return clean({
        ...baseProps,
        type: "field_signature",
        required: field.validation?.required ?? false
      });
    // Layout/decorative fields → content blocks
    case "section_header":
      return { type: "section_header", id: field.id, content: field.label };
    case "divider":
      return { type: "divider", id: field.id };
    case "spacer":
      return { type: "spacer", id: field.id };
    default:
      return clean({ ...baseProps, type: "field_text", inputType: "text" });
  }
}
function ndfToFormSchema(doc) {
  const fields = doc.blocks.map((block) => ndfBlockToFormField(block)).filter((f) => f !== null);
  const schema = {
    fields,
    layout: doc.meta.layout || "single"
  };
  const s = doc.settings || {};
  const settings = {
    submitButtonText: s.submitButtonText ?? "Submit",
    successMessage: s.successMessage ?? "Thank you for your submission!",
    redirectUrl: s.redirectUrl,
    notifyOnSubmission: s.notifyOnSubmission ?? true,
    notificationEmails: s.notificationEmails,
    webhookUrl: s.webhookUrl,
    captureEmailConsent: s.captureEmailConsent ?? false,
    captureSmsConsent: s.captureSmsConsent ?? false,
    consentText: s.consentText,
    allowMultipleSubmissions: s.allowMultipleSubmissions ?? true,
    maxSubmissions: s.maxSubmissions,
    requireAuth: s.requireAuth ?? false
  };
  const branding = s.branding ? {
    logoUrl: s.branding.logoUrl ?? null,
    primaryColor: s.branding.primaryColor ?? "#3b82f6",
    logoPosition: s.branding.logoPosition ?? "center",
    inputBorderRadius: s.branding.inputBorderRadius ?? 8,
    inputBorderColor: s.branding.inputBorderColor ?? null,
    inputBackgroundColor: s.branding.inputBackgroundColor ?? null,
    inputTextColor: s.branding.inputTextColor ?? null
  } : void 0;
  return { schema, settings, branding };
}
function ndfBlockToFormField(block) {
  switch (block.type) {
    case "field_text":
      return {
        id: block.id,
        type: block.inputType,
        label: block.label,
        placeholder: block.placeholder,
        helpText: block.helpText,
        defaultValue: block.defaultValue,
        validation: block.validation ? { ...block.validation, required: block.validation.required ?? false } : void 0,
        conditionalLogic: block.conditionalLogic
      };
    case "field_date":
      return {
        id: block.id,
        type: "date",
        label: block.label,
        placeholder: block.placeholder,
        helpText: block.helpText,
        defaultValue: block.defaultValue,
        validation: block.validation ? { ...block.validation, required: block.validation.required ?? false } : void 0,
        conditionalLogic: block.conditionalLogic
      };
    case "field_time":
      return {
        id: block.id,
        type: "time",
        label: block.label,
        placeholder: block.placeholder,
        helpText: block.helpText,
        defaultValue: block.defaultValue,
        validation: block.validation ? { ...block.validation, required: block.validation.required ?? false } : void 0,
        conditionalLogic: block.conditionalLogic
      };
    case "field_select":
      return {
        id: block.id,
        type: block.selectType,
        label: block.label,
        placeholder: block.placeholder,
        helpText: block.helpText,
        defaultValue: block.defaultValue,
        options: block.options,
        validation: block.validation ? { ...block.validation, required: block.validation.required ?? false } : void 0,
        conditionalLogic: block.conditionalLogic
      };
    case "field_file":
      return {
        id: block.id,
        type: "file",
        label: block.label,
        placeholder: block.placeholder,
        helpText: block.helpText,
        defaultValue: block.defaultValue,
        validation: block.validation ? { ...block.validation, required: block.validation.required ?? false } : void 0,
        conditionalLogic: block.conditionalLogic
      };
    case "field_signature":
      return {
        id: block.id,
        type: "signature",
        label: block.label,
        validation: { required: block.required },
        conditionalLogic: block.conditionalLogic
      };
    case "section_header":
      return {
        id: block.id,
        type: "section_header",
        label: block.content
      };
    case "divider":
      return {
        id: block.id,
        type: "divider",
        label: ""
      };
    case "spacer":
      return {
        id: block.id,
        type: "spacer",
        label: ""
      };
    // Content blocks and pdf_overlay don't map to legacy form fields
    case "paragraph":
    case "heading":
    case "list":
    case "image":
    case "table":
    case "page_break":
    case "pdf_overlay":
      return null;
    default:
      return null;
  }
}
function templateContentToNdf(content, name, pdfSource) {
  const blocks = [];
  const variables = content.variables.map((v) => ({
    name: v,
    label: v.replace(/\./g, " ").replace(/([A-Z])/g, " $1").trim(),
    type: "text"
  }));
  const meta = {
    title: name,
    variables: variables.length > 0 ? variables : void 0
  };
  const signers = content.signers?.map((s) => ({
    id: s.id,
    name: s.name,
    order: s.order
  }));
  const isPdf = !!pdfSource;
  if (isPdf) {
    if (content.textFields) {
      for (const tf of content.textFields) {
        blocks.push({
          type: "pdf_overlay",
          id: tf.id,
          overlayType: "text",
          label: tf.variableName,
          variableName: tf.variableName,
          position: {
            page: tf.position.page,
            x: tf.position.x,
            y: tf.position.y,
            width: tf.position.width,
            height: tf.position.height
          },
          signerId: tf.signerId
        });
      }
    }
    for (const sf of content.signatureFields) {
      const sfExtra = sf;
      const fieldType = typeof sfExtra.fieldType === "string" ? sfExtra.fieldType : "signature";
      const overlayTypeMap = {
        signature: "signature",
        initials: "initials",
        text: "text",
        text_input: "text",
        checkbox: "checkbox",
        date: "date",
        date_signed: "date",
        image: "text"
      };
      const overlayType = overlayTypeMap[fieldType] ?? "signature";
      if (sf.position) {
        blocks.push({
          type: "pdf_overlay",
          id: sf.id,
          overlayType,
          label: sf.label,
          validation: { required: sf.required },
          position: {
            page: sf.position.page,
            x: sf.position.x,
            y: sf.position.y,
            width: sf.position.width,
            height: sf.position.height
          },
          signerId: sf.signerId
        });
      } else {
        blocks.push({
          type: "field_signature",
          id: sf.id,
          label: sf.label,
          required: sf.required,
          signerId: sf.signerId
        });
      }
    }
  } else {
    for (const sf of content.signatureFields) {
      blocks.push({
        type: "field_signature",
        id: sf.id,
        label: sf.label,
        required: sf.required,
        position: sf.position ? {
          page: sf.position.page,
          x: sf.position.x,
          y: sf.position.y,
          width: sf.position.width,
          height: sf.position.height
        } : void 0,
        signerId: sf.signerId
      });
    }
  }
  const pdf = pdfSource ? {
    fileUrl: pdfSource.fileUrl,
    originalName: pdfSource.originalName,
    fileSize: pdfSource.fileSize,
    pageCount: pdfSource.pageCount
  } : void 0;
  return {
    version: "1.0",
    kind: "document",
    meta,
    blocks,
    pdf,
    signers: signers && signers.length > 0 ? signers : void 0
  };
}
function ndfBlocksToHtml(doc) {
  const htmlParts = [];
  for (const block of doc.blocks) {
    switch (block.type) {
      case "heading": {
        const level = block.level || 2;
        const tag = `h${level}`;
        htmlParts.push(`<${tag}>${escapeHtml(block.content)}</${tag}>`);
        break;
      }
      case "paragraph":
        htmlParts.push(`<p>${escapeHtml(block.content)}</p>`);
        break;
      case "list": {
        const tag = block.ordered ? "ol" : "ul";
        const items = block.items.map((item) => `<li>${escapeHtml(item)}</li>`).join("");
        htmlParts.push(`<${tag}>${items}</${tag}>`);
        break;
      }
      case "divider":
        htmlParts.push("<hr />");
        break;
      case "section_header":
        htmlParts.push(`<h3>${escapeHtml(block.content)}</h3>`);
        break;
      case "field_signature":
        htmlParts.push(`<p><strong>[Signature: ${escapeHtml(block.label)}]</strong></p>`);
        break;
    }
  }
  return htmlParts.join("\n");
}
function escapeHtml(text) {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}
function ndfToTemplateContent(doc) {
  const variables = doc.meta.variables?.map((v) => v.name) || [];
  const html = ndfBlocksToHtml(doc);
  const signatureFields = [];
  const textFields = [];
  for (const block of doc.blocks) {
    if (block.type === "field_signature") {
      signatureFields.push({
        id: block.id,
        label: block.label,
        required: block.required,
        position: block.position ? {
          page: block.position.page,
          x: block.position.x,
          y: block.position.y,
          width: block.position.width,
          height: block.position.height
        } : void 0,
        signerId: block.signerId
      });
    } else if (block.type === "pdf_overlay") {
      if (block.overlayType === "signature") {
        signatureFields.push({
          id: block.id,
          label: block.label,
          required: "required" in block ? block.required ?? true : true,
          position: {
            page: block.position.page,
            x: block.position.x,
            y: block.position.y,
            width: block.position.width,
            height: block.position.height
          },
          signerId: block.signerId
        });
      } else if (block.overlayType === "text" || block.overlayType === "date") {
        textFields.push({
          id: block.id,
          variableName: block.variableName || block.label,
          position: {
            page: block.position.page,
            x: block.position.x,
            y: block.position.y,
            width: block.position.width,
            height: block.position.height
          },
          signerId: block.signerId
        });
      }
    }
  }
  const signers = doc.signers?.map((s) => ({
    id: s.id,
    name: s.name,
    order: s.order
  }));
  return {
    html,
    variables,
    signatureFields,
    textFields: textFields.length > 0 ? textFields : void 0,
    pdfPageCount: doc.pdf?.pageCount,
    signers: signers && signers.length > 0 ? signers : void 0
  };
}
function globalTemplateContentToNdf(content, type, name, description) {
  switch (type) {
    case "form":
      return globalFormTemplateToNdf(content, name, description);
    case "document":
      return globalDocumentTemplateToNdf(content, name, description);
    case "sms":
      return globalSmsTemplateToNdf(content, name, description);
    case "email":
      return globalEmailTemplateToNdf(content, name, description);
  }
}
function globalFormTemplateToNdf(content, name, description) {
  const parsed = JSON.parse(content);
  if (parsed.version === "1.0" && parsed.kind) {
    return parsed;
  }
  if (parsed.fields && Array.isArray(parsed.fields)) {
    const blocks = parsed.fields.map((f) => {
      const field = {
        id: f.id || generateBlockId(),
        type: f.type || "text",
        label: f.label || "",
        placeholder: f.placeholder,
        helpText: f.helpText,
        options: f.options,
        validation: f.required ? { required: true } : void 0
      };
      return formFieldToNdfBlock(field);
    });
    return {
      version: "1.0",
      kind: "form",
      meta: {
        title: name || parsed.name || "Untitled Form",
        description: description || parsed.description,
        category: parsed.category
      },
      blocks
    };
  }
  return {
    version: "1.0",
    kind: "form",
    meta: { title: name },
    blocks: []
  };
}
function globalDocumentTemplateToNdf(content, name, description) {
  const parsed = JSON.parse(content);
  if (parsed.version === "1.0" && parsed.kind) {
    return parsed;
  }
  if (parsed.signatureFields || parsed.variables) {
    return templateContentToNdf(
      parsed,
      name,
      parsed.fileUrl ? {
        fileUrl: parsed.fileUrl,
        originalName: parsed.originalName || name,
        fileSize: parsed.fileSize || 0,
        pageCount: parsed.pdfPageCount || 1
      } : void 0
    );
  }
  return {
    version: "1.0",
    kind: "document",
    meta: { title: name, description },
    blocks: []
  };
}
function globalSmsTemplateToNdf(content, name, description) {
  let messageText;
  try {
    const parsed = JSON.parse(content);
    if (parsed.version === "1.0" && parsed.kind) {
      return parsed;
    }
    messageText = parsed.content || parsed.text || parsed.message || content;
  } catch {
    messageText = content;
  }
  const variablePattern = /\{\{([^}]+)\}\}/g;
  const variables = [];
  for (const match of messageText.matchAll(variablePattern)) {
    const varName = match[1]?.trim();
    if (varName && !variables.some((v) => v.name === varName)) {
      variables.push({
        name: varName,
        label: varName.replace(/\./g, " ").replace(/([A-Z])/g, " $1").trim(),
        type: "text"
      });
    }
  }
  return {
    version: "1.0",
    kind: "sms",
    meta: {
      title: name,
      description,
      variables: variables.length > 0 ? variables : void 0
    },
    blocks: [
      { type: "paragraph", id: generateBlockId(), content: messageText }
    ]
  };
}
function globalEmailTemplateToNdf(content, name, description) {
  let bodyText;
  try {
    const parsed = JSON.parse(content);
    if (parsed.version === "1.0" && parsed.kind) {
      return parsed;
    }
    bodyText = parsed.content || parsed.html || parsed.body || content;
  } catch {
    bodyText = content;
  }
  const variablePattern = /\{\{([^}]+)\}\}/g;
  const variables = [];
  for (const match of bodyText.matchAll(variablePattern)) {
    const varName = match[1]?.trim();
    if (varName && !variables.some((v) => v.name === varName)) {
      variables.push({
        name: varName,
        label: varName.replace(/\./g, " ").replace(/([A-Z])/g, " $1").trim(),
        type: "text"
      });
    }
  }
  return {
    version: "1.0",
    kind: "email",
    meta: {
      title: name,
      description,
      variables: variables.length > 0 ? variables : void 0
    },
    blocks: [
      { type: "paragraph", id: generateBlockId(), content: bodyText }
    ]
  };
}
var blockIdCounter = 0;
function generateBlockId() {
  blockIdCounter++;
  return `blk_${Date.now().toString(36)}_${blockIdCounter.toString(36)}`;
}
function resetBlockIdCounter() {
  blockIdCounter = 0;
}

// src/ndf-renderer.ts
function escapeHtml2(unsafe) {
  return unsafe.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}
function substituteVariables(text, variables, highlight = false) {
  return text.replace(/\{\{([^}]+)\}\}/g, (match, varName) => {
    const trimmed = (varName ?? "").trim();
    const resolved = trimmed && variables ? variables[trimmed] : void 0;
    if (resolved !== void 0) {
      return `<strong class="ndf-var-resolved">${escapeHtml2(resolved)}</strong>`;
    }
    if (highlight) {
      return `<span class="ndf-var-token">${escapeHtml2(match)}</span>`;
    }
    return escapeHtml2(match);
  });
}
function textStyleToCss(style) {
  if (!style) return "";
  const parts = [];
  if (style.bold) parts.push("font-weight:700");
  if (style.italic) parts.push("font-style:italic");
  if (style.align) parts.push(`text-align:${style.align}`);
  if (style.fontSize) parts.push(`font-size:${style.fontSize}px`);
  return parts.length > 0 ? ` style="${parts.join(";")}"` : "";
}
function renderFieldLabel(label, required, variables, highlight = false) {
  const content = substituteVariables(label, variables, highlight);
  const requiredMark = required ? ' <span class="ndf-required">*</span>' : "";
  return `<label class="ndf-label">${content}${requiredMark}</label>`;
}
function renderHelpText(helpText, variables, highlight = false) {
  if (!helpText) return "";
  return `<p class="ndf-help-text">${substituteVariables(helpText, variables, highlight)}</p>`;
}
function renderFilledFieldValue(value) {
  if (value === null || value === void 0) return "";
  if (Array.isArray(value)) {
    return value.map((v) => escapeHtml2(String(v))).join(", ");
  }
  if (typeof value === "object") {
    const name = value.name;
    if (name) return escapeHtml2(name);
    return "";
  }
  return escapeHtml2(String(value));
}
function renderSelectValue(options, value) {
  const toLabel = (v) => {
    const opt = options.find((o) => o.value === v);
    return escapeHtml2(opt ? opt.label : v);
  };
  if (Array.isArray(value)) return value.map((v) => toLabel(String(v))).join(", ");
  return toLabel(String(value ?? ""));
}
function renderBlock(block, variables, highlight = false, fieldData) {
  const filledValue = fieldData?.fieldValues?.[block.id];
  const hasFilledValue = filledValue !== void 0 && filledValue !== null && filledValue !== "";
  switch (block.type) {
    // ── Content Blocks ──────────────────────────────
    case "paragraph": {
      const css = textStyleToCss(block.style);
      const content = substituteVariables(block.content, variables, highlight);
      return `<p class="ndf-paragraph"${css}>${content}</p>`;
    }
    case "heading": {
      const tag = `h${block.level}`;
      const content = substituteVariables(block.content, variables, highlight);
      return `<${tag} class="ndf-heading ndf-${tag}">${content}</${tag}>`;
    }
    case "list": {
      const tag = block.ordered ? "ol" : "ul";
      const items = block.items.map((item) => `<li>${substituteVariables(item, variables, highlight)}</li>`).join("\n");
      return `<${tag} class="ndf-list">
${items}
</${tag}>`;
    }
    case "image": {
      const alt = block.alt ? ` alt="${escapeHtml2(block.alt)}"` : ' alt=""';
      const width = block.width ? ` width="${block.width}"` : "";
      const height = block.height ? ` height="${block.height}"` : "";
      const src = substituteVariables(block.src, variables);
      return `<img class="ndf-image" src="${src}"${alt}${width}${height} />`;
    }
    case "table": {
      const thead = block.columns.map((col) => `<th>${substituteVariables(col.header, variables, highlight)}</th>`).join("");
      const tbody = block.rows.map(
        (row) => "<tr>" + row.map((cell) => `<td>${substituteVariables(cell, variables, highlight)}</td>`).join("") + "</tr>"
      ).join("\n");
      return `<table class="ndf-table">
<thead><tr>${thead}</tr></thead>
<tbody>
${tbody}
</tbody>
</table>`;
    }
    case "divider":
      return '<hr class="ndf-divider" />';
    case "spacer": {
      const height = block.height ?? 24;
      return `<div class="ndf-spacer" style="height:${height}px"></div>`;
    }
    case "section_header": {
      const content = substituteVariables(block.content, variables, highlight);
      return `<div class="ndf-section-header">${content}</div>`;
    }
    case "page_break":
      return '<div class="ndf-page-break"></div>';
    // ── Field Blocks ────────────────────────────────
    case "field_text": {
      const isRequired = block.validation?.required;
      const placeholder = block.placeholder ? ` placeholder="${escapeHtml2(block.placeholder)}"` : "";
      const name = ` name="${escapeHtml2(block.id)}"`;
      let input;
      if (hasFilledValue) {
        const multiline = block.inputType === "textarea";
        input = `<div class="ndf-field-value${multiline ? " ndf-field-value--multiline" : ""}">${renderFilledFieldValue(filledValue)}</div>`;
      } else if (block.inputType === "textarea") {
        input = `<textarea class="ndf-input ndf-textarea"${name}${placeholder}></textarea>`;
      } else {
        const typeMap = {
          text: "text",
          email: "email",
          phone: "tel",
          number: "number"
        };
        const inputType = typeMap[block.inputType] ?? "text";
        input = `<input class="ndf-input" type="${inputType}"${name}${placeholder} />`;
      }
      return `<div class="ndf-field" data-field-id="${escapeHtml2(block.id)}">
${renderFieldLabel(block.label, isRequired, variables, highlight)}
${input}
${renderHelpText(block.helpText, variables, highlight)}
</div>`;
    }
    case "field_date": {
      const isRequired = block.validation?.required;
      const name = ` name="${escapeHtml2(block.id)}"`;
      const input = hasFilledValue ? `<div class="ndf-field-value">${renderFilledFieldValue(filledValue)}</div>` : `<input class="ndf-input" type="date"${name} />`;
      return `<div class="ndf-field" data-field-id="${escapeHtml2(block.id)}">
${renderFieldLabel(block.label, isRequired, variables, highlight)}
${input}
${renderHelpText(block.helpText, variables, highlight)}
</div>`;
    }
    case "field_time": {
      const isRequired = block.validation?.required;
      const name = ` name="${escapeHtml2(block.id)}"`;
      const input = hasFilledValue ? `<div class="ndf-field-value">${renderFilledFieldValue(filledValue)}</div>` : `<input class="ndf-input" type="time"${name} />`;
      return `<div class="ndf-field" data-field-id="${escapeHtml2(block.id)}">
${renderFieldLabel(block.label, isRequired, variables, highlight)}
${input}
${renderHelpText(block.helpText, variables, highlight)}
</div>`;
    }
    case "field_select": {
      const isRequired = block.validation?.required;
      const name = ` name="${escapeHtml2(block.id)}"`;
      if (hasFilledValue) {
        const input2 = `<div class="ndf-field-value">${renderSelectValue(block.options, filledValue)}</div>`;
        return `<div class="ndf-field" data-field-id="${escapeHtml2(block.id)}">
${renderFieldLabel(block.label, isRequired, variables, highlight)}
${input2}
${renderHelpText(block.helpText, variables, highlight)}
</div>`;
      }
      let input;
      if (block.selectType === "dropdown") {
        const options = block.options.map(
          (opt) => `<option value="${escapeHtml2(opt.value)}">${escapeHtml2(opt.label)}</option>`
        ).join("\n");
        input = `<select class="ndf-input ndf-select"${name}>
<option value="">Select...</option>
${options}
</select>`;
      } else {
        const type = block.selectType === "radio" ? "radio" : "checkbox";
        input = block.options.map(
          (opt) => `<label class="ndf-option"><input type="${type}"${name} value="${escapeHtml2(opt.value)}" /> ${escapeHtml2(opt.label)}</label>`
        ).join("\n");
        input = `<div class="ndf-option-group">
${input}
</div>`;
      }
      return `<div class="ndf-field" data-field-id="${escapeHtml2(block.id)}">
${renderFieldLabel(block.label, isRequired, variables, highlight)}
${input}
${renderHelpText(block.helpText, variables, highlight)}
</div>`;
    }
    case "field_file": {
      const isRequired = block.validation?.required;
      const name = ` name="${escapeHtml2(block.id)}"`;
      const accept = block.accept ? ` accept="${escapeHtml2(block.accept.join(","))}"` : "";
      let input;
      if (hasFilledValue) {
        const fileUrl = typeof filledValue === "object" && filledValue ? filledValue.url : void 0;
        const isImage = typeof fileUrl === "string" && /^https?:|^data:image\//.test(fileUrl) && /\.(png|jpe?g|gif|webp)(\?|$)/i.test(fileUrl);
        input = isImage ? `<div class="ndf-field-value"><img class="ndf-signature-image" src="${fileUrl}" alt="Uploaded file" /></div>` : `<div class="ndf-field-value">${renderFilledFieldValue(filledValue)}</div>`;
      } else {
        input = `<input class="ndf-input ndf-file-input" type="file"${name}${accept} />`;
      }
      return `<div class="ndf-field" data-field-id="${escapeHtml2(block.id)}">
${renderFieldLabel(block.label, isRequired, variables, highlight)}
${input}
${renderHelpText(block.helpText, variables, highlight)}
</div>`;
    }
    case "field_signature": {
      const sig = fieldData?.signatures?.[block.id];
      const isValidSig = sig?.imageDataUrl?.startsWith("data:image/");
      const area = isValidSig ? `<div class="ndf-signature-area ndf-signature-area--signed"><img class="ndf-signature-image" src="${sig.imageDataUrl}" alt="Signature" /></div>` + (sig.caption ? `
<div class="ndf-signature-caption">${escapeHtml2(sig.caption)}</div>` : "") : `<div class="ndf-signature-area">Sign here</div>`;
      return `<div class="ndf-field ndf-signature-pad" data-field-id="${escapeHtml2(block.id)}">
${renderFieldLabel(block.label, block.required, variables, highlight)}
${area}
${renderHelpText(block.helpText, variables, highlight)}
</div>`;
    }
    case "pdf_overlay":
      return "";
    default:
      return "";
  }
}
function renderNdfToHtml(doc, variables, options) {
  const highlight = options?.highlightVariables ?? false;
  const fieldData = options?.signatures || options?.fieldValues ? { signatures: options.signatures, fieldValues: options.fieldValues } : void 0;
  const blocks = groupNdfFieldRows(doc.blocks).map((row) => {
    if (row.kind === "block") {
      return renderBlock(row.block, variables, highlight, fieldData);
    }
    const cells = row.fields.map((f) => {
      const inner = renderBlock(f, variables, highlight, fieldData);
      if (!inner) return "";
      const wc = ndfFieldWidthClass(f.width);
      return `<div class="ndf-field-cell${wc ? ` ${wc}` : ""}">${inner}</div>`;
    }).filter((h) => h.length > 0).join("\n");
    return cells ? `<div class="ndf-field-row">
${cells}
</div>` : "";
  }).filter((html) => html.length > 0).join("\n");
  const layoutClass = doc.meta.layout === "two-column" ? "ndf-layout-two-column" : "ndf-layout-single";
  return `<div class="ndf-document ${layoutClass}" data-ndf-kind="${escapeHtml2(doc.kind)}">
${blocks}
</div>`;
}
function getNdfStyles() {
  return `<style>
/* \u2500\u2500\u2500 NDF Document Styles \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */
.ndf-document {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  line-height: 1.6;
  color: #1c1917;
  max-width: 800px;
  margin: 0 auto;
}
.ndf-layout-two-column {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}
.ndf-layout-single {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* \u2500\u2500\u2500 Content Blocks \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */
.ndf-paragraph {
  margin: 0;
  padding: 0;
}
.ndf-heading {
  margin: 0;
  font-weight: 700;
  color: #0c0a09;
}
.ndf-h1 { font-size: 1.875rem; line-height: 2.25rem; }
.ndf-h2 { font-size: 1.5rem; line-height: 2rem; }
.ndf-h3 { font-size: 1.25rem; line-height: 1.75rem; }
.ndf-h4 { font-size: 1.125rem; line-height: 1.5rem; }
.ndf-list {
  margin: 0;
  padding-left: 1.5rem;
}
.ndf-list li {
  margin-bottom: 0.25rem;
}
.ndf-image {
  max-width: 100%;
  height: auto;
  border-radius: 0.5rem;
}
.ndf-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}
.ndf-table th,
.ndf-table td {
  border: 1px solid #d6d3d1;
  padding: 0.5rem 0.75rem;
  text-align: left;
}
.ndf-table th {
  background-color: #f5f5f4;
  font-weight: 600;
}
.ndf-table tbody tr:nth-child(even) {
  background-color: #fafaf9;
}
.ndf-divider {
  border: none;
  border-top: 1px solid #d6d3d1;
  margin: 0.5rem 0;
}
.ndf-spacer {
  /* height set inline */
}
.ndf-section-header {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #57534e;
  border-left: 3px solid #6366f1;
  padding-left: 0.75rem;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}
.ndf-page-break {
  border-top: 2px dashed #a8a29e;
  margin: 1rem 0;
  position: relative;
}
.ndf-page-break::after {
  content: 'Page Break';
  position: absolute;
  top: -0.6em;
  left: 50%;
  transform: translateX(-50%);
  background: #fff;
  padding: 0 0.5rem;
  font-size: 0.6875rem;
  color: #a8a29e;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* \u2500\u2500\u2500 Field rows (side-by-side half/third widths) \u2500\u2500\u2500 */
.ndf-field-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-start;
}
.ndf-field-cell {
  flex: 1 1 100%;
  min-width: 0;
}
.ndf-field-cell.ndf-field--half {
  flex: 0 1 calc(50% - 0.5rem);
}
.ndf-field-cell.ndf-field--third {
  flex: 0 1 calc(33.333% - 0.667rem);
}
@media (max-width: 640px) {
  .ndf-field-cell.ndf-field--half,
  .ndf-field-cell.ndf-field--third {
    flex-basis: 100%;
  }
}

/* \u2500\u2500\u2500 Field Blocks \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */
.ndf-field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.ndf-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #44403c;
}
.ndf-required {
  color: #ef4444;
}
.ndf-input {
  display: block;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d6d3d1;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: #1c1917;
  background-color: #fff;
  box-sizing: border-box;
}
.ndf-input:focus {
  outline: 2px solid #6366f1;
  outline-offset: -1px;
  border-color: #6366f1;
}
.ndf-textarea {
  min-height: 5rem;
  resize: vertical;
}
.ndf-select {
  appearance: auto;
}
.ndf-file-input {
  padding: 0.375rem;
}
.ndf-option-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.ndf-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #44403c;
  cursor: pointer;
}
.ndf-option input {
  width: 1rem;
  height: 1rem;
}
.ndf-help-text {
  font-size: 0.75rem;
  color: #78716c;
  margin: 0;
}
.ndf-signature-pad {
  /* extends .ndf-field */
  break-inside: avoid;
  page-break-inside: avoid;
}
.ndf-field {
  break-inside: avoid;
  page-break-inside: avoid;
}
.ndf-signature-area {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 6rem;
  border: 2px dashed #d6d3d1;
  border-radius: 0.5rem;
  color: #a8a29e;
  font-size: 0.875rem;
  background-color: #fafaf9;
}
/* A signed signature area \u2014 holds the baked signature image (solid, not dashed) */
.ndf-signature-area--signed {
  border-style: solid;
  border-color: #d6d3d1;
  background-color: #fff;
  padding: 0.5rem;
}
.ndf-signature-image {
  max-height: 5.5rem;
  max-width: 100%;
  object-fit: contain;
  display: block;
  margin: 0 auto;
}
.ndf-signature-caption {
  margin-top: 0.25rem;
  font-size: 0.6875rem;
  color: #57534e;
  text-align: center;
}
/* A signer-entered value baked into the signed document (text/date/select/file) */
.ndf-field-value {
  display: block;
  width: 100%;
  padding: 0.4rem 0.65rem;
  border-bottom: 1px solid #44403c;
  font-size: 0.875rem;
  line-height: 1.4;
  color: #1c1917;
  min-height: 1.4em;
  box-sizing: border-box;
}
.ndf-field-value--multiline {
  white-space: pre-wrap;
  border: 1px solid #d6d3d1;
  border-radius: 0.375rem;
  min-height: 3rem;
}

/* \u2500\u2500\u2500 Print \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */
@media print {
  .ndf-page-break {
    page-break-before: always;
    border: none;
    margin: 0;
    height: 0;
  }
  .ndf-page-break::after {
    display: none;
  }
}

/* \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550
   Legal / Professional Document Overrides
   Scoped to [data-ndf-kind="document"] so forms,
   emails, and SMS kinds are completely unaffected.
   \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 */

/* Root \u2014 serif font, block flow (not flex gap) */
.ndf-document[data-ndf-kind="document"] {
  font-family: 'Times New Roman', Georgia, 'Palatino Linotype', serif;
  font-size: 12pt;
  line-height: 1.55;
  color: #1a1a1a;
  display: block;
}

/* Paragraphs \u2014 justified text, proper paragraph spacing */
.ndf-document[data-ndf-kind="document"] .ndf-paragraph {
  text-align: justify;
  margin: 0 0 0.9em 0;
  padding: 0;
  hyphens: auto;
}

/* Resolved merge values \u2014 bold so filled-in data stands out from the template prose */
.ndf-var-resolved {
  font-weight: 700;
}

/* Unresolved {{merge}} tokens \u2014 bold indigo pill, shown ONLY on the editor/preview
   canvas (renderNdfToHtml called with highlightVariables:true). The span is never
   emitted by the PDF worker or signer portal, so this rule is inert there. */
.ndf-var-token {
  color: #4f46e5;
  font-weight: 700;
  background: #eef2ff;
  border-radius: 3px;
  padding: 0 2px;
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
}

/* H1 \u2014 Document title: bold, centred, ALL CAPS */
.ndf-document[data-ndf-kind="document"] .ndf-h1 {
  font-size: 14pt;
  font-weight: 700;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0 0 0.3em 0;
  line-height: 1.3;
}

/* H2 \u2014 Numbered section headings: bold, blue underline */
.ndf-document[data-ndf-kind="document"] .ndf-h2 {
  font-size: 11pt;
  font-weight: 700;
  text-align: left;
  text-decoration: underline;
  text-decoration-color: #1e40af;
  text-underline-offset: 3px;
  margin: 1.5em 0 0.55em 0;
  line-height: 1.35;
  text-transform: none;
}

/* H3 / H4 \u2014 Sub-section headings */
.ndf-document[data-ndf-kind="document"] .ndf-h3,
.ndf-document[data-ndf-kind="document"] .ndf-h4 {
  font-size: 11pt;
  font-weight: 700;
  margin: 1.1em 0 0.45em 0;
  line-height: 1.35;
}

/* Lists \u2014 alphabetic lettered clauses (a), (b), (c)\u2026 */
.ndf-document[data-ndf-kind="document"] .ndf-list {
  list-style-type: none;
  padding-left: 0;
  margin: 0 0 0.9em 0;
}
.ndf-document[data-ndf-kind="document"] .ndf-list li {
  text-align: justify;
  margin-bottom: 0.45em;
  padding-left: 1.5em;
  hyphens: auto;
}

/* Divider \u2014 accent rule before signature block */
.ndf-document[data-ndf-kind="document"] .ndf-divider {
  border: none;
  border-top: 1.5px solid #1e40af;
  margin: 1.8em 0;
}

/* Section header \u2014 plain bold text (no left-border widget) */
.ndf-document[data-ndf-kind="document"] .ndf-section-header {
  font-family: 'Times New Roman', Georgia, 'Palatino Linotype', serif;
  font-size: 11pt;
  font-weight: 700;
  text-transform: none;
  letter-spacing: normal;
  border-left: none;
  padding-left: 0;
  padding-top: 0;
  padding-bottom: 0;
  color: inherit;
  margin: 1.1em 0 0.45em 0;
}

/* Spacer \u2014 honoured as-is */
.ndf-document[data-ndf-kind="document"] .ndf-spacer {
  display: block;
}

/* Table \u2014 formal table styling for legal docs */
.ndf-document[data-ndf-kind="document"] .ndf-table {
  font-family: 'Times New Roman', Georgia, 'Palatino Linotype', serif;
  font-size: 11pt;
  border-collapse: collapse;
  width: 100%;
  margin-bottom: 0.9em;
}
.ndf-document[data-ndf-kind="document"] .ndf-table th,
.ndf-document[data-ndf-kind="document"] .ndf-table td {
  border: 1px solid #555;
  padding: 0.4em 0.65em;
  text-align: left;
  vertical-align: top;
}
.ndf-document[data-ndf-kind="document"] .ndf-table th {
  background-color: #f0f0f0;
  font-weight: 700;
}
</style>`;
}
var contactCollectionFormSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string().min(1).max(200),
  slug: z.string().min(1).max(100),
  isEnabled: z.boolean().default(true),
  // Consent customization
  smsConsentText: z.string().max(1e3).optional().nullable(),
  emailConsentText: z.string().max(1e3).optional().nullable(),
  voiceConsentText: z.string().max(1e3).optional().nullable(),
  showSmsConsent: z.boolean().default(true),
  showEmailConsent: z.boolean().default(true),
  showVoiceConsent: z.boolean().default(false),
  // Purpose toggles per channel
  smsShowInformational: z.boolean().default(true),
  smsShowMarketing: z.boolean().default(true),
  emailShowInformational: z.boolean().default(true),
  emailShowMarketing: z.boolean().default(true),
  voiceShowInformational: z.boolean().default(true),
  voiceShowMarketing: z.boolean().default(false),
  // Form customization
  headerText: z.string().max(200).optional().nullable(),
  welcomeMessage: z.string().max(2e3).optional().nullable(),
  successMessage: z.string().max(1e3).optional().nullable(),
  logoUrl: z.string().url().optional().nullable(),
  primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional().nullable(),
  // Include custom fields toggle
  includeCustomFields: z.boolean().default(true),
  // QR Code
  qrCodeUrl: z.string().optional().nullable(),
  submissionCount: z.number().int().default(0),
  createdAt: z.date(),
  updatedAt: z.date()
});
var createContactCollectionFormSchema = contactCollectionFormSchema.omit({
  id: true,
  tenantId: true,
  qrCodeUrl: true,
  submissionCount: true,
  createdAt: true,
  updatedAt: true
});
var updateContactCollectionFormSchema = createContactCollectionFormSchema.partial();
var contactCollectionSubmissionSchema = z.object({
  id: z.string().uuid(),
  formId: z.string().uuid(),
  contactId: z.string().uuid().optional().nullable(),
  data: z.record(z.unknown()),
  // Captured consent
  smsInformational: z.boolean().default(false),
  smsMarketing: z.boolean().default(false),
  emailInformational: z.boolean().default(false),
  emailMarketing: z.boolean().default(false),
  voiceInformational: z.boolean().default(false),
  voiceMarketing: z.boolean().default(false),
  ipAddress: z.string().optional().nullable(),
  userAgent: z.string().optional().nullable(),
  submittedAt: z.date()
});
var contactCollectionFormSubmissionSchema = z.object({
  // Contact fields
  firstName: z.string().min(1, "First name is required").max(100),
  lastName: z.string().max(100).optional(),
  email: z.string().email("Invalid email").optional(),
  phone: z.string().min(10, "Phone must be at least 10 digits").optional(),
  address: z.string().max(200).optional(),
  city: z.string().max(100).optional(),
  state: z.string().max(100).optional(),
  zipCode: z.string().max(15).optional(),
  country: z.string().max(100).optional(),
  // Custom fields (dynamic)
  customFields: z.record(z.unknown()).optional(),
  // Consent
  consent: z.object({
    sms: channelConsentSchema.optional(),
    email: channelConsentSchema.optional(),
    voice: channelConsentSchema.optional()
  }).optional(),
  // Honeypot field (should be empty)
  website: z.string().max(0, "Bot detected").optional()
}).refine(
  (data) => data.email || data.phone,
  { message: "Either email or phone is required", path: ["email"] }
);
var publicFormConfigSchema = z.object({
  name: z.string(),
  headerText: z.string().optional().nullable(),
  welcomeMessage: z.string().optional().nullable(),
  successMessage: z.string().optional().nullable(),
  logoUrl: z.string().optional().nullable(),
  primaryColor: z.string().optional().nullable(),
  // Which consent options to show
  showSmsConsent: z.boolean(),
  showEmailConsent: z.boolean(),
  showVoiceConsent: z.boolean(),
  // Purpose toggles
  smsShowInformational: z.boolean(),
  smsShowMarketing: z.boolean(),
  emailShowInformational: z.boolean(),
  emailShowMarketing: z.boolean(),
  voiceShowInformational: z.boolean(),
  voiceShowMarketing: z.boolean(),
  // Consent text
  smsConsentText: z.string().optional().nullable(),
  emailConsentText: z.string().optional().nullable(),
  voiceConsentText: z.string().optional().nullable(),
  // Custom fields definitions
  customFields: z.array(z.object({
    fieldKey: z.string(),
    name: z.string(),
    fieldType: z.string(),
    isRequired: z.boolean(),
    options: z.array(z.string()).optional().nullable(),
    placeholder: z.string().optional().nullable(),
    helpText: z.string().optional().nullable()
  })).optional(),
  // Branding
  companyName: z.string().optional()
});

// src/submitter-name.ts
var DISQUALIFYING_TOKENS = /* @__PURE__ */ new Set([
  "company",
  "companies",
  "business",
  "organization",
  "organisation",
  "org",
  "employer",
  "product",
  "brand",
  "agency",
  "practice",
  "school",
  "account",
  "username",
  "file",
  "filename",
  "vehicle",
  "property",
  "pet",
  "project",
  "corporation",
  "llc",
  "trust",
  "department",
  "street",
  "city",
  "state",
  "country",
  "bank",
  "plan",
  "policy",
  "domain",
  "website"
]);
var NAME_TOKENS = /* @__PURE__ */ new Set(["name", "names", "named"]);
var PERSON_TOKENS = /* @__PURE__ */ new Set([
  "applicant",
  "insured",
  "policyholder",
  "signer",
  "signee",
  "signatory",
  "respondent",
  "submitter",
  "patient",
  "client",
  "customer",
  "member",
  "employee",
  "participant",
  "guest",
  "owner",
  "holder",
  "contact",
  "requester",
  "requestor",
  "buyer",
  "seller",
  "student",
  "driver"
]);
var FIRST_TOKENS = /* @__PURE__ */ new Set(["first", "given", "forename"]);
var LAST_TOKENS = /* @__PURE__ */ new Set(["last", "family"]);
var STANDALONE_FIRST = /* @__PURE__ */ new Set(["firstname", "givenname", "forename"]);
var STANDALONE_LAST = /* @__PURE__ */ new Set(["lastname", "surname", "familyname"]);
var MAX_NAME_LENGTH = 80;
function tokenize(label) {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim().split(" ").filter(Boolean);
}
function scoreNameLabel(label) {
  const tokens = tokenize(label || "");
  if (tokens.length === 0) return 0;
  if (tokens.some((t) => DISQUALIFYING_TOKENS.has(t))) return -1;
  let score = 0;
  if (tokens.some((t) => NAME_TOKENS.has(t)) || tokens.some((t) => STANDALONE_FIRST.has(t) || STANDALONE_LAST.has(t))) {
    score += 5;
  }
  if (tokens.some((t) => PERSON_TOKENS.has(t))) score += 4;
  return score;
}
function classifyNameLabel(label) {
  const tokens = tokenize(label || "");
  if (tokens.length === 0) return null;
  if (tokens.some((t) => DISQUALIFYING_TOKENS.has(t))) return null;
  const hasNameToken = tokens.some((t) => NAME_TOKENS.has(t));
  if (tokens.some((t) => STANDALONE_FIRST.has(t)) || hasNameToken && tokens.some((t) => FIRST_TOKENS.has(t))) {
    return "first";
  }
  if (tokens.some((t) => STANDALONE_LAST.has(t)) || hasNameToken && tokens.some((t) => LAST_TOKENS.has(t))) {
    return "last";
  }
  return scoreNameLabel(label) > 0 ? "full" : null;
}
function sanitizeName(value) {
  if (typeof value !== "string" && typeof value !== "number") return null;
  const cleaned = String(value).replace(/\s+/g, " ").trim();
  if (!cleaned || cleaned.length > MAX_NAME_LENGTH) return null;
  if (cleaned.includes("@")) return null;
  if (!/[a-z]/i.test(cleaned)) return null;
  return cleaned;
}
function splitFullName(fullName) {
  const parts = fullName.split(" ");
  return {
    firstName: parts[0],
    lastName: parts.length > 1 ? parts.slice(1).join(" ") : void 0
  };
}
function deriveNameParts(fields, values) {
  if (!Array.isArray(fields) || fields.length === 0 || !values || typeof values !== "object") {
    return null;
  }
  let firstName;
  let lastName;
  let best;
  let bestScore = 0;
  for (const field of fields) {
    if (!field?.isText || typeof field.id !== "string") continue;
    const value = sanitizeName(values[field.id]);
    if (!value) continue;
    const kind = classifyNameLabel(field.label || "");
    if (kind === "first") {
      if (!firstName) firstName = value;
    } else if (kind === "last") {
      if (!lastName) lastName = value;
    } else if (kind === "full") {
      const score = scoreNameLabel(field.label || "");
      if (score > bestScore) {
        best = value;
        bestScore = score;
      }
    }
  }
  if (firstName || lastName) return { firstName, lastName };
  if (best) return splitFullName(best);
  return null;
}
function deriveSubmitterName(fields, values) {
  const parts = deriveNameParts(fields, values);
  if (!parts) return null;
  const name = [parts.firstName, parts.lastName].filter(Boolean).join(" ").trim();
  return name || null;
}
function parseObject(source) {
  if (!source) return null;
  if (typeof source === "object") return source;
  if (typeof source !== "string") return null;
  try {
    const parsed = JSON.parse(source);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}
function parseSubmissionData(source) {
  return parseObject(source);
}
function nameFieldsFromLegacySchema(source) {
  const schema = parseObject(source);
  const fields = schema?.fields;
  if (!Array.isArray(fields)) return [];
  return fields.filter((f) => !!f && typeof f === "object").filter((f) => typeof f.id === "string" && typeof f.label === "string").map((f) => ({
    id: f.id,
    label: f.label,
    isText: f.type === "text" || f.type === "textarea"
  }));
}
function nameFieldsFromNdfSchema(source) {
  const doc = parseObject(source);
  const blocks = doc?.blocks;
  if (!Array.isArray(blocks)) return [];
  return blocks.filter((b) => !!b && typeof b === "object").filter((b) => b.type === "field_text" && typeof b.id === "string" && typeof b.label === "string").map((b) => ({
    id: b.id,
    label: b.label,
    isText: b.inputType === void 0 || b.inputType === "text" || b.inputType === "textarea"
  }));
}
function nameFieldsFromForm(form) {
  if (!form) return [];
  const ndfFields = nameFieldsFromNdfSchema(form.ndfSchema);
  return ndfFields.length > 0 ? ndfFields : nameFieldsFromLegacySchema(form.schema);
}
var smsTemplateTypeSchema = z.enum(["document", "form"]);
var smsTemplateSchema = z.object({
  id: z.string().uuid(),
  type: smsTemplateTypeSchema,
  name: z.string().min(1),
  body: z.string().min(1),
  variables: z.array(z.string()),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createSmsTemplateSchema = z.object({
  type: smsTemplateTypeSchema,
  name: z.string().min(1).max(200),
  body: z.string().min(1).max(1600),
  isDefault: z.boolean().optional().default(false),
  isActive: z.boolean().optional().default(true)
});
var updateSmsTemplateSchema = createSmsTemplateSchema.partial().omit({ type: true });
var CONTACT_SMS_VARIABLES = [
  {
    key: "firstName",
    label: "First Name",
    placeholder: "{{firstName}}",
    group: "contact",
    description: "Recipient's first name"
  },
  {
    key: "lastName",
    label: "Last Name",
    placeholder: "{{lastName}}",
    group: "contact",
    description: "Recipient's last name"
  },
  {
    key: "fullName",
    label: "Full Name",
    placeholder: "{{fullName}}",
    group: "contact",
    description: "Recipient's full name"
  }
];
var ORGANISATION_SMS_VARIABLES = [
  {
    key: "orgName",
    label: "Organization Name",
    placeholder: "{{orgName}}",
    group: "organisation",
    description: "Your organization name"
  },
  {
    key: "orgCity",
    label: "City",
    placeholder: "{{orgCity}}",
    group: "organisation",
    description: "Your organization city"
  },
  {
    key: "orgState",
    label: "State / Province",
    placeholder: "{{orgState}}",
    group: "organisation",
    description: "Your organization state or province"
  },
  {
    key: "orgCountry",
    label: "Country",
    placeholder: "{{orgCountry}}",
    group: "organisation",
    description: "Your organization country (ISO-2)"
  }
];
var DOCUMENT_SPECIFIC_VARIABLES = [
  {
    key: "documentName",
    label: "Document Name",
    placeholder: "{{documentName}}",
    group: "document",
    description: "Name of the document to be signed"
  },
  {
    key: "signingUrl",
    label: "Signing URL",
    placeholder: "{{signingUrl}}",
    group: "document",
    description: "Link for the recipient to sign the document"
  },
  {
    key: "expiresAt",
    label: "Expiry Date",
    placeholder: "{{expiresAt}}",
    group: "document",
    description: "Date the signing link expires"
  }
];
var FORM_SPECIFIC_VARIABLES = [
  {
    key: "formName",
    label: "Form Name",
    placeholder: "{{formName}}",
    group: "form",
    description: "Name of the form to be filled"
  },
  {
    key: "formUrl",
    label: "Form URL",
    placeholder: "{{formUrl}}",
    group: "form",
    description: "Link for the recipient to fill the form"
  }
];
var DOCUMENT_SMS_VARIABLES = [
  ...CONTACT_SMS_VARIABLES,
  ...DOCUMENT_SPECIFIC_VARIABLES,
  ...ORGANISATION_SMS_VARIABLES
];
var FORM_SMS_VARIABLES = [
  ...CONTACT_SMS_VARIABLES,
  ...FORM_SPECIFIC_VARIABLES,
  ...ORGANISATION_SMS_VARIABLES
];

// src/merge-tags.ts
var MERGE_TAG_NAMESPACES = ["custom.", "org."];
var SYSTEM_MERGE_TAGS = [
  // Contact
  { key: "fullName", tag: "full_name", label: "Full Name", category: "contact", sample: "John Smith", context: "always" },
  { key: "firstName", tag: "first_name", label: "First Name", category: "contact", sample: "John", context: "always" },
  { key: "lastName", tag: "last_name", label: "Last Name", category: "contact", sample: "Smith", context: "always" },
  { key: "email", tag: "email", label: "Email", category: "contact", sample: "john@example.com", context: "always" },
  { key: "phone", tag: "phone", label: "Phone", category: "contact", sample: "(555) 123-4567", context: "always" },
  // Organization
  { key: "company", tag: "company", label: "Company Name", category: "company", sample: "Acme Corp", context: "always" },
  { key: "companyAddress", tag: "company_address", label: "Company Address", category: "company", sample: "123 Main St", context: "always" },
  { key: "companyCity", tag: "company_city", label: "Company City", category: "company", sample: "San Francisco", context: "always" },
  { key: "companyState", tag: "company_state", label: "Company State", category: "company", sample: "CA", context: "always" },
  { key: "companyZip", tag: "company_zip", label: "Company ZIP", category: "company", sample: "94102", context: "always" },
  { key: "companyPhone", tag: "company_phone", label: "Company Phone", category: "company", sample: "(800) 555-0100", context: "always", advertised: false },
  { key: "orgCountry", tag: "org_country", label: "Company Country", category: "company", sample: "US", context: "always" },
  { key: "orgName", tag: "org_name", label: "Organization Name", category: "company", sample: "Acme Corp", context: "always" },
  { key: "tenantName", tag: "tenant_name", label: "Organization Name (alias)", category: "company", sample: "Acme Corp", context: "always", advertised: false },
  { key: "senderName", tag: "sender_name", label: "Sender Name", category: "company", sample: "Acme Corp", context: "always" },
  { key: "senderEmail", tag: "sender_email", label: "Sender Email", category: "company", sample: "hello@acme.com", context: "always" },
  { key: "senderPhone", tag: "sender_phone", label: "Sender Phone", category: "company", sample: "(800) 555-0100", context: "always", advertised: false },
  { key: "bookingLink", tag: "booking_link", label: "Booking Link", category: "company", sample: "https://calendly.com/company", context: "always" },
  // Contact address
  { key: "fullAddress", tag: "full_address", label: "Full Address", category: "address", sample: "123 Main Street, San Francisco, CA 94102", context: "always" },
  { key: "address", tag: "address", label: "Street Address", category: "address", sample: "123 Main Street", context: "always" },
  { key: "city", tag: "city", label: "City", category: "address", sample: "San Francisco", context: "always" },
  { key: "state", tag: "state", label: "State", category: "address", sample: "CA", context: "always" },
  { key: "zip", tag: "zip", label: "ZIP Code", category: "address", sample: "94102", context: "always" },
  { key: "country", tag: "country", label: "Country", category: "address", sample: "United States", context: "always" },
  // Date & time (generated at send time)
  { key: "date", tag: "date", label: "Current Date", category: "date", sample: "January 29, 2026", context: "always" },
  { key: "today", tag: "today", label: "Today's Date", category: "date", sample: "January 29, 2026", context: "always" },
  { key: "year", tag: "year", label: "Current Year", category: "date", sample: "2026", context: "always" },
  { key: "month", tag: "month", label: "Current Month", category: "date", sample: "January", context: "always" },
  { key: "day", tag: "day", label: "Current Day", category: "date", sample: "29", context: "always" },
  // Document sends (eSignature campaigns)
  { key: "documentName", tag: "document_name", label: "Document Name", category: "document", sample: "Service Agreement", context: "document" },
  { key: "documentId", tag: "document_id", label: "Document ID", category: "document", sample: "DOC-2026-001", context: "document" },
  { key: "signingUrl", tag: "signing_url", label: "Signing Link", category: "document", sample: "https://esign.respectly.ai/s/abc123", context: "document" },
  { key: "expirationDate", tag: "expiration_date", label: "Expiration Date", category: "document", sample: "February 28, 2026", context: "document" },
  // Form sends
  { key: "formName", tag: "form_name", label: "Form Name", category: "form", sample: "Intake Form", context: "form" },
  { key: "formUrl", tag: "form_url", label: "Form Link", category: "form", sample: "https://esign.respectly.ai/f/abc123", context: "form" },
  // Email campaigns. Not advertised: the picker is shared by the SMS and Email
  // editors and this tag is only injected on campaign email sends, so offering it
  // in an SMS template would hand the sender a guaranteed blank. Campaign emails
  // already append an unsubscribe footer automatically; the tag exists so a body
  // that spells it out (which email-template-compliance.ts accepts as satisfying
  // CAN-SPAM) renders a real link instead of nothing.
  { key: "unsubscribeUrl", tag: "unsubscribe_url", label: "Unsubscribe Link", category: "company", sample: "https://esign.respectly.ai/unsubscribe/abc123", context: "email-campaign", advertised: false }
];
function snakeToCamelTag(str) {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}
function camelToSnakeTag(str) {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}
function isNamespacedMergeTag(name) {
  return MERGE_TAG_NAMESPACES.some((ns) => name.startsWith(ns));
}
var SYSTEM_KEYS = new Set(SYSTEM_MERGE_TAGS.map((t) => t.key));
function resolveSystemMergeTagKey(name) {
  const trimmed = name.trim();
  if (SYSTEM_KEYS.has(trimmed)) return trimmed;
  const camel = snakeToCamelTag(trimmed);
  return SYSTEM_KEYS.has(camel) ? camel : null;
}
function classifyMergeTag(name, knownFieldTags, opts) {
  const trimmed = name.trim();
  if (resolveSystemMergeTagKey(trimmed)) return "system";
  const fieldsLoaded = opts?.fieldsLoaded ?? true;
  if (!fieldsLoaded) return "known-field";
  if (isNamespacedMergeTag(trimmed)) {
    return knownFieldTags.has(trimmed) ? "known-field" : "deleted-field";
  }
  if (opts?.bareFieldKeys?.has(trimmed)) return "known-field";
  if (opts?.deletedBareFieldKeys?.has(trimmed)) return "deleted-field";
  return "unknown";
}
function isUnresolvableMergeTagStatus(status) {
  return status === "deleted-field" || status === "unknown";
}

// src/org-message-tags.ts
function getOrgMessageFieldAliases(fields) {
  const keys = [...new Set(fields.map((field) => field.fieldKey).filter(Boolean))].sort();
  const reserved = new Set([...ORG_CUSTOM_FIELD_RESERVED].flatMap((key) => [key, toSnake(key)]));
  const taken = /* @__PURE__ */ new Set([...keys, ...reserved]);
  const aliases = /* @__PURE__ */ new Map();
  for (const key of keys) {
    const stripped = key.replace(/^custom(?=[A-Z_])_?/, "");
    const base = toSnake(stripped) || toSnake(key);
    let alias = base;
    let suffix = 2;
    while (taken.has(alias) && alias !== key || reserved.has(alias)) alias = `${base}_${suffix++}`;
    taken.add(alias);
    aliases.set(key, alias);
  }
  return aliases;
}
function toSnake(key) {
  return key.replace(/([A-Z]+)([A-Z][a-z])/g, "$1_$2").replace(/([a-z0-9])([A-Z])/g, "$1_$2").toLowerCase();
}

// src/workflow-condition-catalog.ts
var VALUELESS_OPERATORS = /* @__PURE__ */ new Set([
  "is_true",
  "is_false",
  "exists",
  "not_exists",
  "is_set",
  "is_not_set",
  "has",
  "has_not"
]);
var OPERATORS_BY_VALUE_TYPE = {
  boolean: ["is_true", "is_false"],
  text: ["equals", "not_equals", "contains", "not_contains", "starts_with", "ends_with", "exists", "not_exists"],
  enum: ["equals", "not_equals", "is_one_of"],
  number: ["equals", "not_equals", "gt", "gte", "lt", "lte", "between"],
  date: ["within_last", "within_next", "more_than_ago", "before", "after", "is_set", "is_not_set"],
  existence: ["exists", "not_exists"],
  relational: ["has", "has_not"],
  count: ["count_gte", "count_lte", "count_eq"]
};
var OPERATOR_LABELS = {
  is_true: "is yes",
  is_false: "is no",
  equals: "is",
  not_equals: "is not",
  contains: "contains",
  not_contains: "doesn't contain",
  starts_with: "starts with",
  ends_with: "ends with",
  exists: "has a value",
  not_exists: "has no value",
  is_one_of: "is one of",
  gt: "is more than",
  gte: "is at least",
  lt: "is less than",
  lte: "is at most",
  between: "is between",
  before: "is before",
  after: "is after",
  within_last: "within the last",
  within_next: "within the next",
  more_than_ago: "more than \u2026 ago",
  is_set: "is set",
  is_not_set: "is not set",
  has: "yes",
  has_not: "no",
  count_gte: "at least",
  count_lte: "at most",
  count_eq: "exactly"
};
var CATEGORY_LABELS = {
  contact: "Contact details",
  consent: "Consent & opt-in",
  compliance: "Compliance (consent records, DNC, eligibility)",
  documents: "Documents & e-signatures",
  engagement: "Messages & engagement",
  forms: "Forms",
  groups: "Groups & stages",
  activity: "Dates & activity",
  voice: "Voice calls",
  workflow: "Workflow state"
};
var CHANNEL_PURPOSE_STATUS_OPTIONS = [
  { value: "UNKNOWN", label: "Unknown" },
  { value: "NONE", label: "None" },
  { value: "OPTOUT", label: "Opted out" },
  { value: "IMPLICIT_OPT_IN", label: "Implicit (EBR)" },
  { value: "EXPRESS_OPTIN", label: "Express opt-in" },
  { value: "WRITTEN_EXPRESS_OPTIN", label: "Written express opt-in" }
];
var SIGNATURE_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "sent", label: "Sent" },
  { value: "viewed", label: "Viewed" },
  { value: "signed", label: "Signed" },
  { value: "declined", label: "Declined" },
  { value: "expired", label: "Expired" }
];
var RECIPIENT_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "sent", label: "Sent" },
  { value: "delivered", label: "Delivered" },
  { value: "failed", label: "Failed" },
  { value: "opened", label: "Opened" },
  { value: "clicked", label: "Clicked" },
  { value: "bounced", label: "Bounced" },
  { value: "unsubscribed", label: "Unsubscribed" }
];
var CONDITION_FIELDS = [
  // ── Contact details ─────────────────────────────────────────────────────────
  { key: "contact.firstName", label: "First name", category: "contact", valueType: "text" },
  { key: "contact.lastName", label: "Last name", category: "contact", valueType: "text" },
  { key: "contact.email", label: "Email address", category: "contact", valueType: "text" },
  { key: "contact.phone", label: "Phone number", category: "contact", valueType: "text" },
  { key: "contact.company", label: "Company", category: "contact", valueType: "text" },
  { key: "contact.address", label: "Street address", category: "contact", valueType: "text" },
  { key: "contact.city", label: "City", category: "contact", valueType: "text" },
  { key: "contact.state", label: "State / province", category: "contact", valueType: "text" },
  { key: "contact.zipCode", label: "Zip / postal code", category: "contact", valueType: "text" },
  { key: "contact.country", label: "Country", category: "contact", valueType: "text" },
  { key: "contact.timezone", label: "Timezone", category: "contact", valueType: "text" },
  {
    key: "contact.b2bOrB2c",
    label: "Audience type",
    category: "contact",
    valueType: "enum",
    options: [
      { value: "B2B", label: "Business (B2B)" },
      { value: "B2C", label: "Consumer (B2C)" }
    ]
  },
  { key: "contact.isActive", label: "Is active", category: "contact", valueType: "boolean" },
  { key: "contact.hasAnyTag", label: "Has any tag", category: "contact", valueType: "boolean" },
  { key: "contact.hasTag", label: "Has tag", category: "contact", valueType: "relational", param: "tag" },
  { key: "contact.custom", label: "Custom field", category: "contact", valueType: "text", param: "custom" },
  // ── Consent & opt-in ────────────────────────────────────────────────────────
  { key: "consent.smsOptIn", label: "SMS opt-in", category: "consent", valueType: "boolean" },
  { key: "consent.emailOptIn", label: "Email opt-in", category: "consent", valueType: "boolean" },
  { key: "consent.voiceOptIn", label: "Voice opt-in", category: "consent", valueType: "boolean" },
  {
    key: "consent.level",
    label: "Consent level",
    category: "consent",
    valueType: "enum",
    options: [
      { value: "transactional", label: "Transactional" },
      { value: "informational", label: "Informational" },
      { value: "marketing", label: "Marketing" }
    ]
  },
  { key: "consent.smsPending", label: "SMS consent pending (double opt-in)", category: "consent", valueType: "boolean" },
  { key: "consent.emailPending", label: "Email consent pending (double opt-in)", category: "consent", valueType: "boolean" },
  { key: "consent.smsOptedOut", label: "Has opted out of SMS", category: "consent", valueType: "boolean" },
  { key: "consent.emailOptedOut", label: "Has opted out of email", category: "consent", valueType: "boolean" },
  {
    key: "consent.lineType",
    label: "Phone line type",
    category: "consent",
    valueType: "enum",
    hint: "Null until a line-type lookup runs.",
    options: [
      { value: "MOBILE", label: "Mobile" },
      { value: "LANDLINE", label: "Landline" },
      { value: "VOIP", label: "VOIP" },
      { value: "UNKNOWN", label: "Unknown" }
    ]
  },
  { key: "consent.lineTypeChecked", label: "Line type has been checked", category: "consent", valueType: "boolean" },
  { key: "consent.smsOptInAt", label: "SMS opt-in date", category: "consent", valueType: "date", unit: "days" },
  { key: "consent.emailOptInAt", label: "Email opt-in date", category: "consent", valueType: "date", unit: "days" },
  { key: "consent.voiceOptInAt", label: "Voice opt-in date", category: "consent", valueType: "date", unit: "days" },
  // ── Compliance: consent records / DNC / eligibility (fail-closed) ────────────
  {
    key: "consentRecord.smsMarketing",
    label: "SMS marketing consent status",
    category: "compliance",
    valueType: "enum",
    options: CHANNEL_PURPOSE_STATUS_OPTIONS,
    load: "consent"
  },
  {
    key: "consentRecord.emailMarketing",
    label: "Email marketing consent status",
    category: "compliance",
    valueType: "enum",
    options: CHANNEL_PURPOSE_STATUS_OPTIONS,
    load: "consent"
  },
  {
    key: "consentRecord.voiceMarketing",
    label: "Voice marketing consent status",
    category: "compliance",
    valueType: "enum",
    options: CHANNEL_PURPOSE_STATUS_OPTIONS,
    load: "consent"
  },
  { key: "consentRecord.hasWrittenExpress", label: "Has written express consent (any channel)", category: "compliance", valueType: "boolean", load: "consent" },
  { key: "consentRecord.marketingExpiringSoon", label: "Marketing consent expiring soon", category: "compliance", valueType: "date", unit: "days", operators: ["within_next", "is_set", "is_not_set"], load: "consent", hint: "Based on the earliest consent-record expiry." },
  { key: "dnc.onAnyList", label: "On a Do-Not-Call list", category: "compliance", valueType: "boolean", load: "consent" },
  { key: "eligibility.marketingSms", label: "Eligible \u2014 marketing SMS", category: "compliance", valueType: "boolean", load: "consent" },
  { key: "eligibility.marketingEmail", label: "Eligible \u2014 marketing email", category: "compliance", valueType: "boolean", load: "consent" },
  { key: "eligibility.marketingVoice", label: "Eligible \u2014 marketing voice", category: "compliance", valueType: "boolean", load: "consent" },
  { key: "eligibility.informationalSms", label: "Eligible \u2014 informational SMS", category: "compliance", valueType: "boolean", load: "consent" },
  { key: "eligibility.informationalEmail", label: "Eligible \u2014 informational email", category: "compliance", valueType: "boolean", load: "consent" },
  { key: "eligibility.informationalVoice", label: "Eligible \u2014 informational voice", category: "compliance", valueType: "boolean", load: "consent" },
  // ── Documents & e-signatures (headline) ─────────────────────────────────────
  { key: "signature.signedAnyDocument", label: "Has signed any document", category: "documents", valueType: "boolean", load: "documents" },
  { key: "signature.signedDocument", label: "Has signed a specific document", category: "documents", valueType: "relational", param: "document", load: "documents" },
  {
    key: "signature.statusForDocument",
    label: "Signature status for a document",
    category: "documents",
    valueType: "enum",
    options: SIGNATURE_STATUS_OPTIONS,
    param: "document",
    load: "documents"
  },
  {
    key: "signature.hasStatus",
    label: "Has a signature in status (any document)",
    category: "documents",
    valueType: "enum",
    options: SIGNATURE_STATUS_OPTIONS,
    load: "documents"
  },
  { key: "signature.declinedAny", label: "Has declined any document", category: "documents", valueType: "boolean", load: "documents" },
  { key: "signature.declinedDocument", label: "Has declined a specific document", category: "documents", valueType: "relational", param: "document", load: "documents" },
  { key: "signature.viewedNotSigned", label: "Viewed but not signed (any document)", category: "documents", valueType: "boolean", load: "documents" },
  { key: "signature.hasOpenRequest", label: "Has a pending/sent signature request", category: "documents", valueType: "boolean", load: "documents" },
  { key: "signature.anyExpired", label: "A signature request expired", category: "documents", valueType: "boolean", load: "documents" },
  { key: "signature.remindersSent", label: "Reminders sent for a document", category: "documents", valueType: "count", unit: "times", param: "document", load: "documents", hint: "How many reminder emails were sent." },
  { key: "signature.lastSignedAt", label: "Last signed date", category: "documents", valueType: "date", unit: "days", load: "documents" },
  { key: "signatureInstance.completed", label: "Multi-signer packet completed", category: "documents", valueType: "relational", param: "document", load: "documents" },
  { key: "signatureInstance.isCurrentSigner", label: "It is this contact\u2019s turn to sign", category: "documents", valueType: "boolean", load: "documents" },
  // ── Messages & engagement ───────────────────────────────────────────────────
  { key: "message.sentAny", label: "Has been sent any campaign", category: "engagement", valueType: "boolean", load: "engagement" },
  { key: "message.deliveredAny", label: "Has been delivered any campaign", category: "engagement", valueType: "boolean", load: "engagement" },
  { key: "message.openedAny", label: "Opened any email", category: "engagement", valueType: "boolean", load: "engagement", hint: "Email only \u2014 SMS has no open tracking." },
  { key: "message.openedRecent", label: "Opened an email recently", category: "engagement", valueType: "date", unit: "days", operators: ["within_last", "more_than_ago"], load: "engagement", hint: "Email only." },
  { key: "message.clickedAny", label: "Clicked any email link", category: "engagement", valueType: "boolean", load: "engagement", hint: "Email only; disabled for transactional/signing emails." },
  { key: "message.clickedRecent", label: "Clicked an email link recently", category: "engagement", valueType: "date", unit: "days", operators: ["within_last", "more_than_ago"], load: "engagement", hint: "Email only." },
  { key: "message.bounced", label: "A message bounced", category: "engagement", valueType: "boolean", load: "engagement" },
  { key: "message.unsubscribed", label: "Unsubscribed via a message", category: "engagement", valueType: "boolean", load: "engagement" },
  { key: "message.failed", label: "A message send failed", category: "engagement", valueType: "boolean", load: "engagement" },
  {
    key: "message.statusForCampaign",
    label: "Engagement status for a campaign",
    category: "engagement",
    valueType: "enum",
    options: RECIPIENT_STATUS_OPTIONS,
    param: "campaign",
    load: "engagement"
  },
  { key: "message.openedCampaign", label: "Opened a specific campaign", category: "engagement", valueType: "relational", param: "campaign", load: "engagement" },
  { key: "message.hasReplied", label: "Has replied (any inbound message)", category: "engagement", valueType: "boolean", load: "engagement" },
  { key: "message.repliedRecent", label: "Replied recently", category: "engagement", valueType: "date", unit: "days", operators: ["within_last", "more_than_ago"], load: "engagement" },
  // ── Forms ───────────────────────────────────────────────────────────────────
  { key: "form.submittedAny", label: "Submitted any form", category: "forms", valueType: "boolean", load: "forms" },
  { key: "form.submitted", label: "Submitted a specific form", category: "forms", valueType: "relational", param: "form", load: "forms" },
  { key: "form.submittedRecent", label: "Submitted a form recently", category: "forms", valueType: "date", unit: "days", operators: ["within_last", "more_than_ago"], load: "forms" },
  // ── Groups & stages (replaces the broken `stage`) ───────────────────────────
  { key: "group.inAnyGroup", label: "Is in any group", category: "groups", valueType: "boolean", load: "groups" },
  { key: "group.inGroup", label: "Is in a specific group", category: "groups", valueType: "relational", param: "group", load: "groups" },
  { key: "group.inStage", label: "Is at a specific stage", category: "groups", valueType: "relational", param: "groupStage", load: "groups" },
  { key: "group.atFinalStage", label: "Reached a final stage (any group)", category: "groups", valueType: "boolean", load: "groups" },
  { key: "group.atDefaultStage", label: "Is at a group\u2019s default stage", category: "groups", valueType: "boolean", load: "groups" },
  // ── Dates & activity ────────────────────────────────────────────────────────
  { key: "contact.createdAt", label: "Created date", category: "activity", valueType: "date", unit: "days" },
  { key: "contact.updatedAt", label: "Last updated date", category: "activity", valueType: "date", unit: "days" },
  { key: "activity.lastMessageAt", label: "Last activity (last message)", category: "activity", valueType: "date", unit: "days", load: "engagement" },
  // ── Voice ───────────────────────────────────────────────────────────────────
  { key: "voice.completedCall", label: "Had a completed call", category: "voice", valueType: "boolean", load: "voice" },
  {
    key: "voice.lastCallStatus",
    label: "Latest call status",
    category: "voice",
    valueType: "enum",
    options: [
      { value: "pending", label: "Pending" },
      { value: "in_progress", label: "In progress" },
      { value: "completed", label: "Completed" },
      { value: "failed", label: "Failed" }
    ],
    load: "voice"
  },
  { key: "voice.consentCaptured", label: "Voice consent captured", category: "voice", valueType: "boolean", load: "voice" },
  {
    key: "voice.consentResponse",
    label: "Voice consent response",
    category: "voice",
    valueType: "enum",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" }
    ],
    load: "voice"
  },
  // ── Workflow / self state ───────────────────────────────────────────────────
  { key: "workflow.enrolledIn", label: "Currently enrolled in a workflow", category: "workflow", valueType: "relational", param: "workflow", load: "workflow" },
  { key: "workflow.completed", label: "Completed a workflow", category: "workflow", valueType: "relational", param: "workflow", load: "workflow" }
];
var BRANCH_QUICK_STARTS = [
  { label: "Signed a document", field: "signature.signedAnyDocument", operator: "is_true" },
  { label: "Opted into SMS", field: "consent.smsOptIn", operator: "is_true" },
  { label: "Opened my email", field: "message.openedAny", operator: "is_true" },
  { label: "Submitted a form", field: "form.submittedAny", operator: "is_true" },
  { label: "Is at a stage", field: "group.inStage", operator: "has" },
  { label: "Has a tag", field: "contact.hasTag", operator: "has" }
];
function isConditionGroup(node) {
  return !!node && typeof node === "object" && "logic" in node && Array.isArray(node.conditions);
}
function isConditionLeaf(node) {
  return !!node && typeof node === "object" && "field" in node && typeof node.field === "string";
}
var LEGACY_FIELD_ALIASES = {
  smsOptIn: "consent.smsOptIn",
  emailOptIn: "consent.emailOptIn",
  voiceOptIn: "consent.voiceOptIn",
  consentLevel: "consent.level",
  isActive: "contact.isActive",
  b2bOrB2c: "contact.b2bOrB2c",
  firstName: "contact.firstName",
  lastName: "contact.lastName",
  email: "contact.email",
  phone: "contact.phone",
  company: "contact.company",
  // `stage` was a known-broken silent no-op with no resolver; legacy rows keep
  // their (always-false) behavior. New conditions use `group.inStage`.
  stage: "group.inStage"
};
var LEGACY_OPERATORS = /* @__PURE__ */ new Set(["equals", "not_equals", "contains", "exists", "not_exists"]);
function isLegacyField(field) {
  if (field in LEGACY_FIELD_ALIASES) return true;
  return field.startsWith("hasTag:") || field.startsWith("custom:");
}
var FIELD_BY_KEY = Object.fromEntries(
  CONDITION_FIELDS.map((f) => [f.key, f])
);
function splitFieldKey(field) {
  const [base = "", ...params] = field.split(":");
  return { base, params };
}
function getFieldDef(field) {
  if (FIELD_BY_KEY[field]) return FIELD_BY_KEY[field];
  return FIELD_BY_KEY[splitFieldKey(field).base];
}
function operatorsForField(def) {
  return def.operators ?? OPERATORS_BY_VALUE_TYPE[def.valueType];
}
function isOperatorAllowed(def, operator) {
  return operatorsForField(def).includes(operator);
}
function operatorNeedsValue(operator) {
  return !VALUELESS_OPERATORS.has(operator);
}
function normalizeCondition(raw) {
  if (!raw || typeof raw !== "object") return null;
  const node = raw;
  if (isConditionGroup(node)) {
    const group = node;
    const conditions = group.conditions.map((c) => normalizeCondition(c)).filter((c) => c !== null);
    return { logic: group.logic === "OR" ? "OR" : "AND", conditions };
  }
  if (typeof node.field === "string" && typeof node.operator === "string") {
    const field = node.field;
    const operator = node.operator;
    const value = node.value;
    const legacy = node.legacy === true || isLegacyField(field) && LEGACY_OPERATORS.has(operator);
    const leaf = { field, operator, value };
    if (legacy) leaf.legacy = true;
    if (typeof node.valueLabel === "string") leaf.valueLabel = node.valueLabel;
    if (typeof node.paramLabel === "string") leaf.paramLabel = node.paramLabel;
    return leaf;
  }
  return null;
}

// src/workflow-validation.ts
function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}
function isWaitUnit(value) {
  return value === "minutes" || value === "hours" || value === "days";
}
var WAIT_DURATION_MIN = 1;
var WAIT_DURATION_MAX = 365;
var MS_PER_WAIT_UNIT = {
  minutes: 6e4,
  hours: 36e5,
  days: 864e5
};
var MAX_WAIT_MS = WAIT_DURATION_MAX * MS_PER_WAIT_UNIT.days;
function validateWaitDuration(duration, unit) {
  if (typeof duration !== "number" || !Number.isFinite(duration) || !isWaitUnit(unit)) {
    return { message: "Set a wait time", severity: "incomplete" };
  }
  if (!Number.isInteger(duration)) {
    return { message: "Wait time must be a whole number", severity: "invalid" };
  }
  if (duration < WAIT_DURATION_MIN || duration > WAIT_DURATION_MAX) {
    return {
      message: `Wait time must be between ${WAIT_DURATION_MIN} and ${WAIT_DURATION_MAX}`,
      severity: "invalid"
    };
  }
  return null;
}
function clampWaitDelayMs(duration, unit) {
  const msPerUnit = MS_PER_WAIT_UNIT[isWaitUnit(unit) ? unit : "days"];
  const floored = typeof duration === "number" && Number.isFinite(duration) ? Math.floor(duration) : WAIT_DURATION_MIN;
  const amount = floored >= WAIT_DURATION_MIN ? floored : WAIT_DURATION_MIN;
  return Math.min(amount * msPerUnit, MAX_WAIT_MS);
}
function validateWorkflowStep(step) {
  const config = step.config ?? {};
  switch (step.stepType) {
    case "send_sms":
      return hasText(config.templateId) ? null : "Select a text template";
    case "send_email":
      return hasText(config.templateId) ? null : "Select an email template";
    case "send_document":
      return hasText(config.documentId) ? null : "Select a document";
    case "send_form":
      return hasText(config.formId) ? null : "Select a form";
    case "wait":
      return validateWaitDuration(config.duration, config.unit)?.message ?? null;
    case "wait_until":
      return hasText(config.targetDate) ? null : "Set a target date";
    case "add_tag":
    case "remove_tag":
      return hasText(config.tagId) ? null : "Select a label";
    case "add_to_group":
    case "move_stage":
      return hasText(config.groupId) && hasText(config.stageId) ? null : "Select a group and stage";
    case "remove_from_group":
      return hasText(config.groupId) ? null : "Select a group";
    case "update_field":
      return hasText(config.fieldKey) && hasText(config.value) ? null : "Choose a field and value";
    case "send_webhook":
      return hasText(config.url) ? null : "Set a webhook URL";
    case "branch":
      return validateBranchCondition(config.condition);
    case "exit":
      return null;
    default:
      return null;
  }
}
function validateWorkflowStepIssue(step) {
  if (step.stepType === "wait") {
    const config = step.config ?? {};
    return validateWaitDuration(config.duration, config.unit);
  }
  const message = validateWorkflowStep(step);
  return message ? { message, severity: "incomplete" } : null;
}
var WORKFLOW_NAME_MAX_LENGTH = 255;
function validateWorkflowName(name) {
  if (typeof name !== "string" || name.trim().length === 0) {
    return { message: "Enter a name", severity: "incomplete" };
  }
  if (name.trim().length > WORKFLOW_NAME_MAX_LENGTH) {
    return {
      message: `Name must be ${WORKFLOW_NAME_MAX_LENGTH} characters or less`,
      severity: "invalid"
    };
  }
  return null;
}
function fitWorkflowName(base, suffix = "") {
  const room = Math.max(0, WORKFLOW_NAME_MAX_LENGTH - suffix.length);
  const trimmed = base.trim();
  return (trimmed.length > room ? trimmed.slice(0, room) : trimmed) + suffix;
}
var MESSAGE_STEP_TYPES = /* @__PURE__ */ new Set(["send_email", "send_sms"]);
function validateSequenceHasMessage(steps) {
  if (steps.some((step) => MESSAGE_STEP_TYPES.has(step.stepType))) return null;
  return {
    message: "Add an Email or Text step \u2014 a sequence of only waits sends nothing",
    severity: "incomplete"
  };
}
function validateSequenceAudience(audience, matchCount) {
  const incomplete = (message) => ({ message, severity: "incomplete" });
  if (audience.type === "tags" && (audience.tagIds?.length ?? 0) === 0) {
    return incomplete("Pick at least one label \u2014 this audience enrolls no one");
  }
  if (audience.type === "manual" && (audience.contactIds?.length ?? 0) === 0) {
    return incomplete("Pick at least one contact \u2014 this audience enrolls no one");
  }
  if (matchCount === 0) {
    return incomplete("This audience has no contacts \u2014 no one would be enrolled");
  }
  return null;
}
var LEGACY_OPERATORS2 = /* @__PURE__ */ new Set(["equals", "not_equals", "contains", "exists", "not_exists"]);
function isNumberLike(value) {
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value === "string" && value.trim() !== "") return Number.isFinite(Number(value));
  return false;
}
function isPositiveNumberLike(value) {
  return isNumberLike(value) && Number(value) > 0;
}
function requiredParamCount(field) {
  const def = getFieldDef(field);
  if (!def?.param) return 0;
  return def.param === "groupStage" ? 2 : 1;
}
function validateLeaf(leaf) {
  if (!hasText(leaf.field)) return "Choose what to check";
  if (!hasText(leaf.operator)) return "Choose how to compare";
  if (leaf.legacy || LEGACY_OPERATORS2.has(leaf.operator)) {
    const def2 = getFieldDef(leaf.field);
    if (!def2) return null;
  }
  const def = getFieldDef(leaf.field);
  if (!def) return `This check isn't available anymore \u2014 pick a new one`;
  if (!isOperatorAllowed(def, leaf.operator)) {
    return `"${OPERATOR_LABEL(leaf.operator)}" can't be used with "${def.label}"`;
  }
  const need = requiredParamCount(leaf.field);
  if (need > 0) {
    const { params } = splitFieldKey(leaf.field);
    const present = params.filter((p2) => p2 && p2.length > 0).length;
    if (present < need) return `Pick which ${def.param === "groupStage" ? "stage" : def.param} for "${def.label}"`;
  }
  if (!operatorNeedsValue(leaf.operator)) return null;
  const v = leaf.value;
  switch (def.valueType) {
    case "number":
      if (leaf.operator === "between") {
        const arr = Array.isArray(v) ? v : String(v ?? "").split(",");
        return arr.length === 2 && arr.every(isNumberLike) ? null : `Enter two numbers for "${def.label}"`;
      }
      return isNumberLike(v) ? null : `Enter a number for "${def.label}"`;
    case "count":
      return isNumberLike(v) && Number(v) >= 0 ? null : `Enter a count for "${def.label}"`;
    case "date":
      if (leaf.operator === "within_last" || leaf.operator === "within_next" || leaf.operator === "more_than_ago") {
        return isPositiveNumberLike(v) ? null : `Enter how many ${def.unit ?? "days"} for "${def.label}"`;
      }
      return hasText(v) ? null : `Pick a date for "${def.label}"`;
    case "enum":
      if (leaf.operator === "is_one_of") {
        const arr = Array.isArray(v) ? v : String(v ?? "").split(",").map((s) => s.trim()).filter(Boolean);
        return arr.length > 0 ? null : `Choose at least one option for "${def.label}"`;
      }
      if (def.options && !def.options.some((o) => o.value === v)) {
        return `Choose a valid option for "${def.label}"`;
      }
      return hasText(v) ? null : `Choose an option for "${def.label}"`;
    default:
      return hasText(v) ? null : `Enter a value for "${def.label}"`;
  }
}
function OPERATOR_LABEL(op) {
  return op.replace(/_/g, " ");
}
function validateConditionNode(node) {
  if (isConditionGroup(node)) {
    if (node.logic !== "AND" && node.logic !== "OR") return "A condition group must use AND or OR";
    if (!node.conditions || node.conditions.length === 0) return "Add at least one condition";
    for (const child of node.conditions) {
      const issue = validateConditionNode(child);
      if (issue) return issue;
    }
    return null;
  }
  return validateLeaf(node);
}
function validateBranchCondition(condition) {
  const node = normalizeCondition(condition);
  if (!node) return "Choose what to check";
  return validateConditionNode(node);
}
function validateWorkflowSteps(steps) {
  const issues = [];
  for (const step of steps) {
    const issue = validateWorkflowStepIssue(step);
    if (issue) {
      issues.push({ stepId: step.id, stepType: step.stepType, missing: issue.message, severity: issue.severity });
    }
  }
  return issues;
}

// src/workflow-activity.ts
var WORKFLOW_MESSAGE_STATUSES = [
  "scheduled",
  "on_hold",
  "sending",
  "sent",
  "delivered",
  "opened",
  "not_sent",
  "failed"
];
var WORKFLOW_MESSAGE_STATUS_LABELS = {
  scheduled: "Scheduled",
  on_hold: "On hold",
  sending: "Sending",
  sent: "Sent",
  delivered: "Delivered",
  opened: "Opened",
  not_sent: "Not sent",
  failed: "Couldn't deliver"
};
function describeMessageStatus(status, channel) {
  const where = channel === "email" ? "inbox" : "phone";
  switch (status) {
    case "scheduled":
      return "Waiting for its turn to send.";
    case "on_hold":
      return "Held because this workflow is paused. It will continue when you turn it back on.";
    case "sending":
      return "Going out right now.";
    case "sent":
      return channel === "email" ? "Handed to the email service. Waiting for the inbox to confirm." : "Handed to the phone carrier. Waiting for the phone to confirm.";
    case "delivered":
      return `Reached their ${where}.`;
    case "opened":
      return "They opened it.";
    case "not_sent":
      return "This message never went out.";
    case "failed":
      return channel === "email" ? "It was sent, but the inbox rejected it." : "It was sent, but the carrier could not deliver it.";
  }
}
function deriveWorkflowMessageStatus(log) {
  switch (log.status) {
    case "held":
      return "on_hold";
    case "pending":
    case "executing":
      return "sending";
    case "failed":
    case "skipped":
      return "not_sent";
  }
  switch (log.deliveryStatus) {
    case "failed":
      return "failed";
    case "opened":
      return "opened";
    case "delivered":
      return "delivered";
    default:
      return "sent";
  }
}
var PROBLEMS = {
  no_phone: () => ({
    title: "No phone number on this contact",
    fix: "Add a mobile number to the contact."
  }),
  no_email: () => ({
    title: "No email address on this contact",
    fix: "Add an email address to the contact."
  }),
  no_consent: (c) => ({
    title: c === "email" ? "This person hasn't agreed to receive emails" : "This person hasn't agreed to receive texts",
    fix: c === "email" ? "Ask them to opt in, or reach them another way." : "Ask them to opt in to texts, or send by email instead."
  }),
  do_not_contact: () => ({
    title: "This person is on your Do-Not-Contact list",
    fix: "Remove them from the list only if they asked to hear from you again."
  }),
  outside_hours: () => ({
    title: "Outside the hours you are allowed to message this person",
    fix: "Nothing to do. These go out during the recipient\u2019s local allowed hours."
  }),
  too_many_messages: () => ({
    title: "This person has already received the most messages allowed for now",
    fix: "Nothing to do. You can message them again once the limit resets."
  }),
  compliance_rule: () => ({
    title: "Blocked by your compliance rules",
    fix: "Open the contact to check their consent and Do-Not-Contact status."
  }),
  no_template: () => ({
    title: "This step has no message chosen",
    fix: "Edit the workflow and pick a message for this step."
  }),
  template_missing: () => ({
    title: "The message for this step was deleted",
    fix: "Edit the workflow and pick a new message for this step."
  }),
  landline: () => ({
    title: "This number can't receive texts (it may be a landline)",
    fix: "Ask for a mobile number, or send by email instead."
  }),
  invalid_number: () => ({
    title: "This phone number doesn't look right",
    fix: "Check the number on the contact and fix any typos."
  }),
  opted_out: (c) => ({
    title: c === "email" ? "This person unsubscribed" : "This person replied STOP to your texts",
    fix: "They asked not to be contacted this way. No action needed."
  }),
  carrier_rejected: () => ({
    title: "The phone carrier rejected the message",
    fix: "Usually temporary. If it keeps happening for many people, contact support."
  }),
  carrier_spam: () => ({
    title: "The phone carrier blocked it as possible spam",
    fix: 'Reword the message (avoid link shorteners, all caps and "free"), and make sure people opted in.'
  }),
  not_registered: () => ({
    title: "Your texting number isn't registered with the phone carriers yet",
    fix: "Finish your business texting registration in Settings. Carriers block texts from unregistered numbers."
  }),
  account_funds: () => ({
    title: "Your texting account ran out of credit",
    fix: "Add credit or update your billing, then send again."
  }),
  country_blocked: () => ({
    title: "Texting to this person's country isn't turned on for your account",
    fix: "Contact support to enable texting to that country, or reach them by email."
  }),
  bounced: () => ({
    title: "The email bounced (the address may not exist)",
    fix: "Check the email address on the contact."
  }),
  marked_spam: () => ({
    title: "This person marked your email as spam",
    fix: "They won\u2019t receive more emails from you. No action needed."
  }),
  email_service: () => ({
    title: "Your email account couldn't send the message",
    fix: "Check your email connection in Settings, then try again."
  }),
  unknown: (c) => ({
    title: c === "email" ? "The email couldn't be sent" : "The text couldn't be sent",
    fix: "If this keeps happening, contact support and share the technical details."
  })
};
function describeProblemGroup(group, channel) {
  return { group, ...PROBLEMS[group](channel ?? "sms") };
}
var CONSENT_RX = /consent|opt-?in|eligib|permission/i;
function classifyPreBlock(code, ruleName, remediationText) {
  switch (code) {
    case "REMED_INTERNAL_DNC_CLEARANCE_REQUIRED":
    case "REMED_REMOVE_FROM_CALL_LIST":
      return "do_not_contact";
    case "REMED_RETRY_AFTER_WINDOW_RESETS":
      return "too_many_messages";
  }
  const hint = `${ruleName ?? ""} ${remediationText ?? ""} ${code ?? ""}`;
  if (/DNC|do.?not.?(contact|call)/i.test(hint)) return "do_not_contact";
  if (/calling.?hours|quiet.?hours|time.?window|WITHIN_HOURS/i.test(hint)) return "outside_hours";
  if (CONSENT_RX.test(hint)) return "no_consent";
  if (/frequency|\bcap\b|\blimit/i.test(hint)) return "too_many_messages";
  return "compliance_rule";
}
function classifyProviderError(text, channel) {
  if (/INSUFFICIENT_CREDIT|insufficient (funds|balance|credit)|out of credit|low balance/i.test(text)) return "account_funds";
  if (/COUNTRY_NOT_ENABLED|country.{0,30}not (enabled|supported|allowed|permitted)|geo.?permission/i.test(text)) return "country_blocked";
  if (/10DLC|not registered|unregistered|campaign.{0,30}(registration|not approved)|toll.?free.{0,20}verif|code 4001[01]\b/i.test(text)) return "not_registered";
  if (channel === "sms" && /spam|code 4000[23]\b/i.test(text)) return "carrier_spam";
  if (/INVALID_RECIPIENT|INVALID_NUMBER|not.?routable|code 40001\b/i.test(text)) return /landline|40001/i.test(text) ? "landline" : "invalid_number";
  if (/landline|not.?sms.?capable|cannot receive (sms|text)|non.?mobile|unreachable handset/i.test(text)) return "landline";
  if (/invalid.*(number|destination|phone)|not a valid|unknown subscriber|unallocated|E\.?164/i.test(text)) return "invalid_number";
  if (/STOP|opted.?out|unsubscribed|blacklist|blocklist/i.test(text)) return "opted_out";
  if (/spam/i.test(text)) return "marked_spam";
  if (/bounce|mailbox|does not exist|no such user|user unknown|invalid.*(email|address|recipient)/i.test(text)) return "bounced";
  if (/oauth|token|smtp|auth|credential|gmail|graph|outlook/i.test(text)) return "email_service";
  if (channel === "sms" && /carrier|reject|undeliver|deliver|filter|block/i.test(text)) return "carrier_rejected";
  if (channel === "email" && /reject|drop|undeliver/i.test(text)) return "bounced";
  return "unknown";
}
function describeSendProblem(input) {
  const channel = input.channel ?? "sms";
  const error = (input.error ?? "").trim();
  const deliveryError = (input.deliveryError ?? "").trim();
  let group = "unknown";
  if (error) {
    if (/no phone number/i.test(error)) group = "no_phone";
    else if (/no email address/i.test(error)) group = "no_email";
    else if (/no (sms|email) template configured/i.test(error)) group = "no_template";
    else if (/template not found/i.test(error)) group = "template_missing";
    else if (/^consent not captured/i.test(error)) group = "no_consent";
    else if (/^PRE blocked/i.test(error) || input.reasonCode) {
      const codeFromError = error.match(/^PRE blocked:\s*([A-Za-z0-9_-]+)/i)?.[1];
      group = classifyPreBlock(input.reasonCode ?? codeFromError, input.blockedRuleName, input.remediationText);
    } else group = classifyProviderError(error, channel);
  } else if (deliveryError) {
    group = classifyProviderError(deliveryError, channel);
  } else if (input.reasonCode) {
    group = classifyPreBlock(input.reasonCode, input.blockedRuleName, input.remediationText);
  }
  const problem = describeProblemGroup(group, channel);
  if (group === "compliance_rule" && input.blockedRuleName) {
    return { ...problem, title: `Blocked by the "${input.blockedRuleName}" rule` };
  }
  return problem;
}
var STEP_NOUNS = {
  send_sms: "Text",
  send_email: "Email",
  send_document: "Document",
  send_form: "Form",
  wait: "Wait",
  wait_until: "Wait until",
  add_tag: "Add tag",
  remove_tag: "Remove tag",
  add_to_group: "Add to group",
  remove_from_group: "Remove from group",
  move_stage: "Move stage",
  update_field: "Update field",
  send_webhook: "Notify another app",
  branch: "Yes/no check",
  exit: "Finish"
};
function describeStep(stepType, config) {
  const c = config ?? {};
  const noun = STEP_NOUNS[stepType] ?? "Step";
  if (stepType === "wait") {
    const n = typeof c.duration === "number" ? c.duration : 1;
    const unit = typeof c.unit === "string" ? c.unit : "days";
    const singular = unit.replace(/s$/, "");
    return `Wait ${n} ${n === 1 ? singular : `${singular}s`}`;
  }
  const name = typeof c.templateName === "string" && c.templateName || typeof c.subject === "string" && c.subject || typeof c.name === "string" && c.name || "";
  return name ? `${noun}: ${name}` : noun;
}
function branchPath(bc) {
  try {
    const p2 = JSON.parse(bc || "null")?.path;
    return p2 === "yes" || p2 === "no" ? p2 : null;
  } catch {
    return null;
  }
}
function nextStepAfter(steps, current) {
  const path = branchPath(current.branchCondition);
  const siblings = steps.filter((s) => (s.parentStepId ?? null) === (current.parentStepId ?? null) && s.sortOrder > current.sortOrder).sort((a, b) => a.sortOrder - b.sortOrder);
  const next = current.parentStepId ? siblings.find((s) => branchPath(s.branchCondition) === path) ?? null : siblings[0] ?? null;
  if (next) return next;
  if (current.parentStepId) {
    const parent = steps.find((s) => s.id === current.parentStepId);
    return parent ? nextStepAfter(steps, parent) : null;
  }
  return null;
}
function resolveUpcomingOutcome(steps, waitStepId) {
  let cursor = steps.find((s) => s.id === waitStepId) ?? null;
  for (let guard = 0; cursor && guard < 200; guard++) {
    const next = nextStepAfter(steps, cursor);
    if (!next) return { kind: "finish" };
    if (next.stepType === "send_sms" || next.stepType === "send_email") return { kind: "send", step: next };
    if (next.stepType === "exit") return { kind: "finish" };
    if (next.stepType === "branch") return { kind: "depends" };
    if (next.stepType === "wait" || next.stepType === "wait_until") return { kind: "wait_again", step: next };
    cursor = next;
  }
  return { kind: "finish" };
}
function waitFiresAt(log, waitConfig) {
  if (log.scheduledFor) return new Date(log.scheduledFor);
  return new Date(new Date(log.startedAt).getTime() + clampWaitDelayMs(waitConfig.duration, waitConfig.unit));
}
function isMessageStep(stepType) {
  return stepType === "send_sms" || stepType === "send_email";
}
var EMPTY_STEP_COUNTS = {
  completed: 0,
  failed: 0,
  skipped: 0,
  held: 0,
  waiting: 0,
  running: 0,
  delivered: 0,
  opened: 0,
  deliveryFailed: 0,
  signed: 0,
  submitted: 0,
  yes: 0,
  no: 0
};
var fmt = (v) => v.toLocaleString("en-US");
var DONE_WORDS = {
  add_tag: "labelled",
  remove_tag: "label removed",
  add_to_group: "added to the group",
  remove_from_group: "removed from the group",
  move_stage: "moved",
  update_field: "updated",
  exit: "finished here"
};
function summarizeStep(stepType, c) {
  const notSent = c.failed + c.skipped;
  const reached = c.completed + notSent + c.held + c.waiting + c.running;
  if (reached === 0) return { headline: "Nobody yet", detail: null, tone: "neutral" };
  const held = c.held > 0 ? { detail: `${fmt(c.held)} on hold`, tone: "warn" } : null;
  switch (stepType) {
    case "send_sms":
    case "send_email":
    case "send_document":
    case "send_form": {
      const headline = `${fmt(c.completed)} sent`;
      if (held) return { headline, ...held };
      if (notSent > 0) return { headline, detail: `${fmt(notSent)} didn't go out`, tone: "warn" };
      if (c.deliveryFailed > 0) return { headline, detail: `${fmt(c.deliveryFailed)} couldn't be delivered`, tone: "warn" };
      if (stepType === "send_document" && c.signed > 0) return { headline, detail: `${fmt(c.signed)} signed`, tone: "good" };
      if (stepType === "send_form" && c.submitted > 0) return { headline, detail: `${fmt(c.submitted)} filled in`, tone: "good" };
      if (c.opened > 0) return { headline, detail: `${fmt(c.opened)} opened`, tone: "good" };
      if (c.delivered > 0) return { headline, detail: `${fmt(c.delivered)} delivered`, tone: "good" };
      return { headline, detail: null, tone: "neutral" };
    }
    case "wait":
    case "wait_until":
      if (c.waiting > 0 || c.held > 0) {
        return { headline: `${fmt(c.waiting + c.held)} waiting`, detail: held?.detail ?? null, tone: held ? "warn" : "neutral" };
      }
      return { headline: `${fmt(c.completed)} waited`, detail: null, tone: "neutral" };
    case "branch":
      return { headline: `${fmt(c.yes)} Yes \xB7 ${fmt(c.no)} No`, detail: held?.detail ?? null, tone: held ? "warn" : "neutral" };
    case "send_webhook": {
      const headline = `${fmt(c.completed)} sent`;
      if (held) return { headline, ...held };
      if (c.deliveryFailed > 0) return { headline, detail: `${fmt(c.deliveryFailed)} not received`, tone: "bad" };
      if (notSent > 0) return { headline, detail: `${fmt(notSent)} didn't go out`, tone: "warn" };
      if (c.delivered > 0) return { headline, detail: `${fmt(c.delivered)} received`, tone: "good" };
      return { headline, detail: null, tone: "neutral" };
    }
    default: {
      const word = DONE_WORDS[stepType] ?? "done";
      const headline = `${fmt(c.completed)} ${word}`;
      if (held) return { headline, ...held };
      if (c.failed > 0) return { headline, detail: `${fmt(c.failed)} failed`, tone: "bad" };
      return { headline, detail: null, tone: "neutral" };
    }
  }
}
var WORKFLOW_STEP_OUTCOMES = [
  "done",
  "waiting",
  "on_hold",
  "running",
  "not_sent",
  "failed",
  "sent",
  "delivered",
  "signed",
  "filled_in",
  "yes",
  "no"
];
var WORKFLOW_STEP_OUTCOME_LABELS = {
  done: "Done",
  waiting: "Waiting",
  on_hold: "On hold",
  running: "In progress",
  not_sent: "Not sent",
  failed: "Failed",
  sent: "Sent",
  delivered: "Received",
  signed: "Signed",
  filled_in: "Filled in",
  yes: "Went Yes",
  no: "Went No"
};
var WORKFLOW_STEP_FILTERS = ["all", "done", "waiting", "problems", "on_hold"];
var WORKFLOW_STEP_FILTER_LABELS = {
  all: "Everyone",
  done: "Done",
  waiting: "Waiting",
  problems: "Problems",
  on_hold: "On hold"
};
function describeStepOutcome(stepType, outcome) {
  switch (outcome) {
    case "waiting":
      return "Waiting for their next step.";
    case "on_hold":
      return "Held because this workflow is paused. It will continue when you turn it back on.";
    case "running":
      return "Happening right now.";
    case "yes":
      return "Matched the check, so they went down the Yes path.";
    case "no":
      return "Didn't match the check, so they went down the No path.";
    case "signed":
      return "They signed the document.";
    case "filled_in":
      return "They filled in the form.";
    case "delivered":
      return "The other app received it.";
    case "sent":
      if (stepType === "send_document") return "The signing link was emailed. Not signed yet.";
      if (stepType === "send_form") return "The form link was emailed. Not filled in yet.";
      if (stepType === "send_webhook") return "Sent to the other app. Waiting to hear back.";
      return "Sent.";
    case "done": {
      if (stepType === "exit") return "They finished the workflow here.";
      const word = DONE_WORDS[stepType];
      return word ? `${word.charAt(0).toUpperCase()}${word.slice(1)}.` : "Done.";
    }
    case "not_sent":
      return "This never went out.";
    case "failed":
      return "Something went wrong at this step.";
  }
}
function describeStepProblem(input) {
  const error = (input.error ?? "").trim();
  const deliveryError = (input.deliveryError ?? "").trim();
  const custom = (group, title, fix) => ({
    group,
    title,
    fix
  });
  switch (input.stepType) {
    case "send_document":
      if (/no document chosen/i.test(error)) return custom("no_document", "This step has no document chosen", "Edit the workflow and pick a document for this step.");
      if (/document is unavailable/i.test(error)) return custom("document_missing", "The document for this step was deleted", "Edit the workflow and pick a document for this step.");
      if (/recipient email, or email consent/i.test(error)) return custom("email_or_consent", "No email address or no email consent for this person", "Check the contact has an email address and has agreed to receive emails.");
      return describeSendProblem({ ...input, channel: "email" });
    case "send_form":
      if (/no form chosen/i.test(error)) return custom("no_form", "This step has no form chosen", "Edit the workflow and pick a form for this step.");
      if (/form is unavailable/i.test(error)) return custom("form_missing", "The form for this step was deleted or unpublished", "Publish the form again, or pick another one in the workflow.");
      if (/recipient email, or email consent/i.test(error)) return custom("email_or_consent", "No email address or no email consent for this person", "Check the contact has an email address and has agreed to receive emails.");
      return describeSendProblem({ ...input, channel: "email" });
    case "send_webhook": {
      if (/no web address/i.test(error)) return custom("no_url", "This step has no web address", "Edit the workflow and add the web address of the app to notify.");
      const text = deliveryError || error;
      if (/HTTP\s*[45]\d\d/.test(text)) return custom("app_rejected", "The other app didn't accept it", "Check the web address in this step, or ask whoever runs that app.");
      if (/timeout|ENOTFOUND|ECONNREFUSED|ECONNRESET|getaddrinfo|network/i.test(text)) return custom("app_unreachable", "Couldn't reach the other app", "Check the web address is right and the app is online.");
      return custom("app_failed", "The other app didn't receive it", "Check the web address in this step, or contact support with the technical details.");
    }
    case "send_sms":
    case "send_email":
      return describeSendProblem({ ...input, channel: input.stepType === "send_email" ? "email" : "sms" });
    default:
      return custom("step_failed", "Something went wrong at this step", "If it keeps happening, contact support and share the technical details.");
  }
}
function providerMessage(raw) {
  if (!raw) return null;
  const text = raw.replace(/\s*\((Telnyx code|ClickSend status)[^)]*\)/g, "").replace(/^HTTP \d{3}:\s*/, "").trim();
  if (!text || /^(Failed to send SMS|SMS send failed|Email send failed|Unknown error)$/i.test(text)) return null;
  if (/^[A-Z0-9_]+$/.test(text)) return null;
  return text;
}
var CONSENT_WORDS = {
  UNKNOWN: "Not captured",
  NONE: "No consent",
  OPTOUT: "Opted out",
  IMPLICIT_OPT_IN: "Implied consent",
  EXPRESS_OPTIN: "Agreed",
  WRITTEN_EXPRESS_OPTIN: "Agreed in writing"
};
var REQUIRED_WORDS = {
  IMPLICIT_OPT_IN: "at least implied consent",
  EXPRESS_OPTIN: "their clear agreement (express consent)",
  WRITTEN_EXPRESS_OPTIN: "written consent"
};
var DNC_WORDS = {
  ON_LIST: "On the list",
  CLEAR: "Not on the list",
  NOT_ON_LIST: "Not on the list",
  UNKNOWN: "Not checked"
};
var CHANNEL_NOUN = { SMS: "texts", EMAIL: "emails", VOICE: "calls" };
var LINE_WORDS = { MOBILE: "Mobile", LANDLINE: "Landline", VOIP: "Internet phone", UNKNOWN: "Not known" };
function purposeWord(purpose) {
  return purpose === "MARKETING" ? "marketing" : "informational";
}
function localTimeWords(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const h = d.getUTCHours();
  const m = d.getUTCMinutes();
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"} on ${days[d.getUTCDay()]}`;
}
function describeFailedCheck(checkId, params, f) {
  const noun = CHANNEL_NOUN[f.channel] ?? "messages";
  const who = f.personName || "This person";
  const onFile = CONSENT_WORDS[f.consentOnFile ?? "UNKNOWN"] ?? "Not captured";
  switch (checkId) {
    case "CHK_CONTACT_ELIGIBLE":
    case "CHK_CASL_CONSENT_VALID":
    case "CHK_CASL_CONSPICUOUS_PUBLICATION_VALID":
    case "CHK_LGPD_LAWFUL_BASIS_VALID":
      return `${who} hasn't agreed to receive ${purposeWord(f.purpose)} ${noun}. Consent on file: ${onFile.toLowerCase()}.`;
    case "CHK_CONSENT_AT_LEAST": {
      const need = REQUIRED_WORDS[String(params?.minConsent ?? "EXPRESS_OPTIN")] ?? REQUIRED_WORDS.EXPRESS_OPTIN;
      return `${purposeWord(f.purpose).charAt(0).toUpperCase()}${purposeWord(f.purpose).slice(1)} ${noun} to ${who} need ${need}. Consent on file: ${onFile.toLowerCase()}.`;
    }
    case "CHK_WIRELESS_MARKETING_CONSENT_OK": {
      const need = REQUIRED_WORDS[String(params?.ifWirelessMinConsent ?? "EXPRESS_OPTIN")] ?? REQUIRED_WORDS.EXPRESS_OPTIN;
      const line = f.lineType === "MOBILE" ? "a mobile number" : "possibly a mobile number (the phone type is not known)";
      return `This is ${line}, and ${purposeWord(f.purpose)} ${noun} to mobiles need ${need}. Consent on file: ${onFile.toLowerCase()}.`;
    }
    case "CHK_INTERNAL_DNC_CLEAR":
      return `${who} is on your Do-Not-Contact list.`;
    case "CHK_NATIONAL_REGISTRY_CLEAR":
      return `This number is on the national Do-Not-Call registry.`;
    case "CHK_NATIONAL_REGISTRY_SCRUBBED": {
      const days = Number(params?.maxAgeDays) || 0;
      return f.nationalRegistry === "ON_LIST" ? "This number is on the national Do-Not-Call registry." : `This number hasn't been checked against the national Do-Not-Call registry${days ? ` in the last ${days} days` : ""}.`;
    }
    case "CHK_WITHIN_CALLING_HOURS": {
      const t = localTimeWords(f.localTime);
      return t ? `It was ${t} where ${who} is \u2014 outside the hours you're allowed to send ${noun}.` : `We couldn't tell what time it is where ${who} is, so we didn't risk sending outside allowed hours.`;
    }
    case "CHK_FREQUENCY_CAP_24H_OK":
      return `${who} already got ${f.attempts24h ?? "several"} messages about this in the last 24 hours (the limit is ${Number(params?.cap) || 3}).`;
    case "CHK_FREQUENCY_CAP_7D_OK":
      return `${who} already got ${f.contacts7d ?? "several"} messages about this in the last 7 days (the limit is ${Number(params?.cap) || 1}).`;
    case "CHK_CHANNEL_PREFERENCE_MATCH":
      return f.preferredChannel ? `${who} prefers to be contacted by ${(CHANNEL_NOUN[f.preferredChannel] ?? f.preferredChannel.toLowerCase()).replace(/s$/, "")}.` : `${who} prefers a different way of being contacted.`;
    case "CHK_REVOCATION_HANDLING_ENABLED":
      if (f.revocationSettingMissing) {
        return `A fault in our system stopped this: your ${f.channel === "EMAIL" ? "unsubscribe" : "STOP-reply"} setting wasn't passed to the compliance check, so the check failed even though nothing was wrong. This has been fixed.`;
      }
      return f.channel === "EMAIL" ? "Your account has unsubscribe handling turned off." : "Your account has STOP-reply handling turned off.";
    case "CHK_SENDER_ID_AND_UNSUB_PRESENT":
      return "The email is missing an unsubscribe link, your postal address, or a clear sender name.";
    default:
      return null;
  }
}
function fixFor(checkIds, f) {
  const has = (...ids) => ids.some((id) => checkIds.includes(id));
  const noun = CHANNEL_NOUN[f.channel] ?? "messages";
  if (has("CHK_INTERNAL_DNC_CLEAR")) return "Only remove them from your Do-Not-Contact list if they asked to hear from you again.";
  if (has("CHK_NATIONAL_REGISTRY_CLEAR")) return "You need their direct permission before contacting a number on the national registry.";
  if (has("CHK_CONTACT_ELIGIBLE", "CHK_CONSENT_AT_LEAST", "CHK_WIRELESS_MARKETING_CONSENT_OK", "CHK_CASL_CONSENT_VALID", "CHK_CASL_CONSPICUOUS_PUBLICATION_VALID", "CHK_LGPD_LAWFUL_BASIS_VALID")) {
    return f.consentOnFile === "OPTOUT" ? `They opted out of ${noun}. Don't contact them this way unless they opt back in.` : `Get their agreement to receive ${noun} (for example with a sign-up form or by recording it on their contact), or reach them another way.`;
  }
  if (has("CHK_WITHIN_CALLING_HOURS")) {
    return f.localTime ? "Nothing to do. Send again during their daytime hours." : "Add their time zone or address to the contact so we know their local time.";
  }
  if (has("CHK_NATIONAL_REGISTRY_SCRUBBED")) return "Run a Do-Not-Call registry check for this number (contact support if you need it turned on).";
  if (has("CHK_FREQUENCY_CAP_24H_OK", "CHK_FREQUENCY_CAP_7D_OK")) return "Nothing to do. You can message them again once the limit resets.";
  if (has("CHK_REVOCATION_HANDLING_ENABLED")) {
    return f.revocationSettingMissing ? `Nothing is wrong with this contact or your settings. You can send to them again.` : "Contact support to turn opt-out handling back on for your account.";
  }
  if (has("CHK_SENDER_ID_AND_UNSUB_PRESENT")) return "Edit the email so it has an unsubscribe link, your postal address and your business name.";
  return "Open the contact to check their consent and Do-Not-Contact status, or contact support.";
}
function explainPreBlock(input) {
  const { facts: f, failures } = input;
  const noun = CHANNEL_NOUN[f.channel] ?? "messages";
  const checkIds = failures.flatMap((r) => r.failedChecks.map((c) => c.checkId));
  const reasons = [];
  for (const r of failures) {
    for (const c of r.failedChecks) {
      const line = describeFailedCheck(c.checkId, c.params, f);
      if (line && !reasons.includes(line)) reasons.push(line);
    }
  }
  if (reasons.length === 0) {
    for (const r of failures) if (r.remediation && !reasons.includes(r.remediation)) reasons.push(r.remediation);
  }
  const facts = [
    { label: `Consent for ${purposeWord(f.purpose)} ${noun}`, value: CONSENT_WORDS[f.consentOnFile ?? "UNKNOWN"] ?? "Not captured" }
  ];
  if (f.channel !== "EMAIL") {
    if (f.internalDnc) facts.push({ label: "Your Do-Not-Contact list", value: DNC_WORDS[f.internalDnc] ?? f.internalDnc });
    if (f.nationalRegistry) facts.push({ label: "National Do-Not-Call registry", value: DNC_WORDS[f.nationalRegistry] ?? f.nationalRegistry });
    if (f.lineType) facts.push({ label: "Phone type", value: LINE_WORDS[f.lineType] ?? f.lineType });
  }
  const t = localTimeWords(f.localTime);
  facts.push({ label: "Their local time", value: t ?? "Not known" });
  const summary = reasons.length ? `Stopped by your compliance rules: ${reasons[0].charAt(0).toLowerCase()}${reasons[0].slice(1)}` : `Stopped by your compliance rules before it was sent.`;
  return {
    found: true,
    summary,
    reasons,
    facts,
    fix: fixFor(checkIds, f),
    rules: failures.map((r) => r.jurisdictionName ? `${r.ruleName} \xB7 ${r.jurisdictionName}` : r.ruleName),
    checkedAt: input.checkedAt
  };
}

// src/campaign-activity.ts
function deriveCampaignMessageStatus(f) {
  if (f.internalDncSuppressed) return "not_sent";
  const st = f.channelStatus ?? (f.singleChannel ? f.legacyStatus : null);
  switch (st) {
    case "held":
      return "on_hold";
    case "cancelled":
      return "not_sent";
    case "delivered":
    case "unsubscribed":
      return "delivered";
    case "opened":
    case "clicked":
      return "opened";
    case "sent":
      return "sent";
    case "failed":
    case "bounced":
      return f.handedToProvider ? "failed" : "not_sent";
  }
  if (f.hasRecordedProblem) return "not_sent";
  const now = f.now ?? /* @__PURE__ */ new Date();
  switch (f.campaignStatus) {
    case "draft":
    case "scheduled":
      return "scheduled";
    case "paused":
      return "on_hold";
    case "cancelled":
      return "not_sent";
    case "sending":
      return f.scheduledSendAt && new Date(f.scheduledSendAt) > now ? "scheduled" : "sending";
    default:
      if (st === "pending") return f.scheduledSendAt && new Date(f.scheduledSendAt) > now ? "scheduled" : "sending";
      return "not_sent";
  }
}
function describeCampaignMessageStatus(status, channel, ctx = {}) {
  switch (status) {
    case "scheduled":
      return ctx.campaignScheduled ? "Goes out when the campaign starts." : "Waiting for a time it\u2019s allowed to reach them, in their own time zone.";
    case "on_hold":
      return "Held because this campaign is paused. It goes out when you resume.";
    case "sending":
      return "Going out right now.";
    case "sent":
      return channel === "email" ? "Handed to the email service. Waiting for the inbox to confirm." : "Handed to the phone carrier. Waiting for the phone to confirm.";
    case "delivered":
      return channel === "email" ? "Reached their inbox." : "Reached their phone.";
    case "opened":
      return "They opened it.";
    case "not_sent":
      return "This never went out.";
    case "failed":
      return channel === "email" ? "It was sent, but the inbox rejected it." : "It was sent, but the carrier could not deliver it.";
  }
}
var CAMPAIGN_PROBLEMS = {
  campaign_cancelled: (c) => ({
    title: `You cancelled the campaign before this ${c === "email" ? "email" : "text"} went out`,
    fix: "Duplicate the campaign if you still want to reach them."
  }),
  check_failed: () => ({
    title: "The compliance check couldn't run, so nothing was sent",
    fix: "Usually temporary. Use \u201CResend failed\u201D to try again."
  }),
  no_channel: () => ({
    title: "None of the campaign\u2019s channels could reach this person",
    fix: "Open the contact to check their phone number, email and consent."
  }),
  never_sent: (c) => ({
    title: c === "email" ? "This email was never sent" : "This text was never sent",
    fix: "Use \u201CResend failed\u201D to try again. If it keeps happening, contact support."
  })
};
function describeCampaignProblemGroup(group, channel) {
  if (group in CAMPAIGN_PROBLEMS) {
    return { group, ...CAMPAIGN_PROBLEMS[group](channel) };
  }
  return describeProblemGroup(group, channel);
}
function describeCampaignProblem(input) {
  const { channel } = input;
  if (input.internalDncSuppressed) return describeProblemGroup("do_not_contact", channel);
  let error = (input.error ?? "").trim();
  let reasonCode = input.reasonCode ?? null;
  if (/campaign cancelled/i.test(error)) return describeCampaignProblemGroup("campaign_cancelled", channel);
  if (/compliance check couldn.t run/i.test(error)) return describeCampaignProblemGroup("check_failed", channel);
  if (/^no eligible channel/i.test(error)) return describeCampaignProblemGroup("no_channel", channel);
  if (/not opted in|consent level .* insufficient/i.test(error)) return describeProblemGroup("no_consent", channel);
  if (/^(REMED|COND|RISK)_[A-Z0-9_]+$/.test(error)) {
    reasonCode = reasonCode ?? error;
    error = `PRE blocked: ${error}`;
  } else if (/^blocked by (PRE|policy)/i.test(error)) {
    error = reasonCode ? `PRE blocked: ${reasonCode}` : "PRE blocked";
  }
  const retried = error.match(/^Delivery failed after \d+ attempts?:\s*(.*)$/i);
  if (retried) error = retried[1] ?? "";
  if (!error && !input.deliveryError && !reasonCode) {
    return describeCampaignProblemGroup(input.campaignCancelled ? "campaign_cancelled" : "never_sent", channel);
  }
  return describeSendProblem({
    error: error || null,
    deliveryError: input.deliveryError,
    reasonCode,
    blockedRuleName: input.blockedRuleName,
    channel
  });
}
function problemReasonPhrase(group, channel, title) {
  const noun = channel === "email" ? "emails" : "texts";
  const phrases = {
    no_phone: "they have no phone number on file",
    no_email: "they have no email address on file",
    no_consent: `they haven't agreed to receive ${noun}`,
    do_not_contact: "they\u2019re on your Do-Not-Contact list",
    outside_hours: "it was outside the hours you\u2019re allowed to message them",
    too_many_messages: "they\u2019d already had the most messages allowed for now",
    landline: "their number can\u2019t receive texts",
    invalid_number: "their phone number doesn\u2019t look right",
    opted_out: channel === "email" ? "they unsubscribed" : "they replied STOP",
    carrier_rejected: "the phone carrier rejected it",
    carrier_spam: "the phone carrier flagged it as possible spam",
    not_registered: "your texting number isn\u2019t registered with the carriers yet",
    account_funds: "your texting account ran out of credit",
    country_blocked: "texting to their country isn\u2019t turned on",
    bounced: "the email bounced",
    marked_spam: "they marked your email as spam",
    email_service: "your email account couldn\u2019t send it",
    campaign_cancelled: "you cancelled the campaign first",
    check_failed: "the compliance check couldn\u2019t run"
  };
  return phrases[group] ?? `of this: \u201C${title}\u201D`;
}
function nounFor(sendType, scope, channels, n = 1) {
  const one = n === 1;
  if (sendType === "document") return one ? "signing request" : "signing requests";
  if (sendType === "form") return one ? "form request" : "form requests";
  const ch = scope === "all" && channels.length === 1 ? channels[0] : scope;
  if (ch === "sms") return one ? "text" : "texts";
  if (ch === "email") return one ? "email" : "emails";
  return one ? "message" : "messages";
}
var people = (n) => `${n.toLocaleString("en-US")} ${n === 1 ? "person" : "people"}`;
var num = (n) => n.toLocaleString("en-US");
function describeCampaignStory(s, scope) {
  const { status, sendType, channels, scheduledAt } = s.campaign;
  const singleChannel = scope !== "all" || channels.length === 1;
  const who = (n) => singleChannel ? people(n) : `${num(n)} ${nounFor(sendType, scope, channels, n)}`;
  const noun = nounFor(sendType, scope, channels);
  const nouns = nounFor(sendType, scope, channels, 2);
  const channel = scope === "email" || scope === "all" && channels[0] === "email" && channels.length === 1 ? "email" : "sms";
  const out = [];
  if (status === "draft") {
    return [[{ kind: "text", text: `This campaign hasn\u2019t been sent yet. It will go to ${people(s.people)}.` }]];
  }
  if (status === "scheduled") {
    const lead = [{ kind: "text", text: `Your ${noun} goes out ` }];
    if (scheduledAt) lead.push({ kind: "when", iso: scheduledAt });
    else lead.push({ kind: "text", text: "soon" });
    lead.push({ kind: "text", text: " to " }, { kind: "count", value: s.upcoming, text: who(s.upcoming), filter: "upcoming", tone: "info" }, { kind: "text", text: "." });
    return [lead];
  }
  const reached = s.sent;
  if (status === "sending" && s.sending + s.upcoming > 0) {
    out.push([
      { kind: "text", text: `Your ${noun} is going out now. ` },
      { kind: "count", value: reached, text: `${who(reached)}`, filter: "sent", tone: "neutral" },
      { kind: "text", text: " so far." }
    ]);
  } else if (reached > 0) {
    out.push([
      { kind: "text", text: `Your ${noun} went to ` },
      { kind: "count", value: reached, text: who(reached), filter: "sent", tone: "neutral" },
      { kind: "text", text: "." }
    ]);
  } else {
    out.push([{ kind: "text", text: `None of your ${nouns} went out yet.` }]);
  }
  if (reached > 0) {
    const got = [];
    if (s.deliveryTracked > 0) {
      got.push({ kind: "count", value: s.delivered, text: `${num(s.delivered)} got it`, filter: "delivered", tone: "good" });
    } else {
      got.push({ kind: "text", text: "Your provider doesn\u2019t confirm delivery for these" });
    }
    if (s.completed !== null && s.completed > 0 && s.completedLabel) {
      got.push({ kind: "text", text: ", and " }, {
        kind: "count",
        value: s.completed,
        text: `${num(s.completed)} ${s.completedLabel === "Signed" ? "signed" : "filled it in"}`,
        filter: "completed",
        tone: "good"
      });
    } else if (s.opened > 0 && (scope === "email" || channels.includes("email"))) {
      got.push({ kind: "text", text: ", and " }, { kind: "count", value: s.opened, text: `${num(s.opened)} opened it`, filter: "delivered", tone: "good" });
    }
    got.push({ kind: "text", text: "." });
    out.push(got);
  }
  if (s.notSent > 0) {
    const top = s.problems[0];
    const sentence = [
      { kind: "count", value: s.notSent, text: `${num(s.notSent)} didn\u2019t go out`, filter: "not_sent", tone: "bad" }
    ];
    if (top) {
      const mostly = top.count >= s.notSent * 0.5 ? top.count === s.notSent ? " because " : ", mostly because " : ", most often because ";
      sentence.push({ kind: "text", text: `${mostly}${problemReasonPhrase(top.group, channel, top.title)}` });
    }
    sentence.push({ kind: "text", text: "." });
    out.push(sentence);
  }
  if (s.failed > 0) {
    out.push([
      { kind: "count", value: s.failed, text: `${num(s.failed)} couldn\u2019t be delivered`, filter: "failed", tone: "warn" },
      { kind: "text", text: " after they were sent." }
    ]);
  }
  if (s.onHold > 0) {
    out.push([
      { kind: "count", value: s.onHold, text: `${num(s.onHold)} ${s.onHold === 1 ? "is" : "are"} on hold`, filter: "on_hold", tone: "warn" },
      { kind: "text", text: " until you resume the campaign." }
    ]);
  }
  if (s.upcoming > 0 && status !== "paused") {
    const w = [
      { kind: "count", value: s.upcoming, text: `${num(s.upcoming)} ${s.upcoming === 1 ? "is" : "are"} waiting`, filter: "upcoming", tone: "info" },
      { kind: "text", text: ` for a time we\u2019re allowed to reach them` }
    ];
    if (s.nextUpcomingAt) w.push({ kind: "text", text: ". The next goes out " }, { kind: "when", iso: s.nextUpcomingAt });
    w.push({ kind: "text", text: "." });
    out.push(w);
  }
  if (status === "cancelled") out.push([{ kind: "text", text: "You cancelled this campaign." }]);
  else if (reached > 0 && s.notSent === 0 && s.failed === 0 && s.onHold === 0 && s.upcoming === 0 && s.sending === 0) {
    out.push([{ kind: "text", text: "Nothing went wrong." }]);
  }
  return out;
}

// src/compliance-review.ts
var COMPLIANCE_TRIAGES = ["you", "none", "ours"];
var COMPLIANCE_TRIAGE_LABELS = {
  you: "Needs you",
  none: "No action needed",
  ours: "Our fault \xB7 fixed"
};
var REASONS = {
  no_consent: {
    title: "They haven't agreed to receive these messages",
    fix: "Get their agreement first, for example with a sign-up form or by recording consent on their contact. Until then, reach them another way.",
    triage: "you"
  },
  opted_out: {
    title: "They opted out",
    fix: "They asked not to be contacted this way. Don't message them unless they opt back in.",
    triage: "none"
  },
  do_not_contact: {
    title: "They\u2019re on your Do-Not-Contact list",
    fix: "Nothing to do. Only remove someone from the list if they asked to hear from you again.",
    triage: "none"
  },
  national_registry: {
    title: "Their number is on the national Do-Not-Call registry",
    fix: "Nothing to do. You need their direct permission before contacting these numbers.",
    triage: "none"
  },
  registry_not_checked: {
    title: "Their number hasn\u2019t been checked against the Do-Not-Call registry",
    fix: "Marketing to these numbers needs a recent registry check. Contact support to turn registry checks on.",
    triage: "you"
  },
  missing_timezone: {
    title: "We don\u2019t know their local time",
    fix: "Add an address or time zone to these contacts so we can send inside their allowed hours.",
    triage: "you"
  },
  outside_hours: {
    title: "It was outside the hours you\u2019re allowed to message them",
    fix: "Nothing to do. Send again during their daytime hours.",
    triage: "none"
  },
  too_many_messages: {
    title: "They\u2019d already had the most messages allowed for now",
    fix: "Nothing to do. You can message them again once the limit resets.",
    triage: "none"
  },
  channel_preference: {
    title: "They prefer to be contacted another way",
    fix: "Nothing to do. Reach them on the channel they prefer.",
    triage: "none"
  },
  opt_out_handling_off: {
    title: "STOP-reply or unsubscribe handling is turned off for your account",
    fix: "Contact support to turn opt-out handling back on. Messages can\u2019t go out until it is.",
    triage: "you"
  },
  email_footer_missing: {
    title: "The email is missing an unsubscribe link, postal address or sender name",
    fix: "Edit the email so it has an unsubscribe link, your postal address and your business name.",
    triage: "you"
  },
  system_fault: {
    title: "A fault on our side stopped these",
    fix: "Nothing was wrong with these contacts or your settings, and the fault has been fixed. You can send to them again.",
    triage: "ours"
  },
  other: {
    title: "Stopped by one of your compliance rules",
    fix: "Open a person below to see exactly which rule stopped it and why.",
    triage: "you"
  }
};
function describeComplianceReason(key) {
  return REASONS[key] ?? REASONS.other;
}
var CONSENT_CHECKS = [
  "CHK_CONTACT_ELIGIBLE",
  "CHK_CONSENT_AT_LEAST",
  "CHK_WIRELESS_MARKETING_CONSENT_OK",
  "CHK_CASL_CONSENT_VALID",
  "CHK_CASL_CONSPICUOUS_PUBLICATION_VALID",
  "CHK_LGPD_LAWFUL_BASIS_VALID"
];
function complianceReasonFor(checkIds, f) {
  const has = (...ids) => ids.some((id) => checkIds.includes(id));
  if (has("CHK_INTERNAL_DNC_CLEAR")) return "do_not_contact";
  if (has("CHK_NATIONAL_REGISTRY_CLEAR")) return "national_registry";
  if (has(...CONSENT_CHECKS)) return f.consentOnFile === "OPTOUT" ? "opted_out" : "no_consent";
  if (has("CHK_WITHIN_CALLING_HOURS")) return f.localTime ? "outside_hours" : "missing_timezone";
  if (has("CHK_NATIONAL_REGISTRY_SCRUBBED")) {
    return f.nationalRegistry === "ON_LIST" ? "national_registry" : "registry_not_checked";
  }
  if (has("CHK_FREQUENCY_CAP_24H_OK", "CHK_FREQUENCY_CAP_7D_OK")) return "too_many_messages";
  if (has("CHK_CHANNEL_PREFERENCE_MATCH")) return "channel_preference";
  if (has("CHK_REVOCATION_HANDLING_ENABLED")) return f.revocationSettingMissing ? "system_fault" : "opt_out_handling_off";
  if (has("CHK_SENDER_ID_AND_UNSUB_PRESENT")) return "email_footer_missing";
  return "other";
}
var COMPLIANCE_REVIEW_WINDOWS = ["24h", "7d", "30d"];
var COMPLIANCE_REVIEW_WINDOW_LABELS = {
  "24h": "Last 24 hours",
  "7d": "Last 7 days",
  "30d": "Last 30 days"
};
var OPT_OUT_HOW = {
  keyword: "Replied STOP",
  link: "Clicked unsubscribe",
  manual: "Marked by your team"
};
function describeOptOutSource(source, channel) {
  if (source && OPT_OUT_HOW[source]) return OPT_OUT_HOW[source];
  return channel === "email" ? "Unsubscribed" : "Opted out";
}
var num2 = (n) => n.toLocaleString("en-US");
var plural = (n, one, many) => `${num2(n)} ${n === 1 ? one : many}`;
var WINDOW_LEAD = {
  "24h": "In the last 24 hours",
  "7d": "In the last 7 days",
  "30d": "In the last 30 days"
};
function describeComplianceStory(s) {
  const out = [];
  const lead = WINDOW_LEAD[s.window];
  if (s.messagesStopped === 0) {
    out.push([{ kind: "text", text: `${lead}, your compliance rules didn\u2019t need to stop any messages.` }]);
  } else {
    out.push([
      { kind: "text", text: `${lead}, your compliance rules stopped ` },
      { kind: "count", value: s.messagesStopped, text: plural(s.messagesStopped, "message", "messages"), filter: "all" },
      { kind: "text", text: ` to ${plural(s.peopleStopped, "person", "people")}.` }
    ]);
    const you = s.triage.you;
    const none = s.triage.none;
    const ours = s.triage.ours;
    const youReasons = s.reasons.filter((r) => r.triage === "you");
    const top = youReasons[0];
    const because = top && top.key !== "other" ? ` \u2014 ${youReasons.length > 1 ? "mostly " : ""}because ${lowerFirst(top.title)}.` : ".";
    if (you > 0) {
      out.push([
        { kind: "count", value: you, text: num2(you), filter: "you" },
        { kind: "text", text: (you === 1 ? " needs something from you" : " need something from you") + because }
      ]);
    } else {
      out.push([{ kind: "text", text: "None of them need anything from you." }]);
    }
    if (none > 0) {
      out.push([
        { kind: "count", value: none, text: num2(none), filter: "none" },
        { kind: "text", text: none === 1 ? " was stopped for a good reason and needs nothing from you." : " were stopped for a good reason and need nothing from you." }
      ]);
    }
    if (ours > 0) {
      out.push([
        { kind: "count", value: ours, text: num2(ours), filter: "ours" },
        { kind: "text", text: ours === 1 ? " was stopped by a fault on our side. It\u2019s fixed." : " were stopped by a fault on our side. It\u2019s fixed." }
      ]);
    }
  }
  const extra = [];
  if (s.optOuts > 0) {
    extra.push(
      { kind: "text", text: "Also, " },
      { kind: "count", value: s.optOuts, text: plural(s.optOuts, "person", "people"), filter: "opt_outs" },
      { kind: "text", text: " opted out" }
    );
  }
  if (s.expiringConsents > 0) {
    extra.push(
      { kind: "text", text: extra.length ? ", and " : "Also, " },
      { kind: "count", value: s.expiringConsents, text: plural(s.expiringConsents, "consent", "consents"), filter: "expiring" },
      { kind: "text", text: s.expiringConsents === 1 ? " runs out in the next 30 days" : " run out in the next 30 days" }
    );
  }
  if (extra.length) {
    extra.push({ kind: "text", text: "." });
    out.push(extra);
  }
  return out;
}
function lowerFirst(s) {
  if (/^[A-Z]{2}/.test(s)) return s;
  return s.charAt(0).toLowerCase() + s.slice(1);
}

// src/message-purpose.ts
var MESSAGE_PURPOSES = ["informational", "marketing", "transactional"];
var MESSAGE_PURPOSE_LABELS = {
  informational: "Informational",
  marketing: "Marketing",
  transactional: "Transactional"
};
var MESSAGE_PURPOSE_DESCRIPTIONS = {
  informational: "Reminders, updates and notices. Needs informational consent.",
  marketing: "Promotions, offers and sales. Needs marketing consent.",
  transactional: "Receipts and account alerts the contact triggered."
};
var MESSAGE_PURPOSE_COPY = {
  fieldLabel: "Message purpose",
  autoOption: "Detect automatically (AI)",
  fieldHelp: "Decides which consent a contact needs before this template can be sent to them.",
  setByAi: "Detected by AI",
  setByYou: "Set by you",
  notClassified: "Not classified yet. It is treated as Marketing until it is.",
  autoAfterSave: "AI will detect the purpose when you save.",
  classificationFailedTitle: "Purpose not detected",
  classificationFailedBody: "Saved, but AI could not detect the message purpose. It is treated as Marketing until it is classified, which happens on its first send. You can also set it yourself."
};
function describeStoredPurpose(messageType, source) {
  if (!messageType) return MESSAGE_PURPOSE_COPY.notClassified;
  const by = source === "user" ? MESSAGE_PURPOSE_COPY.setByYou : MESSAGE_PURPOSE_COPY.setByAi;
  return `${MESSAGE_PURPOSE_LABELS[messageType]} \xB7 ${by}`;
}
var EMAIL_TEMPLATE_KEYS = [
  "verification",
  "welcome",
  "forgot-password",
  "account-linked",
  "payment-success",
  "payment-failed",
  "support-ticket-created",
  "support-reply-received",
  "team-invitation",
  "account-suspended",
  "esignature-request",
  "signature-completed",
  "consent-confirmation"
];
var EMAIL_TEMPLATE_DEFINITIONS = {
  "verification": {
    key: "verification",
    name: "Email Verification",
    description: "Sent when a new user signs up and needs to verify their email address.",
    defaultSubject: "Verify your email address \u2014 Respectly",
    category: "auth",
    senderType: "respectly",
    variables: [
      { key: "firstName", label: "First Name", placeholder: "{{firstName}}", group: "contact", description: "Recipient's first name", sampleValue: "Jane", required: true },
      { key: "verifyUrl", label: "Verification URL", placeholder: "{{verifyUrl}}", group: "system", description: "Link for the user to verify their email", sampleValue: "https://app.respectly.ai/verify-email?token=sample123", required: true }
    ]
  },
  "welcome": {
    key: "welcome",
    name: "Welcome Email",
    description: "Sent after a user successfully verifies their email address.",
    defaultSubject: "Welcome to Respectly!",
    category: "auth",
    senderType: "respectly",
    variables: [
      { key: "firstName", label: "First Name", placeholder: "{{firstName}}", group: "contact", description: "Recipient's first name", sampleValue: "Jane", required: true },
      { key: "loginUrl", label: "Login URL", placeholder: "{{loginUrl}}", group: "system", description: "Link to the login page", sampleValue: "https://app.respectly.ai/login", required: true }
    ]
  },
  "forgot-password": {
    key: "forgot-password",
    name: "Password Reset",
    description: "Sent when a user requests a password reset.",
    defaultSubject: "Reset your password \u2014 Respectly",
    category: "auth",
    senderType: "respectly",
    variables: [
      { key: "firstName", label: "First Name", placeholder: "{{firstName}}", group: "contact", description: "Recipient's first name", sampleValue: "Jane", required: true },
      { key: "resetUrl", label: "Reset URL", placeholder: "{{resetUrl}}", group: "system", description: "Link to reset the password", sampleValue: "https://app.respectly.ai/reset-password?token=sample123", required: true }
    ]
  },
  "account-linked": {
    key: "account-linked",
    name: "Account Linked",
    description: "Sent when a user links an OAuth provider (Google, Microsoft, Apple).",
    defaultSubject: "Your account has been linked \u2014 Respectly",
    category: "auth",
    senderType: "respectly",
    variables: [
      { key: "firstName", label: "First Name", placeholder: "{{firstName}}", group: "contact", description: "Recipient's first name", sampleValue: "Jane", required: true },
      { key: "provider", label: "Provider", placeholder: "{{provider}}", group: "system", description: "OAuth provider name (Google, Microsoft, Apple)", sampleValue: "Google", required: true },
      { key: "linkedAt", label: "Linked Date", placeholder: "{{linkedAt}}", group: "system", description: "Date and time the account was linked", sampleValue: "January 20, 2026", required: true }
    ]
  },
  "payment-success": {
    key: "payment-success",
    name: "Payment Confirmation",
    description: "Sent to the account owner after a successful payment.",
    defaultSubject: "Payment Received \u2014 Respectly",
    category: "billing",
    senderType: "respectly",
    variables: [
      { key: "firstName", label: "First Name", placeholder: "{{firstName}}", group: "contact", description: "Recipient's first name", sampleValue: "Jane", required: true },
      { key: "tenantName", label: "Organization Name", placeholder: "{{tenantName}}", group: "billing", description: "Name of the organization", sampleValue: "Acme Corp", required: true },
      { key: "planName", label: "Plan Name", placeholder: "{{planName}}", group: "billing", description: "Subscription plan purchased", sampleValue: "Professional", required: true },
      { key: "amount", label: "Amount", placeholder: "{{amount}}", group: "billing", description: "Payment amount (e.g. $99.00 CAD)", sampleValue: "$99.00", required: true },
      { key: "invoiceNumber", label: "Invoice Number", placeholder: "{{invoiceNumber}}", group: "billing", description: "Invoice reference number", sampleValue: "INV-0001", required: true },
      { key: "transactionId", label: "Transaction ID", placeholder: "{{transactionId}}", group: "billing", description: "Helcim transaction ID", sampleValue: "TXN-123456", required: true },
      { key: "invoiceUrl", label: "Invoice URL", placeholder: "{{invoiceUrl}}", group: "billing", description: "Link to view the invoice", sampleValue: "https://app.respectly.ai/billing", required: true }
    ]
  },
  "payment-failed": {
    key: "payment-failed",
    name: "Payment Failed",
    description: "Sent to the account owner when a payment fails.",
    defaultSubject: "Payment Failed - Action Required - Respectly",
    category: "billing",
    senderType: "respectly",
    variables: [
      { key: "recipientName", label: "Recipient Name", placeholder: "{{recipientName}}", group: "contact", description: "Recipient's name", sampleValue: "Jane", required: true },
      { key: "tenantName", label: "Organization Name", placeholder: "{{tenantName}}", group: "billing", description: "Name of the organization", sampleValue: "Acme Corp", required: true },
      { key: "amount", label: "Amount", placeholder: "{{amount}}", group: "billing", description: "Payment amount that failed", sampleValue: "$99.00", required: true },
      { key: "failureReason", label: "Failure Reason", placeholder: "{{failureReason}}", group: "billing", description: "Reason the payment failed", sampleValue: "Insufficient funds", required: true },
      { key: "gracePeriodDays", label: "Grace Period (Days)", placeholder: "{{gracePeriodDays}}", group: "billing", description: "Days until account is suspended", sampleValue: "7", required: true },
      { key: "billingUrl", label: "Billing URL", placeholder: "{{billingUrl}}", group: "billing", description: "Link to update payment method", sampleValue: "https://app.respectly.ai/billing", required: true }
    ]
  },
  "support-ticket-created": {
    key: "support-ticket-created",
    name: "Support Ticket Created",
    description: "Sent to the user who opens a new support ticket.",
    defaultSubject: "Support ticket received \u2014 Respectly",
    category: "support",
    senderType: "respectly",
    variables: [
      { key: "firstName", label: "First Name", placeholder: "{{firstName}}", group: "contact", description: "Recipient's first name", sampleValue: "Jane", required: true },
      { key: "ticketSubject", label: "Ticket Subject", placeholder: "{{ticketSubject}}", group: "support", description: "Subject of the support ticket", sampleValue: "Unable to send documents", required: true },
      { key: "ticketId", label: "Ticket ID", placeholder: "{{ticketId}}", group: "support", description: "Unique ticket identifier", sampleValue: "TKT-001", required: true },
      { key: "viewUrl", label: "View URL", placeholder: "{{viewUrl}}", group: "support", description: "Link to view the ticket", sampleValue: "https://app.respectly.ai/support/TKT-001", required: true }
    ]
  },
  "support-reply-received": {
    key: "support-reply-received",
    name: "Support Reply Received",
    description: "Sent to the user when support replies to their ticket.",
    defaultSubject: "New reply on your support ticket \u2014 Respectly",
    category: "support",
    senderType: "respectly",
    variables: [
      { key: "firstName", label: "First Name", placeholder: "{{firstName}}", group: "contact", description: "Recipient's first name", sampleValue: "Jane", required: true },
      { key: "ticketSubject", label: "Ticket Subject", placeholder: "{{ticketSubject}}", group: "support", description: "Subject of the support ticket", sampleValue: "Unable to send documents", required: true },
      { key: "viewUrl", label: "View URL", placeholder: "{{viewUrl}}", group: "support", description: "Link to view the reply", sampleValue: "https://app.respectly.ai/support/TKT-001", required: true }
    ]
  },
  "team-invitation": {
    key: "team-invitation",
    name: "Team Invitation",
    description: "Sent when a user is invited to join an organization.",
    defaultSubject: "You have been invited to join a team \u2014 Respectly",
    category: "team",
    senderType: "respectly",
    variables: [
      { key: "inviterName", label: "Inviter Name", placeholder: "{{inviterName}}", group: "team", description: "Name of the person sending the invite", sampleValue: "John Smith", required: true },
      { key: "organizationName", label: "Organization Name", placeholder: "{{organizationName}}", group: "team", description: "Name of the organization", sampleValue: "Acme Corp", required: true },
      { key: "roleName", label: "Role", placeholder: "{{roleName}}", group: "team", description: "Role the invitee is being assigned", sampleValue: "Member", required: true },
      { key: "acceptUrl", label: "Accept URL", placeholder: "{{acceptUrl}}", group: "team", description: "Link to accept the invitation", sampleValue: "https://app.respectly.ai/invite?token=sample123", required: true }
    ]
  },
  "account-suspended": {
    key: "account-suspended",
    name: "Account Suspended",
    description: "Sent to all active users when their account is suspended by a Super Admin.",
    defaultSubject: "Account Suspended \u2014 Respectly",
    category: "billing",
    senderType: "respectly",
    variables: [
      { key: "recipientName", label: "Recipient Name", placeholder: "{{recipientName}}", group: "contact", description: "Recipient's name", sampleValue: "Jane", required: true },
      { key: "tenantName", label: "Organization Name", placeholder: "{{tenantName}}", group: "billing", description: "Name of the suspended organization", sampleValue: "Acme Corp", required: true },
      { key: "reason", label: "Suspension Reason", placeholder: "{{reason}}", group: "billing", description: "Reason for the suspension", sampleValue: "Overdue payment", required: true },
      { key: "supportEmail", label: "Support Email", placeholder: "{{supportEmail}}", group: "support", description: "Support contact email address", sampleValue: "support@respectly.ai", required: true }
    ]
  },
  "esignature-request": {
    key: "esignature-request",
    name: "eSignature Request",
    description: "Sent to a contact when they are requested to sign a document.",
    defaultSubject: "Document Signature Request \u2014 Respectly",
    category: "documents",
    senderType: "customer",
    variables: [
      { key: "recipientName", label: "Recipient Name", placeholder: "{{recipientName}}", group: "contact", description: "Name of the person being asked to sign", sampleValue: "Jane Doe", required: true },
      { key: "senderName", label: "Sender / Organization Name", placeholder: "{{senderName}}", group: "team", description: "Name of the organization or person requesting the signature", sampleValue: "Acme Corp", required: true },
      { key: "documentName", label: "Document Name", placeholder: "{{documentName}}", group: "document", description: "Name of the document to be signed", sampleValue: "Service Agreement 2026", required: true },
      { key: "message", label: "Custom Message", placeholder: "{{message}}", group: "document", description: "Optional personal message from the sender", sampleValue: "Please review and sign at your earliest convenience.", required: false },
      { key: "signingUrl", label: "Signing URL", placeholder: "{{signingUrl}}", group: "document", description: "Link for the recipient to sign the document", sampleValue: "https://sign.respectly.ai/d/sample123", required: true },
      { key: "expiresAt", label: "Expiry Date", placeholder: "{{expiresAt}}", group: "document", description: "Date the signing link expires (blank if no expiry)", sampleValue: "February 28, 2026", required: false },
      { key: "tenantLogoUrl", label: "Organization Logo URL", placeholder: "{{tenantLogoUrl}}", group: "team", description: "URL to the organization's logo image shown in the email header", sampleValue: "https://example.com/logo.png", required: false },
      { key: "tenantAddress", label: "Organization Address", placeholder: "{{tenantAddress}}", group: "team", description: "Organization's physical postal address shown in the email footer", sampleValue: "123 Main St \xB7 Toronto, ON \xB7 M5V 1A1 \xB7 CA", required: false },
      { key: "tenantWebsite", label: "Organization Website", placeholder: "{{tenantWebsite}}", group: "team", description: "Organization's website URL shown in the email footer", sampleValue: "https://www.acmecorp.com", required: false }
    ]
  },
  "signature-completed": {
    key: "signature-completed",
    name: "Signature Completed",
    description: "Sent to the signer after they successfully sign a document.",
    defaultSubject: "Document signed \u2014 Respectly",
    category: "documents",
    senderType: "customer",
    variables: [
      { key: "recipientName", label: "Recipient Name", placeholder: "{{recipientName}}", group: "contact", description: "Name of the person who signed", sampleValue: "Jane Doe", required: true },
      { key: "documentName", label: "Document Name", placeholder: "{{documentName}}", group: "document", description: "Name of the signed document", sampleValue: "Service Agreement 2026", required: true },
      { key: "signedAt", label: "Signed At", placeholder: "{{signedAt}}", group: "document", description: "Date and time the document was signed", sampleValue: "January 20, 2026 at 2:30 PM", required: true },
      { key: "downloadUrl", label: "Download URL", placeholder: "{{downloadUrl}}", group: "document", description: "Link to download the signed document", sampleValue: "https://sign.respectly.ai/download/sample123", required: true },
      { key: "tenantName", label: "Organization Name", placeholder: "{{tenantName}}", group: "team", description: "Organization's name shown in the email header and footer", sampleValue: "Acme Corp", required: false },
      { key: "tenantLogoUrl", label: "Organization Logo URL", placeholder: "{{tenantLogoUrl}}", group: "team", description: "URL to the organization's logo image shown in the email header", sampleValue: "https://example.com/logo.png", required: false },
      { key: "tenantAddress", label: "Organization Address", placeholder: "{{tenantAddress}}", group: "team", description: "Organization's physical postal address shown in the email footer", sampleValue: "123 Main St \xB7 Toronto, ON \xB7 M5V 1A1 \xB7 CA", required: false },
      { key: "tenantWebsite", label: "Organization Website", placeholder: "{{tenantWebsite}}", group: "team", description: "Organization's website URL shown in the email footer", sampleValue: "https://www.acmecorp.com", required: false }
    ]
  },
  "consent-confirmation": {
    key: "consent-confirmation",
    name: "Consent Confirmation",
    description: "Sent to contacts who must confirm their opt-in when double opt-in is enabled.",
    defaultSubject: "Please confirm your opt-in \u2014 Respectly",
    category: "documents",
    senderType: "customer",
    variables: [
      { key: "firstName", label: "First Name", placeholder: "{{firstName}}", group: "contact", description: "Recipient's first name", sampleValue: "Jane", required: true },
      { key: "tenantName", label: "Organization Name", placeholder: "{{tenantName}}", group: "team", description: "Name of the organization requesting opt-in", sampleValue: "Acme Corp", required: true },
      { key: "confirmationUrl", label: "Confirmation URL", placeholder: "{{confirmationUrl}}", group: "system", description: "Link to confirm the opt-in", sampleValue: "https://app.respectly.ai/consent/confirm?token=sample123", required: true },
      { key: "expiryHours", label: "Expiry (Hours)", placeholder: "{{expiryHours}}", group: "system", description: "Hours until the confirmation link expires", sampleValue: "48", required: true },
      { key: "tenantLogoUrl", label: "Organization Logo URL", placeholder: "{{tenantLogoUrl}}", group: "team", description: "URL to the organization's logo image shown in the email header", sampleValue: "https://example.com/logo.png", required: false },
      { key: "tenantAddress", label: "Organization Address", placeholder: "{{tenantAddress}}", group: "team", description: "Organization's physical postal address shown in the email footer", sampleValue: "123 Main St \xB7 Toronto, ON \xB7 M5V 1A1 \xB7 CA", required: false },
      { key: "tenantWebsite", label: "Organization Website", placeholder: "{{tenantWebsite}}", group: "team", description: "Organization's website URL shown in the email footer", sampleValue: "https://www.acmecorp.com", required: false }
    ]
  }
};
var ALL_EMAIL_TEMPLATE_VARIABLES = (() => {
  const seen = /* @__PURE__ */ new Set();
  const result = [];
  for (const def of Object.values(EMAIL_TEMPLATE_DEFINITIONS)) {
    for (const v of def.variables) {
      if (!seen.has(v.key)) {
        seen.add(v.key);
        result.push(v);
      }
    }
  }
  return result;
})();
var updateEmailTemplateSchema = z.object({
  subject: z.string().min(1, "Subject is required").max(300, "Subject must be under 300 characters"),
  htmlContent: z.string().min(1, "HTML content is required"),
  preheader: z.string().max(200, "Preheader must be under 200 characters").optional()
});
var testSendEmailTemplateSchema = z.object({
  to: z.string().email("A valid email address is required")
});
var setupStepIdSchema = z.enum([
  "welcome",
  "business-details",
  "brand-assets",
  "setup-usecase",
  "contact-import",
  "communications",
  "subscription",
  "setup-ready",
  // Legacy step IDs preserved for backwards-compatibility with stored progress
  "setup-goals",
  "feature-discovery",
  "document-template",
  "campaign-starter"
]);
var setupIntentSchema = z.enum(["campaigns", "documents", "explore"]);
var setupProgressSchema = z.object({
  setupCompletedAt: z.string().datetime().nullable(),
  setupSkippedAt: z.string().datetime().nullable(),
  setupStepsCompleted: z.array(setupStepIdSchema),
  subscriptionPromptTiming: z.enum(["early", "late"]),
  setupIntent: z.array(setupIntentSchema).default([])
});
var updateSetupProgressSchema = z.object({
  completedSteps: z.array(setupStepIdSchema).optional(),
  skippedAt: z.string().datetime().nullable().optional(),
  completedAt: z.string().datetime().nullable().optional(),
  setupIntent: z.array(setupIntentSchema).optional(),
  /** Optional insurance funnel marker, not an installation instruction. */
  selectedKitId: z.literal("insurance-brokers").nullable().optional()
});

// src/short-link.types.ts
var SHORT_LINK_PURPOSES = ["intake_prefill", "signing_link"];

// src/dashboard.types.ts
var DASHBOARD_WIDGET_REGISTRY = {
  // Zone H — page-top KPI strip + onboarding checklist
  "kpi-strip": {
    id: "kpi-strip",
    title: "At a Glance",
    zone: "H",
    defaultVisible: true,
    requiredPermission: null,
    refreshSeconds: 120,
    cacheTtlSeconds: 60,
    realtime: false,
    desktopColSpan: 12
  },
  "getting-started": {
    id: "getting-started",
    title: "Getting Started",
    zone: "H",
    defaultVisible: true,
    requiredPermission: null,
    refreshSeconds: 60,
    cacheTtlSeconds: 30,
    realtime: false,
    desktopColSpan: 12
  },
  // Zone A — live, short cache
  "outstanding-signatures": {
    id: "outstanding-signatures",
    title: "Outstanding Signatures",
    zone: "A",
    defaultVisible: true,
    requiredPermission: "documents:read",
    refreshSeconds: 30,
    cacheTtlSeconds: 30,
    realtime: true,
    desktopColSpan: 3
  },
  "unread-inbox": {
    id: "unread-inbox",
    title: "Unread Inbox",
    zone: "A",
    defaultVisible: true,
    requiredPermission: "inbox:read",
    refreshSeconds: 30,
    cacheTtlSeconds: 30,
    realtime: true,
    desktopColSpan: 3
  },
  "failed-deliveries": {
    id: "failed-deliveries",
    title: "Failed Deliveries (24h)",
    zone: "A",
    defaultVisible: true,
    requiredPermission: "campaigns:read",
    refreshSeconds: 60,
    cacheTtlSeconds: 30,
    realtime: true,
    desktopColSpan: 3
  },
  "compliance-attention": {
    id: "compliance-attention",
    title: "Compliance Attention",
    zone: "A",
    defaultVisible: true,
    requiredPermission: null,
    refreshSeconds: 60,
    cacheTtlSeconds: 60,
    realtime: true,
    desktopColSpan: 3
  },
  // Zone B — analytical
  "intake-form-submission-trend": {
    id: "intake-form-submission-trend",
    title: "Form Submission Trend",
    zone: "B",
    defaultVisible: true,
    requiredPermission: "forms:read",
    refreshSeconds: 120,
    cacheTtlSeconds: 120,
    realtime: false,
    defaultWindow: "30d",
    desktopColSpan: 6
  },
  "average-turnaround-time": {
    id: "average-turnaround-time",
    title: "Average Turnaround Time",
    zone: "B",
    defaultVisible: true,
    requiredPermission: "documents:read",
    refreshSeconds: 300,
    cacheTtlSeconds: 300,
    realtime: false,
    defaultWindow: "30d",
    desktopColSpan: 3
  },
  "document-signature-funnel": {
    id: "document-signature-funnel",
    title: "Document Signature Funnel",
    zone: "B",
    defaultVisible: true,
    requiredPermission: "documents:read",
    refreshSeconds: 120,
    cacheTtlSeconds: 120,
    realtime: false,
    defaultWindow: "30d",
    desktopColSpan: 6
  },
  "multichannel-communication-summary": {
    id: "multichannel-communication-summary",
    title: "Multichannel Communication Summary",
    zone: "B",
    defaultVisible: true,
    requiredPermission: "campaigns:read",
    refreshSeconds: 120,
    cacheTtlSeconds: 120,
    realtime: false,
    defaultWindow: "7d",
    desktopColSpan: 6
  },
  "total-contacts": {
    id: "total-contacts",
    title: "Total Contacts",
    zone: "B",
    defaultVisible: true,
    requiredPermission: "contacts:read",
    refreshSeconds: 300,
    cacheTtlSeconds: 300,
    realtime: false,
    desktopColSpan: 3
  },
  "intake-forms-completed": {
    id: "intake-forms-completed",
    title: "Intake Forms Completed",
    zone: "B",
    defaultVisible: true,
    requiredPermission: "forms:read",
    refreshSeconds: 120,
    cacheTtlSeconds: 120,
    realtime: false,
    desktopColSpan: 3
  },
  // Zone B′ — per-entity tables
  "campaign-activity": {
    id: "campaign-activity",
    title: "Campaign Activity",
    zone: "B-prime",
    defaultVisible: true,
    requiredPermission: "campaigns:read",
    refreshSeconds: 60,
    cacheTtlSeconds: 60,
    realtime: false,
    defaultWindow: "30d",
    desktopColSpan: 12
  },
  "document-status": {
    id: "document-status",
    title: "Document Status",
    zone: "B-prime",
    defaultVisible: true,
    requiredPermission: "documents:read",
    refreshSeconds: 60,
    cacheTtlSeconds: 60,
    realtime: false,
    desktopColSpan: 6
  },
  "form-activity": {
    id: "form-activity",
    title: "Form Activity",
    zone: "B-prime",
    defaultVisible: true,
    requiredPermission: "forms:read",
    refreshSeconds: 60,
    cacheTtlSeconds: 60,
    realtime: false,
    desktopColSpan: 6
  },
  // Zone C
  "consent-status": {
    id: "consent-status",
    title: "Consent Status",
    zone: "C",
    defaultVisible: true,
    requiredPermission: "contacts:read",
    refreshSeconds: 300,
    cacheTtlSeconds: 300,
    realtime: false,
    desktopColSpan: 4
  },
  "recent-opt-outs": {
    id: "recent-opt-outs",
    title: "Recent Opt-Outs",
    zone: "C",
    defaultVisible: true,
    requiredPermission: null,
    refreshSeconds: 120,
    cacheTtlSeconds: 120,
    realtime: false,
    desktopColSpan: 4
  },
  "recent-org-activity": {
    id: "recent-org-activity",
    title: "Recent Activity",
    zone: "C",
    defaultVisible: true,
    requiredPermission: null,
    refreshSeconds: 60,
    cacheTtlSeconds: 60,
    realtime: true,
    desktopColSpan: 4
  },
  "plan-usage": {
    id: "plan-usage",
    title: "Plan & Usage",
    zone: "C",
    defaultVisible: true,
    requiredPermission: null,
    refreshSeconds: 300,
    cacheTtlSeconds: 300,
    realtime: false,
    desktopColSpan: 6
  },
  "quick-actions": {
    id: "quick-actions",
    title: "Quick Actions",
    zone: "C",
    defaultVisible: true,
    requiredPermission: null,
    refreshSeconds: 0,
    cacheTtlSeconds: 0,
    realtime: false,
    desktopColSpan: 6
  }
};
var DASHBOARD_WIDGET_IDS = Object.keys(
  DASHBOARD_WIDGET_REGISTRY
);
var DEFAULT_DASHBOARD_LAYOUT = {
  widgetOrder: DASHBOARD_WIDGET_IDS,
  hiddenWidgets: [],
  density: "comfortable",
  timeWindowDefaults: {},
  updatedAt: (/* @__PURE__ */ new Date(0)).toISOString()
};
var aiToneSchema = z.enum(["professional", "friendly", "urgent", "casual"]);
var replyComplianceSchema = z.object({
  messageType: z.enum(["informational", "marketing", "transactional"]),
  passed: z.boolean(),
  risks: z.array(
    z.object({
      type: z.string(),
      severity: z.enum(["low", "medium", "high"]),
      description: z.string(),
      suggestion: z.string().optional()
    })
  )
});
var suggestReplyRequestSchema = z.object({
  conversationId: z.string().uuid(),
  tone: aiToneSchema.optional(),
  /** Optional steering, e.g. "offer a 10% discount" or "ask for their order number". */
  instruction: z.string().max(500).optional()
});
var suggestReplyResponseSchema = z.object({
  content: z.string(),
  /** Email replies only. */
  subject: z.string().optional(),
  /** True when the contact has NOT opted in for the conversation's channel. */
  optedOut: z.boolean(),
  /** Present when the draft was run through inline compliance analysis. */
  compliance: replyComplianceSchema.optional()
});

// src/ai-config.types.ts
var AI_AGENT_CATEGORIES = ["ops", "tenant_facing", "public"];
var AI_USAGE_FEATURES = [
  "messaging",
  "analysis",
  "documents",
  "cowork",
  "ops",
  "ocr",
  "website_chat"
];

// src/color.ts
var SLATE_900 = "#0F172A";
var SLATE_100 = "#F1F5F9";
function parseHex(hexColor) {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexColor ?? "");
  if (!match || !match[1] || !match[2] || !match[3]) return null;
  return [parseInt(match[1], 16), parseInt(match[2], 16), parseInt(match[3], 16)];
}
function toHex(rgb) {
  return `#${rgb.map(
    (c) => Math.round(Math.min(255, Math.max(0, c))).toString(16).padStart(2, "0")
  ).join("")}`.toUpperCase();
}
function channelLuminance(c8) {
  const c = c8 / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}
function relativeLuminance([r, g, b]) {
  return 0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b);
}
function contrastRatio(a, b) {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
function mixColors(a, b, t) {
  const from = parseHex(a);
  const to = parseHex(b);
  if (!from || !to) return a;
  const k = Math.min(1, Math.max(0, t));
  return toHex(from.map((c, i) => c + (to[i] - c) * k));
}
function isLightColor(color) {
  const rgb = parseHex(color);
  if (!rgb) return true;
  return relativeLuminance(rgb) > 0.5;
}
function getReadableTextColor(hexColor) {
  const LIGHT = "#FFFFFF";
  const background = parseHex(hexColor);
  if (!background) return SLATE_900;
  const dark = parseHex(SLATE_900);
  const light = parseHex(LIGHT);
  return contrastRatio(background, dark) >= contrastRatio(background, light) ? SLATE_900 : LIGHT;
}
function getContrastRatio(a, b) {
  const first = parseHex(a);
  const second = parseHex(b);
  if (!first || !second) return null;
  return contrastRatio(first, second);
}
function adjustForContrast(color, background, target) {
  const hue = parseHex(color);
  const ground = parseHex(background);
  if (!hue || !ground) return null;
  if (contrastRatio(hue, ground) >= target) return toHex(hue);
  const candidates = [];
  for (const toward of ["dark", "light"]) {
    for (let step = 1; step <= 100; step++) {
      const k = step / 100;
      const shifted = toward === "dark" ? hue.map((c) => c * (1 - k)) : hue.map((c) => c + (255 - c) * k);
      const hex = toHex(shifted);
      const parsed = parseHex(hex);
      if (contrastRatio(parsed, ground) >= target) {
        candidates.push({
          hex,
          distance: Math.abs(relativeLuminance(parsed) - relativeLuminance(hue))
        });
        break;
      }
    }
  }
  if (!candidates.length) return null;
  return candidates.sort((a, b) => a.distance - b.distance)[0].hex;
}

// src/chat-admin.types.ts
var CHAT_BRAND_ID_PATTERN = /^[a-z0-9][a-z0-9-]{1,62}$/;
var CHAT_WIDGET_POSITIONS = ["bottom-right", "bottom-left"];
var CHAT_THEME_GROUPS = [
  { key: "header", label: "Header", hint: "The band across the top of the panel" },
  { key: "body", label: "Body", hint: "Behind the conversation" },
  {
    key: "button",
    label: "Buttons",
    hint: "Send, the launcher, links and the visitor\u2019s own bubble"
  }
];
var CHAT_THEME_CONTROLS = [
  {
    key: "headerColor",
    label: "Header",
    group: "header",
    role: "surface",
    default: "#14615F",
    hint: "The band across the top of the panel"
  },
  {
    key: "headerInkColor",
    label: "Header text",
    group: "header",
    role: "text",
    default: "#FFFFFF",
    hint: "The name, the status line and the panel controls"
  },
  {
    key: "bodyColor",
    label: "Body",
    group: "body",
    role: "surface",
    default: "#FBFAF8",
    hint: "Behind the conversation, and behind the composer"
  },
  {
    key: "bodyInkColor",
    label: "Body text",
    group: "body",
    role: "text",
    default: "#0F2B2D",
    hint: "Everything the visitor reads"
  },
  {
    key: "buttonColor",
    label: "Buttons",
    group: "button",
    role: "surface",
    default: "#14615F",
    hint: "Send, the launcher, links and the visitor\u2019s own bubble"
  },
  {
    key: "buttonInkColor",
    label: "Button text",
    group: "button",
    role: "text",
    default: "#FFFFFF",
    hint: "Drawn on top of Buttons"
  }
];
var [FIRST_THEME_KEY, ...REST_THEME_KEYS] = CHAT_THEME_CONTROLS.map((t) => t.key);
var CHAT_THEME_CONTROL_KEYS = [FIRST_THEME_KEY, ...REST_THEME_KEYS];
var CHAT_THEME_DEFAULTS = Object.fromEntries(
  CHAT_THEME_CONTROLS.map((t) => [t.key, t.default])
);
var CHAT_THEME_COLOR_PATTERN = /^[#a-zA-Z0-9(),.%\s/-]{1,64}$/;
var CHAT_THEME_CONTRAST_PAIRS = [
  {
    fg: "headerInkColor",
    bg: "headerColor",
    min: 4.5,
    where: "The name and status in the header"
  },
  { fg: "bodyInkColor", bg: "bodyColor", min: 4.5, where: "Everything the visitor reads" },
  {
    fg: "buttonInkColor",
    bg: "buttonColor",
    min: 4.5,
    where: "The label on Send and the launcher"
  },
  {
    fg: "buttonColor",
    bg: "bodyColor",
    min: 4.5,
    where: "Links and the Send button against the panel"
  }
];
var SIGNAL_SEEDS = {
  light: { person: "#A3552F", danger: "#A03A32" },
  dark: { person: "#E0906A", danger: "#E58C84" }
};
function readable(softened, background, fallback) {
  return adjustForContrast(softened, background, 4.5) ?? fallback;
}
function expandChatTheme(theme) {
  if (!theme) return {};
  const anySet = CHAT_THEME_CONTROL_KEYS.some((key) => {
    const value = theme[key];
    return typeof value === "string" && value.trim().length > 0;
  });
  if (!anySet) return {};
  const c = { ...CHAT_THEME_DEFAULTS, ...theme };
  const body = c.bodyColor;
  const ink = c.bodyInkColor;
  const light = isLightColor(body);
  const seeds = light ? SIGNAL_SEEDS.light : SIGNAL_SEEDS.dark;
  const headerSeeds = isLightColor(c.headerColor) ? SIGNAL_SEEDS.light : SIGNAL_SEEDS.dark;
  return {
    headerColor: c.headerColor,
    onHeaderColor: c.headerInkColor,
    onHeaderSoftColor: readable(
      mixColors(c.headerInkColor, c.headerColor, 0.3),
      c.headerColor,
      c.headerInkColor
    ),
    headerWashColor: mixColors(c.headerColor, c.headerInkColor, 0.14),
    personOnHeaderColor: adjustForContrast(headerSeeds.person, c.headerColor, 4.5) ?? headerSeeds.person,
    dangerOnHeaderColor: adjustForContrast(headerSeeds.danger, c.headerColor, 4.5) ?? headerSeeds.danger,
    groundColor: body,
    surfaceColor: mixColors(body, "#FFFFFF", light ? 0.6 : 0.08),
    quietColor: mixColors(body, ink, 0.055),
    hairlineColor: mixColors(body, ink, 0.135),
    inkColor: ink,
    softInkColor: readable(mixColors(ink, body, 0.32), body, ink),
    primaryColor: c.buttonColor,
    onPrimaryColor: c.buttonInkColor,
    accentColor: c.buttonColor,
    handoffColor: adjustForContrast(seeds.person, body, 4.5) ?? seeds.person,
    dangerColor: adjustForContrast(seeds.danger, body, 3) ?? seeds.danger
  };
}
function collapseChatTheme(stored) {
  if (!stored) return {};
  const current = Object.fromEntries(
    CHAT_THEME_CONTROL_KEYS.filter(
      (key) => typeof stored[key] === "string" && stored[key].trim()
    ).map((key) => [key, stored[key].trim()])
  );
  if (Object.keys(current).length) return current;
  const legacy = {};
  const take = (to, from) => {
    const value = stored[from];
    if (typeof value === "string" && value.trim()) legacy[to] = value.trim();
  };
  take("headerColor", "primaryColor");
  take("headerInkColor", "onPrimaryColor");
  take("bodyColor", "groundColor");
  take("bodyInkColor", "inkColor");
  take("buttonColor", "primaryColor");
  take("buttonInkColor", "onPrimaryColor");
  return legacy;
}

// src/industries.ts
var INDUSTRIES = [
  { value: "consulting", label: "Consulting" },
  { value: "education", label: "Education" },
  { value: "financial", label: "Financial Services" },
  { value: "healthcare", label: "Healthcare" },
  { value: "home-services", label: "Home Services" },
  { value: "hospitality", label: "Hospitality" },
  { value: "insurance", label: "Insurance" },
  { value: "legal", label: "Legal Services" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "real-estate", label: "Real Estate" },
  { value: "recreational", label: "Recreational" },
  { value: "retail", label: "Retail & E-Commerce" },
  { value: "technology", label: "Technology" },
  { value: "other", label: "Other" }
];
var INDUSTRY_SAMPLE_MESSAGES = {
  technology: "Invite trial users to book a free onboarding call before their trial expires next week",
  healthcare: "Notify patients that flu shots are now available \u2014 walk-ins welcome starting Monday",
  financial: "Inform clients that they need to complete their annual compliance documentation",
  finance: "Inform clients that they need to complete their annual compliance documentation",
  insurance: "Notify clients that open enrollment begins next Monday \u2014 schedule a call to review your plan",
  retail: "Notify customers that our store will be closing early, 3pm on Monday and Tuesday this week",
  "real-estate": "Invite prospective buyers to an open house this Saturday from 1pm to 4pm at 123 Oak Street",
  education: "Remind parents that the spring parent-teacher conference sign-up closes this Friday",
  legal: "Let clients know about an upcoming deadline for filing and schedule a call to discuss next steps",
  consulting: "Send a follow-up to prospects who attended last week's webinar with a link to book a discovery call",
  manufacturing: "Let customers know about updated lead times for custom orders placed after June 1st",
  hospitality: "Send a thank-you to recent guests and invite them to leave a review for a discount on their next stay",
  "home-services": "Notify customers that our seasonal lawn care packages are now available \u2014 book before spots fill up",
  recreational: "Remind members that summer league registration closes this Friday \u2014 sign up before spots fill"
};
var DEFAULT_SAMPLE_MESSAGE = "Send a follow-up to recent clients with a thank-you and a link to book their next visit";
function getIndustrySampleMessage(industry) {
  if (!industry) return DEFAULT_SAMPLE_MESSAGE;
  return INDUSTRY_SAMPLE_MESSAGES[industry] ?? DEFAULT_SAMPLE_MESSAGE;
}
var contentSuggestionTypeSchema = z.enum(["form", "document", "sms", "email"]);
var contentSuggestionSourceSchema = z.enum(["catalog", "ai"]);
var contentSuggestionSchema = z.object({
  contentType: contentSuggestionTypeSchema,
  title: z.string(),
  description: z.string(),
  promptText: z.string(),
  reason: z.string().optional(),
  source: contentSuggestionSourceSchema
});
var contentSuggestionsQuerySchema = z.object({
  types: z.string().optional().transform((v) => {
    if (!v?.trim()) return ["form", "document", "sms", "email"];
    const parsed = v.split(",").map((t) => t.trim()).filter(
      (t) => contentSuggestionTypeSchema.safeParse(t).success
    );
    return parsed.length > 0 ? parsed : ["form", "document", "sms", "email"];
  }),
  limit: z.coerce.number().int().min(1).max(20).optional().default(8),
  refresh: z.enum(["true", "false"]).optional().transform((v) => v === "true")
});
var contentSuggestionsResponseSchema = z.object({
  suggestions: z.array(contentSuggestionSchema),
  cached: z.boolean().optional()
});
var INDUSTRY_FORM_CATEGORY_SLUGS = {
  healthcare: ["healthcare-intake", "legal-consent", "customer-feedback"],
  legal: ["legal-consent", "hr-employment", "contact-lead"],
  "real-estate": ["contact-lead", "event-registration", "customer-feedback"],
  financial: ["financial-payments", "legal-consent", "contact-lead"],
  insurance: ["financial-payments", "legal-consent", "contact-lead"],
  education: ["education-training", "event-registration", "customer-feedback"],
  consulting: ["contact-lead", "customer-feedback", "hr-employment"],
  retail: ["customer-feedback", "contact-lead", "event-registration"],
  hospitality: ["customer-feedback", "event-registration", "contact-lead"],
  "home-services": ["customer-feedback", "contact-lead", "legal-consent"],
  manufacturing: ["customer-feedback", "hr-employment", "contact-lead"],
  recreational: ["event-registration", "legal-consent", "customer-feedback"],
  technology: ["contact-lead", "customer-feedback", "hr-employment"],
  other: ["contact-lead", "customer-feedback", "event-registration"],
  individual: ["contact-lead", "customer-feedback", "legal-consent"]
};
var INDUSTRY_DOCUMENT_CATEGORY_SLUGS = {
  healthcare: ["healthcare", "compliance", "business"],
  legal: ["intellectual-property", "business", "compliance"],
  "real-estate": ["real-estate", "business", "finance"],
  financial: ["finance", "compliance", "business"],
  insurance: ["finance", "compliance", "business"],
  education: ["nonprofit", "business", "compliance"],
  consulting: ["business", "employment-law", "compliance"],
  retail: ["consumer", "business", "compliance"],
  hospitality: ["business", "consumer", "compliance"],
  "home-services": ["business", "consumer", "compliance"],
  manufacturing: ["business", "compliance", "employment-law"],
  recreational: ["business", "consumer", "compliance"],
  technology: ["business", "intellectual-property", "employment-law"],
  other: ["business", "compliance", "consumer"],
  individual: ["business", "consumer", "compliance"]
};
var DEFAULT_FORM_SLUGS = ["contact-lead", "customer-feedback", "event-registration"];
var DEFAULT_DOCUMENT_SLUGS = ["business", "compliance", "consumer"];
function getIndustryCategorySlugs(industry, contentType) {
  const key = industry?.toLowerCase().trim() || "other";
  const map = contentType === "form" ? INDUSTRY_FORM_CATEGORY_SLUGS : INDUSTRY_DOCUMENT_CATEGORY_SLUGS;
  return map[key] ?? (contentType === "form" ? DEFAULT_FORM_SLUGS : DEFAULT_DOCUMENT_SLUGS);
}
function getIndustryLabel(industry) {
  const labels = {
    healthcare: "Healthcare",
    legal: "Legal Services",
    "real-estate": "Real Estate",
    financial: "Financial Services",
    insurance: "Insurance",
    education: "Education",
    consulting: "Consulting",
    retail: "Retail",
    hospitality: "Hospitality",
    "home-services": "Home Services",
    manufacturing: "Manufacturing",
    recreational: "Recreational",
    technology: "Technology",
    individual: "Individual",
    other: "your industry"
  };
  const key = industry?.toLowerCase().trim() || "other";
  return labels[key] ?? "your industry";
}
var GENERIC_SMS = [
  {
    title: "Appointment Reminder",
    description: "Remind contacts about an upcoming appointment",
    promptText: "Create a friendly SMS reminder for an upcoming appointment, including date, time, and a reply option to confirm or reschedule",
    matchKeywords: ["appointment", "reminder", "upcoming"]
  },
  {
    title: "Follow-Up After Visit",
    description: "Thank a contact and invite them to book again",
    promptText: "Create a warm follow-up SMS thanking the customer for their recent visit and including a link placeholder to book their next appointment",
    matchKeywords: ["follow-up", "follow up", "thank you", "thank-you"]
  },
  {
    title: "Document Ready to Sign",
    description: "Notify that a document is waiting for signature",
    promptText: "Create an SMS notifying the recipient that a document is ready for their signature, with a short call to action",
    matchKeywords: ["sign", "signature", "document ready"]
  }
];
var GENERIC_EMAIL = [
  {
    title: "Welcome Email",
    description: "Onboard a new contact with next steps",
    promptText: "Create a professional welcome email for a new client with a brief introduction and clear next steps",
    matchKeywords: ["welcome", "onboard", "onboarding"]
  },
  {
    title: "Document Signing Request",
    description: "Email asking the recipient to review and sign",
    promptText: "Create a professional email requesting the recipient review and sign an attached agreement, with a polite deadline",
    matchKeywords: ["sign", "signature", "agreement", "document"]
  },
  {
    title: "Monthly Newsletter",
    description: "Share updates and helpful tips with contacts",
    promptText: "Create a concise monthly newsletter email with a greeting, two to three value-focused updates, and a single call to action",
    matchKeywords: ["newsletter", "monthly update"]
  }
];
var INDUSTRY_SMS_CATALOG = {
  healthcare: [
    {
      title: "Appointment Reminder SMS",
      description: "Remind patients about an upcoming visit",
      promptText: "Create a HIPAA-conscious SMS appointment reminder for a medical practice, including date, time, and instructions to reply CONFIRM or RESCHEDULE",
      matchKeywords: ["appointment", "patient", "reminder", "visit"]
    },
    {
      title: "Test Results Ready",
      description: "Notify patients to check their portal",
      promptText: "Create a brief SMS letting the patient know their test results are available in the patient portal, without including sensitive details",
      matchKeywords: ["test results", "lab results", "portal"]
    },
    ...GENERIC_SMS.slice(2)
  ],
  legal: [
    {
      title: "Consultation Confirmation",
      description: "Confirm a scheduled legal consultation",
      promptText: "Create a professional SMS confirming a legal consultation appointment with date, time, and what documents to bring",
      matchKeywords: ["consultation", "legal", "appointment"]
    },
    {
      title: "Document Review Reminder",
      description: "Remind clients to review pending documents",
      promptText: "Create a professional SMS reminding a client to review and sign pending legal documents",
      matchKeywords: ["review", "legal document", "pending"]
    },
    ...GENERIC_SMS.slice(1)
  ],
  "real-estate": [
    {
      title: "Open House Invitation",
      description: "Invite buyers to an upcoming open house",
      promptText: "Create an SMS inviting prospective buyers to an open house with address, date, time, and RSVP instructions",
      matchKeywords: ["open house", "showing", "property"]
    },
    {
      title: "Showing Confirmation",
      description: "Confirm a property showing appointment",
      promptText: "Create an SMS confirming a property showing with address, date, time, and agent contact info",
      matchKeywords: ["showing", "property tour", "viewing"]
    },
    ...GENERIC_SMS.slice(0, 2)
  ]
};
var INDUSTRY_EMAIL_CATALOG = {
  healthcare: [
    {
      title: "New Patient Welcome",
      description: "Welcome new patients with intake instructions",
      promptText: "Create a welcoming email for new patients with what to expect at their first visit and a link to complete intake forms",
      matchKeywords: ["new patient", "welcome", "intake"]
    },
    {
      title: "Annual Wellness Reminder",
      description: "Encourage patients to schedule preventive care",
      promptText: "Create a caring email reminding patients it is time for their annual wellness visit with a link to schedule",
      matchKeywords: ["wellness", "annual", "preventive"]
    },
    ...GENERIC_EMAIL.slice(1)
  ],
  legal: [
    {
      title: "Engagement Letter Follow-Up",
      description: "Follow up on a pending engagement letter",
      promptText: "Create a professional email following up on an engagement letter that is awaiting the client signature",
      matchKeywords: ["engagement letter", "retainer"]
    },
    {
      title: "Case Status Update",
      description: "Keep clients informed on case progress",
      promptText: "Create a professional case status update email summarizing recent progress and next steps without legal jargon",
      matchKeywords: ["case update", "status update"]
    },
    ...GENERIC_EMAIL.slice(0, 1)
  ],
  "real-estate": [
    {
      title: "Listing Announcement",
      description: "Announce a new property listing to your list",
      promptText: "Create an email announcing a new property listing with key highlights, price, and a call to schedule a showing",
      matchKeywords: ["listing", "new property", "for sale"]
    },
    {
      title: "Offer Status Update",
      description: "Update buyers or sellers on an offer",
      promptText: "Create a professional email updating a client on the status of their real estate offer and recommended next steps",
      matchKeywords: ["offer", "counteroffer", "under contract"]
    },
    ...GENERIC_EMAIL.slice(2)
  ]
};
function getStaticMessageCatalog(industry, contentType) {
  const key = industry?.toLowerCase().trim() || "other";
  const catalog = contentType === "sms" ? INDUSTRY_SMS_CATALOG[key] ?? GENERIC_SMS : INDUSTRY_EMAIL_CATALOG[key] ?? GENERIC_EMAIL;
  const reason = `Common for ${getIndustryLabel(industry)} businesses`;
  return catalog.map((entry) => ({
    contentType,
    title: entry.title,
    description: entry.description,
    promptText: entry.promptText,
    reason,
    source: "catalog",
    matchKeywords: entry.matchKeywords
  }));
}
var ABBREVIATION_EXPANSIONS = {
  nda: ["nda", "non disclosure", "non-disclosure", "confidentiality"],
  hipaa: ["hipaa", "health information", "privacy authorization"],
  tcpa: ["tcpa", "consent", "opt in", "opt-in"],
  waiver: ["waiver", "liability release", "release of liability"],
  intake: ["intake", "registration", "onboarding"]
};
function normalizeContentName(name) {
  return name.toLowerCase().replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();
}
function tokenize2(text) {
  return new Set(normalizeContentName(text).split(" ").filter((t) => t.length > 2));
}
function tokenOverlapRatio(a, b) {
  const tokensA = tokenize2(a);
  const tokensB = tokenize2(b);
  if (tokensA.size === 0 || tokensB.size === 0) return 0;
  let overlap = 0;
  for (const t of tokensA) {
    if (tokensB.has(t)) overlap++;
  }
  return overlap / Math.min(tokensA.size, tokensB.size);
}
function expandKeywords(keywords) {
  const expanded = /* @__PURE__ */ new Set();
  for (const kw of keywords) {
    const norm = normalizeContentName(kw);
    expanded.add(norm);
    for (const [abbr, variants] of Object.entries(ABBREVIATION_EXPANSIONS)) {
      if (norm.includes(abbr) || variants.some((v) => norm.includes(v))) {
        variants.forEach((v) => expanded.add(v));
        expanded.add(abbr);
      }
    }
  }
  return [...expanded];
}
function inventoryMatchesSuggestion(inventoryName, suggestion) {
  const normInv = normalizeContentName(inventoryName);
  const normTitle = normalizeContentName(suggestion.title);
  if (!normInv || !normTitle) return false;
  if (normInv === normTitle) return true;
  if (normInv.includes(normTitle) || normTitle.includes(normInv)) return true;
  const keywords = expandKeywords([
    ...suggestion.matchKeywords,
    suggestion.title,
    ...suggestion.title.split(/\s+/)
  ]);
  for (const kw of keywords) {
    if (kw.length >= 3 && normInv.includes(kw)) return true;
  }
  if (tokenOverlapRatio(inventoryName, suggestion.title) >= 0.8) return true;
  return false;
}
function filterDuplicateSuggestions(suggestions, inventory) {
  return suggestions.filter((suggestion) => {
    const relevantInventory = inventory.filter(
      (item) => item.contentType === suggestion.contentType
    );
    return !relevantInventory.some(
      (item) => inventoryMatchesSuggestion(item.name, suggestion)
    );
  });
}

// src/validation.ts
var NAME_REGEX = /^(?=.*\p{L})[\p{L}\p{M}\s''.-]+$/u;
var NAME_ERROR = "must contain letters only.";
function isValidName(value) {
  return NAME_REGEX.test(value.trim());
}

// src/use-case-kits.data.ts
var GENERATED_KITS = [
  {
    "kitId": "home-services",
    "industryKey": "home-services",
    "name": "Home Services",
    "tagline": 'Turn every "can you come take a look?" into a signed job \u2014 from first call to crew on-site.',
    "scenario": `A homeowner with a leaking water heater fills out your quote request form at 9pm. Respectly tags the lead, drops them into your Job Pipeline, texts back a same-day "we got your request" reply, and books the site visit. By the time your tech finishes the walkthrough, the Service Agreement is already waiting in the homeowner's inbox to e-sign \u2014 and the deposit invoice fires the moment they do.`,
    "icon": "Wrench",
    "accentColor": "#f59e0b",
    "tags": [
      {
        "ref": "tag:new-lead",
        "name": "New Lead",
        "color": "#8b5cf6"
      },
      {
        "ref": "tag:emergency",
        "name": "Emergency",
        "color": "#ef4444"
      },
      {
        "ref": "tag:estimate-sent",
        "name": "Estimate Sent",
        "color": "#f59e0b"
      },
      {
        "ref": "tag:repeat-customer",
        "name": "Repeat Customer",
        "color": "#22c55e"
      }
    ],
    "pipeline": {
      "ref": "pipeline:jobs",
      "name": "Job Pipeline",
      "industry": "home-services",
      "stages": [
        {
          "ref": "stage:new-inquiry",
          "name": "New Inquiry",
          "color": "#8b5cf6",
          "exitCriteria": [
            "Contact info verified",
            "Service type confirmed"
          ],
          "isDefault": true
        },
        {
          "ref": "stage:site-visit",
          "name": "Site Visit Scheduled",
          "color": "#06b6d4",
          "exitCriteria": [
            "Appointment date/time locked",
            "Technician assigned"
          ]
        },
        {
          "ref": "stage:estimate",
          "name": "Estimate Delivered",
          "color": "#f59e0b",
          "exitCriteria": [
            "Quote PDF uploaded",
            "Confirmation of receipt"
          ]
        },
        {
          "ref": "stage:contract",
          "name": "Contract Signed",
          "color": "#22c55e",
          "exitCriteria": [
            "Digital signature received",
            "Deposit invoice sent"
          ]
        },
        {
          "ref": "stage:work-scheduled",
          "name": "Work Scheduled",
          "color": "#6366f1",
          "exitCriteria": [
            "Start date confirmed",
            "Crew lead assigned"
          ],
          "isFinal": true
        }
      ]
    },
    "forms": [
      {
        "ref": "form:quote-request",
        "name": "Free Quote Request",
        "description": "Capture service requests from your website, Google profile, and yard signs \u2014 and route them straight into the Job Pipeline.",
        "samplePrompt": "Create a free quote request form for a home services company that captures full name, email, mobile phone, service address, the type of service needed (plumbing, electrical, HVAC, roofing, general handyman), a short description of the problem, whether it's an emergency, and how soon they need it done \u2014 and capture SMS consent so we can text back a quote.",
        "schema": {
          "layout": "single",
          "fields": [
            {
              "id": "full_name",
              "type": "text",
              "label": "Full Name",
              "placeholder": "Chris Homeowner",
              "validation": {
                "required": true
              }
            },
            {
              "id": "email",
              "type": "email",
              "label": "Email",
              "placeholder": "chris@email.com",
              "validation": {
                "required": true
              }
            },
            {
              "id": "phone",
              "type": "phone",
              "label": "Mobile Phone",
              "placeholder": "(555) 123-4567",
              "validation": {
                "required": true
              }
            },
            {
              "id": "address",
              "type": "text",
              "label": "Service Address",
              "placeholder": "123 Maple St, Springfield",
              "validation": {
                "required": true
              }
            },
            {
              "id": "service_type",
              "type": "dropdown",
              "label": "What do you need help with?",
              "validation": {
                "required": true
              },
              "options": [
                {
                  "label": "Plumbing",
                  "value": "plumbing"
                },
                {
                  "label": "Electrical",
                  "value": "electrical"
                },
                {
                  "label": "HVAC / Heating & Cooling",
                  "value": "hvac"
                },
                {
                  "label": "Roofing",
                  "value": "roofing"
                },
                {
                  "label": "General Handyman / Repairs",
                  "value": "handyman"
                }
              ]
            },
            {
              "id": "description",
              "type": "textarea",
              "label": "Describe the problem",
              "placeholder": "e.g. Water heater is leaking from the bottom and there's no hot water.",
              "helpText": "A few details help us send a tech with the right parts.",
              "validation": {
                "required": true,
                "maxLength": 600
              }
            },
            {
              "id": "is_emergency",
              "type": "radio",
              "label": "Is this an emergency?",
              "validation": {
                "required": true
              },
              "options": [
                {
                  "label": "Yes \u2014 I need someone ASAP",
                  "value": "yes"
                },
                {
                  "label": "No \u2014 it can wait",
                  "value": "no"
                }
              ]
            },
            {
              "id": "timeline",
              "type": "dropdown",
              "label": "How soon do you need this done?",
              "options": [
                {
                  "label": "Today / Emergency",
                  "value": "today"
                },
                {
                  "label": "This week",
                  "value": "this_week"
                },
                {
                  "label": "Within a month",
                  "value": "this_month"
                },
                {
                  "label": "Just getting a quote",
                  "value": "quote_only"
                }
              ]
            },
            {
              "id": "preferred_date",
              "type": "date",
              "label": "Preferred site visit date"
            }
          ]
        },
        "settings": {
          "submitButtonText": "Get My Free Quote",
          "successMessage": "Thanks! We received your request and will text you shortly to confirm your free site visit.",
          "captureSmsConsent": true,
          "consentText": "I agree to receive texts about my quote, appointment reminders, and service updates. Msg & data rates may apply. Reply STOP to opt out."
        },
        "tagRefs": [
          "tag:new-lead"
        ]
      }
    ],
    "documents": [
      {
        "ref": "doc:service-agreement",
        "name": "Home Service Agreement",
        "samplePrompt": "Draft a home service agreement between {{company}} and the customer that covers the scope of work, the agreed price with a deposit, a 1-year workmanship warranty, an access-to-property clause, a cancellation policy, and a customer signature and date field.",
        "tagRefs": [
          "tag:estimate-sent"
        ],
        "ndfDocument": {
          "version": "1.0",
          "kind": "document",
          "meta": {
            "title": "Home Service Agreement",
            "description": "Standard agreement covering scope of work, price, warranty, and terms for a residential service job.",
            "variables": [
              {
                "name": "full_name",
                "label": "Customer Name",
                "type": "text"
              },
              {
                "name": "company",
                "label": "Service Provider",
                "type": "text"
              },
              {
                "name": "address",
                "label": "Service Address",
                "type": "text"
              },
              {
                "name": "date",
                "label": "Date",
                "type": "date"
              }
            ]
          },
          "signers": [
            {
              "id": "signer_1",
              "name": "Customer",
              "order": 1
            }
          ],
          "blocks": [
            {
              "type": "heading",
              "id": "h1",
              "level": 1,
              "content": "Home Service Agreement"
            },
            {
              "type": "paragraph",
              "id": "p1",
              "content": 'This Service Agreement ("Agreement") is entered into on {{date}} between {{company}} ("Contractor") and {{full_name}} ("Customer") for work to be performed at {{address}} (the "Property").'
            },
            {
              "type": "section_header",
              "id": "sh1",
              "content": "1. Scope of Work"
            },
            {
              "type": "paragraph",
              "id": "p2",
              "content": 'Contractor agrees to furnish the labor, materials, and equipment described in the written estimate provided to and accepted by Customer. Any work beyond that estimate (a "change order") must be approved by Customer in writing before it is performed.'
            },
            {
              "type": "section_header",
              "id": "sh2",
              "content": "2. Price & Deposit"
            },
            {
              "type": "paragraph",
              "id": "p3",
              "content": "Customer agrees to pay the total price set out in the accepted estimate. A deposit is due upon signing to schedule the work and order materials, with the remaining balance due upon completion. Invoices not paid within fifteen (15) days of completion may accrue a late fee as permitted by law."
            },
            {
              "type": "section_header",
              "id": "sh3",
              "content": "3. Workmanship Warranty"
            },
            {
              "type": "paragraph",
              "id": "p4",
              "content": "Contractor warrants its workmanship to be free from defects for a period of one (1) year from the date of completion. This warranty does not cover damage caused by misuse, normal wear, acts of nature, or work performed by others. Manufacturer warranties on parts and materials are passed through to Customer."
            },
            {
              "type": "section_header",
              "id": "sh4",
              "content": "4. Access & Conditions"
            },
            {
              "type": "list",
              "id": "l1",
              "ordered": false,
              "items": [
                "Customer will provide safe and reasonable access to the Property during scheduled work hours.",
                "Customer is responsible for disclosing known hazards such as asbestos, mold, or hidden utility lines.",
                "Contractor will leave the work area broom-clean and remove job-related debris upon completion."
              ]
            },
            {
              "type": "section_header",
              "id": "sh5",
              "content": "5. Cancellation"
            },
            {
              "type": "paragraph",
              "id": "p5",
              "content": "Either party may cancel this Agreement with written notice. If Customer cancels after materials have been ordered, Customer may be responsible for restocking fees and non-refundable material costs actually incurred by Contractor."
            },
            {
              "type": "divider",
              "id": "d1"
            },
            {
              "type": "paragraph",
              "id": "p6",
              "content": "By signing below, Customer acknowledges they have read, understood, and agree to the terms of this Agreement and the accepted estimate."
            },
            {
              "type": "field_signature",
              "id": "sig1",
              "label": "Customer Signature",
              "signerId": "signer_1",
              "required": true
            },
            {
              "type": "field_date",
              "id": "fd1",
              "label": "Date Signed",
              "signerId": "signer_1"
            }
          ]
        }
      }
    ],
    "templates": [
      {
        "ref": "tmpl:visit-reminder-sms",
        "type": "sms",
        "name": "Site Visit Reminder",
        "category": "Reminders",
        "samplePrompt": "Write a friendly SMS reminding a homeowner that our technician is coming for their site visit tomorrow at the scheduled time, asking them to reply C to confirm or R to reschedule, and noting the tech will text when they're on the way.",
        "content": {
          "message": "Hi {{first_name}}, reminder: {{company}} is coming to {{address}} tomorrow for your free site visit. Your tech will text when on the way. Reply C to confirm or R to reschedule.",
          "variables": [
            "first_name",
            "company",
            "address"
          ]
        }
      },
      {
        "ref": "tmpl:estimate-ready-email",
        "type": "email",
        "name": "Your Estimate Is Ready",
        "category": "Promotions",
        "samplePrompt": "Write an email letting a homeowner know their estimate for the requested service is ready to review and approve, with a clear button to view and sign the agreement, and a friendly nudge that the price is locked in for 30 days.",
        "content": {
          "subject": "Your estimate from {{company}} is ready",
          "previewText": "Review your quote and book your job \u2014 price locked for 30 days.",
          "body": `<p>Hi {{first_name}},</p><p>Thanks for letting us take a look at the project at <strong>{{address}}</strong>. Your detailed estimate is ready to review.</p><p>Everything we discussed is itemized and your price is <strong>locked in for 30 days</strong>. When you're ready to get on the schedule, just review and sign \u2014 we'll order materials and lock in your start date.</p><p><a href="{{booking_link}}">Review &amp; approve my estimate</a></p><p>Questions? Just reply to this email or text us back.</p><p>Thanks,<br/>{{company}}</p>`,
          "variables": [
            "first_name",
            "address",
            "booking_link",
            "company"
          ]
        }
      }
    ],
    "workflows": [
      {
        "ref": "wf:new-quote-intake",
        "name": "New Quote Intake",
        "description": "When a homeowner submits the Free Quote Request form, tag them, add them to the New Inquiry stage of the Job Pipeline, then send a site visit reminder.",
        "triggerType": "form_submitted",
        "triggerConfig": {
          "formId": {
            "$ref": "form:quote-request"
          },
          "formName": "Free Quote Request"
        },
        "steps": [
          {
            "stepType": "add_tag",
            "sortOrder": 0,
            "config": {
              "tagId": {
                "$ref": "tag:new-lead"
              },
              "tagName": "New Lead"
            }
          },
          {
            "stepType": "add_to_group",
            "sortOrder": 1,
            "config": {
              "groupId": {
                "$ref": "pipeline:jobs"
              },
              "stageId": {
                "$ref": "stage:new-inquiry"
              },
              "groupName": "Job Pipeline",
              "stageName": "New Inquiry"
            }
          },
          {
            "stepType": "wait",
            "sortOrder": 2,
            "config": {
              "duration": 1,
              "unit": "days"
            }
          },
          {
            "stepType": "send_sms",
            "sortOrder": 3,
            "config": {
              "templateId": {
                "$ref": "tmpl:visit-reminder-sms"
              },
              "templateName": "Site Visit Reminder"
            }
          }
        ],
        "activate": true
      },
      {
        "ref": "wf:estimate-to-signature",
        "name": "Estimate To Signature",
        "description": "When a job reaches the Estimate Delivered stage, tag the customer, email their estimate, then send the Home Service Agreement to sign.",
        "triggerType": "contact_stage_changed",
        "triggerConfig": {
          "groupId": {
            "$ref": "pipeline:jobs"
          },
          "stageId": {
            "$ref": "stage:estimate"
          }
        },
        "steps": [
          {
            "stepType": "add_tag",
            "sortOrder": 0,
            "config": {
              "tagId": {
                "$ref": "tag:estimate-sent"
              },
              "tagName": "Estimate Sent"
            }
          },
          {
            "stepType": "send_email",
            "sortOrder": 1,
            "config": {
              "templateId": {
                "$ref": "tmpl:estimate-ready-email"
              },
              "templateName": "Your Estimate Is Ready"
            }
          },
          {
            "stepType": "wait",
            "sortOrder": 2,
            "config": {
              "duration": 1,
              "unit": "hours"
            }
          },
          {
            "stepType": "send_document",
            "sortOrder": 3,
            "config": {
              "documentId": {
                "$ref": "doc:service-agreement"
              },
              "documentName": "Home Service Agreement"
            }
          }
        ],
        "activate": true
      }
    ],
    "coworkPrompts": [
      "Find everyone who submitted the Free Quote Request form in the last 48 hours and marked it an emergency, tag them Emergency, and move them to the Site Visit Scheduled stage.",
      "Send the Home Service Agreement to every contact in the Estimate Delivered stage who hasn't signed yet, and tag them Estimate Sent.",
      "Text the Site Visit Reminder to everyone in the Site Visit Scheduled stage with an appointment tomorrow."
    ]
  },
  {
    "kitId": "insurance-brokers",
    "industryKey": "insurance-brokers",
    "name": "Insurance Brokers",
    "tagline": "Turn every quote request into a bound policy.",
    "scenario": "A small business owner fills out your commercial-lines quote form on a Tuesday afternoon. Respectly instantly tags the lead, drops them into Data Collection, and texts them a link to upload their prior policy and IDs. As you market the risk to carriers and build the comparison sheet, automated reminders and a no-loss letter keep the deal moving until the policy is issued.",
    "icon": "ShieldCheck",
    "accentColor": "#0ea5e9",
    "tags": [
      {
        "ref": "tag:new-lead",
        "name": "New Lead",
        "color": "#0ea5e9"
      },
      {
        "ref": "tag:commercial",
        "name": "Commercial Lines",
        "color": "#8b5cf6"
      },
      {
        "ref": "tag:personal",
        "name": "Personal Lines",
        "color": "#f59e0b"
      },
      {
        "ref": "tag:renewal",
        "name": "Renewal",
        "color": "#22c55e"
      }
    ],
    "pipeline": {
      "ref": "pipeline:policies",
      "name": "Policy Pipeline",
      "industry": "insurance-brokers",
      "stages": [
        {
          "ref": "stage:data-collection",
          "name": "Data Collection",
          "color": "#8b5cf6",
          "exitCriteria": [
            "Application complete",
            "IDs/Previous policies uploaded"
          ],
          "isDefault": true
        },
        {
          "ref": "stage:market-submission",
          "name": "Market Submission",
          "color": "#06b6d4",
          "exitCriteria": [
            "Submitted to 3+ carriers"
          ]
        },
        {
          "ref": "stage:quote-comparison",
          "name": "Quote Comparison",
          "color": "#f59e0b",
          "exitCriteria": [
            "Comparison sheet generated",
            "Review meeting set"
          ]
        },
        {
          "ref": "stage:binding",
          "name": "Binding Requested",
          "color": "#22c55e",
          "exitCriteria": [
            "No-loss letter signed",
            "Binding request sent to carrier"
          ]
        },
        {
          "ref": "stage:policy-issued",
          "name": "Policy Issued",
          "color": "#16a34a",
          "exitCriteria": [
            "Policy number recorded",
            "First premium paid"
          ],
          "isFinal": true
        }
      ]
    },
    "forms": [
      {
        "ref": "form:quote-request",
        "name": "Insurance Quote Request",
        "description": "Capture the details we need to start marketing your risk to carriers.",
        "schema": {
          "layout": "two-column",
          "fields": [
            {
              "id": "sec_contact",
              "type": "section_header",
              "label": "Your Contact Information"
            },
            {
              "id": "first_name",
              "type": "text",
              "label": "First Name",
              "placeholder": "Jane",
              "validation": {
                "required": true,
                "maxLength": 80
              }
            },
            {
              "id": "last_name",
              "type": "text",
              "label": "Last Name",
              "placeholder": "Doe",
              "validation": {
                "required": true,
                "maxLength": 80
              }
            },
            {
              "id": "email",
              "type": "email",
              "label": "Email Address",
              "placeholder": "jane@company.com",
              "validation": {
                "required": true
              }
            },
            {
              "id": "phone",
              "type": "phone",
              "label": "Mobile Phone",
              "placeholder": "(555) 123-4567",
              "helpText": "We may text you a secure upload link.",
              "validation": {
                "required": true
              }
            },
            {
              "id": "company",
              "type": "text",
              "label": "Business / Legal Name",
              "placeholder": "Acme LLC (leave blank for personal)",
              "validation": {
                "maxLength": 120
              }
            },
            {
              "id": "div_1",
              "type": "divider",
              "label": "Divider"
            },
            {
              "id": "sec_coverage",
              "type": "section_header",
              "label": "Coverage Details"
            },
            {
              "id": "line_of_business",
              "type": "dropdown",
              "label": "Type of Coverage",
              "placeholder": "Select a coverage type",
              "validation": {
                "required": true
              },
              "options": [
                {
                  "label": "General Liability",
                  "value": "general_liability"
                },
                {
                  "label": "Commercial Property",
                  "value": "commercial_property"
                },
                {
                  "label": "Business Owners Policy (BOP)",
                  "value": "bop"
                },
                {
                  "label": "Workers' Compensation",
                  "value": "workers_comp"
                },
                {
                  "label": "Commercial Auto",
                  "value": "commercial_auto"
                },
                {
                  "label": "Professional Liability (E&O)",
                  "value": "professional_liability"
                },
                {
                  "label": "Personal Auto",
                  "value": "personal_auto"
                },
                {
                  "label": "Homeowners",
                  "value": "homeowners"
                }
              ]
            },
            {
              "id": "policy_status",
              "type": "radio",
              "label": "Is this a new policy or a renewal?",
              "validation": {
                "required": true
              },
              "options": [
                {
                  "label": "New coverage",
                  "value": "new"
                },
                {
                  "label": "Renewing / switching carriers",
                  "value": "renewal"
                }
              ]
            },
            {
              "id": "effective_date",
              "type": "date",
              "label": "Desired Effective Date",
              "helpText": "When you need coverage to start.",
              "validation": {
                "required": true
              }
            },
            {
              "id": "current_carrier",
              "type": "text",
              "label": "Current Carrier (if any)",
              "placeholder": "e.g. Travelers",
              "validation": {
                "maxLength": 100
              }
            },
            {
              "id": "prior_loss",
              "type": "radio",
              "label": "Any claims or losses in the last 3 years?",
              "validation": {
                "required": true
              },
              "options": [
                {
                  "label": "No",
                  "value": "no"
                },
                {
                  "label": "Yes",
                  "value": "yes"
                }
              ]
            },
            {
              "id": "notes",
              "type": "textarea",
              "label": "Anything else we should know?",
              "placeholder": "Operations, locations, payroll, special requests...",
              "validation": {
                "maxLength": 1e3
              }
            }
          ]
        },
        "settings": {
          "submitButtonText": "Request My Quote",
          "successMessage": "Thanks! Your dedicated broker is reviewing your request and will reach out within one business day.",
          "captureSmsConsent": true,
          "captureEmailConsent": true,
          "consentText": "I agree to receive calls, texts, and emails from this insurance agency about my quote request and policy. Message and data rates may apply; reply STOP to opt out."
        },
        "tagRefs": [
          "tag:new-lead"
        ],
        "samplePrompt": "Build an insurance quote request form that collects contact info, the type of coverage (GL, property, BOP, workers comp, commercial auto, E&O, personal auto, homeowners), whether it's new or a renewal, desired effective date, current carrier, prior losses, and SMS/email consent."
      },
      {
        "ref": "form:loss-history",
        "name": "Loss History & Underwriting Supplement",
        "description": "Help us market your risk accurately by detailing prior claims and exposures.",
        "schema": {
          "layout": "single",
          "fields": [
            {
              "id": "sec_applicant",
              "type": "section_header",
              "label": "Applicant"
            },
            {
              "id": "full_name",
              "type": "text",
              "label": "Named Insured / Applicant",
              "placeholder": "Full legal name",
              "validation": {
                "required": true,
                "maxLength": 120
              }
            },
            {
              "id": "years_in_business",
              "type": "number",
              "label": "Years in Business / Years Licensed Driving",
              "validation": {
                "min": 0,
                "max": 120
              }
            },
            {
              "id": "div_lh",
              "type": "divider",
              "label": "Divider"
            },
            {
              "id": "sec_loss",
              "type": "section_header",
              "label": "Loss History (Last 5 Years)"
            },
            {
              "id": "had_claims",
              "type": "radio",
              "label": "Have you had any claims or losses in the last 5 years?",
              "validation": {
                "required": true
              },
              "options": [
                {
                  "label": "No claims",
                  "value": "none"
                },
                {
                  "label": "Yes, 1-2 claims",
                  "value": "few"
                },
                {
                  "label": "Yes, 3 or more claims",
                  "value": "many"
                }
              ]
            },
            {
              "id": "loss_types",
              "type": "checkbox",
              "label": "What types of losses occurred? (select all that apply)",
              "helpText": "Leave blank if no claims.",
              "options": [
                {
                  "label": "Property damage",
                  "value": "property"
                },
                {
                  "label": "Liability / bodily injury",
                  "value": "liability"
                },
                {
                  "label": "Auto accident",
                  "value": "auto"
                },
                {
                  "label": "Theft",
                  "value": "theft"
                },
                {
                  "label": "Weather / catastrophe",
                  "value": "weather"
                },
                {
                  "label": "Other",
                  "value": "other"
                }
              ]
            },
            {
              "id": "largest_loss",
              "type": "number",
              "label": "Largest single loss amount (USD)",
              "placeholder": "0",
              "validation": {
                "min": 0
              }
            },
            {
              "id": "loss_details",
              "type": "textarea",
              "label": "Describe each loss (date, cause, amount, status)",
              "placeholder": "03/2024 - water damage at main location, $12,000, closed...",
              "validation": {
                "maxLength": 1500
              }
            },
            {
              "id": "div_lh2",
              "type": "divider",
              "label": "Divider"
            },
            {
              "id": "sec_docs",
              "type": "section_header",
              "label": "Supporting Documents"
            },
            {
              "id": "loss_runs",
              "type": "file",
              "label": "Upload current loss runs / prior declarations page",
              "helpText": "PDF or image. We keep this confidential to your file."
            },
            {
              "id": "attest",
              "type": "checkbox",
              "label": "Attestation",
              "validation": {
                "required": true
              },
              "options": [
                {
                  "label": "I certify the information above is true and complete to the best of my knowledge.",
                  "value": "certified"
                }
              ]
            }
          ]
        },
        "settings": {
          "submitButtonText": "Submit Loss History",
          "successMessage": "Got it. Your loss history is on file and we're submitting your risk to markets now.",
          "captureSmsConsent": false,
          "captureEmailConsent": true,
          "consentText": "I agree to receive email updates about my application and policy from this agency."
        },
        "tagRefs": [
          "tag:commercial"
        ],
        "samplePrompt": "Create a loss history and underwriting supplement form for insurance applicants: named insured, years in business, whether they've had claims in the last 5 years, types of losses as checkboxes, largest loss amount, a description field, a file upload for loss runs, and a truthfulness attestation checkbox."
      }
    ],
    "documents": [
      {
        "ref": "doc:no-loss-letter",
        "name": "No-Loss Letter / Statement of No Known Losses",
        "ndfDocument": {
          "version": "1.0",
          "kind": "document",
          "meta": {
            "title": "Statement of No Known Losses (No-Loss Letter)",
            "description": "Signed attestation that the insured is unaware of any claims or losses prior to binding coverage.",
            "variables": [
              {
                "name": "full_name",
                "label": "Named Insured",
                "type": "text"
              },
              {
                "name": "company",
                "label": "Business Name",
                "type": "text"
              },
              {
                "name": "address",
                "label": "Address",
                "type": "text"
              },
              {
                "name": "date",
                "label": "Effective Date",
                "type": "date"
              },
              {
                "name": "today",
                "label": "Date Signed",
                "type": "date"
              }
            ]
          },
          "signers": [
            {
              "id": "signer_1",
              "name": "Named Insured",
              "order": 1
            }
          ],
          "blocks": [
            {
              "type": "heading",
              "id": "h1",
              "level": 1,
              "content": "Statement of No Known Losses"
            },
            {
              "type": "paragraph",
              "id": "p_intro",
              "content": 'This Statement of No Known Losses ("No-Loss Letter") is provided by the undersigned in connection with the binding of insurance coverage effective {{date}}. It is a material representation relied upon by the insurance carrier in agreeing to bind coverage.'
            },
            {
              "type": "section_header",
              "id": "sh_insured",
              "content": "Insured Information"
            },
            {
              "type": "paragraph",
              "id": "p_insured",
              "content": "Named Insured: {{full_name}}\nBusiness / Entity: {{company}}\nAddress: {{address}}"
            },
            {
              "type": "section_header",
              "id": "sh_attest",
              "content": "Attestation"
            },
            {
              "type": "paragraph",
              "id": "p_attest",
              "content": "As of the date signed below, the undersigned represents and warrants that:"
            },
            {
              "type": "list",
              "id": "l_attest",
              "ordered": true,
              "items": [
                "I am not aware of any loss, claim, occurrence, accident, or circumstance that has occurred or may reasonably be expected to give rise to a claim under the coverage being bound.",
                "No loss or damage has occurred to any property to be insured, and no liability-producing event has taken place, between the prior policy expiration and the new effective date.",
                "I understand that any false statement or material omission may void coverage retroactive to inception and may constitute insurance fraud.",
                "I authorize my broker to submit this statement to the carrier as a condition of binding."
              ]
            },
            {
              "type": "divider",
              "id": "d1"
            },
            {
              "type": "section_header",
              "id": "sh_sign",
              "content": "Signature"
            },
            {
              "type": "field_text",
              "id": "f_title",
              "label": "Title / Capacity (e.g. Owner, Officer)",
              "inputType": "text",
              "signerId": "signer_1"
            },
            {
              "type": "field_date",
              "id": "f_date",
              "label": "Date Signed",
              "signerId": "signer_1"
            },
            {
              "type": "field_signature",
              "id": "f_sig",
              "label": "Signature of Named Insured",
              "required": true,
              "signerId": "signer_1"
            },
            {
              "type": "paragraph",
              "id": "p_footer",
              "content": "Executed on {{today}}. This document becomes part of the insured's permanent policy file."
            }
          ]
        },
        "tagRefs": [
          "tag:commercial"
        ],
        "samplePrompt": "Draft a no-loss letter (statement of no known losses) for an insurance broker to send before binding: insured name, business, and address merge fields, a numbered attestation that the insured knows of no losses or claims between the prior expiration and the new effective date, a fraud warning, a title/capacity text field, a date field, and a signature for the named insured."
      }
    ],
    "templates": [
      {
        "ref": "tmpl:upload-docs-sms",
        "type": "sms",
        "name": "Upload Documents Reminder (SMS)",
        "category": "data-collection",
        "content": {
          "message": "Hi {{first_name}}, it's {{company}} Insurance. To quote your coverage we need your prior policy & ID. Securely upload here: {{booking_link}} Reply STOP to opt out.",
          "variables": [
            "first_name",
            "company",
            "booking_link"
          ]
        },
        "samplePrompt": "Write a short, friendly SMS reminding a new insurance lead to upload their prior policy and ID via a secure link so we can start quoting, with the agency name and an opt-out line."
      },
      {
        "ref": "tmpl:quote-ready-email",
        "type": "email",
        "name": "Your Quote Comparison Is Ready (Email)",
        "category": "quote-comparison",
        "content": {
          "subject": "{{first_name}}, your insurance quote comparison is ready",
          "previewText": "We marketed your risk to multiple carriers \u2014 here's how the options stack up.",
          "body": `<p>Hi {{first_name}},</p><p>Great news \u2014 we finished marketing your coverage to several carriers and your side-by-side quote comparison is ready to review.</p><p>We compared premiums, limits, deductibles, and key exclusions so you can choose with confidence. Let's walk through the options together and answer any questions.</p><p><strong>Book your review:</strong> <a href="{{booking_link}}">{{booking_link}}</a></p><p>Once you pick a carrier, the final step is a quick no-loss letter and we'll request binding so your coverage starts on {{date}}.</p><p>Talk soon,<br/>Your team at {{company}}</p>`,
          "variables": [
            "first_name",
            "booking_link",
            "date",
            "company"
          ]
        },
        "samplePrompt": "Write a professional but warm email telling an insurance prospect their quote comparison across multiple carriers is ready, inviting them to book a review meeting via a link, and noting that after they choose, a no-loss letter and binding request will start their coverage."
      }
    ],
    "workflows": [
      {
        "ref": "wf:new-quote-intake",
        "name": "New Quote Request Intake",
        "description": "When a quote request form is submitted, tag the lead, request prior documents by text, and place them in Data Collection.",
        "triggerType": "form_submitted",
        "triggerConfig": {
          "formId": {
            "$ref": "form:quote-request"
          },
          "formName": "Insurance Quote Request"
        },
        "steps": [
          {
            "stepType": "add_tag",
            "config": {
              "tagId": {
                "$ref": "tag:new-lead"
              },
              "tagName": "New Lead"
            },
            "sortOrder": 1
          },
          {
            "stepType": "move_stage",
            "config": {
              "groupId": {
                "$ref": "pipeline:policies"
              },
              "stageId": {
                "$ref": "stage:data-collection"
              },
              "groupName": "Policy Pipeline",
              "stageName": "Data Collection"
            },
            "sortOrder": 2
          },
          {
            "stepType": "send_sms",
            "config": {
              "templateId": {
                "$ref": "tmpl:upload-docs-sms"
              },
              "templateName": "Upload Documents Reminder (SMS)"
            },
            "sortOrder": 3
          },
          {
            "stepType": "wait",
            "config": {
              "duration": 2,
              "unit": "days"
            },
            "sortOrder": 4
          },
          {
            "stepType": "send_form",
            "config": {
              "formId": {
                "$ref": "form:loss-history"
              },
              "formName": "Loss History & Underwriting Supplement"
            },
            "sortOrder": 5
          }
        ],
        "activate": true
      },
      {
        "ref": "wf:binding-no-loss",
        "name": "Binding: Send No-Loss Letter",
        "description": "When a contact moves to Binding Requested, email the quote-ready note and send the no-loss letter for signature.",
        "triggerType": "contact_stage_changed",
        "triggerConfig": {
          "groupId": {
            "$ref": "pipeline:policies"
          },
          "stageId": {
            "$ref": "stage:binding"
          }
        },
        "steps": [
          {
            "stepType": "send_email",
            "config": {
              "templateId": {
                "$ref": "tmpl:quote-ready-email"
              },
              "templateName": "Your Quote Comparison Is Ready (Email)"
            },
            "sortOrder": 1
          },
          {
            "stepType": "send_document",
            "config": {
              "documentId": {
                "$ref": "doc:no-loss-letter"
              },
              "documentName": "No-Loss Letter / Statement of No Known Losses"
            },
            "sortOrder": 2
          }
        ],
        "activate": true
      }
    ],
    "coworkPrompts": [
      "Move every contact in the Data Collection stage that has uploaded their prior policy into Market Submission, then text them letting them know we're shopping their coverage to carriers.",
      "For the Acme LLC lead in Quote Comparison, send the 'Your Quote Comparison Is Ready' email and book a review meeting, then advance them to Binding Requested.",
      "Send the No-Loss Letter / Statement of No Known Losses to all contacts in Binding Requested who haven't signed yet, and remind anyone still unsigned after 2 days."
    ]
  },
  {
    "kitId": "professional-services",
    "industryKey": "professional-services",
    "name": "Professional Services",
    "tagline": "Turn intake inquiries into signed engagements and funded retainers.",
    "scenario": "A prospective client fills out your matter intake form at 9pm describing a contract dispute. By morning the inquiry is logged in your Client Pipeline at Intake Discovery, a conflict-check reminder is on your desk, and an SMS has already confirmed their consultation slot. Once you scope the work, you send a branded engagement letter for e-signature and the matter moves to Retainer Paid the moment funds clear.",
    "icon": "Scale",
    "accentColor": "#6366f1",
    "tags": [
      {
        "ref": "tag:new-inquiry",
        "name": "New Inquiry",
        "color": "#6366f1"
      },
      {
        "ref": "tag:conflict-cleared",
        "name": "Conflict Cleared",
        "color": "#22c55e"
      },
      {
        "ref": "tag:engagement-signed",
        "name": "Engagement Signed",
        "color": "#f59e0b"
      },
      {
        "ref": "tag:high-value-matter",
        "name": "High-Value Matter",
        "color": "#8b5cf6"
      }
    ],
    "pipeline": {
      "ref": "pipeline:matters",
      "name": "Client Pipeline",
      "industry": "professional-services",
      "stages": [
        {
          "ref": "stage:intake-discovery",
          "name": "Intake Discovery",
          "color": "#8b5cf6",
          "exitCriteria": [
            "Matter type defined",
            "Conflict check cleared"
          ],
          "isDefault": true
        },
        {
          "ref": "stage:strategy",
          "name": "Strategy Session",
          "color": "#06b6d4",
          "exitCriteria": [
            "Estimated billable hours/fees calculated"
          ]
        },
        {
          "ref": "stage:engagement-sent",
          "name": "Engagement Sent",
          "color": "#f59e0b",
          "exitCriteria": [
            "Contract signed",
            "Payment terms agreed"
          ]
        },
        {
          "ref": "stage:retainer-paid",
          "name": "Retainer Paid",
          "color": "#22c55e",
          "exitCriteria": [
            "Retainer funds cleared",
            "Matter opened in system"
          ],
          "isFinal": true
        }
      ]
    },
    "forms": [
      {
        "ref": "form:matter-intake",
        "name": "New Matter Intake",
        "description": "Capture the details of a prospective client's matter so we can run a conflict check and scope the engagement.",
        "schema": {
          "layout": "two-column",
          "fields": [
            {
              "id": "sec_contact",
              "type": "section_header",
              "label": "Your Contact Information"
            },
            {
              "id": "first_name",
              "type": "text",
              "label": "First Name",
              "placeholder": "Jordan",
              "validation": {
                "required": true,
                "maxLength": 50
              }
            },
            {
              "id": "last_name",
              "type": "text",
              "label": "Last Name",
              "placeholder": "Avery",
              "validation": {
                "required": true,
                "maxLength": 50
              }
            },
            {
              "id": "email",
              "type": "email",
              "label": "Email Address",
              "placeholder": "you@example.com",
              "validation": {
                "required": true
              }
            },
            {
              "id": "phone",
              "type": "phone",
              "label": "Mobile Phone",
              "placeholder": "(555) 123-4567",
              "helpText": "We may text you to confirm your consultation.",
              "validation": {
                "required": true
              }
            },
            {
              "id": "company",
              "type": "text",
              "label": "Company / Organization",
              "placeholder": "Optional"
            },
            {
              "id": "sec_matter",
              "type": "section_header",
              "label": "About Your Matter"
            },
            {
              "id": "matter_type",
              "type": "dropdown",
              "label": "Type of Matter",
              "helpText": "Choose the area that best fits your situation.",
              "options": [
                {
                  "label": "Business / Contracts",
                  "value": "business"
                },
                {
                  "label": "Employment",
                  "value": "employment"
                },
                {
                  "label": "Real Estate",
                  "value": "real_estate"
                },
                {
                  "label": "Estate Planning",
                  "value": "estate"
                },
                {
                  "label": "Litigation / Dispute",
                  "value": "litigation"
                },
                {
                  "label": "Other",
                  "value": "other"
                }
              ],
              "validation": {
                "required": true
              }
            },
            {
              "id": "opposing_party",
              "type": "text",
              "label": "Opposing or Adverse Party (if any)",
              "placeholder": "Name(s) involved",
              "helpText": "Required so we can clear conflicts of interest."
            },
            {
              "id": "urgency",
              "type": "radio",
              "label": "How urgent is this matter?",
              "options": [
                {
                  "label": "Emergency (deadline within 72 hours)",
                  "value": "emergency"
                },
                {
                  "label": "Time-sensitive (within 2 weeks)",
                  "value": "time_sensitive"
                },
                {
                  "label": "Planning ahead",
                  "value": "planning"
                }
              ],
              "validation": {
                "required": true
              }
            },
            {
              "id": "matter_description",
              "type": "textarea",
              "label": "Briefly describe your situation",
              "placeholder": "Give us the key facts, dates, and what outcome you're hoping for.",
              "validation": {
                "required": true,
                "maxLength": 2e3
              }
            },
            {
              "id": "budget",
              "type": "dropdown",
              "label": "Anticipated Budget",
              "options": [
                {
                  "label": "Under $2,500",
                  "value": "under_2500"
                },
                {
                  "label": "$2,500 - $10,000",
                  "value": "2500_10000"
                },
                {
                  "label": "$10,000 - $50,000",
                  "value": "10000_50000"
                },
                {
                  "label": "$50,000+",
                  "value": "over_50000"
                },
                {
                  "label": "Not sure yet",
                  "value": "unsure"
                }
              ]
            },
            {
              "id": "how_heard",
              "type": "text",
              "label": "How did you hear about us?",
              "placeholder": "Referral, search, etc."
            }
          ]
        },
        "settings": {
          "submitButtonText": "Request a Consultation",
          "successMessage": "Thank you. Your intake has been received. We'll run a conflict check and reach out within one business day to schedule your consultation.",
          "captureSmsConsent": true,
          "captureEmailConsent": true,
          "consentText": "I agree to receive calls, texts, and emails from this firm regarding my inquiry. This submission does not create an attorney-client relationship and does not constitute legal advice."
        },
        "tagRefs": [
          "tag:new-inquiry"
        ],
        "samplePrompt": "Create a new-matter intake form for a law firm that captures contact info, matter type, opposing party for conflict checks, urgency, a description of the situation, and budget, with SMS and email consent."
      },
      {
        "ref": "form:consultation-feedback",
        "name": "Post-Consultation Next Steps",
        "description": "Confirm scope and preferred path after the initial strategy session.",
        "schema": {
          "layout": "single",
          "fields": [
            {
              "id": "intro",
              "type": "section_header",
              "label": "Confirm Your Engagement Preferences"
            },
            {
              "id": "client_name",
              "type": "text",
              "label": "Full Name",
              "validation": {
                "required": true
              }
            },
            {
              "id": "proceed",
              "type": "radio",
              "label": "Would you like to proceed with the engagement we discussed?",
              "options": [
                {
                  "label": "Yes, please send the engagement letter",
                  "value": "yes"
                },
                {
                  "label": "I have a few more questions first",
                  "value": "questions"
                },
                {
                  "label": "Not at this time",
                  "value": "no"
                }
              ],
              "validation": {
                "required": true
              }
            },
            {
              "id": "fee_pref",
              "type": "dropdown",
              "label": "Preferred Fee Arrangement",
              "options": [
                {
                  "label": "Flat fee",
                  "value": "flat"
                },
                {
                  "label": "Hourly with retainer",
                  "value": "hourly"
                },
                {
                  "label": "Contingency",
                  "value": "contingency"
                },
                {
                  "label": "Discuss with attorney",
                  "value": "discuss"
                }
              ]
            },
            {
              "id": "questions",
              "type": "textarea",
              "label": "Any remaining questions or notes for the attorney?",
              "placeholder": "Optional",
              "validation": {
                "maxLength": 1e3
              }
            }
          ]
        },
        "settings": {
          "submitButtonText": "Submit My Preferences",
          "successMessage": "Thanks. We've recorded your preferences and will follow up with the appropriate next step.",
          "captureSmsConsent": false,
          "captureEmailConsent": true
        },
        "tagRefs": [
          "tag:conflict-cleared"
        ],
        "samplePrompt": "Build a short post-consultation form that asks whether the client wants to proceed, their preferred fee arrangement (flat, hourly, contingency), and any remaining questions for the attorney."
      }
    ],
    "documents": [
      {
        "ref": "doc:engagement-letter",
        "name": "Legal Services Engagement Letter",
        "ndfDocument": {
          "version": "1.0",
          "kind": "document",
          "meta": {
            "title": "Legal Services Engagement Letter",
            "description": "Engagement letter establishing the attorney-client relationship, scope of work, and fee arrangement.",
            "variables": [
              {
                "name": "full_name",
                "label": "Client Full Name",
                "type": "text"
              },
              {
                "name": "company",
                "label": "Client Company",
                "type": "text"
              },
              {
                "name": "today",
                "label": "Engagement Date",
                "type": "date"
              },
              {
                "name": "document_name",
                "label": "Matter / Document Name",
                "type": "text"
              }
            ]
          },
          "signers": [
            {
              "id": "signer_1",
              "name": "Client",
              "order": 1
            }
          ],
          "blocks": [
            {
              "type": "heading",
              "id": "h_title",
              "level": 1,
              "content": "Legal Services Engagement Letter"
            },
            {
              "type": "paragraph",
              "id": "p_intro",
              "content": 'This Engagement Letter is entered into on {{today}} between our firm ("the Firm") and {{full_name}}{{company}} ("Client"). It sets out the terms under which the Firm will provide legal services in connection with the matter described below.'
            },
            {
              "type": "section_header",
              "id": "sh_scope",
              "content": "1. Scope of Engagement"
            },
            {
              "type": "paragraph",
              "id": "p_scope",
              "content": "The Firm agrees to represent the Client in connection with {{document_name}}. The scope of this engagement is limited to the services expressly described herein. Any additional matters will require a separate written agreement."
            },
            {
              "type": "section_header",
              "id": "sh_fees",
              "content": "2. Fees and Retainer"
            },
            {
              "type": "list",
              "id": "l_fees",
              "ordered": true,
              "items": [
                "The Client agrees to pay the Firm according to the fee arrangement selected below.",
                "The Client will deposit an initial retainer, to be held in the Firm's trust account and applied against fees and costs as they are incurred.",
                "The Firm will provide itemized invoices on a regular basis. Invoices are due within fifteen (15) days of receipt.",
                "The Client is responsible for out-of-pocket costs such as filing fees, court costs, and third-party expenses."
              ]
            },
            {
              "type": "field_select",
              "id": "f_fee_type",
              "label": "Fee Arrangement",
              "selectType": "radio",
              "options": [
                {
                  "label": "Flat fee",
                  "value": "flat"
                },
                {
                  "label": "Hourly with retainer",
                  "value": "hourly"
                },
                {
                  "label": "Contingency",
                  "value": "contingency"
                }
              ],
              "signerId": "signer_1"
            },
            {
              "type": "field_text",
              "id": "f_retainer",
              "label": "Agreed Retainer Amount (USD)",
              "inputType": "number",
              "signerId": "signer_1"
            },
            {
              "type": "section_header",
              "id": "sh_responsibilities",
              "content": "3. Client Responsibilities"
            },
            {
              "type": "paragraph",
              "id": "p_resp",
              "content": "The Client agrees to provide complete and accurate information, respond promptly to requests, and keep the Firm informed of any developments relevant to the matter. The Firm cannot guarantee any particular outcome."
            },
            {
              "type": "section_header",
              "id": "sh_confidentiality",
              "content": "4. Confidentiality"
            },
            {
              "type": "paragraph",
              "id": "p_conf",
              "content": "Communications between the Client and the Firm are protected by the attorney-client privilege and will be kept confidential in accordance with applicable rules of professional conduct."
            },
            {
              "type": "section_header",
              "id": "sh_termination",
              "content": "5. Termination"
            },
            {
              "type": "paragraph",
              "id": "p_term",
              "content": "Either party may terminate this engagement upon written notice. Upon termination, the Client remains responsible for fees and costs incurred through the termination date, and any unearned retainer will be refunded."
            },
            {
              "type": "divider",
              "id": "d_1"
            },
            {
              "type": "section_header",
              "id": "sh_ack",
              "content": "6. Acknowledgment and Signature"
            },
            {
              "type": "paragraph",
              "id": "p_ack",
              "content": "By signing below, the Client acknowledges that they have read, understood, and agree to the terms of this Engagement Letter."
            },
            {
              "type": "field_date",
              "id": "f_sign_date",
              "label": "Date Signed",
              "signerId": "signer_1"
            },
            {
              "type": "field_signature",
              "id": "f_signature",
              "label": "Client Signature",
              "required": true,
              "signerId": "signer_1"
            }
          ]
        },
        "tagRefs": [
          "tag:engagement-signed"
        ],
        "samplePrompt": "Draft a legal services engagement letter with scope of work, a retainer and fee section (flat / hourly / contingency selectable), client responsibilities, confidentiality, termination, and a client signature with date."
      }
    ],
    "templates": [
      {
        "ref": "tmpl:consult-confirmation-sms",
        "type": "sms",
        "name": "Consultation Confirmation SMS",
        "category": "intake",
        "content": {
          "message": "Hi {{first_name}}, thanks for reaching out to {{company}}. We've received your intake and will run a conflict check shortly. Book your consultation here: {{booking_link}}",
          "variables": [
            "first_name",
            "company",
            "booking_link"
          ]
        },
        "samplePrompt": "Write a short SMS confirming we received a new client's intake, that we're running a conflict check, and inviting them to book a consultation via their booking link."
      },
      {
        "ref": "tmpl:engagement-letter-email",
        "type": "email",
        "name": "Engagement Letter Ready Email",
        "category": "engagement",
        "content": {
          "subject": "Your engagement letter for {{document_name}} is ready to sign",
          "body": "<p>Dear {{first_name}},</p><p>It was a pleasure speaking with you about your matter. As discussed, we've prepared your engagement letter for <strong>{{document_name}}</strong>, which outlines the scope of our work, the fee arrangement, and the retainer.</p><p>Please review and sign the document at your earliest convenience. Once the signed letter and retainer are received, we'll formally open your matter and begin work.</p><p>If you have any questions before signing, simply reply to this email and we'll be glad to help.</p><p>Warm regards,<br/>{{company}}</p>",
          "previewText": "Review and sign your engagement letter to get started.",
          "variables": [
            "first_name",
            "document_name",
            "company"
          ]
        },
        "samplePrompt": "Compose a professional email letting a client know their engagement letter is ready to review and e-sign, explaining that work begins once the signed letter and retainer are received."
      }
    ],
    "workflows": [
      {
        "ref": "wf:intake-to-consult",
        "name": "New Intake to Consultation",
        "description": "When a prospect submits the New Matter Intake form, tag them, confirm by SMS, and place them in Intake Discovery for conflict checking.",
        "triggerType": "form_submitted",
        "triggerConfig": {
          "formId": {
            "$ref": "form:matter-intake"
          },
          "formName": "New Matter Intake"
        },
        "steps": [
          {
            "stepType": "add_tag",
            "config": {
              "tagId": {
                "$ref": "tag:new-inquiry"
              },
              "tagName": "New Inquiry"
            },
            "sortOrder": 1
          },
          {
            "stepType": "move_stage",
            "config": {
              "groupId": {
                "$ref": "pipeline:matters"
              },
              "stageId": {
                "$ref": "stage:intake-discovery"
              },
              "groupName": "Client Pipeline",
              "stageName": "Intake Discovery"
            },
            "sortOrder": 2
          },
          {
            "stepType": "send_sms",
            "config": {
              "templateId": {
                "$ref": "tmpl:consult-confirmation-sms"
              },
              "templateName": "Consultation Confirmation SMS"
            },
            "sortOrder": 3
          }
        ],
        "activate": true
      },
      {
        "ref": "wf:engagement-signed-followup",
        "name": "Engagement Signed Follow-Up",
        "description": "When the engagement letter is signed, tag the client and advance the matter toward retainer collection.",
        "triggerType": "document_signed",
        "steps": [
          {
            "stepType": "add_tag",
            "config": {
              "tagId": {
                "$ref": "tag:engagement-signed"
              },
              "tagName": "Engagement Signed"
            },
            "sortOrder": 1
          },
          {
            "stepType": "move_stage",
            "config": {
              "groupId": {
                "$ref": "pipeline:matters"
              },
              "stageId": {
                "$ref": "stage:retainer-paid"
              },
              "groupName": "Client Pipeline",
              "stageName": "Retainer Paid"
            },
            "sortOrder": 2
          },
          {
            "stepType": "wait",
            "config": {
              "duration": 1,
              "unit": "days"
            },
            "sortOrder": 3
          }
        ],
        "activate": true
      }
    ],
    "coworkPrompts": [
      "Find every contact in the Intake Discovery stage tagged New Inquiry that hasn't booked a consultation yet, and send them the Consultation Confirmation SMS.",
      "Generate an engagement letter from the Legal Services Engagement Letter document for Jordan Avery, send it for signature, and move their matter to Engagement Sent.",
      "Move all clients who signed their engagement letter this week to Retainer Paid, tag them Engagement Signed, and email them the Engagement Letter Ready Email if they haven't received it."
    ]
  },
  {
    "kitId": "retail-b2b",
    "industryKey": "retail-b2b",
    "name": "Retail B2B",
    "tagline": "Turn sample requests into signed purchase orders without dropping a single buyer.",
    "scenario": 'A boutique grocery buyer scans your booth at a trade show and requests a sample pack of your cold-brew line. You ship the samples, follow up when they land, walk the buyer through case pricing and MOQs, run a quick credit application, and close with a signed first PO for six SKUs. Every handoff is tracked so nothing stalls between "interested" and "on the shelf."',
    "icon": "Package",
    "accentColor": "#14b8a6",
    "tags": [
      {
        "ref": "tag:wholesale-buyer",
        "name": "Wholesale Buyer",
        "color": "#14b8a6"
      },
      {
        "ref": "tag:sample-sent",
        "name": "Sample Sent",
        "color": "#8b5cf6"
      },
      {
        "ref": "tag:net-terms",
        "name": "Net Terms",
        "color": "#f59e0b"
      },
      {
        "ref": "tag:key-account",
        "name": "Key Account",
        "color": "#22c55e"
      }
    ],
    "pipeline": {
      "ref": "pipeline:accounts",
      "name": "Wholesale Pipeline",
      "industry": "retail-b2b",
      "stages": [
        {
          "ref": "stage:sample-request",
          "name": "Sample Request",
          "color": "#8b5cf6",
          "exitCriteria": [
            "Samples shipped",
            "Tracking number added"
          ],
          "isDefault": true
        },
        {
          "ref": "stage:sample-feedback",
          "name": "Sample Feedback",
          "color": "#06b6d4",
          "exitCriteria": [
            "Product interest confirmed",
            "Pricing tiers discussed"
          ]
        },
        {
          "ref": "stage:credit-application",
          "name": "Credit Application",
          "color": "#f59e0b",
          "exitCriteria": [
            "Trade references checked",
            "Credit limit approved"
          ]
        },
        {
          "ref": "stage:first-po",
          "name": "First PO",
          "color": "#22c55e",
          "exitCriteria": [
            "SKU list confirmed",
            "PO signed"
          ],
          "isFinal": true
        }
      ]
    },
    "forms": [
      {
        "ref": "form:sample-request",
        "name": "Wholesale Sample Request",
        "description": "Let prospective retail buyers request a product sample pack and tell us about their store.",
        "schema": {
          "layout": "two-column",
          "fields": [
            {
              "id": "sec_business",
              "type": "section_header",
              "label": "Your Business"
            },
            {
              "id": "company",
              "type": "text",
              "label": "Store / Company Name",
              "placeholder": "e.g. Maple Street Market",
              "validation": {
                "required": true,
                "maxLength": 120
              }
            },
            {
              "id": "first_name",
              "type": "text",
              "label": "First Name",
              "placeholder": "Jordan",
              "validation": {
                "required": true
              }
            },
            {
              "id": "last_name",
              "type": "text",
              "label": "Last Name",
              "placeholder": "Rivera",
              "validation": {
                "required": true
              }
            },
            {
              "id": "email",
              "type": "email",
              "label": "Work Email",
              "placeholder": "buyer@store.com",
              "validation": {
                "required": true
              }
            },
            {
              "id": "phone",
              "type": "phone",
              "label": "Mobile Phone",
              "placeholder": "(555) 123-4567",
              "helpText": "We text shipping and tracking updates here.",
              "validation": {
                "required": true
              }
            },
            {
              "id": "channel",
              "type": "dropdown",
              "label": "Retail Channel",
              "options": [
                {
                  "label": "Independent grocery / specialty",
                  "value": "specialty"
                },
                {
                  "label": "Regional chain",
                  "value": "chain"
                },
                {
                  "label": "Convenience / c-store",
                  "value": "cstore"
                },
                {
                  "label": "Online / e-commerce",
                  "value": "online"
                },
                {
                  "label": "Distributor / broker",
                  "value": "distributor"
                }
              ],
              "validation": {
                "required": true
              }
            },
            {
              "id": "locations",
              "type": "number",
              "label": "Number of Store Locations",
              "placeholder": "1",
              "validation": {
                "required": true,
                "min": 1,
                "max": 5e3
              }
            },
            {
              "id": "sec_samples",
              "type": "section_header",
              "label": "Sample Pack"
            },
            {
              "id": "categories",
              "type": "checkbox",
              "label": "Which categories should we send?",
              "options": [
                {
                  "label": "Cold Brew & RTD Coffee",
                  "value": "coldbrew"
                },
                {
                  "label": "Sparkling & Functional Beverages",
                  "value": "sparkling"
                },
                {
                  "label": "Snacks & Pantry",
                  "value": "snacks"
                },
                {
                  "label": "Seasonal / Limited Edition",
                  "value": "seasonal"
                }
              ],
              "validation": {
                "required": true
              }
            },
            {
              "id": "ship_address",
              "type": "text",
              "label": "Ship-To Address",
              "placeholder": "123 Commerce Ave",
              "validation": {
                "required": true
              }
            },
            {
              "id": "ship_city",
              "type": "text",
              "label": "City",
              "placeholder": "Austin",
              "validation": {
                "required": true
              }
            },
            {
              "id": "ship_state",
              "type": "text",
              "label": "State",
              "placeholder": "TX",
              "validation": {
                "required": true,
                "maxLength": 2
              }
            },
            {
              "id": "timeline",
              "type": "radio",
              "label": "How soon are you looking to order?",
              "options": [
                {
                  "label": "This month",
                  "value": "now"
                },
                {
                  "label": "Next 1-3 months",
                  "value": "soon"
                },
                {
                  "label": "Just exploring",
                  "value": "exploring"
                }
              ],
              "validation": {
                "required": true
              }
            },
            {
              "id": "notes",
              "type": "textarea",
              "label": "Anything else we should know?",
              "placeholder": "Shelf space, current suppliers, seasonal needs...",
              "validation": {
                "maxLength": 600
              }
            }
          ]
        },
        "settings": {
          "submitButtonText": "Send My Samples",
          "successMessage": "Your sample pack is on its way! We'll text a tracking number once it ships.",
          "captureSmsConsent": true,
          "captureEmailConsent": true,
          "consentText": "I agree to receive shipping, order, and account text messages and emails from this brand. Msg & data rates may apply. Reply STOP to opt out."
        },
        "tagRefs": [
          "tag:wholesale-buyer"
        ],
        "samplePrompt": "Build a wholesale sample request form for a beverage brand that captures store name, retail channel, number of locations, which product categories to sample, a ship-to address, buying timeline, and SMS+email consent."
      },
      {
        "ref": "form:credit-app",
        "name": "Wholesale Credit Application",
        "description": "Collect trade references and business details to open a net-terms account.",
        "schema": {
          "layout": "single",
          "fields": [
            {
              "id": "sec_account",
              "type": "section_header",
              "label": "Account Information"
            },
            {
              "id": "company",
              "type": "text",
              "label": "Legal Business Name",
              "validation": {
                "required": true
              }
            },
            {
              "id": "dba",
              "type": "text",
              "label": "DBA / Trade Name",
              "placeholder": "If different from legal name"
            },
            {
              "id": "ein",
              "type": "text",
              "label": "EIN / Tax ID",
              "placeholder": "XX-XXXXXXX",
              "validation": {
                "required": true
              }
            },
            {
              "id": "years",
              "type": "number",
              "label": "Years in Business",
              "validation": {
                "required": true,
                "min": 0,
                "max": 200
              }
            },
            {
              "id": "ap_email",
              "type": "email",
              "label": "Accounts Payable Email",
              "validation": {
                "required": true
              }
            },
            {
              "id": "terms",
              "type": "dropdown",
              "label": "Requested Terms",
              "options": [
                {
                  "label": "Prepay / Credit card",
                  "value": "prepay"
                },
                {
                  "label": "Net 15",
                  "value": "net15"
                },
                {
                  "label": "Net 30",
                  "value": "net30"
                },
                {
                  "label": "Net 60",
                  "value": "net60"
                }
              ],
              "validation": {
                "required": true
              }
            },
            {
              "id": "est_monthly",
              "type": "number",
              "label": "Estimated Monthly Order Volume (USD)",
              "placeholder": "5000",
              "validation": {
                "required": true,
                "min": 0
              }
            },
            {
              "id": "div_refs",
              "type": "divider",
              "label": "Divider"
            },
            {
              "id": "sec_refs",
              "type": "section_header",
              "label": "Trade References"
            },
            {
              "id": "ref1_name",
              "type": "text",
              "label": "Reference 1 - Company",
              "validation": {
                "required": true
              }
            },
            {
              "id": "ref1_contact",
              "type": "phone",
              "label": "Reference 1 - Phone",
              "validation": {
                "required": true
              }
            },
            {
              "id": "ref2_name",
              "type": "text",
              "label": "Reference 2 - Company",
              "validation": {
                "required": true
              }
            },
            {
              "id": "ref2_contact",
              "type": "phone",
              "label": "Reference 2 - Phone",
              "validation": {
                "required": true
              }
            },
            {
              "id": "authorize",
              "type": "checkbox",
              "label": "Authorization",
              "options": [
                {
                  "label": "I authorize verification of the trade references and credit information provided above.",
                  "value": "yes"
                }
              ],
              "validation": {
                "required": true
              }
            }
          ]
        },
        "settings": {
          "submitButtonText": "Submit Application",
          "successMessage": "Thanks! Our credit team will review your application and references and follow up within 2 business days.",
          "captureEmailConsent": true,
          "consentText": "I agree to receive account and billing emails from this brand."
        },
        "tagRefs": [
          "tag:net-terms"
        ],
        "samplePrompt": "Create a wholesale credit application form that collects legal business name, EIN, years in business, AP email, requested net terms, estimated monthly volume, two trade references with phone numbers, and an authorization checkbox to verify references."
      }
    ],
    "documents": [
      {
        "ref": "doc:wholesale-agreement",
        "name": "Wholesale Account Agreement",
        "ndfDocument": {
          "version": "1.0",
          "kind": "document",
          "meta": {
            "title": "Wholesale Account Agreement",
            "description": "Terms governing the wholesale supply relationship, pricing, and payment terms between the supplier and the retail buyer.",
            "variables": [
              {
                "name": "company",
                "label": "Buyer Company",
                "type": "text"
              },
              {
                "name": "full_name",
                "label": "Authorized Signer",
                "type": "text"
              },
              {
                "name": "email",
                "label": "Buyer Email",
                "type": "email"
              },
              {
                "name": "date",
                "label": "Effective Date",
                "type": "date"
              }
            ]
          },
          "signers": [
            {
              "id": "signer_1",
              "name": "Authorized Buyer Representative",
              "order": 1
            }
          ],
          "blocks": [
            {
              "type": "heading",
              "id": "h1",
              "level": 1,
              "content": "Wholesale Account Agreement"
            },
            {
              "type": "paragraph",
              "id": "p_intro",
              "content": 'This Wholesale Account Agreement (the "Agreement") is entered into as of {{date}} between the Supplier and {{company}} (the "Buyer"). By signing below, the Buyer agrees to the terms set out in this Agreement for the purchase of products at wholesale.'
            },
            {
              "type": "section_header",
              "id": "sh_terms",
              "content": "1. Pricing & Minimum Orders"
            },
            {
              "type": "list",
              "id": "l_terms",
              "ordered": true,
              "items": [
                "Pricing is governed by the Supplier's then-current wholesale price list and any agreed account-specific pricing tiers.",
                "Each purchase order must meet the published minimum order quantity (MOQ) and case-pack requirements.",
                "Prices are exclusive of applicable taxes, freight, and duties unless otherwise stated on the invoice.",
                "The Supplier may revise list pricing with thirty (30) days written notice."
              ]
            },
            {
              "type": "section_header",
              "id": "sh_payment",
              "content": "2. Payment Terms"
            },
            {
              "type": "paragraph",
              "id": "p_payment",
              "content": "Payment terms are as approved during account onboarding (e.g., prepay, Net 15, Net 30, or Net 60). Past-due balances may accrue a late fee of 1.5% per month and may result in suspension of open terms and a return to prepay status."
            },
            {
              "type": "section_header",
              "id": "sh_ship",
              "content": "3. Shipping, Returns & Risk of Loss"
            },
            {
              "type": "list",
              "id": "l_ship",
              "ordered": true,
              "items": [
                "Title and risk of loss pass to the Buyer upon delivery to the carrier (FOB origin) unless otherwise agreed in writing.",
                "Shortage or damage claims must be reported within five (5) business days of receipt.",
                "Returns require prior written authorization; perishable, dated, and clearance goods are non-returnable."
              ]
            },
            {
              "type": "section_header",
              "id": "sh_resale",
              "content": "4. Resale & Brand Standards"
            },
            {
              "type": "paragraph",
              "id": "p_resale",
              "content": "Products are sold for resale through the Buyer's approved retail channels only. The Buyer agrees not to alter product packaging or labeling and to honor any minimum advertised price (MAP) policy provided by the Supplier."
            },
            {
              "type": "divider",
              "id": "div1"
            },
            {
              "type": "section_header",
              "id": "sh_ack",
              "content": "5. Acknowledgment"
            },
            {
              "type": "paragraph",
              "id": "p_ack",
              "content": "The undersigned represents that they are authorized to bind {{company}} to this Agreement and confirms that the account and contact information on file ({{email}}) is accurate."
            },
            {
              "type": "field_text",
              "id": "f_title",
              "label": "Signer Title",
              "inputType": "text",
              "signerId": "signer_1"
            },
            {
              "type": "field_text",
              "id": "f_email",
              "label": "Confirm Account Email",
              "inputType": "email",
              "signerId": "signer_1"
            },
            {
              "type": "field_date",
              "id": "f_date",
              "label": "Date Signed",
              "signerId": "signer_1"
            },
            {
              "type": "field_signature",
              "id": "f_sig",
              "label": "Authorized Buyer Signature",
              "required": true,
              "signerId": "signer_1"
            }
          ]
        },
        "tagRefs": [
          "tag:net-terms"
        ],
        "samplePrompt": "Draft a wholesale account agreement a retail buyer signs to open an account, covering wholesale pricing and MOQs, payment/net terms with late fees, FOB-origin shipping and return rules, resale and MAP brand standards, plus a signer title, account email, date, and signature field."
      }
    ],
    "templates": [
      {
        "ref": "tmpl:samples-shipped-sms",
        "type": "sms",
        "name": "Samples Shipped SMS",
        "category": "transactional",
        "content": {
          "message": "Hi {{first_name}}, your {{company}} sample pack just shipped! We'll text tracking shortly. Questions on case pricing? Just reply here. - The Wholesale Team",
          "variables": [
            "first_name",
            "company"
          ]
        },
        "samplePrompt": "Write a short, friendly SMS letting a wholesale buyer know their sample pack just shipped, mentioning tracking is coming and inviting them to reply with pricing questions."
      },
      {
        "ref": "tmpl:sample-followup-email",
        "type": "email",
        "name": "Sample Follow-Up Email",
        "category": "sales",
        "content": {
          "subject": "How are the samples, {{first_name}}? Let's talk pricing",
          "previewText": "Your case pricing, MOQs, and next steps for {{company}}.",
          "body": "<p>Hi {{first_name}},</p><p>I hope the sample pack landed well at {{company}}! I'd love to hear which items stood out so we can put together the right assortment for your shelves.</p><p>Here's what we can cover on a quick call:</p><ul><li>Wholesale case pricing and volume tiers</li><li>Minimum order quantities and case packs</li><li>Lead times and reorder cadence</li></ul><p>You can grab a time that works for you here: {{booking_link}}</p><p>When you're ready to open an account, the next step is a quick credit application so we can set up your terms. Just reply and I'll send it over.</p><p>Talk soon,<br/>The Wholesale Team</p>",
          "variables": [
            "first_name",
            "company",
            "booking_link"
          ]
        },
        "samplePrompt": "Write a warm sales follow-up email to a retail buyer after their sample pack arrived, asking which products stood out, offering to discuss case pricing, MOQs, and lead times on a call via a booking link, and teeing up the credit application as the next step."
      }
    ],
    "workflows": [
      {
        "ref": "wf:new-sample-request",
        "name": "New Sample Request Nurture",
        "description": "When a buyer submits the sample request form, tag them, notify them their samples shipped, wait a few days, then follow up about pricing and the credit application.",
        "triggerType": "form_submitted",
        "triggerConfig": {
          "formId": {
            "$ref": "form:sample-request"
          },
          "formName": "Wholesale Sample Request"
        },
        "activate": true,
        "steps": [
          {
            "stepType": "add_tag",
            "config": {
              "tagId": {
                "$ref": "tag:sample-sent"
              },
              "tagName": "Sample Sent"
            },
            "sortOrder": 1
          },
          {
            "stepType": "send_sms",
            "config": {
              "templateId": {
                "$ref": "tmpl:samples-shipped-sms"
              },
              "templateName": "Samples Shipped SMS"
            },
            "sortOrder": 2
          },
          {
            "stepType": "wait",
            "config": {
              "duration": 4,
              "unit": "days"
            },
            "sortOrder": 3
          },
          {
            "stepType": "send_email",
            "config": {
              "templateId": {
                "$ref": "tmpl:sample-followup-email"
              },
              "templateName": "Sample Follow-Up Email"
            },
            "sortOrder": 4
          },
          {
            "stepType": "move_stage",
            "config": {
              "groupId": {
                "$ref": "pipeline:accounts"
              },
              "stageId": {
                "$ref": "stage:sample-feedback"
              },
              "groupName": "Wholesale Pipeline",
              "stageName": "Sample Feedback"
            },
            "sortOrder": 5
          }
        ]
      },
      {
        "ref": "wf:po-signed",
        "name": "First PO Signed - Key Account",
        "description": "When the Wholesale Account Agreement is signed, mark the buyer as a key account and move them into First PO.",
        "triggerType": "document_signed",
        "activate": true,
        "steps": [
          {
            "stepType": "add_tag",
            "config": {
              "tagId": {
                "$ref": "tag:key-account"
              },
              "tagName": "Key Account"
            },
            "sortOrder": 1
          },
          {
            "stepType": "move_stage",
            "config": {
              "groupId": {
                "$ref": "pipeline:accounts"
              },
              "stageId": {
                "$ref": "stage:first-po"
              },
              "groupName": "Wholesale Pipeline",
              "stageName": "First PO"
            },
            "sortOrder": 2
          }
        ]
      }
    ],
    "coworkPrompts": [
      "Find every contact still in the Sample Request stage with no tracking number after 3 days and text them the Samples Shipped SMS, then move them to Sample Feedback.",
      "Send the Wholesale Account Agreement to all buyers in the Credit Application stage whose trade references are checked, and tag them Net Terms once it's out.",
      "Show me everyone who submitted the Wholesale Sample Request form this week, group them by retail channel, and draft a Sample Follow-Up Email to the ones in the Sample Feedback stage."
    ]
  },
  {
    "kitId": "spas-gyms",
    "industryKey": "spas-gyms",
    "name": "Spas & Gyms",
    "tagline": "Turn first-time tours into lifelong members.",
    "scenario": "A prospect spots your studio on Instagram and books a tour. Respectly texts them the address and a confirmation, walks them from a free trial into a goals consultation, and once they're sold it sends the liability waiver and membership agreement to e-sign right from their phone. By the time they leave the front desk, they're enrolled, billed, and already booked for their first class.",
    "icon": "Dumbbell",
    "accentColor": "#ec4899",
    "tags": [
      {
        "ref": "tag:new-lead",
        "name": "New Lead",
        "color": "#8b5cf6"
      },
      {
        "ref": "tag:trial-member",
        "name": "Trial Member",
        "color": "#06b6d4"
      },
      {
        "ref": "tag:vip",
        "name": "VIP",
        "color": "#ec4899"
      },
      {
        "ref": "tag:waiver-pending",
        "name": "Waiver Pending",
        "color": "#f59e0b"
      }
    ],
    "pipeline": {
      "ref": "pipeline:members",
      "name": "Membership Pipeline",
      "industry": "spas-gyms",
      "stages": [
        {
          "ref": "stage:tour-scheduled",
          "name": "Tour Scheduled",
          "color": "#8b5cf6",
          "exitCriteria": [
            "Appointment confirmed",
            "Staff tour-guide assigned"
          ],
          "isDefault": true
        },
        {
          "ref": "stage:trial-active",
          "name": "Trial Active",
          "color": "#06b6d4",
          "exitCriteria": [
            "2+ visits recorded",
            "User feedback captured"
          ]
        },
        {
          "ref": "stage:consultation",
          "name": "Consultation",
          "color": "#f59e0b",
          "exitCriteria": [
            "Pain points documented",
            "Plan recommendation made"
          ]
        },
        {
          "ref": "stage:enrolled",
          "name": "Enrolled",
          "color": "#22c55e",
          "exitCriteria": [
            "Waiver signed",
            "Payment method verified"
          ],
          "isFinal": true
        }
      ]
    },
    "forms": [
      {
        "ref": "form:book-a-tour",
        "name": "Book a Free Tour",
        "description": "Capture walk-in and online prospects and get them scheduled for a guided facility tour.",
        "schema": {
          "layout": "two-column",
          "fields": [
            {
              "id": "section_about",
              "type": "section_header",
              "label": "Tell us about you"
            },
            {
              "id": "first_name",
              "type": "text",
              "label": "First Name",
              "placeholder": "Jordan",
              "validation": {
                "required": true,
                "maxLength": 50
              }
            },
            {
              "id": "last_name",
              "type": "text",
              "label": "Last Name",
              "placeholder": "Rivera",
              "validation": {
                "required": true,
                "maxLength": 50
              }
            },
            {
              "id": "email",
              "type": "email",
              "label": "Email",
              "placeholder": "you@email.com",
              "validation": {
                "required": true
              }
            },
            {
              "id": "phone",
              "type": "phone",
              "label": "Mobile Phone",
              "placeholder": "(555) 123-4567",
              "helpText": "We'll text you a tour confirmation.",
              "validation": {
                "required": true
              }
            },
            {
              "id": "section_goals",
              "type": "section_header",
              "label": "Your goals"
            },
            {
              "id": "primary_goal",
              "type": "dropdown",
              "label": "Primary Goal",
              "options": [
                {
                  "label": "Weight Loss",
                  "value": "weight_loss"
                },
                {
                  "label": "Build Muscle / Strength",
                  "value": "strength"
                },
                {
                  "label": "Improve Mobility & Recovery",
                  "value": "mobility"
                },
                {
                  "label": "Stress Relief & Wellness",
                  "value": "wellness"
                },
                {
                  "label": "Train for an Event",
                  "value": "event"
                }
              ],
              "validation": {
                "required": true
              }
            },
            {
              "id": "experience",
              "type": "radio",
              "label": "Fitness Experience",
              "options": [
                {
                  "label": "Just getting started",
                  "value": "beginner"
                },
                {
                  "label": "Some experience",
                  "value": "intermediate"
                },
                {
                  "label": "Very active",
                  "value": "advanced"
                }
              ],
              "validation": {
                "required": true
              }
            },
            {
              "id": "preferred_time",
              "type": "dropdown",
              "label": "Preferred Tour Time",
              "options": [
                {
                  "label": "Weekday Morning",
                  "value": "wk_am"
                },
                {
                  "label": "Weekday Afternoon",
                  "value": "wk_pm"
                },
                {
                  "label": "Weekday Evening",
                  "value": "wk_eve"
                },
                {
                  "label": "Weekend",
                  "value": "weekend"
                }
              ],
              "validation": {
                "required": true
              }
            },
            {
              "id": "notes",
              "type": "textarea",
              "label": "Anything we should know?",
              "placeholder": "Injuries, scheduling notes, what you're hoping to find...",
              "validation": {
                "maxLength": 500
              }
            }
          ]
        },
        "settings": {
          "submitButtonText": "Book My Free Tour",
          "successMessage": "You're booked! Check your phone for a text confirmation and we'll see you soon.",
          "captureSmsConsent": true,
          "captureEmailConsent": true,
          "consentText": "I agree to receive appointment reminders and promotional messages by text and email. Message and data rates may apply. Reply STOP to opt out."
        },
        "tagRefs": [
          "tag:new-lead"
        ],
        "samplePrompt": "Create a 'Book a Free Tour' lead form for a boutique gym that captures name, email, mobile phone, primary fitness goal, experience level, and a preferred tour time, with SMS and email consent."
      },
      {
        "ref": "form:trial-feedback",
        "name": "Trial Experience Feedback",
        "description": "Collect feedback after a prospect's free trial visits to qualify them for a consultation.",
        "schema": {
          "layout": "single",
          "fields": [
            {
              "id": "intro",
              "type": "section_header",
              "label": "How was your trial?"
            },
            {
              "id": "overall_rating",
              "type": "radio",
              "label": "Overall, how would you rate your visits?",
              "options": [
                {
                  "label": "Loved it",
                  "value": "5"
                },
                {
                  "label": "Pretty good",
                  "value": "4"
                },
                {
                  "label": "It was okay",
                  "value": "3"
                },
                {
                  "label": "Not for me",
                  "value": "2"
                }
              ],
              "validation": {
                "required": true
              }
            },
            {
              "id": "favorite",
              "type": "dropdown",
              "label": "What did you enjoy most?",
              "options": [
                {
                  "label": "Group Classes",
                  "value": "classes"
                },
                {
                  "label": "Strength / Free Weights",
                  "value": "strength"
                },
                {
                  "label": "Spa & Recovery",
                  "value": "spa"
                },
                {
                  "label": "The Coaches & Staff",
                  "value": "staff"
                },
                {
                  "label": "The Community",
                  "value": "community"
                }
              ],
              "validation": {
                "required": true
              }
            },
            {
              "id": "blockers",
              "type": "checkbox",
              "label": "Anything holding you back from joining?",
              "options": [
                {
                  "label": "Price / budget",
                  "value": "price"
                },
                {
                  "label": "Schedule / class times",
                  "value": "schedule"
                },
                {
                  "label": "Want to compare other gyms",
                  "value": "comparing"
                },
                {
                  "label": "Not sure which plan fits",
                  "value": "plan_fit"
                },
                {
                  "label": "Nothing, I'm ready!",
                  "value": "ready"
                }
              ]
            },
            {
              "id": "ready_to_join",
              "type": "radio",
              "label": "Ready to talk membership options?",
              "options": [
                {
                  "label": "Yes, set up my consultation",
                  "value": "yes"
                },
                {
                  "label": "Maybe, send me more info",
                  "value": "maybe"
                },
                {
                  "label": "Not yet",
                  "value": "no"
                }
              ],
              "validation": {
                "required": true
              }
            },
            {
              "id": "comments",
              "type": "textarea",
              "label": "Other comments",
              "placeholder": "Tell us anything else...",
              "validation": {
                "maxLength": 500
              }
            }
          ]
        },
        "settings": {
          "submitButtonText": "Submit Feedback",
          "successMessage": "Thanks for the feedback! A coach will reach out about next steps.",
          "captureSmsConsent": false,
          "captureEmailConsent": false
        },
        "tagRefs": [
          "tag:trial-member"
        ],
        "samplePrompt": "Build a short post-trial feedback form for gym prospects that asks for an overall rating, what they enjoyed most, what's holding them back, and whether they're ready to discuss membership."
      }
    ],
    "documents": [
      {
        "ref": "doc:membership-waiver",
        "name": "Membership Agreement & Liability Waiver",
        "ndfDocument": {
          "version": "1.0",
          "kind": "document",
          "meta": {
            "title": "Membership Agreement & Liability Waiver",
            "description": "Standard membership enrollment agreement including assumption of risk, liability release, and billing authorization for spa and fitness facilities.",
            "variables": [
              {
                "name": "full_name",
                "label": "Member Full Name",
                "type": "text"
              },
              {
                "name": "email",
                "label": "Member Email",
                "type": "email"
              },
              {
                "name": "phone",
                "label": "Member Phone",
                "type": "phone"
              },
              {
                "name": "company",
                "label": "Facility Name",
                "type": "text"
              },
              {
                "name": "date",
                "label": "Effective Date",
                "type": "date"
              }
            ]
          },
          "signers": [
            {
              "id": "signer_1",
              "name": "Member",
              "order": 1
            }
          ],
          "blocks": [
            {
              "type": "heading",
              "id": "h1",
              "level": 1,
              "content": "Membership Agreement & Liability Waiver"
            },
            {
              "type": "paragraph",
              "id": "p_intro",
              "content": 'This Membership Agreement ("Agreement") is entered into as of {{date}} between {{company}} ("Facility") and {{full_name}} ("Member"). By signing below, the Member agrees to the terms set forth in this Agreement.'
            },
            {
              "type": "section_header",
              "id": "sh_member",
              "content": "1. Member Information"
            },
            {
              "type": "field_text",
              "id": "f_name",
              "label": "Full Legal Name",
              "inputType": "text",
              "signerId": "signer_1"
            },
            {
              "type": "field_text",
              "id": "f_email",
              "label": "Email Address",
              "inputType": "email",
              "signerId": "signer_1"
            },
            {
              "type": "field_text",
              "id": "f_phone",
              "label": "Mobile Phone",
              "inputType": "phone",
              "signerId": "signer_1"
            },
            {
              "type": "field_text",
              "id": "f_emergency",
              "label": "Emergency Contact (Name & Phone)",
              "inputType": "text",
              "signerId": "signer_1"
            },
            {
              "type": "section_header",
              "id": "sh_plan",
              "content": "2. Membership Plan"
            },
            {
              "type": "field_select",
              "id": "f_plan",
              "label": "Selected Membership Plan",
              "selectType": "radio",
              "options": [
                {
                  "label": "Month-to-Month",
                  "value": "monthly"
                },
                {
                  "label": "6-Month Commitment",
                  "value": "6mo"
                },
                {
                  "label": "12-Month Commitment",
                  "value": "12mo"
                },
                {
                  "label": "Spa & Recovery Add-On",
                  "value": "spa_addon"
                }
              ],
              "signerId": "signer_1"
            },
            {
              "type": "section_header",
              "id": "sh_risk",
              "content": "3. Assumption of Risk"
            },
            {
              "type": "paragraph",
              "id": "p_risk",
              "content": "The Member acknowledges that participation in physical exercise, fitness classes, spa services, and use of equipment involves inherent risks of injury. The Member voluntarily assumes all such risks and confirms that they are in good physical condition and have no medical condition that would prevent safe participation."
            },
            {
              "type": "section_header",
              "id": "sh_release",
              "content": "4. Release of Liability"
            },
            {
              "type": "list",
              "id": "l_release",
              "ordered": true,
              "items": [
                "The Member releases {{company}}, its owners, employees, and agents from any liability for injury, loss, or damage arising from use of the facility or participation in any activity.",
                "The Member agrees that this release applies to claims of negligence to the fullest extent permitted by law.",
                "This waiver is binding on the Member's heirs, assigns, and legal representatives."
              ]
            },
            {
              "type": "section_header",
              "id": "sh_billing",
              "content": "5. Billing Authorization"
            },
            {
              "type": "paragraph",
              "id": "p_billing",
              "content": "The Member authorizes {{company}} to charge the payment method on file for recurring membership dues and applicable fees. Cancellation requires written notice in accordance with the facility's posted policy."
            },
            {
              "type": "field_select",
              "id": "f_billing_ack",
              "label": "I authorize recurring billing to my payment method on file",
              "selectType": "checkbox",
              "options": [
                {
                  "label": "Yes, I authorize recurring charges",
                  "value": "authorized"
                }
              ],
              "signerId": "signer_1"
            },
            {
              "type": "section_header",
              "id": "sh_policies",
              "content": "6. Facility Policies"
            },
            {
              "type": "paragraph",
              "id": "p_policies",
              "content": "The Member agrees to follow all posted facility rules, including conduct, hygiene, equipment use, and cancellation policies. {{company}} reserves the right to revoke membership for violations of these policies."
            },
            {
              "type": "divider",
              "id": "div1"
            },
            {
              "type": "section_header",
              "id": "sh_sign",
              "content": "7. Acknowledgment & Signature"
            },
            {
              "type": "paragraph",
              "id": "p_ack",
              "content": "I, {{full_name}}, have read and understand this Agreement, including the assumption of risk and release of liability. I am signing freely and voluntarily."
            },
            {
              "type": "field_date",
              "id": "f_sign_date",
              "label": "Date Signed",
              "signerId": "signer_1"
            },
            {
              "type": "field_signature",
              "id": "f_signature",
              "label": "Member Signature",
              "required": true,
              "signerId": "signer_1"
            }
          ]
        },
        "tagRefs": [
          "tag:waiver-pending"
        ],
        "samplePrompt": "Draft a membership agreement and liability waiver for a gym and spa that includes member and emergency contact info, a membership plan selection, assumption of risk, a release of liability, recurring billing authorization, facility policies, and a member signature with date."
      }
    ],
    "templates": [
      {
        "ref": "tmpl:tour-confirmation-sms",
        "type": "sms",
        "name": "Tour Confirmation SMS",
        "category": "appointment",
        "content": {
          "message": "Hi {{first_name}}! You're booked for your free tour at {{company}}. We're at {{address}}, {{city}} {{state}}. Reply with any questions or to reschedule. Can't wait to show you around! Reply STOP to opt out.",
          "variables": [
            "first_name",
            "company",
            "address",
            "city",
            "state"
          ]
        },
        "samplePrompt": "Write a friendly SMS confirming a prospect's free gym tour that includes their first name, the facility name and address, and an invitation to reply to reschedule."
      },
      {
        "ref": "tmpl:welcome-enrolled-email",
        "type": "email",
        "name": "Welcome New Member Email",
        "category": "onboarding",
        "content": {
          "subject": "Welcome to {{company}}, {{first_name}}! \u{1F389}",
          "previewText": "Your membership is active \u2014 here's everything you need to get started.",
          "body": `<h1>Welcome to the family, {{first_name}}!</h1><p>We're thrilled to have you as a member of <strong>{{company}}</strong>. Your waiver is signed, your membership is active, and your fitness journey starts now.</p><h2>Here's how to hit the ground running:</h2><ul><li><strong>Book your first class:</strong> <a href="{{booking_link}}">Reserve your spot here</a></li><li><strong>Meet your coach:</strong> Stop by the front desk on your next visit and we'll introduce you.</li><li><strong>Download the app:</strong> Manage bookings, track visits, and check the class schedule on the go.</li></ul><p>Questions? Just reply to this email or text us anytime \u2014 we're here to help you reach your goals.</p><p>See you soon,<br/>The team at {{company}}</p>`,
          "variables": [
            "first_name",
            "company",
            "booking_link"
          ]
        },
        "samplePrompt": "Write a warm welcome email for a newly enrolled gym member that congratulates them, links to book their first class with the booking link, tells them to meet their coach and download the app, and is signed by the facility team."
      }
    ],
    "workflows": [
      {
        "ref": "wf:tour-booking-followup",
        "name": "Tour Booking Follow-Up",
        "description": "When a prospect books a tour, tag them, confirm via SMS, and place them in the Tour Scheduled stage.",
        "triggerType": "form_submitted",
        "triggerConfig": {
          "formId": {
            "$ref": "form:book-a-tour"
          },
          "formName": "Book a Free Tour"
        },
        "steps": [
          {
            "stepType": "add_tag",
            "config": {
              "tagId": {
                "$ref": "tag:new-lead"
              },
              "tagName": "New Lead"
            },
            "sortOrder": 0
          },
          {
            "stepType": "move_stage",
            "config": {
              "groupId": {
                "$ref": "pipeline:members"
              },
              "stageId": {
                "$ref": "stage:tour-scheduled"
              },
              "groupName": "Membership Pipeline",
              "stageName": "Tour Scheduled"
            },
            "sortOrder": 1
          },
          {
            "stepType": "send_sms",
            "config": {
              "templateId": {
                "$ref": "tmpl:tour-confirmation-sms"
              },
              "templateName": "Tour Confirmation SMS"
            },
            "sortOrder": 2
          }
        ],
        "activate": true
      },
      {
        "ref": "wf:enrollment-onboarding",
        "name": "New Member Onboarding",
        "description": "When the membership waiver is signed, move the member to Enrolled, tag, wait a day, then send the welcome email.",
        "triggerType": "document_signed",
        "triggerConfig": {
          "documentId": {
            "$ref": "doc:membership-waiver"
          },
          "documentName": "Membership Agreement & Liability Waiver"
        },
        "steps": [
          {
            "stepType": "move_stage",
            "config": {
              "groupId": {
                "$ref": "pipeline:members"
              },
              "stageId": {
                "$ref": "stage:enrolled"
              },
              "groupName": "Membership Pipeline",
              "stageName": "Enrolled"
            },
            "sortOrder": 0
          },
          {
            "stepType": "remove_tag",
            "config": {
              "tagId": {
                "$ref": "tag:waiver-pending"
              },
              "tagName": "Waiver Pending"
            },
            "sortOrder": 1
          },
          {
            "stepType": "send_email",
            "config": {
              "templateId": {
                "$ref": "tmpl:welcome-enrolled-email"
              },
              "templateName": "Welcome New Member Email"
            },
            "sortOrder": 2
          },
          {
            "stepType": "wait",
            "config": {
              "duration": 1,
              "unit": "days"
            },
            "sortOrder": 3
          },
          {
            "stepType": "add_tag",
            "config": {
              "tagId": {
                "$ref": "tag:trial-member"
              },
              "tagName": "Trial Member"
            },
            "sortOrder": 4
          }
        ],
        "activate": true
      }
    ],
    "coworkPrompts": [
      "Move everyone in the Trial Active stage who submitted the Trial Experience Feedback form and said they're ready to join into the Consultation stage, and tag them VIP.",
      "Send the Membership Agreement & Liability Waiver to all contacts in the Consultation stage and tag them Waiver Pending.",
      "Show me every contact still in Tour Scheduled from the last 7 days and text them the Tour Confirmation SMS as a reminder."
    ]
  },
  {
    "kitId": "saas-ai-tech",
    "industryKey": "saas-ai-tech",
    "name": "SaaS / AI Tech",
    "tagline": "Turn free trials into product-qualified revenue.",
    "scenario": "A developer signs up for your AI platform's free trial on Tuesday night and runs their first task. By Thursday they've hit the usage cap and invited two teammates, so your team needs to reach them before the trial expires. This kit auto-nurtures every signup, flags product-qualified accounts the moment they show buying intent, and routes them into a sales demo with security review and an order form ready to sign.",
    "icon": "Sparkles",
    "accentColor": "#8b5cf6",
    "tags": [
      {
        "ref": "tag:trial",
        "name": "Free Trial",
        "color": "#8b5cf6"
      },
      {
        "ref": "tag:pql",
        "name": "Product Qualified",
        "color": "#f59e0b"
      },
      {
        "ref": "tag:demo-requested",
        "name": "Demo Requested",
        "color": "#22c55e"
      },
      {
        "ref": "tag:churn-risk",
        "name": "Churn Risk",
        "color": "#ef4444"
      }
    ],
    "pipeline": {
      "ref": "pipeline:accounts",
      "name": "Growth Pipeline",
      "industry": "saas-ai-tech",
      "stages": [
        {
          "ref": "stage:trial-signup",
          "name": "Trial Signup",
          "color": "#8b5cf6",
          "exitCriteria": [
            "Email verified",
            "Company profile complete"
          ],
          "isDefault": true
        },
        {
          "ref": "stage:active-aha",
          "name": "Active / Aha!",
          "color": "#06b6d4",
          "exitCriteria": [
            "First report/AI task completed"
          ]
        },
        {
          "ref": "stage:pql",
          "name": "PQL (Product Qualified)",
          "color": "#f59e0b",
          "exitCriteria": [
            "Usage limit reached",
            "Team members invited"
          ]
        },
        {
          "ref": "stage:sales-demo",
          "name": "Sales Demo",
          "color": "#22c55e",
          "exitCriteria": [
            "Security/IT review passed",
            "Legal terms reviewed"
          ]
        },
        {
          "ref": "stage:closed-won",
          "name": "Closed Won",
          "color": "#16a34a",
          "exitCriteria": [
            "Billing activated",
            "Success manager assigned"
          ],
          "isFinal": true
        }
      ]
    },
    "forms": [
      {
        "ref": "form:demo-request",
        "name": "Book a Product Demo",
        "description": "Capture demo requests from trial users and high-intent visitors and route them straight to sales.",
        "schema": {
          "layout": "two-column",
          "fields": [
            {
              "id": "section_contact",
              "type": "section_header",
              "label": "Tell us about you"
            },
            {
              "id": "first_name",
              "type": "text",
              "label": "First name",
              "placeholder": "Jordan",
              "validation": {
                "required": true,
                "maxLength": 60
              }
            },
            {
              "id": "last_name",
              "type": "text",
              "label": "Last name",
              "placeholder": "Rivera",
              "validation": {
                "required": true,
                "maxLength": 60
              }
            },
            {
              "id": "work_email",
              "type": "email",
              "label": "Work email",
              "placeholder": "jordan@company.com",
              "helpText": "Please use your company email so we can match your trial account.",
              "validation": {
                "required": true
              }
            },
            {
              "id": "phone",
              "type": "phone",
              "label": "Phone",
              "placeholder": "(555) 123-4567",
              "validation": {
                "required": false
              }
            },
            {
              "id": "company",
              "type": "text",
              "label": "Company",
              "placeholder": "Acme AI, Inc.",
              "validation": {
                "required": true,
                "maxLength": 120
              }
            },
            {
              "id": "section_context",
              "type": "section_header",
              "label": "What are you trying to build?"
            },
            {
              "id": "team_size",
              "type": "dropdown",
              "label": "Team size",
              "helpText": "How many people will use the platform?",
              "options": [
                {
                  "label": "Just me",
                  "value": "1"
                },
                {
                  "label": "2-10",
                  "value": "2-10"
                },
                {
                  "label": "11-50",
                  "value": "11-50"
                },
                {
                  "label": "51-200",
                  "value": "51-200"
                },
                {
                  "label": "200+",
                  "value": "200+"
                }
              ],
              "validation": {
                "required": true
              }
            },
            {
              "id": "use_case",
              "type": "dropdown",
              "label": "Primary use case",
              "options": [
                {
                  "label": "AI agents / automation",
                  "value": "agents"
                },
                {
                  "label": "Analytics & reporting",
                  "value": "analytics"
                },
                {
                  "label": "Internal developer tooling",
                  "value": "devtools"
                },
                {
                  "label": "Customer-facing product",
                  "value": "product"
                },
                {
                  "label": "Other",
                  "value": "other"
                }
              ],
              "validation": {
                "required": true
              }
            },
            {
              "id": "timeline",
              "type": "radio",
              "label": "When do you want to go live?",
              "options": [
                {
                  "label": "This month",
                  "value": "this_month"
                },
                {
                  "label": "This quarter",
                  "value": "this_quarter"
                },
                {
                  "label": "Just exploring",
                  "value": "exploring"
                }
              ],
              "validation": {
                "required": true
              }
            },
            {
              "id": "goals",
              "type": "textarea",
              "label": "What does success look like?",
              "placeholder": "We want to automate our support triage and cut response time in half...",
              "validation": {
                "required": false,
                "maxLength": 1e3
              }
            }
          ]
        },
        "settings": {
          "submitButtonText": "Book My Demo",
          "successMessage": "You're booked! Check your email for a calendar invite \u2014 our team will reach out within one business day.",
          "captureEmailConsent": true,
          "captureSmsConsent": true,
          "consentText": "I agree to receive product updates and demo-related messages from this company by email and SMS. Message and data rates may apply. Reply STOP to opt out."
        },
        "tagRefs": [
          "tag:demo-requested"
        ],
        "samplePrompt": "Create a demo request form for our AI SaaS platform that captures work email, company, team size, primary use case, go-live timeline, and what success looks like \u2014 with email and SMS consent and a 'Book My Demo' button."
      },
      {
        "ref": "form:churn-feedback",
        "name": "Trial Feedback / Cancellation",
        "description": "Collect feedback when a trial user is about to cancel so we can recover the account or learn why they're leaving.",
        "schema": {
          "layout": "single",
          "fields": [
            {
              "id": "reason_header",
              "type": "section_header",
              "label": "Help us improve"
            },
            {
              "id": "reason",
              "type": "radio",
              "label": "What's the main reason you're leaving?",
              "options": [
                {
                  "label": "Too expensive",
                  "value": "price"
                },
                {
                  "label": "Missing a feature I need",
                  "value": "missing_feature"
                },
                {
                  "label": "Too hard to set up",
                  "value": "onboarding"
                },
                {
                  "label": "Found a different tool",
                  "value": "competitor"
                },
                {
                  "label": "Just testing / not ready",
                  "value": "not_ready"
                }
              ],
              "validation": {
                "required": true
              }
            },
            {
              "id": "missing",
              "type": "textarea",
              "label": "What's the one thing that would have changed your mind?",
              "placeholder": "A native Slack integration and better docs...",
              "validation": {
                "required": false,
                "maxLength": 800
              }
            },
            {
              "id": "talk_to_us",
              "type": "checkbox",
              "label": "Before you go",
              "options": [
                {
                  "label": "I'd be open to a quick call to see if we can make it work",
                  "value": "open_to_call"
                }
              ]
            }
          ]
        },
        "settings": {
          "submitButtonText": "Submit Feedback",
          "successMessage": "Thanks for the honest feedback \u2014 it genuinely helps us build a better product.",
          "captureEmailConsent": false,
          "captureSmsConsent": false
        },
        "tagRefs": [
          "tag:churn-risk"
        ],
        "samplePrompt": "Build a short trial cancellation / feedback form asking the main reason for leaving (price, missing feature, onboarding, competitor, not ready), an open text box for the one thing that would change their mind, and an opt-in checkbox for a save call."
      }
    ],
    "documents": [
      {
        "ref": "doc:order-form-msa",
        "name": "Order Form & Subscription Agreement",
        "ndfDocument": {
          "version": "1.0",
          "kind": "document",
          "meta": {
            "title": "Order Form & Subscription Agreement",
            "description": "SaaS order form and master subscription agreement covering plan, seats, term, and acceptable use, executed by the customer signer.",
            "variables": [
              {
                "name": "company",
                "label": "Customer Company",
                "type": "text"
              },
              {
                "name": "full_name",
                "label": "Authorized Signer",
                "type": "text"
              },
              {
                "name": "email",
                "label": "Signer Email",
                "type": "email"
              },
              {
                "name": "today",
                "label": "Effective Date",
                "type": "date"
              }
            ]
          },
          "signers": [
            {
              "id": "signer_1",
              "name": "Authorized Signer",
              "order": 1
            }
          ],
          "blocks": [
            {
              "type": "heading",
              "id": "h_title",
              "level": 1,
              "content": "Order Form & Subscription Agreement"
            },
            {
              "type": "paragraph",
              "id": "p_intro",
              "content": 'This Order Form and Subscription Agreement (the "Agreement") is entered into as of {{today}} by and between {{company}} ("Customer") and the service provider ("Provider"). By signing below, the Authorized Signer accepts the terms set out in this Agreement on behalf of Customer.'
            },
            {
              "type": "section_header",
              "id": "sh_plan",
              "content": "1. Subscription Plan & Pricing"
            },
            {
              "type": "paragraph",
              "id": "p_plan",
              "content": "Customer subscribes to the plan, seat count, and usage tier selected below. Fees are billed in advance for the subscription term and are non-refundable except as required by law."
            },
            {
              "type": "field_select",
              "id": "f_plan",
              "label": "Selected Plan",
              "selectType": "dropdown",
              "options": [
                {
                  "label": "Starter \u2014 monthly",
                  "value": "starter"
                },
                {
                  "label": "Growth \u2014 annual",
                  "value": "growth"
                },
                {
                  "label": "Scale \u2014 annual",
                  "value": "scale"
                },
                {
                  "label": "Enterprise \u2014 custom",
                  "value": "enterprise"
                }
              ],
              "signerId": "signer_1"
            },
            {
              "type": "field_text",
              "id": "f_seats",
              "label": "Number of Seats",
              "inputType": "number",
              "signerId": "signer_1"
            },
            {
              "type": "field_select",
              "id": "f_term",
              "label": "Subscription Term",
              "selectType": "radio",
              "options": [
                {
                  "label": "Monthly",
                  "value": "monthly"
                },
                {
                  "label": "12 months",
                  "value": "12mo"
                },
                {
                  "label": "24 months",
                  "value": "24mo"
                }
              ],
              "signerId": "signer_1"
            },
            {
              "type": "section_header",
              "id": "sh_use",
              "content": "2. Acceptable Use & AI Terms"
            },
            {
              "type": "paragraph",
              "id": "p_use",
              "content": "Customer agrees to use the platform in compliance with the Provider's Acceptable Use Policy. Customer is responsible for the data and prompts it submits and will not use the AI services to generate unlawful content, infringe third-party rights, or attempt to reverse-engineer the models."
            },
            {
              "type": "list",
              "id": "l_use",
              "ordered": true,
              "items": [
                "Customer retains ownership of its input data and resulting outputs.",
                "Provider will not use Customer data to train shared models without written consent.",
                "Customer will not resell, sublicense, or benchmark the service without prior written approval.",
                "Each named seat is for a single individual and may not be shared."
              ]
            },
            {
              "type": "section_header",
              "id": "sh_terms",
              "content": "3. Term, Renewal & Data Handling"
            },
            {
              "type": "paragraph",
              "id": "p_terms",
              "content": "This Agreement renews automatically for successive terms unless either party provides notice of non-renewal at least 30 days before the end of the then-current term. Upon termination, Provider will make Customer data available for export for 30 days, after which it may be deleted in accordance with the Provider's data retention policy."
            },
            {
              "type": "divider",
              "id": "d_1"
            },
            {
              "type": "section_header",
              "id": "sh_sign",
              "content": "4. Authorization & Signature"
            },
            {
              "type": "paragraph",
              "id": "p_sign",
              "content": "The undersigned represents that they are authorized to bind {{company}} to this Agreement and agree to its terms."
            },
            {
              "type": "field_text",
              "id": "f_signer_name",
              "label": "Authorized Signer Name",
              "inputType": "text",
              "signerId": "signer_1"
            },
            {
              "type": "field_text",
              "id": "f_title",
              "label": "Title",
              "inputType": "text",
              "signerId": "signer_1"
            },
            {
              "type": "field_text",
              "id": "f_signer_email",
              "label": "Signer Email",
              "inputType": "email",
              "signerId": "signer_1"
            },
            {
              "type": "field_date",
              "id": "f_sign_date",
              "label": "Date",
              "signerId": "signer_1"
            },
            {
              "type": "field_signature",
              "id": "f_sig",
              "label": "Customer Signature",
              "required": true,
              "signerId": "signer_1"
            }
          ]
        },
        "tagRefs": [
          "tag:demo-requested"
        ],
        "samplePrompt": "Generate a SaaS order form and subscription agreement for an AI platform with a plan dropdown (Starter/Growth/Scale/Enterprise), seat count, term selection, an acceptable-use and AI data section, auto-renewal terms, and a signature block for the customer's authorized signer."
      }
    ],
    "templates": [
      {
        "ref": "tmpl:trial-day1-sms",
        "type": "sms",
        "name": "Trial Day 1 \u2014 Get to First Value",
        "category": "onboarding",
        "content": {
          "message": "Hey {{first_name}}, welcome to {{company}}! Your trial is live. The fastest path to value is running your first AI task \u2014 here's a 2-min walkthrough: {{booking_link}} Reply HELP anytime and a human will jump in.",
          "variables": [
            "first_name",
            "company",
            "booking_link"
          ]
        },
        "samplePrompt": "Write a friendly day-1 onboarding SMS for a new AI SaaS trial user that nudges them to run their first task with a quick walkthrough link and offers human help."
      },
      {
        "ref": "tmpl:pql-demo-email",
        "type": "email",
        "name": "PQL \u2014 You're Crushing It, Let's Talk Scale",
        "category": "sales",
        "content": {
          "subject": "{{first_name}}, your team is getting real value \u2014 let's unlock more",
          "body": `<p>Hi {{first_name}},</p><p>I noticed your team at {{company}} has been getting serious value out of the platform \u2014 you've completed your first AI tasks and invited teammates. That's exactly the moment most teams start hitting trial limits.</p><p>I'd love to show you how customers at your stage scale up: higher usage limits, team workspaces, SSO, and security/compliance docs for your IT review.</p><p>Grab a time that works for you here:</p><p><a href="{{booking_link}}">Book a 20-minute demo</a></p><p>Looking forward to it,<br/>The {{company}} Team</p>`,
          "previewText": "You've hit your aha moment \u2014 let's talk about scaling your team.",
          "variables": [
            "first_name",
            "company",
            "booking_link"
          ]
        },
        "samplePrompt": "Write a sales email to a product-qualified trial user whose team has been active and is hitting usage limits, inviting them to a 20-minute demo to discuss higher limits, team workspaces, SSO, and security docs, with a booking link."
      }
    ],
    "workflows": [
      {
        "ref": "wf:trial-onboarding",
        "name": "Trial Onboarding Nurture",
        "description": "When a new trial contact is created, welcome them and tag them as a trial user, then wait before checking in.",
        "triggerType": "contact_created",
        "steps": [
          {
            "stepType": "add_tag",
            "config": {
              "tagId": {
                "$ref": "tag:trial"
              },
              "tagName": "Free Trial"
            },
            "sortOrder": 0
          },
          {
            "stepType": "send_sms",
            "config": {
              "templateId": {
                "$ref": "tmpl:trial-day1-sms"
              },
              "templateName": "Trial Day 1 \u2014 Get to First Value"
            },
            "sortOrder": 1
          },
          {
            "stepType": "wait",
            "config": {
              "duration": 2,
              "unit": "days"
            },
            "sortOrder": 2
          },
          {
            "stepType": "exit",
            "config": {},
            "sortOrder": 3
          }
        ],
        "activate": true
      },
      {
        "ref": "wf:pql-to-demo",
        "name": "PQL to Sales Demo",
        "description": "When an account becomes Product Qualified, email them a demo invite, tag them PQL, and move them into the Sales Demo stage.",
        "triggerType": "contact_stage_changed",
        "triggerConfig": {
          "groupId": {
            "$ref": "pipeline:accounts"
          },
          "stageId": {
            "$ref": "stage:pql"
          }
        },
        "steps": [
          {
            "stepType": "add_tag",
            "config": {
              "tagId": {
                "$ref": "tag:pql"
              },
              "tagName": "Product Qualified"
            },
            "sortOrder": 0
          },
          {
            "stepType": "send_email",
            "config": {
              "templateId": {
                "$ref": "tmpl:pql-demo-email"
              },
              "templateName": "PQL \u2014 You're Crushing It, Let's Talk Scale"
            },
            "sortOrder": 1
          },
          {
            "stepType": "wait",
            "config": {
              "duration": 1,
              "unit": "days"
            },
            "sortOrder": 2
          },
          {
            "stepType": "move_stage",
            "config": {
              "groupId": {
                "$ref": "pipeline:accounts"
              },
              "stageId": {
                "$ref": "stage:sales-demo"
              },
              "groupName": "Growth Pipeline",
              "stageName": "Sales Demo"
            },
            "sortOrder": 3
          }
        ],
        "activate": true
      }
    ],
    "coworkPrompts": [
      "Find every contact in the PQL (Product Qualified) stage that hasn't booked a demo yet and send them the 'PQL \u2014 You're Crushing It, Let's Talk Scale' email, then tag them Demo Requested.",
      "Take everyone who submitted the 'Book a Product Demo' form this week, move them to the Sales Demo stage, and send them the 'Order Form & Subscription Agreement' to review before our call.",
      "Show me trial signups stuck in the Trial Signup stage for more than 5 days with no activity, tag them Churn Risk, and send them the 'Trial Feedback / Cancellation' form to find out what's blocking them."
    ]
  },
  {
    "kitId": "medical-dental",
    "industryKey": "medical-dental",
    "name": "Medical / Dental",
    "tagline": "Turn consultations into confirmed procedures, with consent locked in.",
    "scenario": "A patient calls about a dental implant after seeing an ad, books a consultation, and worries about cost. Your front desk captures their intake and medical history online, walks them through financing, and once they pay the deposit and e-sign the procedure consent and pre-op waivers, the surgery date is locked. No more paper clipboards or chasing signatures the morning of surgery.",
    "icon": "Stethoscope",
    "accentColor": "#10b981",
    "tags": [
      {
        "ref": "tag:new-patient",
        "name": "New Patient",
        "color": "#10b981"
      },
      {
        "ref": "tag:high-value-procedure",
        "name": "High-Value Procedure",
        "color": "#8b5cf6"
      },
      {
        "ref": "tag:financing-pending",
        "name": "Financing Pending",
        "color": "#06b6d4"
      },
      {
        "ref": "tag:consent-signed",
        "name": "Consent Signed",
        "color": "#22c55e"
      }
    ],
    "pipeline": {
      "ref": "pipeline:patients",
      "name": "Patient Pipeline",
      "industry": "medical-dental",
      "stages": [
        {
          "ref": "stage:consultation",
          "name": "Consultation",
          "color": "#8b5cf6",
          "exitCriteria": [
            "Treatment plan created",
            "Medical history cleared"
          ],
          "isDefault": true
        },
        {
          "ref": "stage:financing",
          "name": "Financing Review",
          "color": "#06b6d4",
          "exitCriteria": [
            "Financing application approved",
            "Down payment discussed"
          ]
        },
        {
          "ref": "stage:procedure-booked",
          "name": "Procedure Booked",
          "color": "#f59e0b",
          "exitCriteria": [
            "Deposit paid",
            "Surgery date locked"
          ]
        },
        {
          "ref": "stage:pre-op",
          "name": "Pre-Op",
          "color": "#22c55e",
          "exitCriteria": [
            "Pre-op instructions confirmed",
            "Legal waivers signed"
          ],
          "isFinal": true
        }
      ]
    },
    "forms": [
      {
        "ref": "form:new-patient-intake",
        "name": "New Patient Intake & Medical History",
        "description": "Collect patient demographics, medical history, and consent before the first consultation.",
        "schema": {
          "layout": "two-column",
          "fields": [
            {
              "id": "sec_patient",
              "type": "section_header",
              "label": "Patient Information"
            },
            {
              "id": "first_name",
              "type": "text",
              "label": "First Name",
              "placeholder": "Jordan",
              "validation": {
                "required": true,
                "maxLength": 50
              }
            },
            {
              "id": "last_name",
              "type": "text",
              "label": "Last Name",
              "placeholder": "Rivera",
              "validation": {
                "required": true,
                "maxLength": 50
              }
            },
            {
              "id": "dob",
              "type": "date",
              "label": "Date of Birth",
              "validation": {
                "required": true
              }
            },
            {
              "id": "email",
              "type": "email",
              "label": "Email",
              "placeholder": "you@email.com",
              "validation": {
                "required": true
              }
            },
            {
              "id": "phone",
              "type": "phone",
              "label": "Mobile Phone",
              "placeholder": "(555) 123-4567",
              "validation": {
                "required": true
              }
            },
            {
              "id": "sec_visit",
              "type": "section_header",
              "label": "Reason for Visit"
            },
            {
              "id": "procedure_interest",
              "type": "dropdown",
              "label": "Procedure of Interest",
              "helpText": "What brings you in today?",
              "options": [
                {
                  "label": "Dental Implant",
                  "value": "implant"
                },
                {
                  "label": "Wisdom Tooth Extraction",
                  "value": "extraction"
                },
                {
                  "label": "Root Canal",
                  "value": "root_canal"
                },
                {
                  "label": "Crown or Bridge",
                  "value": "crown_bridge"
                },
                {
                  "label": "Cosmetic / Veneers",
                  "value": "cosmetic"
                },
                {
                  "label": "General Exam & Cleaning",
                  "value": "general"
                }
              ],
              "validation": {
                "required": true
              }
            },
            {
              "id": "pain_level",
              "type": "radio",
              "label": "Current Pain Level",
              "options": [
                {
                  "label": "None",
                  "value": "none"
                },
                {
                  "label": "Mild",
                  "value": "mild"
                },
                {
                  "label": "Moderate",
                  "value": "moderate"
                },
                {
                  "label": "Severe",
                  "value": "severe"
                }
              ],
              "validation": {
                "required": true
              }
            },
            {
              "id": "sec_history",
              "type": "section_header",
              "label": "Medical History"
            },
            {
              "id": "conditions",
              "type": "checkbox",
              "label": "Do you have any of the following?",
              "helpText": "Select all that apply.",
              "options": [
                {
                  "label": "Diabetes",
                  "value": "diabetes"
                },
                {
                  "label": "High Blood Pressure",
                  "value": "hypertension"
                },
                {
                  "label": "Heart Condition",
                  "value": "heart"
                },
                {
                  "label": "Blood Thinners",
                  "value": "blood_thinners"
                },
                {
                  "label": "Pregnancy",
                  "value": "pregnancy"
                },
                {
                  "label": "None of the above",
                  "value": "none"
                }
              ]
            },
            {
              "id": "allergies",
              "type": "textarea",
              "label": "Allergies & Current Medications",
              "placeholder": "List any drug allergies and medications you currently take.",
              "helpText": "Include dosage if known."
            },
            {
              "id": "div1",
              "type": "divider",
              "label": "Divider"
            },
            {
              "id": "consent_note",
              "type": "section_header",
              "label": "Communication Consent"
            }
          ]
        },
        "settings": {
          "submitButtonText": "Submit Intake",
          "successMessage": "Thank you! Our care team will review your history and confirm your consultation shortly.",
          "captureSmsConsent": true,
          "captureEmailConsent": true,
          "consentText": "I consent to receive appointment reminders and treatment-related messages by SMS and email from this practice. Message and data rates may apply. Reply STOP to opt out."
        },
        "tagRefs": [
          "tag:new-patient"
        ],
        "samplePrompt": "Build a new-patient intake form for an oral surgery practice that captures contact info, date of birth, procedure of interest, pain level, medical conditions, allergies/medications, and SMS+email appointment-reminder consent."
      },
      {
        "ref": "form:financing-application",
        "name": "Treatment Financing Application",
        "description": "Pre-qualify patients for a payment plan on high-value procedures.",
        "schema": {
          "layout": "single",
          "fields": [
            {
              "id": "sec_plan",
              "type": "section_header",
              "label": "Treatment & Estimate"
            },
            {
              "id": "procedure",
              "type": "text",
              "label": "Recommended Procedure",
              "placeholder": "e.g. Single dental implant",
              "validation": {
                "required": true
              }
            },
            {
              "id": "estimate",
              "type": "number",
              "label": "Treatment Estimate ($)",
              "placeholder": "3800",
              "validation": {
                "required": true,
                "min": 0
              }
            },
            {
              "id": "sec_finance",
              "type": "section_header",
              "label": "Financing Details"
            },
            {
              "id": "down_payment",
              "type": "number",
              "label": "Down Payment You Can Offer ($)",
              "placeholder": "500",
              "validation": {
                "min": 0
              }
            },
            {
              "id": "term",
              "type": "dropdown",
              "label": "Preferred Payment Term",
              "options": [
                {
                  "label": "6 months (0% interest)",
                  "value": "6mo"
                },
                {
                  "label": "12 months",
                  "value": "12mo"
                },
                {
                  "label": "24 months",
                  "value": "24mo"
                },
                {
                  "label": "36 months",
                  "value": "36mo"
                }
              ],
              "validation": {
                "required": true
              }
            },
            {
              "id": "employment",
              "type": "radio",
              "label": "Employment Status",
              "options": [
                {
                  "label": "Employed",
                  "value": "employed"
                },
                {
                  "label": "Self-Employed",
                  "value": "self_employed"
                },
                {
                  "label": "Retired",
                  "value": "retired"
                },
                {
                  "label": "Other",
                  "value": "other"
                }
              ],
              "validation": {
                "required": true
              }
            },
            {
              "id": "notes",
              "type": "textarea",
              "label": "Anything else we should know?",
              "placeholder": "Optional"
            }
          ]
        },
        "settings": {
          "submitButtonText": "Submit Application",
          "successMessage": "Your financing application is in. We'll review and reach out with your approved plan options.",
          "captureSmsConsent": true,
          "consentText": "I agree to be contacted by SMS regarding my financing application. Reply STOP to opt out."
        },
        "tagRefs": [
          "tag:financing-pending",
          "tag:high-value-procedure"
        ],
        "samplePrompt": "Create a treatment financing application form for a dental practice that captures the recommended procedure, treatment estimate, down payment, preferred payment term, and employment status."
      }
    ],
    "documents": [
      {
        "ref": "doc:procedure-consent",
        "name": "Informed Consent & Pre-Op Waiver",
        "ndfDocument": {
          "version": "1.0",
          "kind": "document",
          "meta": {
            "title": "Informed Consent for Treatment & Pre-Operative Waiver",
            "description": "Surgical/procedural informed consent and pre-operative acknowledgement signed by the patient before the procedure date.",
            "variables": [
              {
                "name": "full_name",
                "label": "Patient Full Name",
                "type": "text"
              },
              {
                "name": "date",
                "label": "Procedure Date",
                "type": "date"
              },
              {
                "name": "company",
                "label": "Practice Name",
                "type": "text"
              },
              {
                "name": "today",
                "label": "Date Signed",
                "type": "date"
              }
            ]
          },
          "signers": [
            {
              "id": "signer_1",
              "name": "Patient",
              "order": 1
            }
          ],
          "blocks": [
            {
              "type": "heading",
              "id": "h1",
              "level": 1,
              "content": "Informed Consent for Treatment & Pre-Operative Waiver"
            },
            {
              "type": "paragraph",
              "id": "p_intro",
              "content": "This document is provided by {{company}} to {{full_name}} in advance of the procedure scheduled for {{date}}. Please read each section carefully. By signing below you confirm that you understand the nature of your treatment, its risks, and the instructions required for a safe procedure."
            },
            {
              "type": "section_header",
              "id": "sh_nature",
              "content": "1. Nature of the Procedure"
            },
            {
              "type": "paragraph",
              "id": "p_nature",
              "content": "Your treating provider has explained the recommended procedure, the reason it is necessary, and the expected outcome. You have had the opportunity to ask questions and have them answered to your satisfaction."
            },
            {
              "type": "section_header",
              "id": "sh_risks",
              "content": "2. Risks & Alternatives"
            },
            {
              "type": "paragraph",
              "id": "p_risks",
              "content": "All procedures carry risk. The following risks have been disclosed to you, along with reasonable alternatives including the option of no treatment:"
            },
            {
              "type": "list",
              "id": "l_risks",
              "ordered": false,
              "items": [
                "Pain, swelling, bruising, or bleeding at the treatment site",
                "Infection requiring further treatment or antibiotics",
                "Temporary or, rarely, permanent numbness or altered sensation",
                "Reaction to anesthesia or prescribed medication",
                "The possibility that additional or follow-up treatment may be required"
              ]
            },
            {
              "type": "section_header",
              "id": "sh_preop",
              "content": "3. Pre-Operative Instructions"
            },
            {
              "type": "list",
              "id": "l_preop",
              "ordered": true,
              "items": [
                "Do not eat or drink anything after midnight before your procedure unless told otherwise.",
                "Arrange for a responsible adult to drive you home if sedation is used.",
                "Take only the medications your provider has approved on the day of the procedure.",
                "Notify the practice immediately if you develop fever, cold, or illness beforehand."
              ]
            },
            {
              "type": "divider",
              "id": "d1"
            },
            {
              "type": "section_header",
              "id": "sh_ack",
              "content": "4. Patient Acknowledgement"
            },
            {
              "type": "paragraph",
              "id": "p_ack",
              "content": "I, {{full_name}}, certify that the information I provided in my medical history is accurate. I acknowledge that no guarantee has been made regarding the result of treatment, and I voluntarily consent to the procedure described above."
            },
            {
              "type": "field_text",
              "id": "f_relationship",
              "label": "If signing on behalf of a minor, relationship to patient",
              "inputType": "text",
              "signerId": "signer_1"
            },
            {
              "type": "field_select",
              "id": "f_sedation",
              "label": "Sedation acknowledgement",
              "selectType": "checkbox",
              "options": [
                {
                  "label": "I understand the sedation/anesthesia plan and post-op transport requirement",
                  "value": "sedation_ack"
                }
              ],
              "signerId": "signer_1"
            },
            {
              "type": "field_date",
              "id": "f_date",
              "label": "Date Signed",
              "signerId": "signer_1"
            },
            {
              "type": "field_signature",
              "id": "f_sig",
              "label": "Patient Signature",
              "required": true,
              "signerId": "signer_1"
            }
          ]
        },
        "tagRefs": [
          "tag:consent-signed"
        ],
        "samplePrompt": "Draft an informed-consent and pre-operative waiver for a dental/oral surgery procedure that covers nature of the procedure, disclosed risks and alternatives, pre-op instructions, a sedation acknowledgement, and a patient signature with date."
      }
    ],
    "templates": [
      {
        "ref": "tmpl:consult-reminder-sms",
        "type": "sms",
        "name": "Consultation Reminder SMS",
        "category": "reminder",
        "content": {
          "message": "Hi {{first_name}}, this is {{company}}. Your consultation is coming up on {{date}}. Please complete your intake here if you haven't: {{booking_link}} Reply C to confirm or call us to reschedule.",
          "variables": [
            "first_name",
            "company",
            "date",
            "booking_link"
          ]
        },
        "samplePrompt": "Write a friendly SMS reminding a patient of their upcoming consultation, including their name, the practice name, the appointment date, and a link to finish their intake form."
      },
      {
        "ref": "tmpl:financing-approved-email",
        "type": "email",
        "name": "Financing Approved & Next Steps Email",
        "category": "transactional",
        "content": {
          "subject": "You're approved, {{first_name}} \u2014 let's book your procedure",
          "previewText": "Your financing is approved. Here are your next steps.",
          "body": `<p>Hi {{first_name}},</p><p>Great news \u2014 your treatment financing application with {{company}} has been approved. You're one step away from getting your procedure on the calendar.</p><p><strong>Next steps:</strong></p><ol><li>Review your approved payment plan with our care coordinator.</li><li>Pay your deposit to lock in your surgery date.</li><li>Sign your informed consent and pre-op waiver online \u2014 it only takes a minute.</li></ol><p>When you're ready, pick a date here: <a href="{{booking_link}}">{{booking_link}}</a></p><p>We're excited to take care of you.</p><p>Warmly,<br/>The team at {{company}}</p>`,
          "variables": [
            "first_name",
            "company",
            "booking_link"
          ]
        },
        "samplePrompt": "Write an email telling a patient their treatment financing is approved, outlining next steps (review plan, pay deposit, sign consent), and inviting them to book their procedure date via a link."
      }
    ],
    "workflows": [
      {
        "ref": "wf:intake-to-consult",
        "name": "New Intake -> Consultation Nurture",
        "description": "When a patient submits the intake form, tag them, place them in the Consultation stage, confirm by SMS, and send the consultation reminder.",
        "triggerType": "form_submitted",
        "triggerConfig": {
          "formId": {
            "$ref": "form:new-patient-intake"
          },
          "formName": "New Patient Intake & Medical History"
        },
        "activate": true,
        "steps": [
          {
            "stepType": "add_tag",
            "config": {
              "tagId": {
                "$ref": "tag:new-patient"
              },
              "tagName": "New Patient"
            },
            "sortOrder": 1
          },
          {
            "stepType": "move_stage",
            "config": {
              "groupId": {
                "$ref": "pipeline:patients"
              },
              "stageId": {
                "$ref": "stage:consultation"
              },
              "groupName": "Patient Pipeline",
              "stageName": "Consultation"
            },
            "sortOrder": 2
          },
          {
            "stepType": "send_sms",
            "config": {
              "templateId": {
                "$ref": "tmpl:consult-reminder-sms"
              },
              "templateName": "Consultation Reminder SMS"
            },
            "sortOrder": 3
          },
          {
            "stepType": "wait",
            "config": {
              "duration": 2,
              "unit": "days"
            },
            "sortOrder": 4
          },
          {
            "stepType": "exit",
            "config": {},
            "sortOrder": 5
          }
        ]
      },
      {
        "ref": "wf:financing-approved-followup",
        "name": "Financing Approved -> Book & Sign",
        "description": "When a patient submits the financing application, tag them, move them to Financing Review, and email approved next steps.",
        "triggerType": "form_submitted",
        "triggerConfig": {
          "formId": {
            "$ref": "form:financing-application"
          },
          "formName": "Treatment Financing Application"
        },
        "activate": true,
        "steps": [
          {
            "stepType": "add_tag",
            "config": {
              "tagId": {
                "$ref": "tag:financing-pending"
              },
              "tagName": "Financing Pending"
            },
            "sortOrder": 1
          },
          {
            "stepType": "move_stage",
            "config": {
              "groupId": {
                "$ref": "pipeline:patients"
              },
              "stageId": {
                "$ref": "stage:financing"
              },
              "groupName": "Patient Pipeline",
              "stageName": "Financing Review"
            },
            "sortOrder": 2
          },
          {
            "stepType": "send_email",
            "config": {
              "templateId": {
                "$ref": "tmpl:financing-approved-email"
              },
              "templateName": "Financing Approved & Next Steps Email"
            },
            "sortOrder": 3
          },
          {
            "stepType": "send_document",
            "config": {
              "documentId": {
                "$ref": "doc:procedure-consent"
              },
              "documentName": "Informed Consent & Pre-Op Waiver"
            },
            "sortOrder": 4
          },
          {
            "stepType": "exit",
            "config": {},
            "sortOrder": 5
          }
        ]
      }
    ],
    "coworkPrompts": [
      "Find every patient still in the Consultation stage who submitted the New Patient Intake & Medical History form more than 3 days ago and send them the Consultation Reminder SMS.",
      "Move all patients tagged Financing Pending whose deposit is paid into the Procedure Booked stage and send them the Informed Consent & Pre-Op Waiver to sign.",
      "Send the Financing Approved & Next Steps Email to everyone in the Financing Review stage who completed the Treatment Financing Application this week, and tag them High-Value Procedure."
    ]
  },
  {
    "kitId": "automotive",
    "industryKey": "automotive",
    "name": "Automotive",
    "tagline": "From test drive to delivery, never lose a buyer in the lot.",
    "scenario": "A shopper walks in on a Saturday, takes a sedan for a spin, and leaves a phone number. By Monday your team has texted them the trade-in appraisal, sent the credit application by link, and booked the delivery appointment \u2014 all without a single sticky note. Every license, signature, and insurance proof lands in one deal record before the keys change hands.",
    "icon": "Car",
    "accentColor": "#ef4444",
    "tags": [
      {
        "ref": "tag:hot-lead",
        "name": "Hot Lead",
        "color": "#ef4444"
      },
      {
        "ref": "tag:trade-in",
        "name": "Trade-In",
        "color": "#06b6d4"
      },
      {
        "ref": "tag:financing",
        "name": "Financing",
        "color": "#f59e0b"
      },
      {
        "ref": "tag:delivered",
        "name": "Delivered",
        "color": "#16a34a"
      }
    ],
    "pipeline": {
      "ref": "pipeline:deals",
      "name": "Sales Pipeline",
      "industry": "automotive",
      "stages": [
        {
          "ref": "stage:test-drive",
          "name": "Test Drive",
          "color": "#8b5cf6",
          "exitCriteria": [
            "Driver's license on file",
            "Feedback survey done"
          ],
          "isDefault": true
        },
        {
          "ref": "stage:appraisal",
          "name": "Appraisal",
          "color": "#06b6d4",
          "exitCriteria": [
            "Trade-in offer delivered"
          ]
        },
        {
          "ref": "stage:finance",
          "name": "F&I / Finance",
          "color": "#f59e0b",
          "exitCriteria": [
            "Credit verified",
            "Interest rate locked"
          ]
        },
        {
          "ref": "stage:order",
          "name": "Order / Allocation",
          "color": "#22c55e",
          "exitCriteria": [
            "VIN assigned",
            "Build sheet signed"
          ]
        },
        {
          "ref": "stage:delivery",
          "name": "Delivery",
          "color": "#16a34a",
          "exitCriteria": [
            "Insurance proof on file",
            "Title signed"
          ],
          "isFinal": true
        }
      ]
    },
    "forms": [
      {
        "ref": "form:test-drive-request",
        "name": "Test Drive Request",
        "description": "Capture lead details, license info, and consent before handing over the keys for a test drive.",
        "schema": {
          "layout": "two-column",
          "fields": [
            {
              "id": "sec_contact",
              "type": "section_header",
              "label": "Your Information"
            },
            {
              "id": "first_name",
              "type": "text",
              "label": "First Name",
              "placeholder": "Jordan",
              "validation": {
                "required": true,
                "maxLength": 50
              }
            },
            {
              "id": "last_name",
              "type": "text",
              "label": "Last Name",
              "placeholder": "Rivera",
              "validation": {
                "required": true,
                "maxLength": 50
              }
            },
            {
              "id": "email",
              "type": "email",
              "label": "Email",
              "placeholder": "you@example.com",
              "validation": {
                "required": true
              }
            },
            {
              "id": "phone",
              "type": "phone",
              "label": "Mobile Phone",
              "placeholder": "(555) 123-4567",
              "validation": {
                "required": true
              }
            },
            {
              "id": "div_1",
              "type": "divider",
              "label": "Divider"
            },
            {
              "id": "sec_vehicle",
              "type": "section_header",
              "label": "Vehicle of Interest"
            },
            {
              "id": "vehicle_interest",
              "type": "dropdown",
              "label": "Which vehicle would you like to drive?",
              "helpText": "Select the model you'd like to test drive today.",
              "options": [
                {
                  "label": "Sedan",
                  "value": "sedan"
                },
                {
                  "label": "SUV / Crossover",
                  "value": "suv"
                },
                {
                  "label": "Pickup Truck",
                  "value": "truck"
                },
                {
                  "label": "EV / Hybrid",
                  "value": "ev"
                },
                {
                  "label": "Sports / Performance",
                  "value": "performance"
                }
              ],
              "validation": {
                "required": true
              }
            },
            {
              "id": "preferred_date",
              "type": "date",
              "label": "Preferred Date",
              "validation": {
                "required": true
              }
            },
            {
              "id": "preferred_time",
              "type": "time",
              "label": "Preferred Time"
            },
            {
              "id": "div_2",
              "type": "divider",
              "label": "Divider"
            },
            {
              "id": "sec_license",
              "type": "section_header",
              "label": "Driver Verification"
            },
            {
              "id": "license_number",
              "type": "text",
              "label": "Driver's License Number",
              "placeholder": "DL #",
              "validation": {
                "required": true,
                "maxLength": 30
              }
            },
            {
              "id": "license_state",
              "type": "text",
              "label": "Issuing State",
              "placeholder": "TX",
              "validation": {
                "required": true,
                "maxLength": 2
              }
            },
            {
              "id": "license_upload",
              "type": "file",
              "label": "Upload Driver's License",
              "helpText": "Front of a valid, unexpired license."
            },
            {
              "id": "has_trade",
              "type": "radio",
              "label": "Do you have a vehicle to trade in?",
              "options": [
                {
                  "label": "Yes",
                  "value": "yes"
                },
                {
                  "label": "No",
                  "value": "no"
                }
              ],
              "validation": {
                "required": true
              }
            }
          ]
        },
        "settings": {
          "submitButtonText": "Reserve My Test Drive",
          "successMessage": "You're booked! We'll text you a confirmation and have the vehicle ready when you arrive.",
          "captureSmsConsent": true,
          "captureEmailConsent": true,
          "consentText": "I agree to receive appointment reminders and follow-up messages from this dealership by text and email. Message and data rates may apply. Reply STOP to opt out."
        },
        "tagRefs": [
          "tag:hot-lead"
        ],
        "samplePrompt": "Create a test drive request form for a car dealership that collects contact info, the model the customer wants to drive, a preferred date and time, driver's license number plus a license photo upload, and asks whether they have a trade-in. Add SMS and email consent."
      },
      {
        "ref": "form:trade-in-appraisal",
        "name": "Trade-In Appraisal",
        "description": "Gather year, make, model, mileage, and condition so the appraisal team can deliver a trade-in offer.",
        "schema": {
          "layout": "two-column",
          "fields": [
            {
              "id": "sec_owner",
              "type": "section_header",
              "label": "Owner Details"
            },
            {
              "id": "first_name",
              "type": "text",
              "label": "First Name",
              "validation": {
                "required": true
              }
            },
            {
              "id": "last_name",
              "type": "text",
              "label": "Last Name",
              "validation": {
                "required": true
              }
            },
            {
              "id": "phone",
              "type": "phone",
              "label": "Mobile Phone",
              "validation": {
                "required": true
              }
            },
            {
              "id": "email",
              "type": "email",
              "label": "Email"
            },
            {
              "id": "div_1",
              "type": "divider",
              "label": "Divider"
            },
            {
              "id": "sec_vehicle",
              "type": "section_header",
              "label": "Your Current Vehicle"
            },
            {
              "id": "trade_year",
              "type": "number",
              "label": "Year",
              "placeholder": "2019",
              "validation": {
                "required": true,
                "min": 1980,
                "max": 2027
              }
            },
            {
              "id": "trade_make",
              "type": "text",
              "label": "Make",
              "placeholder": "Toyota",
              "validation": {
                "required": true
              }
            },
            {
              "id": "trade_model",
              "type": "text",
              "label": "Model",
              "placeholder": "Camry",
              "validation": {
                "required": true
              }
            },
            {
              "id": "trade_mileage",
              "type": "number",
              "label": "Mileage",
              "placeholder": "48000",
              "validation": {
                "required": true,
                "min": 0
              }
            },
            {
              "id": "trade_vin",
              "type": "text",
              "label": "VIN (optional)",
              "helpText": "17-character vehicle identification number.",
              "validation": {
                "maxLength": 17
              }
            },
            {
              "id": "trade_condition",
              "type": "dropdown",
              "label": "Overall Condition",
              "options": [
                {
                  "label": "Excellent",
                  "value": "excellent"
                },
                {
                  "label": "Good",
                  "value": "good"
                },
                {
                  "label": "Fair",
                  "value": "fair"
                },
                {
                  "label": "Poor",
                  "value": "poor"
                }
              ],
              "validation": {
                "required": true
              }
            },
            {
              "id": "loan_balance",
              "type": "radio",
              "label": "Is there a loan balance on the vehicle?",
              "options": [
                {
                  "label": "Paid off",
                  "value": "paid_off"
                },
                {
                  "label": "Still financing",
                  "value": "financing"
                }
              ],
              "validation": {
                "required": true
              }
            },
            {
              "id": "trade_notes",
              "type": "textarea",
              "label": "Anything we should know?",
              "placeholder": "Recent service, accidents, modifications, cosmetic issues..."
            }
          ]
        },
        "settings": {
          "submitButtonText": "Get My Trade-In Offer",
          "successMessage": "Thanks! Our appraisal team will text you a trade-in offer shortly.",
          "captureSmsConsent": true,
          "consentText": "I agree to receive my trade-in offer and related follow-up by text. Message and data rates may apply. Reply STOP to opt out."
        },
        "tagRefs": [
          "tag:trade-in"
        ],
        "samplePrompt": "Build a trade-in appraisal form that captures the owner's contact info and their current vehicle's year, make, model, mileage, VIN, condition, and whether there's still a loan balance, with a notes box for accidents or service history."
      }
    ],
    "documents": [
      {
        "ref": "doc:vehicle-purchase-agreement",
        "name": "Vehicle Purchase Agreement",
        "ndfDocument": {
          "version": "1.0",
          "kind": "document",
          "meta": {
            "title": "Vehicle Purchase Agreement",
            "description": "Buyer's order and purchase agreement covering the vehicle, pricing, trade-in, and delivery terms.",
            "variables": [
              {
                "name": "full_name",
                "label": "Buyer Full Name",
                "type": "text"
              },
              {
                "name": "address",
                "label": "Buyer Address",
                "type": "text"
              },
              {
                "name": "company",
                "label": "Dealership Name",
                "type": "text"
              },
              {
                "name": "date",
                "label": "Agreement Date",
                "type": "date"
              }
            ]
          },
          "signers": [
            {
              "id": "signer_1",
              "name": "Buyer",
              "order": 1
            }
          ],
          "blocks": [
            {
              "type": "heading",
              "id": "h1",
              "level": 1,
              "content": "Vehicle Purchase Agreement"
            },
            {
              "type": "paragraph",
              "id": "p_intro",
              "content": 'This Vehicle Purchase Agreement (the "Agreement") is entered into on {{date}} between {{company}} (the "Dealer") and {{full_name}} (the "Buyer"), residing at {{address}}. By signing below, the Buyer agrees to purchase the vehicle described in this Agreement subject to the terms and conditions set forth herein.'
            },
            {
              "type": "section_header",
              "id": "sh_vehicle",
              "content": "1. Vehicle Information"
            },
            {
              "type": "field_text",
              "id": "f_vin",
              "label": "Vehicle Identification Number (VIN)",
              "inputType": "text",
              "signerId": "signer_1"
            },
            {
              "type": "field_text",
              "id": "f_year_make_model",
              "label": "Year / Make / Model",
              "inputType": "text",
              "signerId": "signer_1"
            },
            {
              "type": "field_text",
              "id": "f_mileage",
              "label": "Odometer Reading (miles)",
              "inputType": "number",
              "signerId": "signer_1"
            },
            {
              "type": "section_header",
              "id": "sh_price",
              "content": "2. Purchase Price & Trade-In"
            },
            {
              "type": "field_text",
              "id": "f_sale_price",
              "label": "Vehicle Sale Price ($)",
              "inputType": "number",
              "signerId": "signer_1"
            },
            {
              "type": "field_text",
              "id": "f_trade_allowance",
              "label": "Trade-In Allowance ($)",
              "inputType": "number",
              "signerId": "signer_1"
            },
            {
              "type": "field_text",
              "id": "f_balance_due",
              "label": "Balance Due ($)",
              "inputType": "number",
              "signerId": "signer_1"
            },
            {
              "type": "paragraph",
              "id": "p_taxes",
              "content": "All applicable sales tax, title, registration, and documentation fees will be added to the balance due and itemized on the final buyer's order. The figures above are estimates and may be adjusted to reflect final financing terms and government charges."
            },
            {
              "type": "section_header",
              "id": "sh_terms",
              "content": "3. Terms & Conditions"
            },
            {
              "type": "list",
              "id": "l_terms",
              "ordered": true,
              "items": [
                "This Agreement is binding upon acceptance by an authorized representative of the Dealer.",
                "The Buyer's trade-in allowance is contingent on the trade vehicle being delivered in substantially the same condition as appraised, with clear title.",
                "Delivery is subject to the Buyer providing valid proof of insurance and a signed title transfer.",
                "Any deposit paid is applied to the purchase price and is refundable only as permitted by applicable state law.",
                "The vehicle is sold subject to the manufacturer's warranty, if any; the Dealer makes no additional warranty unless stated in writing."
              ]
            },
            {
              "type": "divider",
              "id": "d1"
            },
            {
              "type": "section_header",
              "id": "sh_disclosure",
              "content": "4. Disclosures & Acknowledgement"
            },
            {
              "type": "paragraph",
              "id": "p_ack",
              "content": "The Buyer acknowledges having reviewed the vehicle, the odometer disclosure, and the pricing above, and agrees to the terms of this Agreement. The Buyer confirms the information provided is accurate to the best of their knowledge."
            },
            {
              "type": "field_select",
              "id": "f_finance_method",
              "label": "Payment Method",
              "selectType": "radio",
              "options": [
                {
                  "label": "Cash / Outside Financing",
                  "value": "cash"
                },
                {
                  "label": "Dealer-Arranged Financing",
                  "value": "dealer_finance"
                }
              ],
              "signerId": "signer_1"
            },
            {
              "type": "field_date",
              "id": "f_sign_date",
              "label": "Date Signed",
              "signerId": "signer_1"
            },
            {
              "type": "field_signature",
              "id": "f_sig_buyer",
              "label": "Buyer Signature",
              "required": true,
              "signerId": "signer_1"
            }
          ]
        },
        "tagRefs": [
          "tag:financing"
        ],
        "samplePrompt": "Draft a vehicle purchase agreement for a car dealership that includes the VIN, year/make/model, odometer reading, sale price, trade-in allowance, balance due, standard terms and conditions, a payment-method choice, and a buyer signature with date."
      }
    ],
    "templates": [
      {
        "ref": "tmpl:test-drive-confirmation-sms",
        "type": "sms",
        "name": "Test Drive Confirmation",
        "category": "appointment",
        "content": {
          "message": "Hi {{first_name}}, your test drive at {{company}} is confirmed for {{date}}. Bring a valid driver's license and we'll have the keys ready. Questions? Just reply here. Reply STOP to opt out.",
          "variables": [
            "first_name",
            "company",
            "date"
          ]
        },
        "samplePrompt": "Write a short SMS confirming a customer's test drive appointment that reminds them to bring a driver's license, invites them to reply with questions, and includes an opt-out line."
      },
      {
        "ref": "tmpl:trade-in-offer-email",
        "type": "email",
        "name": "Trade-In Offer Ready",
        "category": "sales",
        "content": {
          "subject": "Your trade-in offer from {{company}} is ready, {{first_name}}",
          "previewText": "We've appraised your vehicle \u2014 here's what it's worth toward your next car.",
          "body": `<p>Hi {{first_name}},</p><p>Thanks for letting us appraise your vehicle. Our team has finished the evaluation, and we have a trade-in offer ready to put toward your next car at {{company}}.</p><p>Your offer is valid through <strong>{{expiration_date}}</strong>. To review the numbers, apply your trade toward a vehicle, and lock in your rate, just book a time with us below:</p><p><a href="{{booking_link}}">Book your appointment</a></p><p>Have a question before you come in? Reply to this email or text us \u2014 we're happy to walk through every line of the offer.</p><p>See you soon,<br/>The team at {{company}}</p>`,
          "variables": [
            "first_name",
            "company",
            "expiration_date",
            "booking_link"
          ]
        },
        "samplePrompt": "Write a friendly email letting a customer know their trade-in appraisal offer is ready, noting it's valid through an expiration date, with a booking link to come in and apply it toward a new vehicle."
      }
    ],
    "workflows": [
      {
        "ref": "wf:test-drive-followup",
        "name": "Test Drive Follow-Up",
        "description": "When a test drive request form is submitted, tag the lead hot, confirm by SMS, wait a day, then send the purchase agreement to keep the deal moving.",
        "triggerType": "form_submitted",
        "triggerConfig": {
          "formId": {
            "$ref": "form:test-drive-request"
          },
          "formName": "Test Drive Request"
        },
        "steps": [
          {
            "stepType": "add_tag",
            "config": {
              "tagId": {
                "$ref": "tag:hot-lead"
              },
              "tagName": "Hot Lead"
            },
            "sortOrder": 1
          },
          {
            "stepType": "send_sms",
            "config": {
              "templateId": {
                "$ref": "tmpl:test-drive-confirmation-sms"
              },
              "templateName": "Test Drive Confirmation"
            },
            "sortOrder": 2
          },
          {
            "stepType": "wait",
            "config": {
              "duration": 1,
              "unit": "days"
            },
            "sortOrder": 3
          },
          {
            "stepType": "send_document",
            "config": {
              "documentId": {
                "$ref": "doc:vehicle-purchase-agreement"
              },
              "documentName": "Vehicle Purchase Agreement"
            },
            "sortOrder": 4
          }
        ],
        "activate": true
      },
      {
        "ref": "wf:purchase-signed-to-delivery",
        "name": "Signed Agreement to Delivery",
        "description": "When the purchase agreement is signed, tag the buyer financing, move the deal to the Delivery stage, and email the trade-in offer details.",
        "triggerType": "document_signed",
        "steps": [
          {
            "stepType": "add_tag",
            "config": {
              "tagId": {
                "$ref": "tag:financing"
              },
              "tagName": "Financing"
            },
            "sortOrder": 1
          },
          {
            "stepType": "move_stage",
            "config": {
              "groupId": {
                "$ref": "pipeline:deals"
              },
              "stageId": {
                "$ref": "stage:delivery"
              },
              "groupName": "Sales Pipeline",
              "stageName": "Delivery"
            },
            "sortOrder": 2
          },
          {
            "stepType": "send_email",
            "config": {
              "templateId": {
                "$ref": "tmpl:trade-in-offer-email"
              },
              "templateName": "Trade-In Offer Ready"
            },
            "sortOrder": 3
          }
        ],
        "activate": true
      }
    ],
    "coworkPrompts": [
      "Find every contact still in the Test Drive stage who submitted the Test Drive Request form more than 2 days ago and hasn't moved to Appraisal, then send each of them the Trade-In Offer Ready email.",
      "Move all deals in the F&I / Finance stage that have a signed Vehicle Purchase Agreement into the Delivery stage and tag them Financing.",
      "Send the Test Drive Confirmation text to everyone tagged Hot Lead with an appointment tomorrow, and remind them to bring their driver's license."
    ]
  },
  {
    "kitId": "event-planning",
    "industryKey": "event-planning",
    "name": "Event Planning",
    "tagline": "Turn date inquiries into signed events \u2014 check availability, send proposals, and lock the deposit on autopilot.",
    "scenario": "A couple fills out your inquiry form asking if their date is open. Respectly tags them, places a soft hold on the calendar, drops them into your Event Pipeline, and texts back to schedule a tasting \u2014 then has the Event Services Contract ready to e-sign the moment the proposal is approved.",
    "icon": "PartyPopper",
    "accentColor": "#d946ef",
    "tags": [
      {
        "ref": "tag:new-inquiry",
        "name": "New Inquiry",
        "color": "#d946ef"
      },
      {
        "ref": "tag:date-hold",
        "name": "Date Hold",
        "color": "#8b5cf6"
      },
      {
        "ref": "tag:vip",
        "name": "VIP",
        "color": "#f59e0b"
      },
      {
        "ref": "tag:booked",
        "name": "Booked",
        "color": "#22c55e"
      }
    ],
    "pipeline": {
      "ref": "pipeline:events",
      "name": "Event Pipeline",
      "industry": "event-planning",
      "stages": [
        {
          "ref": "stage:availability",
          "name": "Availability Check",
          "color": "#8b5cf6",
          "exitCriteria": [
            "Date confirmed",
            "Soft-hold placed"
          ],
          "isDefault": true
        },
        {
          "ref": "stage:proposal",
          "name": "Proposal Sent",
          "color": "#06b6d4",
          "exitCriteria": [
            "Proposal sent",
            "Estimated headcount provided"
          ]
        },
        {
          "ref": "stage:tasting",
          "name": "Tasting / Tour",
          "color": "#f59e0b",
          "exitCriteria": [
            "Menu finalized",
            "Floor plan approved"
          ]
        },
        {
          "ref": "stage:contract",
          "name": "Contract Signed",
          "color": "#22c55e",
          "exitCriteria": [
            "Contract signed",
            "Deposit cleared"
          ],
          "isFinal": true
        }
      ]
    },
    "forms": [
      {
        "ref": "form:inquiry",
        "name": "Event Inquiry & Availability",
        "description": "Capture event details and check date availability for prospective clients.",
        "samplePrompt": "Create an event inquiry form that captures full name, email, phone, event type, requested event date, estimated guest count, preferred venue space, budget range, and how they heard about us \u2014 and capture SMS and email consent.",
        "schema": {
          "layout": "single",
          "fields": [
            {
              "id": "full_name",
              "type": "text",
              "label": "Full Name",
              "placeholder": "Jordan & Taylor",
              "validation": {
                "required": true
              }
            },
            {
              "id": "email",
              "type": "email",
              "label": "Email",
              "placeholder": "you@email.com",
              "validation": {
                "required": true
              }
            },
            {
              "id": "phone",
              "type": "phone",
              "label": "Mobile Phone",
              "placeholder": "(555) 123-4567",
              "validation": {
                "required": true
              }
            },
            {
              "id": "event_type",
              "type": "dropdown",
              "label": "Event Type",
              "validation": {
                "required": true
              },
              "options": [
                {
                  "label": "Wedding",
                  "value": "wedding"
                },
                {
                  "label": "Corporate Event",
                  "value": "corporate"
                },
                {
                  "label": "Birthday / Milestone",
                  "value": "birthday"
                },
                {
                  "label": "Gala / Fundraiser",
                  "value": "gala"
                },
                {
                  "label": "Other Celebration",
                  "value": "other"
                }
              ]
            },
            {
              "id": "event_date",
              "type": "date",
              "label": "Requested Event Date",
              "validation": {
                "required": true
              }
            },
            {
              "id": "guest_count",
              "type": "number",
              "label": "Estimated Guest Count",
              "placeholder": "120",
              "validation": {
                "required": true,
                "min": 1
              }
            },
            {
              "id": "venue_space",
              "type": "radio",
              "label": "Preferred Venue Space",
              "options": [
                {
                  "label": "Grand Ballroom",
                  "value": "ballroom"
                },
                {
                  "label": "Garden Terrace",
                  "value": "terrace"
                },
                {
                  "label": "Rooftop Lounge",
                  "value": "rooftop"
                },
                {
                  "label": "No preference yet",
                  "value": "undecided"
                }
              ]
            },
            {
              "id": "budget",
              "type": "dropdown",
              "label": "Estimated Budget",
              "options": [
                {
                  "label": "Under $10k",
                  "value": "under_10k"
                },
                {
                  "label": "$10k \u2013 $25k",
                  "value": "10_25k"
                },
                {
                  "label": "$25k \u2013 $50k",
                  "value": "25_50k"
                },
                {
                  "label": "$50k \u2013 $100k",
                  "value": "50_100k"
                },
                {
                  "label": "$100k+",
                  "value": "100k_plus"
                }
              ]
            },
            {
              "id": "referral",
              "type": "dropdown",
              "label": "How did you hear about us?",
              "options": [
                {
                  "label": "Instagram",
                  "value": "instagram"
                },
                {
                  "label": "Referral",
                  "value": "referral"
                },
                {
                  "label": "Google Search",
                  "value": "google"
                },
                {
                  "label": "Wedding Show / Expo",
                  "value": "expo"
                },
                {
                  "label": "Other",
                  "value": "other"
                }
              ]
            },
            {
              "id": "details",
              "type": "textarea",
              "label": "Tell us about your vision",
              "placeholder": "Theme, must-haves, special requests..."
            }
          ]
        },
        "settings": {
          "submitButtonText": "Check My Date",
          "successMessage": "Thanks! We're checking availability for your date and will text you shortly to schedule a tasting.",
          "captureSmsConsent": true,
          "captureEmailConsent": true,
          "consentText": "I agree to receive texts and emails about my event inquiry, availability, and proposals. Msg & data rates may apply."
        },
        "tagRefs": [
          "tag:new-inquiry"
        ]
      }
    ],
    "documents": [
      {
        "ref": "doc:event-contract",
        "name": "Event Services Contract",
        "samplePrompt": "Draft an event services contract between {{company}} and the client for an event on {{date}}, with sections for services and headcount, a non-refundable deposit, a cancellation policy, a force majeure clause, and a client signature and date field.",
        "tagRefs": [
          "tag:booked"
        ],
        "ndfDocument": {
          "version": "1.0",
          "kind": "document",
          "meta": {
            "title": "Event Services Contract",
            "description": "Booking agreement covering services, deposit, and cancellation terms.",
            "variables": [
              {
                "name": "full_name",
                "label": "Client Name",
                "type": "text"
              },
              {
                "name": "company",
                "label": "Company",
                "type": "text"
              },
              {
                "name": "date",
                "label": "Event Date",
                "type": "date"
              },
              {
                "name": "today",
                "label": "Date Signed",
                "type": "date"
              }
            ]
          },
          "signers": [
            {
              "id": "signer_1",
              "name": "Client",
              "order": 1
            }
          ],
          "blocks": [
            {
              "type": "heading",
              "id": "h1",
              "level": 1,
              "content": "Event Services Contract"
            },
            {
              "type": "paragraph",
              "id": "p1",
              "content": 'This Contract is entered into on {{today}} between {{company}} ("Planner") and {{full_name}} ("Client") for event services to be performed on {{date}}.'
            },
            {
              "type": "section_header",
              "id": "sh1",
              "content": "1. Services & Headcount"
            },
            {
              "type": "paragraph",
              "id": "p2",
              "content": "Planner will provide event planning, coordination, and the agreed venue, catering, and rental services for the event described in the attached proposal. The Client will confirm a final guest count no later than fourteen (14) days before the event date."
            },
            {
              "type": "section_header",
              "id": "sh2",
              "content": "2. Deposit & Payment"
            },
            {
              "type": "paragraph",
              "id": "p3",
              "content": "A non-refundable deposit of 25% of the total is due upon signing to reserve the date. The remaining balance is due fourteen (14) days before the event. The date is not held until the deposit clears."
            },
            {
              "type": "section_header",
              "id": "sh3",
              "content": "3. Cancellation Policy"
            },
            {
              "type": "list",
              "id": "l1",
              "ordered": true,
              "items": [
                "Cancellations more than 90 days before the event forfeit the deposit only.",
                "Cancellations within 90 days are responsible for 50% of the total.",
                "Cancellations within 30 days are responsible for 100% of the total."
              ]
            },
            {
              "type": "section_header",
              "id": "sh4",
              "content": "4. Force Majeure"
            },
            {
              "type": "paragraph",
              "id": "p4",
              "content": "Neither party shall be liable for failure to perform due to causes beyond reasonable control, including acts of God, government restrictions, or public emergencies. In such cases the parties will work in good faith to reschedule."
            },
            {
              "type": "divider",
              "id": "d1"
            },
            {
              "type": "paragraph",
              "id": "p5",
              "content": "By signing below, the Client acknowledges they have read and agree to the terms of this Contract."
            },
            {
              "type": "field_signature",
              "id": "sig1",
              "label": "Client Signature",
              "signerId": "signer_1",
              "required": true
            },
            {
              "type": "field_date",
              "id": "fd1",
              "label": "Date Signed",
              "signerId": "signer_1"
            }
          ]
        }
      }
    ],
    "templates": [
      {
        "ref": "tmpl:tasting-sms",
        "type": "sms",
        "name": "Tasting Invitation",
        "category": "Reminders",
        "samplePrompt": "Write a friendly SMS confirming the client's date is available, inviting them to book a tasting and venue tour, with a link to schedule.",
        "content": {
          "message": "Hi {{first_name}}, great news \u2014 {{date}} is available for your event! Let's get you in for a tasting & tour. Book a time here: {{booking_link}} \u2014 {{company}}",
          "variables": [
            "first_name",
            "date",
            "booking_link",
            "company"
          ]
        }
      },
      {
        "ref": "tmpl:proposal-email",
        "type": "email",
        "name": "Proposal Sent",
        "category": "Proposals",
        "samplePrompt": "Write an email letting the client know their custom event proposal is ready to review, with a button to view it and a note that their date is on soft hold for 7 days.",
        "content": {
          "subject": "Your event proposal is ready to review",
          "previewText": "We've put together a custom proposal for {{date}} \u2014 take a look inside.",
          "body": `<p>Hi {{first_name}},</p><p>It was a pleasure learning about your vision! We've put together a custom proposal for your event on <strong>{{date}}</strong>.</p><p>Your date is on a complimentary <strong>7-day soft hold</strong> while you review. <a href="{{booking_link}}">View your proposal here</a> and reply with any questions.</p><p>We can't wait to bring it to life,<br/>{{company}}</p>`,
          "variables": [
            "first_name",
            "date",
            "booking_link",
            "company"
          ]
        }
      }
    ],
    "workflows": [
      {
        "ref": "wf:new-inquiry-intake",
        "name": "New Inquiry Intake",
        "description": "When a client submits the inquiry form, tag them, place a soft hold in the pipeline, and text a tasting invitation.",
        "triggerType": "form_submitted",
        "triggerConfig": {
          "formId": {
            "$ref": "form:inquiry"
          },
          "formName": "Event Inquiry & Availability"
        },
        "steps": [
          {
            "stepType": "add_tag",
            "sortOrder": 0,
            "config": {
              "tagId": {
                "$ref": "tag:new-inquiry"
              },
              "tagName": "New Inquiry"
            }
          },
          {
            "stepType": "add_to_group",
            "sortOrder": 1,
            "config": {
              "groupId": {
                "$ref": "pipeline:events"
              },
              "stageId": {
                "$ref": "stage:availability"
              },
              "groupName": "Event Pipeline",
              "stageName": "Availability Check"
            }
          },
          {
            "stepType": "wait",
            "sortOrder": 2,
            "config": {
              "duration": 2,
              "unit": "hours"
            }
          },
          {
            "stepType": "send_sms",
            "sortOrder": 3,
            "config": {
              "templateId": {
                "$ref": "tmpl:tasting-sms"
              },
              "templateName": "Tasting Invitation"
            }
          }
        ]
      },
      {
        "ref": "wf:proposal-follow-up",
        "name": "Proposal Follow-Up",
        "description": "When a client is moved to the Proposal Sent stage, email them their custom proposal.",
        "triggerType": "contact_stage_changed",
        "triggerConfig": {
          "groupId": {
            "$ref": "pipeline:events"
          },
          "stageId": {
            "$ref": "stage:proposal"
          }
        },
        "steps": [
          {
            "stepType": "send_email",
            "sortOrder": 0,
            "config": {
              "templateId": {
                "$ref": "tmpl:proposal-email"
              },
              "templateName": "Proposal Sent"
            }
          }
        ]
      }
    ],
    "coworkPrompts": [
      "Tag everyone who submitted the Event Inquiry & Availability form this week as VIP.",
      "Send the Event Services Contract to everyone in the Tasting / Tour stage for signature.",
      "Move all contacts in Proposal Sent who replied this week into the Tasting / Tour stage and text them the Tasting Invitation."
    ]
  }
];

// src/use-case-kits.insurance.ts
function prepareInsuranceKit(kit) {
  return {
    ...kit,
    tagline: "Keep quote paperwork and client follow-up moving.",
    scenario: "A client submits your quote request form. Review the enquiry in your insurance pipeline, collect the underwriting information you need, and send an approved no-loss letter for signature. Configure form destinations, message templates and sending before deliberately activating the draft workflows. Carrier submissions, quoting and binding remain part of your agency process.",
    workflows: kit.workflows.map((workflow) => ({ ...workflow, activate: false }))
  };
}

// src/use-case-kits.ts
var INDUSTRY_TEMPLATE_KEYS = [
  "home-services",
  "real-estate",
  "insurance-brokers",
  "professional-services",
  "retail-b2b",
  "spas-gyms",
  "saas-ai-tech",
  "medical-dental",
  "automotive",
  "event-planning"
];
function summarizeKit(kit) {
  return {
    kitId: kit.kitId,
    industryKey: kit.industryKey,
    name: kit.name,
    tagline: kit.tagline,
    icon: kit.icon,
    accentColor: kit.accentColor,
    entityCounts: {
      tags: kit.tags.length,
      pipelines: 1,
      forms: kit.forms.length,
      documents: kit.documents.length,
      templates: kit.templates.length,
      workflows: kit.workflows.length
    }
  };
}
var realEstateKit = {
  kitId: "real-estate",
  industryKey: "real-estate",
  name: "Real Estate",
  tagline: "Turn open-house traffic into closings \u2014 capture, qualify, and nurture buyers automatically.",
  scenario: "A buyer scans the QR code at your open house and fills out a quick interest form. Respectly tags them, drops them into your buyer pipeline, texts a showing reminder, and has the Buyer Representation Agreement ready to e-sign \u2014 before you\u2019ve left the driveway.",
  icon: "Home",
  accentColor: "#2563eb",
  tags: [
    { ref: "tag:buyer-lead", name: "Buyer Lead", color: "#3b82f6" },
    { ref: "tag:pre-approved", name: "Pre-Approved", color: "#22c55e" },
    { ref: "tag:hot", name: "Hot", color: "#ef4444" },
    { ref: "tag:past-client", name: "Past Client", color: "#a855f7" }
  ],
  pipeline: {
    ref: "pipeline:buyers",
    name: "Buyer Pipeline",
    industry: "real-estate",
    stages: [
      { ref: "stage:buyer-lead", name: "Buyer Lead", color: "#8b5cf6", exitCriteria: ["Phone number verified", "Budget range identified"], isDefault: true },
      { ref: "stage:pre-approval", name: "Pre-Approval", color: "#06b6d4", exitCriteria: ["Mortgage pre-approval letter uploaded"] },
      { ref: "stage:active-search", name: "Active Search", color: "#f59e0b", exitCriteria: ["Top 3 properties identified", "Weekly search alerts set"] },
      { ref: "stage:offer-submitted", name: "Offer Submitted", color: "#22c55e", exitCriteria: ["Signed offer on file", "Deposit instructions sent"] },
      { ref: "stage:closed", name: "Closed", color: "#16a34a", exitCriteria: ["Closing disclosure signed", "Commission recorded"], isFinal: true }
    ]
  },
  forms: [
    {
      ref: "form:intake",
      name: "Property Interest Intake",
      description: "Capture buyer preferences at open houses and from listing pages.",
      samplePrompt: "Create a property interest intake form that captures full name, email, phone, budget range, preferred neighborhoods, number of beds and baths, timeline to buy, and whether they are already pre-approved \u2014 and capture SMS consent.",
      schema: {
        layout: "single",
        fields: [
          { id: "full_name", type: "text", label: "Full Name", placeholder: "Jane Buyer", validation: { required: true } },
          { id: "email", type: "email", label: "Email", placeholder: "jane@email.com", validation: { required: true } },
          { id: "phone", type: "phone", label: "Mobile Phone", placeholder: "(555) 123-4567", validation: { required: true } },
          {
            id: "budget",
            type: "dropdown",
            label: "Budget Range",
            validation: { required: true },
            options: [
              { label: "Under $300k", value: "under_300k" },
              { label: "$300k \u2013 $500k", value: "300_500k" },
              { label: "$500k \u2013 $750k", value: "500_750k" },
              { label: "$750k \u2013 $1M", value: "750k_1m" },
              { label: "$1M+", value: "1m_plus" }
            ]
          },
          {
            id: "areas",
            type: "checkbox",
            label: "Preferred Neighborhoods",
            options: [
              { label: "Downtown", value: "downtown" },
              { label: "Suburbs", value: "suburbs" },
              { label: "Waterfront", value: "waterfront" },
              { label: "Rural", value: "rural" }
            ]
          },
          { id: "beds", type: "number", label: "Bedrooms (min)", placeholder: "3" },
          { id: "baths", type: "number", label: "Bathrooms (min)", placeholder: "2" },
          {
            id: "timeline",
            type: "dropdown",
            label: "Timeline to Buy",
            options: [
              { label: "ASAP (0\u20133 months)", value: "asap" },
              { label: "3\u20136 months", value: "3_6" },
              { label: "6\u201312 months", value: "6_12" },
              { label: "Just browsing", value: "browsing" }
            ]
          },
          {
            id: "pre_approved",
            type: "radio",
            label: "Are you pre-approved for a mortgage?",
            options: [
              { label: "Yes", value: "yes" },
              { label: "No", value: "no" },
              { label: "Not sure", value: "unsure" }
            ]
          }
        ]
      },
      settings: {
        submitButtonText: "Find My Home",
        successMessage: "Thanks! Your agent will reach out shortly with matching listings.",
        captureSmsConsent: true,
        consentText: "I agree to receive texts about matching properties and showings. Msg & data rates may apply."
      },
      tagRefs: ["tag:buyer-lead"]
    }
  ],
  documents: [
    {
      ref: "doc:buyer-rep",
      name: "Buyer Representation Agreement",
      samplePrompt: "Draft a buyer representation agreement between {{company}} and the buyer, with a commission section, an exclusive 90-day term, a non-discrimination clause, and a buyer signature and date field.",
      tagRefs: ["tag:buyer-lead"],
      ndfDocument: {
        version: "1.0",
        kind: "document",
        meta: {
          title: "Buyer Representation Agreement",
          description: "Exclusive buyer agency agreement for a 90-day term.",
          variables: [
            { name: "full_name", label: "Buyer Name", type: "text" },
            { name: "company", label: "Brokerage", type: "text" },
            { name: "date", label: "Date", type: "date" }
          ]
        },
        signers: [{ id: "signer_1", name: "Buyer", order: 1 }],
        blocks: [
          { type: "heading", id: "h1", level: 1, content: "Buyer Representation Agreement" },
          { type: "paragraph", id: "p1", content: 'This Agreement is entered into on {{date}} between {{company}} ("Broker") and {{full_name}} ("Buyer").' },
          { type: "heading", id: "h2", level: 2, content: "1. Exclusive Representation" },
          { type: "paragraph", id: "p2", content: "Buyer grants Broker the exclusive right to locate and negotiate the purchase of real property on Buyer\u2019s behalf for a term of ninety (90) days from the date above." },
          { type: "heading", id: "h3", level: 2, content: "2. Compensation" },
          { type: "paragraph", id: "p3", content: "Broker\u2019s compensation shall be paid at closing per the terms of the applicable listing or, where unpaid by the seller, as separately disclosed and agreed in writing prior to any offer." },
          { type: "heading", id: "h4", level: 2, content: "3. Equal Opportunity" },
          { type: "paragraph", id: "p4", content: "Broker complies with all fair housing laws and does not discriminate on the basis of race, color, religion, sex, disability, familial status, or national origin." },
          { type: "divider", id: "d1" },
          { type: "paragraph", id: "p5", content: "By signing below, Buyer acknowledges they have read and agree to the terms of this Agreement." },
          { type: "field_signature", id: "sig1", label: "Buyer Signature", signerId: "signer_1", required: true },
          { type: "field_date", id: "fd1", label: "Date Signed", signerId: "signer_1" }
        ]
      }
    }
  ],
  templates: [
    {
      ref: "tmpl:showing-sms",
      type: "sms",
      name: "Showing Reminder",
      category: "Reminders",
      samplePrompt: "Write a friendly SMS reminding a buyer about a property showing tomorrow, asking them to reply C to confirm or R to reschedule.",
      content: {
        message: "Hi {{first_name}}, reminder: your showing at {{address}} is tomorrow. Reply C to confirm or R to reschedule. \u2014 {{company}}",
        variables: ["first_name", "address", "company"]
      }
    },
    {
      ref: "tmpl:openhouse-email",
      type: "email",
      name: "Open House Invite",
      category: "Promotions",
      samplePrompt: "Write an open-house invitation email for this Saturday 1\u20134pm that includes the property address and a button to RSVP.",
      content: {
        subject: "You\u2019re invited: open house this Saturday",
        previewText: "Tour {{address}} this weekend \u2014 RSVP inside.",
        body: '<p>Hi {{first_name}},</p><p>You\u2019re invited to an exclusive open house at <strong>{{address}}</strong> this <strong>Saturday from 1\u20134pm</strong>.</p><p>Come see if it\u2019s the one. <a href="{{booking_link}}">RSVP here</a> and I\u2019ll have details ready for you.</p><p>See you there,<br/>{{company}}</p>',
        variables: ["first_name", "address", "booking_link", "company"]
      }
    }
  ],
  workflows: [
    {
      ref: "wf:new-lead-nurture",
      name: "New Lead Nurture",
      description: "When a buyer submits the intake form, tag them, add them to the pipeline, and send a showing reminder.",
      triggerType: "form_submitted",
      triggerConfig: { formId: { $ref: "form:intake" }, formName: "Property Interest Intake" },
      steps: [
        { stepType: "add_tag", sortOrder: 0, config: { tagId: { $ref: "tag:buyer-lead" }, tagName: "Buyer Lead" } },
        { stepType: "add_to_group", sortOrder: 1, config: { groupId: { $ref: "pipeline:buyers" }, stageId: { $ref: "stage:buyer-lead" }, groupName: "Buyer Pipeline", stageName: "Buyer Lead" } },
        { stepType: "wait", sortOrder: 2, config: { duration: 1, unit: "days" } },
        { stepType: "send_sms", sortOrder: 3, config: { templateId: { $ref: "tmpl:showing-sms" }, templateName: "Showing Reminder" } }
      ]
    },
    {
      ref: "wf:post-showing",
      name: "Post-Showing Follow-Up",
      description: "Manually enroll a buyer after a showing to send the open-house invite.",
      triggerType: "manual_enrollment",
      steps: [
        { stepType: "send_email", sortOrder: 0, config: { templateId: { $ref: "tmpl:openhouse-email" }, templateName: "Open House Invite" } }
      ]
    }
  ],
  coworkPrompts: [
    "Tag everyone who submitted the Property Interest Intake form this week as Hot.",
    "Send the Buyer Representation Agreement to everyone in the Offer Submitted stage for signature.",
    'Create a "Just Listed" SMS and send it to all Pre-Approved buyers.'
  ]
};
var ALL_KITS = [realEstateKit, ...GENERATED_KITS.map(
  (kit) => kit.kitId === "insurance-brokers" ? prepareInsuranceKit(kit) : kit
)];
var USE_CASE_KITS = Object.fromEntries(
  ALL_KITS.map((kit) => [kit.kitId, kit])
);
function getUseCaseKit(kitId) {
  return USE_CASE_KITS[kitId];
}
function listUseCaseKitSummaries() {
  return INDUSTRY_TEMPLATE_KEYS.map((key) => USE_CASE_KITS[key]).filter((k) => Boolean(k)).map(summarizeKit);
}
var INDUSTRY_TO_KIT = {
  "real-estate": "real-estate",
  "home-services": "home-services",
  insurance: "insurance-brokers",
  technology: "saas-ai-tech",
  healthcare: "medical-dental",
  retail: "retail-b2b",
  manufacturing: "retail-b2b",
  recreational: "spas-gyms",
  hospitality: "event-planning",
  consulting: "professional-services",
  financial: "professional-services",
  legal: "professional-services",
  education: null,
  other: null
};
function getKitForIndustry(industry) {
  if (!industry) return void 0;
  const key = industry.toLowerCase().trim();
  const kitId = key in INDUSTRY_TO_KIT ? INDUSTRY_TO_KIT[key] : null;
  return kitId ? getUseCaseKit(kitId) : void 0;
}

// src/getting-started/inline.ts
function parseGuideLabels(text) {
  if (!text.includes("**")) return [{ label: false, text }];
  const runs = [];
  let rest = text;
  for (; ; ) {
    const open = rest.indexOf("**");
    if (open === -1) break;
    const close = rest.indexOf("**", open + 2);
    if (close === -1) break;
    const label = rest.slice(open + 2, close);
    if (label.length === 0) break;
    if (open > 0) runs.push({ label: false, text: rest.slice(0, open) });
    runs.push({ label: true, text: label });
    rest = rest.slice(close + 2);
  }
  if (rest.length > 0) runs.push({ label: false, text: rest });
  return runs.length > 0 ? runs : [{ label: false, text }];
}
function stripGuideLabels(text) {
  return parseGuideLabels(text).map((run) => run.text).join("");
}
function hasUnbalancedLabels(text) {
  return (text.split("**").length - 1) % 2 === 1;
}

// src/getting-started/first-run.ts
var more = (label, ref) => ({ type: "docRef", label, ref });
var FIRST_RUN_CHAPTER = {
  slug: "first-run",
  // Inert. Chapter numbers order the public website's single-page guide, which never sees this
  // chapter; in the app the order comes from the `start-here` module in app-docs.ts.
  number: 16,
  title: "Start here",
  summary: "Eight first runs. Do them in order and you will have used every part of the product once, with something real to show for each.",
  icon: "Rocket",
  surface: "app",
  steps: [
    // ────────────────────────────────────────────────────────────────────────────────────
    {
      slug: "your-first-contact",
      title: "Create your first contact",
      why: "Everything else here sends to a contact, and the consent you are allowed to rely on is recorded the moment you save one.",
      route: "/contacts",
      estimatedMinutes: 3,
      prereqs: ["before-you-begin/the-consent-model"],
      keywords: [
        "create your first contact",
        "add a contact",
        "new contact",
        "how do i add someone",
        "add a customer",
        "add a client",
        "first contact",
        "get started with contacts"
      ],
      blocks: [
        { type: "clickPath", path: ["Contacts", "Add Contact"] },
        {
          type: "paragraph",
          text: "By the end of this you will have one person in the system, reachable on at least one channel, with a consent record you could produce if anyone ever asked."
        },
        {
          type: "steps",
          items: [
            {
              text: "Open **Contacts** from the sidebar.",
              note: "If the list is empty you can also start from the **Add Contact** button in the middle of the page."
            },
            {
              text: "Click **Add Contact** at the top right.",
              note: "On a phone, tap the round **+** button at the bottom right and choose **Add Contact**."
            },
            {
              text: "Fill in **First Name**, then a **Phone** number or an **Email** address.",
              note: "First name is the only required field, but a contact with neither a phone nor an email cannot be sent anything \u2014 the form says so under the pair."
            },
            {
              text: "Add **Tags** if you already know how you want to group this person.",
              note: "You can type a new tag name and create it without leaving the form. Tags are how you pick who gets what later."
            },
            {
              text: "Fill in the **Address** section, at least **Country** and **State**.",
              note: "These are not decoration. Country and state decide which jurisdiction rules apply to this contact \u2014 which channels you may use, and at what hours."
            },
            {
              text: "Read the **Consent** section before you save.",
              note: "It shows you exactly what will be captured for this person, based on the address you just entered."
            },
            { text: "Click **Add Contact**." }
          ]
        },
        {
          type: "fields",
          rows: [
            { name: "First Name", value: "Required.", note: "The only field the form insists on." },
            { name: "Last Name", value: "Optional." },
            {
              name: "Phone / Email",
              value: "At least one of the two.",
              note: "The dial code in front of the phone field follows the country you pick below."
            },
            { name: "Tags", value: "Optional. Create new ones inline." },
            {
              name: "Country / State",
              value: "Sets the jurisdiction rules for this contact.",
              note: "Worth getting right even when the rest of the address is blank."
            },
            {
              name: "Custom Fields",
              value: "Only appears once you have defined some.",
              note: "Contacts header, **Custom Fields**."
            }
          ]
        },
        {
          type: "note",
          variant: "compliance",
          text: "Saving a contact captures INFORMATIONAL consent where the jurisdiction allows it \u2014 service messages, receipts, a document to sign. MARKETING consent is never granted automatically, in any jurisdiction, no matter what you type here. You get that from a form the contact submits, or from evidence you upload against their record."
        },
        {
          type: "note",
          variant: "tip",
          text: "Have a list already? Do not type it in. The **Import** button on the same header takes a CSV, Google Contacts or Outlook, up to 10,000 people at a time."
        },
        more("All four ways contacts get in", "contacts/four-ways-in"),
        more("Import a CSV", "contacts/csv-import"),
        more("Capture consent against a contact", "contacts/capture-consent")
      ]
    },
    // ────────────────────────────────────────────────────────────────────────────────────
    {
      slug: "your-first-intake-request",
      title: "Send your first intake form request",
      why: "The cheapest way to get accurate details and lawful consent is to let the contact enter them, on a page you control, with the record written for you.",
      route: "/contacts",
      estimatedMinutes: 5,
      prereqs: ["first-run/your-first-contact"],
      keywords: [
        "send your first intake form request",
        "intake link",
        "intake form",
        "send intake link",
        "ask a contact to confirm their details",
        "prefilled link",
        "collect consent from a contact",
        "bulk intake"
      ],
      blocks: [
        { type: "clickPath", path: ["Contacts", "select contacts", "Send Intake Form"] },
        {
          type: "paragraph",
          text: "An intake request is a personal, prefilled link to your intake form. The contact opens it, corrects what is wrong, agrees to what they are willing to agree to, and their answers come back onto their record with the consent evidence attached."
        },
        {
          type: "steps",
          title: "Send to several contacts at once",
          items: [
            { text: "Open **Contacts**." },
            {
              text: "Tick the checkbox beside one or two contacts.",
              note: "A bulk actions bar appears as soon as anything is selected."
            },
            { text: "Click **Send Intake Form** in that bar." },
            {
              text: "Choose how it goes out: **Auto**, **Email** or **SMS**.",
              note: "**Auto** uses email where you have one and falls back to a text where you do not. It is the right answer almost every time."
            },
            {
              text: 'Read the "will be sent" line before you commit.',
              note: "It tells you how many of the selected contacts will actually receive something, and lists the ones that will not with the reason."
            },
            {
              text: "Click **Send to N contacts**.",
              note: "Progress ticks up live. Contacts who have revoked consent are skipped by the server even if they were selected."
            },
            { text: "Read the **Sent** / **Skipped** / **Failed** summary, then click **Done**." }
          ]
        },
        {
          type: "steps",
          title: "Send to one contact",
          items: [
            { text: "Open the contact, then open their **Consent** page." },
            {
              text: "Click **Send intake link** in the header.",
              note: "Greyed out when the contact has neither an email nor a phone \u2014 add one first."
            },
            { text: "Pick **Email** or **SMS**, then click **Send link**." },
            {
              text: "Check the audit trail further down the same page.",
              note: 'An "intake link sent" entry appears, with the exact link that went out.'
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "Change what the form asks before you send it at scale: the **Intake Form** button on the Contacts header opens your intake form in the editor."
        },
        {
          type: "note",
          variant: "compliance",
          text: "This is the one route that can produce MARKETING consent, because the contact grants it themselves on a page that records what they saw and when. That submission is the evidence."
        },
        more("Intake forms in full", "contacts/intake-forms"),
        more("Capture consent against a contact", "contacts/capture-consent"),
        more("Read what comes back", "inbox/unified-inbox")
      ]
    },
    // ────────────────────────────────────────────────────────────────────────────────────
    {
      slug: "your-first-campaign",
      title: "Send your first email or text campaign",
      why: "One screen writes the message, picks who is allowed to receive it, and sends it. Learn this screen and you have learned most of the product.",
      route: "/send",
      estimatedMinutes: 8,
      prereqs: ["first-run/your-first-contact"],
      keywords: [
        "send your first campaign",
        "send an email campaign",
        "send an sms campaign",
        "send a text message",
        "first campaign",
        "bulk message",
        "send a message to my contacts",
        "write a message with ai"
      ],
      blocks: [
        { type: "clickPath", path: ["Send", "Generate Draft", "Recipients", "Send Now"] },
        {
          type: "paragraph",
          text: "Start with a sentence describing what you want to say. The app writes a version for every channel you asked for, you edit them, you pick who it goes to, and it checks the consent and the clock before anything leaves."
        },
        {
          type: "steps",
          items: [
            { text: "Click **Send** in the sidebar." },
            {
              text: "Type what you want to say into the box under the greeting.",
              note: 'Plain English is enough \u2014 "let everyone know the clinic is closed on Monday". Type **@** to pull a template, form, document, contact or tag into the prompt.'
            },
            { text: "Pick a tone: **Professional**, **Friendly** or **Casual**." },
            {
              text: "Turn on the channels you want \u2014 email, SMS, or both.",
              note: "One generation writes all of them, so turning on both here is cheaper than coming back for the second."
            },
            { text: "Click **Generate Draft**." },
            {
              text: "On **Customize your content**, read and edit each message.",
              note: "This is a draft, not a decision. Rewrite it freely \u2014 merge fields such as the contact first name keep working as long as you leave them intact."
            },
            {
              text: "On **Who are you sending to?**, choose your recipients.",
              note: "Search by name, or select by tag, group or pipeline stage. Selecting everyone is one click."
            },
            {
              text: "On **Ready to send**, read the eligibility and cost summary.",
              note: "Contacts who have not given you consent for this channel and purpose are already excluded from the count. Anyone outside their local calling hours is shown as deferred, not dropped."
            },
            { text: "Click **Send Now** \u2014 or switch to a schedule and click **Schedule Campaign**." }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: 'The wizard progress line reads "Step 1 of 3" once you are past the first screen. It is counting the three phases \u2014 content, recipients, review \u2014 not the steps above. Nothing has gone wrong.'
        },
        {
          type: "note",
          variant: "compliance",
          text: "You cannot send a marketing message to someone who has only given informational consent, and the review screen will not let you talk yourself into it. If the eligible count is lower than you expected, that is the reason \u2014 and the fix is an intake request, not a workaround."
        },
        {
          type: "paragraph",
          text: "Already have the wording saved? Open **Messages**, find the template, and click **Send** on its card \u2014 the same screen opens with the content already in place."
        },
        more("The Send screen, end to end", "send/the-send-screen"),
        more("Recipients and eligibility", "send/recipients-and-eligibility"),
        more("Review and send", "send/review-and-send"),
        more("Write templates with AI", "templates/ai-create"),
        more("Watch the campaign afterwards", "inbox/campaign-dashboards")
      ]
    },
    // ────────────────────────────────────────────────────────────────────────────────────
    {
      slug: "your-first-document",
      title: "Create and send your first document for signing",
      why: "A signed agreement is the highest-value thing this product produces, and the evidence trail behind it is built whether you think about it or not.",
      route: "/forms-and-documents",
      estimatedMinutes: 7,
      prereqs: ["first-run/your-first-contact"],
      keywords: [
        "create and send your first document for signing",
        "e-signature",
        "esign",
        "get a document signed",
        "signature request",
        "send an agreement",
        "first document",
        "upload a pdf to sign"
      ],
      blocks: [
        {
          type: "clickPath",
          path: ["Forms & Documents", "Create New", "A document to get signed"]
        },
        {
          type: "paragraph",
          text: "You will describe the agreement in a sentence, let the app draft it, place the signature fields, and send it. The signer gets their own page; you get a verifiable record of what they saw and when they signed it."
        },
        {
          type: "steps",
          title: "Create it",
          items: [
            { text: "Open **Forms & Documents** and click **Create New**." },
            { text: "On **What would you like to create?**, choose **A document to get signed**." },
            {
              text: "On **Do you have content to start with?**, choose **No, start from scratch**.",
              note: "If you do have a PDF, choose **Yes, I have an existing document** and drop it in \u2014 then pick **Make it editable** to turn it into a real editable agreement, or **Keep as PDF and add fields** to leave the pages exactly as they are and just place fields on top."
            },
            {
              text: "On **Tell us about it**, describe the agreement in a sentence or two.",
              note: "There are example prompts under the box if you would rather start from one."
            },
            { text: "Click **Create with AI**." },
            {
              text: "In the editor, add your signers and drag the signature and data fields onto the document.",
              note: "The field editor is full screen \u2014 the app sidebar is not there. Use the editor own back control to leave, not the browser nav."
            }
          ]
        },
        {
          type: "steps",
          title: "Send it",
          items: [
            { text: "Back on **Forms & Documents**, click **Send** on the document card." },
            {
              text: "The send screen opens on **Set up your signature request**. Pick the document, write the message that goes with it, and choose the channels."
            },
            {
              text: "If the document has two or more signers, map each signer role to a contact and set the order they sign in.",
              note: "This panel only appears for multi-signer documents."
            },
            { text: "Choose recipients, then review and click **Send Now**." }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "The template is never altered by a signature. Each send renders its own copy with the signatures and field values baked in, so the original stays reusable forever."
        },
        {
          type: "note",
          variant: "compliance",
          text: "Signing is informational by nature \u2014 you are sending someone a document they are expecting. You can verify any signed copy later from **Settings**, and so can anyone you give it to."
        },
        more("Create a document", "documents/create-a-document"),
        more("Signers and fields", "documents/signers-and-fields"),
        more("Working from an uploaded PDF", "documents/uploaded-pdfs"),
        more("What the signer sees", "documents/signer-experience"),
        more("Verify a signature and pull the audit trail", "documents/verify-and-audit")
      ]
    },
    // ────────────────────────────────────────────────────────────────────────────────────
    {
      slug: "your-first-form",
      title: "Create and send your first form",
      why: "A form is how information and consent get into the system without you typing either of them, and it works from a link, a QR code or a send.",
      route: "/forms-and-documents",
      estimatedMinutes: 6,
      prereqs: ["first-run/your-first-contact"],
      keywords: [
        "create and send your first form",
        "build a form",
        "make a form",
        "publish a form",
        "share a form",
        "qr code for a form",
        "first form",
        "collect information"
      ],
      blocks: [
        {
          type: "clickPath",
          path: ["Forms & Documents", "Create New", "A form to collect information", "Publish"]
        },
        {
          type: "paragraph",
          text: "Same wizard as a document, different answer to the first question. A form ends up as a public page you can link, print as a QR code, or send to specific contacts \u2014 and every submission lands in your inbox against the right person."
        },
        {
          type: "steps",
          title: "Build it",
          items: [
            { text: "Open **Forms & Documents** and click **Create New**." },
            { text: "Choose **A form to collect information**." },
            { text: "Choose **No, start from scratch**." },
            {
              text: "Describe what the form should ask for, then click **Create with AI**.",
              note: 'Be specific about the fields you need. "A new client intake form asking for contact details, how they heard about us, and consent to be contacted" gives a far better first draft than "an intake form".'
            },
            {
              text: "In the editor, add or remove fields, fix the labels, and set the branding.",
              note: "The preview beside the canvas is what the contact will actually see."
            }
          ]
        },
        {
          type: "steps",
          title: "Share it",
          items: [
            { text: "Click **Publish**." },
            {
              text: "Use whichever of the three the situation calls for: the **QR Code**, the **Public URL**, or **Open Send Wizard**.",
              note: "The QR code downloads as a PNG or an SVG \u2014 the SVG is the one to give a printer."
            },
            {
              text: "If you are sending it: the send screen opens on **Set up your form send**. Pick the form, write the message, choose the channels."
            },
            { text: "Choose recipients, review, and click **Send Now**." },
            {
              text: "Watch the results come in from the inbox.",
              note: "Each submission is tied to the contact who sent it, along with anything they consented to."
            }
          ]
        },
        {
          type: "note",
          variant: "compliance",
          text: "Consent fields on a form are the strongest evidence you can hold, because the record captures what the contact was shown at the moment they agreed. This is where marketing consent is supposed to come from."
        },
        more("Forms vs agreements", "forms/forms-vs-agreements"),
        more("Build your first form", "forms/build-your-first-form"),
        more("Form fields and consent", "forms/form-fields-and-consent"),
        more("Publish and share", "forms/publish-and-share"),
        more("Read submissions", "forms/read-submissions")
      ]
    },
    // ────────────────────────────────────────────────────────────────────────────────────
    {
      slug: "your-first-workflow",
      title: "Create your first workflow",
      why: "A workflow is a rule that runs whether anyone remembers it or not: something happens, and the follow-up goes out on its own.",
      route: "/workflows/new",
      estimatedMinutes: 10,
      prereqs: ["first-run/your-first-campaign"],
      keywords: [
        "create your first workflow",
        "automation",
        "automate follow up",
        "trigger",
        "starting event",
        "workflow builder",
        "when this then that",
        "first workflow"
      ],
      blocks: [
        { type: "clickPath", path: ["Workflows", "Create Workflow", "Activate"] },
        {
          type: "paragraph",
          text: "Every workflow is the same shape: one starting event, then steps that run in order. A good first one is small \u2014 someone fills in your form, wait a day, send them a text."
        },
        {
          type: "steps",
          items: [
            { text: "Open **Workflows** in the sidebar and click **Create Workflow**." },
            {
              text: "Give it a name.",
              note: "It saves as you go \u2014 the header shows **Saved** once it has."
            },
            {
              text: "On the left, open **Starting event** and pick what sets the workflow off.",
              note: "Every option is listed below. If the one you pick needs to know which form or which document, its settings open on the right automatically."
            },
            {
              text: "Click the **+** on the canvas, then choose an action from **Add a step**."
            },
            {
              text: "Configure that step in the panel on the right.",
              note: "On a phone the panels open as sheets, and there is a floating button for **Add a step**."
            },
            { text: "Add as many steps as the job needs, in the order they should run." },
            {
              text: "Click **Activate**.",
              note: "If anything is unfinished you get a list of exactly what \u2014 click any entry to jump straight to that step."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            {
              name: "Contacts",
              value: "A new contact is added \xB7 A contact agrees to receive messages \xB7 A contact unsubscribes \xB7 A contact is added to a group \xB7 A contact moves to a new stage"
            },
            {
              name: "Forms & documents",
              value: "Someone fills out a form \xB7 A document is signed \xB7 A document is declined \xB7 A document is opened"
            },
            {
              name: "Messages",
              value: "A message campaign finishes \xB7 A message is delivered \xB7 An email is opened \xB7 A link in an email is clicked \xB7 A contact replies to you"
            },
            {
              name: "Manual",
              value: "Manually enroll specific contacts",
              note: "You pick who goes in from the workflow page. It does not fire on its own."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            {
              name: "Messages",
              value: "Send a Text Message \xB7 Send an Email \xB7 Send a Document to Sign \xB7 Send a Form to Fill Out"
            },
            {
              name: "Timing",
              value: "Wait Before Continuing \xB7 Wait Until a Date",
              note: "The first waits a duration; the second holds until a specific date and time in a timezone you choose."
            },
            {
              name: "Logic",
              value: "Check a Condition \xB7 End the Workflow",
              note: "**Check a Condition** splits the workflow in two \u2014 a yes branch and a no branch, each with its own steps."
            },
            {
              name: "Organize",
              value: "Add a Label \xB7 Remove a Label \xB7 Add to a Group \xB7 Remove from a Group \xB7 Move to a Stage \xB7 Update Contact Information"
            },
            {
              name: "Advanced",
              value: "Notify Another System",
              note: "Posts to a URL you give it, for anything outside Respectly."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "The builder calls tags **Labels** and triggers **starting events**. Same things, friendlier names \u2014 worth knowing when you are reading the rest of the manual."
        },
        {
          type: "note",
          variant: "warning",
          text: "A workflow that sends will still respect consent and calling hours for every contact it reaches. A step that cannot lawfully send does not silently send anyway."
        },
        more("What workflows are", "automation/workflows-overview"),
        more("Starting events in detail", "automation/triggers"),
        more("Steps and branches", "automation/steps-and-branches"),
        more("Activate and monitor", "automation/activate-and-monitor"),
        more("The workflows your kit already built", "automation/your-kit-workflows")
      ]
    },
    // ────────────────────────────────────────────────────────────────────────────────────
    {
      slug: "your-first-sequence",
      title: "Create your first email sequence",
      why: "A sequence is the simple case of automation: a fixed run of messages, spaced out, sent to a group you choose \u2014 no branching, no conditions, no canvas.",
      route: "/sequences/new",
      estimatedMinutes: 10,
      prereqs: ["first-run/your-first-campaign"],
      keywords: [
        "create your first email sequence",
        "sequence",
        "drip campaign",
        "welcome series",
        "nurture",
        "multi step campaign",
        "timed emails",
        "first sequence"
      ],
      blocks: [
        { type: "clickPath", path: ["Sequences", "Create Sequence", "Activate & Send"] },
        {
          type: "paragraph",
          text: "Four screens: name it, choose who gets it, write the messages with waits between them, and send. Everyone you enrol moves through the same steps in the same order."
        },
        {
          type: "steps",
          items: [
            { text: "Open **Sequences** and click **Create Sequence**." },
            {
              text: "On **Details**, fill in **Sequence Name**, and a **Description** if it helps.",
              note: 'The name is what you will recognise it by later \u2014 "Welcome Series" beats "Sequence 1".'
            },
            {
              text: "On **Audience**, choose who should get it \u2014 everyone, or the contacts carrying a particular label.",
              note: "A live count tells you how many people that actually is, so an empty label is obvious before you build anything."
            },
            {
              text: "On **Messages**, build the run with **Add Email**, **Add Text** and **Add Wait**."
            },
            {
              text: "For each message, choose **Write a message** or **Use a saved template**.",
              note: "Writing inline gives you a merge-tag palette for the contact and company fields. Saving a template instead is worth it the moment you want the same wording somewhere else."
            },
            {
              text: "Reorder or delete steps with the controls on each one until the run reads right."
            },
            {
              text: "On **Review**, read the flow strip across the top.",
              note: "Anything unfinished is called out in an amber banner. It will not let you activate around it."
            },
            {
              text: "Click **Save as Draft**, or **Activate & Send** when you are ready."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            { name: "Send Email", value: "One email. Written inline, or a saved email template." },
            { name: "Send Text", value: "One SMS. Written inline, or a saved SMS template." },
            {
              name: "Wait",
              value: "A pause before the next step \u2014 minutes, hours or days.",
              note: "The gap is what turns a list of messages into a sequence."
            }
          ]
        },
        {
          type: "example",
          title: "Three sequences worth stealing",
          body: "Welcome series \u2014 Email now, wait 2 days, Text, wait 5 days, Email.\nPromotion \u2014 Email now, wait 3 days, Text reminder, wait 2 days, final Email.\nRe-engagement \u2014 Email now, wait 7 days, Text, wait 14 days, last Email."
        },
        {
          type: "note",
          variant: "tip",
          text: "Sequence or workflow? A sequence runs a fixed list for a group you choose. A workflow reacts to something happening and can branch on what it finds. Reach for a sequence first \u2014 it is the same engine underneath, with the hard parts hidden."
        },
        more("Sequences in detail", "automation/sequences"),
        more("Workflows, when you need branching", "automation/workflows-overview"),
        more("Merge tags", "templates/merge-tags")
      ]
    },
    // ────────────────────────────────────────────────────────────────────────────────────
    {
      slug: "getting-help",
      title: "Getting help",
      why: "Three routes, in the order that gets you unstuck fastest. Knowing which one to reach for is most of it.",
      route: "/support",
      estimatedMinutes: 2,
      keywords: [
        "getting help",
        "contact support",
        "raise a ticket",
        "i am stuck",
        "talk to a person",
        "help",
        "cowork",
        "ask the assistant"
      ],
      blocks: [
        { type: "clickPath", path: ["Support", "New ticket"] },
        {
          type: "paragraph",
          text: "Nothing here is a dead end. Start with whichever of these fits, and escalate only when it does not."
        },
        {
          type: "steps",
          items: [
            {
              text: "Search this manual from the box at the top of any documentation page.",
              note: "Press **Ctrl** + **K** to search it from anywhere else in the app without losing what you are doing."
            },
            {
              text: "Ask **CoWork** \u2014 the assistant button at the bottom right of every screen.",
              note: "It does not only answer questions. Ask it to build the thing and it will, then show you what it did before anything is sent."
            },
            {
              text: "Open **Support** in the sidebar and raise a ticket when you need a person.",
              note: "Pick the category that fits, write a **Subject** and a **Description**, and attach up to five files."
            },
            {
              text: "Watch for the reply in the same thread.",
              note: "Replies land back in **Support**, not in your email inbox."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            { name: "Technical", value: "Something is broken or behaving oddly.", note: "Under 2 hours." },
            { name: "Billing", value: "Invoices, cards, plans, usage charges.", note: "Under 4 hours." },
            { name: "Account", value: "Access, roles, colleagues, your own details.", note: "Under 4 hours." },
            {
              name: "Compliance",
              value: "Consent, jurisdiction rules, an audit request.",
              note: "Under 8 hours."
            },
            { name: "Feature Request", value: "Something you wish it did.", note: "One to two days." },
            { name: "Other", value: "Anything that fits none of the above.", note: "Under 8 hours." }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "A ticket that names the screen and quotes what it said gets answered in one round trip. A screenshot is worth more than a paragraph describing one."
        },
        more("Ask CoWork to do it for you", "getting-help/cowork"),
        more("How support works", "getting-help/support"),
        more("Your playbook", "getting-help/your-playbook")
      ]
    }
  ]
};

// src/getting-started/spine.ts
var help = (categorySlug, questionSlug) => ({ kind: "help", categorySlug, questionSlug });
var GUIDE_SPINE = [
  // The eight "create your first ___" walkthroughs. `surface: 'app'`, so the public website
  // never sees them — see first-run.ts for why. Kept first because that is the order a reader
  // meets them in the app, even though the app's own ordering comes from app-docs.ts.
  FIRST_RUN_CHAPTER,
  // ══════════════════════════════════════════════════════════════════════════════════════
  {
    slug: "before-you-begin",
    number: 0,
    title: "Before you begin",
    summary: "Five minutes of reading that will save you hours later \u2014 what the product is for, how consent works, and what you need to have to hand.",
    icon: "Compass",
    steps: [
      {
        slug: "what-respectly-does",
        kind: "concept",
        title: "What Respectly does",
        why: "Knowing which of the five building blocks you are in stops most early confusion.",
        estimatedMinutes: 3,
        keywords: [
          "what is respectly",
          "what does this app do",
          "overview",
          "getting oriented",
          "what can i do here",
          "product overview",
          "building blocks"
        ],
        blocks: [
          {
            type: "paragraph",
            text: "Respectly is one place to reach the people you do business with \u2014 and to prove you were allowed to. Everything in the app is one of five things:"
          },
          {
            type: "list",
            items: [
              "Contacts \u2014 the people you talk to, the consent you hold for each of them, and the pipeline stage they are at.",
              "Templates \u2014 reusable SMS, email and voice-call content with merge fields.",
              'Forms and documents \u2014 forms collect information and consent; documents (called "Agreements" in the UI) get signed.',
              "Sends \u2014 the wizard that puts a message, a form or a document in front of the right people, lawfully.",
              "Automation \u2014 workflows and sequences that do the above without you."
            ]
          },
          {
            type: "paragraph",
            text: "The Inbox is where every one of those lands afterwards, and the Dashboard is the summary. That is the whole product."
          }
        ]
      },
      {
        slug: "the-consent-model",
        kind: "concept",
        title: "The consent model, in two minutes",
        why: 'Consent is not a single on/off switch here, and almost every "why can I not send this?" question traces back to this one idea.',
        estimatedMinutes: 4,
        keywords: [
          "consent",
          "opt in",
          "opt out",
          "how does consent work",
          "permission to message",
          "informational vs marketing",
          "tcpa basics",
          "do i need consent"
        ],
        blocks: [
          {
            type: "paragraph",
            text: "Consent is tracked per contact as a 3x2 matrix: three channels (SMS, Email, Voice) times two purposes (Informational and Marketing). A contact can be reachable by informational email and by nothing else, and Respectly will enforce exactly that."
          },
          {
            type: "list",
            items: [
              "Informational \u2014 service messages: an appointment, a receipt, a document to sign, an update on something they asked for.",
              "Marketing \u2014 anything promotional. Legally much harder, and in most US states it can never be granted automatically."
            ]
          },
          {
            type: "note",
            variant: "compliance",
            text: "Consent is never a bare toggle in this app. Granting it always requires evidence \u2014 an uploaded document, an audio reference, a witnessed verbal, a written note, or a form the contact actually submitted. Reinstating consent someone affirmatively withdrew requires an uploaded document specifically. That is the point: the record has to be worth something if you are ever asked to produce it."
          },
          {
            type: "paragraph",
            text: "Every capture attempt \u2014 granted, revoked, blocked or rejected \u2014 is written to an append-only audit log you can export as a PDF per contact."
          },
          {
            type: "faqRef",
            label: "More on consent and compliance",
            ...help("contacts-consent-management")
          }
        ]
      },
      {
        slug: "what-you-will-need",
        kind: "concept",
        title: "What to have ready",
        why: "Two of these gate features later, and chasing them mid-setup is the usual reason people stall.",
        estimatedMinutes: 2,
        keywords: [
          "what do i need to get started",
          "prerequisites",
          "before i start",
          "checklist",
          "what to have ready"
        ],
        blocks: [
          {
            type: "fields",
            rows: [
              {
                name: "Business name, website and EIN (or local tax ID)",
                value: "Required for a dedicated US SMS number",
                note: "Carriers demand it for 10DLC registration. Without it you can still send on the shared pool."
              },
              {
                name: "Full billing address",
                value: "Required for 10DLC, and it sets your home jurisdiction",
                note: "Your billing country decides which consent rules apply to you by default."
              },
              {
                name: "A logo and a brand colour",
                value: "Optional but do it early",
                note: "They are applied to every form, email and signing page."
              },
              {
                name: "A contact list",
                value: "CSV, Google Contacts or Outlook",
                note: "Max 10,000 contacts and 5 MB per CSV import."
              },
              {
                name: "A payment card",
                value: "Required to finish setup",
                note: "The trial is 14 days; the card is captured during the wizard."
              }
            ]
          },
          {
            type: "note",
            variant: "warning",
            text: "A dedicated US long-code number needs an EIN and a business \u2014 sole proprietors and personal accounts are hard-blocked from 10DLC by the carriers, not by us. If that is you, plan on the shared number pool or a toll-free number."
          }
        ]
      },
      {
        slug: "who-does-what",
        kind: "concept",
        title: "Who on your team does what",
        why: "Deciding roles now avoids a rebuild once several people are in the account.",
        estimatedMinutes: 2,
        keywords: [
          "who does what",
          "roles",
          "team responsibilities",
          "who should do this",
          "division of work"
        ],
        blocks: [
          {
            type: "paragraph",
            text: "Access is controlled by roles, and each role is a set of permissions grouped by area \u2014 Contacts, Documents, Forms, Campaigns, Inbox, Templates, Analytics, Team, Roles, Settings, Billing, Webhooks and AI. System roles cannot be deleted or renamed, but you can edit what they are allowed to do, and you can create your own."
          },
          {
            type: "note",
            variant: "tip",
            text: "A useful default: one owner who holds Billing and Team, everyone who sends holds Campaigns and Contacts, and whoever owns compliance holds Settings. You can change all of this later in Settings > Roles & Permissions."
          }
        ]
      }
    ]
  },
  // ══════════════════════════════════════════════════════════════════════════════════════
  {
    slug: "create-your-account",
    number: 1,
    title: "Create your account",
    summary: "Sign up, verify your email, create the organization, and get past the plan gate.",
    icon: "UserPlus",
    steps: [
      {
        slug: "sign-up",
        title: "Sign up",
        why: "Everything else hangs off the organization this creates.",
        surface: "web",
        estimatedMinutes: 3,
        keywords: ["sign up", "create an account", "register", "new account"],
        blocks: [
          { type: "clickPath", path: ["app.respectly.ai", "Create an account"] },
          {
            type: "steps",
            items: [
              {
                text: "Go to app.respectly.ai and click **Create an account**."
              },
              {
                text: "Fill in your first name, work email, company name and a password.",
                note: "Or click **Continue with Google**, **Microsoft** or **Apple** and you will be asked for the company name and industry straight afterwards."
              },
              {
                text: "Set the industry, even though it is marked optional.",
                note: "It picks your recommended starter kit, the sample copy in the send composer, and which version of this guide you are shown. Skipping it costs you all three."
              },
              {
                text: "Submit the form."
              }
            ]
          }
        ]
      },
      {
        slug: "verify-your-email",
        title: "Verify your email",
        why: "This is a hard gate \u2014 an unverified user is bounced out of every screen in the app.",
        surface: "web",
        route: "/verify-email",
        estimatedMinutes: 2,
        keywords: ["verify my email", "email verification", "confirmation email", "cannot get in"],
        blocks: [
          {
            type: "steps",
            items: [
              {
                text: "Open the verification email and click the link.",
                note: "This is a hard gate \u2014 an unverified user is bounced out of every screen in the app."
              },
              {
                text: "Sign in."
              },
              {
                text: "If it never arrives, click **Resend verification email** on the same screen and check your spam folder.",
                note: "It comes from Respectly, not from your own sending domain, which you have not set up yet \u2014 so your own domain rules will not help it through."
              }
            ]
          }
        ]
      },
      {
        slug: "create-your-organization",
        title: "Create your organization",
        why: "The organization is the tenant \u2014 contacts, templates, consent and billing all belong to it.",
        surface: "web",
        route: "/onboarding",
        estimatedMinutes: 2,
        keywords: ["create organization", "set up my company", "new workspace", "tenant"],
        blocks: [
          {
            type: "steps",
            items: [
              {
                text: "Confirm your email address, company name and industry."
              },
              {
                text: "Click **Create Organization**."
              },
              {
                text: `You land on a short "You're all set!" screen and then the setup wizard.`,
                note: "The organization is the tenant \u2014 contacts, templates, consent and billing all belong to it."
              },
              {
                text: "Bring colleagues in by invitation from inside this organization.",
                note: "One organization per business, not one per user. Anyone who signs up separately gets their own isolated account and will not see your contacts."
              }
            ]
          }
        ]
      },
      {
        slug: "trial-and-plan-gate",
        title: "Understand the trial and the card gate",
        why: "Until a card is on file the app redirects you back to setup from every route \u2014 this surprises people.",
        surface: "web",
        estimatedMinutes: 2,
        keywords: ["trial", "card gate", "why is the app locked", "add a card", "free trial"],
        blocks: [
          {
            type: "steps",
            items: [
              {
                text: "Expect to be redirected. A new signup starts a 14-day trial in a pending-card state.",
                note: "While in that state, opening any page sends you back to the plan step. This is the behaviour, not a bug."
              },
              {
                text: "Add a card at the plan step."
              },
              {
                text: "The whole app unlocks.",
                note: "Adding a card early does not cost you trial days \u2014 the 14 days run regardless."
              }
            ]
          }
        ]
      }
    ]
  },
  // ══════════════════════════════════════════════════════════════════════════════════════
  {
    slug: "guided-setup",
    number: 2,
    title: "Finish guided setup",
    summary: "The eight-step wizard, what each step really does, and what the starter kit builds for you.",
    icon: "Wand2",
    steps: [
      {
        slug: "walk-the-wizard",
        title: "Walk the setup wizard",
        why: "It collects, in one pass, the things half a dozen features later depend on.",
        surface: "web",
        route: "/setup",
        estimatedMinutes: 8,
        keywords: ["setup wizard", "guided setup", "oobe", "first time setup"],
        blocks: [
          {
            type: "steps",
            items: [
              {
                text: "Work through the steps in the left-hand list. Progress saves on every change.",
                note: "Closing the browser costs you nothing, and a banner on the dashboard brings you back."
              },
              {
                text: "**Welcome** \u2014 orientation only, and not skippable."
              },
              {
                text: "**Business Details** \u2014 personal details plus an Individual/Business selector.",
                note: "Choose **Business** and fill in the org name, industry, website, EIN and billing address. The EIN is what a dedicated US number later depends on."
              },
              {
                text: "**Branding** \u2014 logo, brand colour and logo position, with a live preview of your intake form."
              },
              {
                text: "**Your Goals** \u2014 pick **Send Campaigns**, **Collect Documents & eSignatures**, or **Explore Everything**.",
                note: "At least one is required, and it decides which later steps you are shown."
              },
              {
                text: "**Import Contacts** \u2014 CSV with column mapping, or Google / Outlook."
              },
              {
                text: "**Communications** \u2014 your email sender, and whether you want a shared or dedicated phone number."
              },
              {
                text: "**Choose a Plan** \u2014 the card gate. Not skippable for new signups."
              },
              {
                text: "**What's Next** \u2014 a short feature tour."
              }
            ]
          },
          {
            type: "note",
            variant: "tip",
            text: "Every step except Welcome and Choose a Plan has its own skip link. Skipping is safe \u2014 each one has a permanent home in Settings, and this guide covers all of them."
          }
        ]
      },
      {
        slug: "pick-your-kit",
        title: "Pick your industry starter kit",
        why: "This is the single highest-leverage click in setup: it builds a working configuration instead of leaving you with an empty account.",
        estimatedMinutes: 4,
        keywords: [
          "starter kit",
          "industry kit",
          "apply a kit",
          "templates for my industry",
          "use cases",
          "pick a kit"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Use Cases"] },
          {
            type: "paragraph",
            text: "A starter kit is a curated set of tags, a pipeline with named stages, forms, signable documents, SMS and email templates, and live workflows \u2014 all built for one industry."
          },
          {
            type: "steps",
            items: [
              {
                text: "In the setup wizard, look for the recommended kit.",
                note: "It is picked from the industry you chose at signup."
              },
              {
                text: "If no kit is recommended for your industry, browse the gallery instead.",
                note: "There is no kit tailored to your industry yet, so the wizard leads with the gallery. That is expected, not an error."
              },
              {
                text: "Apply whichever of the ten kits is structurally closest to how you work.",
                note: "Nothing is locked to a vertical and everything a kit creates is editable."
              },
              {
                text: "Or skip it and build as you go through this guide.",
                note: "The kit is a head start, not a prerequisite."
              }
            ]
          },
          {
            type: "note",
            variant: "tip",
            text: "If you later set a more specific industry in Settings, Profile, both the recommended kit and this guide follow it."
          },
          {
            type: "action",
            label: "Browse all starter kits",
            to: { kind: "app", route: "/use-cases" }
          }
        ]
      },
      {
        slug: "what-a-kit-creates",
        title: "Know what the kit created \u2014 and change it",
        why: "A kit is a starting point, not a black box; everything it makes is ordinary, editable content.",
        route: "/use-cases",
        requires: { features: ["use_cases"] },
        estimatedMinutes: 4,
        keywords: [
          "what did the kit create",
          "kit contents",
          "change what the kit made",
          "undo a kit",
          "starter kit contents"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Use Cases"] },
          {
            type: "steps",
            items: [
              {
                text: "Open the **Use Cases** page. Every kit is shown as a full walkthrough.",
                note: "The tags, the pipeline stages and their exit criteria, each form and document with the AI prompt that generates it, the message templates, and the workflows."
              },
              {
                text: "Read the kit you applied \u2014 especially the workflows.",
                note: "They were created AND activated. They are already running."
              },
              {
                text: "Click **Apply this kit** to provision the whole set for real."
              },
              {
                text: "Edit anything it made. It is ordinary content with no special status."
              }
            ]
          },
          {
            type: "note",
            variant: "tip",
            text: "Re-applying is safe. Tags and pipelines are matched by name, so nothing is duplicated, and anything you have edited is left alone."
          },
          { type: "action", label: "Open Use Cases", to: { kind: "app", route: "/use-cases" } }
        ]
      },
      {
        slug: "resume-or-skip",
        title: "Resume setup later",
        why: "Knowing where the unfinished steps live means an interrupted setup never becomes an abandoned one.",
        surface: "web",
        estimatedMinutes: 1,
        keywords: ["resume setup", "finish setup later", "skip setup", "setup banner"],
        blocks: [
          {
            type: "steps",
            items: [
              {
                text: "Open the dashboard. A resume banner lists the setup steps you have left.",
                note: "That is where an interrupted setup lives \u2014 it is not lost and it is not hidden in a menu."
              },
              {
                text: "Click through the banner to pick up where you stopped."
              },
              {
                text: "Work the **Getting Started** widget alongside it.",
                note: "It tracks the practical first tasks: add a contact, create an SMS template, create an email template, create a document, build a form, invite your team."
              }
            ]
          }
        ]
      }
    ]
  },
  // ══════════════════════════════════════════════════════════════════════════════════════
  {
    slug: "business-profile",
    number: 3,
    title: "Business profile and branding",
    summary: "The details that unlock SMS registration, set your compliance jurisdiction, and brand every page a customer sees.",
    icon: "Building2",
    steps: [
      {
        slug: "business-details",
        title: "Complete your business details",
        why: "These exact fields are what a carrier checks before it will let you send SMS from your own number.",
        route: "/settings/profile",
        estimatedMinutes: 5,
        keywords: [
          "business details",
          "company name",
          "business address",
          "ein",
          "tax id",
          "update my company information",
          "business profile"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Settings", "Profile"] },
          {
            type: "steps",
            items: [
              {
                text: "Open **Settings**, then **Profile**."
              },
              {
                text: "Check the account is set to **Business**, not Individual. Click **Switch to Business** if it is not.",
                note: "Only a Business profile exposes the company fields SMS registration depends on."
              },
              {
                text: "Fill in **Company Name** \u2014 your registered legal name.",
                note: "It must match the EIN registration, not a trading name."
              },
              {
                text: "Set the **Industry**."
              },
              {
                text: "Fill in **Business ID / EIN** and **Website**."
              },
              {
                text: "Fill in the full billing address \u2014 country, state or province, street, city, postal code.",
                note: "The country sets your home compliance jurisdiction as well as your billing currency."
              },
              {
                text: "Press the **Save Changes** button on each card you filled in.",
                note: "Every card on this page saves separately. Filling three cards and pressing one button saves one card."
              }
            ]
          },
          {
            type: "fields",
            rows: [
              {
                name: "Company Name",
                value: "Your registered legal name",
                note: "Must match the EIN registration, not a trading name."
              },
              {
                name: "Industry",
                value: "Your vertical",
                note: "Drives the recommended kit and this guide."
              },
              {
                name: "Business ID / EIN",
                value: "Your tax ID",
                note: "Required for 10DLC."
              },
              {
                name: "Website",
                value: "https://...",
                note: "Required for 10DLC and for toll-free verification."
              },
              {
                name: "Billing address",
                value: "Country, state/province, street, city, postal code",
                note: "Required for 10DLC and it sets your home compliance jurisdiction."
              }
            ]
          },
          {
            type: "note",
            variant: "warning",
            text: 'Leave any of these blank and the SMS brand registration will fail later with "Tenant business identity incomplete". It is much less painful to fill them in now.'
          }
        ]
      },
      {
        slug: "branding",
        title: "Set your logo and brand colour",
        why: "These are applied automatically to every form, email header and signing page your customers see.",
        route: "/settings/profile",
        estimatedMinutes: 3,
        keywords: [
          "logo",
          "brand colour",
          "brand color",
          "upload my logo",
          "branding",
          "customise the look"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Settings", "Profile"] },
          {
            type: "steps",
            items: [
              {
                text: "Open **Settings**, then **Profile**."
              },
              {
                text: "Upload a logo.",
                note: "It is auto-cropped, and the variants used across hosted forms, emails and signature pages are generated for you."
              },
              {
                text: "Pick a brand colour from the six presets, or enter a custom hex."
              },
              {
                text: "Press **Save Changes** on that card."
              },
              {
                text: "Check a public form afterwards.",
                note: "The banner should carry your logo and company name. If it shows the Respectly wordmark, something above did not save."
              }
            ]
          },
          {
            type: "note",
            variant: "tip",
            text: "These are applied automatically to every form, email header and signing page your customers see. Skipping this puts the Respectly wordmark on your contracts, which is not the impression you want."
          }
        ]
      },
      {
        slug: "booking-link-and-custom-fields",
        title: "Add your booking link and organisation custom fields",
        why: "Both become merge variables you can drop into any message, so you set them once instead of retyping them.",
        route: "/settings/profile",
        estimatedMinutes: 4,
        keywords: [
          "booking link",
          "calendar link",
          "organisation custom fields",
          "org variables",
          "company merge fields"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Settings", "Profile"] },
          {
            type: "steps",
            items: [
              {
                text: "Open **Settings**, then **Profile**."
              },
              {
                text: "Put your scheduling URL in the **Booking Link** card.",
                note: "It becomes available in templates as {{booking_link}}."
              },
              {
                text: "In the **Organisation Custom Fields** card, define your own reusable values.",
                note: "An office address, a licence number, a support line \u2014 each available as {{org.key}}."
              },
              {
                text: "Press **Save Changes** on each card."
              },
              {
                text: "Use them from the variable picker in any template rather than pasting the value.",
                note: "Change it once here and every template that references it updates."
              }
            ]
          },
          {
            type: "note",
            variant: "tip",
            text: "Anything you would otherwise paste into several templates belongs here."
          }
        ]
      },
      {
        slug: "security",
        title: "Secure your login",
        why: "This account holds other people's personal data and their consent record. Treat it accordingly.",
        route: "/settings/security",
        estimatedMinutes: 2,
        keywords: [
          "password",
          "change my password",
          "login methods",
          "google sign in",
          "two factor",
          "secure my account",
          "sign in with microsoft"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Settings", "Security"] },
          {
            type: "steps",
            items: [
              {
                text: "Open **Settings**, then **Security**."
              },
              {
                text: "Read the **Login Methods** section \u2014 Password, Google, Microsoft and Apple.",
                note: "Each row shows either **Connect**, or the linked email address and an **Unlink** button."
              },
              {
                text: "Click **Connect** on a provider to add a second way in, or **Unlink** to remove one."
              },
              {
                text: "Set or change your password from the **Password** row."
              },
              {
                text: "Keep at least two ways in if you can.",
                note: "You cannot unlink your last remaining method \u2014 the app blocks it rather than locking you out."
              }
            ]
          },
          {
            type: "note",
            variant: "tip",
            text: "This account holds other people's personal data and their consent record. A second sign-in method is worth the two minutes."
          }
        ]
      }
    ]
  },
  // ══════════════════════════════════════════════════════════════════════════════════════
  {
    slug: "sending-identity",
    number: 4,
    title: "Your sending identity",
    summary: "Which email address and which phone number your messages come from \u2014 and what it takes to use your own.",
    icon: "AtSign",
    steps: [
      {
        slug: "email-sender",
        title: "Send email from your own address",
        why: "Mail from your own domain gets opened and gets delivered; mail from a shared platform address does neither as well.",
        route: "/settings/email-config",
        estimatedMinutes: 6,
        keywords: [
          "send from my own email",
          "custom email address",
          "smtp",
          "gmail",
          "outlook",
          "email configuration",
          "sender address"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Settings", "Email Configuration"] },
          {
            type: "steps",
            items: [
              {
                text: "Open **Settings**, then **Email Configuration**."
              },
              {
                text: "Turn on **Custom Email Sending**."
              },
              {
                text: "Choose a provider: **Gmail** or **Microsoft** for one-click OAuth, **Apple**, or **Custom SMTP**.",
                note: "Custom SMTP asks for host, port, username, password and the From address."
              },
              {
                text: "Complete the connection \u2014 the consent screen for OAuth, or the credentials for SMTP."
              },
              {
                text: "Click **Send Test Email** BEFORE you save.",
                note: "It uses the configuration on screen, so you find out it is wrong now rather than after a customer does."
              },
              {
                text: "Press **Save Changes**."
              }
            ]
          },
          {
            type: "note",
            variant: "warning",
            text: "If you enable custom sending but do not finish connecting the provider, email keeps going out from the default Respectly address. The page warns you \u2014 read the warning."
          }
        ]
      },
      {
        slug: "email-use-cases",
        title: "Choose what your address is used for",
        why: "You may want contracts to come from you but bulk campaigns to come from elsewhere. This is where you decide.",
        route: "/settings/email-config",
        estimatedMinutes: 2,
        keywords: [
          "what is my email used for",
          "documents forms campaigns email",
          "email use cases",
          "which emails come from me"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Settings", "Email Configuration"] },
          {
            type: "steps",
            items: [
              {
                text: "Open **Settings**, then **Email Configuration**."
              },
              {
                text: "Set the use-case chips \u2014 **Documents**, **Forms**, **Campaigns**.",
                note: "They control which kinds of email use your custom sender. You may want contracts to come from you but bulk campaigns to come from elsewhere."
              },
              {
                text: "Press **Save Changes**."
              }
            ]
          },
          {
            type: "note",
            variant: "compliance",
            text: "Authentication, billing and support email always comes from Respectly regardless of what you set here. Those are not yours to re-route."
          }
        ]
      },
      {
        slug: "phone-number",
        title: "Choose a phone number",
        why: "Your options genuinely differ in cost, lead time and what you must prove, so pick deliberately.",
        estimatedMinutes: 5,
        keywords: [
          "phone number",
          "sms number",
          "get a number",
          "dedicated number",
          "caller id",
          "what number do i text from"
        ],
        blocks: [
          {
            type: "steps",
            items: [
              {
                text: "Reach this at the **Communications** step of the setup wizard.",
                note: "There is no phone-numbers page in Settings \u2014 if you need one later, raise a ticket from Support."
              },
              {
                text: "Start on the shared Respectly pool unless you have a reason not to.",
                note: "Free, included on all plans, works immediately, no registration."
              },
              {
                text: "For a dedicated toll-free number, have your website and billing address ready.",
                note: "Toll-Free Verification takes roughly 2 to 4 weeks. No EIN required."
              },
              {
                text: "For a dedicated local (10DLC) number, have your EIN, business type, website and full billing address ready.",
                note: "Brand registration typically takes 1 to 7 business days, and the campaign takes longer. Sole proprietors are blocked by the carriers, not by us."
              },
              {
                text: "Submit the request. The wizard raises a support ticket.",
                note: "A dedicated number is requested, not bought self-serve \u2014 the Respectly team provisions and registers it."
              }
            ]
          },
          {
            type: "fields",
            rows: [
              {
                name: "Shared Respectly pool",
                value: "Free, included on all plans, works immediately",
                note: "No registration. The right default \u2014 start here and upgrade later."
              },
              {
                name: "Dedicated toll-free",
                value: "Toll-Free Verification, roughly 2-4 weeks",
                note: "Needs your website and billing address. No EIN required."
              },
              {
                name: "Dedicated local (10DLC)",
                value: "Brand then campaign registration, 1-7 business days for the brand",
                note: "Needs EIN, business type, website and full billing address. Sole proprietors are blocked by the carriers."
              }
            ]
          }
        ]
      }
    ]
  },
  // ══════════════════════════════════════════════════════════════════════════════════════
  {
    slug: "compliance",
    number: 5,
    title: "Compliance foundations",
    summary: "What the platform enforces on your behalf, the one switch you control, and where to look when a send is blocked or held.",
    icon: "ShieldCheck",
    steps: [
      {
        slug: "consent-matrix",
        title: "Read the consent matrix",
        why: "It is the thing the send engine actually checks, so it is the thing worth understanding.",
        route: "/settings/compliance",
        estimatedMinutes: 4,
        keywords: [
          "consent matrix",
          "channel and purpose",
          "sms email voice consent",
          "informational marketing consent",
          "read consent"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Settings", "Compliance"] },
          {
            type: "steps",
            items: [
              {
                text: "Read the grid the way the send engine does: **SMS / Email / Voice** against **Informational / Marketing**.",
                note: "Six cells per contact. A send is judged against exactly one of them."
              },
              {
                text: "Read the badge in the cell \u2014 **Granted**, **Opted out**, **Blocked**, **Pending** or **Not captured**.",
                note: "Each carries the date, the source and the rule that produced it."
              },
              {
                text: "Remember the one-way rule: a marketing grant satisfies an informational send, never the other way round.",
                note: "Informational consent will not let you send a promotion, however reasonable that feels."
              }
            ]
          }
        ]
      },
      {
        slug: "contact-creation-defaults",
        title: "Check your contact-creation defaults",
        why: "It explains why a brand-new contact already has some consent and, more importantly, why it does not have marketing consent.",
        route: "/settings/compliance",
        estimatedMinutes: 3,
        keywords: [
          "contact creation defaults",
          "auto grant consent",
          "layer 0",
          "default consent when adding a contact"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Settings", "Compliance"] },
          {
            type: "steps",
            items: [
              {
                text: "Open **Settings**, then **Compliance**."
              },
              {
                text: "Read the **Contact Creation Defaults** card \u2014 an Allow/Block matrix for your billing country and for the global floor.",
                note: "Read-only. This is what happens automatically when a contact is created."
              },
              {
                text: "Note which cells are auto-granted. The engine starts wide open and only restricts where a jurisdiction rule says so.",
                note: "That is why a brand-new contact already has some consent, with a trail recorded for each cell."
              },
              {
                text: "Note which cells are blocked \u2014 marketing, almost certainly.",
                note: "You cannot override this. It has to be captured from the contact instead."
              },
              {
                text: 'Check the row labelled "Your billing country" is the country you expect.',
                note: "It comes from your billing address on Settings, Profile. Wrong country, wrong rulebook, every new contact."
              }
            ]
          },
          {
            type: "note",
            variant: "compliance",
            text: "Most US states BLOCK the automatic granting of marketing consent. That is deliberate. Marketing consent has to be captured from the contact \u2014 through a form they submit, or on the consent screen with evidence attached."
          }
        ]
      },
      {
        slug: "double-opt-in",
        title: "Decide on double opt-in",
        why: "This is the one compliance setting you control, and it is a real trade-off.",
        route: "/settings/compliance",
        estimatedMinutes: 3,
        keywords: [
          "double opt in",
          "confirmation link",
          "confirm consent",
          "turn off double opt in",
          "require confirmation"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Settings", "Compliance"] },
          {
            type: "steps",
            items: [
              {
                text: "Open **Settings**, then **Compliance**."
              },
              {
                text: "Decide the trade-off before you touch the toggle.",
                note: "On: a contact who opts in on a form is sent a confirmation link and stays **Pending** until they click it \u2014 slower, stronger. Off: consent is recorded immediately \u2014 faster, weaker."
              },
              {
                text: "Set the toggle."
              },
              {
                text: 'If you see a "Managed by Respectly" banner instead of a toggle, stop \u2014 the platform sets this for you.',
                note: "Support can change it."
              }
            ]
          },
          {
            type: "note",
            variant: "tip",
            text: "This is the one compliance setting you control. Everything else on this page is derived from your jurisdiction."
          }
        ]
      },
      {
        slug: "jurisdiction-rulebook",
        title: "Read your jurisdiction rulebook",
        why: "Before you argue with a blocked send, read the rule that blocked it.",
        route: "/settings/policies",
        estimatedMinutes: 4,
        keywords: [
          "jurisdiction",
          "state rules",
          "country rules",
          "tcpa",
          "casl",
          "gdpr",
          "what am i allowed to send",
          "legal rules"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Settings", "Policies"] },
          {
            type: "steps",
            items: [
              {
                text: "Open **Settings**, then **Policies**."
              },
              {
                text: "Pick a jurisdiction and a channel."
              },
              {
                text: "Read what it takes to send compliantly there, in plain language.",
                note: "Opt-in requirements, consent checks, permitted sending times, and opt-out handling."
              },
              {
                text: "Read this BEFORE arguing with a blocked send.",
                note: "Rules inherit global, then country, then state, with the most specific one winning \u2014 so the rule that blocked you may not be the one you were looking at."
              }
            ]
          }
        ]
      },
      {
        slug: "calling-hours",
        title: "Understand calling hours and deferral",
        why: "A send that seems to have vanished has usually just been held until the recipient's local window opens.",
        estimatedMinutes: 3,
        keywords: [
          "calling hours",
          "quiet hours",
          "time of day",
          "when can i send",
          "deferred send",
          "too early to text"
        ],
        blocks: [
          {
            type: "steps",
            items: [
              {
                text: "Do not look for a quiet-hours setting. There is not one.",
                note: "Calling hours are platform rules evaluated per recipient against their local time."
              },
              {
                text: "When a send seems to have vanished, check the review step's sending groups.",
                note: "An out-of-hours send is deferred, not dropped."
              },
              {
                text: "Check the Inbox: the campaign reads **Scheduled** with a live countdown.",
                note: "It goes on its own when the window opens. There is nothing to do."
              },
              {
                text: "Complete **country** and **state** on your contacts to stop the fail-closed cases.",
                note: "A contact with no usable timezone is held rather than sent at a possibly illegal hour."
              }
            ]
          }
        ]
      },
      {
        slug: "decisions-log",
        title: "Use the decisions log",
        why: 'It is the answer to "why did this not send?", and it is exportable when someone asks you to prove it.',
        route: "/settings/policies/decisions",
        estimatedMinutes: 3,
        keywords: [
          "decisions log",
          "why was my message blocked",
          "compliance log",
          "policy decisions",
          "audit compliance"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Settings", "Policies", "View Decisions"] },
          {
            type: "steps",
            items: [
              {
                text: "Open **Settings**, then **Policies**, then **View Decisions**."
              },
              {
                text: "Find the evaluation you are asking about and read its outcome \u2014 **Allow**, **Conditional** or **Block**.",
                note: '"Conditional" only appears here. The Inbox shows the same outcomes as **Blocked** or **Needs Review**.'
              },
              {
                text: "Expand the record for the routing path, the individual checks, and the suggested remediation.",
                note: "The remediation line is the useful one \u2014 it says what would have to change."
              },
              {
                text: "Filter by channel, purpose, decision, date, country, state, rule id or recipient."
              },
              {
                text: "Click **Export CSV** when someone needs it in writing."
              }
            ]
          }
        ]
      },
      {
        slug: "opt-outs",
        title: "Know how opt-outs are handled",
        why: "Opt-outs are sticky and channel-wide, and reinstating one deliberately takes real effort.",
        estimatedMinutes: 3,
        keywords: [
          "opt out",
          "unsubscribe",
          "stop message",
          "someone unsubscribed",
          "remove from list",
          "do not contact"
        ],
        blocks: [
          {
            type: "steps",
            items: [
              {
                text: "Treat all four routes as identical, because the engine does.",
                note: "STOP on SMS, an email unsubscribe, a cleared box on a form, and an admin revoke all opt the contact out across that whole channel and record it."
              },
              {
                text: "Expect it to be channel-wide, not cell-wide.",
                note: "An opt-out on SMS marketing takes SMS informational with it."
              },
              {
                text: "To reinstate, capture a written express opt-in with an uploaded document.",
                note: "A checkbox cannot overturn a revocation \u2014 by design."
              },
              {
                text: "Leave marketing email alone: an unsubscribe link and your business mailing address are added automatically.",
                note: "Adding your own is how you end up with two."
              }
            ]
          }
        ]
      }
    ]
  },
  // ══════════════════════════════════════════════════════════════════════════════════════
  {
    slug: "contacts",
    number: 6,
    title: "Contacts",
    summary: "Get your people in, organise them, and capture consent you can actually rely on.",
    icon: "Users",
    steps: [
      {
        slug: "four-ways-in",
        kind: "concept",
        title: "The four ways contacts get in",
        why: "Picking the right door for each source saves a cleanup later.",
        route: "/contacts",
        requires: { permissions: ["contacts:read"], features: ["contacts"] },
        estimatedMinutes: 3,
        keywords: [
          "how do contacts get in",
          "add contacts",
          "ways to add contacts",
          "import options"
        ],
        blocks: [
          {
            type: "list",
            items: [
              "CSV upload \u2014 bulk, with column mapping and duplicate handling.",
              "Google or Outlook \u2014 OAuth import straight from your address book.",
              "Manually \u2014 one at a time, from Add Contact.",
              "Your intake form \u2014 a public link or QR code the contact fills in themselves, which is the only door that also captures marketing consent properly."
            ]
          }
        ]
      },
      {
        slug: "csv-import",
        title: "Import a CSV",
        why: "It is the fastest way to a useful account, and the options page matters more than it looks.",
        route: "/contacts?import=csv",
        requires: { permissions: ["contacts:read"], features: ["contacts"] },
        estimatedMinutes: 8,
        keywords: [
          "import a csv",
          "upload contacts",
          "bulk import",
          "spreadsheet import",
          "how do i import my contact list",
          "import contact list",
          "download template",
          "csv template",
          "excel"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Contacts", "Import"] },
          {
            type: "steps",
            items: [
              {
                text: "Click **Import** in the Contacts page header.",
                note: "On an account with no contacts yet the empty state offers **Import CSV** instead. On mobile, tap the round **+** button, then **Import CSV**."
              },
              {
                text: "Leave the source tile on **CSV File** \u2014 it is selected already.",
                note: "**Google** and **Outlook** are the other two tiles; see Import from Google or Outlook."
              },
              {
                text: 'Under "Download the CSV template", click **Download Template**.',
                note: "Saves contacts-import-template.csv. Keep the header row exactly as it comes \u2014 matching it is what lets the wizard skip the mapping step entirely."
              },
              {
                text: "Fill in your contacts, then drag the file onto **Drag & drop CSV file** \u2014 or click the box to browse.",
                note: "CSV only, up to 5 MB, and a maximum of 10,000 contacts per import."
              },
              {
                text: "Nothing to click here: the file parses and the wizard moves on by itself.",
                note: "Straight to **Import Options** when every column auto-maps, otherwise to **Map Columns**."
              },
              {
                text: "Only if **Map Columns** appears: set each **Your Column** to the right **Our Field**, leave anything you do not want on **Skip this column**, then click **Continue**.",
                note: "First Name plus either Email or Phone are required, as is every starred custom field. Continue stays blocked until they are mapped."
              },
              {
                text: 'On **Import Options**, choose one option under "When duplicate found (same email or phone):".',
                note: "The three choices are compared in the table below."
              },
              {
                text: 'Add a tag under "Apply tags to all imported contacts:".',
                note: "Optional, and worth doing \u2014 tag by source and you can still find this batch in a month."
              },
              {
                text: "Click **Import {N} Contacts** \u2014 the button carries the row count, e.g. **Import 1,250 Contacts**."
              },
              {
                text: "Wait on **Importing Contacts**. This step cannot be cancelled or closed.",
                note: 'It reads "Processing X of Y..." with a live estimate of the time left.'
              },
              {
                text: "On **Import Complete**, check the created / updated / skipped / failed counts, then click **View Contacts**."
              }
            ]
          },
          {
            type: "fields",
            rows: [
              {
                name: "Skip duplicate rows",
                value: "The default, and the safe one",
                note: "Leaves existing contacts untouched."
              },
              {
                name: "Update existing contacts",
                value: "Use when the CSV is more current than the app"
              },
              {
                name: "Create duplicates anyway",
                value: "Really does create a second record",
                note: "Including for repeated rows inside the same file."
              }
            ]
          },
          {
            type: "note",
            variant: "compliance",
            text: "Importing is not consent. Informational consent is auto-applied only where your jurisdiction rule expressly allows it, marketing consent is never imported, and smsOptIn / emailOptIn columns in your CSV are ignored on purpose."
          },
          {
            type: "note",
            variant: "tip",
            text: 'Define your custom fields before a large import, not after. A required custom field with no mapped column forces the Map Columns step and blocks Continue with "Please map a column for: {field}".'
          },
          {
            type: "action",
            label: "Open the import wizard",
            to: { kind: "app", route: "/contacts?import=csv" }
          }
        ]
      },
      {
        slug: "google-outlook-import",
        title: "Import from Google or Outlook",
        why: "It skips the export/reformat step entirely and keeps the fields clean.",
        route: "/contacts?import=google",
        requires: { permissions: ["contacts:read"], features: ["contacts"] },
        estimatedMinutes: 4,
        keywords: [
          "import from google",
          "import from outlook",
          "sync contacts",
          "gmail contacts",
          "microsoft contacts"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Contacts", "Import"] },
          {
            type: "steps",
            items: [
              {
                text: "Click **Import** in the Contacts page header."
              },
              {
                text: "Click the **Google** or the **Outlook** tile."
              },
              {
                text: "Click **Connect with Google** (or **Connect with Outlook**) and finish the sign-in in the popup.",
                note: "The panel says what is read before you agree: only contact names, emails and phone numbers."
              },
              {
                text: "On **Select Contacts**, tick the people you want.",
                note: 'Use **Select all** / **Deselect all**, or the "Search by name, email, phone..." box to narrow the list first. The heading tracks it: "Select contacts (X of Y selected)".'
              },
              {
                text: "Click **Continue**."
              },
              {
                text: "On **Import Options**, pick your duplicate handling and any tags \u2014 the same choices as a CSV import."
              },
              {
                text: "Click **Import {N} Contacts**, wait for **Importing Contacts**, then click **View Contacts**."
              }
            ]
          },
          {
            type: "note",
            variant: "tip",
            text: "There is no mapping step on this route \u2014 the fields come across already named, which is the whole reason to prefer it over exporting to a spreadsheet first."
          },
          {
            type: "note",
            variant: "compliance",
            text: "The consent rules are identical to a CSV import: informational consent only where the jurisdiction rule allows it, and never marketing."
          },
          {
            type: "action",
            label: "Import from Google",
            to: { kind: "app", route: "/contacts?import=google" }
          },
          {
            type: "action",
            label: "Import from Outlook",
            to: { kind: "app", route: "/contacts?import=outlook" }
          }
        ]
      },
      {
        slug: "custom-fields",
        title: "Define your custom fields",
        why: "Do this before importing \u2014 custom fields are what make your messages specific instead of generic.",
        route: "/contacts",
        requires: { permissions: ["contacts:read"], features: ["contacts"] },
        estimatedMinutes: 5,
        keywords: [
          "custom fields",
          "extra fields",
          "add a field to a contact",
          "merge field",
          "custom data"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Contacts", "Custom Fields"] },
          {
            type: "paragraph",
            text: "Custom fields are the data unique to your business \u2014 a policy number, a renewal date, a property address, a membership tier. Each one becomes a merge variable available in every template, form and document."
          },
          {
            type: "steps",
            items: [
              {
                text: "Click **Custom Fields** in the Contacts page header to open the Manage Custom Fields drawer."
              },
              {
                text: "Click **Add Field**.",
                note: "If the name you type matches a field you deleted before, an amber notice tells you that creating it will restore that field along with the values already saved on your contacts."
              },
              {
                text: "Name the field, pick its type, and save it."
              },
              {
                text: "Drag the rows to reorder them.",
                note: "The order here is the order the fields appear on the contact form and card."
              },
              {
                text: "To remove one, use its delete action and confirm **Delete Field**.",
                note: "The dialog names the field and its key. The field leaves contact forms and cards, but values already saved on contacts stay stored."
              },
              {
                text: "To bring one back, scroll to **Deleted fields** at the bottom of the drawer and click **Restore**.",
                note: "It returns to the bottom of the active list with its saved values intact \u2014 unless another active field has since taken its display name, which the server refuses with a message."
              }
            ]
          },
          {
            type: "note",
            variant: "warning",
            text: "Deleting a custom field does not clean up the templates that use it. Those merge tags turn into red warning chips and would send as blank text \u2014 the app warns you before letting the send through."
          },
          {
            type: "note",
            variant: "tip",
            text: "Do this before your first import. A required custom field with no CSV column to map forces the mapping step and blocks the import until you give it one."
          }
        ]
      },
      {
        slug: "tags",
        title: "Set up tags",
        why: "Tags are how you choose who receives what, so a small deliberate set beats a sprawling one.",
        route: "/contacts",
        requires: { permissions: ["contacts:read"], features: ["contacts"] },
        estimatedMinutes: 4,
        keywords: ["tags", "label contacts", "tag a contact", "segment contacts", "group by tag"],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Contacts", "tag filter", "Manage Tags"] },
          {
            type: "paragraph",
            text: "Tags are free-form labels. Filter the contact list by them, apply them in bulk, target a send with them, and add or remove them automatically from a workflow."
          },
          {
            type: "steps",
            items: [
              {
                text: "Open the tag-filter dropdown above the contact list and click **Manage Tags**."
              },
              {
                text: "Give the tag a **Tag Name** and a **Color**, then save it.",
                note: "The drawer previews the tag as you type, so you can see it the way it will look on a contact card."
              },
              {
                text: "To tag people in bulk, switch the list to **Table** view, tick the rows you want, and use **Add Tag** in the toolbar that appears.",
                note: "Selection only exists in Table view \u2014 Grid, Kanban and the mobile card list have no checkboxes, so every bulk action is desktop-table-only."
              },
              {
                text: "Use **Remove Tag** in the same toolbar to take one off the selected contacts."
              },
              {
                text: "To use a tag as a filter, pick it in the tag-filter dropdown; the list narrows immediately."
              }
            ]
          },
          {
            type: "note",
            variant: "tip",
            text: "A small deliberate set beats a sprawling one. Tags are how you choose who receives what, so every tag you cannot define in a sentence is a send you will get wrong later."
          }
        ]
      },
      {
        slug: "pipelines-and-stages",
        title: "Build your pipeline",
        why: "A pipeline turns a contact list into a process you can see, and stages can trigger automation.",
        route: "/contacts",
        requires: { permissions: ["contacts:read"], features: ["contacts"] },
        estimatedMinutes: 6,
        keywords: [
          "pipeline",
          "stages",
          "kanban",
          "sales pipeline",
          "move through stages",
          "groups"
        ],
        blocks: [
          {
            type: "clickPath",
            path: ["Sidebar", "Contacts", "pipeline selector", "Manage Pipelines"]
          },
          {
            type: "paragraph",
            text: "A pipeline is an ordered set of stages a contact moves through. Moving a contact into a stage is itself a workflow trigger, which is what makes a pipeline more than a label."
          },
          {
            type: "steps",
            items: [
              {
                text: "Open the pipeline selector above the contact list and choose **Manage Pipelines**."
              },
              {
                text: "Create the pipeline, then add its stages in the order a contact actually moves through them."
              },
              {
                text: "Close the drawer and pick your new pipeline in the selector."
              },
              {
                text: "Switch the view switcher from **Table** to **Kanban**.",
                note: "The switcher sits above the list and toggles **Table / Grid / Kanban**."
              },
              {
                text: "Drag a contact card between stage columns to move them."
              },
              {
                text: "To move several at once, switch back to **Table**, tick the rows, and use the **Add to Pipeline** modal from the toolbar \u2014 pick the Pipeline and Stage, then **Add to {Pipeline}**."
              }
            ]
          },
          {
            type: "note",
            variant: "tip",
            text: 'Build the stages before the automation. A workflow that triggers on "moved to stage" needs the stage to exist first, and renaming a stage afterwards means revisiting every workflow that names it.'
          }
        ]
      },
      {
        slug: "capture-consent",
        title: "Capture consent with evidence",
        why: 'This is the screen that turns "we think they said yes" into something you can produce on request.',
        route: "/contacts",
        requires: { permissions: ["contacts:update"], features: ["contacts"] },
        estimatedMinutes: 6,
        keywords: [
          "capture consent",
          "proof of consent",
          "consent evidence",
          "record consent",
          "how do i prove consent"
        ],
        blocks: [
          { type: "clickPath", path: ["Contacts", "a contact", "Manage Consents"] },
          {
            type: "steps",
            items: [
              {
                text: "Open a contact from the list, then click **Manage Consents** in Quick Actions."
              },
              {
                text: "Find the cell you want in the matrix \u2014 rows **SMS / Email / Voice**, columns **Informational / Marketing** \u2014 and click it.",
                note: 'An uncaptured cell prompts "Click to capture with evidence". A cell reading **Blocked** is locked by a jurisdiction rule and cannot be clicked; it shows the jurisdiction and rule id instead.'
              },
              {
                text: "In the **Record consent** drawer, pick an **Evidence type**.",
                note: "Uploaded document, Audio recording, Verbal with witness, Written note, or API reference. The fields below change with your choice \u2014 an upload box, a recording URL and context, a witness name and statement, a note, or an external URL and reference id."
              },
              {
                text: "Paste the exact wording the contact was shown into **Disclosure shown to contact**.",
                note: "The exact text matters more than a summary of it \u2014 this is the field an auditor reads."
              },
              {
                text: "Choose a **Consent level** \u2014 **Express opt-in** or **Written express opt-in**.",
                note: "Written express opt-in only becomes selectable when the evidence type is **Uploaded document**."
              },
              {
                text: "Click **Record consent**.",
                note: "A server rejection \u2014 missing evidence, for instance \u2014 comes back as a toast and nothing is recorded."
              },
              {
                text: "Check the **Audit trail** card underneath.",
                note: "Every attempt lands there: granted, revoked, blocked or rejected, with timestamp, actor, IP and the evidence."
              }
            ]
          },
          {
            type: "note",
            variant: "compliance",
            text: "Written express opt-in requires an uploaded artifact and is what TCPA expects for automated marketing. There is no bare toggle anywhere in the product: a grant always needs evidence, and reinstating a channel the contact previously revoked needs an uploaded document."
          },
          {
            type: "note",
            variant: "tip",
            text: "The same cells are clickable from the Consent preferences card on the contact page and from the Edit Contact modal \u2014 all three open this identical drawer and write the same audited record."
          }
        ]
      },
      {
        slug: "intake-forms",
        title: "Publish your intake form",
        why: "A contact filling in their own preferences is the strongest consent evidence you can get, and the cheapest to collect.",
        route: "/contact-forms",
        requires: { permissions: ["contacts:read"], features: ["contacts"] },
        estimatedMinutes: 7,
        keywords: [
          "intake form",
          "contact collection form",
          "sign up form",
          "collect contacts",
          "qr code form"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Contacts", "Intake Form"] },
          {
            type: "paragraph",
            text: "Every account gets a built-in intake form, public at /collect/{slug}. A contact filling in their own preferences is the strongest consent evidence you can get, and the cheapest to collect."
          },
          {
            type: "steps",
            items: [
              {
                text: "Click **Intake Form** in the Contacts page header."
              },
              {
                text: "In the editor, choose which contact fields appear and which custom fields to include.",
                note: "Ask for the least you need. Every extra field costs you completions."
              },
              {
                text: "Set the branding so the page looks like you, not like a generic form."
              },
              {
                text: "Turn on the consent options you actually want \u2014 SMS, email and voice, each split into informational and marketing.",
                note: "The public page groups these into two cards, Service Notifications and Marketing Messages, so the contact chooses each deliberately."
              },
              {
                text: "Save, then share the form as a link or a QR code."
              },
              {
                text: "To send it to contacts you already have, use **Send intake link** on a contact consent screen, or tick rows in Table view and click **Send Intake Form**.",
                note: 'The bulk dialog previews the outcome before you commit \u2014 "18 of 20 will be sent", plus the reason for each skip.'
              }
            ]
          },
          {
            type: "note",
            variant: "compliance",
            text: "Submitting the form replaces the contact's previous preferences rather than adding to them \u2014 anything left unchecked is turned off. That is what makes it a lawful opt-out route as well as an opt-in one, and the page says so to the contact."
          },
          {
            type: "action",
            label: "Open intake forms",
            to: { kind: "app", route: "/contact-forms" }
          }
        ]
      },
      {
        slug: "bulk-actions-and-export",
        title: "Bulk actions, intake links and export",
        why: "The fastest way to fix consent across an imported list, and the export you will want at audit time.",
        route: "/contacts",
        requires: { permissions: ["contacts:read"], features: ["contacts"] },
        estimatedMinutes: 4,
        keywords: [
          "bulk actions",
          "export contacts",
          "download contacts",
          "select all",
          "bulk tag",
          "bulk delete",
          "csv export"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Contacts", "Table", "select rows"] },
          {
            type: "steps",
            items: [
              {
                text: "Switch the contact list to **Table** view.",
                note: "Selection exists only here \u2014 Grid, Kanban and the mobile card list have no checkboxes."
              },
              {
                text: "Filter the list to the set you want FIRST, then tick rows.",
                note: "Changing any filter clears the selection."
              },
              {
                text: "Tick rows, or use the header checkbox to take the current page of 20.",
                note: 'When the whole page is checked and more contacts match, a banner offers "Select all {N} contacts matching this filter" \u2014 capped at 500, the same ceiling the bulk endpoints enforce.'
              },
              {
                text: "Pick an action from the bulk bar: **Send Message**, **Send Document**, **Send Intake Form**, **Add Tag**, **Remove Tag**, **Update Consent**, **Export** or **Delete**."
              },
              {
                text: "For an imported list, reach for **Send Intake Form**.",
                note: "It is the practical way to collect real marketing consent from people whose consent you only assumed."
              },
              {
                text: "For **Export**, check the scope the dialog opened on before confirming.",
                note: "It picks the narrowest available \u2014 your selection, then your filter, then everything \u2014 so it never silently widens."
              }
            ]
          }
        ]
      }
    ]
  },
  // ══════════════════════════════════════════════════════════════════════════════════════
  {
    slug: "templates",
    number: 7,
    title: "Message templates",
    summary: "Reusable SMS, email and voice content \u2014 generated by AI from one prompt, then edited.",
    icon: "MessageSquare",
    steps: [
      {
        slug: "templates-hub",
        title: "Find your templates",
        why: "All three channels live in one library, which is not obvious from the sidebar label.",
        route: "/templates",
        requires: { permissions: ["templates:read"], features: ["templates"] },
        estimatedMinutes: 2,
        keywords: [
          "find my templates",
          "template library",
          "where are my templates",
          "browse templates"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Templates", "Messages"] },
          {
            type: "steps",
            items: [
              {
                text: "Open **Messages** under the **Templates** section of the sidebar.",
                note: 'The heading reads "Messages" and the subtitle spells out why all three channels share one page: "All your SMS, Email, and Voice templates in one place."'
              },
              {
                text: "Narrow the library with the channel pills \u2014 **All**, **SMS**, **Email**, **Voice**.",
                note: "SMS is tinted violet, Email blue and Voice amber, so you can pick a channel out of the grid at a glance."
              },
              {
                text: 'Narrow further with the status filter (**All / Active / Archived**), the **Category** filter, or the "Search templates..." box.'
              },
              {
                text: "Switch between the grid and list toggle \u2014 your choice is remembered for next time."
              },
              {
                text: "Use the actions on any card: **Send**, **Edit**, **Archive** (or **Restore**), **Duplicate** and **Delete**.",
                note: "**Send** is offered on active templates only, which is what makes archiving a way to retire a template without deleting it."
              }
            ]
          },
          { type: "action", label: "Open Messages", to: { kind: "app", route: "/templates" } }
        ]
      },
      {
        slug: "ai-create",
        title: "Generate all three channels from one prompt",
        why: "One description produces an SMS, an email and a voice script that say the same thing \u2014 far faster than writing three.",
        route: "/templates/create",
        requires: { permissions: ["templates:read"], features: ["templates", "ai_generation"] },
        estimatedMinutes: 5,
        keywords: [
          "generate templates with ai",
          "ai template",
          "write a template for me",
          "one prompt three channels",
          "create templates"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Templates", "Messages", "Create Template"] },
          {
            type: "steps",
            items: [
              {
                text: "Click **Create Template** on the Messages page."
              },
              {
                text: "Describe the message in the prompt box.",
                note: 'Say what the message must achieve and what it must contain, not how to word it. "Confirm the appointment was booked and include the date and a calendar link" beats "write a nice confirmation".'
              },
              {
                text: "Pick a **Tone** \u2014 **Professional**, **Friendly**, **Casual** or **Urgent**."
              },
              {
                text: "Click **Generate All Templates**.",
                note: 'The button reads "Generating..." while it works, and lands you on Step 2.'
              },
              {
                text: "On **Review your templates**, use the channel toggle pills to keep only the drafts you want.",
                note: "Turning a channel off hides its card and drops it from the save count."
              },
              {
                text: "Give each remaining draft a **Template name**, an optional **Description** and a **Category**, and edit the content inline."
              },
              {
                text: "Click **Save {N} Template(s)**.",
                note: 'Nothing is stored until you do \u2014 see "Save templates from the review step" for what a refresh costs you here.'
              }
            ]
          },
          {
            type: "note",
            variant: "tip",
            text: 'If you only want one channel, do not use this route \u2014 scroll past the prompt box to "Or skip the AI and create one" and start from blank instead. It makes no AI request at all.'
          },
          {
            type: "action",
            label: "Create templates",
            to: { kind: "app", route: "/templates/create" }
          }
        ]
      },
      {
        slug: "merge-tags",
        title: "Use merge tags safely",
        why: "A broken merge tag sends as blank text, which is worse than not sending at all.",
        estimatedMinutes: 4,
        keywords: [
          "merge tags",
          "variables",
          "personalisation",
          "first name placeholder",
          "insert a variable",
          "why is my message blank"
        ],
        blocks: [
          {
            type: "steps",
            items: [
              {
                text: "Use the variable picker or the **Insert Variable** chips. Do not type a tag by hand.",
                note: "The picker is fed by the same catalogue the delivery worker resolves at send time, so nothing you pick from it can send blank. Typing is where blanks come from."
              },
              {
                text: "Match the syntax to the channel: **{{first_name}}** for SMS and email, **{first_name}** for voice scripts.",
                note: "Double braces on two channels, single on the third. This is the single most common merge mistake."
              },
              {
                text: "Check the **Variables in this template** panel before you save.",
                note: `A tag pointing at a deleted custom field, or simply mistyped, shows there as a red chip with the reason \u2014 "this custom field was deleted" or "isn't a recognized merge tag" \u2014 plus "It will send as blank text".`
              },
              {
                text: "Look at the preview. A broken tag renders blank there.",
                note: "That blank is exactly what the recipient would see."
              },
              {
                text: "If **Unresolved merge tags** appears on save, click **Go back and fix** \u2014 not **Save anyway**.",
                note: "A broken merge tag sends as blank text, which is worse than not sending at all."
              }
            ]
          },
          {
            type: "note",
            variant: "warning",
            text: "In the email editor the subject line takes variables too, and it is checked separately from the body. A broken tag in a subject line is the one people miss."
          }
        ]
      },
      {
        slug: "sms-template",
        title: "Write an SMS template",
        why: "SMS is billed and read by the segment, so the counter matters.",
        route: "/sms-templates/new",
        requires: { permissions: ["templates:read"], features: ["templates"] },
        estimatedMinutes: 5,
        keywords: [
          "write an sms template",
          "text message template",
          "how do i compose an sms",
          "sms content",
          "character count"
        ],
        blocks: [
          {
            type: "clickPath",
            path: ["Sidebar", "Templates", "Messages", "Create Template", "SMS Template"]
          },
          {
            type: "steps",
            items: [
              {
                text: "Open **Create SMS Template** \u2014 from the quick-create **SMS Template** button, or by clicking **Edit** on an existing SMS card."
              },
              {
                text: "In the **Template Details** card, fill in **Template Name** and pick a **Category**; add a **Description** if it needs one."
              },
              {
                text: "Write the **Message**.",
                note: 'The placeholder tells you the syntax: "Type your message here... Use {{variable}} to add personalization."'
              },
              {
                text: "Insert any personalisation from the variable picker rather than typing it.",
                note: "It lists the system tags plus your own Contact Custom Fields and Organization fields."
              },
              {
                text: "Watch the live segment and character counter as you type, and check the **Live Preview** panel beside it.",
                note: "SMS is billed and read by the segment. Keep to one where you can."
              },
              {
                text: "Click **Save Template**.",
                note: 'If a merge tag will not resolve, an "Unresolved merge tags" confirmation appears with **Go back and fix** and **Save anyway**. Go back and fix it.'
              }
            ]
          },
          {
            type: "note",
            variant: "compliance",
            text: "Marketing SMS needs a visible opt-out and the editor reminds you. Replying STOP is handled automatically when a contact sends it, but the words have to be in your message."
          }
        ]
      },
      {
        slug: "email-template",
        title: "Write an email template",
        why: "The subject line does most of the work, and preview text does the rest.",
        route: "/email-templates/new",
        requires: { permissions: ["templates:read"], features: ["templates"] },
        estimatedMinutes: 5,
        keywords: [
          "write an email template",
          "email content",
          "subject line",
          "how do i compose an email",
          "html email"
        ],
        blocks: [
          {
            type: "clickPath",
            path: ["Sidebar", "Templates", "Messages", "Create Template", "Email Template"]
          },
          {
            type: "steps",
            items: [
              {
                text: "Open **Create Email Template** \u2014 from the quick-create **Email Template** button, or **Edit** on an existing email card."
              },
              {
                text: "Fill in **Template Details**: **Template Name**, **Category**, and a **Description** if useful."
              },
              {
                text: "Write the **Subject Line**.",
                note: 'It does most of the work. Merge tags are allowed here \u2014 the placeholder shows the shape: "e.g., Welcome to {{company}}, {{first_name}}!"'
              },
              {
                text: "Write the **Email Body** in the rich-text editor.",
                note: "Bold, italic, H1, H2, bullet and numbered lists, links, undo and redo."
              },
              {
                text: "Open **Advanced Options** and set the **Preview Text**.",
                note: "This is the line the inbox shows after the subject. Leaving it empty wastes the second-best piece of real estate you have."
              },
              {
                text: "Check the **Live Preview** on both the **Desktop** and **Mobile** toggles."
              },
              {
                text: "Click **Save Template**.",
                note: 'The subject is checked for broken merge tags separately from the body, and both are flagged in the same "Variables in this template" panel.'
              }
            ]
          },
          {
            type: "note",
            variant: "tip",
            text: "Marketing emails get an unsubscribe link and your business mailing address added automatically. Do not hand-roll your own."
          }
        ]
      },
      {
        slug: "voice-script",
        title: "Write a voice script",
        why: "Voice is a separate channel with its own consent and its own variable syntax.",
        route: "/voice-scripts/new",
        requires: { features: ["voice_scripts"] },
        estimatedMinutes: 4,
        keywords: [
          "voice script",
          "voice call",
          "phone script",
          "how do i compose a voice call",
          "ai voice"
        ],
        blocks: [
          {
            type: "clickPath",
            path: ["Sidebar", "Templates", "Messages", "Create Template", "Voice Template"]
          },
          {
            type: "steps",
            items: [
              {
                text: "Open **Create Voice Script** \u2014 from the quick-create **Voice Template** button, or **Edit** on an existing voice card."
              },
              {
                text: "Enter a **Script Name** (up to 100 characters) and a **Description** (up to 500)."
              },
              {
                text: "Pick a **Call Type**.",
                note: "Required before the script can be saved. The three options are outbound AI, inbound hotline and three-way merge."
              },
              {
                text: "Write the **Script Content** \u2014 between 50 and 5,000 characters.",
                note: "Voice uses SINGLE braces: {first_name}, not {{first_name}}. Pasting a body across from an SMS template will not substitute."
              },
              {
                text: "Open **Call Settings (optional)** if the defaults do not suit.",
                note: "Max Duration, Retry Attempts and Retry Interval \u2014 left alone they are 10 minutes, 2 retries and 24 hours."
              },
              {
                text: "Add **Tags** if you want the script findable by them."
              },
              {
                text: "Click **Preview** to hear it back with sample values and an estimated duration, then **Save**.",
                note: "A save-status indicator tracks it: Saving, Saved, Save failed, or Unsaved changes."
              }
            ]
          },
          {
            type: "note",
            variant: "warning",
            text: "Voice is a separate channel with its own consent and its own variable syntax. Both differences bite people who assume a voice script is just an SMS read aloud."
          }
        ]
      }
    ]
  },
  // ══════════════════════════════════════════════════════════════════════════════════════
  {
    slug: "forms",
    number: 8,
    title: "Forms",
    summary: "Collect information and consent with a link or a QR code.",
    icon: "ClipboardList",
    steps: [
      {
        slug: "forms-vs-agreements",
        kind: "concept",
        title: "Forms or agreements?",
        why: "They share one list and one builder, so knowing which you are making avoids a wrong turn at step one.",
        route: "/forms-and-documents",
        requires: { permissions: ["forms:read"], features: ["forms_documents"] },
        estimatedMinutes: 2,
        keywords: [
          "form or document",
          "forms vs agreements",
          "which should i use",
          "difference between a form and a document"
        ],
        blocks: [
          {
            type: "paragraph",
            text: "The Forms & Documents page holds both. An Information Form gathers responses, consent or registrations through a shared link or QR code. An Agreement is sent to named people for e-signature. Chapter 9 covers agreements."
          }
        ]
      },
      {
        slug: "build-your-first-form",
        title: "Build a form",
        why: "Describing what you need in a sentence is dramatically faster than dragging fields.",
        route: "/forms-and-documents",
        requires: { permissions: ["forms:read"], features: ["forms_documents"] },
        estimatedMinutes: 8,
        keywords: ["build a form", "create a form", "new form", "form builder", "make a form"],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Forms & Documents", "Create New"] },
          {
            type: "steps",
            items: [
              {
                text: "Open **Forms & Documents** in the sidebar and click **Create New**."
              },
              {
                text: "Tell the Smart Create wizard what you are making.",
                note: "An **Information Form** gathers responses, consent or registrations through a shared link or QR code. An **Agreement** is sent to named people for e-signature."
              },
              {
                text: "Say whether you have existing content to start from."
              },
              {
                text: "Describe what you need in a sentence.",
                note: 'This is dramatically faster than dragging fields. "Registration for a Saturday class, name, phone, which session, and consent to text reminders" is enough.'
              },
              {
                text: "Click **Create with AI**."
              },
              {
                text: "Edit the result like any other form \u2014 the wizard gives you a starting point, not a finished thing."
              }
            ]
          }
        ]
      },
      {
        slug: "form-fields-and-consent",
        title: "Set the fields, branding and consent capture",
        why: "The consent section is the part that makes a form worth building rather than emailing a questionnaire.",
        route: "/forms-and-documents",
        requires: { permissions: ["forms:read"], features: ["forms_documents"] },
        estimatedMinutes: 6,
        keywords: [
          "form fields",
          "add a field",
          "consent checkbox on a form",
          "form branding",
          "form settings"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Forms & Documents", "Edit"] },
          {
            type: "steps",
            items: [
              {
                text: "Drag field blocks from the palette: **Text**, **Date**, **Time**, **Select**, **File Upload**, **Signature**.",
                note: "These ask the respondent for something and store an answer."
              },
              {
                text: "Drag content blocks for everything that only explains: paragraph, heading, list, image, table, divider, section header, page break.",
                note: "They collect nothing, and a section header plus a page break turns one intimidating page into three short ones."
              },
              {
                text: "Click a field to open its properties, and set the label, whether it is required, validation and help text."
              },
              {
                text: "Set the **Width** \u2014 full, half or third.",
                note: "Half and third-width fields sit side by side and stack back to full width on a phone, so First name and Last name take one line instead of two."
              },
              {
                text: "Open form settings and turn on consent capture for email, SMS, or both.",
                note: "Turn on only the channels this form is genuinely asking about."
              },
              {
                text: "Write the consent text your contacts will read.",
                note: "It is snapshotted with every submission as evidence, so it needs to say what you are actually asking for."
              }
            ]
          },
          {
            type: "note",
            variant: "compliance",
            text: "Consent given on a form replaces what the contact had before, and only for the channels the form actually shows. A form that shows email alone can never affect their SMS consent."
          }
        ]
      },
      {
        slug: "publish-and-share",
        title: "Publish and share it",
        why: "A form that is not published is invisible; the three distribution routes suit different situations.",
        route: "/forms-and-documents",
        requires: { permissions: ["forms:read"], features: ["forms_documents"] },
        estimatedMinutes: 4,
        keywords: [
          "publish a form",
          "share a form",
          "public url",
          "qr code",
          "embed a form",
          "form link"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Forms & Documents", "Publish", "Share Form"] },
          {
            type: "steps",
            items: [
              {
                text: "Click **Publish**.",
                note: "A form that is not published is invisible \u2014 the public address serves nothing."
              },
              {
                text: "Click **Share Form**."
              },
              {
                text: "For print, download the **QR code** as PNG or SVG.",
                note: "This is the one people underuse. On a counter, a leaflet or a van door it collects contacts and consent with no work from you at all."
              },
              {
                text: "For anywhere online, copy the public URL and paste it."
              },
              {
                text: "To send it to specific contacts, click **Open Send Wizard** and distribute by email or SMS."
              }
            ]
          }
        ]
      },
      {
        slug: "read-submissions",
        title: "Read the submissions",
        why: "There are two views \u2014 a table for the data and a dashboard for the pattern.",
        route: "/forms-and-documents",
        requires: { permissions: ["forms:read"], features: ["forms_documents"] },
        estimatedMinutes: 3,
        keywords: [
          "form submissions",
          "who filled out my form",
          "read responses",
          "export submissions",
          "submission data"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Forms & Documents", "View Submissions"] },
          {
            type: "steps",
            items: [
              {
                text: "For the data: click **View Submissions** on the form card.",
                note: "A searchable table with a date filter and CSV export."
              },
              {
                text: "For the pattern: open the form from the **Inbox** instead.",
                note: "The richer dashboard lives there \u2014 submission trends, per-field completion rates and answer distributions."
              },
              {
                text: "Read the completion rates before you read the answers.",
                note: "They tell you where people gave up, which is the thing you can actually fix."
              }
            ]
          }
        ]
      }
    ]
  },
  // ══════════════════════════════════════════════════════════════════════════════════════
  {
    slug: "documents",
    number: 9,
    title: "Documents and e-signature",
    summary: "Create agreements, send them for signature, and keep an audit trail that stands up.",
    icon: "FileSignature",
    steps: [
      {
        slug: "create-a-document",
        title: "Create an agreement",
        why: "Describing the agreement gets you a working draft in seconds; you keep the legal judgement.",
        route: "/forms-and-documents?filter=document",
        requires: { permissions: ["documents:read"], features: ["forms_documents"] },
        estimatedMinutes: 8,
        keywords: [
          "create an agreement",
          "new document",
          "contract",
          "upload a pdf",
          "make a document to sign"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Forms & Documents", "Create New", "Agreement"] },
          {
            type: "steps",
            items: [
              {
                text: "Open **Forms & Documents** and click **Create New**."
              },
              {
                text: "Choose **Agreement** \u2014 the thing you send to named people for e-signature."
              },
              {
                text: "Choose your starting point: **No, start from scratch** to describe what you need, or **Yes, I have an existing document** to upload a PDF."
              },
              {
                text: "If you described it, read the draft that comes back and accept it with **Use This Document**.",
                note: "It arrives with its signature and date fields already placed, which is most of the work."
              },
              {
                text: "If you uploaded, answer the one question that is hard to change later: **Keep as PDF and add fields**, or **Make it editable**.",
                note: "Keep it as a PDF when the wording belongs to someone else. Make it editable when the wording is yours."
              }
            ]
          },
          {
            type: "note",
            variant: "warning",
            text: "An AI-generated agreement is a starting draft. Have anything legally significant reviewed before you send it to a customer."
          }
        ]
      },
      {
        slug: "signers-and-fields",
        title: "Add signers and signing fields",
        why: "Assigning each field to a signer is what makes multi-party signing work in the right order.",
        route: "/forms-and-documents?filter=document",
        requires: { permissions: ["documents:read"], features: ["forms_documents"] },
        estimatedMinutes: 7,
        keywords: [
          "add signers",
          "signature field",
          "place fields",
          "multiple signers",
          "who signs where"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Forms & Documents", "Edit"] },
          {
            type: "steps",
            items: [
              {
                text: "Open the agreement in the editor and type the prose directly."
              },
              {
                text: "Type **/** to open the slash menu wherever a field belongs.",
                note: "It inserts signing fields \u2014 Signature, Text, Date, Time, Select, File Upload \u2014 and merge tokens: Date Signed, recipient tokens, your organisation tokens and your custom fields."
              },
              {
                text: "Add each party in the signers panel."
              },
              {
                text: "Click a field to open **Field Properties** and set its **assigned signer**.",
                note: "This is the step that makes multi-party signing work in the right order. Do it for every field."
              },
              {
                text: "Set the label, whether it is required, validation and width while you are in there."
              },
              {
                text: "Walk the document once before sending and check every field has an owner.",
                note: "A field left unassigned is a field nobody is asked to complete."
              }
            ]
          },
          {
            type: "note",
            variant: "tip",
            text: "In a multi-signer agreement each party sees earlier signatures and values read-only in place, and fields belonging to others are locked. Assign every field deliberately."
          }
        ]
      },
      {
        slug: "uploaded-pdfs",
        title: "Place fields on an uploaded PDF",
        why: "When the wording is fixed \u2014 a carrier form, a regulator's form \u2014 you overlay rather than rebuild.",
        route: "/forms-and-documents?filter=document",
        requires: { permissions: ["documents:read"], features: ["forms_documents"] },
        estimatedMinutes: 6,
        keywords: [
          "uploaded pdf",
          "place fields on a pdf",
          "pdf overlay",
          "existing pdf",
          "my own contract"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Forms & Documents", "Edit"] },
          {
            type: "steps",
            items: [
              {
                text: "Open a PDF-backed agreement. The editor shows the pages with a signers panel.",
                note: "Use this route when the wording is fixed \u2014 a carrier form, a regulator's form \u2014 and you overlay rather than rebuild."
              },
              {
                text: "Drag signature and text fields onto each page where they belong."
              },
              {
                text: "Check placement with **Preview**, which opens the document read-only.",
                note: "An **Edit** button switches back. It is the safe way to check placement without nudging a field out of position."
              },
              {
                text: "Note how this editor saves: about every 30 seconds, automatically.",
                note: "The newer full-screen editor does NOT \u2014 it saves only when you press **Save**. Do not assume one behaves like the other."
              }
            ]
          }
        ]
      },
      {
        slug: "send-for-signature",
        title: "Send it for signature",
        why: "Signature sends run through the same wizard and the same consent checks as everything else.",
        route: "/send",
        requires: { permissions: ["documents:send"], features: ["send", "forms_documents"] },
        estimatedMinutes: 5,
        keywords: [
          "send for signature",
          "how do i send a document for signature",
          "request a signature",
          "esign",
          "get something signed"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Send", "Request a Signature"] },
          {
            type: "steps",
            items: [
              {
                text: "Click the **Send** icon on the document card, or use **Request a Signature** on the Send screen.",
                note: "Both land in the same wizard, running the same consent checks as everything else."
              },
              {
                text: "Pick the document."
              },
              {
                text: "Set the delivery channels the invitation goes out on."
              },
              {
                text: "On the recipients step, pick your contacts \u2014 or, for a multi-signer agreement, assign one contact per role.",
                note: "The step changes to **Assign signers** on its own when the document has more than one signer."
              },
              {
                text: "Continue to review, then send or schedule."
              }
            ]
          }
        ]
      },
      {
        slug: "signer-experience",
        title: "Know what your signer sees",
        why: "You will be asked. Being able to answer without guessing saves a support call every time.",
        estimatedMinutes: 4,
        keywords: [
          "what does the signer see",
          "signing experience",
          "signer portal",
          "recipient view"
        ],
        blocks: [
          {
            type: "steps",
            items: [
              {
                text: "Your signer opens the link. There is no login.",
                note: "You will be asked about this. Being able to answer without guessing saves a support call every time."
              },
              {
                text: "They confirm their identity and agree to the terms."
              },
              {
                text: "The document renders with clickable fields, a required-field count and a **Sign next field** button.",
                note: "They cannot miss a required field \u2014 the count tracks it."
              },
              {
                text: "They provide a signature: drawn, typed in one of four fonts, or uploaded as an image."
              },
              {
                text: "They click **Complete Signing** \u2014 or **Decline to Sign**, which asks for a reason."
              },
              {
                text: "They see a confirmation and get an email with the signed document attached."
              }
            ]
          }
        ]
      },
      {
        slug: "verify-and-audit",
        title: "Verify a signed PDF and pull the audit trail",
        why: "This is what you produce when someone disputes a signature.",
        route: "/settings/verify-signature",
        estimatedMinutes: 3,
        keywords: [
          "verify a signed pdf",
          "audit trail",
          "proof of signature",
          "is this signature valid",
          "certificate"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Settings", "Verify PDF Signature"] },
          {
            type: "steps",
            items: [
              {
                text: "Open **Settings**, then **Verify PDF Signature**."
              },
              {
                text: "Drop the signed PDF in."
              },
              {
                text: "Read the result: the signer details, the certificate information and an integrity result.",
                note: "This is what you produce when someone disputes a signature."
              },
              {
                text: "For the fuller picture, open the document campaign in the **Inbox** and click **Download Audit Trail PDF**.",
                note: "It is generated from the record rather than stored as a file, so it is current at the moment you download it."
              }
            ]
          }
        ]
      }
    ]
  },
  // ══════════════════════════════════════════════════════════════════════════════════════
  {
    slug: "send",
    number: 10,
    title: "Send",
    summary: "The one flow that puts a message, a form or a document in front of people \u2014 and the checks it runs first.",
    icon: "Send",
    steps: [
      {
        slug: "the-send-screen",
        title: "Start every send in one place",
        why: "There is exactly one compose screen; every entry point in the app lands here.",
        route: "/send",
        requires: { permissions: ["campaigns:send"], features: ["send"] },
        estimatedMinutes: 3,
        keywords: [
          "send a message",
          "how do i send",
          "new send",
          "start a campaign",
          "broadcast",
          "send screen"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Send"] },
          {
            type: "steps",
            items: [
              {
                text: "Click **Send** in the sidebar.",
                note: "Every entry point in the app lands here \u2014 the floating button, the inbox, the dashboard, and the Send actions on a contact."
              },
              {
                text: "Describe the message in the prompt box."
              },
              {
                text: "Set the **Tone** dropdown beside it.",
                note: "Professional, Friendly or Casual, with Professional as the default."
              },
              {
                text: "Turn on the channel pills you want \u2014 **SMS**, **Email**, **Voice**.",
                note: "Email is on by default. Turn it off if you only want a text."
              },
              {
                text: "Or skip the prompt entirely with a shortcut: **Request a Signature**, **Send a Form** or **Templates**.",
                note: "Each opens a different branch of the wizard rather than drafting a message."
              },
              {
                text: "Use the **+** button to @-mention an existing template, document, form, tag or pipeline stage.",
                note: 'What you mention decides the branch \u2014 see "Attach a template, form or document to the prompt".'
              },
              {
                text: "Click **Generate Draft**.",
                note: 'Arriving from Contacts with rows ticked carries them here as a "To:" chip, so recipients are already set.'
              }
            ]
          },
          {
            type: "note",
            variant: "tip",
            text: "Drafts auto-save. If you walk away mid-send, the **Draft Campaigns** panel on this screen offers it back \u2014 but only on this screen, never from inside the wizard."
          },
          { type: "action", label: "Open Send", to: { kind: "app", route: "/send" } }
        ]
      },
      {
        slug: "generate-and-edit",
        title: "Generate the draft and edit per channel",
        why: "Each channel gets its own card, so the SMS is not just a truncated email.",
        route: "/send",
        requires: { permissions: ["campaigns:send"], features: ["send"] },
        estimatedMinutes: 5,
        keywords: [
          "generate a draft",
          "edit my message",
          "ai draft",
          "change the wording",
          "per channel content",
          "compose content"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Send", "Generate Draft"] },
          {
            type: "steps",
            items: [
              {
                text: 'Click **Generate Draft**. You land on "Customize your content", one card per enabled channel.',
                note: "Each channel gets its own card, so the SMS is not just a truncated email."
              },
              {
                text: "Set each card to **Template**, **Compose** or **AI**.",
                note: "Template loads a saved template into the card, Compose is hand-written, AI is the generated draft."
              },
              {
                text: "Click **Edit** on a card to open the inline editor beside its live preview."
              },
              {
                text: "Use the **Insert Variable** row rather than typing merge tags by hand."
              },
              {
                text: "Read the compliance strip under each card before moving on.",
                note: "It shows the message type \u2014 **Marketing**, **Informational** or **Transactional** \u2014 and whether the content passes. That classification is what decides who is eligible two steps later, so check it rather than skimming it."
              },
              {
                text: "Continue to **Select Recipients** once every card has content."
              }
            ]
          }
        ]
      },
      {
        slug: "recipients-and-eligibility",
        title: "Choose recipients and read the eligibility flags",
        why: "This screen tells you, per contact, exactly why someone will not receive it.",
        route: "/send",
        requires: { permissions: ["campaigns:send"], features: ["send"] },
        estimatedMinutes: 5,
        keywords: [
          "choose recipients",
          "who am i sending to",
          "eligibility",
          "why is a contact excluded",
          "select contacts",
          "not deliverable"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Send", "Select Recipients"] },
          {
            type: "steps",
            items: [
              {
                text: "Pick your recipients \u2014 individually, or by the tag or pipeline stage you mentioned earlier."
              },
              {
                text: "Read the eligibility indicator on each row.",
                note: 'It says "OK to send", or names the exact problem: "Missing phone for SMS & Voice", "No consent for SMS & Email".'
              },
              {
                text: "Read the consent badges beside it for the detail behind that verdict.",
                note: "This screen tells you, per contact, exactly why someone will not receive the send."
              },
              {
                text: "Check the summary counts \u2014 selected, visible, hidden.",
                note: "A large hidden count usually means a filter is still applied from wherever you came in."
              },
              {
                text: "Continue to review.",
                note: "**Review Campaign** stays disabled until at least one selected contact can actually receive the send. An amber note under the list says why \u2014 nothing selected yet, or everyone selected is blocked."
              }
            ]
          },
          {
            type: "note",
            variant: "tip",
            text: 'Consistently seeing "No consent for marketing" is not a bug \u2014 it is the auto-grant block. Marketing consent is never granted for you. Send those contacts your intake form and let them opt in themselves.'
          }
        ]
      },
      {
        slug: "review-and-send",
        title: "Review, then send or schedule",
        why: "The review screen is the last place the numbers are explained before anything leaves.",
        route: "/send",
        requires: { permissions: ["campaigns:send"], features: ["send"] },
        estimatedMinutes: 5,
        keywords: [
          "review and send",
          "send now",
          "schedule a campaign",
          "send later",
          "final check",
          "how do i schedule"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Send", "Review & Send"] },
          {
            type: "steps",
            items: [
              {
                text: "Name the campaign.",
                note: "Leave it and one is generated for you from the form, document or template name plus the date."
              },
              {
                text: "Check the three summary rows \u2014 content, recipients and delivery."
              },
              {
                text: "Read **DELIVERY ELIGIBILITY** carefully. This is the number that matters.",
                note: "It breaks down per channel and purpose, and names each excluded contact and why."
              },
              {
                text: "Read **WHEN TO SEND** separately. These are sending groups, split by calling-hour rules.",
                note: "Each group is sent now, scheduled by you, or deferred automatically until its window opens. Expand a group to give it a specific time."
              },
              {
                text: "Click **Send Now**, or **Schedule Campaign** if you set a time.",
                note: 'The button label tells you which one you are about to do. Both stay disabled, with a "Nothing to send yet." alert, if no selected recipient can receive the send.'
              }
            ]
          },
          {
            type: "note",
            variant: "warning",
            text: "Sending groups are about timing only \u2014 they do not check consent. Reading a sending-group count as an eligibility number is the easiest mistake to make on this screen. DELIVERY ELIGIBILITY is the number that tells you who will actually receive it."
          }
        ]
      }
    ]
  },
  // ══════════════════════════════════════════════════════════════════════════════════════
  {
    slug: "inbox",
    number: 11,
    title: "Inbox",
    summary: "Everything that happened, in one stream \u2014 and the place replies come back to.",
    icon: "Inbox",
    steps: [
      {
        slug: "unified-inbox",
        title: "Work the unified inbox",
        why: "Campaigns, conversations, forms, documents and consent events all land here.",
        route: "/messaging",
        requires: { permissions: ["inbox:read"], features: ["messaging"] },
        estimatedMinutes: 4,
        keywords: [
          "inbox",
          "where are my replies",
          "unified inbox",
          "incoming messages",
          "filter the inbox"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Inbox"] },
          {
            type: "steps",
            items: [
              {
                text: "Click **Inbox** in the sidebar.",
                note: "Campaigns, conversations, forms, documents and consent events all land in the same stream."
              },
              {
                text: "Click a filter pill to narrow it: **All**, **Campaigns**, **Documents**, **Forms**, **SMS**, **Email**, **Voice**, **Website Chat**, **Opt-Out**, **Opt-In**.",
                note: "Each carries a live count that respects your search and date filter."
              },
              {
                text: 'Type into "Search messages..." to narrow further \u2014 every pill count re-tallies against it.'
              },
              {
                text: "Tick rows to reveal the bulk bar: **Archive**, **Read**, **Unread**, **Resend**, **Export**, **Delete**."
              },
              {
                text: "Read the status badges on a row before opening it.",
                note: "**Scheduled** with a countdown means a campaign is held for calling hours and will send itself. A red **Blocked** badge means it will not. **Needs Review** means something else wants your attention."
              }
            ]
          },
          { type: "action", label: "Open the Inbox", to: { kind: "app", route: "/messaging" } }
        ]
      },
      {
        slug: "conversations",
        title: "Reply to conversations",
        why: "Inbound SMS and email are two-way here, and replies are still consent-checked.",
        route: "/messaging",
        requires: { permissions: ["inbox:read"], features: ["messaging"] },
        estimatedMinutes: 4,
        keywords: [
          "reply to a contact",
          "conversation",
          "respond to a text",
          "message thread",
          "answer a reply"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Inbox", "SMS or Email", "the conversation"] },
          {
            type: "steps",
            items: [
              {
                text: "Click the **SMS** or **Email** pill, then open a thread."
              },
              {
                text: "Read the thread. Messages are grouped by day, with delivery and read receipts.",
                note: "The contact panel on the right carries consent and assignment."
              },
              {
                text: "Check for an amber banner before you type.",
                note: "It appears when the contact has not opted in for that channel \u2014 the reply may be refused."
              },
              {
                text: "Type in the composer, or click **Suggest reply** to draft one from the thread.",
                note: "Text conversations show a live segment counter and quick replies; email conversations have an attachment button."
              },
              {
                text: "Send."
              }
            ]
          },
          {
            type: "note",
            variant: "tip",
            text: "When the amber banner appears, fix the consent on the contact rather than retrying the send. Retrying a blocked reply just produces another blocked reply."
          }
        ]
      },
      {
        slug: "campaign-dashboards",
        title: "Read a campaign dashboard",
        why: "It tells you, in plain words, who got your message, who didn\u2019t, and why.",
        route: "/messaging",
        requires: { permissions: ["inbox:read"], features: ["messaging"] },
        estimatedMinutes: 4,
        keywords: [
          "campaign results",
          "delivery report",
          "did my message send",
          "open rate",
          "campaign dashboard",
          "failed deliveries",
          "who did not get my message",
          "why didn't my text go out",
          "blocked by compliance"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Inbox", "Campaigns", "the campaign"] },
          {
            type: "steps",
            items: [
              {
                text: "Click the **Campaigns** pill and open the campaign."
              },
              {
                text: "Read **The story so far** at the top.",
                note: "A few plain sentences: how many it went to, how many got it, how many didn\u2019t go out and the main reason, and what is still waiting. Click any coloured number to see exactly those people."
              },
              {
                text: "Follow the path: **Coming up**, **Sent**, **Delivered** \u2014 and **Signed** or **Filled in** for documents and forms.",
                note: "Below it, chips show what **didn\u2019t go out**, what was sent but **couldn\u2019t be delivered**, and what is **on hold**."
              },
              {
                text: "Read **Why some messages didn\u2019t arrive** for the reasons, biggest first, each with what you can do."
              },
              {
                text: "Scroll to **Everyone on this campaign** and click a person.",
                note: "The side panel shows what happened to their message and why \u2014 for a compliance block, the exact requirement that wasn\u2019t met and what was on file for them."
              },
              {
                text: "Open **What you sent** at the bottom for the message itself, the campaign details and its compliance score."
              },
              {
                text: "Use the **Actions** menu for **Pause**, **Resume**, **Cancel**, **Duplicate** or **Resend to Failed**.",
                note: "Only the actions that apply to the current status appear."
              }
            ]
          }
        ]
      },
      {
        slug: "handle-opt-outs",
        title: "Watch the opt-out filter",
        why: "A rising opt-out rate is the earliest signal that your sending is off, and it is legally material.",
        route: "/messaging?filter=opted_out",
        requires: { permissions: ["inbox:read"], features: ["messaging"] },
        estimatedMinutes: 2,
        keywords: ["someone opted out", "unsubscribe handling", "opt out filter", "stop replies"],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Inbox", "Opt-Out"] },
          {
            type: "steps",
            items: [
              {
                text: "Click the **Opt-Out** pill.",
                note: "It lists every consent withdrawal with its source."
              },
              {
                text: "Read them. There is nothing to process \u2014 opt-outs are applied automatically.",
                note: "The reading is the job: a rising opt-out rate is the earliest signal that your sending is off, and it is legally material."
              },
              {
                text: "Check the **Recent Opt-Outs** widget on the dashboard for the same signal without leaving the overview."
              },
              {
                text: "When a source keeps recurring, go and change the sending behind it.",
                note: "Opt-outs cluster around one campaign, one list or one frequency far more often than they spread evenly."
              }
            ]
          },
          {
            type: "action",
            label: "Open opt-outs",
            to: { kind: "app", route: "/messaging?filter=opted_out" }
          }
        ]
      }
    ]
  },
  // ══════════════════════════════════════════════════════════════════════════════════════
  {
    slug: "automation",
    number: 12,
    title: "Automation",
    summary: "Workflows react to things that happen; sequences run planned campaigns over time.",
    icon: "Zap",
    steps: [
      {
        slug: "workflows-overview",
        kind: "concept",
        title: "Understand workflows",
        why: "A trigger plus steps is the whole model \u2014 once that lands, the builder is straightforward.",
        route: "/workflows",
        requires: { features: ["workflows"] },
        estimatedMinutes: 3,
        keywords: [
          "workflows",
          "automation",
          "automate",
          "what is a workflow",
          "run something automatically"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Workflows"] },
          {
            type: "paragraph",
            text: "A workflow is a starting event that enrols contacts, then a sequence of steps they move through. The canonical example: a new contact is added, send a welcome text, wait two days, send a follow-up email."
          }
        ]
      },
      {
        slug: "triggers",
        title: "Pick a starting event",
        why: "Choosing the right trigger is most of the design work.",
        route: "/workflows/new",
        requires: { features: ["workflows"] },
        estimatedMinutes: 4,
        keywords: ["trigger", "what starts a workflow", "when this happens", "workflow events"],
        blocks: [
          {
            type: "clickPath",
            path: ["Sidebar", "Workflows", "Create Workflow", "Starting event"]
          },
          {
            type: "steps",
            items: [
              {
                text: "Open **Workflows** and click **Create Workflow**."
              },
              {
                text: "Name it in the header. It starts as **Untitled Workflow**.",
                note: "Do this now \u2014 an untitled workflow is unfindable in three weeks."
              },
              {
                text: "On the **Starting event** tab, pick the category that matches what you are reacting to.",
                note: "**Contacts**, **Forms & Documents**, **Messages**, or **Manual**."
              },
              {
                text: "Choose the specific event within it.",
                note: "Contacts: a new contact is added; agrees to receive messages; unsubscribes; is added to a group; moves to a new stage. Forms & Documents: fills out a form; a document is signed, declined or opened. Messages: a campaign finishes; a message is delivered; an email is opened; a link is clicked; a contact replies. Manual: you enrol contacts yourself."
              },
              {
                text: "Narrow it in the config panel on the right.",
                note: "A specific form rather than all forms, one group, one stage, one document. This is where most misconfigured workflows go wrong."
              }
            ]
          },
          {
            type: "note",
            variant: "tip",
            text: "Choosing the right trigger is most of the design work. A workflow that enrols nobody is almost always a trigger problem, not a step problem."
          }
        ]
      },
      {
        slug: "steps-and-branches",
        title: "Add steps and branches",
        why: "The condition step is what turns a linear reminder into something that actually reacts.",
        route: "/workflows/new",
        requires: { features: ["workflows"] },
        estimatedMinutes: 7,
        keywords: [
          "workflow steps",
          "actions",
          "branch",
          "condition",
          "wait step",
          "build a workflow"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Workflows", "Create Workflow", "Add a step"] },
          {
            type: "steps",
            items: [
              {
                text: "Switch to the **Add a step** tab."
              },
              {
                text: "Pick a category: **Messages**, **Timing**, **Logic**, **Organize** or **Advanced**.",
                note: "Messages send a text, an email, a document to sign, or a form to fill out. Timing waits. Logic branches or ends. Organize adds and removes labels, groups and stages, or updates contact information. Advanced notifies another system by webhook."
              },
              {
                text: "Put a **Wait Before Continuing** between any two message steps.",
                note: "A workflow with no waits sends everything within seconds of the trigger, which reads as spam."
              },
              {
                text: "Add **Check a Condition** where the workflow should react rather than just proceed.",
                note: "Its config panel opens with one-tap common checks \u2014 signed a document, opted into SMS, opened my email, submitted a form, is at a stage, has a tag \u2014 plus a search for everything else."
              },
              {
                text: "Add a second condition to reveal the **All / Any** toggle.",
                note: "A plain-English preview restates the whole rule so you can check it reads the way you meant."
              },
              {
                text: "Hang steps off the green **If YES** and red **If NO** arms."
              }
            ]
          },
          {
            type: "note",
            variant: "tip",
            text: 'To chase someone who has NOT done something, do not look for a "has not" check. Put a "has signed this document" check in and hang the reminder off the NO arm.'
          }
        ]
      },
      {
        slug: "activate-and-monitor",
        title: "Activate it and watch the numbers",
        why: "A workflow saved as a draft does nothing at all, which catches people out.",
        route: "/workflows",
        requires: { features: ["workflows"] },
        estimatedMinutes: 3,
        keywords: [
          "activate a workflow",
          "turn on automation",
          "workflow stats",
          "is my workflow running",
          "pause a workflow"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Workflows", "Activate"] },
          {
            type: "steps",
            items: [
              {
                text: "Click **Activate** in the builder header.",
                note: "A workflow saved as a draft does nothing at all. This catches people out more than anything else in the chapter."
              },
              {
                text: "Open the workflow page and read the four tiles: **Total Contacts**, **Currently Active**, **Completed**, **Dropped Out** with an exit rate."
              },
              {
                text: "Read **Your workflow at a glance** at the top: every step shows its result underneath.",
                note: "Amber or red under a step is the thing to look at."
              },
              {
                text: "Click a step to see everyone who went through it and what happened to each of them."
              }
            ]
          }
        ]
      },
      {
        slug: "sequences",
        title: "Run a sequence",
        why: "When you want a planned drip rather than a reaction, this is the tool.",
        route: "/sequences",
        requires: { features: ["workflows"] },
        estimatedMinutes: 4,
        keywords: [
          "sequence",
          "drip campaign",
          "follow up series",
          "multi step campaign",
          "nurture"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Sequences"] },
          {
            type: "steps",
            items: [
              {
                text: "Open **Sequences** in the sidebar when you want a planned drip rather than a reaction.",
                note: "Same engine, simplified wizard."
              },
              {
                text: "Build the linear set of sends in the four-step wizard: Details, Audience, Messages, Review."
              },
              {
                text: "Enrol contacts deliberately rather than waiting for a trigger.",
                note: "That is the whole difference from a workflow."
              },
              {
                text: "Check the eligibility numbers rather than assuming a fault when fewer people receive it than you enrolled."
              }
            ]
          },
          {
            type: "note",
            variant: "compliance",
            text: "Sequences are usually marketing, so every recipient needs marketing consent on that channel. Expect enrolment counts to be lower than your contact count."
          }
        ]
      },
      {
        slug: "your-kit-workflows",
        title: "Read the workflows your kit installed",
        why: "They are already running. Knowing what they do is not optional.",
        route: "/workflows",
        requires: { features: ["workflows"] },
        estimatedMinutes: 4,
        keywords: ["kit workflows", "prebuilt automations", "what workflows do i have"],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Workflows"] },
          {
            type: "steps",
            items: [
              {
                text: "Open **Workflows** straight after applying a starter kit.",
                note: "The kit created and activated them immediately. They are already running."
              },
              {
                text: "Open each one and read it, trigger and steps.",
                note: "Knowing what is messaging your contacts on your behalf is not optional."
              },
              {
                text: "Edit, pause or delete any of them.",
                note: "They are ordinary workflows with no special status."
              }
            ]
          }
        ]
      }
    ]
  },
  // ══════════════════════════════════════════════════════════════════════════════════════
  {
    slug: "dashboard",
    number: 13,
    title: "Dashboard and reporting",
    summary: "Make the home screen show the handful of numbers you actually act on.",
    icon: "LayoutDashboard",
    steps: [
      {
        slug: "customize-widgets",
        title: "Customise your widgets",
        why: "The default layout is generic; five minutes here makes the home screen genuinely useful.",
        route: "/",
        estimatedMinutes: 4,
        keywords: [
          "customise the dashboard",
          "customize dashboard",
          "move widgets",
          "hide a widget",
          "rearrange dashboard"
        ],
        blocks: [
          { type: "clickPath", path: ["Dashboard", "Customize"] },
          {
            type: "steps",
            items: [
              {
                text: "Click **Customize** on the dashboard. The grid switches into edit mode."
              },
              {
                text: "Drag widgets into the order you want."
              },
              {
                text: "Hide the ones you never act on.",
                note: "They go into a **Hidden** panel you can restore from later, not away for good."
              },
              {
                text: "Switch the density between **comfortable** and **compact**.",
                note: "Compact fits noticeably more onto one screen."
              },
              {
                text: "Press **Done**.",
                note: "The layout is saved per user, so this changes only your own dashboard."
              },
              {
                text: "If you make a mess of it, press **Reset**.",
                note: "It puts every widget back, and asks you to confirm first."
              }
            ]
          }
        ]
      },
      {
        slug: "widgets-to-watch",
        title: "The widgets worth watching daily",
        why: "Four of the twenty tell you whether something needs your attention today.",
        route: "/",
        estimatedMinutes: 3,
        keywords: [
          "what should i check daily",
          "important widgets",
          "dashboard overview",
          "daily routine"
        ],
        blocks: [
          {
            type: "steps",
            items: [
              {
                text: "Put these four at the top: **Outstanding Signatures**, **Unread Inbox**, **Failed Deliveries (24h)**, **Compliance Attention**.",
                note: "Four of the twenty widgets tell you whether something needs your attention today. These are them."
              },
              {
                text: "Check **Outstanding Signatures** \u2014 agreements sent and not yet signed, oldest first.",
                note: "The oldest row is usually the one to chase."
              },
              {
                text: "Check **Unread Inbox** \u2014 replies waiting on a human."
              },
              {
                text: "Check **Failed Deliveries (24h)**.",
                note: "Usually a bad number or a bounced address."
              },
              {
                text: "Check **Compliance Attention** \u2014 sends blocked or held, and consent that needs capturing."
              },
              {
                text: "Click any row to drill through to the record it describes.",
                note: "The rows are links, not just counts."
              }
            ]
          },
          {
            type: "note",
            variant: "tip",
            text: "The rest are for looking back rather than acting today: Form Submission Trend, Average Turnaround Time, Document Signature Funnel, Consent Status, Recent Opt-Outs, Plan & Usage, and Quick Actions."
          }
        ]
      },
      {
        slug: "exports",
        title: "Get the data out",
        why: "Auditors, accountants and your own spreadsheets all want a file, not a screenshot.",
        estimatedMinutes: 2,
        keywords: [
          "export data",
          "download a report",
          "get my data out",
          "csv export",
          "reporting"
        ],
        blocks: [
          {
            type: "steps",
            items: [
              {
                text: "For contacts: **Export** on the contact list, scoped to your selection or filter."
              },
              {
                text: "For form submissions: the CSV export from the submissions table."
              },
              {
                text: "For one contact's consent history: **Log PDF** on the contact."
              },
              {
                text: "For compliance decisions: **Export CSV** from **Settings**, **Policies**, **Decisions**."
              },
              {
                text: "For signature evidence: **Download Audit Trail PDF** on a document campaign."
              },
              {
                text: "For invoices: **Download** per row in Billing history."
              }
            ]
          },
          {
            type: "note",
            variant: "tip",
            text: "Auditors, accountants and your own spreadsheets all want a file, not a screenshot. Every one of these is generated live, so take it when you need it."
          }
        ]
      }
    ]
  },
  // ══════════════════════════════════════════════════════════════════════════════════════
  {
    slug: "team-billing",
    number: 14,
    title: "Team and billing",
    summary: "Bring colleagues in with the right access, and keep the account funded.",
    icon: "CreditCard",
    steps: [
      {
        slug: "invite-your-team",
        title: "Invite your team",
        why: "Shared logins destroy your audit trail \u2014 every consent record names an actor.",
        route: "/settings/team",
        requires: { permissions: ["team:read"] },
        estimatedMinutes: 3,
        keywords: [
          "invite a teammate",
          "add a user",
          "invite member",
          "how do i add someone to my account",
          "seats"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Settings", "Team Members"] },
          {
            type: "steps",
            items: [
              {
                text: "Decide who the owner is before you invite anyone.",
                note: "The owner cannot be removed, deactivated or role-changed afterwards."
              },
              {
                text: "Open **Settings**, then **Team Members**, and click **Invite Member**."
              },
              {
                text: "Enter the email address and pick a role."
              },
              {
                text: "Check the seat banner.",
                note: "It shows how many of your plan's seats are used, so you find out you are out of seats now rather than on the third invitation."
              },
              {
                text: "Send it. The invitation appears under **Pending Invitations**, where it can be resent or revoked."
              }
            ]
          },
          {
            type: "note",
            variant: "warning",
            text: "Shared logins destroy your audit trail \u2014 every consent record names an actor. One person, one login, always."
          }
        ]
      },
      {
        slug: "roles-and-permissions",
        title: "Set up roles",
        why: "Most people need much less access than the default, and the editor makes that easy to arrange.",
        route: "/settings/roles",
        requires: { permissions: ["roles:read"] },
        estimatedMinutes: 5,
        keywords: [
          "roles",
          "permissions",
          "restrict access",
          "custom role",
          "what can a member see"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Settings", "Roles & Permissions"] },
          {
            type: "steps",
            items: [
              {
                text: "Open **Settings**, then **Roles & Permissions**.",
                note: "Look at the existing roles before creating one \u2014 adjusting an existing role is usually less work."
              },
              {
                text: "Click **Create Role**."
              },
              {
                text: "Give it a name, a description and a colour."
              },
              {
                text: "Tick the permissions in the matrix, grouped by area.",
                note: "Contacts, Documents, Forms, Campaigns, Inbox, Templates, Analytics, Team, Roles, Settings, Billing, Webhooks and AI."
              },
              {
                text: "Start from nothing and tick what the job needs.",
                note: "Most people need much less access than the default."
              },
              {
                text: "Save."
              }
            ]
          },
          {
            type: "note",
            variant: "tip",
            text: "System roles cannot be deleted or renamed, but you can edit what they may do."
          }
        ]
      },
      {
        slug: "billing",
        title: "Manage your plan and payment",
        why: "Everything billing-related is one page with four tabs.",
        route: "/billing",
        requires: { permissions: ["billing:read"] },
        estimatedMinutes: 4,
        keywords: [
          "billing",
          "my plan",
          "invoices",
          "change plan",
          "payment method",
          "subscription"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Settings", "Billing"] },
          {
            type: "steps",
            items: [
              {
                text: "Open **Billing**. Everything is on this one page, across four tabs."
              },
              {
                text: "Start on **Overview** \u2014 your plan, current period, next billing date and status.",
                note: "Below it, usage meters for SMS, emails, voice minutes, documents, form submissions, contacts, AI credits and seats."
              },
              {
                text: "Use **Plans** to change tier.",
                note: "Upgrades are prorated and immediate; downgrades take effect at the end of the period."
              },
              {
                text: "Use **Add-ons** to buy extra capacity without moving plan."
              },
              {
                text: "Use **Payment** to manage the cards on file."
              }
            ]
          }
        ]
      },
      {
        slug: "trial-and-card",
        title: "Understand your trial and the card gate",
        why: "A trial that lapses locks every page, and the warning signs are easy to miss.",
        route: "/billing",
        requires: { permissions: ["billing:read"] },
        estimatedMinutes: 2,
        keywords: [
          "trial",
          "free trial",
          "how many days left in my trial",
          "card gate",
          "add a card",
          "payment method",
          "why is the app locked",
          "pending card"
        ],
        blocks: [
          { type: "clickPath", path: ["Top bar", "days left in trial", "Billing", "Payment"] },
          {
            type: "steps",
            items: [
              {
                text: "Watch the countdown chip in the top bar.",
                note: 'It reads "{N} day(s) left in trial", or "Trial ends today" on the last day. On a wide screen it appends "\xB7 Add billing".'
              },
              {
                text: "Click it to go straight to Billing."
              },
              {
                text: "Add a card on the **Payment** tab.",
                note: "Adding one does not shorten the trial \u2014 the remaining days are kept, and the card is only charged when the trial ends. Adding it early simply removes the gate."
              }
            ]
          },
          {
            type: "note",
            variant: "warning",
            text: "If the trial lapses without a card, the app stops letting you open its pages and sends you back to the plan step until a card is on file. Documentation stays reachable throughout, so you can still read this page."
          },
          { type: "action", label: "Open Billing", to: { kind: "app", route: "/billing" } }
        ]
      },
      {
        slug: "usage-and-limits",
        title: "Stay ahead of your limits",
        why: "Hitting a hard limit mid-campaign is avoidable if you watch the meters.",
        route: "/billing",
        requires: { permissions: ["billing:read"] },
        estimatedMinutes: 2,
        keywords: [
          "usage",
          "limits",
          "how many messages left",
          "quota",
          "ran out of credits",
          "plan usage"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Settings", "Billing", "Overview"] },
          {
            type: "steps",
            items: [
              {
                text: "Watch the **Plan & Usage** widget on the dashboard.",
                note: "It mirrors the billing meters, so you do not have to go looking."
              },
              {
                text: "Open **Billing**, **Overview** for the full set of meters."
              },
              {
                text: "Act on a meter approaching its limit BEFORE a campaign, not during one.",
                note: "Hitting a hard limit mid-campaign is avoidable."
              },
              {
                text: "When you do hit one, take the next tier or the add-on the modal offers.",
                note: "A soft limit instead warns that overage charges apply and lets you continue."
              }
            ]
          }
        ]
      }
    ]
  },
  // ══════════════════════════════════════════════════════════════════════════════════════
  {
    slug: "getting-help",
    number: 15,
    title: "Getting help",
    summary: "The assistant, the support queue, and what to do next.",
    icon: "LifeBuoy",
    steps: [
      {
        slug: "cowork",
        title: "Ask CoWork to do it for you",
        why: "It builds real things in your account, so it is faster than clicking for most one-off jobs.",
        requires: { features: ["cowork"] },
        estimatedMinutes: 3,
        keywords: ["cowork", "ai assistant", "ask the assistant", "do it for me", "chat with ai"],
        blocks: [
          {
            type: "steps",
            items: [
              {
                text: "Open CoWork from anywhere in the app.",
                note: "It is available on every screen, not a page you navigate to."
              },
              {
                text: "Ask in plain English for what you want built.",
                note: "Draft a template, build a form, create a workflow, find contacts. It does the work rather than telling you where the button is."
              },
              {
                text: "Read the confirmation card before you approve it.",
                note: "Anything that creates or sends stops and shows you exactly what it is about to do first."
              }
            ]
          },
          {
            type: "note",
            variant: "tip",
            text: "For most one-off jobs this is faster than clicking through the screens yourself."
          }
        ]
      },
      {
        slug: "cowork-answers-questions",
        title: "Ask CoWork how something works",
        why: "It answers from this manual and links you to the article, instead of guessing.",
        requires: { features: ["cowork"] },
        estimatedMinutes: 2,
        keywords: [
          "ask cowork a question",
          "how do i",
          "cowork help",
          "does cowork know how the app works",
          "cowork article links",
          "will cowork change things if i ask a question",
          "ask instead of searching the docs"
        ],
        blocks: [
          {
            type: "steps",
            items: [
              {
                text: "Open CoWork and ask your question the way you would ask a colleague.",
                note: '"How do I import contacts from a CSV?" works as well as any search term.'
              },
              {
                text: "Read the answer, then open the article cards underneath it.",
                note: "Those cards are the pages in this manual the answer came from. Clicking one opens it here in the app."
              },
              {
                text: "Say the word if you would rather CoWork just did it.",
                note: "It only offers when it genuinely can. For screens it cannot touch \u2014 billing, most settings \u2014 it explains the steps and leaves them to you."
              }
            ]
          },
          {
            type: "note",
            variant: "tip",
            text: "Asking a question never changes anything in your account. If a question could be read as an instruction, CoWork shows you a confirmation card rather than acting on it."
          },
          {
            type: "note",
            variant: "warning",
            text: "If this manual does not cover something, CoWork says so and offers to put you in touch with a person. It will not invent an answer."
          },
          { type: "docRef", label: "Reach a person instead", ref: "getting-help/support" }
        ]
      },
      {
        slug: "cowork-talk-to-a-person",
        title: "Get a person from inside CoWork",
        why: "It raises a ticket with your conversation attached, so you do not have to explain it twice.",
        requires: { features: ["cowork"] },
        estimatedMinutes: 2,
        keywords: [
          "talk to a person",
          "talk to a human",
          "speak to someone",
          "live agent",
          "cowork cannot help",
          "escalate to support",
          "get help from a person in cowork"
        ],
        blocks: [
          {
            type: "steps",
            items: [
              {
                text: "Click **Talk to a person** under the CoWork message box.",
                note: "It is always there \u2014 you do not have to ask the assistant first."
              },
              {
                text: "Pick what it is about, and add anything you want the team to know.",
                note: "Both are optional. Your recent conversation is attached either way."
              },
              {
                text: "Click **Raise a ticket**."
              },
              {
                text: "Follow the link to your ticket, and read replies there.",
                note: "The thread lives in **Support** in the sidebar, the same as any other ticket."
              }
            ]
          },
          {
            type: "note",
            variant: "warning",
            text: "Nobody joins the CoWork chat. This raises a support ticket and the team replies in your Support inbox \u2014 asking again within a day adds to the same ticket rather than opening a second one."
          },
          { type: "docRef", label: "How support tickets work", ref: "getting-help/support" }
        ]
      },
      {
        slug: "support",
        title: "Raise a support ticket",
        why: "It is also how you request a dedicated phone number, which has no self-serve route.",
        route: "/support",
        estimatedMinutes: 2,
        keywords: [
          "support",
          "contact support",
          "raise a ticket",
          "get help from a person",
          "report a problem"
        ],
        blocks: [
          { type: "clickPath", path: ["Sidebar", "Support", "New ticket"] },
          {
            type: "steps",
            items: [
              {
                text: "Open **Support** in the sidebar and click **New ticket**."
              },
              {
                text: "Choose a category \u2014 **Billing**, **Technical**, **Compliance**, **Account**, **Feature Request** or **Other**.",
                note: "Each one shows its response SLA, so you can see what you are signing up for."
              },
              {
                text: "Write a subject and a description, and attach up to five files."
              },
              {
                text: "Submit. Replies arrive in the same thread."
              }
            ]
          },
          {
            type: "note",
            variant: "tip",
            text: "This is also how you request a dedicated phone number \u2014 there is no self-serve route for one."
          },
          { type: "action", label: "Open Support", to: { kind: "app", route: "/support" } }
        ]
      },
      {
        slug: "your-playbook",
        title: "What to do next",
        why: 'A short, concrete list beats "explore the product".',
        estimatedMinutes: 3,
        keywords: ["what next", "what should i do now", "playbook", "next steps"],
        blocks: [
          {
            type: "steps",
            items: [
              {
                text: "Send your intake form to everyone you imported.",
                note: "So their consent is captured by them rather than assumed by you. This is the highest-value thing on the list."
              },
              {
                text: "Send one small real campaign to a handful of contacts, then read the campaign dashboard.",
                note: "Small and real beats large and hypothetical."
              },
              {
                text: "Open every workflow your starter kit activated and confirm you are happy with what it does."
              },
              {
                text: "Invite one colleague and check they can see what they need and nothing more."
              },
              {
                text: "Come back to this guide for any chapter you skipped.",
                note: "The Help icon in the top bar reopens it."
              }
            ]
          }
        ]
      }
    ]
  }
];

// src/getting-started/overlays/consulting.ts
var consultingOverlay = {
  industry: "consulting",
  headline: "Set up Respectly for your practice, end to end",
  scenario: "A discovery call goes well, you promise a proposal, and it goes out four days later because you were delivering. Set up the way this guide describes and the enquiry qualifies itself, the proposal and the statement of work go out as signable documents, and the follow-up runs without you.",
  stepBlocks: {
    "business-profile/booking-link-and-custom-fields": [
      {
        type: "note",
        variant: "tip",
        text: "The booking link is doing most of the work in a consulting account. Put it in your email signature, every template, and the confirmation on your intake form."
      }
    ],
    "documents/create-a-document": [
      {
        type: "example",
        title: "The documents worth templating",
        body: "Mutual NDA \xB7 Statement of work \xB7 Master services agreement \xB7 Change order. Each one has a handful of variables \u2014 client name, scope, fee, dates \u2014 and the rest is fixed."
      }
    ]
  }
};

// src/getting-started/overlays/education.ts
var educationOverlay = {
  industry: "education",
  headline: "Set up Respectly for your institution, end to end",
  scenario: "Term starts, permission slips come back on paper (or not at all), and reaching every parent about a closure means a phone tree. Set up the way this guide describes and enrolment forms, consent slips and every announcement run from one place, with a record of who was told what.",
  stepBlocks: {
    "guided-setup/pick-your-kit": [
      {
        type: "paragraph",
        text: "There is no education starter kit yet, so build the basics yourself \u2014 it is about an hour. The rest of this guide walks you through each piece, and the kit gallery is worth browsing for structures you can adapt."
      },
      {
        type: "example",
        title: "A sensible starting structure",
        body: "Tags: Parent, Student, Staff, Alumni, Prospective. Pipeline: Enquiry, Application, Offer, Enrolled, Withdrawn. Forms: enrolment, permission slip, media consent. Documents: enrolment agreement, field-trip consent."
      }
    ],
    "before-you-begin/the-consent-model": [
      {
        type: "note",
        variant: "compliance",
        text: "You are usually messaging a parent about a student, so record consent against the parent \u2014 they are the contact with the phone number and the legal standing. Closure and timetable notices are informational; fundraising and open-day invitations are marketing."
      }
    ],
    "contacts/custom-fields": [
      {
        type: "example",
        title: "Custom fields worth defining first",
        body: "Student name \xB7 Year group \xB7 Homeroom or form \xB7 Relationship to student \xB7 Second guardian \xB7 Emergency contact \xB7 Media consent given"
      }
    ],
    "documents/create-a-document": [
      {
        type: "example",
        title: "The documents worth templating",
        body: "Enrolment agreement \xB7 Field-trip permission and medical consent \xB7 Photo and media release \xB7 Acceptable-use policy acknowledgement"
      }
    ]
  }
};

// src/getting-started/overlays/financial.ts
var financialOverlay = {
  industry: "financial",
  headline: "Set up Respectly for your firm, end to end",
  scenario: "Annual reviews slip, compliance documentation gets chased by email, and nobody can quickly show when a client last acknowledged a disclosure. Set up the way this guide describes and every review is scheduled, every disclosure is signed and filed, and the record is one click away.",
  stepBlocks: {
    "before-you-begin/the-consent-model": [
      {
        type: "note",
        variant: "compliance",
        text: "If you are FINRA or SEC regulated, outbound client communication is likely subject to supervision, review and archiving obligations that sit on top of everything in this guide. Involve your compliance officer before the first campaign, not after."
      }
    ],
    "business-profile/booking-link-and-custom-fields": [
      {
        type: "example",
        title: "Custom fields worth defining first",
        body: "Account type \xB7 Advisor of record \xB7 Last review date \xB7 Next review due \xB7 Risk profile reviewed on \xB7 Disclosure version acknowledged"
      }
    ],
    "dashboard/exports": [
      {
        type: "note",
        variant: "compliance",
        text: "Set a recurring export of the consent log and the policy decisions CSV into wherever you archive client communications. Regulatory retention periods outlive most software subscriptions."
      }
    ]
  }
};

// src/getting-started/overlays/healthcare.ts
var healthcareOverlay = {
  industry: "healthcare",
  headline: "Set up Respectly for your practice, end to end",
  scenario: "A new patient books a consultation. Their intake paperwork arrives on the day, half of it blank, and the consent to treat is signed on a clipboard nobody can find afterwards. Set up the way this guide describes and the booking sends the intake form, captures consent with an audit trail, files the signed treatment agreement, and reminds them the day before.",
  stepBlocks: {
    "before-you-begin/the-consent-model": [
      {
        type: "note",
        variant: "compliance",
        text: "Appointment reminders and results notifications are informational. Anything promoting a service they have not asked for is marketing and needs marketing consent. Keeping the two apart is the single most important habit in a clinical account."
      }
    ],
    "contacts/csv-import": [
      {
        type: "note",
        variant: "warning",
        text: "Do not put clinical detail in contact custom fields. Use Respectly for scheduling, consent, forms and signatures, and keep diagnoses and treatment notes in your clinical system. Custom fields are visible to every teammate with contact access."
      }
    ],
    "forms/form-fields-and-consent": [
      {
        type: "example",
        title: "A patient intake form that earns its place",
        body: "Demographics, insurance carrier and member id, medical history, current medications, allergies, emergency contact, consent to treat, and a signature field \u2014 collected before the appointment rather than in the waiting room."
      }
    ],
    "documents/signers-and-fields": [
      {
        type: "note",
        variant: "compliance",
        text: "For a minor or a patient under a power of attorney, add the guardian as a second signer with their own fields rather than letting one person sign for both. The audit trail then names who actually consented."
      }
    ]
  }
};

// src/getting-started/overlays/home-services.ts
var homeServicesOverlay = {
  industry: "home-services",
  headline: "Set up Respectly for your business, end to end",
  scenario: "Someone calls asking if you can come and take a look. You write the address on the back of a docket, quote from the van, and never hear back. Set up the way this guide describes and the enquiry books itself, the quote goes out as a signable document, and the follow-up happens whether or not you remember.",
  stepBlocks: {
    "sending-identity/phone-number": [
      {
        type: "note",
        variant: "tip",
        text: "Field trades live on SMS. Customers reply to texts they would never answer as a call, and a text arrives while someone is at work. Prioritise getting SMS working over email."
      }
    ],
    "business-profile/booking-link-and-custom-fields": [
      {
        type: "example",
        title: "Custom fields worth defining first",
        body: "Service address (when it differs from billing) \xB7 Property type \xB7 Job type \xB7 Access notes \xB7 Preferred visit window \xB7 Last service date \xB7 Equipment or model installed"
      }
    ],
    "automation/steps-and-branches": [
      {
        type: "example",
        title: "The two automations that pay for themselves",
        body: "A day-before appointment reminder by SMS, which cuts no-shows more than anything else you can build. And a quote follow-up: wait three days, check whether the document was signed, and nudge on the NO arm."
      }
    ]
  }
};

// src/getting-started/overlays/hospitality.ts
var hospitalityOverlay = {
  industry: "hospitality",
  headline: "Set up Respectly for your venue, end to end",
  scenario: "An enquiry comes in about a date, you hold it provisionally, and three weeks later nobody is sure whether it is booked. Set up the way this guide describes and the enquiry captures the date and the headcount, the contract and deposit terms go out for signature, and every milestone before the event is scheduled.",
  stepBlocks: {
    "business-profile/booking-link-and-custom-fields": [
      {
        type: "example",
        title: "Custom fields worth defining first",
        body: "Event date \xB7 Event type \xB7 Guest count \xB7 Package \xB7 Deposit paid \xB7 Balance due date \xB7 Dietary requirements \xB7 On-the-day contact"
      }
    ],
    "automation/triggers": [
      {
        type: "note",
        variant: "tip",
        text: '"Wait until a date" is the step that matters for a venue. Drive final headcount requests, balance reminders and the week-before confirmation off the event date on the contact rather than off when they enquired.'
      }
    ],
    "send/review-and-send": [
      {
        type: "note",
        variant: "compliance",
        text: "Post-stay review requests are marketing in most jurisdictions, not service messages. Collect that consent at booking, on the same form that captures the event details."
      }
    ]
  }
};

// src/getting-started/overlays/insurance.ts
var insuranceOverlay = {
  industry: "insurance",
  headline: "Set up Respectly for your agency, end to end",
  scenario: "Start with the insurance kit, review its forms, message templates and sample no-loss letter, then configure sending and test with synthetic data. The kit workflows stay in draft until you review their steps and destinations and deliberately activate them. Follow an enquiry through your pipeline, collect the required information and retrieve the completed records. Your agency and carrier continue to handle quotes, submissions and binding.",
  stepBlocks: {
    "before-you-begin/the-consent-model": [
      {
        type: "note",
        variant: "compliance",
        text: "Insurance is one of the most heavily policed verticals for outbound contact. A renewal reminder to an existing policyholder is informational. A cross-sell for a product they do not hold is marketing, and needs marketing consent on that channel \u2014 even though it is the same person and the same phone number."
      }
    ],
    "before-you-begin/what-you-will-need": [
      {
        type: "note",
        variant: "tip",
        text: "Agencies are almost always incorporated, so the EIN route to a dedicated 10DLC number is usually open to you. Worth starting early \u2014 brand vetting takes 1\u20137 business days and the campaign approval takes longer still."
      }
    ],
    "business-profile/booking-link-and-custom-fields": [
      {
        type: "example",
        title: "Custom fields worth defining before you import",
        body: "Policy number \xB7 Carrier \xB7 Policy type (personal / commercial lines) \xB7 Renewal date \xB7 Premium \xB7 Producer of record \xB7 Prior carrier"
      },
      {
        type: "paragraph",
        text: "Maintain renewal dates on your contacts, select the relevant audience and schedule a reminder campaign using the scheduling options available to your plan. Verify your dates and audience before sending; the starter kit does not install a per-policy renewal scheduler."
      }
    ],
    "compliance/contact-creation-defaults": [
      {
        type: "note",
        variant: "compliance",
        text: "Adding or importing a policyholder does not create blanket messaging permission. Collect or import consent evidence, select the appropriate message purpose, and review the actual eligibility decision for each channel before sending."
      }
    ],
    "contacts/csv-import": [
      {
        type: "example",
        title: "A sensible first import",
        body: 'Export your book of business from your AMS, keep name, email, mobile, country, state, policy number, carrier and renewal date, and tag the whole batch "AMS import 2026". You then have a named batch to send the intake form to.'
      },
      {
        type: "note",
        variant: "warning",
        text: "Include country and state. Insurance is regulated state by state, and the consent rules and calling hours applied to each contact are chosen from exactly those two fields."
      }
    ],
    "contacts/intake-forms": [
      {
        type: "paragraph",
        text: "For an agency the intake form does double duty: it collects the marketing consent your import could not, and it confirms the contact details on file are still current \u2014 which is the same information you would otherwise chase at renewal."
      }
    ],
    "documents/create-a-document": [
      {
        type: "note",
        variant: "compliance",
        text: "Carrier and state-mandated forms have fixed wording. Upload those as PDFs and place signature and data fields on top rather than regenerating them \u2014 the text has to stay exactly as issued."
      }
    ],
    "documents/verify-and-audit": [
      {
        type: "paragraph",
        text: "Keep the audit trail PDF with the policy file. When a carrier or your state DOI asks when a signature was obtained and what the signer was shown, that document is the answer."
      }
    ],
    "send/recipients-and-eligibility": [
      {
        type: "example",
        title: "A renewal campaign that will actually deliver",
        body: "Select the contacts whose renewal dates you have confirmed, review Email and SMS eligibility, choose the appropriate message purpose, and schedule a test campaign. A renewal audience still needs recipient and sender eligibility checks."
      }
    ],
    "automation/steps-and-branches": [
      {
        type: "example",
        title: "The branch worth building first",
        body: "Before activating the quote-request workflow, review its form destination and follow-up conditions. Configure the supported completion branch for your process and test a new submission, old submission history, replies and opt-outs. Do not assume that the starter workflow stops on completion or supports concurrent enquiries for the same contact."
      }
    ]
  },
  extraSteps: {
    compliance: [
      {
        slug: "insurance-record-retention",
        title: "Line up retention with your state requirements",
        why: "Insurance record-retention periods are set by your state DOI and are usually longer than anyone expects, so decide what you export and where it lives before you need it.",
        estimatedMinutes: 4,
        blocks: [
          {
            type: "steps",
            items: [
              {
                text: "Find out your state DOI retention period before you need it.",
                note: "Nothing in the app knows it. Respectly keeps the consent record and the signing audit trail for as long as the contact exists, but how long YOU must keep them is a decision for your agency."
              },
              {
                text: "Export consent history per contact with **Log PDF** on the contact page."
              },
              {
                text: "Export signature evidence per send with **Download Audit Trail PDF** on the document campaign."
              },
              {
                text: "Export compliance decisions with **Export CSV** from **Settings**, **Policies**, **Decisions**."
              },
              {
                text: "Agree with your E&O carrier which of these you are expected to retain, and for how long."
              },
              {
                text: "Put a recurring calendar entry against the export.",
                note: "Nothing in the app will prompt you. This step is the whole article."
              }
            ]
          },
          {
            type: "note",
            variant: "compliance",
            text: "Insurance record-retention periods are set by your state DOI and are usually longer than anyone expects. Decide what you export and where it lives before somebody asks for it."
          }
        ]
      }
    ]
  }
};

// src/getting-started/overlays/legal.ts
var legalOverlay = {
  industry: "legal",
  headline: "Set up Respectly for your practice, end to end",
  scenario: "A prospective client calls, you talk for twenty minutes, and the engagement letter sits in your drafts for a week. Set up the way this guide describes and the enquiry captures itself, the conflicts questions are asked up front, and the engagement letter goes out for signature the same day.",
  stepBlocks: {
    "before-you-begin/the-consent-model": [
      {
        type: "note",
        variant: "compliance",
        text: "Your state bar almost certainly regulates client solicitation more tightly than telecom law does. Respectly enforces the telecom rules; the advertising rules are still yours to meet. Have someone check any marketing template against your bar rules before it goes out."
      }
    ],
    "forms/form-fields-and-consent": [
      {
        type: "example",
        title: "A matter intake form worth building",
        body: "Matter type, opposing parties (for the conflicts check), key dates and limitation periods, referral source, and a clear statement that submitting the form does not create an attorney-client relationship."
      }
    ],
    "documents/signers-and-fields": [
      {
        type: "note",
        variant: "compliance",
        text: "Engagement letters and fee agreements are exactly what e-signature is for. Keep the audit trail PDF with the matter file \u2014 it records what the client was shown and when they signed it."
      }
    ]
  }
};

// src/getting-started/overlays/manufacturing.ts
var manufacturingOverlay = {
  industry: "manufacturing",
  headline: "Set up Respectly for your operation, end to end",
  scenario: "Lead times change, and telling every affected customer means someone working through a spreadsheet for an afternoon. Set up the way this guide describes and the notice goes to exactly the right accounts, with the quote and the purchase order signed in the same flow.",
  stepBlocks: {
    "contacts/tags": [
      {
        type: "note",
        variant: "tip",
        text: "In B2B you are usually messaging several people at one account. Tag by account, by product line and by role, so an operational notice reaches buyers and a spec change reaches engineers."
      }
    ],
    "send/generate-and-edit": [
      {
        type: "example",
        title: "Operational notices are informational",
        body: "A lead-time change, a shipment delay or a recall notice on something a customer already ordered is a service message. Your whole account list is eligible for it \u2014 you do not need marketing consent to tell someone their order is late."
      }
    ]
  }
};

// src/getting-started/overlays/other.ts
var otherOverlay = {
  industry: "other",
  headline: "Set up Respectly end to end",
  scenario: "Follow these chapters in order and you will finish with a fully configured account: your senders verified, consent captured lawfully, contacts imported and organised, templates and documents ready, your first campaign sent, and automation running.",
  stepBlocks: {
    "guided-setup/pick-your-kit": [
      {
        type: "paragraph",
        text: "No kit is recommended for your industry, which is fine \u2014 every kit is editable and none of them is locked to a vertical. Browse the gallery and apply whichever is structurally closest to how you work, or skip it and build as you go through this guide."
      },
      {
        type: "note",
        variant: "tip",
        text: "If you later set a more specific industry in Settings > Profile, both the recommended kit and this guide follow it."
      }
    ]
  }
};

// src/getting-started/overlays/real-estate.ts
var realEstateOverlay = {
  industry: "real-estate",
  headline: "Set up Respectly for your brokerage, end to end",
  scenario: "Forty people walk through an open house and sign a paper sheet you can barely read. Two get followed up. Set up the way this guide describes and a QR code on the counter captures each visitor with their budget, their timeline and their consent, tags them, and starts the follow-up before you have locked the front door.",
  stepBlocks: {
    "contacts/intake-forms": [
      {
        type: "example",
        title: "The open-house QR code",
        body: "Print the intake form QR at A5, stand it on the kitchen island, and ask visitors to scan it instead of signing a sheet. You get a real contact record, a lawful consent trail and their search criteria \u2014 from the same thirty seconds of their attention."
      }
    ],
    "business-profile/booking-link-and-custom-fields": [
      {
        type: "example",
        title: "Custom fields worth defining first",
        body: "Budget range \xB7 Preferred areas \xB7 Beds and baths \xB7 Timeline to buy \xB7 Pre-approved (yes / no / not sure) \xB7 Buyer or seller \xB7 Current address"
      }
    ],
    "compliance/opt-outs": [
      {
        type: "note",
        variant: "compliance",
        text: "Property alerts to an active buyer you represent are informational. A market update sent to everyone who ever attended an open house is marketing \u2014 and the people who came to look at a neighbour\u2019s house never agreed to it."
      }
    ]
  }
};

// src/getting-started/overlays/recreational.ts
var recreationalOverlay = {
  industry: "recreational",
  headline: "Set up Respectly for your club, end to end",
  scenario: "A prospect takes a tour, loves it, and never comes back \u2014 because nothing happened after they walked out. Set up the way this guide describes and the tour books itself, the waiver is signed before they arrive, and the follow-up runs whether or not the desk is busy.",
  stepBlocks: {
    "documents/create-a-document": [
      {
        type: "note",
        variant: "compliance",
        text: "Liability waivers are the document that matters most here, and they are worthless if you cannot show who signed and when. Send them ahead of the first visit rather than handing over a clipboard at the door, and keep the audit trail PDF."
      }
    ],
    "documents/signers-and-fields": [
      {
        type: "note",
        variant: "compliance",
        text: "For anyone under 18, add the parent or guardian as their own signer with their own fields. A waiver signed by the minor is not a waiver."
      }
    ],
    "automation/steps-and-branches": [
      {
        type: "example",
        title: "The follow-up that converts tours",
        body: 'Trigger on the tour booking form. Send the waiver, remind them the day before, then check "has signed the waiver" \u2014 nudge on the NO arm, and on the YES arm move them to the trial stage and start the membership sequence.'
      }
    ]
  }
};

// src/getting-started/overlays/retail.ts
var retailOverlay = {
  industry: "retail",
  headline: "Set up Respectly for your business, end to end",
  scenario: "You have a customer list and no lawful way to text it. Set up the way this guide describes and a QR code at the till collects real, evidenced marketing consent, and your promotions go to people who actually asked for them.",
  stepBlocks: {
    "compliance/contact-creation-defaults": [
      {
        type: "note",
        variant: "compliance",
        text: "Retail is almost entirely marketing traffic, which is the hardest consent to hold. Someone buying from you is not someone agreeing to be texted. Plan on collecting consent explicitly at the point of sale rather than inferring it from a transaction."
      }
    ],
    "contacts/intake-forms": [
      {
        type: "example",
        title: "The QR code at the till",
        body: "A small stand by the register offering a reason to sign up. It captures the contact, the channel preferences and the consent text they were shown, in one scan \u2014 and it is the only route that produces marketing consent you can actually rely on."
      }
    ],
    "templates/sms-template": [
      {
        type: "note",
        variant: "compliance",
        text: "Every promotional SMS needs your business name and a visible opt-out. Keep it to one segment: retail lists are large and segments are billed per recipient."
      }
    ]
  }
};

// src/getting-started/overlays/technology.ts
var technologyOverlay = {
  industry: "technology",
  headline: "Set up Respectly for your product, end to end",
  scenario: "Trials start, nobody activates, and the only outreach is a generic drip that ignores what the user actually did. Set up the way this guide describes and trials get sequenced on what they did, order forms and DPAs get signed in-app, and your team sees the replies.",
  stepBlocks: {
    "before-you-begin/the-consent-model": [
      {
        type: "note",
        variant: "compliance",
        text: "Product and lifecycle email is informational; a feature announcement aimed at upsell is marketing. Selling into the EU or Canada brings GDPR and CASL into scope, and both are stricter than US rules about what a signup implies."
      }
    ],
    "automation/sequences": [
      {
        type: "example",
        title: "A trial sequence that is not just a countdown",
        body: "Branch on whether the user has completed activation. On the NO arm send help getting started; on the YES arm send the upgrade path. One sequence, two genuinely different conversations."
      }
    ],
    "documents/create-a-document": [
      {
        type: "example",
        title: "The documents worth templating",
        body: "Order form \xB7 Data processing agreement \xB7 Mutual NDA \xB7 Security questionnaire cover letter"
      }
    ]
  }
};

// src/getting-started/overlays/index.ts
var INDUSTRY_OVERLAYS = {
  consulting: consultingOverlay,
  education: educationOverlay,
  financial: financialOverlay,
  healthcare: healthcareOverlay,
  "home-services": homeServicesOverlay,
  hospitality: hospitalityOverlay,
  insurance: insuranceOverlay,
  legal: legalOverlay,
  manufacturing: manufacturingOverlay,
  "real-estate": realEstateOverlay,
  recreational: recreationalOverlay,
  retail: retailOverlay,
  technology: technologyOverlay,
  other: otherOverlay
};

// src/getting-started/articles/contacts.ts
var contactsArticles = {
  contacts: [
    {
      slug: "add-a-contact-by-hand",
      title: "Add one contact by hand",
      why: "It is the quickest way to get a single person into the app, and the form decides what you are allowed to send them.",
      route: "/contacts",
      requires: { permissions: ["contacts:create"], features: ["contacts"] },
      estimatedMinutes: 3,
      keywords: [
        "add a contact",
        "how do i add a contact manually",
        "create a new contact",
        "new contact form",
        "add contact button",
        "enter a customer by hand",
        "why is first name required",
        "add at least one phone or email"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Contacts", "Add Contact"] },
        {
          type: "steps",
          items: [
            {
              text: "Click **Add Contact** in the Contacts page header.",
              note: "On mobile the same modal opens from the floating add button instead."
            },
            {
              text: "Fill in **Identity** \u2014 **First Name**, and at least one of **Phone** or **Email**.",
              note: 'First Name is the only starred field, but the line "Add at least one \u2014 phone or email." under the row turns red if you leave both blank.'
            },
            {
              text: "Add **Tags** while you are here.",
              note: "Tagging on the way in is the difference between finding this contact by source later and not."
            },
            {
              text: "Fill in **Address** \u2014 Street Address, Country, State, City, ZIP Code.",
              note: "Country and State are not cosmetic: they decide which consent rules apply to this contact."
            },
            {
              text: "Fill in any custom fields your account defines."
            },
            {
              text: "Read the **Consent** section, but do not expect to set anything in it.",
              note: "When adding a contact it is a read-only jurisdiction preview of what will be auto-granted on save."
            },
            {
              text: "Click **Add Contact** to save, or **Cancel** to leave.",
              note: "If your plan is already at its contact limit, the button is blocked before the form even opens."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            {
              name: "First Name",
              value: "Required",
              note: "The only starred field on the form."
            },
            {
              name: "Last Name",
              value: "Optional"
            },
            {
              name: "Phone / Email",
              value: "At least one of the two",
              note: "Neither is required on its own."
            },
            {
              name: "Tags",
              value: "Optional",
              note: "Tag on the way in so you can find this contact by source later."
            },
            {
              name: "Address",
              value: "Street Address, Country, State, City, ZIP Code",
              note: "Country and state decide which consent rules apply to this contact."
            }
          ]
        },
        {
          type: "note",
          variant: "compliance",
          text: "Informational consent is applied only where the jurisdiction rule allows it; marketing consent is never granted automatically. To record real consent you need evidence, on the consent screen or from a form the contact submitted."
        },
        { type: "action", label: "Open Contacts", to: { kind: "app", route: "/contacts" } }
      ]
    },
    {
      slug: "find-a-contact",
      title: "Search and filter the contact list",
      why: "Every bulk action, export and send starts from the set of contacts you have filtered down to.",
      route: "/contacts",
      requires: { permissions: ["contacts:read"], features: ["contacts"] },
      estimatedMinutes: 3,
      keywords: [
        "search contacts",
        "how do i find a contact",
        "filter contacts by tag",
        "filter by pipeline",
        "search contacts box",
        "look up a customer",
        "global search",
        "why did my selection disappear"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Contacts"] },
        {
          type: "steps",
          items: [
            {
              text: "Type into the box marked **Search contacts...** above the list.",
              note: 'For a single person whose name you already know, the top-bar search \u2014 "Search contacts, documents, forms..." \u2014 is faster than filtering the list at all.'
            },
            {
              text: "Narrow further with the tag-filter dropdown.",
              note: "It lists your tags, with a **Manage Tags** link at the bottom."
            },
            {
              text: "Narrow further again with the pipeline selector next to it \u2014 the same selector that drives the Kanban view."
            },
            {
              text: 'All three combine. Whatever is left on screen is the "filtered contacts" scope.',
              note: "That scope is what **Export** and the bulk actions operate on, so getting it right here is the whole job."
            },
            {
              text: "Only now tick the rows you want.",
              note: "Do this last. Changing any filter clears the selection."
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: "Changing any filter clears your row selection \u2014 including a filter you change by accident. If a bulk action suddenly says nothing is selected, this is why."
        }
      ]
    },
    {
      slug: "table-grid-kanban-views",
      title: "Switch between Table, Grid and Kanban",
      why: "Only one of the three lets you select rows, which is why bulk actions sometimes seem to be missing.",
      route: "/contacts",
      requires: { permissions: ["contacts:read"], features: ["contacts"] },
      estimatedMinutes: 3,
      keywords: [
        "contact views",
        "kanban view",
        "grid view",
        "table view",
        "how do i see contacts as a board",
        "where are the checkboxes",
        "why can i not select contacts",
        "switch contact layout"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Contacts", "view switcher"] },
        {
          type: "steps",
          items: [
            {
              text: "Find the view switcher above the contact list \u2014 it toggles **Table / Grid / Kanban**."
            },
            {
              text: "Click **Table** for the full list with checkboxes.",
              note: "The only view with row selection, and therefore the only view where bulk actions exist."
            },
            {
              text: "Click **Grid** for contact cards \u2014 good for browsing, not for working."
            },
            {
              text: "Click **Kanban** for the pipeline board, then pick a pipeline in the selector and drag cards between stages."
            },
            {
              text: "In Table view, use the header checkbox to select the current page of 20 rows.",
              note: 'When the whole page is checked and more contacts match your filter, a banner offers "Select all {N} contacts matching this filter", capped at 500 \u2014 the same ceiling the bulk endpoints enforce.'
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: 'Selection exists only in Table view. Grid, Kanban and the mobile card list render no checkboxes, so every bulk action \u2014 Send Message, Send Document, Send Intake Form, Add Tag, Remove Tag, Update Consent, Export, Delete \u2014 is desktop Table view only. That is the answer to "where did the bulk actions go".'
        }
      ]
    },
    {
      slug: "edit-a-contact",
      title: "Edit a contact's details",
      why: "Changing a phone, email, country or state re-runs the jurisdiction rules, so an edit can change what you may send.",
      route: "/contacts",
      requires: { permissions: ["contacts:update"], features: ["contacts"] },
      estimatedMinutes: 4,
      keywords: [
        "edit a contact",
        "how do i change a contact phone number",
        "update contact details",
        "change a contact email",
        "edit contact modal",
        "save changes contact",
        "fix a contact address",
        "why did consent change after i edited a contact"
      ],
      blocks: [
        { type: "clickPath", path: ["Contacts", "a contact", "Edit Contact"] },
        {
          type: "steps",
          items: [
            {
              text: "Open the contact, then click **Edit Contact**.",
              note: "It is a button on the contact page and an item in the Contact actions menu (the vertical-dots button, top right)."
            },
            {
              text: "Change what you need across **Identity**, **Address** and your custom fields.",
              note: 'The modal is the one you added the contact with, now described as "Update the contact information below."'
            },
            {
              text: "Click **Save Changes**.",
              note: "Or **Cancel** to drop the edits \u2014 but see the next step, because Cancel does not undo everything."
            },
            {
              text: "To change consent while you are here, click a cell in the **Consent** matrix.",
              note: "In edit mode this section is the live 3x2 matrix, not the preview you saw when adding. It opens the same evidence drawer as the full consent screen."
            },
            {
              text: "Be aware that a consent write from this modal applies immediately.",
              note: "It does not wait for **Save Changes**, and **Cancel** does not undo it. The **Full history** link jumps to the consent screen."
            }
          ]
        },
        {
          type: "note",
          variant: "compliance",
          text: "If your edit changes the phone, email, country or state, the jurisdiction rule is re-applied: cells the new jurisdiction allows are granted, and cells it now blocks are revoked if they had only been auto-granted in the first place. An explicit opt-out and anything captured with evidence \u2014 a form submission, the consent screen, a double opt-in confirmation \u2014 are never touched."
        }
      ]
    },
    {
      slug: "move-a-contact-between-stages",
      title: "Move a contact to another pipeline stage",
      why: "Stage changes are a workflow trigger, so moving a card can be what starts your follow-up automation.",
      route: "/contacts",
      requires: { permissions: ["contacts:update"], features: ["contacts"] },
      estimatedMinutes: 3,
      prereqs: ["contacts/pipelines-and-stages"],
      keywords: [
        "move a contact to another stage",
        "how do i drag a contact in kanban",
        "change pipeline stage",
        "add contacts to a pipeline",
        "move deal to next stage",
        "contact pipeline progress",
        "add to pipeline modal"
      ],
      blocks: [
        { type: "clickPath", path: ["Contacts", "Kanban", "pipeline selector", "drag the card"] },
        {
          type: "steps",
          items: [
            {
              text: "Switch the contact list to **Kanban** in the view switcher."
            },
            {
              text: "Choose the pipeline you want in the pipeline selector."
            },
            {
              text: "Drag the contact card from one stage column to another.",
              note: "The contact page shows the same thing as a **Pipeline Progress** section."
            },
            {
              text: "To put contacts into a pipeline in the first place, switch to **Table**, tick the rows, and open the **Add to Pipeline** modal from the bulk toolbar.",
              note: "Pick a **Pipeline** and a **Stage**, then click **Add to {Pipeline}**."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "A contact sits in at most one stage per pipeline, so moving is a move and not a copy. Both adding a contact to a pipeline and moving them between stages fire workflow triggers you can build automation on \u2014 which is why a stage change is worth doing deliberately."
        }
      ]
    },
    {
      slug: "duplicate-contacts",
      title: "Deal with duplicate contacts",
      why: "Duplicates split a person's consent record and history across two rows, which is exactly what you do not want at audit time.",
      route: "/contacts",
      requires: { permissions: ["contacts:read"], features: ["contacts"] },
      estimatedMinutes: 4,
      prereqs: ["contacts/csv-import"],
      keywords: [
        "duplicate contacts",
        "how do i merge duplicate contacts",
        "remove duplicate contacts",
        "why do i have two of the same contact",
        "duplicate handling on import",
        "skip duplicate rows",
        "update existing contacts",
        "stop the import creating duplicates"
      ],
      blocks: [
        {
          type: "paragraph",
          text: "Respectly treats two contacts as the same person when their email matches exactly (ignoring case) or their phone digits match. The check runs on create and on import, within your account only."
        },
        {
          type: "steps",
          items: [
            {
              text: "Decide the outcome before the import, not after: the **Import Options** step is where duplicates are settled.",
              note: "By the time a duplicate exists, your only tool is a re-import."
            },
            {
              text: "Pick **Skip duplicate rows** to leave existing contacts exactly as they are. This is the default."
            },
            {
              text: "Pick **Update existing contacts** when the CSV is more current than the app \u2014 the matching contact is updated from the file."
            },
            {
              text: "Pick **Create duplicates anyway** only if you mean it.",
              note: "It really does create a second record, including for rows repeated inside the same file."
            },
            {
              text: "To consolidate two records that already exist, re-import the good data with **Update existing contacts**, then delete the record you no longer want.",
              note: "There is no merge action in the contact list."
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: "Duplicates split a person's consent record and history across two rows. One row can be opted out while the other still looks sendable, which is exactly the state you do not want to be in at audit time."
        },
        {
          type: "note",
          variant: "tip",
          text: "Phone matching compares digit sequences, so a shorter number contained inside a longer one can match. Store numbers in a consistent full format and this stops happening."
        }
      ]
    },
    {
      slug: "delete-a-contact",
      title: "Delete contacts, one or many",
      why: "Deletes here are permanent from your side \u2014 there is no undo and no bin to restore from.",
      route: "/contacts",
      requires: { permissions: ["contacts:delete"], features: ["contacts"] },
      estimatedMinutes: 3,
      keywords: [
        "delete a contact",
        "how do i delete contacts in bulk",
        "remove a contact",
        "delete multiple contacts",
        "can i undo deleting a contact",
        "bulk delete contacts",
        "get rid of a contact record"
      ],
      blocks: [
        { type: "clickPath", path: ["Contacts", "a contact", "Contact actions", "Delete Contact"] },
        {
          type: "steps",
          items: [
            {
              text: "Export first if there is any chance you will need the data.",
              note: "There is no undo and no bin. This is the only safety net that exists."
            },
            {
              text: "Open the contact, then open the Contact actions menu \u2014 the vertical-dots button, top right."
            },
            {
              text: "Choose **Delete Contact** and confirm.",
              note: 'The dialog asks "Are you sure you want to delete {name}? This action cannot be undone."'
            },
            {
              text: "To delete several at once, switch to **Table** view, tick the rows, and click **Delete** in the bulk-action toolbar.",
              note: "Capped at 500 contacts per go, like every bulk action."
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: "A deleted contact disappears from the list, from search and from their activity timeline, and the app offers no way to bring them back."
        }
      ]
    },
    {
      slug: "export-contacts-csv",
      title: "Export contacts to CSV",
      why: "It is your backup, your migration path and the file you hand over when someone asks what you hold on a person.",
      route: "/contacts",
      requires: { permissions: ["contacts:export"], features: ["contacts"] },
      estimatedMinutes: 3,
      keywords: [
        "export contacts",
        "how do i download my contacts",
        "export contacts to csv",
        "download contact list",
        "export selected contacts",
        "export filtered contacts",
        "get my data out",
        "csv of contacts and consent"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Contacts", "Export"] },
        {
          type: "steps",
          items: [
            {
              text: "Narrow the list first if you want a subset \u2014 search, tag filter, pipeline selector, or ticked rows.",
              note: "The dialog offers scopes based on what is live when you open it, so set this up before clicking Export."
            },
            {
              text: "Click **Export** in the Contacts page header.",
              note: "The same dialog opens from **Export** in the bulk-action toolbar."
            },
            {
              text: "Pick a scope. The dialog has already chosen the narrowest one available.",
              note: "Your selection first, then your active filter, then everything \u2014 so an export never silently widens back out."
            },
            {
              text: "If you deliberately want everything, pick **All contacts (N)**.",
              note: "A note appears telling you it ignores your active filter or selection. That note is the point."
            },
            {
              text: "Click **Export {N} Contacts**.",
              note: "The CSV downloads straight away \u2014 name and contact information, company and address, tags, consent status, and a column per custom field."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            {
              name: "All contacts (N)",
              value: "Everything in the account"
            },
            {
              name: "Filtered contacts (N)",
              value: "Only shown when a search, tag or pipeline filter is active"
            },
            {
              name: "Selected contacts (N)",
              value: "Only shown when rows are checked"
            }
          ]
        }
      ]
    },
    {
      slug: "read-the-consent-matrix",
      title: "Read a contact's consent matrix",
      why: 'The matrix is the answer to "why can I not send this to them", and each badge means something specific.',
      route: "/contacts",
      requires: { permissions: ["contacts:read"], features: ["contacts"] },
      estimatedMinutes: 5,
      keywords: [
        "consent matrix",
        "what does not captured mean",
        "why is a consent cell blocked",
        "how do i see a contact consent",
        "consent preferences card",
        "consent audit trail",
        "why can i not message this contact",
        "contact consent screen"
      ],
      blocks: [
        { type: "clickPath", path: ["Contacts", "a contact", "Manage Consents"] },
        {
          type: "steps",
          items: [
            {
              text: "Open the contact and read the **Consent preferences** card for the quick answer.",
              note: "It is the compact view of the same matrix."
            },
            {
              text: "Click **Manage Consents** for the full screen \u2014 matrix, audit trail and intake-link sender.",
              note: "The header there carries a **Jurisdiction \xB7 {code}** badge: the rules actually being applied to this contact."
            },
            {
              text: "Read the grid: rows are the channels **SMS**, **Email** and **Voice**; columns are the purposes **Informational** and **Marketing**.",
              note: "Six cells, and a send is judged against exactly one of them."
            },
            {
              text: "Read the badge in the cell you care about \u2014 see the table below for what each one means.",
              note: "A captured cell also shows its capture date, source and rule id underneath."
            },
            {
              text: "Scroll to the **Audit trail** card for the history behind the badge.",
              note: 'Every attempt, with an "{N} entries" badge: timestamp, channel and purpose, an outcome of Granted / Revoked / Blocked / Rejected / Sent, the capture path, the rule id, the actor and IP, and a preview of the evidence. A contact with no history reads "No capture attempts recorded yet."'
            }
          ]
        },
        {
          type: "fields",
          rows: [
            {
              name: "Granted",
              value: "You may send on this channel for this purpose",
              note: "The capture date, source and rule id are shown underneath."
            },
            {
              name: "Opted out",
              value: "The contact withdrew consent",
              note: "Reinstating it needs an uploaded document."
            },
            {
              name: "Blocked",
              value: "A jurisdiction rule prohibits capture",
              note: "The cell is locked and shows the jurisdiction and rule id instead."
            },
            {
              name: "Not captured",
              value: "Nothing recorded either way",
              note: 'The cell prompts "Click to capture with evidence".'
            },
            {
              name: "Pending",
              value: "A double opt-in confirmation has been sent and not yet clicked"
            }
          ]
        },
        {
          type: "note",
          variant: "prereq",
          text: "Without the contacts:update permission the cells render as plain badges you can read but not click."
        }
      ]
    },
    {
      slug: "revoke-consent-for-a-contact",
      title: "Revoke consent on behalf of a contact",
      why: "When someone asks you to stop by phone or email, this is the record that proves you acted on it.",
      route: "/contacts",
      requires: { permissions: ["contacts:update"], features: ["contacts"] },
      estimatedMinutes: 4,
      prereqs: ["contacts/read-the-consent-matrix"],
      keywords: [
        "revoke consent",
        "how do i opt a contact out",
        "stop messaging a contact",
        "admin opt out",
        "record an opt out",
        "unsubscribe a contact manually",
        "they asked me to stop texting them",
        "undo an opt out"
      ],
      blocks: [
        { type: "clickPath", path: ["Contacts", "a contact", "Manage Consents", "a granted cell"] },
        {
          type: "steps",
          items: [
            {
              text: "Open the contact and click **Manage Consents**."
            },
            {
              text: "Click the cell you need to switch off \u2014 the channel and purpose the person asked you to stop."
            },
            {
              text: "Use the revoke side of the drawer."
            },
            {
              text: "Give a reason. It is required.",
              note: "A revoke with no reason is rejected, exactly as a grant with no evidence is."
            },
            {
              text: "Confirm. The change takes effect immediately.",
              note: "It is written to the audit trail as **Revoked**, with your name, the time and the reason."
            }
          ]
        },
        {
          type: "note",
          variant: "compliance",
          text: "Reinstating consent someone affirmatively withdrew \u2014 by texting STOP, by clicking unsubscribe, or by an earlier revoke like this one \u2014 requires an uploaded document and a Written express opt-in. A written note or a verbal will be rejected. The gate covers the whole channel, not just the one cell."
        },
        {
          type: "note",
          variant: "tip",
          text: "Prefer the routes the contact drives themselves: STOP to an SMS, the unsubscribe link in an email, or unticking the box on your intake form. This screen is for the request that arrived by phone or in person."
        }
      ]
    },
    {
      slug: "bulk-update-consent",
      title: "Update consent for many contacts at once",
      why: "It is the practical way to record one consent event that covered a whole list, without opening every contact.",
      route: "/contacts",
      requires: { permissions: ["contacts:update"], features: ["contacts"] },
      estimatedMinutes: 5,
      prereqs: ["contacts/capture-consent"],
      keywords: [
        "bulk consent update",
        "how do i opt in many contacts at once",
        "update consent for multiple contacts",
        "bulk opt out",
        "grant consent in bulk",
        "why were some contacts blocked in a bulk consent update",
        "mass consent change"
      ],
      blocks: [
        { type: "clickPath", path: ["Contacts", "Table", "select rows", "Update Consent"] },
        {
          type: "steps",
          items: [
            {
              text: "Switch the contact list to **Table** view and tick two or more rows.",
              note: "The bulk-action toolbar only appears here."
            },
            {
              text: 'Click **Update Consent** \u2014 the dialog is titled "Update consent for {N} contacts".'
            },
            {
              text: "Pick an **Action** \u2014 **Grant consent** or **Revoke consent**."
            },
            {
              text: "Pick one or more **Channels** \u2014 SMS, Email, Voice."
            },
            {
              text: "Pick one or more **Purposes** \u2014 Informational, Marketing.",
              note: "Channels and purposes are applied as a cross-product, so two channels and two purposes is four cells per contact."
            },
            {
              text: "For a grant, add evidence \u2014 **Written note**, **Verbal with witness** or **API reference**, plus an optional disclosure. For a revoke, give a reason.",
              note: "There is no file upload here on purpose; see the note below."
            },
            {
              text: "Confirm, then read the summary.",
              note: "It reports how many of the selected contacts were processed, with the granted / revoked / blocked cell counts. Each contact still gets its own audit entry."
            }
          ]
        },
        {
          type: "note",
          variant: "compliance",
          text: "Bulk grants take non-file evidence only and always record an Express opt-in. One document cannot stand in for many people, so cells that need written proof \u2014 automated marketing, most obviously \u2014 are rejected per contact and counted in the result rather than quietly granted."
        },
        {
          type: "note",
          variant: "tip",
          text: "Bulk actions are capped at 500 contacts per run."
        }
      ]
    },
    {
      slug: "send-an-intake-link",
      title: "Send the intake form to contacts",
      why: "Sending people their own prefilled form is the only way to turn an imported list into consent you can rely on.",
      route: "/contacts",
      requires: { permissions: ["contacts:update"], features: ["contacts"] },
      estimatedMinutes: 5,
      prereqs: ["contacts/intake-forms"],
      keywords: [
        "send intake link",
        "how do i send the intake form to a contact",
        "send intake form in bulk",
        "ask contacts to confirm their preferences",
        "prefilled intake link",
        "why was a contact skipped from the intake send",
        "collect marketing consent from an imported list"
      ],
      blocks: [
        {
          type: "clickPath",
          path: ["Contacts", "a contact", "Manage Consents", "Send intake link"]
        },
        {
          type: "steps",
          title: "One contact",
          items: [
            {
              text: "For one contact: open them, click **Manage Consents**, then **Send intake link** at the top right."
            },
            {
              text: 'In the "Send intake form link" dialog, choose **Email** or **SMS**.',
              note: "The channel you have no address for is disabled. If both are, the button itself is disabled with a tooltip explaining why."
            },
            {
              text: "Check the destination under **Delivering to**, then click **Send link**.",
              note: "The send is logged in the audit trail as a **Sent** entry."
            }
          ]
        },
        {
          type: "steps",
          title: "A whole list",
          items: [
            {
              text: "Switch to **Table** view and tick the rows you want."
            },
            {
              text: "Click **Send Intake Form** in the bulk toolbar \u2014 between *Send Document* and *Add Tag*."
            },
            {
              text: "Choose **Send via** \u2014 **Auto**, **Email** or **SMS**.",
              note: "Auto uses email where the contact has one and SMS otherwise."
            },
            {
              text: "Read the preflight before you commit.",
              note: 'It is computed from the rows already on screen, so it costs nothing: "18 of 20 will be sent", plus "2 will be skipped \u2014 no email address on file".'
            },
            {
              text: 'Confirm and watch it stream \u2014 "Sending 12/20...".',
              note: "The results panel replaces the form with sent / skipped / failed tiles and a **Not sent** list grouped by reason. It does not auto-close, because the skips are the part worth reading."
            }
          ]
        },
        {
          type: "note",
          variant: "compliance",
          text: "Contacts are skipped when they have no address for the chosen channel, when they have affirmatively withdrawn consent on it, or when they are DNC-listed. Contacts who have simply never opted in are not skipped \u2014 they are the whole point of the exercise."
        }
      ]
    },
    {
      slug: "resend-double-opt-in",
      title: "Resend a double opt-in confirmation",
      why: "A contact stuck as Pending has consented on your form but is not yet reachable, and a resend is the only way to finish it.",
      route: "/contacts",
      requires: { permissions: ["contacts:update"], features: ["contacts"] },
      estimatedMinutes: 2,
      keywords: [
        "resend double opt in",
        "how do i resend a confirmation",
        "contact stuck awaiting confirmation",
        "pending consent",
        "confirmation link expired",
        "double opt in resend button",
        "contact never clicked the confirmation link"
      ],
      blocks: [
        { type: "clickPath", path: ["Contacts", "a contact", "Consent preferences", "Resend"] },
        {
          type: "steps",
          items: [
            {
              text: "Open the contact and find the **Consent preferences** card.",
              note: 'A channel mid double opt-in shows a blue banner: "Awaiting {SMS|email} confirmation from contact".'
            },
            {
              text: "Click **Resend** beside that banner.",
              note: "It cancels the outstanding confirmation and issues a fresh one, which expires 72 hours later."
            },
            {
              text: "Check the cell afterwards. It reads **Pending**, not **Granted**.",
              note: "Until the contact clicks the link the channel is not opted in, and nothing may be sent on it."
            }
          ]
        },
        {
          type: "note",
          variant: "prereq",
          text: "This state only exists when double opt-in is switched on. It is a Settings, Compliance option, and the platform can decide whether tenants may change it at all \u2014 so if you never see a Pending cell, that is why."
        }
      ]
    },
    {
      slug: "download-consent-log-pdf",
      title: "Download a contact's consent log",
      why: "It is the single document you hand over when a regulator, a carrier or a lawyer asks what consent you hold.",
      route: "/contacts",
      requires: { permissions: ["contacts:read"], features: ["contacts"] },
      estimatedMinutes: 2,
      prereqs: ["contacts/read-the-consent-matrix"],
      keywords: [
        "consent log pdf",
        "how do i download proof of consent",
        "tcpa proof",
        "export consent history",
        "log pdf button",
        "evidence of opt in",
        "consent record for a complaint"
      ],
      blocks: [
        { type: "clickPath", path: ["Contacts", "a contact", "Consent preferences", "Log PDF"] },
        {
          type: "steps",
          items: [
            {
              text: "Open the contact and scroll to the **Consent preferences** card."
            },
            {
              text: "Click **Log PDF** at the bottom of the card, next to **Manage**.",
              note: "It builds the PDF in your browser and downloads it straight away \u2014 there is no job to wait for."
            },
            {
              text: "Hand the file over as it is.",
              note: "It is written for someone outside the company: plain-English labels rather than internal keywords, a Permitted Message Types section, a per-channel marketing column, the consent level, the opt-in and opt-out history, and any signatures on record. IP addresses appear only for actions the contact took themselves."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "Take the PDF at the moment you need it rather than filing one away. It is generated from live data, so a fresh download always reflects the current record and the full audit trail behind it."
        }
      ]
    },
    {
      slug: "restore-a-deleted-custom-field",
      title: "Restore a custom field you deleted",
      why: "Deleting a field hides its values rather than destroying them, so a mistaken delete is fully recoverable.",
      route: "/contacts",
      requires: { permissions: ["contacts:read"], features: ["contacts"] },
      estimatedMinutes: 3,
      prereqs: ["contacts/custom-fields"],
      keywords: [
        "restore a custom field",
        "i deleted a custom field by mistake",
        "undelete custom field",
        "deleted fields section",
        "does deleting a custom field delete the data",
        "bring back a contact field",
        "recover custom field values"
      ],
      blocks: [
        {
          type: "clickPath",
          path: ["Sidebar", "Contacts", "Custom Fields", "Deleted fields", "Restore"]
        },
        {
          type: "steps",
          items: [
            {
              text: "Click **Custom Fields** in the Contacts page header."
            },
            {
              text: "Scroll to the **Deleted fields** section at the bottom of the drawer."
            },
            {
              text: "Click **Restore** on the row you want back.",
              note: "The field returns to the end of the active list and its saved values reappear on every contact card."
            },
            {
              text: "If the restore fails, read the message: another active field has taken the deleted one's display name.",
              note: "Rename or remove the newer field, then restore again."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "If you recreate the field by name instead, **Add Field** notices: typing a name that matches a deleted field shows an amber warning that creating it will restore that field along with its previously saved values. The create endpoint reuses the soft-deleted row rather than reserving the key forever."
        },
        {
          type: "note",
          variant: "warning",
          text: 'Deleting a field only removes it from forms and cards \u2014 the "Delete Custom Field" dialog says so. Values already saved on contacts stay stored, which is why this recovery works at all.'
        }
      ]
    }
  ]
};

// src/getting-started/articles/templates.ts
var templatesArticles = {
  templates: [
    {
      slug: "filter-the-library",
      title: "Find a template in the library",
      why: "All three channels share one list, so without the filters you are scrolling past two channels you did not want.",
      route: "/templates",
      requires: { permissions: ["templates:read"], features: ["templates"] },
      estimatedMinutes: 2,
      keywords: [
        "how do i find a template",
        "how do i filter my templates",
        "show only sms templates",
        "search templates",
        "where are my email templates",
        "archived templates",
        "template categories filter",
        "grid or list view templates"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Templates", "Messages"] },
        {
          type: "steps",
          items: [
            {
              text: "Open **Messages** \u2014 every template you have, all three channels, one list."
            },
            {
              text: "Click a channel pill: **All**, **SMS**, **Email** or **Voice**."
            },
            {
              text: "Set the status filter \u2014 **All**, **Active** or **Archived**."
            },
            {
              text: "Set the **Category** filter to whatever you categorised the template as."
            },
            {
              text: 'Type into "Search templates..." for anything the filters cannot express.'
            },
            {
              text: "Toggle grid or list. The choice sticks between visits.",
              note: "The channel filter lives in the URL, so /templates?filter=sms is a link you can bookmark or share. Accepted values are sms, email, voice and all."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: 'Two empty states, two different problems. "No templates yet" means the library really is empty. "No templates match your filters" means you have templates and the current filters are hiding them \u2014 clear the filters rather than creating a duplicate.'
        },
        { type: "action", label: "Open Messages", to: { kind: "app", route: "/templates" } }
      ]
    },
    {
      slug: "quick-create-a-template",
      title: "Quick-create a single-channel template",
      why: "When you only want one SMS or one email, the AI route makes you discard two drafts you never asked for.",
      route: "/templates/create",
      requires: { permissions: ["templates:create"], features: ["templates"] },
      estimatedMinutes: 4,
      keywords: [
        "how do i create a single template",
        "how do i create an sms template without ai",
        "start from blank template",
        "quick create template",
        "skip the ai template generator",
        "create one email template",
        "blank voice template"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Templates", "Messages", "Create Template"] },
        {
          type: "steps",
          items: [
            {
              text: "Click **Create Template** on the Messages page."
            },
            {
              text: 'Ignore the prompt box. Scroll past it to the divider reading "Or skip the AI and create one".',
              note: "The AI route drafts all three channels at once, so using it for a single SMS means discarding two drafts you never asked for."
            },
            {
              text: 'Click **SMS Template**, **Email Template** or **Voice Template** \u2014 each labelled "Start from blank".',
              note: "No AI request is made and no AI credits are consumed, so this route works even when your AI balance is exhausted."
            },
            {
              text: "You land straight on the review step with a single card and its editor already open.",
              note: "Fill in the name and the content, then save."
            }
          ]
        }
      ]
    },
    {
      slug: "save-generated-templates",
      title: "Save templates from the review step",
      why: "Generation does not save anything \u2014 until you press the save button on step 2 the drafts exist only in the browser.",
      route: "/templates/create/preview",
      requires: { permissions: ["templates:create"], features: ["templates"] },
      estimatedMinutes: 4,
      prereqs: ["templates/ai-create"],
      keywords: [
        "how do i save a generated template",
        "save n templates button",
        "step 2 review and edit",
        "keep only the sms version",
        "my generated templates disappeared",
        "back to generate link",
        "why did my template not save"
      ],
      blocks: [
        {
          type: "clickPath",
          path: ["Sidebar", "Templates", "Messages", "Create Template", "Generate All Templates"]
        },
        {
          type: "steps",
          items: [
            {
              text: 'After **Generate All Templates**, you land on "Review your templates", badged **Step 2 \u2014 Review & edit**.',
              note: "Nothing has been saved yet. The drafts exist only in your browser."
            },
            {
              text: "Turn off the channel toggle pills for any draft you do not want to keep.",
              note: "Turning one off hides its card and drops it from the count on the save button."
            },
            {
              text: "Edit each remaining draft inline, and give it a **Template name**, an optional **Description** and a **Category**."
            },
            {
              text: "Click **Save {N} Template(s)** \u2014 the count tracks your toggles.",
              note: "Saved templates land in the library and are immediately selectable from a send."
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: "This step does not auto-save. Refreshing the page \u2014 unlike the Send wizard \u2014 puts you back at the start of the create flow with the drafts gone. **Back to Generate** is safe: the drafts survive it."
        }
      ]
    },
    {
      slug: "name-and-categorise",
      title: "Name, describe and categorise a template",
      why: "The name and category are the only things you have to find a template by later, and every send-time picker shows them.",
      route: "/templates/create/preview",
      requires: { permissions: ["templates:create"], features: ["templates"] },
      estimatedMinutes: 3,
      keywords: [
        "how do i name a template",
        "what are the template categories",
        "how do i categorise a template",
        "template description field",
        "rename a template",
        "why is my template hard to find"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Templates", "Messages", "Create Template"] },
        {
          type: "steps",
          items: [
            {
              text: "Fill in **Template name** \u2014 what the template is for, in a few words.",
              note: "Required. AI-generated drafts arrive with a suggested name you can overwrite."
            },
            {
              text: "Add a **Description (optional)** saying when to use it.",
              note: "On generated drafts this is seeded from the start of your prompt."
            },
            {
              text: "Pick a **Category**.",
              note: "No category, Notification, Reminder, Confirmation, Marketing, Welcome, Follow-up, Receipt or Other. Optional, and never guessed for you \u2014 generated drafts always start with none."
            },
            {
              text: "To rename or recategorise later, open **Edit** from the template card in the library.",
              note: "The same three fields appear there as the **Template Details** card in the standalone SMS and Email editors."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            {
              name: "Template name",
              value: "What the template is for, in a few words.",
              note: "Required. AI-generated drafts arrive with a suggested name you can overwrite."
            },
            {
              name: "Description (optional)",
              value: "A longer note about when to use it.",
              note: "Seeded from the start of your prompt on generated drafts."
            },
            {
              name: "Category",
              value: "One of No category, Notification, Reminder, Confirmation, Marketing, Welcome, Follow-up, Receipt or Other.",
              note: "Optional, and never guessed for you."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "The name and category are the only things you have to find a template by later, and every send-time picker shows them. Name it for the job it does, not for the day you wrote it."
        }
      ]
    },
    {
      slug: "insert-variables",
      title: "Insert variables into a template",
      why: "Picking a variable from the list is the only way to be sure the tag you typed actually exists.",
      estimatedMinutes: 3,
      requires: { permissions: ["templates:read"], features: ["templates"] },
      prereqs: ["templates/merge-tags"],
      keywords: [
        "how do i insert a variable",
        "how do i personalise a template",
        "insert variable chip row",
        "all variables picker",
        "add first name to a template",
        "use a custom field in a template",
        "variables in this template panel"
      ],
      blocks: [
        {
          type: "steps",
          items: [
            {
              text: "In an inline editor \u2014 the template review step, or the Send wizard \u2014 use the **Insert Variable** chip row above the field.",
              note: "It opens with a few favourites for that channel, then every custom field your tenant has defined."
            },
            {
              text: "Click **All variables** in that row for the full categorised picker."
            },
            {
              text: "In the standalone SMS, Email and Voice editors, use the variable picker instead.",
              note: "It lists the system tags plus your own fields, grouped as **Contact Custom Fields** and **Organization**."
            },
            {
              text: "Let it insert the namespaced form \u2014 {{custom.<key>}}.",
              note: "Typing the bare {{<key>}} works just as well, but only if you spell it right, which is the whole argument for the picker."
            },
            {
              text: "In the email editor, insert into the **Subject Line** the same way.",
              note: "The subject takes variables and is checked separately from the body."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "The chips come from the same catalogue the delivery worker resolves at send time, so nothing you pick from the list can send blank. Typing a tag by hand is where blanks come from."
        }
      ]
    },
    {
      slug: "voice-call-type",
      title: "Set a voice script call type and call settings",
      why: "The call type decides what kind of call the script belongs to, and it is required before the script can be saved.",
      route: "/voice-scripts/new",
      requires: { features: ["voice_scripts"] },
      estimatedMinutes: 4,
      keywords: [
        "how do i set the call type on a voice script",
        "what is outbound ai",
        "inbound hotline script",
        "three way merge call",
        "voice script call settings",
        "max duration retry attempts",
        "preview a voice script"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Templates", "Messages", "Voice"] },
        {
          type: "steps",
          items: [
            {
              text: "Open a voice script for editing, or start a new one."
            },
            {
              text: "Enter the **Script Name** \u2014 up to 100 characters, with a live counter."
            },
            {
              text: "Enter the **Description** \u2014 up to 500 characters."
            },
            {
              text: "Choose a **Call Type**: outbound AI, inbound hotline, or three-way merge.",
              note: "Required. The script cannot be saved without it."
            },
            {
              text: "Write the **Script Content** \u2014 between 50 and 5,000 characters, using single-brace variables.",
              note: 'The placeholder says it: "Write your voice script here... Use {variable_name} to insert dynamic values."'
            },
            {
              text: "Expand **Call Settings (optional)** to change **Max Duration** (minutes), **Retry Attempts** or **Retry Interval** (hours).",
              note: "Defaults are 10 minutes, 2 retries and 24 hours. Add **Tags** here too."
            },
            {
              text: "Click **Preview** to render the script with sample values and an estimated duration, then **Save**.",
              note: "The save-status indicator reads Saving, Saved, Save failed or Unsaved changes."
            }
          ]
        }
      ]
    },
    {
      slug: "duplicate-a-template",
      title: "Duplicate a template",
      why: "Copying a working template is safer than editing it, because anything already sending from it keeps the wording it had.",
      route: "/templates",
      requires: { permissions: ["templates:create"], features: ["templates"] },
      estimatedMinutes: 2,
      keywords: [
        "how do i duplicate a template",
        "how do i copy a template",
        "clone a template",
        "make a variant of a template",
        "template copy suffix",
        "duplicate a voice script"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Templates", "Messages", "Duplicate"] },
        {
          type: "steps",
          items: [
            {
              text: "Find the template in the library and click **Duplicate** on its card."
            },
            {
              text: 'The copy appears with " (Copy)" appended to the name.',
              note: "Voice scripts duplicate the same way, tags included."
            },
            {
              text: "Click **Edit** on the copy, rename it, and change the wording."
            },
            {
              text: "Leave the original alone.",
              note: "That is the point: anything already sending from it keeps the wording it had."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "Duplicating is also how you build channel variants of the same message. The library stores one channel per template, so an SMS and an email version are always two separate rows."
        }
      ]
    },
    {
      slug: "archive-or-delete",
      title: "Archive, restore or delete a template",
      why: "Archiving takes a template out of the pickers without destroying the record behind messages you already sent.",
      route: "/templates",
      requires: { permissions: ["templates:delete"], features: ["templates"] },
      estimatedMinutes: 2,
      keywords: [
        "how do i delete a template",
        "how do i archive a template",
        "how do i restore an archived template",
        "hide a template from the send picker",
        "get rid of an old template",
        "where did my archived templates go"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Templates", "Messages", "Archive"] },
        {
          type: "steps",
          items: [
            {
              text: "To retire a template, click **Archive** on its card.",
              note: "It drops out of the default view immediately."
            },
            {
              text: "To see archived templates again, switch the status filter from **Active** to **Archived**."
            },
            {
              text: "To bring one back, click **Restore** on the archived card."
            },
            {
              text: "Use **Delete** only when you genuinely want the record gone.",
              note: "Archiving already stops a template being used \u2014 **Send** is offered on active templates only."
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: "Archiving takes a template out of the pickers without destroying the record behind messages you already sent. Deleting is the one that does not. Reach for Archive first."
        }
      ]
    },
    {
      slug: "send-from-a-template",
      title: "Send a campaign straight from a template",
      why: "You do not have to start at the Send screen \u2014 the library can launch a send with the template already loaded.",
      route: "/templates",
      requires: { permissions: ["campaigns:send"], features: ["templates", "send"] },
      estimatedMinutes: 3,
      prereqs: ["send/the-send-screen"],
      keywords: [
        "how do i send a template",
        "how do i send a campaign from a template",
        "send button on a template card",
        "send campaign from the editor",
        "use a saved template in a send",
        "why is send greyed out on my template"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Templates", "Messages", "Send"] },
        {
          type: "steps",
          items: [
            {
              text: "Find the template in the library and click **Send** on its card.",
              note: "Offered on active templates only."
            },
            {
              text: "The send flow opens with that template already chosen.",
              note: "You go straight to recipients and review rather than starting from the Send screen and picking it again."
            },
            {
              text: "While editing an active SMS or Email template, use the **Send Campaign** button instead.",
              note: "Same shortcut, without leaving the editor."
            },
            {
              text: "From inside the Send wizard, reach the same templates per channel: set a channel card to **Template** mode and pick from the selector.",
              note: "Or @-mention the template in the prompt box before generating."
            }
          ]
        },
        {
          type: "note",
          variant: "prereq",
          text: "Sending from here needs the campaigns:send permission, which is separate from the permission to read and edit templates. Without it the library still opens, but the Send action does not appear."
        }
      ]
    }
  ]
};

// src/getting-started/articles/send.ts
var sendArticles = {
  send: [
    {
      slug: "compose-an-sms",
      title: "Compose an SMS",
      why: "An SMS that spills past one segment costs more to send and can arrive split in two.",
      route: "/send",
      requires: { permissions: ["campaigns:send"], features: ["send"] },
      estimatedMinutes: 4,
      keywords: [
        "how do i compose an sms",
        "how do i send a text message",
        "write an sms",
        "sms character limit",
        "sms segments",
        "text message send",
        "send a text to my contacts",
        "sms opt out wording"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Send", "SMS", "Generate Draft"] },
        {
          type: "steps",
          items: [
            {
              text: "On the Send screen, describe the message in the prompt box."
            },
            {
              text: "Turn on the **SMS** channel pill, and turn **Email** off if you only want a text.",
              note: "Email is on by default."
            },
            {
              text: "Click **Generate Draft**.",
              note: 'With no channel selected the button stays disabled and an inline note reads "Select at least one delivery channel".'
            },
            {
              text: 'On "Customize your content", find the SMS card and click **Edit** to open the inline editor.'
            },
            {
              text: "Watch the live character and segment counter as you write.",
              note: "Plain GSM-7 fits 160 characters in one segment, or 153 per part once it splits. One accented or emoji character switches the whole message to UCS-2, dropping those limits to 70 and 67. The progress bar shifts green to amber to red as you cross them."
            },
            {
              text: "Switch the card between **Template**, **Compose** and **AI** if the draft is not what you wanted.",
              note: "Template loads a saved SMS template into the card, Compose is hand-written, AI is the generated draft."
            },
            {
              text: "Continue to **Select Recipients** once the card has content."
            }
          ]
        },
        {
          type: "note",
          variant: "compliance",
          text: "The editor carries the reminder for a reason: keep messages under 160 characters to avoid splitting into multiple segments, and always include opt-out instructions for marketing messages."
        }
      ]
    },
    {
      slug: "compose-an-email",
      title: "Compose an email",
      why: "Email is the only channel with a subject line, a preview text and formatting, and all three are edited in the same card.",
      route: "/send",
      requires: { permissions: ["campaigns:send"], features: ["send"] },
      estimatedMinutes: 4,
      keywords: [
        "how do i compose an email",
        "how do i send an email campaign",
        "email subject line",
        "preview text email",
        "format an email body",
        "send an email to contacts",
        "email unsubscribe link"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Send", "Email", "Generate Draft"] },
        {
          type: "steps",
          items: [
            {
              text: "Describe the message in the prompt box and click **Generate Draft**.",
              note: "Email is the default channel, so it is already on."
            },
            {
              text: "Click **Edit** on the Email card to open the inline editor beside the live preview."
            },
            {
              text: "Write the **Subject**. Merge tags are allowed.",
              note: "Required, and trimmed before saving."
            },
            {
              text: "Set the **Preview text** \u2014 the snippet an inbox shows after the subject.",
              note: "Optional, and the most-wasted line in email. Fill it in."
            },
            {
              text: "Write the **Body** using the toolbar: Bold, Italic, H1, H2, bullet and ordered lists, link, undo and redo.",
              note: "Required."
            },
            {
              text: "Continue to **Select Recipients**."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            {
              name: "Subject",
              value: "The subject line. Merge tags are allowed.",
              note: "Required. Trimmed before saving."
            },
            {
              name: "Preview text",
              value: "The snippet an inbox shows after the subject.",
              note: "Optional, but recommended."
            },
            {
              name: "Body",
              value: "The message, with the formatting toolbar.",
              note: "Required."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "An unsubscribe link and your business mailing address are added automatically to every marketing email. Do not add your own."
        }
      ]
    },
    {
      slug: "compose-a-voice-call",
      title: "Compose a voice call",
      why: "Voice is a spoken script rather than a written message, so it has its own length rules and its own variable syntax.",
      route: "/send",
      requires: { permissions: ["campaigns:send"], features: ["send"] },
      estimatedMinutes: 4,
      keywords: [
        "how do i send a voice call",
        "how do i compose a voice message",
        "voice script send",
        "automated phone call to contacts",
        "call script length",
        "voice call duration",
        "voice variables single brace"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Send", "Voice", "Generate Draft"] },
        {
          type: "steps",
          items: [
            {
              text: "Turn on the **Voice** channel pill BEFORE clicking **Generate Draft**.",
              note: "Enabling it afterwards means generating again."
            },
            {
              text: "Click **Generate Draft**. A **Voice Call** card appears alongside any other channel you enabled."
            },
            {
              text: "Click **Edit** and write the script \u2014 between 50 and 5,000 characters.",
              note: "The editor shows a live word count and an estimated duration calculated at 150 words per minute, so you can hear roughly how long the call will run before you send it."
            },
            {
              text: "Use single-brace variables: {first_name}.",
              note: "Not the double braces SMS and email use."
            },
            {
              text: "Continue to **Select Recipients**."
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: "Pasting an SMS body into the voice card will not substitute \u2014 the braces are wrong and every variable will read out literally. Voice is a separate channel with its own consent and its own syntax."
        }
      ]
    },
    {
      slug: "attach-a-resource",
      title: "Attach a template, form or document to the prompt",
      why: "Naming an existing resource in the prompt is what routes the send into the right branch instead of drafting a message from scratch.",
      route: "/send",
      requires: { permissions: ["campaigns:send"], features: ["send"] },
      estimatedMinutes: 3,
      keywords: [
        "how do i attach a template to a send",
        "how do i mention a document in the send prompt",
        "@ mention resource picker",
        "attach a form to a message",
        "plus button on the send screen",
        "send an existing template",
        "pick a document instead of writing a message"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Send", "+"] },
        {
          type: "steps",
          items: [
            {
              text: "Click the **+** button under the prompt box to open the resource picker."
            },
            {
              text: "Search it. It covers your documents, forms, message templates, contacts, tags and pipeline stages.",
              note: "What you choose drops into the prompt as an @-mention."
            },
            {
              text: "Mention a **document** to turn the send into a signature request, with that document already selected."
            },
            {
              text: "Mention a **form** to turn the send into a form send, with that form already selected."
            },
            {
              text: "Mention a **message template** to open the matching channel card in **Template** mode."
            },
            {
              text: "Mention nothing and the prompt is drafted by AI for the channels you picked.",
              note: "That is the default branch, not a fallback."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "The **Templates** shortcut below the composer skips the AI entirely and takes you into the wizard with the channel cards already set to Template mode."
        }
      ]
    },
    {
      slug: "pick-a-tone",
      title: "Pick a tone for the draft",
      why: "Tone is the one dial that changes how the generated draft reads, and it is set before generation rather than after.",
      route: "/send",
      requires: { permissions: ["campaigns:send"], features: ["send", "ai_generation"] },
      estimatedMinutes: 2,
      keywords: [
        "how do i change the tone of a message",
        "tone dropdown send",
        "professional friendly casual",
        "make the message sound friendlier",
        "ai writing style",
        "where is the urgent tone"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Send", "Tone"] },
        {
          type: "steps",
          items: [
            {
              text: "Set the **Tone** dropdown beside the channel pills BEFORE generating.",
              note: "Tone is applied at generation, not afterwards."
            },
            {
              text: "Choose **Professional**, **Friendly** or **Casual**. Professional is the default."
            },
            {
              text: "Click **Generate Draft**. The tone carries into the wizard with the prompt, the channels and any recipients."
            },
            {
              text: "To change tone afterwards, click **Back** to the entry screen \u2014 your prompt text is restored \u2014 pick the new tone, and click **Generate Draft** again.",
              note: "There is no re-tone button inside the wizard."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "Urgent is not offered here. It exists on the template creation screen at /templates/create, so generate the template there and load it into the send."
        }
      ]
    },
    {
      slug: "request-a-signature",
      title: "Send a document for signature",
      why: "A signature request is a different branch of the send flow, not a message with a file attached.",
      route: "/send",
      requires: { permissions: ["documents:send"], features: ["send", "forms_documents"] },
      estimatedMinutes: 5,
      prereqs: ["documents/create-a-document"],
      keywords: [
        "how do i send a document for signature",
        "how do i request a signature",
        "send an agreement to be signed",
        "esignature request",
        "send a contract for signing",
        "document templates vs voice consent templates",
        "signature request setup"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Send", "Request a Signature"] },
        {
          type: "steps",
          items: [
            {
              text: "Click the **Request a Signature** shortcut below the prompt box.",
              note: 'The wizard opens on "Set up your signature request" \u2014 a different branch, not a message with a file attached.'
            },
            {
              text: 'In the **TEMPLATE** section \u2014 "Choose the content to send for signature" \u2014 switch between **Document Templates** and **Voice Consent Templates**.'
            },
            {
              text: "Pick the item to send."
            },
            {
              text: "Set the delivery channels the invitation goes out on."
            },
            {
              text: "Continue to recipients, then to review, and send or schedule as normal."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "From Contacts, the **Send Document** action jumps straight to this setup step with the selected contacts already filled in."
        },
        {
          type: "note",
          variant: "compliance",
          text: "Signing invitations are always transactional, so they are not gated by marketing consent the way a broadcast is."
        }
      ]
    },
    {
      slug: "send-a-form",
      title: "Send a form to contacts",
      why: "Sending a form is how you collect answers or consent back from people rather than telling them something.",
      route: "/send",
      requires: { permissions: ["campaigns:send"], features: ["send", "forms_documents"] },
      estimatedMinutes: 4,
      prereqs: ["forms/build-your-first-form"],
      keywords: [
        "how do i send a form",
        "how do i send a form to my contacts",
        "send a questionnaire",
        "form send setup",
        "collect answers from contacts",
        "send an intake form by sms"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Send", "Send a Form"] },
        {
          type: "steps",
          items: [
            {
              text: "Click the **Send a Form** shortcut below the prompt box.",
              note: 'The wizard opens on "Set up your form send".'
            },
            {
              text: 'In the **FORM** section \u2014 "Choose the form to send to recipients" \u2014 pick the form.'
            },
            {
              text: "Set the delivery channels the invitation goes out on."
            },
            {
              text: "Continue to recipients, then review, then send or schedule."
            },
            {
              text: "Alternatively, @-mention a form in the prompt box.",
              note: "That reaches the same branch with the form already selected."
            }
          ]
        },
        { type: "action", label: "Open Send", to: { kind: "app", route: "/send" } }
      ]
    },
    {
      slug: "read-the-compliance-strip",
      title: "Read the compliance strip and the message type badge",
      why: "The message type shown here is what decides who is eligible to receive the send two steps later.",
      route: "/send",
      requires: { permissions: ["campaigns:send"], features: ["send"] },
      estimatedMinutes: 4,
      keywords: [
        "what does the compliance strip mean",
        "marketing informational transactional badge",
        "tcpa compliant badge",
        "why is my message marked marketing",
        "compliance issue on my draft",
        "message type send wizard",
        "how do i change the message type"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Send", "Generate Draft"] },
        {
          type: "steps",
          items: [
            {
              text: "Look under each channel card preview, below the content, for the compliance strip."
            },
            {
              text: "Read the message type badge \u2014 **Marketing**, **Informational** or **Transactional**.",
              note: "This is what decides who is eligible to receive the send two steps later."
            },
            {
              text: "Read the second badge. **TCPA Compliant** means the content passes.",
              note: "When it fails it reads **Compliance Issue**, with the top risk named beneath it."
            },
            {
              text: "Check every active channel, not just the one you wrote.",
              note: "The most restrictive type across your active channels governs the whole send: one marketing channel puts the entire campaign under the marketing rules."
            },
            {
              text: "Act on a **Compliance Issue** before continuing \u2014 but note nothing forces you to.",
              note: "The panel is advisory. It never stops you moving on to Select Recipients; it only tells you what the eligibility numbers are about to look like."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "The strip appears for all three source modes. A template you loaded or wording you typed by hand is classified exactly like an AI draft, because that classification drives recipient eligibility and the message type recorded on the campaign."
        }
      ]
    },
    {
      slug: "regenerate-and-fix",
      title: "Use Regenerate & Fix on a flagged draft",
      why: "It rewrites the draft against the specific risk that was flagged, which is faster and more reliable than guessing at the wording yourself.",
      route: "/send",
      requires: { permissions: ["campaigns:send"], features: ["send", "ai_generation"] },
      estimatedMinutes: 3,
      keywords: [
        "how do i fix a compliance issue",
        "what is regenerate and fix",
        "my message failed the compliance check",
        "rewrite a flagged message",
        "regenerate the draft",
        "why is regenerate and fix missing"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Send", "Generate Draft", "Regenerate & Fix"] },
        {
          type: "steps",
          items: [
            {
              text: "Find the channel card showing **Compliance Issue** and read the risk named under it."
            },
            {
              text: "Click **Regenerate & Fix** on that card.",
              note: "Offered on AI-drafted cards only."
            },
            {
              text: "Wait. It re-runs the composer from your original prompt with the flagged risks appended.",
              note: "The result is re-checked automatically, so the badge updates on its own \u2014 there is nothing to click afterwards."
            },
            {
              text: "If the card is in **Template** or **Compose** mode, there is no button. Open the inline editor and fix the wording yourself.",
              note: "Or switch the card to **AI** and generate a fresh draft, which brings the button back."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "It rewrites against the specific risk that was flagged, which is faster and more reliable than guessing at the wording yourself."
        }
      ]
    },
    {
      slug: "assign-signers",
      title: "Assign signers on a multi-signer document",
      why: "A document with more than one signer role needs a named contact per role before it can go out at all.",
      route: "/send",
      requires: { permissions: ["documents:send"], features: ["send", "forms_documents"] },
      estimatedMinutes: 4,
      prereqs: ["documents/signers-and-fields"],
      keywords: [
        "how do i assign signers",
        "how do i send a document to two signers",
        "multi signer document send",
        "assign a contact to each signer role",
        "signing order",
        "why does step 2 say assign signers"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Send", "Request a Signature", "Assign signers"] },
        {
          type: "steps",
          items: [
            {
              text: "Pick a document with more than one signer role. Step 2 changes on its own.",
              note: 'Instead of "Who are you sending to?" it reads **Assign signers** \u2014 "Assign a contact to each signer role".'
            },
            {
              text: "Pick the contact who fills each role."
            },
            {
              text: "Continue to review, then send or schedule."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "Signers are notified in order. Only the first signer is emailed when the send goes out; each later signer is notified once the one before them finishes."
        },
        {
          type: "note",
          variant: "warning",
          text: "This also happens when the document was chosen outside the wizard \u2014 from a Send icon, an @-mention or a suggestion \u2014 because the wizard loads the document signer list first. If that lookup fails it falls back to treating the document as single-signer, so check the step you land on."
        }
      ]
    },
    {
      slug: "fix-unresolved-merge-tags",
      title: "Fix an unresolved merge tag before sending",
      why: "A tag that cannot resolve sends as blank text, so the recipient sees a gap where their name should be.",
      route: "/send",
      requires: { permissions: ["campaigns:send"], features: ["send"] },
      estimatedMinutes: 3,
      prereqs: ["templates/merge-tags"],
      keywords: [
        "how do i fix an unresolved merge tag",
        "why is my merge tag blank",
        "unresolved merge tags dialog",
        "this custom field was deleted",
        "isnt a recognized merge tag",
        "amber warning on my message card",
        "send anyway merge tag"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Send", "Generate Draft", "Edit"] },
        {
          type: "steps",
          items: [
            {
              text: "Look for the amber warning banner on a channel card.",
              note: `It names the cause: "{{tag}} \u2014 this custom field was deleted. It will send as blank text." or "{{tag}} isn't a recognized merge tag. It will send as blank text."`
            },
            {
              text: "Click **Edit** on the flagged card to open the inline editor."
            },
            {
              text: "Remove the tag, or replace it using the **Insert Variable** row.",
              note: "The row only offers tags that exist, which is the point of using it."
            },
            {
              text: "Save the card. The banner clears once no unresolvable tag remains.",
              note: "The preview stops rendering the tag as a blank red block at the same moment."
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: 'If you click **Send Now** or **Schedule Campaign** with a tag still unresolved, an "Unresolved merge tags" dialog lists them and offers **Go back and fix** alongside **Send anyway** / **Schedule anyway**. The send is never hard-blocked, because re-creating a deleted custom field with the same key makes the tag work again \u2014 but a blank where a name should be is what the recipient sees.'
        }
      ]
    },
    {
      slug: "sending-groups",
      title: "Understand sending groups on the review step",
      why: "Sending groups are about calling hours only, and reading them as an eligibility count is the easiest mistake to make on this screen.",
      route: "/send",
      requires: { permissions: ["campaigns:send"], features: ["send"] },
      estimatedMinutes: 4,
      prereqs: ["compliance/calling-hours"],
      keywords: [
        "what are sending groups",
        "why is my send deferred",
        "when to send section",
        "calling hours on the review screen",
        "why do some recipients go out later",
        "sending groups vs delivery eligibility"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Send", "Review & Send", "WHEN TO SEND"] },
        {
          type: "steps",
          items: [
            {
              text: "On the review step, find the **WHEN TO SEND** section.",
              note: "It groups your recipients into **SENDING GROUPS** by the calling-hour rules that apply where they are."
            },
            {
              text: "Read each group's disposition: sent now, scheduled by you, or deferred automatically until its window opens.",
              note: "A deferred group is not a problem \u2014 it is the product refusing to text someone at 3am."
            },
            {
              text: "Expand a group to schedule a specific time for it."
            },
            {
              text: "Then look UP at **DELIVERY ELIGIBILITY** for the number that actually matters.",
              note: "Sending groups say when; eligibility says who."
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: 'These counts are scheduling only. They do not check consent and they are not an eligibility signal. Reading a sending-group count as "how many people will get this" is the easiest mistake to make on this screen.'
        }
      ]
    },
    {
      slug: "schedule-a-campaign",
      title: "Schedule a campaign for later",
      why: "Scheduling holds the whole campaign until a time you choose instead of sending the moment you finish the wizard.",
      route: "/send",
      requires: { permissions: ["campaigns:send"], features: ["send"] },
      estimatedMinutes: 3,
      keywords: [
        "how do i schedule a campaign",
        "how do i send a message later",
        "schedule campaign button",
        "send later",
        "pick a send date and time",
        "change the timezone of a send",
        "where do scheduled campaigns go"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Send", "Review & Send", "Schedule Campaign"] },
        {
          type: "steps",
          items: [
            {
              text: 'Work through the wizard to the review step, "Ready to send".'
            },
            {
              text: "Give the campaign a name.",
              note: "Leave it and one is generated from the form, document or template name plus the date."
            },
            {
              text: "Choose to schedule rather than send immediately, and pick the date and time.",
              note: "It has to be in the future."
            },
            {
              text: "Check the timezone.",
              note: "It defaults to the one your browser reports, which is not always the one your recipients are in."
            },
            {
              text: "Click **Schedule Campaign**.",
              note: "The button reads **Send Now** when no schedule is set, so the label tells you which one you are about to do."
            },
            {
              text: "You land in the Inbox, where the campaign is listed with its scheduled status.",
              note: "Individual sending groups can still be given their own times in **WHEN TO SEND**."
            }
          ]
        },
        { type: "action", label: "Open the Inbox", to: { kind: "app", route: "/messaging" } }
      ]
    },
    {
      slug: "resume-a-draft",
      title: "Resume a saved send draft",
      why: "A send you walked away from is not lost \u2014 but it is only offered back to you in one place.",
      route: "/send",
      requires: { permissions: ["campaigns:send"], features: ["send"] },
      estimatedMinutes: 2,
      keywords: [
        "how do i resume a draft send",
        "where are my saved drafts",
        "i left the send wizard halfway",
        "draft campaigns panel",
        "recover an unfinished send",
        "delete a saved draft"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Send", "Draft Campaigns", "Resume"] },
        {
          type: "steps",
          items: [
            {
              text: "Go to the Send entry screen. Not into the wizard \u2014 the entry screen.",
              note: "Resuming is offered here only. If you are already partway through a different send, click **Back** first."
            },
            {
              text: "Find the **Draft Campaigns** panel.",
              note: 'Each row shows when it was saved \u2014 "Saved {time ago}".'
            },
            {
              text: "Click **Resume** on the row you want.",
              note: "The whole wizard is restored, including the step you were on."
            },
            {
              text: "Use the delete action on a row to clear a draft you will not go back to."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "Drafts save themselves as you move between steps, and again if you leave the page mid-send. They are held per user per tenant, up to 20 of them, and expire after seven days."
        }
      ]
    }
  ]
};

// src/getting-started/articles/forms.ts
var formsArticles = {
  forms: [
    {
      slug: "form-field-types",
      title: "Which field type to use",
      why: "Picking the wrong type is what produces unusable answers \u2014 free text where you needed a date, or a phone number nobody can dial.",
      route: "/forms-and-documents?filter=form",
      requires: { permissions: ["forms:read"], features: ["forms_documents"] },
      estimatedMinutes: 4,
      prereqs: ["forms/build-your-first-form"],
      keywords: [
        "field types",
        "what field types can a form have",
        "how do i add a dropdown to a form",
        "date field",
        "file upload field",
        "signature field on a form",
        "add a phone number question",
        "form question types"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Forms & Documents", "Edit"] },
        {
          type: "steps",
          items: [
            {
              text: "Decide first whether the block should collect an answer.",
              note: "Field blocks ask the respondent for something and store an answer. Content blocks \u2014 paragraph, heading, list, image, table, divider, spacer, section header, page break \u2014 only explain, and collect nothing."
            },
            {
              text: "For anything free-form, drag a **Text Field**, then set its input type.",
              note: "text, textarea, email, phone or number \u2014 so the answer is checked the way you would expect."
            },
            {
              text: "For a calendar date, use a **Date Field**, never a text field.",
              note: "A typed date arrives in a dozen formats and none of them sort."
            },
            {
              text: "For a choice, use a **Select Field** and pick dropdown, radio or checkbox, then edit the options."
            },
            {
              text: "For a document or photo, use **File Upload**.",
              note: "The submission stores a link to the uploaded file, not the file itself."
            },
            {
              text: "For an acknowledgement, use **Signature**.",
              note: "Fine as an acknowledgement. For a legally binding signature, send an agreement instead."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            {
              name: "Text Field",
              value: "Names, addresses, reference numbers, free comments",
              note: "Set its input type to text, textarea, email, phone or number."
            },
            {
              name: "Date Field",
              value: "A calendar date",
              note: "Use this rather than a text field."
            },
            {
              name: "Time Field",
              value: "A time of day"
            },
            {
              name: "Select Field",
              value: "A choice from a list you control",
              note: "Choose dropdown, radio or checkbox, then edit the options."
            },
            {
              name: "File Upload",
              value: "A document or photo from the respondent",
              note: "Stores a link to the file, not the file itself."
            },
            {
              name: "Signature",
              value: "A signature drawn inside the form",
              note: "For a legally binding signature, send an agreement instead."
            },
            {
              name: "Overlay Field",
              value: "A field positioned on top of an uploaded PDF",
              note: "Only appears on PDF-backed agreements, not on a normal form."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "A section header and a page break together turn one intimidating page into three short ones. Section headers become real headings on the public page, so they also help anyone using a screen reader."
        }
      ]
    },
    {
      slug: "field-properties-and-width",
      title: "Set a field's label, validation and width",
      why: "The properties panel is where a generic field becomes one that collects exactly what you need and rejects what you do not.",
      route: "/forms-and-documents?filter=form",
      requires: { permissions: ["forms:read"], features: ["forms_documents"] },
      estimatedMinutes: 5,
      prereqs: ["forms/build-your-first-form"],
      keywords: [
        "make a field required",
        "how do i make a form field required",
        "field validation",
        "help text",
        "placeholder text",
        "put two fields side by side",
        "field width half third",
        "edit field options"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Forms & Documents", "Edit", "Field Properties"] },
        {
          type: "steps",
          items: [
            {
              text: "Click any field to open the **Field Properties** panel.",
              note: "On the right on desktop, a bottom sheet on mobile. Everything about that one field is set here, including deleting it."
            },
            {
              text: "Write the **Label** \u2014 the question as the respondent reads it.",
              note: "It also becomes the column heading in the CSV export, so write it for both audiences."
            },
            {
              text: "Set **Required** if the form should not submit without it."
            },
            {
              text: "Set the **Input type** (text fields) or the **Options** and their display style (select fields)."
            },
            {
              text: "Add a **Placeholder** and **Help text** where the label alone is not enough."
            },
            {
              text: "Set **Validation** \u2014 minimum and maximum length, a pattern, and the message shown when it fails.",
              note: "The failure message is the one the respondent reads, so make it say what to do."
            },
            {
              text: "Set the **Width** \u2014 **Full**, **Half** or **Third**.",
              note: "The quickest win in the panel. Half and third-width fields sit side by side and stack back to full width on a phone, so First name and Last name take one line instead of two and the form looks half as long."
            }
          ]
        }
      ]
    },
    {
      slug: "form-consent-capture",
      title: "Capture email and SMS consent on a form",
      why: "A form is the cheapest lawful way to collect consent and the only one that leaves the contact's own choice on the record.",
      route: "/forms-and-documents?filter=form",
      requires: { permissions: ["forms:read"], features: ["forms_documents"] },
      estimatedMinutes: 6,
      prereqs: ["forms/form-fields-and-consent"],
      keywords: [
        "consent checkbox on a form",
        "how do i collect sms consent on a form",
        "opt in through a form",
        "email consent capture",
        "consent text",
        "can a form opt someone out",
        "communication preferences on a form"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Forms & Documents", "Edit", "Form Behavior"] },
        {
          type: "steps",
          items: [
            {
              text: "Open **Form Behavior** in the editor."
            },
            {
              text: "Turn on **Capture email consent**, **Capture SMS consent**, or both.",
              note: "Turn on only the channels this form is genuinely asking about \u2014 a channel you do not show can never be affected by a submission."
            },
            {
              text: "Write the consent text the respondent will read.",
              note: "It is snapshotted with every submission as the evidence, so it has to say what you are actually asking for."
            },
            {
              text: "Check the public form. The boxes appear in a **Communication Preferences** section above the submit button.",
              note: 'Under your consent text: "I agree to receive email communications" and "I agree to receive SMS messages".'
            }
          ]
        },
        {
          type: "note",
          variant: "compliance",
          text: "A submission restates the contact's preferences rather than adding to them. A box left unticked by someone you already hold consent for is an opt-out, recorded with a reason and an audit entry. Channels the form does not display are never touched."
        },
        {
          type: "note",
          variant: "warning",
          text: "Someone brand new who ticks nothing is not opted out \u2014 they had nothing to withdraw. Only a submission matched to an existing contact can revoke consent."
        },
        {
          type: "note",
          variant: "tip",
          text: `The page is honest with the visitor about this. Once someone has touched a consent box and ended with none selected, an amber notice appears: "You haven't selected any communication preferences. If you previously opted in, submitting this form will opt you out of every option shown above." Submit stays enabled, because that is how somebody opts out of everything.`
        }
      ]
    },
    {
      slug: "brand-a-form",
      title: "Brand the form",
      why: "A form carrying your logo and colours is completed far more often than one that looks like it came from a stranger.",
      route: "/forms-and-documents?filter=form",
      requires: { permissions: ["forms:read"], features: ["forms_documents"] },
      estimatedMinutes: 4,
      keywords: [
        "add my logo to a form",
        "how do i brand a form",
        "form colours",
        "form colors",
        "change the form button colour",
        "rounded corners on form fields",
        "form styling"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Forms & Documents", "Edit", "Branding"] },
        {
          type: "steps",
          items: [
            {
              text: "Open the **Branding** section in the form editor.",
              note: "The logo starts as the one on your profile, so most accounts need to change very little here."
            },
            {
              text: "Set the logo and where it sits."
            },
            {
              text: "Set the primary colour \u2014 it drives the accents and the submit button."
            },
            {
              text: "Set the input styling: border radius, border colour, background colour and text colour.",
              note: "Leave these alone unless you have a house style to match. The defaults are fine."
            },
            {
              text: "Check your profile has a logo and a company name.",
              note: "The hosted form carries a banner strip above the page with both. With neither set it falls back to the Respectly wordmark, which is not the name you want on your own form."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "A form carrying your logo and colours is completed far more often than one that looks like it came from a stranger. This is not decoration."
        },
        {
          type: "action",
          label: "Set your logo and brand colour",
          to: { kind: "app", route: "/settings/profile" }
        }
      ]
    },
    {
      slug: "form-behaviour-settings",
      title: "What happens after someone presses submit",
      why: "These settings control the last thing a respondent sees, and three of them do less than their labels suggest.",
      route: "/forms-and-documents?filter=form",
      requires: { permissions: ["forms:read"], features: ["forms_documents"] },
      estimatedMinutes: 4,
      keywords: [
        "change the submit button text",
        "success message",
        "redirect after submitting a form",
        "notify me when someone submits a form",
        "how do i get an email when a form is submitted",
        "allow multiple submissions",
        "form settings"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Forms & Documents", "Edit", "Form Behavior"] },
        {
          type: "steps",
          items: [
            {
              text: "Open **Form Behavior** in the editor."
            },
            {
              text: "Set the **Submit button text**.",
              note: 'Defaults to "Submit". "Book my place" converts better than "Submit" does.'
            },
            {
              text: "Write the **Success message**.",
              note: 'Defaults to "Thank you for your submission!". This is the last thing a respondent reads \u2014 say what happens next.'
            },
            {
              text: "Set a **Redirect URL** if they should go somewhere afterwards.",
              note: "It is shown as a continue button on the success screen, not an automatic jump."
            },
            {
              text: "Set the consent toggles \u2014 **Capture email consent** and **Capture SMS consent**.",
              note: 'These two genuinely work; see "Capture email and SMS consent on a form".'
            },
            {
              text: "Ignore **Notify on submission**, **Allow multiple submissions** and **Require auth** for now.",
              note: "They are stored but not yet acted on. Watch the form dashboard or the Inbox instead of waiting for an email."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            {
              name: "Submit button text",
              value: "The wording on the button",
              note: 'Defaults to "Submit".'
            },
            {
              name: "Success message",
              value: "What the respondent reads once it is sent",
              note: 'Defaults to "Thank you for your submission!".'
            },
            {
              name: "Redirect URL",
              value: "A page to send them on to",
              note: "Shown as a continue button on the success screen."
            },
            {
              name: "Notify on submission",
              value: "Addresses to tell about new submissions",
              note: "Stored but not yet acted on."
            },
            {
              name: "Capture email consent",
              value: "Show the email consent box"
            },
            {
              name: "Capture SMS consent",
              value: "Show the SMS consent box"
            },
            {
              name: "Allow multiple submissions",
              value: "Whether one person may submit more than once",
              note: "Stored but not yet acted on."
            },
            {
              name: "Require auth",
              value: "Whether the form asks who they are first",
              note: "Stored but not yet acted on."
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: "Three of these settings do less than their labels suggest. No submission notification email is sent, nothing is blocked by the multiple-submission or auth toggles. Do not build a process on them yet."
        }
      ]
    },
    {
      slug: "form-link-and-qr-code",
      title: "Get the public link and the QR code",
      why: "Every way of distributing a form starts from one of these two, and both are generated for you the moment you publish.",
      route: "/forms-and-documents?filter=form",
      requires: { permissions: ["forms:read"], features: ["forms_documents"] },
      estimatedMinutes: 3,
      prereqs: ["forms/publish-and-share"],
      keywords: [
        "form link",
        "where is the public url for my form",
        "how do i get the qr code for a form",
        "download qr code",
        "copy shareable link",
        "share a form",
        "print a form qr code"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Forms & Documents", "Edit", "Share"] },
        {
          type: "steps",
          items: [
            {
              text: "Publish the form. The editor's top-right button then reads **Share**.",
              note: "Both the link and the QR code are generated the moment you publish."
            },
            {
              text: "Click **Share** to open the **Share Form** modal."
            },
            {
              text: "Under **QR Code**, download it as PNG or SVG for print.",
              note: "SVG for anything that will be scaled \u2014 a poster, a van door, a menu."
            },
            {
              text: "Under **Public URL**, use **Copy**, or **Open form in new tab** to check it."
            },
            {
              text: "Under **Send to Contacts**, click **Open Send Wizard** to hand the form to the Send flow."
            },
            {
              text: "For the short version, use the standalone **QR Code** modal from the form card.",
              note: 'Just "Download QR Code" and "Copy shareable link".'
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "The address does not change when you edit the form, and unpublishing then republishing restores the same one. A QR code you printed last year still works. The link is hosted on the signing portal at /form-ndf/ followed by the form's own address; older forms sit at /form/ instead."
        }
      ]
    },
    {
      slug: "embed-a-form",
      title: "Embed the form on your own website",
      why: "An embedded form keeps people on your own page instead of asking them to follow a link they may not follow.",
      route: "/messaging",
      requires: {
        permissions: ["forms:read", "inbox:read"],
        features: ["forms_documents", "messaging"]
      },
      estimatedMinutes: 4,
      prereqs: ["forms/publish-and-share"],
      keywords: [
        "embed a form",
        "how do i embed a form on my website",
        "iframe code for a form",
        "put a form on my site",
        "copy html",
        "website form code"
      ],
      blocks: [
        {
          type: "clickPath",
          path: ["Sidebar", "Inbox", "Forms", "Public Access", "EMBED ON WEBSITE"]
        },
        {
          type: "steps",
          items: [
            {
              text: "Publish the form first.",
              note: "An embedded snippet pointing at an unpublished form shows your visitors nothing."
            },
            {
              text: "Open the form from the **Inbox** to reach its dashboard."
            },
            {
              text: "Find the **Public Access** panel."
            },
            {
              text: "Under **EMBED ON WEBSITE**, click **Copy HTML**."
            },
            {
              text: "Paste the snippet into your own page wherever the form should appear.",
              note: "It loads the same hosted form as the public link, so branding, consent capture and submissions all behave identically."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "An embedded form keeps people on your own page instead of asking them to follow a link they may not follow. The same panel also carries the **PUBLIC LINK** and **QR CODE** if you want those instead."
        }
      ]
    },
    {
      slug: "unpublish-or-delete-a-form",
      title: "Take a form offline",
      why: "Unpublishing stops new submissions without losing a single one you already have.",
      route: "/forms-and-documents?filter=form",
      requires: { permissions: ["forms:publish"], features: ["forms_documents"] },
      estimatedMinutes: 3,
      prereqs: ["forms/publish-and-share"],
      keywords: [
        "unpublish a form",
        "how do i stop a form from accepting submissions",
        "take a form offline",
        "delete a form",
        "turn off a form",
        "pause a form",
        "does deleting a form delete the submissions"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Forms & Documents", "Unpublish"] },
        {
          type: "steps",
          items: [
            {
              text: "To stop new submissions, unpublish it. There are three switches for the same thing.",
              note: `"Unpublish form" at the bottom of the **Share Form** modal, **Unpublish** in the editor's top-right menu, or the toggle in the **Public Access** panel on the form dashboard.`
            },
            {
              text: "Check what happens: the public address stops serving the form.",
              note: "Everything already collected stays exactly where it was."
            },
            {
              text: "To bring it back, republish. The same address returns.",
              note: "Which is why a printed QR code survives an unpublish."
            },
            {
              text: "To remove it from the list entirely, use **Delete** in the card's **More** menu.",
              note: "It stops the public address working. It is not a hard erase \u2014 the record and its submissions are retained \u2014 but the form is gone from your list."
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: "Unpublish before you delete anything you have printed a QR code for. A deleted form and an unpublished one look identical to the person scanning it, but only one of them can be brought back from the list."
        }
      ]
    },
    {
      slug: "export-submissions",
      title: "Export submissions to CSV",
      why: "The export is the copy you can hand to an accountant, an auditor or a spreadsheet.",
      route: "/forms-and-documents?filter=form",
      requires: { permissions: ["forms:read"], features: ["forms_documents"] },
      estimatedMinutes: 3,
      prereqs: ["forms/read-submissions"],
      keywords: [
        "export form submissions",
        "how do i download form responses",
        "csv export",
        "download submissions to excel",
        "form responses spreadsheet",
        "filter submissions by date",
        "link a submission to a contact"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Forms & Documents", "More", "View Submissions"] },
        {
          type: "steps",
          items: [
            {
              text: "Open the form card's **More** menu and click **View Submissions**."
            },
            {
              text: "Narrow the table if you want to check something first \u2014 search box, date-range filter, pagination.",
              note: 'Set the range the right way round: backwards gives you "Start date must be on or before the end date."'
            },
            {
              text: "Click export.",
              note: "It covers every submission, not just the page in front of you \u2014 the filter narrows the view, not the file."
            },
            {
              text: "Open the CSV.",
              note: "Columns are the submission ID, the linked contact's name, email and phone, one column per field using your field labels, the IP address and the submitted time. A field allowing several answers is joined into one cell with commas."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "A submission that arrived with no email and no phone has no contact attached, so those columns are blank. You can link it to the right contact from the table afterwards. Signature fields render as images in the table rather than text."
        }
      ]
    },
    {
      slug: "form-dashboard",
      title: "Read the form dashboard",
      why: "The submissions table tells you what people answered; the dashboard tells you where they gave up.",
      route: "/messaging",
      requires: {
        permissions: ["forms:read", "inbox:read"],
        features: ["forms_documents", "messaging"]
      },
      estimatedMinutes: 4,
      prereqs: ["forms/read-submissions"],
      keywords: [
        "form analytics",
        "how many people filled in my form",
        "response rate",
        "submission trends",
        "field insights",
        "which form question do people skip",
        "form dashboard",
        "form statistics"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Inbox", "Forms"] },
        {
          type: "steps",
          items: [
            {
              text: "Open the form from the **Inbox** \u2014 this is the dashboard, not the submissions table."
            },
            {
              text: "Read the four tiles: **Submissions**, **Today**, **Response Rate**, **Linked Contacts**."
            },
            {
              text: "Read **Submission Trends** over 7, 30 or 90 days.",
              note: "It names the peak day and a daily average."
            },
            {
              text: "Read **Field Insights** \u2014 a completion percentage and answer distribution for every field.",
              note: "This is the one to act on. A field with a much lower completion percentage than the ones around it is where people are abandoning the form, usually because it is required and it should not be."
            },
            {
              text: "For an individual answer, click **View** on a row in the submissions table.",
              note: "A side panel opens with the full responses, the device and IP, the consent given and a **View Contact** link."
            },
            {
              text: "Use the actions menu for **Duplicate Form** or **Delete Form**."
            }
          ]
        }
      ]
    }
  ]
};

// src/getting-started/articles/documents.ts
var documentsArticles = {
  documents: [
    {
      slug: "upload-a-pdf",
      title: "Upload a PDF and choose how to use it",
      why: "The choice you make straight after the upload decides which editor you get and whether the wording stays fixed.",
      route: "/forms-and-documents?filter=document",
      requires: { permissions: ["documents:create"], features: ["forms_documents"] },
      estimatedMinutes: 5,
      prereqs: ["documents/create-a-document"],
      keywords: [
        "upload a pdf",
        "how do i upload a contract to sign",
        "keep as pdf or make it editable",
        "add fields to an existing pdf",
        "turn a pdf into an editable agreement",
        "pdf size limit",
        "use my own contract"
      ],
      blocks: [
        {
          type: "clickPath",
          path: [
            "Sidebar",
            "Forms & Documents",
            "Create New",
            "Agreement",
            "Yes, I have an existing document"
          ]
        },
        {
          type: "steps",
          items: [
            {
              text: "Click **Create New**, choose **Agreement**, then **Yes, I have an existing document**."
            },
            {
              text: "Upload the file. PDF only, up to 50 MB.",
              note: "Anything else is rejected at this step."
            },
            {
              text: "Answer the question that follows \u2014 and answer it deliberately, because it is hard to change later.",
              note: '"Keep as PDF and add fields" or "Make it editable".'
            },
            {
              text: "Choose **Keep as PDF and add fields** whenever the wording belongs to someone else.",
              note: "A carrier form, a regulator's form, a landlord's lease. The original pages stay untouched and signatures are stamped where you put them."
            },
            {
              text: "Choose **Make it editable** when the wording is yours and you expect to change it.",
              note: "You get the inline editor, and each signed copy is rendered fresh rather than stamped."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            {
              name: "Keep as PDF and add fields",
              value: "The original pages, untouched",
              note: "You drag fields onto the pages and signatures are stamped onto the PDF where you put them."
            },
            {
              name: "Make it editable",
              value: "The wording becomes text you can edit",
              note: "You get the inline editor, and each signed copy is rendered fresh rather than stamped."
            }
          ]
        }
      ]
    },
    {
      slug: "generate-with-ai",
      title: "Generate an agreement from a description",
      why: "A described agreement arrives as a working draft with its signature and date fields already placed, which is most of the work.",
      route: "/forms-and-documents?filter=document",
      requires: { permissions: ["documents:create"], features: ["forms_documents"] },
      estimatedMinutes: 6,
      prereqs: ["documents/create-a-document"],
      keywords: [
        "generate an agreement with ai",
        "how do i create a contract with ai",
        "write a waiver for me",
        "ai document wizard",
        "describe what you need",
        "regenerate a document",
        "import a word document",
        "scan a document into an agreement"
      ],
      blocks: [
        {
          type: "clickPath",
          path: [
            "Sidebar",
            "Forms & Documents",
            "Create New",
            "Agreement",
            "No, start from scratch"
          ]
        },
        {
          type: "steps",
          items: [
            {
              text: "Click **Create New**, choose **Agreement**, then **No, start from scratch**."
            },
            {
              text: 'Write your description in the "Tell us about it" box \u2014 between 10 and 2,000 characters.',
              note: "Name the parties and say what each one has to do. A description that reads like instructions to a junior colleague produces a far better draft than one that names a document type."
            },
            {
              text: "Or pick from the prompt library instead of writing your own.",
              note: "Example prompt chips sit beneath the box."
            },
            {
              text: 'Click **Create with AI** and wait through "Building your content...".'
            },
            {
              text: "Read the block-by-block preview that comes back.",
              note: "Regenerate it if it is wrong, rather than accepting and fixing by hand."
            },
            {
              text: "Click **Use This Document** to accept it.",
              note: "It becomes a normal agreement you can edit like any other."
            },
            {
              text: "To rebuild an existing file instead, use the wizard's **Import Document** mode.",
              note: "Drop in a PDF, Word or Excel file or an image up to 20 MB and it is read and rebuilt as an editable agreement rather than left as a flat page."
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: "Generating and importing both spend AI credits from your plan."
        }
      ]
    },
    {
      slug: "place-fields-on-a-pdf",
      title: "Place signature and data fields on a PDF",
      why: "Nothing can be signed until there is a field to sign in, and the spot you drop it on is exactly where the signature lands.",
      route: "/forms-and-documents?filter=document",
      requires: { permissions: ["documents:update"], features: ["forms_documents"] },
      estimatedMinutes: 7,
      prereqs: ["documents/uploaded-pdfs"],
      keywords: [
        "add a signature box to a pdf",
        "how do i put a signature field on a pdf",
        "place fields on a document",
        "date field on a pdf",
        "checkbox on a pdf",
        "initials field",
        "does the pdf editor save automatically"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Forms & Documents", "Edit"] },
        {
          type: "steps",
          items: [
            {
              text: "Open the PDF-backed agreement in the editor.",
              note: "You get the document pages, a name field, a signers panel starting with **Signer 1**, a preview toggle, and **Save** and **Send** actions."
            },
            {
              text: "Drag signature, initials, text, date and checkbox fields onto the page where each belongs.",
              note: "The spot you drop it on is exactly where the signature lands. Positions are recorded per page, so a field dropped on page three is stamped onto page three of the signed copy at that spot, whatever size screen the signer uses."
            },
            {
              text: "Click **Preview** to check placement read-only.",
              note: "An **Edit** button goes back. It is the safe way to check without nudging a field out of position."
            },
            {
              text: "Click **Save**.",
              note: "The full-screen editor saves only when you press Save, and returns you to Forms & Documents once it has."
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: "The two PDF editors save differently. The full-screen editor saves on **Save** only; the older PDF editor auto-saves about every 30 seconds. Do not assume your work is safe because the other one behaves that way."
        }
      ]
    },
    {
      slug: "add-more-signers",
      title: "Add more than one signer",
      why: "Assigning every field to a named signer is what makes a two-party agreement sign in order instead of all at once.",
      route: "/forms-and-documents?filter=document",
      requires: { permissions: ["documents:update"], features: ["forms_documents"] },
      estimatedMinutes: 7,
      prereqs: ["documents/signers-and-fields"],
      keywords: [
        "multiple signers",
        "how do i add a second signer",
        "two people need to sign",
        "assign signers",
        "signer roles",
        "signing order",
        "counter signature",
        "who signs first"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Forms & Documents", "Edit", "Field Properties"] },
        {
          type: "steps",
          items: [
            {
              text: "Add each party in the signers panel."
            },
            {
              text: "Open each field and set its **assigned signer** in **Field Properties**.",
              note: "Once there is more than one signer the fields are colour-coded, so you can see at a glance who is being asked for what."
            },
            {
              text: "Walk the whole document and check every field has an owner.",
              note: "A field left unassigned is a field nobody is asked to complete."
            },
            {
              text: "At send time the recipients step becomes **Assign signers** \u2014 choose one contact per role.",
              note: "That happens whether you picked the agreement inside the wizard or opened it with the Send icon on its card."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "The signers go one at a time. When one finishes, the next is notified. Each sees the earlier signatures and answers read-only in place, with the other parties' fields locked; a signature field waiting on somebody else says it is awaiting another signer rather than offering a signing pad."
        }
      ]
    },
    {
      slug: "preview-a-document",
      title: "Preview an agreement before you send it",
      why: "Preview is read-only, so you can check the wording and the field placement without nudging anything by accident.",
      route: "/forms-and-documents?filter=document",
      requires: { permissions: ["documents:read"], features: ["forms_documents"] },
      estimatedMinutes: 3,
      keywords: [
        "preview a document",
        "how do i see what my agreement looks like",
        "check a document before sending",
        "read only view",
        "preview merge tags",
        "what will the signer see"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Forms & Documents", "Preview"] },
        {
          type: "steps",
          items: [
            {
              text: "Click **Preview** on the agreement.",
              note: "It opens the same full-screen editor **Edit** does, but read-only \u2014 so you can check the wording and the field placement without nudging anything by accident."
            },
            {
              text: "Read it as the recipient will.",
              note: "In the rich-text editor, preview fills your merge tokens with sample data, so you read the sentence rather than a row of placeholders."
            },
            {
              text: "Click **Edit** when you find something to fix.",
              note: "An editable agreement opens in the inline editor; a PDF-backed one opens in the PDF editor."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "Preview on a FORM behaves differently \u2014 it opens the live public form in a new tab, which is exactly what a respondent would see."
        }
      ]
    },
    {
      slug: "duplicate-a-document",
      title: "Duplicate an agreement",
      why: "Duplicating keeps a proven agreement untouched while you make the version with the one clause that differs.",
      route: "/forms-and-documents?filter=document",
      requires: { permissions: ["documents:create"], features: ["forms_documents"] },
      estimatedMinutes: 2,
      keywords: [
        "duplicate a document",
        "how do i copy an agreement",
        "make a second version of a contract",
        "copy of",
        "reuse a document",
        "clone an agreement"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Forms & Documents", "More", "Duplicate"] },
        {
          type: "steps",
          items: [
            {
              text: "Open the agreement card's **More** menu and click **Duplicate**."
            },
            {
              text: 'The copy is named "Copy of" plus the original name and starts as a draft.',
              note: "The content, the fields and the tags all come with it."
            },
            {
              text: "Rename it, change the clause that differs, and send."
            },
            {
              text: "Leave the original alone. That is the point.",
              note: "Duplicating keeps a proven agreement untouched while you make the version with the one clause that differs."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "Signatures belong to the send, not to the agreement, so a duplicate always starts clean. Duplicating a document that has been signed a hundred times gives you a fresh template, not a hundred signatures."
        }
      ]
    },
    {
      slug: "track-signing-progress",
      title: "Track who has viewed and who has signed",
      why: "Every signature request moves through the same states, and which one a recipient is stuck in tells you what to do next.",
      route: "/messaging",
      requires: {
        permissions: ["documents:read", "inbox:read"],
        features: ["forms_documents", "messaging"]
      },
      estimatedMinutes: 4,
      prereqs: ["documents/send-for-signature"],
      keywords: [
        "has my document been signed",
        "how do i see if someone opened my agreement",
        "signature status",
        "track a signature request",
        "who has not signed yet",
        "document statuses",
        "audit trail pdf"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Inbox", "Documents"] },
        {
          type: "steps",
          items: [
            {
              text: "Click the **Documents** filter in the Inbox.",
              note: "This is the list of everything you have sent for signature."
            },
            {
              text: "Read each recipient's own status \u2014 see the table below.",
              note: "Which state a recipient is stuck in tells you what to do next."
            },
            {
              text: "Open the send itself for the campaign dashboard.",
              note: "How many it reached and how many **Signed**, why any didn\u2019t go out, every person with their own explanation, and **Download audit trail** under **What you sent**."
            },
            {
              text: "For anyone sitting on **Pending** or **Viewed**, send a reminder from the document panel.",
              note: "Most unsigned documents are forgotten rather than refused."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            {
              name: "Pending",
              value: "Sent to them, not yet opened"
            },
            {
              name: "Sent",
              value: "Queued behind someone else",
              note: "Multi-signer only \u2014 they are waiting their turn."
            },
            {
              name: "Viewed",
              value: "They opened the signing page"
            },
            {
              name: "Signed",
              value: "Done"
            },
            {
              name: "Declined",
              value: "They refused, and gave a reason"
            },
            {
              name: "Expired",
              value: "The agreement passed its expiry date before they signed"
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "Expiry comes from the agreement's own expiry date, not from the send. Once it passes, anyone opening the link gets an expired page rather than a signing page."
        }
      ]
    },
    {
      slug: "declined-signatures",
      title: "What happens when a signer declines",
      why: "A decline stops the agreement dead and does not chase you about it, so you need to know where it turns up.",
      route: "/messaging",
      requires: {
        permissions: ["documents:read", "inbox:read"],
        features: ["forms_documents", "messaging"]
      },
      estimatedMinutes: 4,
      prereqs: ["documents/send-for-signature"],
      keywords: [
        "signer declined",
        "what happens if someone refuses to sign",
        "decline to sign",
        "decline reason",
        "rejected agreement",
        "how do i know if a signature was declined",
        "resend a declined document"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Inbox", "Documents"] },
        {
          type: "steps",
          items: [
            {
              text: "Check the **Documents** filter in the Inbox. Do not wait for an email.",
              note: "A decline does not reliably notify you, and it stops the agreement dead without chasing you about it."
            },
            {
              text: "Find the request recorded as declined and read the reason.",
              note: "The decline modal on the signing page will not close without one, so there is always a reason, a time and an IP address on the record."
            },
            {
              text: "On a multi-signer agreement, expect everything to have paused.",
              note: "Nobody further down the order is asked to sign \u2014 which is right. You do not want three parties signing something the first one rejected."
            },
            {
              text: "Fix whatever they objected to and send the agreement again.",
              note: "The declined link cannot be reused: once a request is signed, declined or expired it returns a completed page. Sending again gives them a fresh link."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "A decline is a workflow trigger, alongside viewed and signed. If declines matter to you, build a workflow on it rather than remembering to check."
        }
      ]
    },
    {
      slug: "after-the-signature",
      title: "What happens after the last signature",
      why: "The signed copy, the emails and the seal are all produced after the signer closes the page, not while they are on it.",
      requires: { permissions: ["documents:read"], features: ["forms_documents"] },
      estimatedMinutes: 4,
      prereqs: ["documents/send-for-signature"],
      keywords: [
        "where is my signed document",
        "how do i download the signed pdf",
        "does the signer get a copy",
        "signed copy email",
        "digital signature seal",
        "what happens after someone signs",
        "signed pdf download link expired"
      ],
      blocks: [
        {
          type: "steps",
          items: [
            {
              text: "Nothing happens while the signer is on the page \u2014 the signed copy is built after the last required signature arrives.",
              note: "An uploaded PDF has the signature images and answers stamped onto the original pages; an editable agreement is rendered fresh with them baked in."
            },
            {
              text: "Your original is untouched either way, and can be sent again.",
              note: "That is what makes an agreement a template rather than a one-shot."
            },
            {
              text: "The signer gets a confirmation email with a link to the signed document.",
              note: 'Every owner on your account gets a "document signed" notification with a link back to it. On a multi-signer agreement those go out only once everybody has finished.'
            },
            {
              text: "The finished document is sealed with a digital signature.",
              note: "On a multi-signer agreement the seal is applied to the final copy only \u2014 sealing an intermediate version and then adding another signature would break the seal it just applied."
            },
            {
              text: "Get the signed copy from the app rather than from the email if any time has passed.",
              note: "The download links in those emails are good for 24 hours."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "To prove a signature later, drop the PDF into Verify PDF Signature \u2014 you get the signer details, the certificate information and an integrity result."
        },
        {
          type: "action",
          label: "Verify a signed PDF",
          to: { kind: "app", route: "/settings/verify-signature" }
        }
      ]
    }
  ]
};

// src/getting-started/articles/automation.ts
var automationArticles = {
  automation: [
    // ── The headline task ────────────────────────────────────────────────────────────────
    {
      slug: "form-to-document-workflow",
      title: "Send a document to sign automatically after a form is submitted",
      why: "This is the most common automation there is, and doing it by hand means every new enquiry waits on someone noticing it.",
      route: "/workflows/new",
      requires: { features: ["workflows"] },
      estimatedMinutes: 12,
      keywords: [
        "how do i automate a workflow to send a document for signature after someone submits an intake form",
        "send document after form submission",
        "automatically send agreement when form is filled out",
        "form submitted trigger send document to sign",
        "auto send contract after intake form",
        "intake form to signature automation",
        "workflow form submitted send document",
        "new enquiry automatically gets the agreement",
        "send esignature request automatically"
      ],
      prereqs: [
        "automation/workflows-overview",
        "forms/build-your-first-form",
        "documents/create-a-document"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Workflows", "Create Workflow"] },
        {
          type: "note",
          variant: "prereq",
          text: "Build the form and the document first \u2014 the workflow can only pick from what already exists."
        },
        {
          type: "steps",
          items: [
            {
              text: "Open **Workflows** and click **Create Workflow**. Name it in the header \u2014 it starts as **Untitled Workflow**."
            },
            {
              text: "On the **Starting event** tab, under **Forms & Documents**, choose **Someone fills out a form**."
            },
            {
              text: 'In the config panel on the right, under "Which form(s)?", pick **A specific form** and select your intake form.',
              note: "**All forms** starts the workflow for any submission, which is rarely what you want here."
            },
            {
              text: "Switch to the **Add a step** tab. Under **Timing**, add **Wait Before Continuing** and set it to 5 minutes, or an hour.",
              note: "So the document does not arrive in the same breath as the confirmation."
            },
            {
              text: "Add a step again. Under **Messages**, add **Send a Document to Sign**."
            },
            {
              text: 'In its config panel, under "Which document?", select the document you want signed.'
            },
            {
              text: "Click **Activate** in the builder header."
            }
          ]
        },
        {
          type: "steps",
          title: "Adding judgement to it",
          items: [
            {
              text: "Add a **Check a Condition** step between the wait and the send."
            },
            {
              text: "Use the **Has a tag** or **Is at a stage** check so only the submissions you actually want to paper get the document."
            },
            {
              text: "Hang the **Send a Document to Sign** step off the **If YES** arm."
            },
            {
              text: "Put a plain follow-up email on the **If NO** arm \u2014 or nothing at all.",
              note: "Anything on that arm runs for everyone else."
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: "The Send a Document to Sign step only runs for a contact who has an email address and has opted in to email. A form submission that captured email consent satisfies both; one that did not will be skipped silently."
        },
        {
          type: "note",
          variant: "tip",
          text: "To chase the ones who do not sign, add a second **Wait** after the document step, then a **Check a Condition** using the **Signed a document** quick check, and put the reminder text or email on the **If NO** arm."
        },
        {
          type: "action",
          label: "Build this workflow",
          to: { kind: "app", route: "/workflows/new" }
        }
      ]
    },
    // ── Triggers ─────────────────────────────────────────────────────────────────────────
    {
      slug: "contact-triggers",
      title: "Start a workflow from something a contact does",
      why: "The five Contacts triggers cover the whole lifecycle from arriving to unsubscribing, and picking the wrong one is why a workflow enrols nobody.",
      route: "/workflows/new",
      requires: { features: ["workflows"] },
      estimatedMinutes: 4,
      keywords: [
        "contact triggers workflow",
        "how do i run a workflow when a new contact is added",
        "welcome message for new contacts automatically",
        "trigger when contact moves to a new stage",
        "trigger when a contact joins a group",
        "workflow when someone unsubscribes",
        "automate on contact created"
      ],
      prereqs: ["automation/workflows-overview"],
      blocks: [
        {
          type: "clickPath",
          path: ["Sidebar", "Workflows", "Create Workflow", "Starting event", "Contacts"]
        },
        {
          type: "steps",
          items: [
            {
              text: "On the **Starting event** tab, open the **Contacts** category."
            },
            {
              text: "Pick the event that matches the lifecycle moment you are reacting to \u2014 see the table below.",
              note: "Picking the wrong one is why a workflow enrols nobody."
            },
            {
              text: "For **A contact is added to a group**, narrow it in the config panel to one group, and optionally one stage."
            },
            {
              text: "For **A contact moves to a new stage**, narrow it to the specific stage.",
              note: "Left wide, it fires on every move."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            {
              name: "A new contact is added",
              value: "Someone new is added to your contacts",
              note: "Fires on both manual creation and import, so a 500-row import enrols 500 people."
            },
            {
              name: "A contact agrees to receive messages",
              value: "Someone gives consent to hear from you"
            },
            {
              name: "A contact unsubscribes",
              value: "Someone opts out of receiving messages"
            },
            {
              name: "A contact is added to a group",
              value: "A contact joins one of your groups",
              note: "Can be narrowed to one group, and optionally one stage."
            },
            {
              name: "A contact moves to a new stage",
              value: "A contact progresses in your pipeline",
              note: "Narrow it to the specific stage, or it fires on every move."
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: "A contact can only be enrolled in the same workflow once. If they are added to the group again later, they are not enrolled again \u2014 there is no re-enrolment or cooldown setting."
        }
      ]
    },
    {
      slug: "form-and-document-triggers",
      title: "Start a workflow from a form or a document",
      why: "These four triggers are what turn paperwork into follow-up without anyone watching an inbox.",
      route: "/workflows/new",
      requires: { features: ["workflows"] },
      estimatedMinutes: 4,
      keywords: [
        "form and document triggers",
        "how do i trigger a workflow when a form is submitted",
        "run something when a document is signed",
        "follow up when a document is declined",
        "trigger when someone opens a document but does not sign",
        "automate after signature",
        "intake form trigger workflow"
      ],
      prereqs: ["automation/workflows-overview"],
      blocks: [
        {
          type: "clickPath",
          path: ["Sidebar", "Workflows", "Create Workflow", "Starting event", "Forms & Documents"]
        },
        {
          type: "steps",
          items: [
            {
              text: "On the **Starting event** tab, open the **Forms & Documents** category."
            },
            {
              text: "Pick the event \u2014 see the table below."
            },
            {
              text: "For **Someone fills out a form**, choose **All forms** or **A specific form**.",
              note: "The picker lists your own forms under **Forms** and your built-in intake form under **Intake forms**."
            },
            {
              text: "For **A document is signed**, narrow it to one document if the follow-up is document-specific."
            },
            {
              text: "Use **A document is opened** for the nudge case.",
              note: "Opened, not signed \u2014 that is the gap worth chasing."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            {
              name: "Someone fills out a form",
              value: "A contact completes one of your forms",
              note: "Choose All forms or A specific form."
            },
            {
              name: "A document is signed",
              value: "A contact signs one of your documents",
              note: "Can be narrowed to one document."
            },
            {
              name: "A document is declined",
              value: "A contact declines to sign a document"
            },
            {
              name: "A document is opened",
              value: "A contact opens a document you sent",
              note: "Opened, not signed \u2014 this is the nudge trigger."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "Because the built-in intake form appears in the form picker, submitting your default contact-collection form can start a workflow too."
        }
      ]
    },
    {
      slug: "message-triggers",
      title: "Start a workflow from a message event",
      why: "Delivery, opens, clicks and replies are the signals that tell you someone is paying attention, and they are worth acting on while they still are.",
      route: "/workflows/new",
      requires: { features: ["workflows"] },
      estimatedMinutes: 4,
      keywords: [
        "message triggers workflow",
        "how do i trigger a workflow when someone replies",
        "automate when a link in an email is clicked",
        "run a workflow when an email is opened",
        "trigger after a campaign finishes",
        "workflow on message delivered",
        "follow up automatically when a contact replies to me"
      ],
      prereqs: ["automation/workflows-overview"],
      blocks: [
        {
          type: "clickPath",
          path: ["Sidebar", "Workflows", "Create Workflow", "Starting event", "Messages"]
        },
        {
          type: "steps",
          items: [
            {
              text: "On the **Starting event** tab, open the **Messages** category."
            },
            {
              text: "Pick the signal you want to act on \u2014 see the table below.",
              note: "Delivery, opens, clicks and replies are the signals that tell you someone is paying attention, and they are worth acting on while they still are."
            },
            {
              text: "Narrow it in the config panel where the event allows it."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            {
              name: "A message campaign finishes",
              value: "All messages in a campaign have been sent"
            },
            {
              name: "A message is delivered",
              value: "A text or email reaches a contact"
            },
            {
              name: "An email is opened",
              value: "A contact opens an email you sent"
            },
            {
              name: "A link in an email is clicked",
              value: "A contact clicks a link in your email"
            },
            {
              name: "A contact replies to you",
              value: "A contact sends you a message"
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: "A contact who replies is often asking a person a question. If you automate a reply to a reply, make sure a human still sees the conversation in the Inbox."
        }
      ]
    },
    {
      slug: "manual-enrollment",
      title: "Enrol contacts into a workflow yourself",
      why: "Some automations should run for a list you choose rather than for whoever happens to trip an event.",
      route: "/workflows",
      requires: { features: ["workflows"] },
      estimatedMinutes: 4,
      keywords: [
        "manual enrollment workflow",
        "how do i add specific contacts to a workflow",
        "enroll contacts manually",
        "pick people to run a workflow on",
        "manually enroll specific contacts trigger",
        "re-engagement list workflow",
        "enroll contacts button greyed out"
      ],
      prereqs: ["automation/workflows-overview"],
      blocks: [
        {
          type: "clickPath",
          path: ["Sidebar", "Workflows", "Create Workflow", "Starting event", "Manual"]
        },
        {
          type: "steps",
          items: [
            {
              text: "On the **Starting event** tab, choose **Manually enroll specific contacts**.",
              note: "Nothing fires automatically after this \u2014 you pick the contacts yourself, whenever you want the workflow to run for them."
            },
            {
              text: "Build the steps and click **Activate** as normal."
            },
            {
              text: "Open the workflow from the Workflows list to reach its page."
            },
            {
              text: "Click **Enroll Contacts** in the header.",
              note: 'Disabled until the workflow is Active \u2014 the tooltip reads "Activate the workflow to enroll contacts".'
            },
            {
              text: "In **Add Recipients**, search and tick the people you want, then click **Add Contacts**.",
              note: "Up to 500 at a time. Anyone already enrolled is skipped."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: 'This is also the trigger to use when testing \u2014 see "Test a workflow before you switch it on".'
        }
      ]
    },
    // ── Steps ────────────────────────────────────────────────────────────────────────────
    {
      slug: "message-steps",
      title: "Send a text, email, document or form from a workflow",
      why: "These four steps are the ones that actually reach the contact, and each has its own requirement that quietly skips people who do not meet it.",
      route: "/workflows/new",
      requires: { features: ["workflows"] },
      estimatedMinutes: 6,
      keywords: [
        "workflow send text message step",
        "how do i send an email from a workflow",
        "send a document to sign from a workflow",
        "send a form to fill out automatically",
        "which template does a workflow send",
        "workflow message step did not send",
        "automation send sms email"
      ],
      prereqs: [
        "automation/workflows-overview",
        "templates/sms-template",
        "templates/email-template"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Workflows", "Add a step", "Messages"] },
        {
          type: "steps",
          items: [
            {
              text: "On the **Add a step** tab, open the **Messages** category."
            },
            {
              text: "Pick the step, then pick the template, document or form in its config panel.",
              note: "The pickers only list what is already in your account \u2014 there is no way to write the message inside the workflow builder."
            },
            {
              text: "Check the contact requirement for the step you chose before you rely on it.",
              note: "Each of these four quietly skips people who do not meet it: a phone number for text, an email address for email, an email address plus email opt-in for a document or a form."
            },
            {
              text: "For **Send a Form to Fill Out**, note there is no text-message version.",
              note: "Email only."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            {
              name: "Send a Text Message",
              value: 'Pick a saved text template under "Which text message template?"',
              note: "Needs the contact to have a phone number."
            },
            {
              name: "Send an Email",
              value: 'Pick a saved email template under "Which email template?"',
              note: "Needs an email address. An unsubscribe link is added for you."
            },
            {
              name: "Send a Document to Sign",
              value: 'Pick the document under "Which document?"',
              note: "Needs an email address and email opt-in. The contact gets a signing link."
            },
            {
              name: "Send a Form to Fill Out",
              value: "Pick the form \u2014 your own forms and your built-in intake form are listed separately",
              note: "Email only, and needs email opt-in."
            }
          ]
        },
        {
          type: "note",
          variant: "compliance",
          text: "Text and email steps are checked against your consent and jurisdiction rules before they send. A blocked send is recorded as failed for that step, and the contact still moves on to the next step rather than getting stuck."
        }
      ]
    },
    {
      slug: "contact-update-steps",
      title: "Label, group and update a contact from a workflow",
      why: "These steps are how a workflow leaves a trace, so the next campaign or filter can find the people it just touched.",
      route: "/workflows/new",
      requires: { features: ["workflows"] },
      estimatedMinutes: 5,
      keywords: [
        "workflow add a label step",
        "how do i tag a contact automatically",
        "move a contact to a stage automatically",
        "add contact to a group from a workflow",
        "update contact information step",
        "remove a label in a workflow",
        "automation change contact field"
      ],
      prereqs: ["contacts/tags", "contacts/pipelines-and-stages"],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Workflows", "Add a step", "Organize"] },
        {
          type: "steps",
          items: [
            {
              text: "On the **Add a step** tab, open the **Organize** category."
            },
            {
              text: "Pick the step and fill in its config panel \u2014 see the table below.",
              note: "These steps are how a workflow leaves a trace, so the next campaign or filter can find the people it just touched."
            },
            {
              text: "For **Add to a Group** and **Move to a Stage**, set BOTH the group and the stage.",
              note: "The step needs both to place the contact."
            },
            {
              text: "For **Update Contact Information**, choose the field and type the **New value**.",
              note: "Only the standard contact fields and your own custom fields are offered."
            },
            {
              text: "For a webhook, use **Notify Another System** under **Advanced**.",
              note: "Set the Website or App URL and leave the Request type on POST unless the other system asked for PUT."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            {
              name: "Add a Label",
              value: 'Choose the label under "Which label to add?"'
            },
            {
              name: "Remove a Label",
              value: 'Choose the label under "Which label to remove?"'
            },
            {
              name: "Add to a Group",
              value: "Choose the group, then a stage",
              note: "The step needs both to place the contact."
            },
            {
              name: "Remove from a Group",
              value: 'Choose the group under "Which group to remove from?"'
            },
            {
              name: "Move to a Stage",
              value: 'Choose the group, then "Which stage?"'
            },
            {
              name: "Update Contact Information",
              value: "Choose the field, then type the New value",
              note: "Standard contact fields and your own custom fields only."
            }
          ]
        }
      ]
    },
    {
      slug: "wait-steps",
      title: "Space steps out with Wait and Wait Until",
      why: "Back-to-back messages read as spam, and a workflow with no waits sends everything within seconds of the trigger.",
      route: "/workflows/new",
      requires: { features: ["workflows"] },
      estimatedMinutes: 4,
      keywords: [
        "workflow wait step",
        "how do i add a delay between workflow messages",
        "wait before continuing vs wait until a date",
        "pause a workflow for 3 days",
        "wait until a specific date and time",
        "space out automated messages",
        "delay in automation"
      ],
      prereqs: ["automation/workflows-overview"],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Workflows", "Add a step", "Timing"] },
        {
          type: "steps",
          items: [
            {
              text: "On the **Add a step** tab, open the **Timing** category."
            },
            {
              text: "For almost everything, add **Wait Before Continuing** \u2014 a number plus **Minutes**, **Hours** or **Days**.",
              note: 'It asks "How long to wait?" and pauses that long, measured from when the contact reaches the step.'
            },
            {
              text: "Use **Wait Until a Date** only for a fixed event date.",
              note: 'It asks "Wait until what date and time?" and holds every contact until that single moment \u2014 the same moment for everyone. A date in the past means the workflow simply continues.'
            },
            {
              text: "Put a wait between any two message steps.",
              note: "Back-to-back messages read as spam, and a workflow with no waits sends everything within seconds of the trigger."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            {
              name: "Wait Before Continuing",
              value: "A number plus Minutes, Hours or Days",
              note: "Relative to when the contact reaches the step. Use this for almost everything."
            },
            {
              name: "Wait Until a Date",
              value: "One exact date and time",
              note: "The same moment for everyone. Use it for a fixed event date, not for a delay."
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: "Wait Until a Date is measured against the server clock rather than your own timezone, so treat the time as approximate. If the exact hour matters, use Wait Before Continuing."
        }
      ]
    },
    {
      slug: "build-a-branch",
      title: "Build a Check a Condition branch",
      why: "A branch is the difference between a workflow that messages everyone the same way and one that reacts to what the contact actually did.",
      route: "/workflows/new",
      requires: { features: ["workflows"] },
      estimatedMinutes: 8,
      keywords: [
        "check a condition step",
        "how do i add an if then branch to a workflow",
        "branch on whether a contact signed a document",
        "yes and no path workflow",
        "only send if the contact has a tag",
        "workflow condition builder",
        "send a reminder if they have not signed",
        "all or any condition"
      ],
      prereqs: ["automation/steps-and-branches"],
      blocks: [
        {
          type: "clickPath",
          path: ["Sidebar", "Workflows", "Add a step", "Logic", "Check a Condition"]
        },
        {
          type: "steps",
          items: [
            {
              text: "Add the **Check a Condition** step. Its config panel opens with a **Common checks** row."
            },
            {
              text: "Tap one of the chips \u2014 **Signed a document**, **Opted into SMS**, **Opened my email**, **Submitted a form**, **Is at a stage**, **Has a tag**.",
              note: 'Or type into "Search checks..." for anything else.'
            },
            {
              text: "Pick the specifics if the check asks for them \u2014 which document, which label."
            },
            {
              text: "Set the comparison. It reads as a sentence.",
              note: 'A Yes/No toggle, a dropdown, a number, or a "within the last [7] days" control, depending on the check.'
            },
            {
              text: "Click **+ Add condition** for a second check.",
              note: "A **Match All / Any of these are true** toggle appears, and a preview restates the whole rule in plain English. Read that preview \u2014 it is the cheapest way to catch a rule that says the opposite of what you meant."
            },
            {
              text: "Back on the canvas, add steps under the green **If YES** column and the red **If NO** column.",
              note: "Steps on an arm run only for the contacts that took it, and when the arm runs out the contact carries on with whatever sits below the branch."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: 'To chase someone who has not done something, do not look for a "has not" check. Use the positive check \u2014 Signed a document \u2014 and put the reminder on the If NO arm.'
        },
        {
          type: "note",
          variant: "compliance",
          text: "Consent and do-not-call checks fail closed: if the answer genuinely cannot be established, the contact takes the NO path rather than being assumed safe to message."
        }
      ]
    },
    {
      slug: "end-a-workflow",
      title: "Stop a contact early with End the Workflow",
      why: "Without an Exit step, a contact who has already done what you wanted keeps receiving the chase messages.",
      route: "/workflows/new",
      requires: { features: ["workflows"] },
      estimatedMinutes: 3,
      keywords: [
        "end the workflow step",
        "how do i stop a workflow for one contact",
        "exit step automation",
        "stop sending reminders once they sign",
        "remove someone from a workflow early",
        "workflow exit vs pause"
      ],
      prereqs: ["automation/steps-and-branches"],
      blocks: [
        {
          type: "clickPath",
          path: ["Sidebar", "Workflows", "Add a step", "Logic", "End the Workflow"]
        },
        {
          type: "steps",
          items: [
            {
              text: "Add a **Check a Condition** step asking whether the contact has already done the thing."
            },
            {
              text: "Add **End the Workflow** under **Logic** on the **If YES** arm.",
              note: 'The config panel says it plainly: "Workflow ends here. When a contact reaches this step, they will exit the workflow. No further steps will run."'
            },
            {
              text: "Leave the chase messages below the branch.",
              note: "They then only ever reach the people who still need them."
            },
            {
              text: "Check the workflow page afterwards \u2014 contacts who hit it are counted as **Dropped Out**.",
              note: "That is not a failure signal here; it is the step working."
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: "Exiting is final for that contact \u2014 the same trigger firing again will not re-enrol them, because a contact can only be enrolled in a workflow once."
        }
      ]
    },
    // ── Running them ─────────────────────────────────────────────────────────────────────
    {
      slug: "test-before-activating",
      title: "Test a workflow before you switch it on",
      why: "An active workflow messages real contacts immediately, and there is no undo on a sent text.",
      route: "/workflows",
      requires: { features: ["workflows"] },
      estimatedMinutes: 6,
      keywords: [
        "how do i test a workflow",
        "test a workflow before activating",
        "try an automation on myself first",
        "workflow will not activate",
        "finish setting up your steps error",
        "needs setup badge workflow",
        "dry run automation"
      ],
      prereqs: ["automation/activate-and-monitor"],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Workflows", "Card menu", "Duplicate"] },
        {
          type: "note",
          variant: "warning",
          text: "There is no preview or simulation. An active workflow messages real contacts immediately, and there is no undo on a sent text. The way to test is to run it for one person you control."
        },
        {
          type: "steps",
          items: [
            {
              text: "Open the workflow menu on its card and choose **Duplicate**."
            },
            {
              text: "Edit the copy and change its starting event to **Manually enroll specific contacts**."
            },
            {
              text: "Shorten the **Wait** steps in the copy.",
              note: "Otherwise you will be waiting the real two days to see step three. Remember to put them back in the real workflow."
            },
            {
              text: "Click **Activate** on the copy, open it, click **Enroll Contacts** and pick yourself or a test contact."
            },
            {
              text: "Watch the result appear under each step in **Your workflow at a glance** on the workflow page.",
              note: "Click a step to see your test contact and exactly what happened to them there."
            },
            {
              text: "When it behaves, archive the copy and activate the real one."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: 'A step missing its configuration carries a red **Needs setup** badge on the canvas, and **Activate** refuses with a "Finish setting up your steps" dialog that lists each unfinished step and jumps you to it. A workflow with no steps at all cannot be activated either.'
        },
        {
          type: "note",
          variant: "warning",
          text: "You can only enrol a contact once per workflow, so a second test on the same person needs a fresh duplicate."
        }
      ]
    },
    {
      slug: "pause-resume-archive",
      title: "Pause, resume and archive a workflow",
      why: "Turning an automation off is the first thing you will want when something reads wrong, and the three options do quite different things.",
      route: "/workflows",
      requires: { features: ["workflows"] },
      estimatedMinutes: 4,
      keywords: [
        "how do i pause a workflow",
        "stop a workflow from running",
        "resume a paused workflow",
        "archive a workflow",
        "delete a workflow",
        "turn off an automation",
        "stop a workflow from sending",
        "what happens to people when i pause",
        "where did my deleted workflow go",
        "workflow status draft active paused archived"
      ],
      prereqs: ["automation/activate-and-monitor"],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Workflows", "Card menu"] },
        {
          type: "steps",
          items: [
            {
              text: "To stop everything, click **Pause** on the card menu or in the builder header.",
              note: "No new contacts enter, and nothing is sent to the people already part-way through. They are put on hold."
            },
            {
              text: "To start again, click **Resume** on the card menu, or **Activate** on the workflow page.",
              note: "Everyone on hold carries on from where they stopped. A wait that ended while paused moves on straight away."
            },
            {
              text: "To take it out of the way for good, use **Archive** on the **More** menu of the workflow page.",
              note: "An archived workflow cannot be activated and is hidden until you select the **Archived** tab."
            },
            {
              text: "Filter the list with the status tabs across the top \u2014 **All**, **Active**, **Draft**, **Paused**, **Archived**.",
              note: "They map exactly onto the four states in the table below."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            {
              name: "Draft",
              value: "Saved but doing nothing at all",
              note: "Every new workflow starts here."
            },
            {
              name: "Active",
              value: "Enrolling contacts and running steps"
            },
            {
              name: "Paused",
              value: "No new contacts are enrolled and nothing is sent",
              note: "Contacts already part-way through are on hold until you resume."
            },
            {
              name: "Archived",
              value: "Out of the way and cannot be activated",
              note: "Hidden from the list until you select the Archived tab."
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: "Pausing holds every text and email straight away, including for people already part-way through. A message that was already going out at the moment you paused can still finish sending. The steps in **Your workflow at a glance** show what is on hold."
        },
        {
          type: "note",
          variant: "tip",
          text: "**Delete** on the card menu archives the workflow rather than destroying it \u2014 it reappears under the Archived tab, with its history intact."
        }
      ]
    },
    {
      slug: "duplicate-a-workflow",
      title: "Duplicate a workflow",
      why: "Copying a working automation is faster and safer than rebuilding one that is nearly the same.",
      route: "/workflows",
      requires: { features: ["workflows"] },
      estimatedMinutes: 2,
      keywords: [
        "how do i duplicate a workflow",
        "copy a workflow",
        "clone an automation",
        "reuse a workflow for another form",
        "duplicate workflow to test"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Workflows", "Card menu", "Duplicate"] },
        {
          type: "steps",
          items: [
            {
              text: "Click **Duplicate** on the workflow card menu, or on the **More** menu of the workflow page."
            },
            {
              text: "The copy arrives as a **Draft**. It enrols nobody yet.",
              note: "Which is what makes this safe."
            },
            {
              text: "Open it and change what needs changing."
            },
            {
              text: "Click **Activate**."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "This is the cheapest way to run the same follow-up off a second form or document: duplicate it, then change only the trigger config."
        }
      ]
    },
    {
      slug: "read-workflow-results",
      title: "Read what happened at each step",
      why: "A contact moving past a step does not mean the step worked for them. Each step says what really happened, and why when it did not.",
      route: "/workflows",
      requires: { features: ["workflows"] },
      estimatedMinutes: 5,
      keywords: [
        "workflow dashboard numbers",
        "how do i see who is in a workflow",
        "see who is in a workflow",
        "see workflow",
        "what happened at each workflow step",
        "workflow step results",
        "dropped out exit rate meaning",
        "why did my workflow stop for someone",
        "workflow analytics",
        "did the label get added",
        "did they sign the document from the workflow",
        "yes no check results",
        "workflow funnel"
      ],
      prereqs: ["automation/activate-and-monitor"],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Workflows", "View"] },
        {
          type: "steps",
          items: [
            {
              text: "Open the workflow from the Workflows list."
            },
            {
              text: "Read **Your workflow at a glance** at the top. Every step shows its result underneath.",
              note: 'For example "9,866 labelled", "4,979 sent", "9,865 waiting", "4,210 Yes \xB7 1,032 No". Amber or red means something needs a look.'
            },
            {
              text: "Click a step to open it below the tiles.",
              note: "The step that most needs attention opens on its own."
            },
            {
              text: "Read **Why it didn't work for some people**, if it appears.",
              note: "Each reason is in plain words with what you can do about it. Click a reason to see exactly who it affected."
            },
            {
              text: "Use **Everyone at this step** to check an individual contact.",
              note: "Filter by **Done**, **Problems**, **Waiting** or **On hold**, or search by name, email or phone. Click a person for the full story."
            },
            {
              text: "Read the four tiles for the whole workflow \u2014 see the table below."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            { name: "Total Contacts", value: "Entered this workflow" },
            { name: "Currently Active", value: "In progress right now", note: "Includes everyone sitting inside a Wait step." },
            { name: "Completed", value: "Finished the full workflow" },
            { name: "Dropped Out", value: "Left early, shown with an exit rate", note: "Almost always contacts who hit an End the Workflow step." }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "A failed step does not strand the contact \u2014 they carry on to the next step. That is why everyone can reach the end while a text, document or notification quietly did not work. The result under each step is where that shows."
        }
      ]
    },
    {
      slug: "see-what-your-workflow-sent",
      title: "See what your workflow's texts and emails did",
      why: "A contact moving through a step does not mean their message arrived. This is where you see what was really sent, delivered, held back, and why.",
      route: "/workflows",
      requires: { features: ["workflows"] },
      estimatedMinutes: 4,
      keywords: [
        "did my workflow send",
        "workflow messages sent delivered",
        "why did my workflow text not send",
        "why did my workflow email not send",
        "workflow failed messages",
        "workflow delivery report",
        "when will the next workflow message go out",
        "scheduled workflow messages",
        "sequence messages delivered",
        "blocked by compliance rules workflow",
        "messages on hold paused workflow"
      ],
      prereqs: ["automation/activate-and-monitor"],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Workflows", "View", "A text or email step"] },
        {
          type: "steps",
          items: [
            {
              text: "Open the workflow (or sequence) and click a text or email step in **Your workflow at a glance**.",
              note: "Its panel opens under the tiles."
            },
            {
              text: "Read the journey from left to right: **Coming up**, **Sent**, **Delivered**.",
              note: "Click any of them to list just those messages."
            },
            {
              text: "If you see **didn't go out**, read **Why some messages didn't arrive**.",
              note: "Each reason is in plain words with what you can do about it. Click a reason to see exactly who it affected."
            },
            {
              text: "Read **Coming up** to see what happens next for people sitting in a wait, and when.",
              note: "It also tells you when the next step is not a message at all, for example when people will just finish the workflow."
            },
            {
              text: "Click any person in **Every message** for the full story of that one message.",
              note: "The panel shows when it was sent and delivered, the reason if it failed, and a link to the contact."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            { name: "Sent", value: "Handed to the phone carrier or email service" },
            { name: "Delivered", value: "Confirmed as reaching their phone or inbox", note: "Some email accounts (your own Gmail, Outlook or SMTP) cannot confirm delivery, so those stay at Sent." },
            { name: "Opened", value: "They opened the email" },
            { name: "Didn't go out", value: "Never sent", note: "Usually no phone number or email, no consent, or your compliance rules blocked it." },
            { name: "Couldn't deliver", value: "Sent, but the carrier or inbox rejected it" },
            { name: "On hold", value: "Waiting because the workflow is paused", note: "Carries on when you resume." }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "Messages sent before this section existed show as **Sent** without delivery confirmation."
        }
      ]
    },
    // ── Sequences ────────────────────────────────────────────────────────────────────────
    {
      slug: "build-a-sequence",
      title: "Build a sequence in the four-step wizard",
      why: "A sequence is the right tool when you know who you want to reach and in what order, rather than waiting for an event.",
      route: "/sequences/new",
      requires: { features: ["workflows"] },
      estimatedMinutes: 10,
      keywords: [
        "how do i create a sequence",
        "build a drip campaign",
        "sequence wizard details audience messages review",
        "send a series of emails over time",
        "nurture sequence setup",
        "add email text and wait steps to a sequence",
        "activate and send sequence"
      ],
      prereqs: ["automation/sequences"],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Sequences", "Create Sequence"] },
        {
          type: "steps",
          items: [
            {
              text: "Open **Sequences** and click **Create Sequence**.",
              note: "On the **Details** step, give it a **Sequence Name** and an optional **Description**. The name is capped at 255 characters with a live count."
            },
            {
              text: "On **Audience**, choose **Everyone**, **By label**, or **Choose people**.",
              note: "The wizard shows how many contacts the choice matches \u2014 read that number now rather than being surprised at Review."
            },
            {
              text: "On **Messages**, click **Add Email**, **Add Text** or **Add Wait** to build the run of steps.",
              note: "Reorder them with the arrows on each card."
            },
            {
              text: "For each Email or Text step, choose **Write a message** or **Use a saved template**.",
              note: "Writing inline gives you a subject and body for email, or the message text for a text, with an **Insert merge tag** palette and a character and segment count. A Wait step is a number plus a unit."
            },
            {
              text: "On **Review**, check the Name, Audience, step count and the **Flow** strip.",
              note: "Review lists whatever is still missing and keeps **Activate & Send** disabled until it is fixed."
            },
            {
              text: "Click **Activate & Send**.",
              note: "**Save as Draft** leaves it switched off, which is the right choice if you are not ready."
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: "A sequence cannot be activated until it has at least one Email or Text step and an audience that matches somebody."
        },
        {
          type: "note",
          variant: "compliance",
          text: "Sequences are marketing, so every recipient needs marketing consent on that channel. Expect the number who actually receive a step to be lower than the audience count."
        },
        {
          type: "action",
          label: "Create a sequence",
          to: { kind: "app", route: "/sequences/new" }
        }
      ]
    },
    {
      slug: "enroll-into-a-sequence",
      title: "Enrol contacts into a live sequence",
      why: "The audience is fixed when you activate, so adding people later is a deliberate second action.",
      route: "/sequences",
      requires: { features: ["workflows"] },
      estimatedMinutes: 4,
      keywords: [
        "how do i add contacts to a sequence",
        "enroll contacts sequence",
        "add someone to a running drip",
        "sequence enroll contacts button disabled",
        "sequence dashboard",
        "pause a sequence",
        "duplicate a sequence"
      ],
      prereqs: ["automation/sequences"],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Sequences", "the sequence", "Enroll Contacts"] },
        {
          type: "steps",
          items: [
            {
              text: "Open the sequence to reach its page.",
              note: "The audience is fixed when you activate, so adding people later is a deliberate second action."
            },
            {
              text: "Click **Enroll Contacts** in the header.",
              note: 'Disabled until the sequence is **Active**, with the tooltip "Activate the sequence to enroll contacts".'
            },
            {
              text: "In the **Add Recipients** picker, search, tick the people you want, and click **Add Contacts**.",
              note: "Up to 500 at a time. Anyone already enrolled is skipped. They start at the first step."
            },
            {
              text: "Watch the same page a workflow has: **Your sequence at a glance** with a result under every step, the four tiles, and the panel for whichever step you click.",
              note: "The header carries **Edit**, **Pause** or **Activate**, and a **More** menu with **Duplicate** and **Archive**."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "Pausing a sequence stops new enrolments. Contacts already part-way through are allowed to finish, which is why resuming one does not re-enrol its original audience."
        }
      ]
    }
  ]
};

// src/getting-started/articles/inbox.ts
var inboxArticles = {
  inbox: [
    {
      slug: "filter-pills",
      title: "Know what each inbox filter shows",
      why: "The pills do not slice the same list ten ways \u2014 SMS and Email hold conversations only, and campaigns live elsewhere.",
      route: "/messaging",
      requires: { permissions: ["inbox:read"], features: ["messaging"] },
      estimatedMinutes: 4,
      keywords: [
        "inbox filter pills",
        "what does the campaigns tab show",
        "why is my campaign not under sms",
        "inbox tabs explained",
        "where do form submissions appear in the inbox",
        "opt-out tab inbox",
        "website chat tab",
        "inbox counts wrong"
      ],
      prereqs: ["inbox/unified-inbox"],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Inbox"] },
        {
          type: "steps",
          items: [
            {
              text: "Pick the pill that matches the KIND of thing you are looking for, not the channel it used.",
              note: "This is the distinction people get wrong: **SMS** and **Email** hold two-way conversations only. A text campaign is under **Campaigns**."
            },
            {
              text: "Read the count on the pill \u2014 it is exactly the number of rows that pill will show under your current search."
            },
            {
              text: "If a badge and a list disagree, reload rather than working from the number.",
              note: "One of them is stale."
            },
            {
              text: 'Tick rows to reveal the bulk bar: **Archive**, **Read**, **Unread**, **Resend**, **Export**, **Delete**, with a "{N} selected" counter and **Clear**.'
            }
          ]
        },
        {
          type: "fields",
          rows: [
            {
              name: "All",
              value: "Everything, newest activity first"
            },
            {
              name: "Campaigns",
              value: "Sends you made from the Send screen"
            },
            {
              name: "Documents",
              value: "Signature requests and their progress"
            },
            {
              name: "Forms",
              value: "Your forms and the submissions against them"
            },
            {
              name: "SMS",
              value: "Two-way text conversations only",
              note: "A text campaign is under Campaigns, not here."
            },
            {
              name: "Email",
              value: "Two-way email conversations only",
              note: "Same rule \u2014 email campaigns are under Campaigns."
            },
            {
              name: "Voice",
              value: "Voice sends"
            },
            {
              name: "Website Chat",
              value: "Conversations from a chat widget on your site",
              note: "Only appears if your account owns a widget."
            },
            {
              name: "Opt-Out",
              value: "Every consent withdrawal, with its source"
            },
            {
              name: "Opt-In",
              value: "Every consent grant"
            }
          ]
        }
      ]
    },
    {
      slug: "search-the-inbox",
      title: "Find something in the inbox",
      why: "The inbox is one stream of everything you have ever sent, so search is how you get back to a particular message.",
      route: "/messaging",
      requires: { permissions: ["inbox:read"], features: ["messaging"] },
      estimatedMinutes: 3,
      keywords: [
        "how do i search the inbox",
        "find a message i sent",
        "search messages by contact name",
        "no results for my search inbox",
        "find an old campaign",
        "inbox search box"
      ],
      prereqs: ["inbox/unified-inbox"],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Inbox", "Search messages..."] },
        {
          type: "steps",
          items: [
            {
              text: "Click the **All** pill first if you are not sure what kind of item you are after.",
              note: "Search applies to whichever pill you are on, so starting narrow hides the answer."
            },
            {
              text: 'Type into "Search messages..." at the top of the list.'
            },
            {
              text: "Read the pill counts, not just the results.",
              note: "Every count re-tallies against your search, so the counts tell you which kind of item it was \u2014 the fastest way to find something you cannot place."
            },
            {
              text: "Click that pill to see just those rows."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: 'Empty results are worded for where you are: "No results for {your query}" when the search found nothing, versus a filter-specific message like "No campaigns yet" when the pill genuinely holds nothing. The two mean different things.'
        }
      ]
    },
    {
      slug: "reply-to-a-conversation",
      title: "Reply to a text or email conversation",
      why: "A reply that comes back and sits unanswered is worse than not having asked, and the composer is also where consent gets checked.",
      route: "/messaging",
      requires: { permissions: ["inbox:read"], features: ["messaging"] },
      estimatedMinutes: 5,
      keywords: [
        "how do i reply to a contact",
        "reply to an sms in the inbox",
        "answer an email conversation",
        "suggest reply button",
        "send a message from the inbox",
        "send message inbox",
        "reply to a customer",
        "answer a text conversation",
        "reply blocked opted out",
        "attach a file to an email reply"
      ],
      prereqs: ["inbox/conversations"],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Inbox", "SMS or Email", "the conversation"] },
        {
          type: "steps",
          items: [
            {
              text: "Click the **SMS** or **Email** pill and open the conversation. It opens as a full page."
            },
            {
              text: "Read the thread \u2014 grouped by day, inbound on the left, outbound on the right with delivery and read receipts."
            },
            {
              text: 'Check for the amber banner: "This contact has not opted in for {channel}".',
              note: "Your reply may be refused. Fix the consent on the contact rather than retrying the send."
            },
            {
              text: "Type in the composer and press **Enter** to send.",
              note: "Shift+Enter starts a new line."
            },
            {
              text: "Or click **Suggest reply** to draft an answer from the thread, then **Use this reply** \u2014 or **Regenerate**."
            },
            {
              text: "Watch the extras for your channel: a live character and segment count plus quick replies on text, an attachment button on email."
            },
            {
              text: "Click **Back to Inbox** to return to the list, or **View Full Contact** to open the contact record."
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: "Replying to a resolved conversation reopens it. The banner says so before you send, but it is easy to click past."
        }
      ]
    },
    {
      slug: "assign-resolve-reopen",
      title: "Assign, resolve and reopen a conversation",
      why: "Without an owner and a resolved state, a shared inbox is just a list nobody is sure has been dealt with.",
      route: "/messaging",
      requires: { permissions: ["inbox:read"], features: ["messaging"] },
      estimatedMinutes: 4,
      keywords: [
        "how do i assign a conversation to a teammate",
        "resolve a conversation",
        "reopen a resolved conversation",
        "mark a thread as done",
        "change conversation priority",
        "archive a conversation",
        "who is handling this contact"
      ],
      prereqs: ["inbox/conversations"],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Inbox", "the conversation", "Resolve"] },
        {
          type: "steps",
          items: [
            {
              text: "Open the conversation and click **Resolve** \u2014 the green button in the header.",
              note: "Once resolved the same button becomes **Reopen**."
            },
            {
              text: "To give it an owner, open the header menu (the vertical-dots button) and choose **Assign to**.",
              note: "**Assigned** is also editable in the contact panel on the right."
            },
            {
              text: "Use **Change Priority** in the same menu \u2014 **Low**, **Normal**, **High** or **Urgent**."
            },
            {
              text: "Use **Archive Conversation** at the bottom of that menu to take it out of the working list."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "A thread stops counting as unread when you actually open or interact with it, not when it happens to refresh in the background. A reply that lands while the page sits untouched stays bold until someone genuinely looks at it."
        }
      ]
    },
    {
      slug: "take-over-website-chat",
      title: "Take over a website chat from the assistant",
      why: "A visitor asking for a person needs one, and joining the chat is the act that hands it over.",
      route: "/messaging",
      requires: { permissions: ["inbox:read"], features: ["messaging"] },
      estimatedMinutes: 4,
      keywords: [
        "how do i take over a website chat",
        "join a chat from the ai assistant",
        "website chat handover",
        "reply to a visitor on my website",
        "the assistant is answering",
        "end a website chat",
        "chat widget conversation inbox"
      ],
      prereqs: ["inbox/unified-inbox"],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Inbox", "Website Chat", "the chat"] },
        {
          type: "steps",
          items: [
            {
              text: "Click the **Website Chat** pill and open the conversation.",
              note: "The pill only appears if your account owns a widget."
            },
            {
              text: 'Read the mode in the header: "The assistant is answering", "Waiting for a person", "You are in this chat", or "Ended".',
              note: "Below it: the widget name, the visitor email if they gave one, how many times they have been in touch, and the page they arrived on."
            },
            {
              text: "Look for the amber banner \u2014 it carries the handover note from the assistant when the visitor has asked for a person.",
              note: "That is the one you want to act on first."
            },
            {
              text: "Send a message. That is the takeover \u2014 there is no separate button.",
              note: 'The strip says so: "The assistant is handling this. Sending a message takes it over." The composer placeholder changes from "Type to join this chat..." to "Reply to the visitor...".'
            },
            {
              text: "Click **End chat** when you are done, which closes it for both sides."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "Every outbound turn is labelled with who wrote it \u2014 Assistant, or the name of the colleague who did \u2014 so the transcript stays a record of who said what."
        },
        {
          type: "note",
          variant: "warning",
          text: "A website chat has no attachments, no text templates and no segment counter. None of them mean anything on this channel, so do not go looking for them."
        }
      ]
    },
    {
      slug: "read-the-delivery-funnel",
      title: "See who got your campaign, and why some didn\u2019t",
      why: "Sent is not delivered, and the reason a message didn\u2019t arrive is usually something you can fix.",
      route: "/messaging",
      requires: { permissions: ["inbox:read"], features: ["messaging"] },
      estimatedMinutes: 5,
      keywords: [
        "campaign delivery funnel",
        "how many people received my campaign",
        "difference between sent and delivered",
        "open rate and click rate campaign",
        "why did my messages fail",
        "why didn't my text go out",
        "blocked by compliance rule",
        "not opted in",
        "who has not received my campaign",
        "campaign dashboard metrics"
      ],
      prereqs: ["inbox/campaign-dashboards"],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Inbox", "Campaigns", "the campaign"] },
        {
          type: "steps",
          items: [
            {
              text: "Open the campaign from the **Campaigns** pill."
            },
            {
              text: "Read **The story so far**, then click the number you care about.",
              note: "For example, click **42 didn\u2019t go out** \u2014 the list below shows exactly those 42 people."
            },
            {
              text: "For an SMS + email campaign, switch between **All**, **Texts** and **Emails**.",
              note: "Each person can get both, so each channel is counted on its own."
            },
            {
              text: "Read **Why some messages didn\u2019t arrive**, and click a reason to list only those people.",
              note: 'Each reason says what you can do \u2014 for example "Ask them to opt in to texts, or send by email instead."'
            },
            {
              text: "Click a person to see why it happened.",
              note: "A compliance block names the rule and the requirement that wasn\u2019t met. A carrier or inbox rejection shows their own words. **Download compliance report (PDF)** is there too."
            },
            {
              text: "Check **Coming up** for messages waiting to go out.",
              note: "Texts held for calling hours go out on their own at a time the person can be reached \u2014 nothing to do."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            { name: "Coming up", value: "Waiting to go out \u2014 the campaign hasn\u2019t started, or it\u2019s waiting for the person\u2019s allowed hours" },
            { name: "Sent", value: "Handed to the phone carrier or email service" },
            { name: "Delivered", value: "Confirmed as reaching their phone or inbox" },
            { name: "Signed / Filled in", value: "Documents and forms only \u2014 they finished it" },
            { name: "Didn\u2019t go out", value: "Never sent \u2014 no phone or email, no consent, on your Do-Not-Contact list, or stopped by a compliance rule" },
            { name: "Couldn\u2019t deliver", value: "Sent, but the carrier or inbox rejected it" },
            { name: "On hold", value: "Waiting because you paused the campaign" }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "Some text providers never confirm delivery. When that is the case the page says so instead of showing a delivery rate."
        }
      ]
    },
    {
      slug: "manage-a-running-campaign",
      title: "Pause, resume, cancel or duplicate a campaign",
      why: "A send that is going out wrong can be stopped, but only while it is still going out.",
      route: "/messaging",
      requires: { permissions: ["inbox:read"], features: ["messaging"] },
      estimatedMinutes: 4,
      keywords: [
        "how do i stop a campaign that is sending",
        "pause a campaign",
        "cancel a campaign",
        "resume a paused campaign",
        "duplicate a campaign",
        "send the same campaign again",
        "campaign actions menu"
      ],
      prereqs: ["inbox/campaign-dashboards"],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Inbox", "Campaigns", "the campaign", "Actions"] },
        {
          type: "steps",
          items: [
            {
              text: "Open the campaign and click **Actions**."
            },
            {
              text: "Click **Pause Campaign** to stop it where it is.",
              note: "Only offered while it is sending. Nothing more goes out while it is paused \u2014 messages that come up are held and show as **On hold**."
            },
            {
              text: "Click **Resume Campaign** to pick it back up from where it paused.",
              note: "Held messages and anyone not reached yet go out. Nobody gets a message twice."
            },
            {
              text: "Click **Cancel Campaign** to end it.",
              note: 'Messages already sent stay sent, and nothing more goes out. Anyone not reached shows as **Didn\u2019t go out** with the reason "You cancelled the campaign".'
            },
            {
              text: "Click **Duplicate Campaign** to open Send with the same content and recipients already filled in, then change what you need and send it.",
              note: "The copy is also saved in your Send drafts, so you can leave and come back to it. Consent is checked again for each contact when you send."
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: "Only the actions that apply to the current status appear, so a finished campaign offers Duplicate and nothing else. If the button you want is missing, the campaign has moved past it."
        },
        {
          type: "note",
          variant: "tip",
          text: "A send held for calling hours is not stuck. It reads **Scheduled** with a countdown and sends itself when the window opens."
        }
      ]
    },
    {
      slug: "resend-failed-messages",
      title: "Resend the messages that failed",
      why: "Failures are usually a temporary provider problem, and a resend costs a click.",
      route: "/messaging",
      requires: { permissions: ["inbox:read"], features: ["messaging"] },
      estimatedMinutes: 3,
      keywords: [
        "how do i resend failed messages",
        "resend to failed campaign",
        "retry undelivered messages",
        "some contacts did not receive the message",
        "resend from the inbox",
        "bulk resend"
      ],
      prereqs: ["inbox/campaign-dashboards"],
      blocks: [
        {
          type: "clickPath",
          path: ["Sidebar", "Inbox", "Campaigns", "the campaign", "Actions", "Resend to Failed"]
        },
        {
          type: "steps",
          items: [
            {
              text: "Open the campaign from the **Campaigns** pill."
            },
            {
              text: "Click **Didn\u2019t go out** or **Couldn\u2019t deliver** under the path first.",
              note: "Optional, but it shows you exactly who you are about to resend to, and why it failed the first time."
            },
            {
              text: "Click **Actions**, then **Resend to Failed**.",
              note: "It only appears when there is something to resend. Only the channel that failed is sent again \u2014 if someone got the email but not the text, they only get the text."
            },
            {
              text: "To resend to a hand-picked subset instead, tick those rows and click **Resend** in the bulk bar."
            }
          ]
        },
        {
          type: "note",
          variant: "compliance",
          text: "A resend is a fresh send and is checked afresh. Anyone who has opted out since the original campaign will not receive it \u2014 which is correct, and worth expecting in the numbers."
        },
        {
          type: "note",
          variant: "tip",
          text: "Failures are usually a temporary provider problem, so a resend is worth a click before you go looking for a cause."
        }
      ]
    },
    {
      slug: "send-signature-reminder",
      title: "Send a reminder for a document nobody has signed",
      why: "Most unsigned documents are forgotten rather than refused, and a single nudge closes the majority of them.",
      route: "/messaging",
      requires: { permissions: ["inbox:read"], features: ["messaging"] },
      estimatedMinutes: 3,
      keywords: [
        "how do i send a signature reminder",
        "chase an unsigned document",
        "remind all pending signers",
        "nudge someone to sign",
        "document awaiting signature reminder",
        "when was the last reminder sent"
      ],
      prereqs: ["inbox/unified-inbox"],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Inbox", "Documents", "the document"] },
        {
          type: "steps",
          items: [
            {
              text: "Click the **Documents** pill and open the document row. A panel slides in."
            },
            {
              text: "Read **Signature Progress** to see who is still outstanding.",
              note: "The panel also records **Last Reminder**, so you can see whether they have already been chased."
            },
            {
              text: "Click **Send Reminder** for a single signer.",
              note: "When several are outstanding the button reads **Remind All ({N})** instead."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "Most unsigned documents are forgotten rather than refused, and a single nudge closes the majority of them. If you find yourself doing this by hand every week, it is a workflow: a document is opened, wait, then a text or email."
        },
        {
          type: "action",
          label: "Automate the chase",
          to: { kind: "app", route: "/workflows/new" }
        }
      ]
    },
    {
      slug: "form-dashboard-and-sharing",
      title: "Read a form dashboard and share the form",
      why: "A form nobody can reach collects nothing, and the sharing panel is where its public link, QR code and embed code live.",
      route: "/messaging",
      requires: { permissions: ["inbox:read"], features: ["messaging"] },
      estimatedMinutes: 5,
      keywords: [
        "how do i see form submissions",
        "form dashboard",
        "public link for my form",
        "form qr code",
        "embed a form on my website",
        "response rate form",
        "which questions do people skip",
        "form submission details"
      ],
      prereqs: ["inbox/unified-inbox", "forms/publish-and-share"],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Inbox", "Forms", "the form"] },
        {
          type: "steps",
          items: [
            {
              text: "Click the **Forms** pill and open the form."
            },
            {
              text: "Read the four tiles \u2014 **Submissions**, **Today**, **Response Rate**, **Linked Contacts**.",
              note: "Response Rate is the one that tells you whether the form itself is the problem."
            },
            {
              text: "Turn on the publish toggle in **Public Access**.",
              note: "A form nobody can reach collects nothing."
            },
            {
              text: "Share it one of three ways from the same panel.",
              note: "**PUBLIC LINK** with copy and open buttons, **QR CODE** with generate and download, or **EMBED ON WEBSITE** with a **Copy HTML** snippet."
            },
            {
              text: "Check **Submission Trends** over 7, 30 or 90 days."
            },
            {
              text: "Read **Field Insights** for per-question completion and answer spread.",
              note: "The fastest way to spot a question people abandon \u2014 a field with a low completion rate is usually one to make optional or cut."
            },
            {
              text: "Use the **Submissions** table for individual answers.",
              note: '"Search contacts...", a date range, and a **View** on every row that opens the full answers alongside device, consent and a **View Contact** link.'
            }
          ]
        },
        {
          type: "fields",
          rows: [
            {
              name: "Submissions",
              value: "Everything received"
            },
            {
              name: "Today",
              value: "Received since midnight"
            },
            {
              name: "Response Rate",
              value: "How often being shown the form leads to a submission"
            },
            {
              name: "Linked Contacts",
              value: "Submissions matched to a contact record"
            }
          ]
        }
      ]
    },
    {
      slug: "download-recipient-pdf",
      title: "Download a compliance report or audit trail",
      why: "If consent is ever questioned, the PDF is the record that answers it \u2014 and you want to know where it lives before you need it.",
      route: "/messaging",
      requires: { permissions: ["inbox:read"], features: ["messaging"] },
      estimatedMinutes: 3,
      keywords: [
        "how do i download proof of consent",
        "compliance report pdf",
        "audit trail pdf download",
        "evidence a contact agreed",
        "download signed document",
        "per recipient pdf campaign",
        "signature audit trail"
      ],
      prereqs: ["inbox/campaign-dashboards"],
      blocks: [
        {
          type: "clickPath",
          path: ["Sidebar", "Inbox", "Campaigns", "the campaign", "Recipients"]
        },
        {
          type: "steps",
          items: [
            {
              text: "For one contact on a campaign: open the campaign, scroll to the **Recipients** table, and click **Download compliance report** on their row.",
              note: "One PDF for that one contact."
            },
            {
              text: "For a document campaign as a whole: click **Download Audit Trail PDF** on the **Compliance Score** card."
            },
            {
              text: "For a single document: open it from the **Documents** pill and click **Download Audit Trail** in its panel.",
              note: "Offered always, regardless of status \u2014 you do not have to wait for it to be signed."
            },
            {
              text: "Once signed, use **Download Signed** in the same panel for one signer.",
              note: "When several have signed it reads **Download All ({N})** and gives you a zip."
            }
          ]
        },
        {
          type: "note",
          variant: "compliance",
          text: "The audit trail is generated from the record rather than stored as a file, so it is always current at the moment you download it. Take it when you need it rather than filing one away."
        }
      ]
    }
  ]
};

// src/getting-started/articles/settings.ts
var settingsArticles = {
  // ══════════════════════════════════════════════════════════════════════════════════════
  "business-profile": [
    {
      slug: "change-your-password",
      title: "Change your password",
      why: "A password you share, reuse or half-remember is the weakest point in an account that holds other people's consent records.",
      route: "/settings/security",
      estimatedMinutes: 2,
      keywords: [
        "change password",
        "how do i change my password",
        "reset my password",
        "set a password",
        "update password",
        "password strength",
        "i forgot my password",
        "new password"
      ],
      blocks: [
        {
          type: "clickPath",
          path: ["Sidebar", "Settings", "Security", "Login Methods", "Change password"]
        },
        {
          type: "steps",
          items: [
            {
              text: "Open **Settings**, then **Security**."
            },
            {
              text: "Find the **Password** row in **Login Methods** and click **Change password**.",
              note: "It reads **Set password** instead if you signed up with Google, Microsoft or Apple and have never had one. Either opens the same dialog."
            },
            {
              text: "Enter your **Current password**.",
              note: "Not asked for when you are setting a first password."
            },
            {
              text: "Enter the **New password**. A strength meter scores it as you type.",
              note: "At least 8 characters with an uppercase letter, a lowercase letter, a number and a special character."
            },
            {
              text: "Enter it again under **Confirm password** and save.",
              note: "A mismatch is rejected before anything is saved."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            {
              name: "Current password",
              value: "The password you sign in with today",
              note: "Not asked for when you are setting a first password."
            },
            {
              name: "New password",
              value: "The one you want",
              note: "A strength meter scores it as you type."
            },
            {
              name: "Confirm password",
              value: "The same again",
              note: "A mismatch is rejected before anything is saved."
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: "Five failed sign-in attempts lock the account for 15 minutes. If you are locked out, wait it out or use the forgot-password link rather than guessing again."
        },
        {
          type: "action",
          label: "Open Security",
          to: { kind: "app", route: "/settings/security" }
        }
      ]
    },
    {
      slug: "linked-sign-in-accounts",
      title: "Connect or unlink Google, Microsoft and Apple sign-in",
      why: "Linking a provider gives you a second way in, which matters on the day you cannot remember your password.",
      route: "/settings/security",
      estimatedMinutes: 3,
      keywords: [
        "link google account",
        "sign in with google",
        "connect microsoft",
        "how do i link my google account",
        "unlink apple",
        "social login",
        "sso",
        "remove a login method",
        "login methods"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Settings", "Security", "Login Methods"] },
        {
          type: "steps",
          items: [
            {
              text: "Open **Settings**, then **Security**."
            },
            {
              text: "Read **Login Methods** \u2014 Password, Google, Microsoft and Apple.",
              note: "Each provider row shows either **Connect**, or the linked email address and an **Unlink** button."
            },
            {
              text: "Click **Connect** on the provider you want to add.",
              note: "You go to the provider's consent screen and come back with the account linked."
            },
            {
              text: "Click **Unlink** to remove one \u2014 but set a password first if it is your only method.",
              note: "The app blocks unlinking your last remaining method rather than locking you out."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "Your email address is fixed on the Profile page and cannot be edited there. Linking a provider adds a way to sign in; it does not change which address the account belongs to."
        }
      ]
    },
    {
      slug: "individual-or-business-profile",
      title: "Switch between an Individual and a Business profile",
      why: "Only a Business profile exposes the company fields that SMS registration and your compliance jurisdiction depend on.",
      route: "/settings/profile",
      estimatedMinutes: 3,
      keywords: [
        "switch to business",
        "individual or business account",
        "how do i change my account to a business",
        "sole trader profile",
        "company details",
        "business profile",
        "switch to individual",
        "ein field missing"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Settings", "Profile"] },
        {
          type: "steps",
          items: [
            {
              text: "Open **Settings**, then **Profile**."
            },
            {
              text: "Find the card below **Personal Information** carrying **Switch to Business** or **Switch to Individual**.",
              note: "Which one you see depends on where you are now."
            },
            {
              text: "Click **Switch to Business**.",
              note: "Switching is not destructive \u2014 it changes which fields the page asks you for, nothing else."
            },
            {
              text: "Fill in the fields that appear: Company Name, Industry, Business ID / EIN, Logo, Website and a billing address.",
              note: "An Individual profile has none of these \u2014 only First Name, Last Name, the read-only Email Address, Phone Number and Photo."
            },
            {
              text: "Press **Save Changes** on each card separately.",
              note: "Fill a card in, press its own button, then move to the next one."
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: "Stay on Individual and there is nowhere to enter an EIN or a website, so a dedicated local number can never be registered for you. Switch to Business before you request one."
        }
      ]
    },
    {
      slug: "billing-address",
      title: "Add your billing address",
      why: "The country you put here decides your home compliance jurisdiction and the currency you are billed in.",
      route: "/settings/profile",
      estimatedMinutes: 3,
      keywords: [
        "billing address",
        "how do i add my billing address",
        "change my billing country",
        "what currency am i billed in",
        "state and postal code",
        "company address",
        "why is my jurisdiction wrong",
        "address for invoices"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Settings", "Profile", "Switch to Business"] },
        {
          type: "steps",
          items: [
            {
              text: "Open **Settings**, then **Profile**, and make sure you are on a Business profile."
            },
            {
              text: "Find the billing address on the Business card, alongside Company Name and Business ID / EIN."
            },
            {
              text: "Set the **Country** first.",
              note: "The state and postal-code labels change to match it \u2014 a Canadian address asks for a province and a postal code rather than a state and a ZIP."
            },
            {
              text: "Fill in the state or province, street, city and postal code.",
              note: "Carriers check this against your tax registration for 10DLC brand registration."
            },
            {
              text: "Press **Save Changes** on that card."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            {
              name: "Country",
              value: "Where the business is registered",
              note: "Sets your home jurisdiction and your billing currency."
            },
            {
              name: "State / province",
              value: "The regional label follows the country",
              note: "Needed for 10DLC brand registration."
            },
            {
              name: "Street, city, postal code",
              value: "The registered address",
              note: "Carriers check this against your tax registration."
            }
          ]
        },
        {
          type: "note",
          variant: "compliance",
          text: 'Settings, Compliance shows a Contact Creation Defaults matrix with a row labelled "Your billing country". That row is derived from this field \u2014 get the country wrong and every new contact is evaluated against the wrong rulebook.'
        },
        {
          type: "note",
          variant: "warning",
          text: "Canada is billed in CAD and everywhere else in USD. Once a paid subscription exists the currency is fixed and cannot be changed, so set the country before you subscribe."
        }
      ]
    }
  ],
  // ══════════════════════════════════════════════════════════════════════════════════════
  "sending-identity": [
    {
      slug: "connect-gmail-or-microsoft",
      title: "Connect a Gmail or Microsoft account for sending",
      why: "It is the shortest route to sending from your own address \u2014 a consent screen instead of a set of SMTP credentials.",
      route: "/settings/email-config",
      estimatedMinutes: 5,
      keywords: [
        "connect gmail",
        "how do i send email from my gmail",
        "connect microsoft 365 email",
        "outlook sending",
        "oauth email",
        "admin consent microsoft",
        "reconnect my email account",
        "send from my own address"
      ],
      blocks: [
        {
          type: "clickPath",
          path: ["Sidebar", "Settings", "Email Configuration", "Custom Email Sending"]
        },
        {
          type: "steps",
          items: [
            {
              text: "Turn on the **Custom Email Sending** toggle."
            },
            {
              text: "Pick which use cases it applies to with the **Documents**, **Forms** and **Campaigns** chips."
            },
            {
              text: "Choose **Gmail** or **Microsoft** in the provider picker."
            },
            {
              text: "Click **Connect**.",
              note: "You are sent to the provider, you consent to send mail on your behalf, and you are returned to the page with the connected account shown."
            },
            {
              text: "Press **Save Changes**."
            }
          ]
        },
        {
          type: "note",
          variant: "prereq",
          text: "On a Microsoft work or school account the tenant administrator may have to approve the permission first. The flow redirects through the admin consent screen and back on its own \u2014 if you are not an administrator, someone who is will have to complete that step."
        },
        {
          type: "note",
          variant: "warning",
          text: "If Custom Email Sending is on but the provider is not fully connected, email keeps going out from the default Respectly address. The page warns you when that is the case."
        },
        {
          type: "note",
          variant: "tip",
          text: "A connection can expire. If a send or a test fails asking you to reconnect, press **Connect** again \u2014 nothing else needs redoing."
        },
        {
          type: "action",
          label: "Open Email Configuration",
          to: { kind: "app", route: "/settings/email-config" }
        }
      ]
    },
    {
      slug: "custom-smtp",
      title: "Set up custom SMTP",
      why: "It is the route for Apple Mail and for any mailbox that is not Gmail or Microsoft.",
      route: "/settings/email-config",
      estimatedMinutes: 6,
      keywords: [
        "smtp settings",
        "how do i set up smtp",
        "custom smtp host and port",
        "app password",
        "apple mail sending",
        "from address",
        "smtp username and password",
        "send email from my own server"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Settings", "Email Configuration", "Custom SMTP"] },
        {
          type: "steps",
          items: [
            {
              text: "Turn on **Custom Email Sending**."
            },
            {
              text: "Pick **Apple** or **Custom SMTP** in the provider picker. The credential panel appears.",
              note: "This is the route for Apple Mail and for any mailbox that is not Gmail or Microsoft."
            },
            {
              text: "Enter the **Host** and **Port** from your mail provider's documentation."
            },
            {
              text: "Enter the **Username** \u2014 usually the full mailbox address \u2014 and the **Password**.",
              note: "Use an app password where your provider offers one. The page shows app-password guidance for the host you entered, because most providers will not accept your normal login password from an application."
            },
            {
              text: "Set the **From email and name** to what the recipient should see.",
              note: "Use an address on a domain you control."
            },
            {
              text: "Click **Send Test Email**, then **Save Changes**."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            {
              name: "Host",
              value: "Your provider's SMTP server",
              note: "From your mail provider's documentation."
            },
            {
              name: "Port",
              value: "The submission port your provider specifies"
            },
            {
              name: "Username",
              value: "Usually the full mailbox address"
            },
            {
              name: "Password",
              value: "An app password where your provider offers one",
              note: "Stored encrypted and never shown back to you."
            },
            {
              name: "From email and name",
              value: "What the recipient sees in their inbox",
              note: "Use an address on a domain you control."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "When you come back to edit the page the password field is blank \u2014 that is the saved password being withheld, not a lost one. Leave it blank and the existing password is kept; type a new one only when you actually want to replace it."
        },
        {
          type: "note",
          variant: "compliance",
          text: "Authentication, billing and support email always comes from Respectly regardless of what you configure here. Replies to your sends arrive in the Respectly inbox, not in the connected mailbox."
        }
      ]
    },
    {
      slug: "send-a-test-email",
      title: "Send a test email",
      why: "It is the only way to find out your credentials are wrong before a customer does.",
      route: "/settings/email-config",
      estimatedMinutes: 2,
      keywords: [
        "send test email",
        "how do i test my email settings",
        "test smtp connection",
        "check email is working",
        "email test failed",
        "verify email configuration",
        "did my email setup work"
      ],
      blocks: [
        {
          type: "clickPath",
          path: ["Sidebar", "Settings", "Email Configuration", "Send Test Email"]
        },
        {
          type: "steps",
          items: [
            {
              text: "Type a recipient address into the test field.",
              note: "Send it to a mailbox on a different provider. Mail to yourself can be delivered when mail to the outside world is not."
            },
            {
              text: "Click **Send Test Email** \u2014 before you save.",
              note: "It uses the configuration currently on screen, so you can test before saving rather than saving something broken and finding out later."
            },
            {
              text: 'Look for a message with the subject "Respectly \u2014 Test Email".',
              note: "Nothing arrives: check the host, port and username, and whether your provider requires an app password."
            },
            {
              text: "If it arrives from the wrong address, correct the **From email** field."
            },
            {
              text: "If you are asked to reconnect, the OAuth connection has expired \u2014 press **Connect** again."
            }
          ]
        }
      ]
    }
  ],
  // ══════════════════════════════════════════════════════════════════════════════════════
  compliance: [
    {
      slug: "run-a-compliance-test",
      title: "Run a compliance test and read the decision",
      why: "You can find out whether a send would be allowed without sending anything to anyone.",
      route: "/settings/policies",
      estimatedMinutes: 4,
      keywords: [
        "run a compliance test",
        "how do i test if i can send to someone",
        "why was my message blocked",
        "preview a policy decision",
        "check consent before sending",
        "decision record",
        "what does conditional mean",
        "remediation"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Settings", "Policies", "Run a compliance test"] },
        {
          type: "steps",
          items: [
            {
              text: "Open **Settings**, then **Policies**."
            },
            {
              text: "Click **Run a compliance test**, alongside **View Decisions**.",
              note: "It runs the same engine a real send runs, so the answer you get is the answer you would have got \u2014 without sending anything to anyone."
            },
            {
              text: "Read the outcome: **Allow**, **Conditional** or **Block**.",
              note: "**Conditional** does not mean blocked. It means the send may go ahead with a condition attached \u2014 commonly a calling-hours window, so the message is held until the recipient's local window opens rather than dropped."
            },
            {
              text: "Expand the record for the detail.",
              note: "The routing path it took through global, country and state rules, each individual check it evaluated, the consent auto-captured at contact creation, and the remediation that would clear a failure."
            },
            {
              text: "Reopen it later from **Settings**, **Policies**, **Decisions** rather than re-running it.",
              note: "A test is written to the decisions log like any other evaluation."
            }
          ]
        },
        {
          type: "action",
          label: "Open Policies",
          to: { kind: "app", route: "/settings/policies" }
        }
      ]
    },
    {
      slug: "export-decisions-csv",
      title: "Export the decisions log to CSV",
      why: "When a regulator, a carrier or a client asks you to prove what you sent and why, this is the file.",
      route: "/settings/policies/decisions",
      estimatedMinutes: 3,
      keywords: [
        "export decisions",
        "how do i export the compliance log",
        "download compliance decisions csv",
        "audit evidence for a regulator",
        "filter policy decisions",
        "proof of consent checks",
        "compliance report",
        "decision log csv"
      ],
      blocks: [
        {
          type: "clickPath",
          path: ["Sidebar", "Settings", "Policies", "View Decisions", "Export CSV"]
        },
        {
          type: "steps",
          items: [
            {
              text: "Open **Settings**, **Policies**, **View Decisions**.",
              note: "The page opens with a stats bar \u2014 Total Evaluations, Allow, Conditional and Block \u2014 above the log itself."
            },
            {
              text: "Set the **Date Range** to the period in question.",
              note: "Narrow first, then export, so the file contains the evidence you were asked for and nothing else."
            },
            {
              text: "Filter by **Channel**, **Purpose** and **Decision** to isolate what you are proving."
            },
            {
              text: "Narrow further by **Country**, **State**, **Rule ID** or **Source**, or search for a single recipient."
            },
            {
              text: "Click **Export CSV**."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: 'If the table reads "No decisions match your filters" the filters are too tight, not the log empty. "No decisions yet" is the genuinely empty state.'
        },
        {
          type: "action",
          label: "Open Policy Decisions",
          to: { kind: "app", route: "/settings/policies/decisions" }
        }
      ]
    }
  ],
  // ══════════════════════════════════════════════════════════════════════════════════════
  dashboard: [
    {
      slug: "hide-and-restore-a-widget",
      title: "Hide a widget and get it back",
      why: "A widget you never act on is noise sitting on top of the one you do.",
      route: "/",
      estimatedMinutes: 2,
      keywords: [
        "hide a widget",
        "how do i remove a widget from the dashboard",
        "restore a hidden widget",
        "show hidden widgets",
        "i deleted a widget by mistake",
        "reset dashboard",
        "dashboard layout"
      ],
      blocks: [
        { type: "clickPath", path: ["Dashboard", "Customize"] },
        {
          type: "steps",
          items: [
            {
              text: "Press **Customize** on the dashboard. The grid switches into edit mode."
            },
            {
              text: "Hide any widget you do not want.",
              note: "A widget you never act on is noise sitting on top of the one you do."
            },
            {
              text: "To bring one back, open the **Hidden** panel \u2014 it is labelled with a count \u2014 and press **Show** on the widget you want returned."
            },
            {
              text: "Press **Done**."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "**Reset**, also in the Customize bar, puts every widget back and asks you to confirm first. It is the quickest way out of a layout you have made a mess of."
        },
        {
          type: "note",
          variant: "tip",
          text: "The layout is saved per user, so hiding a widget affects only your own dashboard, not your colleagues'."
        }
      ]
    },
    {
      slug: "reorder-widgets",
      title: "Reorder your widgets",
      why: "What you need to act on today belongs at the top, where you will see it without scrolling.",
      route: "/",
      estimatedMinutes: 2,
      keywords: [
        "reorder widgets",
        "how do i move a widget",
        "drag widgets on the dashboard",
        "rearrange dashboard",
        "put a widget at the top",
        "change widget order",
        "customize dashboard layout"
      ],
      blocks: [
        { type: "clickPath", path: ["Dashboard", "Customize"] },
        {
          type: "steps",
          items: [
            {
              text: "Press **Customize**. In edit mode the widgets become draggable."
            },
            {
              text: "Drag the four you act on daily to the top.",
              note: "Outstanding Signatures, Unread Inbox, Failed Deliveries (24h) and Compliance Attention are the four that tell you something needs doing today."
            },
            {
              text: "Put the trend charts below them.",
              note: "The dashboard is a twelve-column grid on a wide screen and a single column on a phone, so the order you set here is exactly the order a phone reads top to bottom."
            },
            {
              text: "Press **Done** when the order looks right."
            }
          ]
        }
      ]
    },
    {
      slug: "dashboard-density",
      title: "Change the dashboard density",
      why: "Compact fits noticeably more onto one screen, which is the difference between scanning the dashboard and scrolling it.",
      route: "/",
      estimatedMinutes: 1,
      keywords: [
        "dashboard density",
        "compact dashboard",
        "how do i fit more widgets on screen",
        "comfortable or compact",
        "make widgets smaller",
        "dashboard too spread out"
      ],
      blocks: [
        { type: "clickPath", path: ["Dashboard", "Customize"] },
        {
          type: "steps",
          items: [
            {
              text: "Press **Customize** on the dashboard."
            },
            {
              text: "Find **Density** in the Customize bar and choose **comfortable** or **compact**.",
              note: "Comfortable is the default. Compact fits noticeably more onto one screen, which is the difference between scanning the dashboard and scrolling it."
            },
            {
              text: "Press **Done**. The choice is saved with the rest of your layout."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "Compact pairs well with a long list of widgets. If you have hidden most of them, comfortable reads better."
        }
      ]
    }
  ],
  // ══════════════════════════════════════════════════════════════════════════════════════
  "team-billing": [
    {
      slug: "resend-or-revoke-an-invitation",
      title: "Resend or revoke an invitation",
      why: "Invitations expire, and an unclaimed one sitting in an ex-colleague's inbox is a live way into your account.",
      route: "/settings/team",
      requires: { permissions: ["team:read", "team:invite"] },
      estimatedMinutes: 2,
      keywords: [
        "resend invitation",
        "how do i resend an invite",
        "revoke an invitation",
        "cancel a team invite",
        "invitation expired",
        "this email already has a pending invitation",
        "colleague never got the invite email",
        "pending invitations"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Settings", "Team Members", "Pending Invitations"] },
        {
          type: "steps",
          items: [
            {
              text: "Open **Settings**, then **Team Members**, and scroll to **Pending Invitations** below the members list."
            },
            {
              text: "Read the row: Email, Role, Invited By, Sent and Expires.",
              note: "An invitation is valid for seven days."
            },
            {
              text: "Click **Resend** when the first email was missed or has expired.",
              note: "It issues the invitation email again. You do not need to revoke first."
            },
            {
              text: "Click **Revoke** to cancel it.",
              note: "The link in the original email stops working \u2014 which is the point when the invitation is sitting in an ex-colleague's inbox."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: 'Inviting the same address twice is refused with "This email already has a pending invitation." That is what Resend is for.'
        },
        {
          type: "note",
          variant: "tip",
          text: "Someone you removed from the team can be invited again. Accepting the new invitation reactivates their old account rather than creating a second one."
        },
        {
          type: "action",
          label: "Open Team Members",
          to: { kind: "app", route: "/settings/team" }
        }
      ]
    },
    {
      slug: "change-a-members-role",
      title: "Change a member's role",
      why: "Access granted on someone's first day is rarely the access they should still have six months later.",
      route: "/settings/team",
      requires: { permissions: ["team:read", "team:manage"] },
      estimatedMinutes: 2,
      keywords: [
        "change a role",
        "how do i change someones role",
        "make someone an admin",
        "downgrade a team member to viewer",
        "change member permissions",
        "promote a user",
        "why cant i change the owner role"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Settings", "Team Members", "Change Role"] },
        {
          type: "steps",
          items: [
            {
              text: "Open **Settings**, then **Team Members**."
            },
            {
              text: "Find the person in the **Members** table \u2014 Member, Role, Status and Last Active.",
              note: "Last Active is worth a glance while you are here."
            },
            {
              text: "Click **Change Role** on their row and pick the new role."
            },
            {
              text: "Expect a delay. The change takes effect the next time that person's session refreshes.",
              note: "Not instantly, and not on your screen."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            {
              name: "Admin",
              value: "Everything except managing billing and creating or editing roles"
            },
            {
              name: "Agent",
              value: "The operational subset \u2014 contacts, documents, campaigns and the inbox"
            },
            {
              name: "Viewer",
              value: "Read-only"
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: "The owner's role cannot be changed, and the Owner role itself cannot be handed to anyone else. If the wrong person is the owner, that is a support ticket, not a settings change."
        }
      ]
    },
    {
      slug: "deactivate-or-remove-a-member",
      title: "Deactivate or remove a member",
      why: "These two look similar and are not: one is a pause, the other frees a seat.",
      route: "/settings/team",
      requires: { permissions: ["team:read", "team:manage", "team:remove"] },
      estimatedMinutes: 3,
      keywords: [
        "remove a team member",
        "how do i deactivate someone",
        "someone left the company",
        "free up a seat",
        "deactivate vs remove",
        "reactivate a member",
        "revoke access",
        "delete a user"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Settings", "Team Members"] },
        {
          type: "steps",
          items: [
            {
              text: "Open **Settings**, then **Team Members**, and find the person in the **Members** table."
            },
            {
              text: "For a leave of absence, use **Deactivate**.",
              note: "A reversible pause: they stay in the members list, are signed out immediately, and **Activate** puts them back. The seat stays used."
            },
            {
              text: "For someone who has left, use **Remove**.",
              note: "They disappear from the list, are signed out, lose their role, and their seat is freed. Only Remove gives you the seat back."
            },
            {
              text: "Either way, their history stays.",
              note: "Removal is not a deletion \u2014 the record is kept so the audit trail still names them as the actor on everything they did."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            {
              name: "Deactivate",
              value: "A reversible pause",
              note: "They stay in the list, are signed out immediately, and Activate puts them back."
            },
            {
              name: "Remove",
              value: "Takes them off the team",
              note: "They are signed out, lose their role, and their seat is freed."
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: "The owner cannot be deactivated or removed."
        },
        {
          type: "note",
          variant: "tip",
          text: "A removed person can be invited again later."
        }
      ]
    },
    {
      slug: "create-a-custom-role",
      title: "Create a custom role and set its permissions",
      why: "The four built-in roles are broad by design; a custom role is how you give someone exactly what their job needs.",
      route: "/settings/roles",
      requires: { permissions: ["roles:read", "roles:create"] },
      estimatedMinutes: 5,
      keywords: [
        "create a role",
        "how do i create a custom role",
        "set permissions for a role",
        "restrict access to billing",
        "permissions matrix",
        "edit a system role",
        "delete a role",
        "role in use"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Settings", "Roles & Permissions", "Create Role"] },
        {
          type: "steps",
          items: [
            {
              text: "Open **Settings**, then **Roles & Permissions**.",
              note: "The role cards show each role's name, how many members hold it and how many permissions it carries."
            },
            {
              text: "Consider editing an existing role first \u2014 click its card to open the same editor titled **Edit Role**.",
              note: "Tightening an existing role is usually less work than building a new one and reassigning everyone."
            },
            {
              text: "Click **Create Role**."
            },
            {
              text: "Enter a **Role Name** (unique within your account) and a **Description**.",
              note: "Write the description. The next admin will not remember your reasoning."
            },
            {
              text: "Pick a **Color** \u2014 it is how the role is badged around the app."
            },
            {
              text: "Tick the **Permissions** you need in the matrix.",
              note: "Grouped by Contacts, Documents, Forms, Campaigns, Inbox, Templates, Analytics, Team, Roles, Settings, Billing, Webhooks and AI, with an **All Permissions** master toggle at the top."
            },
            {
              text: "Start from nothing and tick what the job needs, rather than starting from everything and removing.",
              note: "The second approach reliably leaves something behind."
            },
            {
              text: "Save."
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: "A role that still has members assigned to it cannot be deleted. Move those people to another role first."
        },
        {
          type: "action",
          label: "Open Roles & Permissions",
          to: { kind: "app", route: "/settings/roles" }
        }
      ]
    },
    {
      slug: "upgrade-or-downgrade-your-plan",
      title: "Upgrade or downgrade your plan",
      why: "The two take effect at different times, and being surprised by that is an avoidable annoyance.",
      route: "/billing",
      requires: { permissions: ["billing:read", "billing:manage"] },
      estimatedMinutes: 4,
      keywords: [
        "upgrade my plan",
        "how do i change my plan",
        "downgrade plan",
        "switch to annual billing",
        "when does a downgrade take effect",
        "proration",
        "annual discount",
        "change subscription"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Settings", "Billing", "Plans"] },
        {
          type: "steps",
          items: [
            {
              text: "Open **Billing**, then the **Plans** tab."
            },
            {
              text: "Set the **Monthly / Annual** toggle above the cards.",
              note: "Annual is discounted by 20%."
            },
            {
              text: "Find your current plan \u2014 it carries a **Current Plan** badge and a disabled button.",
              note: "Every other card offers **Upgrade** or **Downgrade**."
            },
            {
              text: "Click **Upgrade** and confirm in the **Confirm Upgrade** dialog.",
              note: "Immediate and prorated \u2014 you are credited for the unused part of the old plan and charged the balance now."
            },
            {
              text: "Or click **Downgrade** and confirm in the **Confirm Downgrade** dialog.",
              note: "Scheduled, not immediate. It takes effect at the end of the current period and you keep what you paid for until then."
            }
          ]
        },
        {
          type: "fields",
          rows: [
            {
              name: "Upgrade",
              value: "Immediate, via a Confirm Upgrade dialog",
              note: "Prorated \u2014 credited for the unused part of the old plan, charged the balance now."
            },
            {
              name: "Downgrade",
              value: "Scheduled, via a Confirm Downgrade dialog",
              note: "Takes effect at the end of the current period."
            }
          ]
        },
        {
          type: "note",
          variant: "prereq",
          text: "Moving off the Free plan needs a card on file. If there is none, add one on the **Payment** tab first and the upgrade will go through."
        },
        { type: "action", label: "Open Billing", to: { kind: "app", route: "/billing" } }
      ]
    },
    {
      slug: "buy-an-add-on",
      title: "Buy an add-on",
      why: "It raises a single limit you are about to hit without moving your whole account onto a bigger plan.",
      route: "/billing",
      requires: { permissions: ["billing:read", "billing:manage"] },
      estimatedMinutes: 3,
      keywords: [
        "buy an add-on",
        "how do i buy more ai credits",
        "buy extra sms credits",
        "purchase more capacity",
        "add-ons not available on the free plan",
        "cancel an add-on",
        "add-on failed retry",
        "extra contacts"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Settings", "Billing", "Add-ons"] },
        {
          type: "steps",
          items: [
            {
              text: "Open **Billing**, then the **Add-ons** tab."
            },
            {
              text: "Check **Active Purchases** first for what you already hold.",
              note: "The add-on, quantity, type, status and the date it started \u2014 with **Cancel**, **Buy Again**, and **Retry** where a purchase errored."
            },
            {
              text: "Find what you need under **Available to Purchase**."
            },
            {
              text: "Set the quantity with the stepper and click **Purchase**."
            },
            {
              text: "Check the total in the **Add** dialog before you commit.",
              note: "A one-time add-on is charged straight away; a recurring one attaches to your subscription and appears on the next invoice."
            },
            {
              text: "The extra capacity applies to the current period immediately.",
              note: "You do not have to wait for a renewal."
            }
          ]
        },
        {
          type: "note",
          variant: "warning",
          text: 'On the Free plan the tab reads "Add-ons are not available on the Free plan". Upgrading is the only route to extra capacity there.'
        }
      ]
    },
    {
      slug: "update-your-payment-card",
      title: "Update your payment card",
      why: "An expired card is the most common reason an account slips into past due, and it is the easiest to prevent.",
      route: "/billing",
      requires: { permissions: ["billing:read", "billing:manage"] },
      estimatedMinutes: 3,
      keywords: [
        "update my card",
        "how do i add a payment method",
        "change my credit card",
        "card expired",
        "remove a card",
        "set default card",
        "no payment method on file",
        "payment failed"
      ],
      blocks: [
        {
          type: "clickPath",
          path: ["Sidebar", "Settings", "Billing", "Payment", "Add Payment Method"]
        },
        {
          type: "steps",
          items: [
            {
              text: "Open **Billing**, then the **Payment** tab."
            },
            {
              text: "Click **Add Payment Method** and add the NEW card first.",
              note: "Do this before removing the old one, so there is never a moment with no card on file. The card form comes from the payment provider \u2014 Respectly never sees or stores the number."
            },
            {
              text: "Open the new card's menu and choose **Set as Default**.",
              note: "The **Default** badge marks the card that will actually be charged."
            },
            {
              text: "Open the old card's menu, choose **Remove**, and confirm."
            },
            {
              text: "Check the list reads the way you expect \u2014 brand, last four digits, expiry date, and the Default badge in the right place."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: 'An expired card is the most common reason an account slips into past due. With no card on file the tab reads "No payment method on file" and explains what that means for your particular state \u2014 on trial, on Free, or on a paid plan.'
        }
      ]
    },
    {
      slug: "find-your-invoices",
      title: "Find and download your invoices",
      why: "Your accountant wants a PDF, and every charge on the account is listed in one place.",
      route: "/billing",
      requires: { permissions: ["billing:read"] },
      estimatedMinutes: 2,
      keywords: [
        "download an invoice",
        "where are my invoices",
        "how do i get a receipt",
        "billing history",
        "invoice pdf",
        "what was i charged for",
        "overage charge on my bill",
        "past due invoice"
      ],
      blocks: [
        {
          type: "clickPath",
          path: ["Sidebar", "Settings", "Billing", "Overview", "Billing History"]
        },
        {
          type: "steps",
          items: [
            {
              text: "Open **Billing** and stay on the **Overview** tab."
            },
            {
              text: "Scroll to **Billing History**.",
              note: "It covers all charges \u2014 subscription renewals, add-on purchases, overages and credits."
            },
            {
              text: "Find the row you want: Invoice #, Description, Type, Amount, Date and Status.",
              note: "Type is one of Subscription, Add-on, Overage or Credit. Status is one of Paid, Pending, Overdue or Cancelled."
            },
            {
              text: "Click **Download** on that row to produce the PDF."
            },
            {
              text: "Act on any **Overdue** row the day you see it.",
              note: "Unpaid invoices move the account to past due, and from there into a grace period."
            }
          ]
        }
      ]
    }
  ],
  // ══════════════════════════════════════════════════════════════════════════════════════
  "getting-help": [
    {
      slug: "what-cowork-can-do",
      title: "What CoWork can do for you",
      why: "Knowing where the assistant's limits are saves you asking it for things it will never do.",
      requires: { features: ["cowork"] },
      estimatedMinutes: 4,
      keywords: [
        "what can cowork do",
        "ai assistant",
        "can cowork send messages for me",
        "can the assistant change consent",
        "cowork limits",
        "ask cowork to build a workflow",
        "does cowork confirm before sending",
        "cowork cannot upload a file"
      ],
      blocks: [
        {
          type: "steps",
          items: [
            {
              text: "Ask it to read and analyse anything you can see.",
              note: "Contacts, tags, pipelines, templates, documents, forms and their submissions, workflows, campaigns and the inbox. CoWork acts as you, inside your account, and never has more access than your own role does."
            },
            {
              text: "Ask it to create and update.",
              note: "Contacts, tags, pipelines and stages; documents, forms, SMS and email templates \u2014 each generated populated rather than empty."
            },
            {
              text: "Ask it to send.",
              note: "A document for signature, a form, your intake form to a contact, an SMS, email or voice campaign, and replies in the inbox."
            },
            {
              text: "Ask it to automate.",
              note: "It writes a whole workflow as a trigger plus ordered steps, creating anything a step depends on first."
            },
            {
              text: "Do not ask it to change consent, or to hand you a file.",
              note: "Granting, revoking and opting out are yours alone; and there is no file channel in the chat."
            }
          ]
        },
        {
          type: "note",
          variant: "compliance",
          text: "CoWork never changes consent. It can read consent and opt-out history and warn you about ineligible recipients before a send, but granting, revoking and opting out are yours alone."
        },
        {
          type: "note",
          variant: "tip",
          text: "Reading and creating happen straight away. Anything that deletes or sends stops and shows you a confirmation card first, listing exactly what it is about to do."
        },
        {
          type: "note",
          variant: "warning",
          text: "There is no file channel in the chat. CoWork cannot take a PDF you want to upload and cannot hand you a CSV or a signed document to download \u2014 use the relevant page for those."
        }
      ]
    },
    {
      slug: "verify-a-pdf-signature",
      title: "Verify a signed PDF",
      why: "It proves a document you have been handed is the one that was signed and has not been altered since.",
      route: "/settings/verify-signature",
      estimatedMinutes: 2,
      keywords: [
        "verify a signature",
        "how do i check a pdf is genuine",
        "is this signed document authentic",
        "verify pdf signature",
        "check document integrity",
        "signature certificate",
        "has this contract been tampered with"
      ],
      blocks: [
        { type: "clickPath", path: ["Sidebar", "Settings", "Verify PDF Signature"] },
        {
          type: "steps",
          items: [
            {
              text: "Open **Settings**, then **Verify PDF Signature**."
            },
            {
              text: "Drag a PDF onto the drop zone, or click it to browse.",
              note: "PDF files only, up to 50 MB."
            },
            {
              text: "Read the result: the signer details, the certificate the document was signed with, and an integrity banner.",
              note: "The integrity banner is the answer \u2014 it tells you whether the file has been changed since signing."
            },
            {
              text: "Click **Verify another PDF** to clear it for the next one."
            }
          ]
        },
        {
          type: "note",
          variant: "tip",
          text: "This works on any signed PDF you have been sent, not only ones from your own account \u2014 it is a checker, not a lookup of your records."
        },
        {
          type: "note",
          variant: "compliance",
          text: "For your own sends, the Audit Trail PDF on a document campaign is the fuller evidence record. Verification confirms the file; the audit trail explains how it got signed."
        },
        {
          type: "action",
          label: "Open Verify PDF Signature",
          to: { kind: "app", route: "/settings/verify-signature" }
        }
      ]
    }
  ]
};

// src/getting-started/articles/index.ts
var SOURCES = [
  contactsArticles,
  templatesArticles,
  sendArticles,
  formsArticles,
  documentsArticles,
  automationArticles,
  inboxArticles,
  settingsArticles
];
var TASK_ARTICLES = SOURCES.reduce((acc, source) => {
  for (const [chapterSlug, steps] of Object.entries(source)) {
    acc[chapterSlug] = [...acc[chapterSlug] ?? [], ...steps];
  }
  return acc;
}, {});

// src/getting-started/build.ts
var GUIDE_APP_BASE_URL = "https://app.respectly.ai";
var GUIDE_SITE_BASE_URL = "https://respectly.ai";
var INDUSTRY_TO_SOLUTION_SLUG = {
  education: "education",
  healthcare: "healthcare",
  "home-services": "home-services",
  insurance: "insurance",
  "real-estate": "real-estate",
  legal: "legal-financial",
  financial: "legal-financial",
  recreational: "fitness",
  consulting: null,
  hospitality: null,
  manufacturing: null,
  retail: null,
  technology: null,
  other: null
};
function resolveLink(link, surface, opts = {}) {
  const locale = opts.locale ?? "en";
  const appBase = opts.appBaseUrl ?? GUIDE_APP_BASE_URL;
  const siteBase = opts.siteBaseUrl ?? GUIDE_SITE_BASE_URL;
  if (link.kind === "external") {
    return { href: link.href, external: true };
  }
  if (link.kind === "app") {
    return surface === "app" ? { href: link.route, external: false } : { href: appBase + link.route, external: true };
  }
  const path = "/" + locale + "/help-center/" + link.categorySlug;
  const anchor = link.questionSlug ? "#" + link.questionSlug : "";
  return surface === "web" ? { href: path + anchor, external: false } : { href: siteBase + path + anchor, external: true };
}
function stepAnchor(chapterSlug, stepSlug) {
  return chapterSlug + "--" + stepSlug;
}
var p = (text) => ({ type: "paragraph", text });
var list = (items, ordered = false) => ({ type: "list", items, ordered });
function describeWorkflowStep(stepType, config) {
  const c = config ?? {};
  const label = (k) => typeof c[k] === "string" ? c[k] : void 0;
  switch (stepType) {
    case "add_tag":
      return 'Tag them "' + (label("tagName") ?? "a tag") + '"';
    case "move_stage":
      return 'Move them to "' + (label("stageName") ?? "a stage") + '" in ' + (label("groupName") ?? "the pipeline");
    case "send_sms":
      return 'Text them "' + (label("templateName") ?? "an SMS template") + '"';
    case "send_email":
      return 'Email them "' + (label("templateName") ?? "an email template") + '"';
    case "send_form":
      return 'Send them the "' + (label("formName") ?? "form") + '"';
    case "send_document":
      return 'Send "' + (label("documentName") ?? "the document") + '" for signature';
    case "wait": {
      const amount = typeof c.duration === "number" ? c.duration : typeof c.value === "number" ? c.value : void 0;
      const unit = label("unit") ?? "days";
      return amount ? "Wait " + amount + " " + unit : "Wait";
    }
    default:
      return stepType.replace(/_/g, " ");
  }
}
var KIT_DERIVED = {
  "guided-setup/pick-your-kit": (kit) => [
    p(
      'For your industry the recommended kit is "' + kit.name + '" \u2014 ' + kit.tagline + " Applying it creates " + kit.tags.length + " tags, the " + kit.pipeline.name + " (" + kit.pipeline.stages.length + " stages), " + kit.forms.length + " form" + (kit.forms.length === 1 ? "" : "s") + ", " + kit.documents.length + " document" + (kit.documents.length === 1 ? "" : "s") + ", " + kit.templates.length + " message templates and " + kit.workflows.length + " ready-to-run workflow" + (kit.workflows.length === 1 ? "" : "s") + "."
    ),
    { type: "example", title: "What this looks like for you", body: kit.scenario },
    {
      type: "note",
      variant: "tip",
      text: "Applying a kit is safe to re-run. Tags and pipelines are matched by name, so nothing is duplicated, and anything you have already built is left alone."
    }
  ],
  "contacts/tags": (kit) => [
    {
      type: "example",
      title: "Tags in the " + kit.name + " kit",
      body: kit.tags.map((t) => t.name).join(" \xB7 ")
    },
    p("Use these as your starting vocabulary and add your own \u2014 tags are how you segment who gets what.")
  ],
  "contacts/pipelines-and-stages": (kit) => [
    p("The " + kit.name + ' kit builds the "' + kit.pipeline.name + '" with these stages, in order:'),
    list(
      kit.pipeline.stages.map((s) => s.name + " \u2014 move on once: " + s.exitCriteria.join("; ")),
      true
    ),
    {
      type: "note",
      variant: "tip",
      text: 'Those exit criteria are the useful part. Agreeing what "done" means for each stage is what stops a pipeline turning into a list of everyone you have ever met.'
    }
  ],
  "templates/sms-template": (kit) => {
    const sms = kit.templates.find((t) => t.type === "sms");
    if (!sms || !sms.content.message) return [];
    return [
      { type: "example", title: "Example \u2014 " + sms.name, body: sms.content.message },
      p('Prompt used to generate it: "' + sms.samplePrompt + '"')
    ];
  },
  "templates/email-template": (kit) => {
    const email = kit.templates.find((t) => t.type === "email");
    if (!email || !email.content.subject) return [];
    const body = "Subject: " + email.content.subject + (email.content.previewText ? "\nPreview: " + email.content.previewText : "");
    return [
      { type: "example", title: "Example \u2014 " + email.name, body },
      p('Prompt used to generate it: "' + email.samplePrompt + '"')
    ];
  },
  "forms/build-your-first-form": (kit) => {
    const form = kit.forms[0];
    if (!form) return [];
    return [
      p(
        "The " + kit.name + ' kit ships "' + form.name + '"' + (form.description ? " \u2014 " + form.description : "") + "."
      ),
      { type: "example", title: "Prompt that generates this form", body: form.samplePrompt },
      p("Paste that into the AI form wizard to produce the same form yourself, then edit the fields.")
    ];
  },
  "documents/create-a-document": (kit) => {
    const doc = kit.documents[0];
    if (!doc) return [];
    return [
      p("The " + kit.name + ' kit ships "' + doc.name + '" as a reusable, signable template.'),
      { type: "example", title: "Prompt that generates this document", body: doc.samplePrompt }
    ];
  },
  "automation/your-kit-workflows": (kit) => {
    if (kit.workflows.length === 0) return [];
    const blocks = [
      p(
        "The " + kit.name + " kit activates " + kit.workflows.length + " workflow" + (kit.workflows.length === 1 ? "" : "s") + ":"
      )
    ];
    for (const wf of kit.workflows) {
      blocks.push(
        p(
          wf.name + " \u2014 " + (wf.description ?? "triggered on " + wf.triggerType.replace(/_/g, " ")) + ":"
        )
      );
      blocks.push(list(wf.steps.map((s) => describeWorkflowStep(s.stepType, s.config)), true));
    }
    blocks.push({
      type: "note",
      variant: "tip",
      text: "Open each one and read it before you rely on it \u2014 they are ordinary workflows you can edit."
    });
    return blocks;
  },
  "getting-help/your-playbook": (kit) => kit.coworkPrompts.length === 0 ? [] : [p("Ask CoWork to do any of these for you in plain English:"), list(kit.coworkPrompts)]
};
var NO_KIT_BLOCKS = {
  "guided-setup/pick-your-kit": [
    {
      type: "note",
      variant: "tip",
      text: "There is no starter kit tailored to your industry yet, so the setup wizard leads with the kit gallery instead of a recommendation. Browse the ten kits and apply whichever is closest \u2014 everything it creates is editable, and nothing is locked to an industry."
    },
    { type: "action", label: "Browse all starter kits", to: { kind: "app", route: "/use-cases" } }
  ]
};
function cloneChapters(chapters) {
  return chapters.map((chapter) => ({
    ...chapter,
    steps: chapter.steps.map((step) => ({
      ...step,
      requires: step.requires ? { ...step.requires } : void 0,
      blocks: [...step.blocks]
    }))
  }));
}
var industryLabel = (industry) => INDUSTRIES.find((i) => i.value === industry)?.label ?? industry;
function buildIndustryGuide(industry) {
  const overlay = INDUSTRY_OVERLAYS[industry];
  const kit = getKitForIndustry(industry);
  const chapters = cloneChapters(GUIDE_SPINE);
  for (const chapter of chapters) {
    for (const step of chapter.steps) {
      const key = chapter.slug + "/" + step.slug;
      const derived = kit ? KIT_DERIVED[key]?.(kit) : NO_KIT_BLOCKS[key];
      if (derived && derived.length > 0) step.blocks = [...step.blocks, ...derived];
      const extra = overlay?.stepBlocks?.[key];
      if (extra && extra.length > 0) step.blocks = [...step.blocks, ...extra];
    }
    const tasks = TASK_ARTICLES[chapter.slug];
    if (tasks && tasks.length > 0) {
      chapter.steps = [
        ...chapter.steps,
        ...tasks.map((step) => ({
          ...step,
          requires: step.requires ? { ...step.requires } : void 0,
          blocks: [...step.blocks]
        }))
      ];
    }
    const extraSteps = overlay?.extraSteps?.[chapter.slug];
    if (extraSteps && extraSteps.length > 0) chapter.steps = [...chapter.steps, ...extraSteps];
  }
  const stepCount = chapters.reduce((n, c) => n + c.steps.length, 0);
  const estimatedMinutes = chapters.reduce(
    (n, c) => n + c.steps.reduce((m, s) => m + (s.estimatedMinutes ?? 3), 0),
    0
  );
  return {
    industry,
    label: industryLabel(industry),
    headline: overlay?.headline ?? "Set up Respectly end to end",
    scenario: overlay?.scenario ?? "Follow these chapters in order and you will finish with a fully configured account \u2014 senders verified, consent captured lawfully, contacts imported, and your first campaign sent.",
    kitId: kit?.industryKey ?? null,
    chapters,
    stepCount,
    estimatedMinutes
  };
}
function listIndustryGuideSummaries() {
  return INDUSTRIES.map(({ value }) => {
    const guide = buildIndustryGuide(value);
    return {
      industry: guide.industry,
      label: guide.label,
      headline: guide.headline,
      kitId: guide.kitId,
      chapterCount: guide.chapters.length,
      stepCount: guide.stepCount,
      estimatedMinutes: guide.estimatedMinutes
    };
  });
}
function isIndustryValue(value) {
  if (!value) return false;
  return INDUSTRIES.some((i) => i.value === value);
}
function resolveGuideIndustry(industry) {
  if (!industry) return null;
  const key = industry.toLowerCase().trim();
  return isIndustryValue(key) ? key : null;
}

// src/getting-started/app-docs.ts
var DOC_MODULES = [
  {
    slug: "start-here",
    title: "Start here",
    icon: "Rocket",
    blurb: "Eight first runs, in order. Each one ends with something real.",
    articles: [
      "first-run/your-first-contact",
      "first-run/your-first-intake-request",
      "first-run/your-first-campaign",
      "first-run/your-first-document",
      "first-run/your-first-form",
      "first-run/your-first-workflow",
      "first-run/your-first-sequence",
      "first-run/getting-help"
    ]
  },
  {
    slug: "foundations",
    title: "How Respectly works",
    icon: "Compass",
    blurb: "What the app is for, and how consent works before you send anything.",
    articles: [
      "before-you-begin/what-respectly-does",
      "before-you-begin/the-consent-model",
      "before-you-begin/what-you-will-need",
      "before-you-begin/who-does-what",
      "guided-setup/pick-your-kit",
      "guided-setup/what-a-kit-creates"
    ]
  },
  {
    slug: "contacts",
    title: "Contacts",
    icon: "Users",
    blurb: "The people you talk to, the consent you hold, and the stage they are at.",
    articles: [
      "contacts/four-ways-in",
      "contacts/csv-import",
      "contacts/google-outlook-import",
      "contacts/custom-fields",
      "contacts/tags",
      "contacts/pipelines-and-stages",
      "contacts/capture-consent",
      "contacts/intake-forms",
      "contacts/bulk-actions-and-export"
    ]
  },
  {
    slug: "messages-templates",
    title: "Messages & templates",
    icon: "MessageSquare",
    blurb: "Reusable SMS, email and voice content with merge fields.",
    articles: [
      "templates/templates-hub",
      "templates/ai-create",
      "templates/sms-template",
      "templates/email-template",
      "templates/voice-script",
      "templates/merge-tags"
    ]
  },
  {
    slug: "send",
    title: "Send",
    icon: "Send",
    blurb: "One screen starts every message, signature request and form send.",
    articles: [
      "send/the-send-screen",
      "send/generate-and-edit",
      "send/recipients-and-eligibility",
      "send/review-and-send"
    ]
  },
  {
    slug: "inbox",
    title: "Inbox",
    icon: "Inbox",
    blurb: "Everything that comes back \u2014 replies, submissions, signatures, opt-outs.",
    articles: [
      "inbox/unified-inbox",
      "inbox/conversations",
      "inbox/campaign-dashboards",
      "inbox/handle-opt-outs"
    ]
  },
  {
    slug: "forms",
    title: "Forms",
    icon: "ClipboardList",
    blurb: "Collect information \u2014 and consent \u2014 from a public page you control.",
    articles: [
      "forms/forms-vs-agreements",
      "forms/build-your-first-form",
      "forms/form-fields-and-consent",
      "forms/publish-and-share",
      "forms/read-submissions"
    ]
  },
  {
    slug: "documents",
    title: "Documents & e-signature",
    icon: "FileSignature",
    blurb: "Agreements that get signed, and the evidence that they were.",
    articles: [
      "documents/create-a-document",
      "documents/signers-and-fields",
      "documents/uploaded-pdfs",
      "documents/send-for-signature",
      "documents/signer-experience",
      "documents/verify-and-audit"
    ]
  },
  {
    slug: "automation",
    title: "Automation",
    icon: "Zap",
    blurb: "Rules that run in the background so nobody has to remember.",
    articles: [
      "automation/workflows-overview",
      "automation/triggers",
      "automation/steps-and-branches",
      "automation/activate-and-monitor",
      "automation/sequences",
      "automation/your-kit-workflows"
    ]
  },
  {
    slug: "dashboard",
    title: "Dashboard & reporting",
    icon: "LayoutDashboard",
    blurb: "What to watch daily, and how to get the numbers out.",
    articles: ["dashboard/customize-widgets", "dashboard/widgets-to-watch", "dashboard/exports"]
  },
  {
    slug: "team-billing",
    title: "Team & billing",
    icon: "CreditCard",
    blurb: "Colleagues, what they can reach, and keeping the account funded.",
    articles: [
      "team-billing/invite-your-team",
      "team-billing/roles-and-permissions",
      "team-billing/billing",
      "team-billing/trial-and-card",
      "team-billing/usage-and-limits"
    ]
  },
  {
    slug: "settings-compliance",
    title: "Settings & compliance",
    icon: "ShieldCheck",
    blurb: "Your business details, who you send as, and the rules you send under.",
    articles: [
      "business-profile/business-details",
      "business-profile/branding",
      "business-profile/booking-link-and-custom-fields",
      "business-profile/security",
      "sending-identity/email-sender",
      "sending-identity/email-use-cases",
      "sending-identity/phone-number",
      "compliance/consent-matrix",
      "compliance/contact-creation-defaults",
      "compliance/double-opt-in",
      "compliance/jurisdiction-rulebook",
      "compliance/calling-hours",
      "compliance/decisions-log",
      "compliance/opt-outs"
    ]
  },
  {
    slug: "getting-help",
    title: "Getting help",
    icon: "LifeBuoy",
    blurb: "Ask the assistant, or reach a person.",
    articles: [
      "getting-help/cowork",
      "getting-help/cowork-answers-questions",
      "getting-help/cowork-talk-to-a-person",
      "getting-help/support",
      "getting-help/your-playbook"
    ]
  }
];
var articleRef = (chapterSlug, stepSlug) => chapterSlug + "/" + stepSlug;
var isAppStep = (chapter, step) => (chapter.surface ?? "both") !== "web" && (step.surface ?? "both") !== "web";
var isWebStep = (chapter, step) => (chapter.surface ?? "both") !== "app" && (step.surface ?? "both") !== "app";
function buildAppDocs(industry) {
  const guide = buildIndustryGuide(industry);
  const steps = /* @__PURE__ */ new Map();
  for (const chapter of guide.chapters) {
    for (const step of chapter.steps) {
      if (isAppStep(chapter, step)) {
        steps.set(articleRef(chapter.slug, step.slug), { chapter, step });
      }
    }
  }
  const byRef = {};
  const modules = DOC_MODULES.map((mod) => {
    const articles = [];
    for (const ref of mod.articles) {
      const found = steps.get(ref);
      if (!found) continue;
      const article = {
        ref,
        chapterSlug: found.chapter.slug,
        chapterTitle: found.chapter.title,
        moduleSlug: mod.slug,
        moduleTitle: mod.title,
        step: found.step
      };
      articles.push(article);
      byRef[ref] = article;
    }
    return { ...mod, articles };
  });
  const moduleForChapter = /* @__PURE__ */ new Map();
  for (const mod of modules) {
    for (const a of mod.articles) {
      if (!moduleForChapter.has(a.chapterSlug)) moduleForChapter.set(a.chapterSlug, mod);
    }
  }
  for (const [ref, { chapter, step }] of steps) {
    if (byRef[ref]) continue;
    const mod = moduleForChapter.get(chapter.slug);
    if (!mod) continue;
    const article = {
      ref,
      chapterSlug: chapter.slug,
      chapterTitle: chapter.title,
      moduleSlug: mod.slug,
      moduleTitle: mod.title,
      step
    };
    mod.articles.push(article);
    byRef[ref] = article;
  }
  return {
    industry: guide.industry,
    label: guide.label,
    headline: guide.headline,
    scenario: guide.scenario,
    modules,
    byRef,
    articleCount: Object.keys(byRef).length
  };
}

// src/getting-started/search.ts
var STOPWORDS = /* @__PURE__ */ new Set([
  "a",
  "an",
  "and",
  "are",
  "as",
  "at",
  "be",
  "by",
  "can",
  "do",
  "does",
  "for",
  "from",
  "get",
  "how",
  "i",
  "in",
  "is",
  "it",
  "me",
  "my",
  "of",
  "on",
  "or",
  "set",
  "that",
  "the",
  "their",
  "there",
  "this",
  "to",
  "up",
  "was",
  "what",
  "when",
  "where",
  "which",
  "who",
  "why",
  "will",
  "with",
  "you",
  "your"
]);
var tokenize3 = (s) => s.toLowerCase().replace(/[^a-z0-9\s-]/g, " ").split(/[\s-]+/).filter(Boolean);
function blockText(blocks) {
  const out = [];
  for (const b of blocks) {
    switch (b.type) {
      case "paragraph":
        out.push(b.text);
        break;
      case "list":
        out.push(b.items.join(" "));
        break;
      case "note":
        out.push(b.text);
        break;
      case "clickPath":
        out.push(b.path.join(" "));
        break;
      case "steps":
        out.push(b.title ?? "");
        out.push(b.items.map((i) => i.text + " " + (i.note ?? "")).join(" "));
        break;
      case "fields":
        out.push(b.rows.map((r) => r.name + " " + r.value + " " + (r.note ?? "")).join(" "));
        break;
      case "example":
        out.push(b.title + " " + b.body);
        break;
      case "action":
        out.push(b.label);
        break;
      case "faqRef":
        out.push(b.label);
        break;
    }
  }
  return stripGuideLabels(out.join(" ")).toLowerCase();
}
var toEntry = (a) => {
  const title = a.step.title;
  return {
    ref: a.ref,
    chapterSlug: a.chapterSlug,
    stepSlug: a.step.slug,
    title,
    why: a.step.why,
    moduleSlug: a.moduleSlug,
    moduleTitle: a.moduleTitle,
    route: a.step.route,
    titleLower: title.toLowerCase(),
    titleTokens: tokenize3(title),
    keywordText: (a.step.keywords ?? []).join(" ").toLowerCase(),
    // The module and chapter titles ride along in the body so "billing" finds articles filed
    // under Team & billing even when the word never appears in the prose.
    bodyText: [a.step.why, a.moduleTitle, a.chapterTitle, blockText(a.step.blocks)].join(" ").toLowerCase()
  };
};
function buildDocsSearchIndex(docs) {
  return Object.values(docs.byRef).map(toEntry);
}
function requiredMatches(tokenCount) {
  if (tokenCount <= 3) return tokenCount;
  return Math.max(3, Math.ceil(tokenCount * 0.6));
}
function scoreEntry(entry, tokens, phrase) {
  let score = 0;
  let matched = 0;
  for (const token of tokens) {
    const inTitle = entry.titleLower.includes(token);
    const inKeywords = entry.keywordText.includes(token);
    const inBody = entry.bodyText.includes(token);
    if (!inTitle && !inKeywords && !inBody) continue;
    matched++;
    if (inTitle) {
      score += 10;
      if (entry.titleTokens.some((t) => t.startsWith(token))) score += 6;
    }
    if (inKeywords) score += 8;
    if (inBody) score += 1;
  }
  if (matched < requiredMatches(tokens.length)) return 0;
  score *= matched / tokens.length;
  if (phrase.length > 2) {
    if (entry.titleLower.includes(phrase)) score += 25;
    else if (entry.keywordText.includes(phrase)) score += 18;
  }
  score -= Math.min(entry.titleTokens.length, 8) * 0.1;
  return score;
}
function searchDocs(index, query, limit = 8) {
  const raw = tokenize3(query);
  if (raw.length === 0) return [];
  const meaningful = raw.filter((t) => !STOPWORDS.has(t));
  const tokens = meaningful.length > 0 ? meaningful : raw;
  const phrase = tokens.join(" ");
  const hits = [];
  for (const entry of index) {
    const score = scoreEntry(entry, tokens, phrase);
    if (score > 0) hits.push({ entry, score });
  }
  hits.sort((a, b) => b.score - a.score || a.entry.title.localeCompare(b.entry.title));
  return hits.slice(0, limit);
}
function looksLikeQuestion(query) {
  return /^\s*(how|what|where|why|when|can|does|do)\b/i.test(query);
}

// src/getting-started/validate.ts
function blockStrings(block) {
  switch (block.type) {
    case "paragraph":
    case "note":
      return [block.text];
    case "list":
      return block.items;
    case "clickPath":
      return block.path;
    case "steps":
      return [block.title ?? "", ...block.items.flatMap((i) => [i.text, i.note ?? ""])];
    case "fields":
      return block.rows.flatMap((r) => [r.name, r.value, r.note ?? ""]);
    case "example":
      return [block.title, block.body];
    case "action":
    case "faqRef":
    case "docRef":
      return [block.label];
    default:
      return [];
  }
}
function procedureCoverage() {
  const missing = [];
  let total = 0;
  for (const chapter of GUIDE_SPINE) {
    for (const step of [...chapter.steps, ...TASK_ARTICLES[chapter.slug] ?? []]) {
      if (step.kind === "concept") continue;
      total += 1;
      if (step.blocks.some((b) => b.type === "steps")) continue;
      missing.push(chapter.slug + "/" + step.slug);
    }
  }
  for (const overlay of Object.values(INDUSTRY_OVERLAYS)) {
    for (const [chapterSlug, extras] of Object.entries(overlay.extraSteps ?? {})) {
      for (const step of extras) {
        if (step.kind === "concept") continue;
        total += 1;
        if (step.blocks.some((b) => b.type === "steps")) continue;
        missing.push(overlay.industry + ":" + chapterSlug + "/" + step.slug);
      }
    }
  }
  return { total, missing };
}
function spineStepKeys() {
  const keys = /* @__PURE__ */ new Set();
  for (const chapter of GUIDE_SPINE) {
    for (const step of chapter.steps) keys.add(chapter.slug + "/" + step.slug);
    for (const step of TASK_ARTICLES[chapter.slug] ?? []) keys.add(chapter.slug + "/" + step.slug);
  }
  return keys;
}
function guideAnchors(industry) {
  return buildIndustryGuide(industry).chapters.flatMap(
    (c) => c.steps.map((s) => stepAnchor(c.slug, s.slug))
  );
}
function validateGuide() {
  const issues = [];
  const chapterSlugs = new Set(GUIDE_SPINE.map((c) => c.slug));
  const stepKeys = spineStepKeys();
  for (const { value } of INDUSTRIES) {
    const anchors = guideAnchors(value);
    const seen = /* @__PURE__ */ new Set();
    for (const anchor of anchors) {
      if (seen.has(anchor)) {
        issues.push({
          kind: "duplicate-anchor",
          message: 'Duplicate anchor "' + anchor + '" in the ' + value + " guide."
        });
      }
      seen.add(anchor);
    }
  }
  for (const ref of procedureCoverage().missing) {
    issues.push({
      kind: "missing-procedure",
      message: 'Article "' + ref + `" has no steps block. Add one, or mark it kind: 'concept' if nothing is clicked.`
    });
  }
  for (const chapter of GUIDE_SPINE) {
    for (const step of [...chapter.steps, ...TASK_ARTICLES[chapter.slug] ?? []]) {
      if (step.blocks.length === 0) {
        issues.push({
          kind: "empty-step",
          message: 'Step "' + chapter.slug + "/" + step.slug + '" has no blocks.'
        });
      }
      if (step.route && !step.route.startsWith("/")) {
        issues.push({
          kind: "bad-route",
          message: 'Step "' + chapter.slug + "/" + step.slug + '" has a non-absolute route.'
        });
      }
      for (const block of step.blocks) {
        for (const text of blockStrings(block)) {
          if (hasUnbalancedLabels(text)) {
            issues.push({
              kind: "bad-inline-markup",
              message: 'Step "' + chapter.slug + "/" + step.slug + '" has an unpaired ** in: ' + text.slice(0, 60)
            });
          }
        }
      }
      for (const ref of step.prereqs ?? []) {
        if (!stepKeys.has(ref)) {
          issues.push({
            kind: "unknown-prereq",
            message: 'Step "' + chapter.slug + "/" + step.slug + '" lists unknown prereq "' + ref + '".'
          });
        }
      }
    }
  }
  const webOnly = /* @__PURE__ */ new Set();
  const appEligible = /* @__PURE__ */ new Set();
  const chaptersWithArticles = /* @__PURE__ */ new Set();
  for (const chapter of GUIDE_SPINE) {
    for (const step of [...chapter.steps, ...TASK_ARTICLES[chapter.slug] ?? []]) {
      const ref = articleRef(chapter.slug, step.slug);
      if (isAppStep(chapter, step)) {
        appEligible.add(ref);
        chaptersWithArticles.add(chapter.slug);
      } else {
        webOnly.add(ref);
      }
    }
  }
  const placed = /* @__PURE__ */ new Set();
  for (const mod of DOC_MODULES) {
    for (const ref of mod.articles) {
      if (webOnly.has(ref)) {
        issues.push({
          kind: "web-only-doc-ref",
          message: 'Module "' + mod.slug + '" lists "' + ref + '", which is surface: web.'
        });
      } else if (!appEligible.has(ref)) {
        issues.push({
          kind: "unknown-doc-ref",
          message: 'Module "' + mod.slug + '" lists unknown article "' + ref + '".'
        });
      }
      if (placed.has(ref)) {
        issues.push({
          kind: "duplicate-doc-ref",
          message: 'Article "' + ref + '" is listed by more than one module.'
        });
      }
      placed.add(ref);
    }
  }
  for (const chapter of GUIDE_SPINE) {
    for (const step of [...chapter.steps, ...TASK_ARTICLES[chapter.slug] ?? []]) {
      for (const block of step.blocks) {
        if (block.type !== "docRef") continue;
        if (webOnly.has(block.ref)) {
          issues.push({
            kind: "web-only-doc-ref",
            message: 'Article "' + articleRef(chapter.slug, step.slug) + '" links to "' + block.ref + '", which is surface: web and has no in-app page.'
          });
        } else if (!appEligible.has(block.ref)) {
          issues.push({
            kind: "unknown-doc-ref",
            message: 'Article "' + articleRef(chapter.slug, step.slug) + '" links to unknown article "' + block.ref + '".'
          });
        }
      }
    }
  }
  const coveredChapters = /* @__PURE__ */ new Set();
  for (const mod of DOC_MODULES) {
    for (const ref of mod.articles) coveredChapters.add(ref.slice(0, ref.indexOf("/")));
  }
  for (const chapterSlug of chaptersWithArticles) {
    if (!coveredChapters.has(chapterSlug)) {
      issues.push({
        kind: "unplaced-article",
        message: 'Chapter "' + chapterSlug + '" has app articles but no module lists any of them, so nothing in the app links to them.'
      });
    }
  }
  for (const overlay of Object.values(INDUSTRY_OVERLAYS)) {
    for (const key of Object.keys(overlay.stepBlocks ?? {})) {
      if (!stepKeys.has(key)) {
        issues.push({
          kind: "unknown-overlay-step",
          message: 'Overlay "' + overlay.industry + '" targets unknown step "' + key + '".'
        });
      }
    }
    for (const [key, blocks] of Object.entries(overlay.stepBlocks ?? {})) {
      for (const block of blocks) {
        for (const text of blockStrings(block)) {
          if (hasUnbalancedLabels(text)) {
            issues.push({
              kind: "bad-inline-markup",
              message: 'Overlay "' + overlay.industry + '" block on "' + key + '" has an unpaired ** in: ' + text.slice(0, 60)
            });
          }
        }
      }
    }
    for (const key of Object.keys(overlay.extraSteps ?? {})) {
      if (!chapterSlugs.has(key)) {
        issues.push({
          kind: "unknown-overlay-chapter",
          message: 'Overlay "' + overlay.industry + '" targets unknown chapter "' + key + '".'
        });
      }
    }
  }
  return issues;
}

export { ACTION_COLORS, ACTION_LABELS, AI_AGENT_CATEGORIES, AI_USAGE_FEATURES, ALL_EMAIL_TEMPLATE_VARIABLES, BRANCH_QUICK_STARTS, CAMPAIGN_FIELD_LABELS, CATEGORY_LABELS, CHAT_BRAND_ID_PATTERN, CHAT_THEME_COLOR_PATTERN, CHAT_THEME_CONTRAST_PAIRS, CHAT_THEME_CONTROLS, CHAT_THEME_CONTROL_KEYS, CHAT_THEME_DEFAULTS, CHAT_THEME_GROUPS, CHAT_WIDGET_POSITIONS, COMPLIANCE_REVIEW_WINDOWS, COMPLIANCE_REVIEW_WINDOW_LABELS, COMPLIANCE_TRIAGES, COMPLIANCE_TRIAGE_LABELS, CONDITION_FIELDS, CONTACT_FIELD_LABELS, CONTACT_IMPORT_CUSTOM_PREFIX, CONTACT_IMPORT_OPTIN_ALIASES, CONTACT_IMPORT_REQUIRED_SUFFIX, CONTACT_IMPORT_STANDARD_ALIASES, CONTACT_NOTE_MAX_LENGTH, CONTACT_SMS_VARIABLES, DASHBOARD_WIDGET_IDS, DASHBOARD_WIDGET_REGISTRY, DEFAULT_DASHBOARD_LAYOUT, DELIVERY_METHOD_CONFIG, DOCUMENT_FIELD_LABELS, DOCUMENT_SMS_VARIABLES, DOC_MODULES, EMAIL_TEMPLATE_DEFINITIONS, EMAIL_TEMPLATE_KEYS, EMPTY_STEP_COUNTS, ENTITY_ICONS, FIELD_LABELS, FORM_FIELD_LABELS, FORM_SMS_VARIABLES, GUIDE_APP_BASE_URL, GUIDE_SITE_BASE_URL, GUIDE_SPINE, INDUSTRIES, INDUSTRY_DOCUMENT_CATEGORY_SLUGS, INDUSTRY_FORM_CATEGORY_SLUGS, INDUSTRY_OVERLAYS, INDUSTRY_TEMPLATE_KEYS, INDUSTRY_TO_KIT, INDUSTRY_TO_SOLUTION_SLUG, LAYOUT_FIELD_TYPES, LEGACY_FIELD_ALIASES, MAX_WAIT_MS, MERGE_TAG_NAMESPACES, MESSAGE_PURPOSES, MESSAGE_PURPOSE_COPY, MESSAGE_PURPOSE_DESCRIPTIONS, MESSAGE_PURPOSE_LABELS, NAME_ERROR, NAME_REGEX, NDF_BLOCK_TYPES, NDF_CONTENT_BLOCK_TYPES, NDF_FIELD_BLOCK_TYPES, OPERATORS_BY_VALUE_TYPE, OPERATOR_LABELS, OPT_IN_CONFIRMATION_SMS, OPT_IN_CONFIRMATION_SMS_GENERIC, OPT_OUT_CONFIRMATION_SMS, ORGANISATION_SMS_VARIABLES, ORG_CUSTOM_FIELD_RESERVED, OrgCustomFieldSchema, OrgCustomFieldsSchema, PRE_BUNDLE_CANONICALIZATION_VERSION, PRE_BUNDLE_SCHEMA_VERSION, PRE_CHECK_IDS, PRE_CONDITION_CODES, PRE_CONDITION_DESCRIPTIONS, PRE_CONDITION_LABELS, PRE_DECISION_DESCRIPTIONS, PRE_DECISION_LABELS, PRE_REMEDIATION_CODES, PRE_REMEDIATION_DESCRIPTIONS, PRE_REMEDIATION_LABELS, PRE_RISK_FLAGS, PRE_RISK_FLAG_LABELS, ROLE_PERMISSIONS, SEND_TYPE_CONFIG, SHORT_LINK_PURPOSES, SLATE_100, SLATE_900, SMS_OPT_IN_KEYWORDS, SMS_OPT_OUT_KEYWORDS, STATUS_BADGE_CONFIG, SYSTEM_MERGE_TAGS, TAB_COUNT_KEY, TENANT_FEATURE_CATALOG, TENANT_FEATURE_KEYS, USE_CASE_KITS, VALUELESS_OPERATORS, VOICE_SCRIPT_FIELD_LABELS, WAIT_DURATION_MAX, WAIT_DURATION_MIN, WORKFLOW_MESSAGE_STATUSES, WORKFLOW_MESSAGE_STATUS_LABELS, WORKFLOW_NAME_MAX_LENGTH, WORKFLOW_STEP_FILTERS, WORKFLOW_STEP_FILTER_LABELS, WORKFLOW_STEP_OUTCOMES, WORKFLOW_STEP_OUTCOME_LABELS, accountDetailSchema, accountListFiltersSchema, accountListItemSchema, accountNoteSchema, accountSortFieldSchema, accountTagSchema, accountUsageStatsSchema, activityItemSchema, activityListResponseSchema, activityPaginationSchema, activityQueryParamsSchema, activityStatsSchema, activityStatusSchema, activitySummarySchema, activityTabSchema, activityTagSchema, addOnCatalogFiltersSchema, addOnCatalogSchema, addOnCategorySchema, addOnPurchaseSchema, addOnTypeSchema, addSupportMessageDtoSchema, adjustForContrast, adminOptOutEventsQuerySchema, adminUserFiltersSchema, aggregationPeriodSchema, aiFlaggedContentSchema, aiToneSchema, analyticsQuerySchema, apiErrorResponseSchema, apiErrorSchema, apiSuccessSchema, articleRef, assignAddOnToPlanDtoSchema, assignDiscountDtoSchema, assignSupportRequestDtoSchema, auditActionSchema, auditActivityDataPointSchema, auditEntityTypeSchema, auditExportRequestSchema, auditLogFiltersSchema, auditLogSchema, auditStatsSchema, authUserSchema, authorTypeSchema, availableAddOnSchema, b2bOrB2cSchema, billingCycleSchema, billingDashboardSchema, billingStatusSchema, buildAppDocs, buildDocsSearchIndex, buildIndustryGuide, bulkActionRequestSchema, bulkActionResponseSchema, bundleCallingHourWindowSchema, bundleCheckDefinitionSchema, bundleDncRegistrySchema, bundleFeatureFlagDefaultSchema, bundleFrequencyCapSchema, bundleJurisdictionSchema, bundleRuleSchema, callingHourWindowSchema, camelToSnakeTag, campaignAudienceTypeSchema, campaignDetailResponseSchema, campaignDetailSchema, campaignEnrollmentSchema, campaignSchema, campaignStatsSchema, campaignStatusSchema, campaignStepSchema, campaignStepStatsSchema, campaignStepTypeSchema, campaignTriggerSchema, campaignTypeSchema, cancelAccountDtoSchema, changeActionSchema, changeDetailSchema, changeEntrySchema, changeListResponseSchema, changeUserSchema, changesQuerySchema, channelConsentSchema, channelSchema, chartDataSchema, clampWaitDelayMs, classifyMergeTag, classifyNameLabel, clicksendInboundSmsMessageSchema, clicksendInboundSmsSchema, collapseChatTheme, complianceCheckRequestSchema, complianceCheckSchema, complianceReasonFor, complianceSettingsSchema, consentFormValuesSchema, consentLevelSchema, consentMatrixSchema, consentRecordSchema, consentStatusSchema, contactCollectionFormSchema, contactCollectionFormSubmissionSchema, contactCollectionSubmissionSchema, contactColumnFilterSchema, contactColumnFiltersSchema, contactEligibilityFilterSchema, contactFilterChannelSchema, contactFilterSchema, contactGroupInfoSchema, contactGroupMembershipSchema, contactGroupSchema, contactGroupStageSchema, contactImportRowSchema, contactListFilterSchema, contactListFilterToSearchParams, contactNoteSchema, contactOrSendSchema, contactSavedViewSchema, contactSchema, contentSuggestionSchema, contentSuggestionSourceSchema, contentSuggestionTypeSchema, contentSuggestionsQuerySchema, contentSuggestionsResponseSchema, contrastRatio, conversationActionResponseSchema, conversationDetailResponseSchema, conversationDetailSchema, conversationListItemSchema, conversationMessageSchema, conversationSchema, conversationStatusSchema, conversationsListResponseSchema, costEstimateSchema, createAccountDtoSchema, createAccountNoteSchema, createAccountTagDtoSchema, createAddOnCatalogDtoSchema, createCallingHourWindowSchema, createCampaignSchema, createCampaignStepDtoSchema, createContactCollectionFormSchema, createContactGroupSchema, createContactGroupStageSchema, createContactNoteSchema, createContactSchema, createContactWithConsentSchema, createCustomFieldSchema, createDiscountDtoSchema, createDncRegistrySchema, createDocumentSchema, createFeatureFlagDtoSchema, createFormSchema, createFrequencyCapConfigSchema, createJurisdictionSchema, createPlanDtoSchema, createPlatformCampaignDtoSchema, createPreCheckDefinitionSchema, createRuleSchema, createSignatureRequestSchema, createSmsTemplateSchema, createSuperAdminUserSchema, createSupportRequestDtoSchema, createTagSchema, createTemplateCategoryDtoSchema, createTemplateDtoSchema, createTenantSchema, createUserSchema, createVoiceScriptSchema, createWebhookSchema, customFieldDefinitionListResponseSchema, customFieldDefinitionSchema, customFieldFilterSchema, customFieldHeaderAliases, customFieldTarget, customFieldTypeSchema, customFieldValueSchema, customFieldValuesSchema, dashboardStatsSchema, dateFieldBlockSchema, dateRangeSchema, declineDocumentSchema, deliveryChannelSchema, deliveryConfigSchema, deliveryMethodSchema, deriveCampaignMessageStatus, deriveNameParts, deriveSubmitterName, deriveWorkflowMessageStatus, describeCampaignMessageStatus, describeCampaignProblem, describeCampaignProblemGroup, describeCampaignStory, describeComplianceReason, describeComplianceStory, describeFailedCheck, describeMessageStatus, describeOptOutSource, describePreDecision, describeProblemGroup, describeSendProblem, describeStep, describeStepOutcome, describeStepProblem, describeStoredPurpose, discountListFiltersSchema, discountSchema, discountScopeSchema, discountTypeSchema, discountWithStatsSchema, disputeStatusSchema, dividerBlockSchema, dncRegistrySchema, dncStatusSchema, documentSchema, documentStatusSchema, documentTypeSchema, ebrEventTypeSchema, eligibilityFlagsSchema, eligibilityStatusSchema, emailConfigSchema, emailProviderSchema, enrollTenantsInCampaignDtoSchema, enrollmentStatusSchema, expandChatTheme, explainPreBlock, exportFormatSchema, exportRequestSchema, exportResponseSchema, extractVariableReferences, featureFlagFiltersSchema, featureFlagSchema, featureFlagScopeSchema, fieldValidationSchema, fileFieldBlockSchema, filterDuplicateSuggestions, findUnmappedRequiredCustomFields, fitWorkflowName, flagSeveritySchema, flagStatusSchema, flaggedContentTypeSchema, formBrandingSchema, formFieldSchema, formFieldTypeSchema, formResponseFieldSchema, formSchema, formSchemaDefinition, formSchemaToNdf, formSettingsSchema, formSubmissionResponseSchema, formSubmissionSchema, formatFieldName, freePlanAccountSchema, frequencyCapConfigSchema, generateBlockId, getContrastRatio, getFieldBlocks, getFieldDef, getFieldLabel, getIndustryCategorySlugs, getIndustryLabel, getIndustrySampleMessage, getKitForIndustry, getNdfStyles, getOrgMessageFieldAliases, getReadableTextColor, getSignatureBlocks, getStaticMessageCatalog, getUseCaseKit, globalTemplateContentToNdf, globalTemplateSchema, groupListItemSchema, groupNdfFieldRows, guideAnchors, hasUnbalancedLabels, headingBlockSchema, healthStatusSchema, imageBlockSchema, impersonationResponseSchema, impersonationSessionSchema, importTemplateHeaderForCustomField, initiateVoiceCallSchema, invoiceLineItemSchema, invoiceListFiltersSchema, invoiceSchema, invoiceStatusSchema, isAppStep, isConditionGroup, isConditionLeaf, isContentBlock, isEmptyContactListFilter, isFieldBlock, isImportableCustomField, isIndustryValue, isLayoutFieldType, isLegacyField, isLightColor, isMessageStep, isNamespacedMergeTag, isOperatorAllowed, isPdfOverlayBlock, isUnresolvableMergeTagStatus, isValidName, isWaitUnit, isWebStep, jurisdictionSchema, kanbanContactSchema, kanbanGroupSchema, kanbanStageSchema, lifecycleEventTypeSchema, limitIncreaseTypeSchema, listBlockSchema, listConversationsQuerySchema, listIndustryGuideSummaries, listUseCaseKitSummaries, looksLikeQuestion, maintenanceModeSchema, manualOptChangeRequestSchema, mapUltravoxStatus, markAsReadRequestSchema, messageDirectionSchema, messagePurposeSchema, messageSchema, messageStatusSchema, messageTypeSchema, mixColors, moveContactSchema, mrrBreakdownSchema, multiSignerStatusSchema, nameFieldsFromForm, nameFieldsFromLegacySchema, nameFieldsFromNdfSchema, ndfBlockSchema, ndfConditionalLogicSchema, ndfDocumentMetaSchema, ndfFieldValidationSchema, ndfFieldWidthBasis, ndfFieldWidthClass, ndfFieldWidthSchema, ndfFormBrandingSchema, ndfFormSettingsSchema, ndfKindSchema, ndfPdfSourceSchema, ndfSignerSchema, ndfToFormSchema, ndfToTemplateContent, ndfVariableDefinitionSchema, ndfVersionSchema, nexusDocumentSchema, normalizeCondition, normalizeContentName, normalizeImportHeader, normalizedPositionSchema, operatorNeedsValue, operatorsForField, optInSourceSchema, optOutActionSchema, optOutChannelSchema, optOutEventSchema, optOutHistoryQuerySchema, optOutHistoryResponseSchema, optOutSourceSchema, overageTypeSchema, pageBreakBlockSchema, paginatedAccountListSchema, paginatedAddOnCatalogSchema, paginatedAiFlagsListSchema, paginatedAuditLogsSchema, paginatedCampaignsListSchema, paginatedDiscountsListSchema, paginatedEnrollmentsListSchema, paginatedFeatureFlagsListSchema, paginatedInvoicesListSchema, paginatedPlansListSchema, paginatedResponseSchema, paginatedSuperAdminUserListSchema, paginatedSupportRequestListSchema, paginatedTagsListSchema, paginatedTemplatesListSchema, paginationMetaSchema, paginationSchema, paragraphBlockSchema, parseEligibleForParam, parseGuideLabels, parseHex, parseSubmissionData, pastDueAccountSchema, paymentDisputeSchema, paymentSummarySchema, pdfOverlayFieldBlockSchema, pipelineMembershipFilterSchema, planAddOnAssignmentSchema, planDistributionSchema, planListFiltersSchema, planSchema, planStatusSchema, planWithStatsSchema, platformCampaignListFiltersSchema, platformCampaignSchema, platformCampaignStatusSchema, platformHealthCheckSchema, platformHealthStatusSchema, platformServiceSchema, preAiClassificationFiltersSchema, preAiClassificationSchema, preBundleIntegritySchema, preBundleMetadataSchema, preBundlePayloadSchema, preBundleSchema, preChannelSchema, preCheckDefinitionSchema, preCheckIdSchema, preConditionCodeSchema, preConditionOutputSchema, preConsentCaptureTraceEntrySchema, preContactFrequencySchema, preDecisionLogFiltersSchema, preDecisionLogSchema, preDecisionSchema, preInputSchema, preOutputSchema, preRecipientSchema, preRemediationCodeSchema, preRemediationOutputSchema, preRiskFlagOutputSchema, preRiskFlagSchema, preSummarySchema, preTraceEntrySchema, previewVoiceScriptSchema, prioritySchema, problemReasonPhrase, procedureCoverage, providerMessage, providerStatusSchema, publicDocumentSchema, publicFileUploadResponseSchema, publicFormConfigSchema, publicFormSchema, publicFormSubmissionSchema, publicSignDocumentSchema, queueStatsSchema, reactivateAccountDtoSchema, recipientScheduleOverrideSchema, recipientSelectionSchema, recipientStatusSchema, recipientWithContactSchema, recipientsListResponseSchema, registryTypeSchema, relativeLuminance, renderNdfToHtml, reorderCategoriesDtoSchema, reorderCustomFieldsSchema, replyComplianceSchema, reportRequestSchema, resetBlockIdCounter, resetPasswordDtoSchema, resolveContactImportHeaders, resolveGuideIndustry, resolveLink, resolveSupportRequestDtoSchema, resolveSystemMergeTagKey, resolveUpcomingOutcome, revenueMetricsSchema, revenueTrendDataPointSchema, reviewAiFlagDtoSchema, ruleEffectSchema, ruleLayerSchema, ruleRequiredCheckSchema, ruleSchema, ruleTypeSchema, scheduleOptionsSchema, scoreNameLabel, searchDocs, sectionHeaderBlockSchema, selectFieldBlockSchema, sendIntentSchema, sendMessageSchema, sendReminderRequestSchema, sendReminderResponseSchema, sendReplyRequestSchema, sendReplyResponseSchema, sendTypeSchema, serviceHealthSchema, setupIntentSchema, setupProgressSchema, setupStepIdSchema, signDocumentSchema, signatureCompletionResponseSchema, signatureDataSchema, signatureDeliveryChannelSchema, signatureDeliveryStatusSchema, signatureFieldBlockSchema, signatureFieldPositionSchema, signatureInstanceSchema, signatureInstanceStatusSchema, signatureSchema, signatureStatusSchema, signatureWithRelationsSchema, signerContextSchema, signerMappingInstanceSchema, signerMappingSchema, sinchInboundSmsSchema, smsConfigSchema, smsProviderSchema, smsTemplateSchema, smsTemplateTypeSchema, snakeToCamelTag, spacerBlockSchema, spineStepKeys, splitFieldKey, startImpersonationDtoSchema, stepAnchor, stripGuideLabels, stripImportHeaderHint, subdivisionTypeSchema, subscriptionInfoSchema, suggestReplyRequestSchema, suggestReplyResponseSchema, summarizeKit, summarizeStep, superAdminAuditActionSchema, superAdminAuditLogSchema, superAdminAuthUserSchema, superAdminEntityTypeSchema, superAdminLoginRequestSchema, superAdminLoginResponseSchema, superAdminPlanSchema, superAdminRefreshRequestSchema, superAdminRefreshResponseSchema, superAdminRoleSchema, superAdminUserListItemSchema, superAdminUserSchema, supportCategorySchema, supportListFiltersSchema, supportMessageSchema, supportPrioritySchema, supportQueueSummarySchema, supportRequestSchema, supportRequestStatusSchema, suspendAccountDtoSchema, tableBlockSchema, tagSchema, templateCategoryEntitySchema, templateCategorySchema, templateCategoryTypeSchema, templateContentSchema, templateContentToNdf, templateListFiltersSchema, templateSignerSchema, templateTypeSchema, templateVisibilitySchema, tenantDiscountSchema, tenantLifecycleEventSchema, tenantSchema, tenantSettingsSchema, tenantSourceSchema, tenantStatusSchema, tenantUsageSummarySchema, testSendEmailTemplateSchema, textFieldBlockSchema, textFieldSchema, textStyleSchema, timeFieldBlockSchema, timeSeriesPointSchema, timelineEventSchema, timelineEventTypeSchema, timelineResponseSchema, toHex, toggleFeatureFlagDtoSchema, toggleMaintenanceModeDtoSchema, tokenStatusSchema, tokenValidationResponseSchema, ultravoxStatusSchema, ultravoxWebhookPayloadSchema, unassignAddOnFromPlanDtoSchema, unifiedInboxChannelSchema, unifiedInboxContactSchema, unifiedInboxCountsSchema, unifiedInboxDetailResponseSchema, unifiedInboxFilterSchema, unifiedInboxFiltersSchema, unifiedInboxItemDetailSchema, unifiedInboxItemSchema, unifiedInboxItemTypeSchema, unifiedInboxListResponseSchema, unifiedInboxMessageSchema, unifiedInboxPaginationSchema, unifiedInboxQueryParamsSchema, unreadCountResponseSchema, unsubscribeInfoSchema, unsubscribeTokenSchema, updateAccountDtoSchema, updateAccountNoteSchema, updateAddOnCatalogDtoSchema, updateCallingHourWindowSchema, updateCampaignSchema, updateComplianceSettingsDtoSchema, updateContactCollectionFormSchema, updateContactGroupSchema, updateContactGroupStageSchema, updateContactNoteSchema, updateContactSchema, updateContactWithConsentSchema, updateConversationRequestSchema, updateConversationResponseSchema, updateCustomFieldSchema, updateDiscountDtoSchema, updateDncRegistrySchema, updateDocumentSchema, updateEmailConfigDtoSchema, updateEmailTemplateSchema, updateFeatureFlagDtoSchema, updateFormSchema, updateFrequencyCapConfigSchema, updateJurisdictionSchema, updatePlanDtoSchema, updatePlatformCampaignDtoSchema, updatePreCheckDefinitionSchema, updateRuleSchema, updateSMSConfigDtoSchema, updateSetupProgressSchema, updateSmsTemplateSchema, updateSuperAdminUserSchema, updateSupportRequestDtoSchema, updateTemplateCategoryDtoSchema, updateTemplateDtoSchema, updateTemplateVisibilityDtoSchema, updateTenantSchema, updateUserSchema, updateVoiceScriptSchema, usageOverageSchema, usageStatusSchema, usageTypeSchema, userRoleSchema, userSchema, userSettingsSchema, userWithFullNameSchema, validateBranchCondition, validateGuide, validateNdfSignatureAssignments, validateSequenceAudience, validateSequenceHasMessage, validateTemplateSignatureAssignments, validateWaitDuration, validateWorkflowName, validateWorkflowStep, validateWorkflowStepIssue, validateWorkflowSteps, viewModeSchema, voiceCallResultSchema, voiceCallSchema, voiceCallStatusSchema, voiceCallTypeSchema, voiceConsentTypeSchema, voiceScriptCallTypeSchema, voiceScriptReferenceSchema, voiceScriptSchema, voiceScriptSettingsSchema, voiceScriptUsageStatsSchema, voiceScriptWithTagsSchema, waitFiresAt, webhookEventSchema, webhookEventTypeSchema, webhookSchema };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map